// BTCNJCC Main Application Logic & Media Telemetry
import { initAmbientWallpaper } from './src/ambient-wallpaper.js';
import { AutoUpdateManager } from './src/AutoUpdateManager.js';
import { syncCornerVersionBadge, resolveEffectiveAppVersion } from './src/version-sync.js';
import { transitionWithPeel, pagePeelEngine } from './src/page-peel-transition.js';
import { getRandomEasterEgg } from './src/typewriter-easter-eggs.js';

const RAW_BUILD_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : null;
import {
  sanitizeContributorName,
  formatPrefixedMediaFilename,
  extractContributorFromFilename,
  computeCanonicalParenthesizedFilename,
  parseCanonicalFilenameParts,
  parseContentDispositionFilename,
  parseGooglePhotosSharedAlbumHtml,
  createOwnerTaggedFile,
  createZipArchiveBlob,
  resolveRideCategoryFolder,
  resolveOsSafeZipEntryPath,
  groupMediaCenterRidesByYearAndCategory,
  createMultiVolumeZipArchives,
  formatStableDurationEta,
  computeAnnualDownloadTelemetry
} from './src/media-owner-tagger.js';

export {
  sanitizeContributorName,
  formatPrefixedMediaFilename,
  extractContributorFromFilename,
  computeCanonicalParenthesizedFilename,
  parseCanonicalFilenameParts,
  parseContentDispositionFilename,
  parseGooglePhotosSharedAlbumHtml,
  createOwnerTaggedFile,
  createZipArchiveBlob,
  resolveRideCategoryFolder,
  resolveOsSafeZipEntryPath,
  groupMediaCenterRidesByYearAndCategory,
  createMultiVolumeZipArchives,
  formatStableDurationEta,
  computeAnnualDownloadTelemetry
};

export const APP_VERSION = resolveEffectiveAppVersion(RAW_BUILD_VERSION, '1.1.125');

// Non-destructive version badge synchronization (blocks visual downgrade flicker)
syncCornerVersionBadge(APP_VERSION);

/**
 * Purges any foreign BTCNJYIR storage keys, DOM lock classes, or rogue Service Workers
 * so BTCNJCC (a completely separate application) is never affected by BTCNJYIR state.
 */
export function purgeForeignYirArtifacts() {
  if (typeof document !== 'undefined' && document.documentElement && document.documentElement.classList) {
    document.documentElement.classList.remove('is-access-locked', 'site-kill-switch-active', 'hide-uc-banner');
  }
  if (typeof localStorage !== 'undefined') {
    try {
      const foreignKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('btcnjyir_') || key.startsWith('btcnj_yir_'))) {
          foreignKeys.push(key);
        }
      }
      foreignKeys.forEach(k => {
        try { localStorage.removeItem(k); } catch (_) {}
      });
    } catch (_) {}
  }
  if (typeof sessionStorage !== 'undefined') {
    try {
      const foreignSessionKeys = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && (key.startsWith('btcnjyir_') || key.startsWith('btcnj_yir_'))) {
          foreignSessionKeys.push(key);
        }
      }
      foreignSessionKeys.forEach(k => {
        try { sessionStorage.removeItem(k); } catch (_) {}
      });
    } catch (_) {}
  }
}

/**
 * Frictionless Client Storage Version Reconciler
 * Automatically invalidates and purges legacy/outdated client cache partitions across version updates
 * without requiring manual user intervention or clearing site data.
 */
export function reconcileClientStorageVersion(currentVersion) {
  purgeForeignYirArtifacts();
  if (typeof localStorage === 'undefined' || !currentVersion) return;
  try {
    const storedVersion = localStorage.getItem('btcnjcc_active_version');
    if (storedVersion !== currentVersion) {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('btcnjcc_media_rides_') || key === 'btcnjcc_media_rides_v2')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(k => {
        try { localStorage.removeItem(k); } catch (_) {}
      });
      try { localStorage.removeItem('btcnjcc_mobile_aero_layout'); } catch (_) {}
      localStorage.setItem('btcnjcc_active_version', currentVersion);
    }
  } catch (_) {}
}

reconcileClientStorageVersion(APP_VERSION);

let globalToastTimer = null;
export function showGlobalToast(msg) {
  if (typeof document === 'undefined' || !msg) return;
  let container = document.getElementById('global-toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'global-toast-container';
    container.className = 'global-toast-container';
    document.body.appendChild(container);
  }
  container.innerHTML = `<div class="global-toast"><span class="global-toast-icon">✓</span><span class="global-toast-text">${escapeHtml(msg)}</span></div>`;
  clearTimeout(globalToastTimer);
  globalToastTimer = setTimeout(() => {
    if (container) container.innerHTML = '';
  }, 2400);
}

// Configuration & Production Endpoints
const CONFIG = {
  R5M_BASE_URL: 'https://r5m-quarr.web.app',
  ALBUMS_API_URL: 'https://r5m-quarr.web.app/api/get-photo-albums',
  MEDIA_CATALOG_API_URL: 'https://r5m-quarr.web.app/api/get-media-catalog',
  SET_RIDE_MEDIA_API_URL: 'https://r5m-quarr.web.app/api/set-ride-media',
  SYNC_SINGLE_MEDIA_API_URL: 'https://us-central1-quarr-rule-5.cloudfunctions.net/syncSingleMedia',
  PLAYLIST_API_URL: 'https://r5m-quarr.web.app/api/get-playlist-videos',
  FIRESTORE_BTCNJ_RIDES_URL: 'https://firestore.googleapis.com/v1/projects/quarr-rule-5/databases/(default)/documents/btcnj_rides',
  YT_PLAYLIST_URL: 'https://youtube.com/playlist?list=PLgvVTo25qNhbFz-aoolyQ5JCllHUw3Mt9',
  FIRESTORE_FEEDBACK_URL: 'https://firestore.googleapis.com/v1/projects/btcnjcc/databases/(default)/documents/feedback',
  FIRESTORE_VIDEO_OVERRIDES_URL: 'https://firestore.googleapis.com/v1/projects/btcnjcc/databases/(default)/documents/ride_video_overrides',
  STORAGE_KEY_VIDEO_OVERRIDES: 'btcnjcc_ride_video_overrides_v1',
  FIRESTORE_MEDIA_OVERRIDES_URL: 'https://firestore.googleapis.com/v1/projects/btcnjcc/databases/(default)/documents/media_overrides',
  STORAGE_KEY_MEDIA_OVERRIDES: 'btcnjcc_media_overrides_v1',
  FIRESTORE_BTCNJ_RIDES_LOOKUP_URL: 'https://firestore.googleapis.com/v1/projects/quarr-rule-5/databases/(default)/documents/btcnj_rides',
  CACHE_KEY_MEDIA_RIDES: `btcnjcc_media_rides_v${APP_VERSION}`,
  CACHE_TTL_MS: 5 * 60 * 1000 // 5 minutes
};

// ============================================================================
// 1. Resilient Client-Side URL Shortcut Router (Edge Fallback)
// ============================================================================
const SHORTCUT_ROUTES = {
  '/v': CONFIG.YT_PLAYLIST_URL,
  '/vid': CONFIG.YT_PLAYLIST_URL,
  '/vids': CONFIG.YT_PLAYLIST_URL,
  '/video': CONFIG.YT_PLAYLIST_URL,
  '/videos': CONFIG.YT_PLAYLIST_URL,
  '/r5': 'https://r5m-quarr.web.app/',
  '/r5m': 'https://r5m-quarr.web.app/'
};

export function sanitizeViewUrl() {
  if (typeof window === 'undefined' || !window.history || typeof window.history.replaceState !== 'function') return;
  const pathname = (window.location.pathname || '').toLowerCase().replace(/\/+$/, '');
  const search = window.location.search || '';
  if (isEmbeddedMode(search) || isHeadlessMode(search)) {
    return;
  }
  const isPhotoRoute = ['/photos', '/photo', '/p'].includes(pathname) || search.includes('view=photos') || search.includes('photos=') || (['/photos', '/photo', '/p'].includes(pathname) && (search.includes('layout=') || search.includes('mode=')));
  if (isPhotoRoute) {
    const params = new URLSearchParams(search);
    params.delete('view');
    params.delete('photos');
    params.delete('albums');
    const remaining = params.toString();
    const canonicalUrl = '/photos' + (remaining ? '?' + remaining : '') + (window.location.hash || '');
    if (window.location.pathname !== '/photos' || (remaining ? '?' + remaining : '') !== search) {
      window.history.replaceState(null, '', canonicalUrl);
    }
  }
}

export function resolveCurrentView() {
  if (typeof window === 'undefined' || !window.location) return 'videos';
  const pathname = (window.location.pathname || '').toLowerCase().replace(/\/+$/, '');
  if (['/photos', '/photo', '/p'].includes(pathname)) {
    return 'photos';
  }
  const params = new URLSearchParams(window.location.search || '');
  const viewParam = (params.get('view') || '').toLowerCase();
  if (['photos', 'photo'].includes(viewParam)) {
    return 'photos';
  }
  if (params.get('photos') === '1' || params.get('photos') === 'true') {
    return 'photos';
  }
  return 'videos';
}

export let currentView = resolveCurrentView();

// Immediately sanitize address bar URL if entered via short path or query string
sanitizeViewUrl();

// Sticky mode tracking for headless and embedded states
let _isStickyHeadless = false;
let _isStickyEmbedded = false;

// Synchronize early embed and headless body classes
if (typeof document !== 'undefined') {
  if (isHeadlessMode()) {
    document.documentElement.classList.add('headless-mode');
    if (document.body) document.body.classList.add('headless-mode');
    else document.addEventListener('DOMContentLoaded', () => document.body && document.body.classList.add('headless-mode'));
  }
  if (isEmbeddedMode()) {
    document.documentElement.classList.add('is-embedded');
    if (document.body) document.body.classList.add('is-embedded');
    else document.addEventListener('DOMContentLoaded', () => document.body && document.body.classList.add('is-embedded'));
  }
}

const currentPath = typeof window !== 'undefined' && window.location
  ? window.location.pathname.toLowerCase().replace(/\/+$/, '')
  : '';
if (currentPath && SHORTCUT_ROUTES[currentPath]) {
  window.location.replace(SHORTCUT_ROUTES[currentPath]);
}

// ============================================================================
// 2. YouTube Action Video Playlist Catalog (Synced Index)
// ============================================================================
export const PLAYLIST_VIDEOS = [
  { videoId: 'oMTcUL_OKnU', title: 'Music Boonton Coffee Ride IHEFC   Monster Hill' },
  { videoId: '9pbxpeXNbO8', title: 'Music Boonton Coffee Ride IHEFC  Boonton Coffee Co' },
  { videoId: 'zu5mRU9_Wts', title: 'Boonton Coffee - the descent - 20260927.1' },
  { videoId: 'PJfHcAtNMxo', title: 'btcnj - Boonton - 20260927 0' },
  { videoId: '_eZWEfgfoKc', title: 'btcnj - Boonton - 20260927 1' },
  { videoId: 'HlajY9-UeNc', title: 'btcnj - cookie monster 20260924' },
  { videoId: 'hFGiQ3e4k98', title: 'BTCNJ   Tour de Peconic Bay Shelter Island 20260919 01' },
  { videoId: 'uoQdQWfmTkQ', title: 'BTCNJ   Tour de Peconic Bay Shelter Island 20260919 02' },
  { videoId: 'OLdt6-4vOqc', title: 'BTCNJ   Tour de Peconic Bay Shelter Island 20260919 03' },
  { videoId: '9XLklY0orgA', title: 'btcnj - Catskills Bound (alt 01) - 20260912' },
  { videoId: 'kGQ2VAkpsV4', title: 'btcnj - Catskills Bound - 20260912' },
  { videoId: '3znaZsOIHKM', title: 'btcnj - Acme Katonah neutral grounds redux 20260905' },
  { videoId: 'wkht7rswmO0', title: 'BTCNJ   Morris Co  We\'re back 20260815 02', isPortrait: true },
  { videoId: 'UZxjQEmdbIk', title: 'BTCNJ   Morris Co  We\'re back 20260815 01', isPortrait: true },
  { videoId: 'x1NqhiJ314g', title: 'BTCNJ   Morris Co  We\'re back 20260815 03', isPortrait: true },
  { videoId: '5b0ObtTIsgY', title: 'btcnj - Anna\'s Rule #9 Birthday Ride - 20260711', isPortrait: true },
  { videoId: 'OOYwj6bISw8', title: 'btcnj - andrew and gavin heart 20260321' },
  { videoId: 'OzzMSCnEa2c', title: 'btcnj - road signs and cyclists - yir 2025' },
  { videoId: 'bRWNY_ZG6eM', title: "btcnj - Hawk's Nest - The Devil's Ride - 20251101.11" },
  { videoId: 'gqgpWV__3To', title: 'btcnj - Did someone say donuts? - ihefc - 20251018.14' },
  { videoId: 'DV0hVP0kuQM', title: '(ABBREV. PREVIEW - Strong Language) btcnj - Did someone say donuts - ihefc - 20251018.0.abbrev_ughhh' },
  { videoId: '2GsHBw1Aujw', title: "btcnj - Eric's Birthday Ride Around Sundown Wild Forest - IHEFC certified - 20251011.11" },
  { videoId: 'uA11iL0N5ys', title: 'btcnj - IHEFC - promo - 20251009.07' },
  { videoId: 'wGOqoPfOMcw', title: 'btcnj - cookie monster 03' },
  { videoId: '00IqRrn_xYc', title: 'btcnj - cookie monster 01' },
  { videoId: 'K5BI_yZtAJw', title: 'btcnj - Cookie Ride to Parsippany - 20251004.7' },
  { videoId: '2KLzv8XZxdk', title: "btcnj - From NY to CT & Annie's Birthday - 20251005.1" },
  { videoId: 'vBWjhkMT2h4', title: 'btcnj - jersey shores - "drone" cinematics - 20250928.7' },
  { videoId: 'jS6aCtFR8VQ', title: 'btcnj - eric and skeletons - 20250927 0' },
  { videoId: 'ToQsWGci_yA', title: 'btcnj - keith action figure - pink thunder - 20250929' },
  { videoId: 'TBoFTkGJrzs', title: 'btcnj - Celdhan Action Figure - Limited Bachelor Edition - 20250928' },
  { videoId: 'z4k33LyeXZI', title: 'btcnj - mt greylock - the summit - 20250920.0' },
  { videoId: 'Tgj74n-suXg', title: 'btcnj - mt greylock - field of dreams - 20250920.0' },
  { videoId: 'PQ1xJZaARFg', title: 'btcnj   mt greylock   figurines   20250920', isPortrait: true },
  { videoId: '9cSDDkJsNtc', title: 'btcnj   mt greylock   figurines bikes   20250920', isPortrait: true },
  { videoId: 'cQqjEYsNS34', title: 'btcnj - mt greylock figurines 20250921' },
  { videoId: 'AkagOH4PJS0', title: 'btcnj - high point - audio overview - 20250913ao.0' },
  { videoId: 'xzZhiA8wnHg', title: 'vids - The High Point Challenge - 20250913' },
  { videoId: 'HGBvHMmFU74', title: 'btcnj - high point - climbs - 20250913m.2' },
  { videoId: 'XE-DhbwK4Z0', title: 'btcnj - high point - group chilling - 20250913m.1' },
  { videoId: 'MWF7pbLmyVw', title: 'btcnj - high point - fast descent tucks - 20250913m.7' },
  { videoId: '30hAyVnh91U', title: 'btcnj - 90s advertisement 202509' },
  { videoId: '4mVM_CEAOoQ', title: 'btcnj - ILEFC - 20250916' },
  { videoId: '3IBy_4lIJzk', title: 'btcnj  - Arden Valley (music 2) - 20250906s', isPortrait: true },
  { videoId: '_iqCMgryUjo', title: 'btcnj - Arden Valley (music) - 20250906' },
  { videoId: 'fa_sqH5w2KM', title: 'btcnj - Arden Valley 5.2 - 20250906' },
  { videoId: 'P66cGGvmTls', title: 'btcnj - Arden Valley 6 - overview - 20250906' },
  { videoId: 'h9LeqN037Wg', title: 'btcnj - cows (on left) blue sky (on top) buddies (on right) - 20250830' },
  { videoId: 'WRMATMtoKkQ', title: 'btcnj   cows   descent   20250830b' }
];

// Offline Fallback Album Rides
export const FALLBACK_ALBUM_RIDES = [
  {
    rideId: '38132',
    title: 'Boonton Coffee Ride',
    dateIso: '2026-10-03',
    dateStr: '2026-10-03',
    time24: '08:15',
    timeRaw: '8:15a',
    startTime: '08:15',
    meetTime: '8:15 AM',
    leaders: ['Eric Schmidt'],
    rwgpsUrl: 'https://ridewithgps.com/routes/57395260',
    miles: 62.0,
    climbFt: 3600,
    category: 'B*',
    photoAlbumUrl: 'https://photos.app.goo.gl/bjaGrY2RnLfWvKpH8',
    photoCount: 1,
    coverPhotoUrl: 'https://lh3.googleusercontent.com/pw/AP1GczOYdj4pEEzbxdfb7GRa1w55kTKYCWN6vHgY7Doyfx0QzU12CEozEURT5S-PMynPzUS4ui2sCA6imwfXmZ3J_Dwt0RxdWAN9DI0-9T5f1ho2aJDr-aiq=w600-h315-p-k'
  },
  {
    rideId: '38060',
    title: 'Cookie Ride to Titanic',
    dateIso: '2026-10-17',
    dateStr: '2026-10-17',
    leaders: ['Gene Sullivan', 'Keith Schlanger'],
    rwgpsUrl: 'https://ridewithgps.com/routes/57244927',
    miles: 51.0,
    climbFt: 2930,
    category: 'B*',
    photoAlbumUrl: 'https://photos.app.goo.gl/99Q5hZtNTMH8oGmf7',
    photoCount: 6,
    coverPhotoUrl: 'https://lh3.googleusercontent.com/pw/AP1GczP2hF8HQTNssv6KHgA-lwDRVESO1z5RV5rRs86lOF1Tu9WMfFo6fXdWyAHcAkNVfQZm1q-oNoUR7ATn1FI2Qun5tideFdbAI0lYKH9XWWHugIYkjgMy=w600-h315-p-k'
  },
  {
    rideId: '37622',
    title: 'Tour de Peconic Bay / Shelter Island',
    dateIso: '2026-09-19',
    dateStr: '2026-09-19',
    leaders: ['Gene Sullivan', 'Anna Bielen'],
    rwgpsUrl: 'https://ridewithgps.com/routes/56389470',
    miles: 64.2,
    climbFt: 1420,
    category: 'B+',
    photoAlbumUrl: 'https://photos.app.goo.gl/bd3EPHLZYcW1xVrq5',
    photoCount: 95,
    coverPhotoUrl: 'https://lh3.googleusercontent.com/pw/AP1GczNA5F5FMOqXxk43GINsCO5xLFjw96adErWgu3w33-VmUjnLRFrY7rrdh6BqQCdO7hS-eUs8qhHG0rboTJdDbjzF6EHc5H5eL-LspJPqctXivv8qnPOk=w600-h315-p-k'
  },
  {
    rideId: '37590',
    title: 'Catskills Bound',
    dateIso: '2026-09-12',
    dateStr: '2026-09-12',
    leaders: ['Eric Schmidt', 'Anna Bielen', 'Howard Goldman'],
    rwgpsUrl: 'https://ridewithgps.com/routes/57081087',
    miles: 58.4,
    climbFt: 4120,
    category: 'B+',
    photoAlbumUrl: 'https://photos.app.goo.gl/3weqKMvLr7U593X16',
    photoCount: 59,
    coverPhotoUrl: 'https://lh3.googleusercontent.com/pw/AP1GczOxsCq3H4RhzJ7JNvBTYqNctalibdpksOvDYPg9SU0VU4x7pjSomLz7cTAPxeGDWuoksRW6XKQ34LQR9q6_A2Zdzk3bcH9_uDPdmnQRlM51UvrOhqWI=w600-h315-p-k'
  },
  {
    rideId: '37575',
    title: 'Acme Katonah neutral grounds redux',
    dateIso: '2026-09-05',
    dateStr: '2026-09-05',
    leaders: ['Dan Lipshitz', 'Annie Chen'],
    rwgpsUrl: 'https://ridewithgps.com/routes/56214002',
    miles: 51.0,
    climbFt: 2950,
    category: 'B',
    photoAlbumUrl: 'https://photos.app.goo.gl/7wmNLLxgYa4gAqm1A',
    photoCount: 17,
    coverPhotoUrl: 'https://lh3.googleusercontent.com/pw/AP1GczNvtBOchnu_gh_ol5aCj4tcfPJY3T2lfpRAg2OXrxtDn9h4AcojKUZgzFmoobx_3ZGzHq86SrhCp8xO4cC6VPNQkTwHCzmInssl2TdAgXi0Yow9MxIA=w600-h315-p-k'
  },
  {
    rideId: '37560',
    title: 'Ramapo Rally Metric Ride',
    dateIso: '2026-08-30',
    dateStr: '2026-08-30',
    leaders: ['Keith Schlanger', 'Gene Sullivan', 'Eric Schmidt', 'Gavin Chow'],
    rwgpsUrl: 'https://ridewithgps.com/routes/56150201',
    miles: 62.5,
    climbFt: 3180,
    category: 'B+',
    photoAlbumUrl: 'https://photos.app.goo.gl/crWoj2atwxfJjgxh9',
    photoCount: 25,
    coverPhotoUrl: 'https://lh3.googleusercontent.com/pw/AP1GczMfNNJ9eaghe7AmcRSm8SMNV92U2CM9qOjLm7WlB8tBOGI7cPjZGlsQbaBY9t2Ge_s6gUcKEHanwq5lyifw7fumCiiGH8sDajQp8NfCI3gxGIRwHea4=w600-h315-p-k'
  },
  {
    rideId: '37542',
    title: 'Morris Co. We\'re back!',
    dateIso: '2026-08-15',
    dateStr: '2026-08-15',
    leaders: ['Eric Schmidt', 'Gene Sullivan', 'Gavin Chow'],
    rwgpsUrl: 'https://ridewithgps.com/routes/56023190',
    miles: 54.8,
    climbFt: 3340,
    category: 'B+',
    photoAlbumUrl: 'https://photos.app.goo.gl/oKyCgjz3GPVG8wfHA',
    photoCount: 9,
    coverPhotoUrl: 'https://lh3.googleusercontent.com/pw/AP1GczOQq61SPZSJU4kLFFPZUYHK-G2-VwJVnjChBY-p4xO41rtb-_ropyJGKmExEeMH2XiwMZNTgKDstN5Xj63-TuKc4wSWsBAtCe393_KNQFKb5l5HgpZ4=w600-h315-p-k'
  }
];

// Verified Google Photos Album Metadata Pre-Index (URL -> { photoCount, coverPhotoUrl, dateIso })
export const KNOWN_ALBUMS_MAP = {
  'https://photos.app.goo.gl/99Q5hZtNTMH8oGmf7': {
    rideId: '38060',
    title: "Cookie Ride to Titanic",
    dateIso: '2026-10-17',
    photoCount: 6,
    coverPhotoUrl: 'https://lh3.googleusercontent.com/pw/AP1GczP2hF8HQTNssv6KHgA-lwDRVESO1z5RV5rRs86lOF1Tu9WMfFo6fXdWyAHcAkNVfQZm1q-oNoUR7ATn1FI2Qun5tideFdbAI0lYKH9XWWHugIYkjgMy=w600-h315-p-k',
    photos: [
      'https://lh3.googleusercontent.com/pw/AP1GczP2hF8HQTNssv6KHgA-lwDRVESO1z5RV5rRs86lOF1Tu9WMfFo6fXdWyAHcAkNVfQZm1q-oNoUR7ATn1FI2Qun5tideFdbAI0lYKH9XWWHugIYkjgMy',
      'https://lh3.googleusercontent.com/pw/AP1GczPN5ieFIP9w_M3v14xgwMcU3LMEyaEXsSZ2sDBiyXmyxULRkxlA0KKIXk2BJDA370EBLN056p8mZvWloswC1XA6F-v7OH7sP84vNsgGB0htr9qy_pbI',
      'https://lh3.googleusercontent.com/pw/AP1GczNu-tc0vB9j8b1vFs15FcOD_tYmkjPkHFcKf1hhe92eGAUKjvlKuGVw3ZSxMZNqGBsZFYEZSzh-NLQG4FqtGY6150W2gq6gM-Ll-z_fr1XZd-36_tMp',
      'https://lh3.googleusercontent.com/pw/AP1GczPEMn0Hl3fX0SlhnvFa2bjDfHZvsSe_LTLgKqhGXdWptRDlvC3rLJW02t1bQVRzHTqfQeHRIlj7bnHJzqXMfn4IIgnLvrKfe4O7eAJu-hXH34WTW37o',
      'https://lh3.googleusercontent.com/pw/AP1GczMSD8IiM-s99sRAJ2ipqNxaGbtw0XiRsi_DawIXm-DnxgwDiHcRDTz_j6Doaf_7UZ8Vlvyi3LTGagit12q59TWAte0G_fwqAKlIRHUq6v0eNsRs-Xwm',
      'https://lh3.googleusercontent.com/pw/AP1GczOLDB5IC4GkO4c_BvljKuz0JiwNt_4lPLnW9LeXTFYCGREAnQ5WQcYMvBJBSlPeVac6sUJ6INwj9ONpU8o9u_7kNhB0Y0a3JIxMigRIl_Qfc3pJRSs'
    ]
  },
  'https://photos.app.goo.gl/bd3EPHLZYc4EfwfZ9': {
    rideId: '37960',
    title: "Tour de Peconic Bay / Shelter Island",
    dateIso: '2026-09-19',
    photoCount: 95,
    coverPhotoUrl: 'https://lh3.googleusercontent.com/pw/AP1GczNA5F5FMOqXxk43GINsCO5xLFjw96adErWgu3w33-VmUjnLRFrY7rrdh6BqQCdO7hS-eUs8qhHG0rboTJdDbjzF6EHc5H5eL-LspJPqctXivv8qnPOk=w600-h315-p-k',
    photos: [
      'https://lh3.googleusercontent.com/pw/AP1GczNA5F5FMOqXxk43GINsCO5xLFjw96adErWgu3w33-VmUjnLRFrY7rrdh6BqQCdO7hS-eUs8qhHG0rboTJdDbjzF6EHc5H5eL-LspJPqctXivv8qnPOk',
      'https://lh3.googleusercontent.com/pw/AP1GczNl4_AdynZjuBFfOyzWX1xoCd8k88a9B5yyldtWqZn6xSdTAMw6d6SRfXmtPEun9UCdWF2v-TptIY8ryuCDyBVNdG-NP_l7UUN3ipkKjSQgikyxVjcn',
      'https://lh3.googleusercontent.com/pw/AP1GczMI7sfQftpoGYPyGigCROHC8F3ox0BT7PU4ZYuucJ8AiZ_zCKGs0c04MQxFcP7MGPF6XZh9t4BL9pbE5i4giJronwvrjlL06kxpsUGtnYborcBT41-z',
      'https://lh3.googleusercontent.com/pw/AP1GczN7-Z6Tpq4PWpTrcT2jkDLg7CSi5YbfLYqZqCsfZSJKE52CkqwWXUl-r34pmCKJc155W44QaolZeHQnqqLW8lullWOXukpm-awCUBqCe7ejHzR0twMj',
      'https://lh3.googleusercontent.com/pw/AP1GczPO-manMT4BXfGxKz-JUGP0uI2uoTus119EmhEYip4LeDbTKHYpYffp0Ds78R8T_aGDx0Wnq8-Yf05KFkXGi-QQxDtWhWeqU6tg2KPYMjZ91IkTAtcV',
      'https://lh3.googleusercontent.com/pw/AP1GczNYTLlXMf_DIsTeCIzn2iBGGIcs-u8vfuUfuX4f355d48gqWl48iXpNIBupoleDVwmlZSOGlHDy6x-aUKJ922WFSrH93th-B_JsN4KaxZweXzg-ttFm',
      'https://lh3.googleusercontent.com/pw/AP1GczNZGE10vJbsQ0iXIQ3YEUe33SwUEnoWdzvQeypoCkWXQhbTpQCE2w46lV5AiBwGLypbibBN1GUrmJSgNJuuSOInJRHFi5qHC6MzjMtj6grKTQjm9AKC',
      'https://lh3.googleusercontent.com/pw/AP1GczP3tgl-XrBDNyXHSGzaX4lqutvvRP1FEdIGki-Dl0MC1c7fo3B0jITaxp2nIVKAtsd-h4KrObc3jCPlAwK56RvyEufOnGlCckGUy2U1Wm7syzcyWoMx',
      'https://lh3.googleusercontent.com/pw/AP1GczOVsh84I2bjbf3QpJFd24a8L1-CDqoDk4mmiD8SC-DnQ1OY_QRZ64fMAgZQEegXJZcX7VqIjnfLIwvIhywCEMrnzNFozo8_hBlKNyYX90rniLKxeMzq',
      'https://lh3.googleusercontent.com/pw/AP1GczOWpGL5oHpZPF2FrFB9Jgmx3f0HNQN-hlLE4Wz4Mcb3M9oYWcz7rvr1YDpvrb0nprgA5kIWao05LgcPLpJ7ZkzkT-OkxTM9egZWjfBa11rDrv0WLQvC',
      'https://lh3.googleusercontent.com/pw/AP1GczNvCgVriMfbwaa3dTR2IgGox1XcDNa2e21PG9mC-IXH8WKiwrI3cTjKGGGyYT4Bm8SfIjtw26YlsLyYYicbt8ypKnliRNobmLzbc15mQqvLUfYC1hXg',
      'https://lh3.googleusercontent.com/pw/AP1GczPQsMtiU-8_yFnmEHHmKFw4vRal79H0CacuQmt074ITu5v2R0g1qRQqC8ebSQT9BpJW0FNRdJQC2ybDEfbHWb0frsuyx1d-tBm_G9rnCnCgwhl0n3GC',
      'https://lh3.googleusercontent.com/pw/AP1GczM2RU8ZOXAp7TswWGJfkhuhlTUm4ZGR3xgfnBf8pjHLm8NzzSaL0KiTRzloHUc2XUXQGd2gBytAbXPV7fso2GJv1GAiupXYMo-KqFva03T4dpyx_zkQ',
      'https://lh3.googleusercontent.com/pw/AP1GczOC0ytVtesJMHhYKGCjf-ETM_Ckn8t5UOJOsFaebeuKKeFpxPD9hl4k5HOOBSJ7yBxyt0rBXiKTaGF5HiYcHHJk2uGIUPGfMKsM87itccT2wFRSDjAU',
      'https://lh3.googleusercontent.com/pw/AP1GczOg-U_cc1h23aNVLC06_f51UNIubIQRYE8h91AfBFQddSsZx-Rt98ywuOTdE9RUvQ2rLXoekoaxxX-yb3GvXj3iOMVGrHnNmpinFA0uK4MC4CGekKPG',
      'https://lh3.googleusercontent.com/pw/AP1GczNqmJMNsYtkdh-PCziITInn-Bs8fIuTh3tY-M4Z0UosdabxDwwBOVqNDQ_agaSxB3-UiSQcLrOf15ZQ-rFFgDT_YVl4bs8EDVDakY0VHyJqmqvmKiTG',
      'https://lh3.googleusercontent.com/pw/AP1GczNdUMcxQ0Ys1_JgrK75LGa0P9AKjZLI_fw41cRam24yGnujYT2kAaFQbt7zuSV_lj6kRMkehFT_a2XMVaUSk6cODhlwNOlt32gAjb_oHR2Xx9Wo9Q',
      'https://lh3.googleusercontent.com/pw/AP1GczNgd3OplUY5r4_NseKsSW1RRpOQzqJCh5NRpKgDcqku5kZDYGY3-qP34_zkbwAKSAQbcoAashNs7fXSsAougiXVo8pSlgHbpgomK4aGr57dgcGSkedd',
      'https://lh3.googleusercontent.com/pw/AP1GczN35zzyFYYyZkLyxeKdj-0wlram2n-zVPkwTEg0iULVaVFuV37O12nKLcfjiouX8VI2ogznWS64txgtGdexzlaxyGBxLiKxAALw8r586RJEsFZsFpRe',
      'https://lh3.googleusercontent.com/pw/AP1GczOxzIcQbWbV2IKsDbrt1ONAnsdA3JbYRcDa50eYs2quneLEEbXLRVzh9C8RM2emRqFo2xy4Fc_Pr33IpR5wrpYoiMtq4u88mhm4NtdioJkKJ_zdPe8',
      'https://lh3.googleusercontent.com/pw/AP1GczOJNWpZUiI6EqnsLfrrZzazL_cRqvyT60dbNOiceyaqz3upbdaFrOXIgueTQFjaDMr6FlnZok6rLlMLT2iKWt8KyDeLN5PMC-Esm_uuFgm05EEeTXQE',
      'https://lh3.googleusercontent.com/pw/AP1GczMp5EyaGsu4qgCLcZktKBtZn7hpt9MSSEzijcJ2VNkjgZ0S_F4twN8yk9Bg7Dz_WNeGWqVOZpr6EzS9HUTvw6wGUSHa_PDmaaY6PJpeM6nZK3IctDJv',
      'https://lh3.googleusercontent.com/pw/AP1GczOeMQIOTKe11_Cz7_kG4dTtrlpEr093lr_5zRg0I9kFszmX8ER-Dc5FiG4fplh5MllDrg4ItGX12HaFksXsqqikpFF0sVOyqlkvNtnnBJOKPKg19Ml9',
      'https://lh3.googleusercontent.com/pw/AP1GczMapWWdwp13VdIXFDhvt9Ujo-R6OrKfJOjcUKz7lhC0ihsOLLQsNHOvtV7RfQ6kn02HLdP3uLl8EMxG0aTGvPXk87njPbiPDWwzKfbpXlWvbgPCkWo',
      'https://lh3.googleusercontent.com/pw/AP1GczOc-lBURXggmdO-mutoStTfQr-Upo1Ky6O31MbO0_qDvHrQaulI9NPWPdw-9yg0Xd5qHQukpP2nFoEn7dWZ6o3SyLn4eloe-sK3Jb-JRIwgphPMMxOb',
      'https://lh3.googleusercontent.com/pw/AP1GczPQSZeTVgQDHs1lkB0dn8V9Jis6K4_OdDqyJRwU6Mq1Xi_GVOJB-C-77nagvWxVc5firWlxrUNruV_qvonSN42PHKi4w9yk8quuuGrhTOudyS5EMvXj',
      'https://lh3.googleusercontent.com/pw/AP1GczMp6veMEh1PmVoDoc_P3qLFDbgOwxsA0DLVzte3N3CSf2ISX8im94gJHnrTLoXECiCZaihEdrLm3myxcA1rHBoChT2LgYzOQFiCguTEXrpuT36T48i8',
      'https://lh3.googleusercontent.com/pw/AP1GczMvjxp_RgC5XQ3_7E482fWEtH8kRz-Q0cQUbaEBtwHjk7Mh3PA3MP56q6hi9aC53Lti_1f-724rFMvRcIHgb44YhL_sJ2zj71IxrvhhUvtjzpbIZAei',
      'https://lh3.googleusercontent.com/pw/AP1GczMZo0r7PUwDxFfz3fa4kCmIAMqaA1Kmw8aX_iDHlt0Sy8svEnEXfmhy_FGlRrIEnaxfpk-h9YByl3L-myCVEitlJk07OF6Zo30iDVE8-jSXJ2WJdocu',
      'https://lh3.googleusercontent.com/pw/AP1GczMz-Q3yon_QC9tGeUM9o0R1_2m1l5IrpFb1cCFrc9PhiH1uE6CZeD65wvjQ7xLOqKl9cDZrrLfGZyRMbW3ruOT6B8y8XzGDIr9rR6LkZZ_qsH49Yg',
      'https://lh3.googleusercontent.com/pw/AP1GczP9IFwT70o4A83qzT9IBFh-RtQFGfGiioxR_QDx6TVXTT9GGJi0IAR8fL9l-7cPps9T_74G0HPYvxuPUFtzSIdqrZaYljE5F02exkXCsEb-kM6ntTeZ',
      'https://lh3.googleusercontent.com/pw/AP1GczO2zJWwC18YAapZCFF4z5jparIGIwKU245nARhkC6HAAh2WHh1dWKpp2L6WSpEhSdsD_alxfY4JKrRyQokgFKsQG0gb_R8uHPQlv522PX2RSI8-haEt',
      'https://lh3.googleusercontent.com/pw/AP1GczN-9H0FwXpiEk7EL4oa7fP81Ku50gou13FwPmbLDbs7L5vRqNfv84jmoTSVY9E2wyyfsIPQIzwcrhnDO2uHRKLopmDGZGKzzqHgCqUDmFrruRPz-Gez',
      'https://lh3.googleusercontent.com/pw/AP1GczO2Ds61iImDMVIZHoq27wuxEb_MTQQZq-QRMrk5l5S7NOHLBoC6J4fYgtnK8Gu3e8bpu2TNYfbhW_m8a4K6utzSxDf9ajPdWf6CDGElh40F4wSUDPoK',
      'https://lh3.googleusercontent.com/pw/AP1GczO68d32yPIBGsLDvh_jMj4F_1Cfvxs3j3X-okLMQBmqMakEYob0wCPbXqCFuf90lCqtYVy3ciL5UMwvxwJWeAPKTC-hQTshs2t-GiR1-ey3ckK19rmv',
      'https://lh3.googleusercontent.com/pw/AP1GczOn45j9fao5H-SM2emhqpwc59la5Q21XdZBi60adbn2lQc4hYQhfxj1M1-g7qdb-2_GLFVpXLFYzWE3ygnSmak_pgMQBwhWEK6asnBzuQKXFnNLt1Oh',
      'https://lh3.googleusercontent.com/pw/AP1GczPy9-RiRbApERA-gRuwu7QQkIx3qdhCLH1McyreukVm1qm8mtpruKkTQICfMX2Wss4fnDi_UUkTTvhj3mYJxQzILp_BhfdQizm4XE1WXkRkV88x2Krc',
      'https://lh3.googleusercontent.com/pw/AP1GczNjOS-edsagEdp66bQZtu516AtIeZV2ABIVInZuq6a72YIdnjqrtI8rsIdR1fx1Lno9e3qCYIq6LTQ5yuCXGRUFvVYh88qaJ3izLVrL5L7prGz8qhFE',
      'https://lh3.googleusercontent.com/pw/AP1GczNCqPguIQ9n34pc2GLig14O375t9dtfsu6t26pDifTHU2Mtgmf0mLZdB3EuVHmyNzJU80j7ihv5VZb4EUYYo7Q1g1HDH6i7ec-VoU4ZsN3PMKKEfTzp',
      'https://lh3.googleusercontent.com/pw/AP1GczPQDRsD6PWzPS6Et5EkkS_5V_cyWWfF5E4nf0H387iF8S2eXOObHv5ue6CGmJ5ZHdyXmh0oFRqRjXRBBQrSF4zNl9zSLyAKjtUTJqSN3MScMF6jua-3',
      'https://lh3.googleusercontent.com/pw/AP1GczM61SQGiKJ4lf02nqeoy_EStsCnrqcuBrWgEKrODaV_fGRbGGAfFOftH_lVlbLWX5-EqowybkuVf2rjx8c2UWMQzNAoqhITePbVi1-CZrfVPaLwtuBf',
      'https://lh3.googleusercontent.com/pw/AP1GczP9llyY_wvfxSdy-eiY6KuRm0d6q3X5RWiQ7WMLzObLVO_HkPyYJGrah4ed5utEL6NRnjx7J-V9O93XokLG4DCEQFDiyRuMPKHUTYvbiMOepjhueA',
      'https://lh3.googleusercontent.com/pw/AP1GczNKmeeqzWPyKprHMglxENy8hXHxqZDu-q1nyDARBq_384QnZR2psJnms55ksOqGpFfplCeMLYPWBdakddaaKRkWm9t3vI6OYHhpwslSWHbQdMByVPg5',
      'https://lh3.googleusercontent.com/pw/AP1GczMe_CqxXCYDYn0EFQmq8tJ5zdoHg_sldlzPzib4rIGZKFFJODtRlwvnsqCPpAbhpjzsHQH9TgjO1RaAR1PCbLrx55CCWeXwyg9gjjrJInoWst6op70M',
      'https://lh3.googleusercontent.com/pw/AP1GczMkwnNdKECG3TnSmgaL2iryitGA7UBR7w3nqK9OrOEg9xz8bLFSINnoXkqCz4P3eP4sj9QvhKFpFs7_YeGZF4AQRSaWIl0r2h5hm8qLvugk-YDLp4hI',
      'https://lh3.googleusercontent.com/pw/AP1GczPcUbfpvZsqvkOfu3adefYVd1NGBqUwe8yHoOq7X-0r9OcohcdurmAINS2yF62WZY8vnA2SYDu8ztJbvD-n_qBpEfCuvzfmBVq_GuYDUkT-Ej11VtLe',
      'https://lh3.googleusercontent.com/pw/AP1GczNnSRHtfAFMT6dJpsrkEce0aLg8QGk8h93gaSck5r_B2xxXGB3xvFYy9S-VYc5LUm9JCv9rKZbLZXOdPwUJbEnS6miwvFJA6KLwvVH8QnBzNHM2sgxg',
      'https://lh3.googleusercontent.com/pw/AP1GczPBOhZ-ii0MLvUcYzsUcdIc1WgLAK0cfSz3-Soh3AidBUNHkHFzWnFiGJoCIFqbg8y5LosRi6ANZvt_CyCc4e_o1vs3PqHQzB7-PaVCnySdDMm5CQ',
      'https://lh3.googleusercontent.com/pw/AP1GczMPBpXMQHhGBQ3gI3FjOW5-zxi2fvpnHtPRKoZ_W0U_IN8oCaA9PQ-7WKxq8JN15kEAXwx36gSVrdBOXX9LUxrQETgfXKLNPh9wmPjyvQHtM5diBrOw',
      'https://lh3.googleusercontent.com/pw/AP1GczMrq6pywTDb60cLVbwSmSEAShLQiyNkHaIazV2MlbMaHi-AJ7FPT221MQLQ4SWC2QQFMexL8hAs7hTHbK2OmytwXzIyhPV13MKIF1hklmb22R8t7ucL',
      'https://lh3.googleusercontent.com/pw/AP1GczNN6cN9vt2vzXVWdkA_z7c0MpBZLXwyUCAEUYnfHwEaz0PW-RDeX22F48L78Ja6n0DcHxkbyYBc7bGsFtM4iY1d77R32PEG6lSDZQoDEHiaLP_NaQvZ',
      'https://lh3.googleusercontent.com/pw/AP1GczN2UTcAeersE9mHZJR3R3dJewrqPZtqg_MUegjFHu3t7aQvmlXK26fdgT0pcld0NooSm75O6yNDYJG5sZwYKuB7xmMV04qq5ofDJcvCGJpQVCViF_6P',
      'https://lh3.googleusercontent.com/pw/AP1GczPaPNrtGEQembmPRZbl08K2LfgA_eR4bz6VZuAzMyKY1qqbDzviHpesFUvm0ttrQ3vlWaVYK5JGqKmhUDtMnvzMWBA33gW99gayI8urgziBz9xcNIj6',
      'https://lh3.googleusercontent.com/pw/AP1GczN4fZMZEy6sOCLd1HX-pzzMjmrD6wc5to2iJAtTMCsq_Y_Eu9emJtuVecex2TeaDRDaK4i0PpNu3ZCZ1Dand0u-lb00VZgj0dFWS9-YSxaE1KTrqowY',
      'https://lh3.googleusercontent.com/pw/AP1GczMOAvyQhBICqtSBpGdkj1cb7oi7gzuLxd9RrFgqFC4rWvkxRfUZabFY-yKeoe-lrQUsYeVPfK5ejayBnoF3KQKhz5xC_u3meSa1k5rqOnz3grzmdaZg',
      'https://lh3.googleusercontent.com/pw/AP1GczM0JnpyKaCKNegv8xnPCzIJgu8bB82qyNBJVAInkvu8qoHSQRLkODL7fka5G77oBuI_myKlVuhT9Otih1TbdXWDDW79MdHEQdn06FRRMAuphCRW8NCf',
      'https://lh3.googleusercontent.com/pw/AP1GczPxYBz7jkGeDwX11mqabuhVdz5QIZ_m3OHvRRTT-9RblqdE661VsL7o9Va95UFS_z2L82XBSSn66BLNEn_n_ss9fwiRcXNz9eswFZ_4FJR5d0Z3QZJp',
      'https://lh3.googleusercontent.com/pw/AP1GczOcGDb3JVUkLssam84SGyIzRNjMILUVN03sb7BblWDJHsV4Ss2dlQ4Y_Lo8qfn1wdeiH2J0xTsvHLAhdP1ci3aPKUV8uftDpb0-v5dCQAhmoV9aGJ3O',
      'https://lh3.googleusercontent.com/pw/AP1GczO40PSEZbN65BYO2iPV6A5X8ttDACUpKiJCS4mU2HNuhN_wKXPAhC-sxbZSb9poN_9qs_E2EpbBS1nUTmoyG-KlJpaoVqIiPj1_SDcemn3r3r-tpzAH',
      'https://lh3.googleusercontent.com/pw/AP1GczMp1mux6XXJN-rxSjc2lY8QzIoHUa8gtU8275KU-rr7hYQ7MZ6VXGywvleET90K0dZZ15nholK-oadSnXJtXve6c6qNHpueQfVJtk_xWXDNxVC5zApW',
      'https://lh3.googleusercontent.com/pw/AP1GczNKOfedtGqrXQZNpof3K5wevlOwD-X0kQdy5OC99oxUOMy5UQMzJ6GQJRLBxue2Jx418NSFn3Jxvh9M-PI83wTsE-OmlyhlllrJvi2k4Ep_lsqUzQDg',
      'https://lh3.googleusercontent.com/pw/AP1GczOrpJbDtwuI9TcHNNbar6ChWgZOFo2nM4ETPeToaUfSwIeU_YOacLM3F_03kVkiIUr3WfKF1_AbSlzlhj0JZBSNp5k-iGYtningzMglzGyTTVjsovjF',
      'https://lh3.googleusercontent.com/pw/AP1GczMBKrPNvbPcxfNQ_EKcclAAUtCR7zKvLC_VA-lqdW2CBt3wW5apdCEyZcfjnm9A6NXLOuqzZxOapDDpxA6X1yfcRftB4eMLtsULIxx1JnFKAVAehWff',
      'https://lh3.googleusercontent.com/pw/AP1GczNIaGJeLQaqvzfVT8khg6dCXzbacKdmXvxigQYJmCI0Qis6wUfzDyQ0Wv1TRNBG-nEZlvVQVzuAZunYN7paPnFhDFBDSyfotktufB0HCdIUZ1UAdxRp',
      'https://lh3.googleusercontent.com/pw/AP1GczNvo9S1ip3YXQpumjU1BS7hgI-wn0rePyiHWxkkbQvuFB_F7UJrcwPJFQbtqdTNpjRU53wKYpNn49reMbe679oEKdA--S_uVSiYW-F-fSZ_nFBqISNj',
      'https://lh3.googleusercontent.com/pw/AP1GczM6CXf67xrYSewjpwaOuaPyJ0LOo9MgXPmRIPB6HXTK3vgrzSba4CCb7XkYr6O5vOgLMnDrgSR_boCD20mffbyuAE9wPKSsGHaSf8cj8ElZ90tgEwu6',
      'https://lh3.googleusercontent.com/pw/AP1GczPE6IFGVpFZeXSc_ubmd2chLKTl-G3JZwZq_pZQaUPflCCNTIZKPYBi9PTG2SPWy4YlOuEbblQrQV4zN3AO0VE2lFiaSyn8VqaueflcJVofbbZiHnYl',
      'https://lh3.googleusercontent.com/pw/AP1GczPp7JiT6L6_-vXXmE-Jlz48th0Q2T-rcjN40RrP0P_sj-LDYR-Zq0JBOAbzg2B0TBtg5ePXgR7t3osCBd4huZhEy0qnFB1-ziSY_QhqOsPk72pyKBAa',
      'https://lh3.googleusercontent.com/pw/AP1GczN1_P1gbtXV60jc0Fn0RqLJVFy3iwc52XjhmiZl-XJAFP1uBdAB4hmdgDyXAL6hzIwCEU0jZ4vZuEPfAP9gRwI8HgeCf4eytiuF7Dvl0aT9v3Ks7ldH',
      'https://lh3.googleusercontent.com/pw/AP1GczO1GpsnrbBpiLFG8bjlkI9ULAlg1hTGDPHbQghdRWfm5Z3EMwdoDQXePxPmxAbpZaX489b0HBQ9KkTaLbnypxbYWjnA0fB4NBO7v8WjwZgCyyQrL07n',
      'https://lh3.googleusercontent.com/pw/AP1GczPL7u79bi2ummzz95yNPiTzMw7etrj_uNlCmLkepuGpjnp8L7iUGtsMlZ3SBnyZjnAL32D5hkkgkGDSxJV6y_zyOOQZvTeqnkPSH9OILxlZIYyh3uIY',
      'https://lh3.googleusercontent.com/pw/AP1GczOgcpT6_7YwnpfbEQYroOTGcMnmBBeJgpX58-PcQ6hpQ9kGHAVW0MWgMhj_Q3OCXQ5X_hRuZUEu4rRjJ35bibshUt4_InctB32uA0YEKPSJvM09pdYT',
      'https://lh3.googleusercontent.com/pw/AP1GczPhrG3e439rgb4T1e0yG1S4ipI29uko3Y_HF8oJ4lFq3Usmg_OEB1xw6NwAKbmnm8pAz6hF0BKs_hVwZRLieFVhblUcijm8WN0S6eoOIW9Z12rHWKU',
      'https://lh3.googleusercontent.com/pw/AP1GczPHA5d5w8ThgqDPsQnnZ2X6rLUl6oyMZWHUjmA_s_NfdMaII-DRHPUOGBh0qojJcpTLXr3U6nudUVlWdNVEYj0Z-tYYlw2pzervrck5mcoIsH--BjI',
      'https://lh3.googleusercontent.com/pw/AP1GczNGDDcYoLcih370pGe8e5XfXpKyYCIPDXQ5gRFCSLS4q7-GiIV_zaGDXRdrAirLpIrVP0o4RlmwOFRVxvlgm8TG9yv1Idf4iLroiwSzPbqCC2MCjPE',
      'https://lh3.googleusercontent.com/pw/AP1GczP1cfizyaGO13TtUuWvzkHPeyyH_MHz3y53kQ1BSRfMuSsjBawjG6vNZk71iLaBzbr-0JO3DDIvljRObf0C4v-iCL2yTaCgdG3gjggg_sK0SP1Val0',
      'https://lh3.googleusercontent.com/pw/AP1GczPrIvTQLg39LWG1JxkzINtrTi6tpZZvQsIDbQSi8uuDajGdDnTwOnFvguO4LOzcgEW0fCPTOEwpXdLJaSx-tVYmYQaz-cN5ZRifvbT1Cje7sSKm358',
      'https://lh3.googleusercontent.com/pw/AP1GczOx3-YaQBsSQayVyxEUBgWnlXMGWNohZ6V4DVSAZf1bPey-TxKxX3PaEJyR_H7tBhYDdtV7skhJ9f74iATLu6Fx3ZC7WOPew3gHMBkNoLbqQs2CF7s',
      'https://lh3.googleusercontent.com/pw/AP1GczMjJW2wgJxPbSPzDF9fxYsNvYMVRBX2k4GT3gorUX7WA-hiDySY6P21V6fpd2zHpj5zESie5KW0AMN3rjSnhKbVnEYiN94nvUFF1GkkFu321rysfKc',
      'https://lh3.googleusercontent.com/pw/AP1GczO7X0Qb4Q3VXB01tgDuk12hSLKIQkJOjvaxjM0lysirjkjvuLxaUmAd-e9uDdz7PQxtKqW8kbXpx1_MP2k_fiJU_05cKoe2WhOrttIw7LM1GyURvmw',
      'https://lh3.googleusercontent.com/pw/AP1GczOYjtiq_DBH__vcg85nhMrVU6afaq0VqgNeNsfzkn_TrUA1tqlZ3fFGG5uy3ji2OaB_EDZZhyfdkwStHHa7_CvZf5uMnQN2C0lyuJRPgLqkAhQPOw',
      'https://lh3.googleusercontent.com/pw/AP1GczOsZOSLR8VA4J8SDFzbj83mJxdIznD3Z0MO8YRdz-OzJvv9V54jiaOkX6rOHmcgAEQc525P8d-0-sWATi-VqeBVLL0K8coW0lhmmWXV5d_pLmpXKOWw',
      'https://lh3.googleusercontent.com/pw/AP1GczM3G6tTqeTKQHSzEetLeqevKg0dQOYBr5JQU4ZhSiRpTFK9hi6uy0wuOoAR51LC1wysDtq-n8kbSp4XNtMv4Yk6bj-qRoKXQykfAzEeigzrzeaATTEF',
      'https://lh3.googleusercontent.com/pw/AP1GczPYbBStwDMpCHsSs1YQ_IC5FVl1r66F7buGYs9YOpX7cdv6_c2wfMILtN8wJBDYZvHyDlxOijXLqvh3svlAL9my1lO53JiN1oAjgKHpCPxFr1aJgw',
      'https://lh3.googleusercontent.com/pw/AP1GczPdaTyQw-O-GKM-xTyz__HLyBXOqNUy8Gcp0pejuV6XN-80lF8qPShrchOpa2R6JvGYLSiibf1qee-R3Bq1Zr7w-uHSbFTWsrlZo8OXhl2nUCu3sw',
      'https://lh3.googleusercontent.com/pw/AP1GczOsjsWkzf46ElCKorUiyI2k0x9FDKonKMEtZNJM64pg93xiP4zeO6KJw3VuzE7cFcZFmt2jU6dK0al3sTbmIhTH043-Ke9wfB0scQ3CbMbvlEZprFzQ',
      'https://lh3.googleusercontent.com/pw/AP1GczOInCUNZQUZzENPbmNf-u5KW324CgEq44JV4DXuxQxKzj2FpNw4lcsQMFB-N5aLApo8pJdPMEaAIR1cMJWQSZX42MGv2Qh025Vd5FJZ8eVSLUes8YYI',
      'https://lh3.googleusercontent.com/pw/AP1GczP_QwLHcpsx-Gz5qOc2qoCYEIAMrZRtlrhmuq5OrBLw5x_263bAbYI1dqxqInNLx8IxTX7TBO_h-oyGG5Aqc4oUTZCn3HbYHaeyLxxSoK-0rkrjfxFi',
      'https://lh3.googleusercontent.com/pw/AP1GczMvfZyf5qFVlE2Mn07F8_zLkWAN__KrhA5Wsgma6j4ywA9lImiiVO0Kj85RG2hTu8UtBy5w47DZHh7DZQu80hyY1bThSm7cspJfrbku7cHb9scV2Xz4',
      'https://lh3.googleusercontent.com/pw/AP1GczM2sbJsJXsrQoN499i2fcvEWklc6kCuI0ozj-kljIz-_M7_zf-kUUmR-PQG4QQnprzZCGWkjz_sSLOT0GcrSFKI_Gjs68_uI0rjri8l4E1YKFQ-2Y6H',
      'https://lh3.googleusercontent.com/pw/AP1GczOMHtSjf8OVic8DxlbhdtMCZgrOEjlIJfh3WT-pvOeb-M-C-pdDZS7NjpR_Bc3xRNokohWQkk0SHeYgU57TN7-rLkwiK3eRU7pOfdEsHmA6QAqFpCnM',
      'https://lh3.googleusercontent.com/pw/AP1GczOjrWemJebexoCtGECdC0fiGCEfZyJtf4p8sBLu98krUgepyalG7x1jVlXRlm5qDVlPOauAlkU_DqISrLvWG_VIXdNC2bB2d4_kEbcGbY_JOv_7HSUw',
      'https://lh3.googleusercontent.com/pw/AP1GczMHP10t2UKKaRDY10hCqfMzcCO-RwYeEGC9Fe6xSFG3eYZWrUFHWLAftZmsfdDbyd-v-FCtd-Bz_JT_E7HCTMMjetp9mSyLTF9bH1wkYktFccMy4WKi',
      'https://lh3.googleusercontent.com/pw/AP1GczPR9NRDVRcXwqhFJMA-TRsMBVQjYuk3tkFMmrt1--4yoIAUck-m7iQS7w6yliCMNcsHFSb7eJsw89Odp6uz4eEQWWQpdTSp4HmcCypmfXkGpbdtf7G9'
    ]
  },
  'https://photos.app.goo.gl/bd3EPHLZYcW1xVrq5': {
    rideId: '37960',
    title: "Tour de Peconic Bay / Shelter Island",
    dateIso: '2026-09-19',
    photoCount: 95,
    coverPhotoUrl: 'https://lh3.googleusercontent.com/pw/AP1GczNA5F5FMOqXxk43GINsCO5xLFjw96adErWgu3w33-VmUjnLRFrY7rrdh6BqQCdO7hS-eUs8qhHG0rboTJdDbjzF6EHc5H5eL-LspJPqctXivv8qnPOk=w600-h315-p-k',
    photos: [
      'https://lh3.googleusercontent.com/pw/AP1GczNA5F5FMOqXxk43GINsCO5xLFjw96adErWgu3w33-VmUjnLRFrY7rrdh6BqQCdO7hS-eUs8qhHG0rboTJdDbjzF6EHc5H5eL-LspJPqctXivv8qnPOk',
      'https://lh3.googleusercontent.com/pw/AP1GczNl4_AdynZjuBFfOyzWX1xoCd8k88a9B5yyldtWqZn6xSdTAMw6d6SRfXmtPEun9UCdWF2v-TptIY8ryuCDyBVNdG-NP_l7UUN3ipkKjSQgikyxVjcn',
      'https://lh3.googleusercontent.com/pw/AP1GczMI7sfQftpoGYPyGigCROHC8F3ox0BT7PU4ZYuucJ8AiZ_zCKGs0c04MQxFcP7MGPF6XZh9t4BL9pbE5i4giJronwvrjlL06kxpsUGtnYborcBT41-z',
      'https://lh3.googleusercontent.com/pw/AP1GczN7-Z6Tpq4PWpTrcT2jkDLg7CSi5YbfLYqZqCsfZSJKE52CkqwWXUl-r34pmCKJc155W44QaolZeHQnqqLW8lullWOXukpm-awCUBqCe7ejHzR0twMj',
      'https://lh3.googleusercontent.com/pw/AP1GczPO-manMT4BXfGxKz-JUGP0uI2uoTus119EmhEYip4LeDbTKHYpYffp0Ds78R8T_aGDx0Wnq8-Yf05KFkXGi-QQxDtWhWeqU6tg2KPYMjZ91IkTAtcV',
      'https://lh3.googleusercontent.com/pw/AP1GczNYTLlXMf_DIsTeCIzn2iBGGIcs-u8vfuUfuX4f355d48gqWl48iXpNIBupoleDVwmlZSOGlHDy6x-aUKJ922WFSrH93th-B_JsN4KaxZweXzg-ttFm',
      'https://lh3.googleusercontent.com/pw/AP1GczNZGE10vJbsQ0iXIQ3YEUe33SwUEnoWdzvQeypoCkWXQhbTpQCE2w46lV5AiBwGLypbibBN1GUrmJSgNJuuSOInJRHFi5qHC6MzjMtj6grKTQjm9AKC',
      'https://lh3.googleusercontent.com/pw/AP1GczP3tgl-XrBDNyXHSGzaX4lqutvvRP1FEdIGki-Dl0MC1c7fo3B0jITaxp2nIVKAtsd-h4KrObc3jCPlAwK56RvyEufOnGlCckGUy2U1Wm7syzcyWoMx',
      'https://lh3.googleusercontent.com/pw/AP1GczOVsh84I2bjbf3QpJFd24a8L1-CDqoDk4mmiD8SC-DnQ1OY_QRZ64fMAgZQEegXJZcX7VqIjnfLIwvIhywCEMrnzNFozo8_hBlKNyYX90rniLKxeMzq',
      'https://lh3.googleusercontent.com/pw/AP1GczOWpGL5oHpZPF2FrFB9Jgmx3f0HNQN-hlLE4Wz4Mcb3M9oYWcz7rvr1YDpvrb0nprgA5kIWao05LgcPLpJ7ZkzkT-OkxTM9egZWjfBa11rDrv0WLQvC',
      'https://lh3.googleusercontent.com/pw/AP1GczNvCgVriMfbwaa3dTR2IgGox1XcDNa2e21PG9mC-IXH8WKiwrI3cTjKGGGyYT4Bm8SfIjtw26YlsLyYYicbt8ypKnliRNobmLzbc15mQqvLUfYC1hXg',
      'https://lh3.googleusercontent.com/pw/AP1GczPQsMtiU-8_yFnmEHHmKFw4vRal79H0CacuQmt074ITu5v2R0g1qRQqC8ebSQT9BpJW0FNRdJQC2ybDEfbHWb0frsuyx1d-tBm_G9rnCnCgwhl0n3GC',
      'https://lh3.googleusercontent.com/pw/AP1GczM2RU8ZOXAp7TswWGJfkhuhlTUm4ZGR3xgfnBf8pjHLm8NzzSaL0KiTRzloHUc2XUXQGd2gBytAbXPV7fso2GJv1GAiupXYMo-KqFva03T4dpyx_zkQ',
      'https://lh3.googleusercontent.com/pw/AP1GczOC0ytVtesJMHhYKGCjf-ETM_Ckn8t5UOJOsFaebeuKKeFpxPD9hl4k5HOOBSJ7yBxyt0rBXiKTaGF5HiYcHHJk2uGIUPGfMKsM87itccT2wFRSDjAU',
      'https://lh3.googleusercontent.com/pw/AP1GczOg-U_cc1h23aNVLC06_f51UNIubIQRYE8h91AfBFQddSsZx-Rt98ywuOTdE9RUvQ2rLXoekoaxxX-yb3GvXj3iOMVGrHnNmpinFA0uK4MC4CGekKPG',
      'https://lh3.googleusercontent.com/pw/AP1GczNqmJMNsYtkdh-PCziITInn-Bs8fIuTh3tY-M4Z0UosdabxDwwBOVqNDQ_agaSxB3-UiSQcLrOf15ZQ-rFFgDT_YVl4bs8EDVDakY0VHyJqmqvmKiTG',
      'https://lh3.googleusercontent.com/pw/AP1GczNdUMcxQ0Ys1_JgrK75LGa0P9AKjZLI_fw41cRam24yGnujYT2kAaFQbt7zuSV_lj6kRMkehFT_a2XMVaUSk6cODhlwNOlt32gAjb_oHR2Xx9Wo9Q',
      'https://lh3.googleusercontent.com/pw/AP1GczNgd3OplUY5r4_NseKsSW1RRpOQzqJCh5NRpKgDcqku5kZDYGY3-qP34_zkbwAKSAQbcoAashNs7fXSsAougiXVo8pSlgHbpgomK4aGr57dgcGSkedd',
      'https://lh3.googleusercontent.com/pw/AP1GczN35zzyFYYyZkLyxeKdj-0wlram2n-zVPkwTEg0iULVaVFuV37O12nKLcfjiouX8VI2ogznWS64txgtGdexzlaxyGBxLiKxAALw8r586RJEsFZsFpRe',
      'https://lh3.googleusercontent.com/pw/AP1GczOxzIcQbWbV2IKsDbrt1ONAnsdA3JbYRcDa50eYs2quneLEEbXLRVzh9C8RM2emRqFo2xy4Fc_Pr33IpR5wrpYoiMtq4u88mhm4NtdioJkKJ_zdPe8',
      'https://lh3.googleusercontent.com/pw/AP1GczOJNWpZUiI6EqnsLfrrZzazL_cRqvyT60dbNOiceyaqz3upbdaFrOXIgueTQFjaDMr6FlnZok6rLlMLT2iKWt8KyDeLN5PMC-Esm_uuFgm05EEeTXQE',
      'https://lh3.googleusercontent.com/pw/AP1GczMp5EyaGsu4qgCLcZktKBtZn7hpt9MSSEzijcJ2VNkjgZ0S_F4twN8yk9Bg7Dz_WNeGWqVOZpr6EzS9HUTvw6wGUSHa_PDmaaY6PJpeM6nZK3IctDJv',
      'https://lh3.googleusercontent.com/pw/AP1GczOeMQIOTKe11_Cz7_kG4dTtrlpEr093lr_5zRg0I9kFszmX8ER-Dc5FiG4fplh5MllDrg4ItGX12HaFksXsqqikpFF0sVOyqlkvNtnnBJOKPKg19Ml9',
      'https://lh3.googleusercontent.com/pw/AP1GczMapWWdwp13VdIXFDhvt9Ujo-R6OrKfJOjcUKz7lhC0ihsOLLQsNHOvtV7RfQ6kn02HLdP3uLl8EMxG0aTGvPXk87njPbiPDWwzKfbpXlWvbgPCkWo',
      'https://lh3.googleusercontent.com/pw/AP1GczOc-lBURXggmdO-mutoStTfQr-Upo1Ky6O31MbO0_qDvHrQaulI9NPWPdw-9yg0Xd5qHQukpP2nFoEn7dWZ6o3SyLn4eloe-sK3Jb-JRIwgphPMMxOb',
      'https://lh3.googleusercontent.com/pw/AP1GczPQSZeTVgQDHs1lkB0dn8V9Jis6K4_OdDqyJRwU6Mq1Xi_GVOJB-C-77nagvWxVc5firWlxrUNruV_qvonSN42PHKi4w9yk8quuuGrhTOudyS5EMvXj',
      'https://lh3.googleusercontent.com/pw/AP1GczMp6veMEh1PmVoDoc_P3qLFDbgOwxsA0DLVzte3N3CSf2ISX8im94gJHnrTLoXECiCZaihEdrLm3myxcA1rHBoChT2LgYzOQFiCguTEXrpuT36T48i8',
      'https://lh3.googleusercontent.com/pw/AP1GczMvjxp_RgC5XQ3_7E482fWEtH8kRz-Q0cQUbaEBtwHjk7Mh3PA3MP56q6hi9aC53Lti_1f-724rFMvRcIHgb44YhL_sJ2zj71IxrvhhUvtjzpbIZAei',
      'https://lh3.googleusercontent.com/pw/AP1GczMZo0r7PUwDxFfz3fa4kCmIAMqaA1Kmw8aX_iDHlt0Sy8svEnEXfmhy_FGlRrIEnaxfpk-h9YByl3L-myCVEitlJk07OF6Zo30iDVE8-jSXJ2WJdocu',
      'https://lh3.googleusercontent.com/pw/AP1GczMz-Q3yon_QC9tGeUM9o0R1_2m1l5IrpFb1cCFrc9PhiH1uE6CZeD65wvjQ7xLOqKl9cDZrrLfGZyRMbW3ruOT6B8y8XzGDIr9rR6LkZZ_qsH49Yg',
      'https://lh3.googleusercontent.com/pw/AP1GczP9IFwT70o4A83qzT9IBFh-RtQFGfGiioxR_QDx6TVXTT9GGJi0IAR8fL9l-7cPps9T_74G0HPYvxuPUFtzSIdqrZaYljE5F02exkXCsEb-kM6ntTeZ',
      'https://lh3.googleusercontent.com/pw/AP1GczO2zJWwC18YAapZCFF4z5jparIGIwKU245nARhkC6HAAh2WHh1dWKpp2L6WSpEhSdsD_alxfY4JKrRyQokgFKsQG0gb_R8uHPQlv522PX2RSI8-haEt',
      'https://lh3.googleusercontent.com/pw/AP1GczN-9H0FwXpiEk7EL4oa7fP81Ku50gou13FwPmbLDbs7L5vRqNfv84jmoTSVY9E2wyyfsIPQIzwcrhnDO2uHRKLopmDGZGKzzqHgCqUDmFrruRPz-Gez',
      'https://lh3.googleusercontent.com/pw/AP1GczO2Ds61iImDMVIZHoq27wuxEb_MTQQZq-QRMrk5l5S7NOHLBoC6J4fYgtnK8Gu3e8bpu2TNYfbhW_m8a4K6utzSxDf9ajPdWf6CDGElh40F4wSUDPoK',
      'https://lh3.googleusercontent.com/pw/AP1GczO68d32yPIBGsLDvh_jMj4F_1Cfvxs3j3X-okLMQBmqMakEYob0wCPbXqCFuf90lCqtYVy3ciL5UMwvxwJWeAPKTC-hQTshs2t-GiR1-ey3ckK19rmv',
      'https://lh3.googleusercontent.com/pw/AP1GczOn45j9fao5H-SM2emhqpwc59la5Q21XdZBi60adbn2lQc4hYQhfxj1M1-g7qdb-2_GLFVpXLFYzWE3ygnSmak_pgMQBwhWEK6asnBzuQKXFnNLt1Oh',
      'https://lh3.googleusercontent.com/pw/AP1GczPy9-RiRbApERA-gRuwu7QQkIx3qdhCLH1McyreukVm1qm8mtpruKkTQICfMX2Wss4fnDi_UUkTTvhj3mYJxQzILp_BhfdQizm4XE1WXkRkV88x2Krc',
      'https://lh3.googleusercontent.com/pw/AP1GczNjOS-edsagEdp66bQZtu516AtIeZV2ABIVInZuq6a72YIdnjqrtI8rsIdR1fx1Lno9e3qCYIq6LTQ5yuCXGRUFvVYh88qaJ3izLVrL5L7prGz8qhFE',
      'https://lh3.googleusercontent.com/pw/AP1GczNCqPguIQ9n34pc2GLig14O375t9dtfsu6t26pDifTHU2Mtgmf0mLZdB3EuVHmyNzJU80j7ihv5VZb4EUYYo7Q1g1HDH6i7ec-VoU4ZsN3PMKKEfTzp',
      'https://lh3.googleusercontent.com/pw/AP1GczPQDRsD6PWzPS6Et5EkkS_5V_cyWWfF5E4nf0H387iF8S2eXOObHv5ue6CGmJ5ZHdyXmh0oFRqRjXRBBQrSF4zNl9zSLyAKjtUTJqSN3MScMF6jua-3',
      'https://lh3.googleusercontent.com/pw/AP1GczM61SQGiKJ4lf02nqeoy_EStsCnrqcuBrWgEKrODaV_fGRbGGAfFOftH_lVlbLWX5-EqowybkuVf2rjx8c2UWMQzNAoqhITePbVi1-CZrfVPaLwtuBf',
      'https://lh3.googleusercontent.com/pw/AP1GczP9llyY_wvfxSdy-eiY6KuRm0d6q3X5RWiQ7WMLzObLVO_HkPyYJGrah4ed5utEL6NRnjx7J-V9O93XokLG4DCEQFDiyRuMPKHUTYvbiMOepjhueA',
      'https://lh3.googleusercontent.com/pw/AP1GczNKmeeqzWPyKprHMglxENy8hXHxqZDu-q1nyDARBq_384QnZR2psJnms55ksOqGpFfplCeMLYPWBdakddaaKRkWm9t3vI6OYHhpwslSWHbQdMByVPg5',
      'https://lh3.googleusercontent.com/pw/AP1GczMe_CqxXCYDYn0EFQmq8tJ5zdoHg_sldlzPzib4rIGZKFFJODtRlwvnsqCPpAbhpjzsHQH9TgjO1RaAR1PCbLrx55CCWeXwyg9gjjrJInoWst6op70M',
      'https://lh3.googleusercontent.com/pw/AP1GczMkwnNdKECG3TnSmgaL2iryitGA7UBR7w3nqK9OrOEg9xz8bLFSINnoXkqCz4P3eP4sj9QvhKFpFs7_YeGZF4AQRSaWIl0r2h5hm8qLvugk-YDLp4hI',
      'https://lh3.googleusercontent.com/pw/AP1GczPcUbfpvZsqvkOfu3adefYVd1NGBqUwe8yHoOq7X-0r9OcohcdurmAINS2yF62WZY8vnA2SYDu8ztJbvD-n_qBpEfCuvzfmBVq_GuYDUkT-Ej11VtLe',
      'https://lh3.googleusercontent.com/pw/AP1GczNnSRHtfAFMT6dJpsrkEce0aLg8QGk8h93gaSck5r_B2xxXGB3xvFYy9S-VYc5LUm9JCv9rKZbLZXOdPwUJbEnS6miwvFJA6KLwvVH8QnBzNHM2sgxg',
      'https://lh3.googleusercontent.com/pw/AP1GczPBOhZ-ii0MLvUcYzsUcdIc1WgLAK0cfSz3-Soh3AidBUNHkHFzWnFiGJoCIFqbg8y5LosRi6ANZvt_CyCc4e_o1vs3PqHQzB7-PaVCnySdDMm5CQ',
      'https://lh3.googleusercontent.com/pw/AP1GczMPBpXMQHhGBQ3gI3FjOW5-zxi2fvpnHtPRKoZ_W0U_IN8oCaA9PQ-7WKxq8JN15kEAXwx36gSVrdBOXX9LUxrQETgfXKLNPh9wmPjyvQHtM5diBrOw',
      'https://lh3.googleusercontent.com/pw/AP1GczMrq6pywTDb60cLVbwSmSEAShLQiyNkHaIazV2MlbMaHi-AJ7FPT221MQLQ4SWC2QQFMexL8hAs7hTHbK2OmytwXzIyhPV13MKIF1hklmb22R8t7ucL',
      'https://lh3.googleusercontent.com/pw/AP1GczNN6cN9vt2vzXVWdkA_z7c0MpBZLXwyUCAEUYnfHwEaz0PW-RDeX22F48L78Ja6n0DcHxkbyYBc7bGsFtM4iY1d77R32PEG6lSDZQoDEHiaLP_NaQvZ',
      'https://lh3.googleusercontent.com/pw/AP1GczN2UTcAeersE9mHZJR3R3dJewrqPZtqg_MUegjFHu3t7aQvmlXK26fdgT0pcld0NooSm75O6yNDYJG5sZwYKuB7xmMV04qq5ofDJcvCGJpQVCViF_6P',
      'https://lh3.googleusercontent.com/pw/AP1GczPaPNrtGEQembmPRZbl08K2LfgA_eR4bz6VZuAzMyKY1qqbDzviHpesFUvm0ttrQ3vlWaVYK5JGqKmhUDtMnvzMWBA33gW99gayI8urgziBz9xcNIj6',
      'https://lh3.googleusercontent.com/pw/AP1GczN4fZMZEy6sOCLd1HX-pzzMjmrD6wc5to2iJAtTMCsq_Y_Eu9emJtuVecex2TeaDRDaK4i0PpNu3ZCZ1Dand0u-lb00VZgj0dFWS9-YSxaE1KTrqowY',
      'https://lh3.googleusercontent.com/pw/AP1GczMOAvyQhBICqtSBpGdkj1cb7oi7gzuLxd9RrFgqFC4rWvkxRfUZabFY-yKeoe-lrQUsYeVPfK5ejayBnoF3KQKhz5xC_u3meSa1k5rqOnz3grzmdaZg',
      'https://lh3.googleusercontent.com/pw/AP1GczM0JnpyKaCKNegv8xnPCzIJgu8bB82qyNBJVAInkvu8qoHSQRLkODL7fka5G77oBuI_myKlVuhT9Otih1TbdXWDDW79MdHEQdn06FRRMAuphCRW8NCf',
      'https://lh3.googleusercontent.com/pw/AP1GczPxYBz7jkGeDwX11mqabuhVdz5QIZ_m3OHvRRTT-9RblqdE661VsL7o9Va95UFS_z2L82XBSSn66BLNEn_n_ss9fwiRcXNz9eswFZ_4FJR5d0Z3QZJp',
      'https://lh3.googleusercontent.com/pw/AP1GczOcGDb3JVUkLssam84SGyIzRNjMILUVN03sb7BblWDJHsV4Ss2dlQ4Y_Lo8qfn1wdeiH2J0xTsvHLAhdP1ci3aPKUV8uftDpb0-v5dCQAhmoV9aGJ3O',
      'https://lh3.googleusercontent.com/pw/AP1GczO40PSEZbN65BYO2iPV6A5X8ttDACUpKiJCS4mU2HNuhN_wKXPAhC-sxbZSb9poN_9qs_E2EpbBS1nUTmoyG-KlJpaoVqIiPj1_SDcemn3r3r-tpzAH',
      'https://lh3.googleusercontent.com/pw/AP1GczMp1mux6XXJN-rxSjc2lY8QzIoHUa8gtU8275KU-rr7hYQ7MZ6VXGywvleET90K0dZZ15nholK-oadSnXJtXve6c6qNHpueQfVJtk_xWXDNxVC5zApW',
      'https://lh3.googleusercontent.com/pw/AP1GczNKOfedtGqrXQZNpof3K5wevlOwD-X0kQdy5OC99oxUOMy5UQMzJ6GQJRLBxue2Jx418NSFn3Jxvh9M-PI83wTsE-OmlyhlllrJvi2k4Ep_lsqUzQDg',
      'https://lh3.googleusercontent.com/pw/AP1GczOrpJbDtwuI9TcHNNbar6ChWgZOFo2nM4ETPeToaUfSwIeU_YOacLM3F_03kVkiIUr3WfKF1_AbSlzlhj0JZBSNp5k-iGYtningzMglzGyTTVjsovjF',
      'https://lh3.googleusercontent.com/pw/AP1GczMBKrPNvbPcxfNQ_EKcclAAUtCR7zKvLC_VA-lqdW2CBt3wW5apdCEyZcfjnm9A6NXLOuqzZxOapDDpxA6X1yfcRftB4eMLtsULIxx1JnFKAVAehWff',
      'https://lh3.googleusercontent.com/pw/AP1GczNIaGJeLQaqvzfVT8khg6dCXzbacKdmXvxigQYJmCI0Qis6wUfzDyQ0Wv1TRNBG-nEZlvVQVzuAZunYN7paPnFhDFBDSyfotktufB0HCdIUZ1UAdxRp',
      'https://lh3.googleusercontent.com/pw/AP1GczNvo9S1ip3YXQpumjU1BS7hgI-wn0rePyiHWxkkbQvuFB_F7UJrcwPJFQbtqdTNpjRU53wKYpNn49reMbe679oEKdA--S_uVSiYW-F-fSZ_nFBqISNj',
      'https://lh3.googleusercontent.com/pw/AP1GczM6CXf67xrYSewjpwaOuaPyJ0LOo9MgXPmRIPB6HXTK3vgrzSba4CCb7XkYr6O5vOgLMnDrgSR_boCD20mffbyuAE9wPKSsGHaSf8cj8ElZ90tgEwu6',
      'https://lh3.googleusercontent.com/pw/AP1GczPE6IFGVpFZeXSc_ubmd2chLKTl-G3JZwZq_pZQaUPflCCNTIZKPYBi9PTG2SPWy4YlOuEbblQrQV4zN3AO0VE2lFiaSyn8VqaueflcJVofbbZiHnYl',
      'https://lh3.googleusercontent.com/pw/AP1GczPp7JiT6L6_-vXXmE-Jlz48th0Q2T-rcjN40RrP0P_sj-LDYR-Zq0JBOAbzg2B0TBtg5ePXgR7t3osCBd4huZhEy0qnFB1-ziSY_QhqOsPk72pyKBAa',
      'https://lh3.googleusercontent.com/pw/AP1GczN1_P1gbtXV60jc0Fn0RqLJVFy3iwc52XjhmiZl-XJAFP1uBdAB4hmdgDyXAL6hzIwCEU0jZ4vZuEPfAP9gRwI8HgeCf4eytiuF7Dvl0aT9v3Ks7ldH',
      'https://lh3.googleusercontent.com/pw/AP1GczO1GpsnrbBpiLFG8bjlkI9ULAlg1hTGDPHbQghdRWfm5Z3EMwdoDQXePxPmxAbpZaX489b0HBQ9KkTaLbnypxbYWjnA0fB4NBO7v8WjwZgCyyQrL07n',
      'https://lh3.googleusercontent.com/pw/AP1GczPL7u79bi2ummzz95yNPiTzMw7etrj_uNlCmLkepuGpjnp8L7iUGtsMlZ3SBnyZjnAL32D5hkkgkGDSxJV6y_zyOOQZvTeqnkPSH9OILxlZIYyh3uIY',
      'https://lh3.googleusercontent.com/pw/AP1GczOgcpT6_7YwnpfbEQYroOTGcMnmBBeJgpX58-PcQ6hpQ9kGHAVW0MWgMhj_Q3OCXQ5X_hRuZUEu4rRjJ35bibshUt4_InctB32uA0YEKPSJvM09pdYT',
      'https://lh3.googleusercontent.com/pw/AP1GczPhrG3e439rgb4T1e0yG1S4ipI29uko3Y_HF8oJ4lFq3Usmg_OEB1xw6NwAKbmnm8pAz6hF0BKs_hVwZRLieFVhblUcijm8WN0S6eoOIW9Z12rHWKU',
      'https://lh3.googleusercontent.com/pw/AP1GczPHA5d5w8ThgqDPsQnnZ2X6rLUl6oyMZWHUjmA_s_NfdMaII-DRHPUOGBh0qojJcpTLXr3U6nudUVlWdNVEYj0Z-tYYlw2pzervrck5mcoIsH--BjI',
      'https://lh3.googleusercontent.com/pw/AP1GczNGDDcYoLcih370pGe8e5XfXpKyYCIPDXQ5gRFCSLS4q7-GiIV_zaGDXRdrAirLpIrVP0o4RlmwOFRVxvlgm8TG9yv1Idf4iLroiwSzPbqCC2MCjPE',
      'https://lh3.googleusercontent.com/pw/AP1GczP1cfizyaGO13TtUuWvzkHPeyyH_MHz3y53kQ1BSRfMuSsjBawjG6vNZk71iLaBzbr-0JO3DDIvljRObf0C4v-iCL2yTaCgdG3gjggg_sK0SP1Val0',
      'https://lh3.googleusercontent.com/pw/AP1GczPrIvTQLg39LWG1JxkzINtrTi6tpZZvQsIDbQSi8uuDajGdDnTwOnFvguO4LOzcgEW0fCPTOEwpXdLJaSx-tVYmYQaz-cN5ZRifvbT1Cje7sSKm358',
      'https://lh3.googleusercontent.com/pw/AP1GczOx3-YaQBsSQayVyxEUBgWnlXMGWNohZ6V4DVSAZf1bPey-TxKxX3PaEJyR_H7tBhYDdtV7skhJ9f74iATLu6Fx3ZC7WOPew3gHMBkNoLbqQs2CF7s',
      'https://lh3.googleusercontent.com/pw/AP1GczMjJW2wgJxPbSPzDF9fxYsNvYMVRBX2k4GT3gorUX7WA-hiDySY6P21V6fpd2zHpj5zESie5KW0AMN3rjSnhKbVnEYiN94nvUFF1GkkFu321rysfKc',
      'https://lh3.googleusercontent.com/pw/AP1GczO7X0Qb4Q3VXB01tgDuk12hSLKIQkJOjvaxjM0lysirjkjvuLxaUmAd-e9uDdz7PQxtKqW8kbXpx1_MP2k_fiJU_05cKoe2WhOrttIw7LM1GyURvmw',
      'https://lh3.googleusercontent.com/pw/AP1GczOYjtiq_DBH__vcg85nhMrVU6afaq0VqgNeNsfzkn_TrUA1tqlZ3fFGG5uy3ji2OaB_EDZZhyfdkwStHHa7_CvZf5uMnQN2C0lyuJRPgLqkAhQPOw',
      'https://lh3.googleusercontent.com/pw/AP1GczOsZOSLR8VA4J8SDFzbj83mJxdIznD3Z0MO8YRdz-OzJvv9V54jiaOkX6rOHmcgAEQc525P8d-0-sWATi-VqeBVLL0K8coW0lhmmWXV5d_pLmpXKOWw',
      'https://lh3.googleusercontent.com/pw/AP1GczM3G6tTqeTKQHSzEetLeqevKg0dQOYBr5JQU4ZhSiRpTFK9hi6uy0wuOoAR51LC1wysDtq-n8kbSp4XNtMv4Yk6bj-qRoKXQykfAzEeigzrzeaATTEF',
      'https://lh3.googleusercontent.com/pw/AP1GczPYbBStwDMpCHsSs1YQ_IC5FVl1r66F7buGYs9YOpX7cdv6_c2wfMILtN8wJBDYZvHyDlxOijXLqvh3svlAL9my1lO53JiN1oAjgKHpCPxFr1aJgw',
      'https://lh3.googleusercontent.com/pw/AP1GczPdaTyQw-O-GKM-xTyz__HLyBXOqNUy8Gcp0pejuV6XN-80lF8qPShrchOpa2R6JvGYLSiibf1qee-R3Bq1Zr7w-uHSbFTWsrlZo8OXhl2nUCu3sw',
      'https://lh3.googleusercontent.com/pw/AP1GczOsjsWkzf46ElCKorUiyI2k0x9FDKonKMEtZNJM64pg93xiP4zeO6KJw3VuzE7cFcZFmt2jU6dK0al3sTbmIhTH043-Ke9wfB0scQ3CbMbvlEZprFzQ',
      'https://lh3.googleusercontent.com/pw/AP1GczOInCUNZQUZzENPbmNf-u5KW324CgEq44JV4DXuxQxKzj2FpNw4lcsQMFB-N5aLApo8pJdPMEaAIR1cMJWQSZX42MGv2Qh025Vd5FJZ8eVSLUes8YYI',
      'https://lh3.googleusercontent.com/pw/AP1GczP_QwLHcpsx-Gz5qOc2qoCYEIAMrZRtlrhmuq5OrBLw5x_263bAbYI1dqxqInNLx8IxTX7TBO_h-oyGG5Aqc4oUTZCn3HbYHaeyLxxSoK-0rkrjfxFi',
      'https://lh3.googleusercontent.com/pw/AP1GczMvfZyf5qFVlE2Mn07F8_zLkWAN__KrhA5Wsgma6j4ywA9lImiiVO0Kj85RG2hTu8UtBy5w47DZHh7DZQu80hyY1bThSm7cspJfrbku7cHb9scV2Xz4',
      'https://lh3.googleusercontent.com/pw/AP1GczM2sbJsJXsrQoN499i2fcvEWklc6kCuI0ozj-kljIz-_M7_zf-kUUmR-PQG4QQnprzZCGWkjz_sSLOT0GcrSFKI_Gjs68_uI0rjri8l4E1YKFQ-2Y6H',
      'https://lh3.googleusercontent.com/pw/AP1GczOMHtSjf8OVic8DxlbhdtMCZgrOEjlIJfh3WT-pvOeb-M-C-pdDZS7NjpR_Bc3xRNokohWQkk0SHeYgU57TN7-rLkwiK3eRU7pOfdEsHmA6QAqFpCnM',
      'https://lh3.googleusercontent.com/pw/AP1GczOjrWemJebexoCtGECdC0fiGCEfZyJtf4p8sBLu98krUgepyalG7x1jVlXRlm5qDVlPOauAlkU_DqISrLvWG_VIXdNC2bB2d4_kEbcGbY_JOv_7HSUw',
      'https://lh3.googleusercontent.com/pw/AP1GczMHP10t2UKKaRDY10hCqfMzcCO-RwYeEGC9Fe6xSFG3eYZWrUFHWLAftZmsfdDbyd-v-FCtd-Bz_JT_E7HCTMMjetp9mSyLTF9bH1wkYktFccMy4WKi',
      'https://lh3.googleusercontent.com/pw/AP1GczPR9NRDVRcXwqhFJMA-TRsMBVQjYuk3tkFMmrt1--4yoIAUck-m7iQS7w6yliCMNcsHFSb7eJsw89Odp6uz4eEQWWQpdTSp4HmcCypmfXkGpbdtf7G9'
    ]
  },
  'https://photos.app.goo.gl/3weqKMvLrk1RCkVa7': {
    rideId: '37915',
    title: "Catskills Bound",
    dateIso: '2026-09-12',
    photoCount: 65,
    coverPhotoUrl: 'https://lh3.googleusercontent.com/pw/AP1GczOxsCq3H4RhzJ7JNvBTYqNctalibdpksOvDYPg9SU0VU4x7pjSomLz7cTAPxeGDWuoksRW6XKQ34LQR9q6_A2Zdzk3bcH9_uDPdmnQRlM51UvrOhqWI=w600-h315-p-k',
    photos: [
      'https://lh3.googleusercontent.com/pw/AP1GczOxsCq3H4RhzJ7JNvBTYqNctalibdpksOvDYPg9SU0VU4x7pjSomLz7cTAPxeGDWuoksRW6XKQ34LQR9q6_A2Zdzk3bcH9_uDPdmnQRlM51UvrOhqWI',
      'https://lh3.googleusercontent.com/pw/AP1GczOtkqJBV0icTnJU8jn-JoFJfcHGxerEqPRpjQgoloE1dk4tNYDyOua9ZkbkmXF2iEwK4hEEUuNH1ZlJZwW17BJB38ScjD-NZyaroKn9hK8RVGVXpR5h',
      'https://lh3.googleusercontent.com/pw/AP1GczNOY7MrIRYl84nJXQSv_V6gGNVu_r3OVLgqAg-gibx4CddGATuPmoZzWGMPDtdmpWYCA4jnBomzAczmo7VjeMBot0phoOXY7xHepk0Z7SqU6vi5LZ2I',
      'https://lh3.googleusercontent.com/pw/AP1GczNyd3CnGpCozu6DHdTcMDKvawWNxw4iYZzb_MvOBp-uBVz0PL09SLShjq_VYzdqeeG8YB2MicVXI-X2Dm3sGiJM1HiyFCnMqSplJ6UOA7Wcr9OzbKuc',
      'https://lh3.googleusercontent.com/pw/AP1GczORhCxEVT1dUYHsNnPBg5TLAqq6t1dFem-jyEg5J8jixJ-GhslRgGenYN_28PBveb_cOD931tjaypyyi26ULB27m_kYXMcYzZ8NyxhFF4nvh1STEbNJ',
      'https://lh3.googleusercontent.com/pw/AP1GczNRQbBODE4hle0_w61_sqE03nqCrLIZqpn45GxhXbKxCs-lSXHh2qtRLLURna87KV5l39pBU-lidk8jJVIP5Bt4w5Bc_9MpDSz_ROpDR1HHi5Ecn5rk',
      'https://lh3.googleusercontent.com/pw/AP1GczPFTd5Q1mzxQw4QNWxQNScQoqUjqcWhQE5Ek3fNWCjKnzT_cCh0RUFJxxHgfe5NmvA4isgtx0DYMdsKLgd17t0gG4JNKscPz_40r2i8_g2EjfZR3arS',
      'https://lh3.googleusercontent.com/pw/AP1GczMc6nsqRF01LFGXEVl-qf7tXLK-XQ-IeymUfNl7YtLtJsvqzC-302SX3qOmsyYcbYUNDMA0JDKptIJ2K3hxgtp4M4ZYeh1p0h8uPXfzJIKxpFXT9GA',
      'https://lh3.googleusercontent.com/pw/AP1GczN6uijhe57OMrvNPdHvMfPNfLdXIkTs79yHDOFj-YM4vXyD1WpRnYxPaG7ODOuVqvPN8wnzte8ZLIBfV3Pdiwd0i2LC0AfzoDOxarIvJWXmfCeieDuL',
      'https://lh3.googleusercontent.com/pw/AP1GczNuG_aj6Thnqv_9dtq9kOWpNaCAjvbRMIC6m-UQm-Vd97WzIprqyjgt0PE5cZtey9A_gZ4lrIT2DlDYaUH642Xk7EOZPydKzEHQ6ajLk4JLmuSG-RQz',
      'https://lh3.googleusercontent.com/pw/AP1GczN5n_HnmoRHSLWyUtFtO2h1CLgfwxSrxLkmRGDrNVTK0N6q_5YI8QEqGwuTB8tq_8p9DIF2PI-F5h3LzWrqZw-AkiWC55k8erSSL4LoqrFUQZa1If44',
      'https://lh3.googleusercontent.com/pw/AP1GczNQyRwlkot1ukDhTxln6JPMhha0J7Wwvk8y29PoO9njrDvyPtTRgZQiLcUYyUuPxw_gqcfFReUSkOkgmlQnO41QnIRS6kBeVHjuHu23JLRaWTYJuLA',
      'https://lh3.googleusercontent.com/pw/AP1GczPeJl-aNhtlchnQGYrmxWsNDVvGY0pB0rUHXiXPaUEAdFeq-mYUDq4eGp65HK_KxLpGcujHe22sKgHwqOnGp7Esc-S1sfkTzojzAy7Y0rIjVRYwO_9J',
      'https://lh3.googleusercontent.com/pw/AP1GczPDpUQC4xwLj535am7zvdWRAqRvmWjFDoXx7Lhp8ae7RjVfjfFitMcaxd290W1Gy_5Ouqbxo8qZII-n45tTGEu2zhDGNq_fsiN6JkUn__V8ct_8NHMS',
      'https://lh3.googleusercontent.com/pw/AP1GczNVCD8LvnjNg7WzQ78wRYPbS_RqItFPMEVDi7mt5NYIfvsYvEOwJtlVpCEJ0GIwjYwdw_UBQ3qSYRj767rNI_P9ZXKtOkJde56p8dcIv1PyVd-F_yDe',
      'https://lh3.googleusercontent.com/pw/AP1GczPTdTgslPXipsAVaKGMM1RwOvmiIxn8Gl4jdKIqFtEu_6Y7eUyL2Bm9FFMt1-hkJHvM6786OiSrqzn9sWQYRN0-JKl13FSd5NC0KSS92hn68CGLpoim',
      'https://lh3.googleusercontent.com/pw/AP1GczMYHuR82ufw8wrCFnnDaQ6t0YHZwW7asTP6duY0p9YQ1Eirag-3rZ66wBV-Nqx89ww4mtu3fkc0knCGy0yrN4DPRclanc7-gK1w3CiajTTR0qzy2g',
      'https://lh3.googleusercontent.com/pw/AP1GczPXkzyPzofbvP6TyrTuuf9Asjapd6oTPEBcOo_ts7PLdvWUjOokOkpTZHb_ip_t0SfZ3CmhMlFed5p9Dqy-e8gTvx6AouiJPjA6kkVLMCbvQ96dTAo',
      'https://lh3.googleusercontent.com/pw/AP1GczOa1N703rUfM0uT_Zcr_T-KJXSOHz-wmrosu1bYrqlC4QvevN1iTOqOYT6sZnCdetKeqEs6D4pxCazP_MwNSMboAvEA7Tp-7vZ3ivIjzLKQa7kJCUdA',
      'https://lh3.googleusercontent.com/pw/AP1GczNC4xvlgAyNiJ1_brtVUNoHlfBKs0dsppcrIRAhxyWUolarA398sgTqkFFkujHBoTW6pC49_V2bZf4ouZvZWuYNM7MXH7GfzPoY7UpKTfiVeZc8Qw',
      'https://lh3.googleusercontent.com/pw/AP1GczMjk-6DUfdlHdGIw1-mX5WT3fTdKi3zIyNHF0li-lnIT0SjLTqXbJ0GI4HBdwdwlowvziF1lPBzy8o4TvQ5BE23eqyf7uMbTyCqqTR3nDTwYZUoydg',
      'https://lh3.googleusercontent.com/pw/AP1GczOAnVJN9fC2Z_75PALvXYpXY72GRFZvginveF4Ga80jz3aOAWWXcv2PJGSUE_1FahA1BQU-kQtSXTkdURrrSB1al3oknt5njzkAGiO9g5JTHrDyLN70',
      'https://lh3.googleusercontent.com/pw/AP1GczPRWX0vrthizNqmBGcs8z0_Pi7DTxDIaZeHIzO0V-IDLtXBPNzYeiSPwrDGEtthZ2kk2GRIHEUSHls-u_C65O7XUmW6SIOm9ki4Imbmlwy0mDi5knDi',
      'https://lh3.googleusercontent.com/pw/AP1GczNZ_6Bxw3nd2cYoHXt1vzPSpfPvE0wUZjt6cJwunWM6iN-ayUWaMPvv8NtQGWfkHLM43Un5zBCozhCr_kgwpqLDsIH-rhKLMa38-YebQE0z7NwseZII',
      'https://lh3.googleusercontent.com/pw/AP1GczMRdX3Cptcs9walXCIpnDTaxdPOos0ICl40zwwyRfIBGhd_4-fp-wXFy0QX8MhwxLKF343-ZdEZhZmUA9RLEupLkAu5cPacAZGKi8OucF1uUqRKKXg',
      'https://lh3.googleusercontent.com/pw/AP1GczNrNr-HIkOVN4hJKlRPuLTCnJLD_WL7KUC446phhNM-aOocxhYEP2S1z2MDHvxLVlBfveTTEj5GFQJDswkCuPzK9QKTnNqUkDyZx_kd0YQD3g9ndubJ',
      'https://lh3.googleusercontent.com/pw/AP1GczOp4ZgnH0V8OnzXGGdvh0lZt7ouhci8-E-0FahEO0HYNj01AZmYp48n5zn816YiRKYdCxR2CjwuweWMx6airKpjpzyf-Ed9UsXonwpzBnvpRQwliqOZ',
      'https://lh3.googleusercontent.com/pw/AP1GczOfuwD_9KeeeakvO-XGTIWDyhs9K-Swjh5mdOH23SJtcc2NoRyJ7CowHToAyH0FfHQsR07nSr5uo02en5eaB7_9wg_WITcrpgM2kJOimFpwJG7-vdtG',
      'https://lh3.googleusercontent.com/pw/AP1GczO8B4-S6oC-orC3lbui25jeeGibPey946hOP_TfepVbQ2ORid-UdMeDYGLHA6JfYsfxTNDL9uJG14lxd66CqTsFYI7bJ7AF55Pf1wtw49_6O69lDKIM',
      'https://lh3.googleusercontent.com/pw/AP1GczPGfBdyrGdMFAfJMH7_gEk39Tbj2G9971YomryzRSg_OzJR2Ffgnd3dYKKiNi8hJLGk74vl08AqVKgU3_rnforoEICpTpU4dWnmWSTFpeMZjT7iB0SR',
      'https://lh3.googleusercontent.com/pw/AP1GczM04KTfsVD2Ip0B77yReDqntfrby7Ts0Di6OXutY91Xlndgw5B-ac4jKdpdROpOI_JhxGYbFhXQ2NqivIA-w86RQSIFGm9fnaTqX5ZfpdAYRQZXjs3f',
      'https://lh3.googleusercontent.com/pw/AP1GczOZebfJDPgJ5LSCEeuh3w8UvSX58vQNzmF1OCUsvT2H8oL6iS-iOgCimB9ZHBNc0UfPK-_ashRdBUpnxOZ5P8lbahwltk_prTuAuyavyWV9c6kC61Ok',
      'https://lh3.googleusercontent.com/pw/AP1GczPMrbLx_WWn7irrkBB8T_b10coO_bc1d8v7PqV7366IfZCUVyS7jVPf0O7l6I10EM6NRLpruJfDfoALLMK-wkS9bXspvNK2mLhG5zJQBz4p1IGYxWrM',
      'https://lh3.googleusercontent.com/pw/AP1GczOLF1bvK_lWRzUsbsqigWj1uin7Hf8zLp9-KiIh0V50jM73YglUVIMUmZRQSFGw8allfDZzIvY9i69kuEU8cMHAWvDBboXVMFSxhXV-vKZZF41M3p_7',
      'https://lh3.googleusercontent.com/pw/AP1GczOxnP8vE99lqHlF3eN06iN4X6Kzq29ZRkivVjM--QWnDPchqDvUBwZWyhD-0kc5jrQnJQOAv50MS3zR_L_IVFkr1Ok68K9T259VlahMRq0f10uqExwP',
      'https://lh3.googleusercontent.com/pw/AP1GczM1nYVZpgtmv-0eMTAwExGMCiE6FMJXcwL-UpOmBVzuoFZk7nR0RBL5CWBosqStBV396WNZd9hOZ12dmeH5RKqJDfVDC7I2bi6Z6SBIvJs0s-XXlA1w',
      'https://lh3.googleusercontent.com/pw/AP1GczNcsP6lXpWrSwfHoDrveKACzQ-5iseIkseJXa5M6EkQDFPPLM0yzC6C6nOLWJV9Nfu1qbatQLbpjEzUf9FpZcQ2NHA_r7lLqbVcfl0l7zF1L08EjHNn',
      'https://lh3.googleusercontent.com/pw/AP1GczOw4MPd3zZQED6BrR27wX061s9o0fN_BpUY7G9td-UPiw2An2OnxuxlfpzXF3eOav-7NSGQOTJLoCwCFg1icgFnLmQ74zissKX4PLwRM26YFO0kKgSO',
      'https://lh3.googleusercontent.com/pw/AP1GczM5kkzqHSUy6VzEu-AAi3K0k7i_x-RLg70F-pyQxNYjypXEW7TOjy00tX8LV9rGkOi_aZf2xsM6vu_gOh7WqACsSH9T__FsKlsAV_ss7cDKyizIFKZO',
      'https://lh3.googleusercontent.com/pw/AP1GczODQC9JVbpnwgIskAghjo0R3OyMd1SC2iPSV8IQHoLaDUKccDr2BTzFCINT-LyJ_4Db9StgeUurUNWZatcJgcxydAjqlQkRze5fqJY46brHzkT11ByA',
      'https://lh3.googleusercontent.com/pw/AP1GczMkLHtWm7DWuhc4gPDyuDt0kHtT8CdNMsjlleVe9-Oqa2yM6rfNnBM7Q3Ru0IhBhNhukqUysyJpJSAGOcaBiRMPEHdSZMxDrqKVZMWbH1k7dLKN-IWl',
      'https://lh3.googleusercontent.com/pw/AP1GczPyDSNfgpT_cMDLf6Mfq0_e0hRdEDatP2BrL_mg1vZc4Z8NxYjxl34lAEvtcBONf84Kwez8qhqFcAvh46Z7n0QSM6oKI6y9spyMLzBKSVTJj1Q9Bibv',
      'https://lh3.googleusercontent.com/pw/AP1GczNclHvzz6I56zF1BBJD9ShQbIgad_quFMBGyQHxRZKtRfexK3DKKtVcSfcMfOxdEBOWf0DO2U-_EYLzaK4wViKWTj9wOF4uPUMjm1sWpTWaGattoQ',
      'https://lh3.googleusercontent.com/pw/AP1GczNMAU1STdocXAoVFg-TzslJZWx_pBs-NH2m-QtbOhzEtxfzn8OVfvwawaGhuTWBIhy-2DVJWid78dgE6RFwKUcTY5yYv2-VIiU6ZFr-DxgNd55kES5n',
      'https://lh3.googleusercontent.com/pw/AP1GczOtQ0u-94CPVoz4oitfS9NIJqTrSWCG4VFPT8xsQj7JzFWKvGoeEQI40xvh5zJmm4NeeEz0Wgw8SDPDAHrrvDKgJEpy5LjJAiDtKFnnVnN4nSlENHGr',
      'https://lh3.googleusercontent.com/pw/AP1GczPtFBO5X1e0huLLGyGkkn2qEaoQe3kTKVeJK_gwgMsFDLqfLeaK0wwcPF_th0AF2iilUIrAJK-42cH7ZPPkK1RMtwNudx_QALYH9TNe2X7XoZ41Mr1c',
      'https://lh3.googleusercontent.com/pw/AP1GczP6fQqeDxAk-YpElzXRu3Py5eaMx52RxxRY2t3NDhwy4hDNcaQXV1VzS9ds__XuEtu2UeXFRi06FHoFrpn0WTU2bddNf6ftEDvACRd3EXIsVmAg4aa3',
      'https://lh3.googleusercontent.com/pw/AP1GczNEFUiXSb4wFM7gYGx-1BWFZafkCN0U-yEEMLq3vay3SK_zI-dd25u8As_La90fmEpf2NtCyW209mpqdR-OG1uJgIQSEx4-RQlTRyTOhkhXZ2C3bnOq',
      'https://lh3.googleusercontent.com/pw/AP1GczNoVlbDvZ4-dsV14nRcA1xDHcnoJBvPXEqBFj1Lu5TcH9XjZ9gdb1L2vP5-m1IPijNWiZv0Ss9cQEzBG56rUQEz9klwTIKABbnilBhzyrueH3w2p_dK',
      'https://lh3.googleusercontent.com/pw/AP1GczMilv18dVaYu-o7qPhQTaw5yF5-xXbUrTLCDbT3QHFUpQSe76M2kC4AikF-piHILfzTi5BBchQJk9TWE-6h0EmPKUp21hSp4lj0ZOXgAuei03SkvSAv',
      'https://lh3.googleusercontent.com/pw/AP1GczM7uU2My4OjCBTKwMAMsceJHAESev6Ed4VFZ4NnBzS0AwxLsmI6yLy-byuEWXIWQ2_CGPHF46OjSFxtsJko4f7Eb_kPsQyx_3CK_u_4V3XDMamzFftC',
      'https://lh3.googleusercontent.com/pw/AP1GczMCYzhCEPUzRlGBkACFrIT6tM67ZyQ6mM2jIx3wPKyV9y1v9pjvJ4OteSCsx3xYwDNZmUprTei4VwPKBpb1GP3ozvsmEhTIh2hKp250xc9kS_9f5CdO',
      'https://lh3.googleusercontent.com/pw/AP1GczOiZDBEJPyrVYzNg69v6Edlyo_IS5ZVHR10Py5Je4B04OVunBOvpTIk3rfiroXmntG7o8tcAgAJqGB_2jJHZ81F35Rm75j4s7JOnHSPgtOCPE9kuT0',
      'https://lh3.googleusercontent.com/pw/AP1GczOUgA6BoMy4YJQLIyL6IUyBrlKQgSjo_6eOC9H_NcXnun35YZu-iP73-7tXd4_KFoqSrMVtepk5xnyODT0dTKTL5wYtWAaD9Bo-EiaYiBIdKru49Q',
      'https://lh3.googleusercontent.com/pw/AP1GczPlwHxxtSp8mNe3Kkp2OAS56uRC85bbiXKOba_a7U-KaNzxj1Yt6pqT2EkO29P7PMVWMejLmhKOJbgCNvGqWYPqaVwbAfxm9R0vqJnYAzU9VV9H81Fg',
      'https://lh3.googleusercontent.com/pw/AP1GczPypHE1Kr73HfQqeJNISNnhjRC_f9HuWJlJZv6duA3FjifPToY8QQHTvLfNHkVjyv1Un2OR8lPqjaoi8XumSo5KhQUJeqVHY_gCh5tkpymr7uevJvM_',
      'https://lh3.googleusercontent.com/pw/AP1GczOE7gjZuZBYIAXLV9fiRN5vyH8Nur7QF6lgdAvxHTE3Do9PWXCJtRe8Y2xsPJPnQUm5j2gtdUuZhe9TphX9zNV-bQpPiL91RxJnumHQc1gDPL7VF40A',
      'https://lh3.googleusercontent.com/pw/AP1GczMRFut7yCFvQYOW0l-KwuG54ut_9L1h-OMJjY57LjsXr-pBBE1O3g0XuAb9mjF7nNWKAH20D-Ov8Hw_vTiddJjOFUvIlgpbFrgZmY3TfrhU_LLPVQ',
      'https://lh3.googleusercontent.com/pw/AP1GczN-bUVfYU7nYbEgAOmk-n2TM5qElUNmi9g_21moKHrNPNLR6SsmsFPYLSFUpfbAIfWXSpo-cyzFCaW9sm1XOAbU2JTIsh50Civt-lVODPs8BcL-FUvi',
      'https://lh3.googleusercontent.com/pw/AP1GczPr8jBYqPmg-R9hsyPf4e-dIa0lrWxWtNd3Q8iZqbY_DjWnCaNSqU1CXFChvwPVEKhv7Xuo5TfoA-nXHZih-rgXR5XA3mskTmOnW1t8jKRc740PIp-K',
      'https://lh3.googleusercontent.com/pw/AP1GczMNph9lnPP_zdhONdDmqd7B4Du-FxdsoFRpYz7zVKkPN7zexUIkVMFt29uBpBYdiSK8RzWbf5BBg-B3vAZiRaLTKbZsmJm-8QlDT9H0ZGp79A076M5C',
      'https://lh3.googleusercontent.com/pw/AP1GczOavR4gqwfmYRYv7hSTsH_upIhob6WnDC27FEFuv8c0K2H26mrsk1o99pE3NMF_ATXPWut2XuXljGS8f37GA7m_MTpekW02xCYpbjel45fzSfFtZS62',
      'https://lh3.googleusercontent.com/pw/AP1GczMTwws5jaJ0gTeK0xhTBc8WMllFYJ4HRkej9Djv_LqSqjgq-thAHuLmCCdY2kliAEWODRZO7mKpOE4pIw5EHTU_ev1QS9amSajWsW3oFCk9syFz2jrR',
      'https://lh3.googleusercontent.com/pw/AP1GczPWAgPpxlQtL8eL9GrTQsJGKvJ3ijVmJWrKeVaqPJ1Hg-JcOGmZ8mp2h6tn_S95e2zXXaj67BFtQ_elpjX3Y9toGMOV6JmEP69bseVfWhkq5gb_Y_qD',
      'https://lh3.googleusercontent.com/pw/AP1GczMviM6lFkidNtu10E698eJGU_IBYuyjvfGVGnxgbI-giaoNke4yLZuAVwFy1Ndz4WRqExQ3aLH2_zyoxxdaRKwDNbubakUB-o9OGq9xwf0KIojVXd37'
    ]
  },
  'https://photos.app.goo.gl/3weqKMvLr7U593X16': {
    rideId: '37915',
    title: "Catskills Bound",
    dateIso: '2026-09-12',
    photoCount: 65,
    coverPhotoUrl: 'https://lh3.googleusercontent.com/pw/AP1GczOxsCq3H4RhzJ7JNvBTYqNctalibdpksOvDYPg9SU0VU4x7pjSomLz7cTAPxeGDWuoksRW6XKQ34LQR9q6_A2Zdzk3bcH9_uDPdmnQRlM51UvrOhqWI=w600-h315-p-k',
    photos: [
      'https://lh3.googleusercontent.com/pw/AP1GczOxsCq3H4RhzJ7JNvBTYqNctalibdpksOvDYPg9SU0VU4x7pjSomLz7cTAPxeGDWuoksRW6XKQ34LQR9q6_A2Zdzk3bcH9_uDPdmnQRlM51UvrOhqWI',
      'https://lh3.googleusercontent.com/pw/AP1GczOtkqJBV0icTnJU8jn-JoFJfcHGxerEqPRpjQgoloE1dk4tNYDyOua9ZkbkmXF2iEwK4hEEUuNH1ZlJZwW17BJB38ScjD-NZyaroKn9hK8RVGVXpR5h',
      'https://lh3.googleusercontent.com/pw/AP1GczNOY7MrIRYl84nJXQSv_V6gGNVu_r3OVLgqAg-gibx4CddGATuPmoZzWGMPDtdmpWYCA4jnBomzAczmo7VjeMBot0phoOXY7xHepk0Z7SqU6vi5LZ2I',
      'https://lh3.googleusercontent.com/pw/AP1GczNyd3CnGpCozu6DHdTcMDKvawWNxw4iYZzb_MvOBp-uBVz0PL09SLShjq_VYzdqeeG8YB2MicVXI-X2Dm3sGiJM1HiyFCnMqSplJ6UOA7Wcr9OzbKuc',
      'https://lh3.googleusercontent.com/pw/AP1GczORhCxEVT1dUYHsNnPBg5TLAqq6t1dFem-jyEg5J8jixJ-GhslRgGenYN_28PBveb_cOD931tjaypyyi26ULB27m_kYXMcYzZ8NyxhFF4nvh1STEbNJ',
      'https://lh3.googleusercontent.com/pw/AP1GczNRQbBODE4hle0_w61_sqE03nqCrLIZqpn45GxhXbKxCs-lSXHh2qtRLLURna87KV5l39pBU-lidk8jJVIP5Bt4w5Bc_9MpDSz_ROpDR1HHi5Ecn5rk',
      'https://lh3.googleusercontent.com/pw/AP1GczPFTd5Q1mzxQw4QNWxQNScQoqUjqcWhQE5Ek3fNWCjKnzT_cCh0RUFJxxHgfe5NmvA4isgtx0DYMdsKLgd17t0gG4JNKscPz_40r2i8_g2EjfZR3arS',
      'https://lh3.googleusercontent.com/pw/AP1GczMc6nsqRF01LFGXEVl-qf7tXLK-XQ-IeymUfNl7YtLtJsvqzC-302SX3qOmsyYcbYUNDMA0JDKptIJ2K3hxgtp4M4ZYeh1p0h8uPXfzJIKxpFXT9GA',
      'https://lh3.googleusercontent.com/pw/AP1GczN6uijhe57OMrvNPdHvMfPNfLdXIkTs79yHDOFj-YM4vXyD1WpRnYxPaG7ODOuVqvPN8wnzte8ZLIBfV3Pdiwd0i2LC0AfzoDOxarIvJWXmfCeieDuL',
      'https://lh3.googleusercontent.com/pw/AP1GczNuG_aj6Thnqv_9dtq9kOWpNaCAjvbRMIC6m-UQm-Vd97WzIprqyjgt0PE5cZtey9A_gZ4lrIT2DlDYaUH642Xk7EOZPydKzEHQ6ajLk4JLmuSG-RQz',
      'https://lh3.googleusercontent.com/pw/AP1GczN5n_HnmoRHSLWyUtFtO2h1CLgfwxSrxLkmRGDrNVTK0N6q_5YI8QEqGwuTB8tq_8p9DIF2PI-F5h3LzWrqZw-AkiWC55k8erSSL4LoqrFUQZa1If44',
      'https://lh3.googleusercontent.com/pw/AP1GczNQyRwlkot1ukDhTxln6JPMhha0J7Wwvk8y29PoO9njrDvyPtTRgZQiLcUYyUuPxw_gqcfFReUSkOkgmlQnO41QnIRS6kBeVHjuHu23JLRaWTYJuLA',
      'https://lh3.googleusercontent.com/pw/AP1GczPeJl-aNhtlchnQGYrmxWsNDVvGY0pB0rUHXiXPaUEAdFeq-mYUDq4eGp65HK_KxLpGcujHe22sKgHwqOnGp7Esc-S1sfkTzojzAy7Y0rIjVRYwO_9J',
      'https://lh3.googleusercontent.com/pw/AP1GczPDpUQC4xwLj535am7zvdWRAqRvmWjFDoXx7Lhp8ae7RjVfjfFitMcaxd290W1Gy_5Ouqbxo8qZII-n45tTGEu2zhDGNq_fsiN6JkUn__V8ct_8NHMS',
      'https://lh3.googleusercontent.com/pw/AP1GczNVCD8LvnjNg7WzQ78wRYPbS_RqItFPMEVDi7mt5NYIfvsYvEOwJtlVpCEJ0GIwjYwdw_UBQ3qSYRj767rNI_P9ZXKtOkJde56p8dcIv1PyVd-F_yDe',
      'https://lh3.googleusercontent.com/pw/AP1GczPTdTgslPXipsAVaKGMM1RwOvmiIxn8Gl4jdKIqFtEu_6Y7eUyL2Bm9FFMt1-hkJHvM6786OiSrqzn9sWQYRN0-JKl13FSd5NC0KSS92hn68CGLpoim',
      'https://lh3.googleusercontent.com/pw/AP1GczMYHuR82ufw8wrCFnnDaQ6t0YHZwW7asTP6duY0p9YQ1Eirag-3rZ66wBV-Nqx89ww4mtu3fkc0knCGy0yrN4DPRclanc7-gK1w3CiajTTR0qzy2g',
      'https://lh3.googleusercontent.com/pw/AP1GczPXkzyPzofbvP6TyrTuuf9Asjapd6oTPEBcOo_ts7PLdvWUjOokOkpTZHb_ip_t0SfZ3CmhMlFed5p9Dqy-e8gTvx6AouiJPjA6kkVLMCbvQ96dTAo',
      'https://lh3.googleusercontent.com/pw/AP1GczOa1N703rUfM0uT_Zcr_T-KJXSOHz-wmrosu1bYrqlC4QvevN1iTOqOYT6sZnCdetKeqEs6D4pxCazP_MwNSMboAvEA7Tp-7vZ3ivIjzLKQa7kJCUdA',
      'https://lh3.googleusercontent.com/pw/AP1GczNC4xvlgAyNiJ1_brtVUNoHlfBKs0dsppcrIRAhxyWUolarA398sgTqkFFkujHBoTW6pC49_V2bZf4ouZvZWuYNM7MXH7GfzPoY7UpKTfiVeZc8Qw',
      'https://lh3.googleusercontent.com/pw/AP1GczMjk-6DUfdlHdGIw1-mX5WT3fTdKi3zIyNHF0li-lnIT0SjLTqXbJ0GI4HBdwdwlowvziF1lPBzy8o4TvQ5BE23eqyf7uMbTyCqqTR3nDTwYZUoydg',
      'https://lh3.googleusercontent.com/pw/AP1GczOAnVJN9fC2Z_75PALvXYpXY72GRFZvginveF4Ga80jz3aOAWWXcv2PJGSUE_1FahA1BQU-kQtSXTkdURrrSB1al3oknt5njzkAGiO9g5JTHrDyLN70',
      'https://lh3.googleusercontent.com/pw/AP1GczPRWX0vrthizNqmBGcs8z0_Pi7DTxDIaZeHIzO0V-IDLtXBPNzYeiSPwrDGEtthZ2kk2GRIHEUSHls-u_C65O7XUmW6SIOm9ki4Imbmlwy0mDi5knDi',
      'https://lh3.googleusercontent.com/pw/AP1GczNZ_6Bxw3nd2cYoHXt1vzPSpfPvE0wUZjt6cJwunWM6iN-ayUWaMPvv8NtQGWfkHLM43Un5zBCozhCr_kgwpqLDsIH-rhKLMa38-YebQE0z7NwseZII',
      'https://lh3.googleusercontent.com/pw/AP1GczMRdX3Cptcs9walXCIpnDTaxdPOos0ICl40zwwyRfIBGhd_4-fp-wXFy0QX8MhwxLKF343-ZdEZhZmUA9RLEupLkAu5cPacAZGKi8OucF1uUqRKKXg',
      'https://lh3.googleusercontent.com/pw/AP1GczNrNr-HIkOVN4hJKlRPuLTCnJLD_WL7KUC446phhNM-aOocxhYEP2S1z2MDHvxLVlBfveTTEj5GFQJDswkCuPzK9QKTnNqUkDyZx_kd0YQD3g9ndubJ',
      'https://lh3.googleusercontent.com/pw/AP1GczOp4ZgnH0V8OnzXGGdvh0lZt7ouhci8-E-0FahEO0HYNj01AZmYp48n5zn816YiRKYdCxR2CjwuweWMx6airKpjpzyf-Ed9UsXonwpzBnvpRQwliqOZ',
      'https://lh3.googleusercontent.com/pw/AP1GczOfuwD_9KeeeakvO-XGTIWDyhs9K-Swjh5mdOH23SJtcc2NoRyJ7CowHToAyH0FfHQsR07nSr5uo02en5eaB7_9wg_WITcrpgM2kJOimFpwJG7-vdtG',
      'https://lh3.googleusercontent.com/pw/AP1GczO8B4-S6oC-orC3lbui25jeeGibPey946hOP_TfepVbQ2ORid-UdMeDYGLHA6JfYsfxTNDL9uJG14lxd66CqTsFYI7bJ7AF55Pf1wtw49_6O69lDKIM',
      'https://lh3.googleusercontent.com/pw/AP1GczPGfBdyrGdMFAfJMH7_gEk39Tbj2G9971YomryzRSg_OzJR2Ffgnd3dYKKiNi8hJLGk74vl08AqVKgU3_rnforoEICpTpU4dWnmWSTFpeMZjT7iB0SR',
      'https://lh3.googleusercontent.com/pw/AP1GczM04KTfsVD2Ip0B77yReDqntfrby7Ts0Di6OXutY91Xlndgw5B-ac4jKdpdROpOI_JhxGYbFhXQ2NqivIA-w86RQSIFGm9fnaTqX5ZfpdAYRQZXjs3f',
      'https://lh3.googleusercontent.com/pw/AP1GczOZebfJDPgJ5LSCEeuh3w8UvSX58vQNzmF1OCUsvT2H8oL6iS-iOgCimB9ZHBNc0UfPK-_ashRdBUpnxOZ5P8lbahwltk_prTuAuyavyWV9c6kC61Ok',
      'https://lh3.googleusercontent.com/pw/AP1GczPMrbLx_WWn7irrkBB8T_b10coO_bc1d8v7PqV7366IfZCUVyS7jVPf0O7l6I10EM6NRLpruJfDfoALLMK-wkS9bXspvNK2mLhG5zJQBz4p1IGYxWrM',
      'https://lh3.googleusercontent.com/pw/AP1GczOLF1bvK_lWRzUsbsqigWj1uin7Hf8zLp9-KiIh0V50jM73YglUVIMUmZRQSFGw8allfDZzIvY9i69kuEU8cMHAWvDBboXVMFSxhXV-vKZZF41M3p_7',
      'https://lh3.googleusercontent.com/pw/AP1GczOxnP8vE99lqHlF3eN06iN4X6Kzq29ZRkivVjM--QWnDPchqDvUBwZWyhD-0kc5jrQnJQOAv50MS3zR_L_IVFkr1Ok68K9T259VlahMRq0f10uqExwP',
      'https://lh3.googleusercontent.com/pw/AP1GczM1nYVZpgtmv-0eMTAwExGMCiE6FMJXcwL-UpOmBVzuoFZk7nR0RBL5CWBosqStBV396WNZd9hOZ12dmeH5RKqJDfVDC7I2bi6Z6SBIvJs0s-XXlA1w',
      'https://lh3.googleusercontent.com/pw/AP1GczNcsP6lXpWrSwfHoDrveKACzQ-5iseIkseJXa5M6EkQDFPPLM0yzC6C6nOLWJV9Nfu1qbatQLbpjEzUf9FpZcQ2NHA_r7lLqbVcfl0l7zF1L08EjHNn',
      'https://lh3.googleusercontent.com/pw/AP1GczOw4MPd3zZQED6BrR27wX061s9o0fN_BpUY7G9td-UPiw2An2OnxuxlfpzXF3eOav-7NSGQOTJLoCwCFg1icgFnLmQ74zissKX4PLwRM26YFO0kKgSO',
      'https://lh3.googleusercontent.com/pw/AP1GczM5kkzqHSUy6VzEu-AAi3K0k7i_x-RLg70F-pyQxNYjypXEW7TOjy00tX8LV9rGkOi_aZf2xsM6vu_gOh7WqACsSH9T__FsKlsAV_ss7cDKyizIFKZO',
      'https://lh3.googleusercontent.com/pw/AP1GczODQC9JVbpnwgIskAghjo0R3OyMd1SC2iPSV8IQHoLaDUKccDr2BTzFCINT-LyJ_4Db9StgeUurUNWZatcJgcxydAjqlQkRze5fqJY46brHzkT11ByA',
      'https://lh3.googleusercontent.com/pw/AP1GczMkLHtWm7DWuhc4gPDyuDt0kHtT8CdNMsjlleVe9-Oqa2yM6rfNnBM7Q3Ru0IhBhNhukqUysyJpJSAGOcaBiRMPEHdSZMxDrqKVZMWbH1k7dLKN-IWl',
      'https://lh3.googleusercontent.com/pw/AP1GczPyDSNfgpT_cMDLf6Mfq0_e0hRdEDatP2BrL_mg1vZc4Z8NxYjxl34lAEvtcBONf84Kwez8qhqFcAvh46Z7n0QSM6oKI6y9spyMLzBKSVTJj1Q9Bibv',
      'https://lh3.googleusercontent.com/pw/AP1GczNclHvzz6I56zF1BBJD9ShQbIgad_quFMBGyQHxRZKtRfexK3DKKtVcSfcMfOxdEBOWf0DO2U-_EYLzaK4wViKWTj9wOF4uPUMjm1sWpTWaGattoQ',
      'https://lh3.googleusercontent.com/pw/AP1GczNMAU1STdocXAoVFg-TzslJZWx_pBs-NH2m-QtbOhzEtxfzn8OVfvwawaGhuTWBIhy-2DVJWid78dgE6RFwKUcTY5yYv2-VIiU6ZFr-DxgNd55kES5n',
      'https://lh3.googleusercontent.com/pw/AP1GczOtQ0u-94CPVoz4oitfS9NIJqTrSWCG4VFPT8xsQj7JzFWKvGoeEQI40xvh5zJmm4NeeEz0Wgw8SDPDAHrrvDKgJEpy5LjJAiDtKFnnVnN4nSlENHGr',
      'https://lh3.googleusercontent.com/pw/AP1GczPtFBO5X1e0huLLGyGkkn2qEaoQe3kTKVeJK_gwgMsFDLqfLeaK0wwcPF_th0AF2iilUIrAJK-42cH7ZPPkK1RMtwNudx_QALYH9TNe2X7XoZ41Mr1c',
      'https://lh3.googleusercontent.com/pw/AP1GczP6fQqeDxAk-YpElzXRu3Py5eaMx52RxxRY2t3NDhwy4hDNcaQXV1VzS9ds__XuEtu2UeXFRi06FHoFrpn0WTU2bddNf6ftEDvACRd3EXIsVmAg4aa3',
      'https://lh3.googleusercontent.com/pw/AP1GczNEFUiXSb4wFM7gYGx-1BWFZafkCN0U-yEEMLq3vay3SK_zI-dd25u8As_La90fmEpf2NtCyW209mpqdR-OG1uJgIQSEx4-RQlTRyTOhkhXZ2C3bnOq',
      'https://lh3.googleusercontent.com/pw/AP1GczNoVlbDvZ4-dsV14nRcA1xDHcnoJBvPXEqBFj1Lu5TcH9XjZ9gdb1L2vP5-m1IPijNWiZv0Ss9cQEzBG56rUQEz9klwTIKABbnilBhzyrueH3w2p_dK',
      'https://lh3.googleusercontent.com/pw/AP1GczMilv18dVaYu-o7qPhQTaw5yF5-xXbUrTLCDbT3QHFUpQSe76M2kC4AikF-piHILfzTi5BBchQJk9TWE-6h0EmPKUp21hSp4lj0ZOXgAuei03SkvSAv',
      'https://lh3.googleusercontent.com/pw/AP1GczM7uU2My4OjCBTKwMAMsceJHAESev6Ed4VFZ4NnBzS0AwxLsmI6yLy-byuEWXIWQ2_CGPHF46OjSFxtsJko4f7Eb_kPsQyx_3CK_u_4V3XDMamzFftC',
      'https://lh3.googleusercontent.com/pw/AP1GczMCYzhCEPUzRlGBkACFrIT6tM67ZyQ6mM2jIx3wPKyV9y1v9pjvJ4OteSCsx3xYwDNZmUprTei4VwPKBpb1GP3ozvsmEhTIh2hKp250xc9kS_9f5CdO',
      'https://lh3.googleusercontent.com/pw/AP1GczOiZDBEJPyrVYzNg69v6Edlyo_IS5ZVHR10Py5Je4B04OVunBOvpTIk3rfiroXmntG7o8tcAgAJqGB_2jJHZ81F35Rm75j4s7JOnHSPgtOCPE9kuT0',
      'https://lh3.googleusercontent.com/pw/AP1GczOUgA6BoMy4YJQLIyL6IUyBrlKQgSjo_6eOC9H_NcXnun35YZu-iP73-7tXd4_KFoqSrMVtepk5xnyODT0dTKTL5wYtWAaD9Bo-EiaYiBIdKru49Q',
      'https://lh3.googleusercontent.com/pw/AP1GczPlwHxxtSp8mNe3Kkp2OAS56uRC85bbiXKOba_a7U-KaNzxj1Yt6pqT2EkO29P7PMVWMejLmhKOJbgCNvGqWYPqaVwbAfxm9R0vqJnYAzU9VV9H81Fg',
      'https://lh3.googleusercontent.com/pw/AP1GczPypHE1Kr73HfQqeJNISNnhjRC_f9HuWJlJZv6duA3FjifPToY8QQHTvLfNHkVjyv1Un2OR8lPqjaoi8XumSo5KhQUJeqVHY_gCh5tkpymr7uevJvM_',
      'https://lh3.googleusercontent.com/pw/AP1GczOE7gjZuZBYIAXLV9fiRN5vyH8Nur7QF6lgdAvxHTE3Do9PWXCJtRe8Y2xsPJPnQUm5j2gtdUuZhe9TphX9zNV-bQpPiL91RxJnumHQc1gDPL7VF40A',
      'https://lh3.googleusercontent.com/pw/AP1GczMRFut7yCFvQYOW0l-KwuG54ut_9L1h-OMJjY57LjsXr-pBBE1O3g0XuAb9mjF7nNWKAH20D-Ov8Hw_vTiddJjOFUvIlgpbFrgZmY3TfrhU_LLPVQ',
      'https://lh3.googleusercontent.com/pw/AP1GczN-bUVfYU7nYbEgAOmk-n2TM5qElUNmi9g_21moKHrNPNLR6SsmsFPYLSFUpfbAIfWXSpo-cyzFCaW9sm1XOAbU2JTIsh50Civt-lVODPs8BcL-FUvi',
      'https://lh3.googleusercontent.com/pw/AP1GczPr8jBYqPmg-R9hsyPf4e-dIa0lrWxWtNd3Q8iZqbY_DjWnCaNSqU1CXFChvwPVEKhv7Xuo5TfoA-nXHZih-rgXR5XA3mskTmOnW1t8jKRc740PIp-K',
      'https://lh3.googleusercontent.com/pw/AP1GczMNph9lnPP_zdhONdDmqd7B4Du-FxdsoFRpYz7zVKkPN7zexUIkVMFt29uBpBYdiSK8RzWbf5BBg-B3vAZiRaLTKbZsmJm-8QlDT9H0ZGp79A076M5C',
      'https://lh3.googleusercontent.com/pw/AP1GczOavR4gqwfmYRYv7hSTsH_upIhob6WnDC27FEFuv8c0K2H26mrsk1o99pE3NMF_ATXPWut2XuXljGS8f37GA7m_MTpekW02xCYpbjel45fzSfFtZS62',
      'https://lh3.googleusercontent.com/pw/AP1GczMTwws5jaJ0gTeK0xhTBc8WMllFYJ4HRkej9Djv_LqSqjgq-thAHuLmCCdY2kliAEWODRZO7mKpOE4pIw5EHTU_ev1QS9amSajWsW3oFCk9syFz2jrR',
      'https://lh3.googleusercontent.com/pw/AP1GczPWAgPpxlQtL8eL9GrTQsJGKvJ3ijVmJWrKeVaqPJ1Hg-JcOGmZ8mp2h6tn_S95e2zXXaj67BFtQ_elpjX3Y9toGMOV6JmEP69bseVfWhkq5gb_Y_qD',
      'https://lh3.googleusercontent.com/pw/AP1GczMviM6lFkidNtu10E698eJGU_IBYuyjvfGVGnxgbI-giaoNke4yLZuAVwFy1Ndz4WRqExQ3aLH2_zyoxxdaRKwDNbubakUB-o9OGq9xwf0KIojVXd37'
    ]
  },
  'https://photos.app.goo.gl/7wmNLLxgYa4gAqm1A': {
    rideId: '37878',
    title: "Acme Katonah neutral grounds redux",
    dateIso: '2026-09-05',
    photoCount: 17,
    coverPhotoUrl: 'https://lh3.googleusercontent.com/pw/AP1GczNvtBOchnu_gh_ol5aCj4tcfPJY3T2lfpRAg2OXrxtDn9h4AcojKUZgzFmoobx_3ZGzHq86SrhCp8xO4cC6VPNQkTwHCzmInssl2TdAgXi0Yow9MxIA=w600-h315-p-k',
    photos: [
      'https://lh3.googleusercontent.com/pw/AP1GczNvtBOchnu_gh_ol5aCj4tcfPJY3T2lfpRAg2OXrxtDn9h4AcojKUZgzFmoobx_3ZGzHq86SrhCp8xO4cC6VPNQkTwHCzmInssl2TdAgXi0Yow9MxIA',
      'https://lh3.googleusercontent.com/pw/AP1GczNYJQz6wa_-6_eiTV7o4eASRRnJ0vIoJ4rHhmnx0dIIMoe9jnW60rNWdvaB6r3xBqIEk27HfYWh4kR1Ku1S-l9ykMbiMVcdKNsCCd_ZO769tp3fc7lT',
      'https://lh3.googleusercontent.com/pw/AP1GczNeOfj9dgzeEBVoGHc-xNPWOK1_wxJ8dYXB9vKtL2aKFV4ZzL-1PbdYiFYghpII7pxOmn0z9SAQsm4mzuad0osdpLxKAixifdUvZ7KnqUwAb_cWe5MG',
      'https://lh3.googleusercontent.com/pw/AP1GczMqmdNktqr4HGEFdGFoucdB5PHpZJdMeR4i-LZvnUndq-wowuSDWG2eDNRkmpZqLyngcK7PKBsORh1bmkmRZo6_El_z540mi7l9nEqbny6XH9VooxG7',
      'https://lh3.googleusercontent.com/pw/AP1GczO-WcTr-p-H9YxeU-gcutIbz-gGclvetpg244TW2qsYLNXAEH1JIGr47Eu7RWefJIyksv-JpIx-tlP28Y5DzWSOMeygUOFpfFDSm9OjzH9nbiJVer1Z',
      'https://lh3.googleusercontent.com/pw/AP1GczNtI9nJWQPUcdc4vuRVST1WOJ3Cyarz9ettEcrarMVHkuxrH7l8DHKWh209VtqxBc5XOv1RHS27fe7X2oTP_hJr8EFi38luDqkK7UNuNFXDSN1_0Wqa',
      'https://lh3.googleusercontent.com/pw/AP1GczMFLTF0ESDVxj5A9oVgwdAhZPApd1ZMNSoKb6thRZSNjBO4y-Y5NI6eWnM48KW6eZB80BAC7bKJeKNg1bF999yThMf7uNtOy6vs_Dhq0nFiovZoEZCz',
      'https://lh3.googleusercontent.com/pw/AP1GczNQNtW6XaPKtzxxNm0WzpfJfyXsOG-QNxizR0wL_BbKfKm4It41qHU_ccSgJQtHAM6d55BTWRsomn47D1qUPAIkHJy7R_y087A5az33-DBlQvk3Ld0',
      'https://lh3.googleusercontent.com/pw/AP1GczP8UeqUGk_Kw7i-BhaN_TYN6dFDj_tmDNivnnr60c8_5jYaCOogbIzLe9nQ3cNpfMBFICk-qrZHH7XCW2kZCGrx6d2oJs1_v7CEujg16bseUSD2_63y',
      'https://lh3.googleusercontent.com/pw/AP1GczNJsQreK4xb1iMweonRqtNvrm16h_1J4cj18W5vYx2OyjajZpC5o3M1PVmj8rFqmRffcul4TWCTdpFZNf4QsbHnSasSpAAc8DNSAau6EJrOn7ZQoA',
      'https://lh3.googleusercontent.com/pw/AP1GczO6a57qYHGz-95arKChOq_0sYhVJdVdgn6Jw675LYD3UoLY-YUqgQVLF9Esc0J1bz1KTBx9o209u379QGwkWQs_5di7yRAiqZtfQFclPsazkNRDvy1T',
      'https://lh3.googleusercontent.com/pw/AP1GczPufESVmxHY0hHUEl2hT4TT74T4S7lwy8ML1A1gRb440dkzi-1GA15FB8WCCBM7oSS2WPT9IBr0T6-2EvnlqvSrwGESI6cWeAiQiF-Snb7MfMaoJU8',
      'https://lh3.googleusercontent.com/pw/AP1GczM7nOv5D9HBk2WfWg0lYjDevI4-KZuKdFiMZwtKz2jtIrvjb99jVsjo7RodWcy3NmU-XW8NR_3P0luhfxvvXLxiC8mjvkJmkQl6GMr_g6h_D_CcCDc1',
      'https://lh3.googleusercontent.com/pw/AP1GczN_za2oxWFSY6xOeOPJrFE6F3y1nYqQbG7uVFrlHP-ylmgDX5vgjdxgMYDcs6fRQdaOds83Ecj8StL7wCnMAM5gA9rG2mhIEjkD0ArGVZE173dUxxU',
      'https://lh3.googleusercontent.com/pw/AP1GczPoZS-8KY16yfa1woSYvUE2UdZKuWwHSg7ywaKcE5oKyfp9nsCVajjgny03vw_-VYyee3SUKA_Dr4dNOYStuxpG2oacdLUS9cqcDEt2h-nQj74Y-Vnu',
      'https://lh3.googleusercontent.com/pw/AP1GczMRvLt66yOdL4HpzyJRkQfwoEDVjCvvk8kgjIjEtg9BomhVDSAKeny1DXQH_lpF-McSLTJf7yEO3vAXYvnVfCefXLulFPqwDwpMPwGjeYLui78aNg',
      'https://lh3.googleusercontent.com/pw/AP1GczPzburwtJhKOqArJcYf_9GiJEDoCcp7oU4_1cbVJV0KyQtMRIugAHEWh2go-jcSmzeO5tfccDy5V-EI50wNVM0uZZ11dcbGq6PbvKL_uj_w-s-yOw'
    ]
  },
  'https://photos.app.goo.gl/crWoj2atwxfJjgxh9': {
    rideId: '37833',
    title: "Ramapo Rally Metric Ride",
    dateIso: '2026-08-30',
    photoCount: 25,
    coverPhotoUrl: 'https://lh3.googleusercontent.com/pw/AP1GczMfNNJ9eaghe7AmcRSm8SMNV92U2CM9qOjLm7WlB8tBOGI7cPjZGlsQbaBY9t2Ge_s6gUcKEHanwq5lyifw7fumCiiGH8sDajQp8NfCI3gxGIRwHea4=w600-h315-p-k',
    photos: [
      'https://lh3.googleusercontent.com/pw/AP1GczMfNNJ9eaghe7AmcRSm8SMNV92U2CM9qOjLm7WlB8tBOGI7cPjZGlsQbaBY9t2Ge_s6gUcKEHanwq5lyifw7fumCiiGH8sDajQp8NfCI3gxGIRwHea4',
      'https://lh3.googleusercontent.com/pw/AP1GczOqXsyC2vuHronVp6IYGVUmcxikOydbsUtYQRnitkGebsb6lEAYGxVPZRvjlxqk5cczC5e6OjkvPEgcKfHZVIOc3o_cy8J0M6K-8ST5QNkX5tfpSxnL',
      'https://lh3.googleusercontent.com/pw/AP1GczPV6D5ZpWK3-1aZGPSbcfaTnaADg4NV7p8BcFQxao0bURW4Ruh_xAtQ3PvfYabrhqreLwBs7om_uILM1cElg0yeUM6MPBallpk0JKEQyx9FVoTw3ij2',
      'https://lh3.googleusercontent.com/pw/AP1GczOOP-vQ2oVsewTjXZ_sTvQzURcLsl2fvcOV3Ju5HZrdjrW053BmC7QRyvtRxUgu7rWSSbTlTFD4MqXU4_wMu380J-lKNFolIiz-Yri5D2TihvgQ2jij',
      'https://lh3.googleusercontent.com/pw/AP1GczOSwwUkRzbVOr-Lw-rVPlnRHbZfkKJs8jBiimwpjJWaOxVj0NRJgTCvZfKn7IW5sBMoQby40Qtsp7JwrI7areCwm9SMYS51O3pvl8kbiBcZkCmI94NI',
      'https://lh3.googleusercontent.com/pw/AP1GczPJDlhFnjexoZ7JdGFOrXGebiiKTdjBMTO_MUyePLws3hLLdB-QMo2y6WYN1xuxJ5xa7wqq_GHjub1OauKCJWdOW5eH7thUrgyevD3JfpqqSWUdbA2Z',
      'https://lh3.googleusercontent.com/pw/AP1GczMmT-RR6yGvRONfRu5d38uknybkKw-j5ji4Qqtc2Y3VaEet-Vu1gvWwcwCfa9jYXx3tsgzoExwi1mYmQpUtPnMoDNuQOVWtc1qqsBj0_zurq9hmOu-U',
      'https://lh3.googleusercontent.com/pw/AP1GczMz_50PeVF23wH51HdZYh43nckzg9lj8AVPsLQ6KOII-HWAbHitCYEMDdlZZEfRkgoAmDR-_wsyFU_J05MQ_14qyIFCzPadJKWYTMX2cKIzEfbxy6j_',
      'https://lh3.googleusercontent.com/pw/AP1GczOXw6KPbvblqImzc8kR_xrCH9cgngTl8hJK_j76_dpez8oDKwLtrtM1T7QggdZ0cXk14OTSNyeqC4G_d2MREc0nOB8j2RZX_tlOhlqJ2c8ytIk5Xit7',
      'https://lh3.googleusercontent.com/pw/AP1GczOBbqWRAgRn2RjQWoS66d7Akxnf_Eqs3MzVckmDHoSjbOQtLkRT993SfmsVKsuN97uZed_ZGMS2ELnci_tFxLM_nZcs0HkAsd4h-mCmwfFNmkU9f2ZH',
      'https://lh3.googleusercontent.com/pw/AP1GczNuEnN5pBHqQgLFzR5xtevhz_IwlYgjHpuZbyoYiXUTuGvJL-aHtbuWCBg-Bw32EL5SDz1Fgn6RPeyMBtuknFR12BwswaoqKKhA4SqqziV0dmm9Q6tY',
      'https://lh3.googleusercontent.com/pw/AP1GczPk_s2n2jxLSr6RaTUCDBLmjmYCwq4OLB06ldxL4iT35ULH1u7LSqm5zRPl0yrGVA5di-z-a4TvajcevltzPIiXr9EW2onQHTW5FPk-mxL67OH8x2LA',
      'https://lh3.googleusercontent.com/pw/AP1GczMnWwGjoeHcwVLU0U2xPlNMEPIGzGF_JJ3dnYK6__L9O9c6i32SvQRiP1AG7QBz52KR8PyzYkxe8fySr4DLkl0-3LpUOhYqHLTEwnxvzcYuuFOlTNTT',
      'https://lh3.googleusercontent.com/pw/AP1GczPYEEdcuiPkLY1-VWqsLCWK1iRgIwl1rpd0I-co9KESrrfHMNidCpAIGUvcy6GGyNOJ0hwQrE-8J2399rVUjcEUyI-62B0uXZ6fA8I9PdCmX50Ow_Bt',
      'https://lh3.googleusercontent.com/pw/AP1GczPKw-uKuPLXwI91O4iTVfDGxaeuY0r4k1hAuQ_RCL-nwXhCGvdUwi4kZaEr8IaPNGRmccOTy5f_RC12fIplzuow6SzD7Hv66TrGAXbLbQbp4RyusNKW',
      'https://lh3.googleusercontent.com/pw/AP1GczN2tuppv62PGfl8akb0gsNabQaUhbYYwP5EJ5jlPxNogVXUQ03WnaJFXIq8YLnigOy2cVEzY7VSjTSeVKvWjUGRctvfEG5BuDCVuF0CJPuAjIiX8jI2',
      'https://lh3.googleusercontent.com/pw/AP1GczOhLHvXx299Ps3Eg-qM0rTjgFWOtOmlI_xNB3qHkBwQ4hGk8fTCIvkPh1Tfqsf_oxs4tFYUloFh4eAaIh4k-6Y1YZgKWKBhQBHJgGGHI14wVaMRFPdT',
      'https://lh3.googleusercontent.com/pw/AP1GczNBR7gMZ_aIvtOrHleISR7JdkzrLP5__csXfJM8p7CqAHEc_1hbVtv-vd3Yk3tq14u-jHkA8vhibEskm--nHyRn01wAbNMDHmpkoiM6uU-oOgptDllJ',
      'https://lh3.googleusercontent.com/pw/AP1GczONU-HRCmNEV7Vqwy_2Y-zfVuLybIYyb5osV6cPsnazs-Bwy8UJNm-sH_I62snOtQ0ZHEwbGu372VE-FIai-rMRWA3MZQHqM4lS--F2aDTu5mz3AkCE',
      'https://lh3.googleusercontent.com/pw/AP1GczN42VOx-Awz-W9HvPylNIUQi-kP7hHlPXQ5Ow_2R7-B5gKQuNfCcpeQdpwrKNo9m8XHxQ77Kd38zYINLAU2z6M9PxAv6_Sl3c5q9VQkFXlf_CmWXd-7',
      'https://lh3.googleusercontent.com/pw/AP1GczN3MiPbZjUvt946YABSvGQFX4jdJUu06082QgD_CO9_UyXYwHtXPgbnEUz10_opKGTdeMqZFukHeSnZf5ipJxKgIMt1Iop7BZsNx3cd1T-FghQ0fDXI',
      'https://lh3.googleusercontent.com/pw/AP1GczOB0m-B_R48e8UVkw_5AyQvkhD2A-fZx_IDdOUcy0_hXx4Ae-Im9IJEdrJ8dNrapeBZycpyZYqdyDWsqEOoFWnbzUBcy9hGwC-Gk0uiKUOCcUdpQJUk',
      'https://lh3.googleusercontent.com/pw/AP1GczMF0KKPdkVAEwTmPowZk_mPQcZPwfMCpBRGt5aKpisE52JMseGm6EzLmBzRHxLvPbXdCSbXjyxuq1RQWEt8hIDOIRHiug3KQq3quVz1Ytd0UM-dJIVb',
      'https://lh3.googleusercontent.com/pw/AP1GczPu2m46P2saaC-67Wo1uvfstVIrG0yXoiPV7LVeCpnZaxZQ0oYmhlwpoa4bS-76tIPdzHhL8ahkghDkDFUnceLgat7UgbN2TOpwm74T4FPXztHlZRSd',
      'https://lh3.googleusercontent.com/pw/AP1GczOYs8rSQeyM62NYEvjMQ2rmfqyDhb0wVUVj9HOLJaYL_7RvWNgv8WlepJf-M0g1f6lVENE4CIpt3ZGTxLwT3-AqUd9fX3ajbHxhfdaE6_EyiDGu_yXw'
    ]
  },
  'https://photos.app.goo.gl/oKyCgjz3GPVG8wfHA': {
    rideId: '37721',
    title: "Morris Co. We're back!",
    dateIso: '2026-08-15',
    photoCount: 9,
    coverPhotoUrl: 'https://lh3.googleusercontent.com/pw/AP1GczOQq61SPZSJU4kLFFPZUYHK-G2-VwJVnjChBY-p4xO41rtb-_ropyJGKmExEeMH2XiwMZNTgKDstN5Xj63-TuKc4wSWsBAtCe393_KNQFKb5l5HgpZ4=w600-h315-p-k',
    photos: [
      'https://lh3.googleusercontent.com/pw/AP1GczOQq61SPZSJU4kLFFPZUYHK-G2-VwJVnjChBY-p4xO41rtb-_ropyJGKmExEeMH2XiwMZNTgKDstN5Xj63-TuKc4wSWsBAtCe393_KNQFKb5l5HgpZ4',
      'https://lh3.googleusercontent.com/pw/AP1GczOFp3tq23db5AgTekQMVyLA_tiWy5pKEVt9oT-EZ9gzjXoNcnF2n3h2Sy3LaKidxeg1FJX2bVBLGFdedznJF6YWcv6X_2R-cPT1-3hDoK7b3-Z4RzCX',
      'https://lh3.googleusercontent.com/pw/AP1GczPHa1MZ8jBJLYCWRE_Q_dlmRBBPh1ndmZSqBmmV0ztSAzAsTpZr7su9W15SSEAIm_UyJt_tZmo824qb0KhjGy6CV9N6vgqurSYcRpG2VHr0r6fqevVn',
      'https://lh3.googleusercontent.com/pw/AP1GczPRU-_PACLMmOArcQfqZQQHH7DZzblBvzy4YA31EONrhj1An99lxzR21BIv0ni-6oTrKrjJLJFR2-kXvhKOni7LJnqd61WgovwzCf11PMQBJ5PtDAWm',
      'https://lh3.googleusercontent.com/pw/AP1GczOd6I21EY9Zgsz6oMNt8TIkmlvEqiRLgicLjjFiX-sloYN08y5czaajvR9wY4B0IVi4w7RJXLNbSzP2dY7XdKKwrK8Y49x47Wh1WVIw1uLpCQ5ERjv_',
      'https://lh3.googleusercontent.com/pw/AP1GczP9LNChGQcVWfuQ5XsKIa2C3SL9UY8Uk3sJoMs7UInQVHHLwmc7VjhqYFODlseBTutCxXzO5sZIcmcDd2fRd1DUjQTZljT3JoH5ul9bqJ6Rz7c1-o9Y',
      'https://lh3.googleusercontent.com/pw/AP1GczNXj_ZIPX-Kg8ZF5BLMo9RJHYbNy6dN1OjYc9RlPbCf_tCPrZYHAPzWyB3z2CPQXe9OBfbPD-fJeE2Hq5cx5eAvzUXz6_Un8Oo8ecah8bXlmsOHe55B',
      'https://lh3.googleusercontent.com/pw/AP1GczON5hBf6TB5s0Zc8mTnAKDKsRGuTAIhWA_3TdQHsQJIKEv0rQ2Kp1sXvb__Lu6W8Glirmik9u7CN9St0fYPXp80QEnJ4YExYaaMwQ06KEpYRa0wA22p',
      'https://lh3.googleusercontent.com/pw/AP1GczNhWThsYgcgkieJTsl7884yK7pjkUaA2KTA7dbeo3ArF8qxF76WuP4CUQZIJP8OZZlL9iDJiOnHd1iQCi_SzoPf9lRMhdPX3zdGKBCn7r_-2JqtUUR9'
    ]
  },
  'https://photos.app.goo.gl/cg2FwtsnUU7V4Uqa7': {
    rideId: '37674',
    title: "A visit to New Paltz",
    dateIso: '2026-08-08',
    photoCount: 26,
    coverPhotoUrl: 'https://lh3.googleusercontent.com/pw/AP1GczPxOSuQAOKtKLUT2-Hdhma4fpqzteXpEzvAmHpyW2cY8n8Tx9nmK7DkBpfCAXTCn9BFs3dO8eBMnWHwDqJAghtVa-K2RX7l28LQ0IL31Ai_ZSjF83pe=w600-h315-p-k',
    photos: [
      'https://lh3.googleusercontent.com/pw/AP1GczPxOSuQAOKtKLUT2-Hdhma4fpqzteXpEzvAmHpyW2cY8n8Tx9nmK7DkBpfCAXTCn9BFs3dO8eBMnWHwDqJAghtVa-K2RX7l28LQ0IL31Ai_ZSjF83pe',
      'https://lh3.googleusercontent.com/pw/AP1GczNk9ng_nugBgvubMd1YfbJ_5zeURbc3zcdyV5yjMdtriLQYzig7Wn0LxIlxEhsO6ZTHOIrfSWxIoPf8gy4TPH60dQf9l-1hrnWmwsWOGmNGUnWbGOvn',
      'https://lh3.googleusercontent.com/pw/AP1GczOL2FLu0I9LboOCsVb7uhhoZ4e8qCDHj9XE0H9KmbTkGVdNKjTjWzPL7s4SSZLvOdCldGD4agT-qJJzKcHwYaF6TE7u64pBL4u8x6OnCBDJ_5XnLw',
      'https://lh3.googleusercontent.com/pw/AP1GczOc5lkPJGMmR-kcbMaGZLqIJPx4H7InlXQLR0BJDA8Il8Lv0MMLM1ahji91rDbh5p1446dLhPAzQv4h_ShxnoKYdHXxa5t2iCxZlVFczkPIdNu5eVPh',
      'https://lh3.googleusercontent.com/pw/AP1GczPZCH_5VhYjtSmoHYcVkgCNn1JLcmj8veXQB2gTn0B8jbEXSz3vAQwNg6oQl4kmz8fFl_fHNr1GSGsFtgvoMoZwk5KETRyhQDMibN86r42nUfg-vftP',
      'https://lh3.googleusercontent.com/pw/AP1GczPgdJiGGB_a_bKG1fKzafQUaRBspBvog26fV8IbB6mMN4MNle05cUQ6G2zOSpZwMAIlAsGrpG_VjxlY_brReku17yXUXV1xw7uA3kCFwBEO9T-uNSFS',
      'https://lh3.googleusercontent.com/pw/AP1GczNnk3SJQ9ilewhlSx5D3bakT1ZEDUd9c1_L49y4nvg69Uv5BYZX4JvKGYjJyRCtoRmpArLJyIxav9u1uOeXr2YjGbugJoJOgHQFthtPQlgqGo9of-TV',
      'https://lh3.googleusercontent.com/pw/AP1GczO25swYcLRNg3GPeo-lvJ_7qJz3w6gX6Umt1VCHQ1JPikmYRINR94yxQOw31IWrKWQbWclSayg_jJAEE4kuplwYumCiJcv9rAP8ELKtpTtTDFoBHWj-',
      'https://lh3.googleusercontent.com/pw/AP1GczO2Qop91x_9nIpNE7k5hjlZZLMOIbzRRfeT7lPCjm65kPpTrcd2ZHGge25H7-gh49nVXRcUdZg9IehHNEeBepcIRCy1tdEKV6GhNlQz0jYIe5xiNhSd',
      'https://lh3.googleusercontent.com/pw/AP1GczOcsTeCW6blIbLkr2w-RIPInxe4SWS20Mwdylr1NrQ2pG2F4jgn3re5W4vSaffFHK5Gu6mdN03sQMwdJ7mcgLxQ50UWHn3ezzA_m9FjiA08b_IAcnPo',
      'https://lh3.googleusercontent.com/pw/AP1GczMU3jK0uIHRlFgEV-9iwQNPFjDFPUh2U_3GQ3S96E-r65mfRSYOV6ZIiosZtkaQmOUC1aPjVWa7xYi2RrZHx6ph_41gj2aG_R-k3SykTOrKZqEGVF0P',
      'https://lh3.googleusercontent.com/pw/AP1GczP87Z_GJUUHtsee7AFuiZ71iYUuPmocbM1Jf671-a-QAA2xqrB-II2OYlwTisdGgTPU4OmIKK6KUBHFor6eV-0Z5czI9hu30mMc-8xqTnhVFFdjakqY',
      'https://lh3.googleusercontent.com/pw/AP1GczMUSkCTdRRLuXtstsFT8R7dxss_WG6BNy4dHs96MOnzYg79xui8NKJTLbBNfiRCWFFF1oFpUmh8hCVfm1AEbPI6QRc44Uc8pEXCSoQRHUbz1zF6VKu-',
      'https://lh3.googleusercontent.com/pw/AP1GczPZNlb82btTZ01k8YoWIpvdP8vN2LUYrVJ-F1R7IqLHv9CMBQ0BH8C0ZSAZBoIhVyHiBiPZZqkWFVJGUQkBdCLPP_WtxB129NhvsNmXKPCr8ve-mPvc',
      'https://lh3.googleusercontent.com/pw/AP1GczNCjLPIye24-pz_A3LBZ0eeSA6yVGMI-kJU8aSaROh9GzaNptPtd4x7bYlTq11dvtt3HRuijMXAHodrHFuJnvHCI1KjToQJM9mXsP3NwjGN1UMOeXMr',
      'https://lh3.googleusercontent.com/pw/AP1GczPW3XROmsF3u6Fc4j3gwS9rmAt6JRZjd5RjAvF1Qw5w4n_IdMqpX70vwkOMs76JSPH8XPSABbP6waB7iK5WSysJ1Y4g-AXC5AIXpyhtIv__m1E23z3O',
      'https://lh3.googleusercontent.com/pw/AP1GczMD1kSEkZ5mN2GDVaO8OVNpRrUb2Wr5B-WoJR9XUJwHSBIYM-gFX-_UK5oGH1Iu4eakl-7I_K4gjFAR6QasXmtCsdDLG9DjfKXpJxVvjGKc4du-ST47',
      'https://lh3.googleusercontent.com/pw/AP1GczMy2jdZk-IQCpUbnwiNxRJJbZTx9nekciW3ePjGqA4Y3cQK9FAM_sW8spyGeCGFm__cPQn9aVn6S9vzVAS-v7-jwc0bAbU43aAEhXCH9u8yQQOnAGFt',
      'https://lh3.googleusercontent.com/pw/AP1GczOY52SO2VDRetyJy2gy_QX0hfvedn3Wo350f95jD1xApcfv8d45dzUqx-sxArHEJ1i81U6N7hbOxmyV6G_RVDacMxiuRWaIeORjmhQ2snEInmkdswaY',
      'https://lh3.googleusercontent.com/pw/AP1GczM7e-_dM5dbJW5iypSBpC2BVpB_D8cXdmABo4xysTZ9MRVrGVcUG8stl1ZC0_FADpgO7k1V23eBwalDkiIeIzfho5pdw-6P7c_L3u2IEov8yHWvWKqO',
      'https://lh3.googleusercontent.com/pw/AP1GczOS8dALMcypZVS6ImukD_-lMmpCjoCItqiGJIGvOJXV6ZVokaR4qIooYf5nBsgFQq9mjUmROnpsLxX7cNVGH1Vz0YdZePAMyiLIbThxc36sDx__ed0U',
      'https://lh3.googleusercontent.com/pw/AP1GczOz7_gRJP2Hyr7KFXG8AqEYRK1s-aU2562xeoCzzT7VpgeuPwdoKJVwQexoPMisnjxbLZngeEpNVVz_tRdC9ArS6ZXgS_lCVzyyrmOc8KPgmvpH5xPG',
      'https://lh3.googleusercontent.com/pw/AP1GczMEIR4bKUPUmB0uczHqw_dFHS5aQUqBl858VhkYPgIhZS3Rz6b_LykLSn7lBynmDe41XXEIbwCLNYuC0MvjhuWjZKjyJtAdSkp40ByM10NoFgGISw',
      'https://lh3.googleusercontent.com/pw/AP1GczMA5okBz3k30Rnx7TULp3m34LQHDCUpFeyZSXBbQDnQ6-MYCFBNUlqOW6IM_upsmueKcxUUO3JJdVoccFTwqajqMzM5onsRZCoj7dar9OdKkdBUiDIx',
      'https://lh3.googleusercontent.com/pw/AP1GczMMG9cuGEGearoFsEVim7Hy41WkFCqUlhD94qaIHZXYm2Fo8rIEo89DkErH0Jfm9AjRqIWDjX29ub_jWdE611tU0qlIkDeb-UAMtIbL2fC6uq3vysOi'
    ]
  },
  'https://photos.app.goo.gl/RDaPftoCBLkYhdtK8': {
    rideId: '37622',
    title: "Avion! Avion!",
    dateIso: '2026-08-01',
    photoCount: 49,
    coverPhotoUrl: 'https://lh3.googleusercontent.com/pw/AP1GczO80cbjG4evdSWeRV4AunY9w_h3UJv3_T6UY44OvDC7vUG4aq0jLtImOQioVGTAaidMp0oaSbEy1lnme3JxDO8Bl6nY1PWESon8Opsz4GyWbUl7JxQr=w600-h315-p-k',
    photos: [
      'https://lh3.googleusercontent.com/pw/AP1GczO80cbjG4evdSWeRV4AunY9w_h3UJv3_T6UY44OvDC7vUG4aq0jLtImOQioVGTAaidMp0oaSbEy1lnme3JxDO8Bl6nY1PWESon8Opsz4GyWbUl7JxQr',
      'https://lh3.googleusercontent.com/pw/AP1GczMN2kMWVxXmEO9s0jfUq6XWJiotx0_Fkyr7mibl1lT-WwzVZVFLS-fKhK0ZDPJqr6lTtFSfxsHpEku3486BXiYamLFM2SODELyFFWBqRCmYdQP7af7b',
      'https://lh3.googleusercontent.com/pw/AP1GczPBdsTuMfqLooQREYIJFi0VH_fMavvaTrh0WfOFbGjG1V9vl1G8yDegilD78SOuaLGqkrutho-RJu7YJso_Zhdt9ep6Kk_75IugkNIeaChUjwouJg',
      'https://lh3.googleusercontent.com/pw/AP1GczMGBZnPHhUZ31M9th6FWcxsbUXHmewvHJNXvNXcApsr9ZEbhdWLK8ALaX9Mg_pIGHBs2lPtRri2RSKce3xtGpeTdiaZyNbF7X5FsPI_i0Y6q0CeQn9Q',
      'https://lh3.googleusercontent.com/pw/AP1GczOt3Ru6s25c9AOmI5_8_MTNB8Itfsjp-Z3ZcEBY0flD3WJsaiHajw-504OXbNRJrWvnLI1EZcTDcZdUOy1R4d-9OcmR5VS2JUISqeWGpO0ykTcwIQ',
      'https://lh3.googleusercontent.com/pw/AP1GczNuEQRxRhmWzq0ylgzYYfLbtOQve4NEGYVFNULh7s2W6xpiefIlrHEnQAmkzPBo8A1UOjPgKBhBiTXB4zgQBkZmZA93Vybh4Pn8TdQ8u5aD2VgJSw',
      'https://lh3.googleusercontent.com/pw/AP1GczO4qjUKDqcgLC6HJCbih7oc2Q9IV-CCOuyTqgB_YO17Z1bWE8bYVFQ1OvyjWUrLXxHZshnm4BIeUCWAmpCwI4Mf3RuEUgANb2FRm43RFvkYxz1e0bE7',
      'https://lh3.googleusercontent.com/pw/AP1GczNx4cTk7SJTUM8FQZH_NHLIbnw51znB68m_dj1aR2iHx-9L19FrhvHZPfxXlmKS6fSBzriQ-CAjH-7cwkayOsK-n5KEHn4sozHfayP5eqpt_03TrkGw',
      'https://lh3.googleusercontent.com/pw/AP1GczNFiBug5cs_e7SbjTVhp31IMFIeR03Tnez3nk9RCrHKC8bDiXdHTG6qk_-nWws1aMPDu2Pq_NQMOnuS0PxhWdEVufSXVlnyGOXrRBEtAc9SSVqb8w',
      'https://lh3.googleusercontent.com/pw/AP1GczNzndH_gzzpFZlOJwgv2AfHU72UNPAZcyKjYk_9T-vqwszVS_HORFKyzPTi5nf3Y3dUUoTH-riiMAqChviTEj5DzZLSwQ2Cbt8N3dG2vRNDdt_i6pRn',
      'https://lh3.googleusercontent.com/pw/AP1GczMhl04EfrFt_4aOnuZH77h4rZF7t5QhnXNzb48L5y7eopMyjv4jVnz6zZFcNK11XFbmNbSLx6Cbl7njcPRSg6xuJaoMVxVKN0taEOyF5dBUmZm39LIA',
      'https://lh3.googleusercontent.com/pw/AP1GczN0PWMz4jnXfHNzlQeaZOcaCyFSvXHf0-GmnpNEJ8xrGHA90kQvlxk6yORhozLbe9QGakq5oCf-x0gdG42nTEYispkNt2hYUZDYJNpsHaEMQXYFrvTG',
      'https://lh3.googleusercontent.com/pw/AP1GczPwbtWgSXDUR7X0dO7pn9-kqUjFUG9HBPGv4I8eG8TnOqQ8VptwtTicB-a1PE4gx_62TL24nOGGUbhzv_tRLbmjVgyGILKmqk07AoCY3FLqoucorVkY',
      'https://lh3.googleusercontent.com/pw/AP1GczMX9X6zqdCSxxH3_GL7U2BCIv3X7eytkueXr-xggMyuh4BRG_PL5INGYCcIYaPlQHu05-DT67cSVutfkxR2U_BKPJNQ2ubNQDB00h1NF7dZ67USlK0K',
      'https://lh3.googleusercontent.com/pw/AP1GczMNqNzacqE8LTim8FBChe3DRo-tQTmgzoapIJsNGeS4OH2MR7YAKZIJTXFinmLS2W75VOV7ZUqt4USA0GBnZjlJzgv811O2VXQuntcQ-0h-rGCPn-y-',
      'https://lh3.googleusercontent.com/pw/AP1GczOdk-dbo5s5BzlAa-dhD2xxnOCXiTfF_H_LJxUBGxpLr2jxsaejZZVYFqaQ7paUZKaq8rMKDSnCU51mS6SCKUDz8IysgGjyUSIBYsPfPfSgsx0v-Jl7',
      'https://lh3.googleusercontent.com/pw/AP1GczPebiUfj1hi3nyy_mYdKeWCxoRaIMDsP9eZKifyeuiMM2Olenpj98AHjrZRqO79IHBvtIPdnd9WroFMBXQtDiyhqDk0mWVuBhRipO2Bc0DlEBPhnGYz',
      'https://lh3.googleusercontent.com/pw/AP1GczPjGvZdNsTBbXAS3TXAeZguN6ZtYp97peW-C8JvvjEqp1yfj918Sq55y-o-wT8Zbs_n2K-XImetRnzt1DG8KEv0Fjpfr5jr2AzY5j6d6xX7PAeePyx_',
      'https://lh3.googleusercontent.com/pw/AP1GczPk2hFBS20oU_YyovOwLO30MqBe_6b99a5DTDxuu8YCNem0BIxyn1MZy0ktvZ5x10cOLBayDjDdalguYJDmMkGGlvXNhzG5vG7XlQDH_f0cTO7AeF-c',
      'https://lh3.googleusercontent.com/pw/AP1GczMpJ9IEvh8iiEozFqvDlTqBeZ91drzHbJgeeFWw4DFg72ytL-2q9QYwm2K9CabbWsRziUtIWNehHvEzrZf6qL6Ja74ddb-tlFSvvLX1Wxh2r36hBTdg',
      'https://lh3.googleusercontent.com/pw/AP1GczM2WrH6AQHEHGK8G0JDCR-bko3M9Wt1GKR4jp9XzmkmgQiEkNQj51OA2XhMRYr379GycbDU6jlm8cTc_Pu_h3uGXgAZwoG-_B-Tqfj8AfTnh8Jc1Rtd',
      'https://lh3.googleusercontent.com/pw/AP1GczODwBrsMu_uaXBlVa9AE4aU9cHSACni117d1vfKk8p3Qf3VyexpPiitYztuT5yoV7Seh78u58elxO9hip7Dqh4IbtARyV7R1aTcuatjSkwk_vha9pbp',
      'https://lh3.googleusercontent.com/pw/AP1GczO1WozSQP9DDiWhAjQk8W7hW7v4yV76KjDf7cbC_Jvs3_Hnu8S3SL15WnlVQ7ACdyx-Xm7MNl8p9dJTqHv9WZgS1R1NxaOz85qfNpXUDjvn-5PNPXIN',
      'https://lh3.googleusercontent.com/pw/AP1GczO7O3fVIAA5gayAreqsAzqeGTaqzzOCBI53O4lWHmj1Cvt0XaY7RaZ9MOyrGG0CV-TIPkV1FlGXwkhqiqXiUi7pBUBFWuCia3BjVoAE4t0Ix70hPqhq',
      'https://lh3.googleusercontent.com/pw/AP1GczNgeof-KP6ZCODTuVeRp3PUYiC8ge4kDMRGFvE8s7qhON-R0KmxdOWCPaHHtrpkImAyGGgORyXJN5n5V4My9fA9LJ7ShuqnRBKnqioiDDFQJhspGyxW',
      'https://lh3.googleusercontent.com/pw/AP1GczNrfic0xrbM0UdIWZ1JQchojxcU4ML9hzfdfLGRyxgjqc6-XV2HhuVU8W03UHNEN4LJzLZVg3VyLqo9VhXxtyZ0QoLZx4-DOfo1mGsLPKVcSt_9lQk4',
      'https://lh3.googleusercontent.com/pw/AP1GczM2o9GVisHQZvJdEN4yMKt9AXI4AMA-kusr9Xs53H9WQVHMtR47WEqnXL2sV_cq_AV4-CykgQaq8tL62mTTrYSqrACRb_2YNiGWx7nLiUh48pCO1FDu',
      'https://lh3.googleusercontent.com/pw/AP1GczPlnIHoNtEkBBi-xAjfn0lXGID0lCQQCRpguyrtFTAyjLUO2XjjHHajcoB3chcpL5YqyKqJgWbcWHxBgVzJs7aSFadlzlgA_aZARHLxbadjRbLI8q58',
      'https://lh3.googleusercontent.com/pw/AP1GczMuqash96guIAzRHBHb3VlhXaZvwqgo7_cAEc5hDKNFq2MsTq9nVDdctfVggjZ-tBwYQP8xs33Iskxqd-Huew0q2QaamWj566xxh7A7NUYDPrId0PsS',
      'https://lh3.googleusercontent.com/pw/AP1GczPlLK7HYfUNq5lQIN8SdFdcEYPhljrEy5lUBUFVt18WPoYZBOxKFxP0axPRLuqyziM6vqyinfEq049QZBZvZ4RY6VNFREOWi1BcrBpz57b9T0zKCEkh',
      'https://lh3.googleusercontent.com/pw/AP1GczNt6Gh-_fmwigsfTmmKxsDFrNjGQ-QNRpcz8oUm1aeiS4V-HnJ6aI_WLxE8Ll84bn7C9cjBXgfIbWpRzkHMGzsYRkmkun4N75Awud8F6Y2b4s32Or8Z',
      'https://lh3.googleusercontent.com/pw/AP1GczOTLmoP7ZwW4sSnFj6oFqdGyatIPWjFt6KMwUiD1PwQfrA86SAkE7gUuduykyEzJSZvCig_-86AsT8sH0mkiqHF0amXWBJBI05nbLVm-PeCRYMaRs8w',
      'https://lh3.googleusercontent.com/pw/AP1GczNggGfsn_jmlTw6vXVf9W2uM2SDGxoUjR6xMaJAjZTmTdy3ikEbp_5RdByp9I_uTC3HyguUI6JreayrHoHBON5oS36Gb9dY-FoThV26eslhhcXcUN13',
      'https://lh3.googleusercontent.com/pw/AP1GczPTryxSY3h0JvpGUOYqu1GUykt28rz6_3dzQTBciTDkDz43SyIMpG-XHwpTc0Xlnrn4XSfss3Ds14SGysdHXWRystCXNFBjrA4DpceGGQbmCFnmtrWj',
      'https://lh3.googleusercontent.com/pw/AP1GczOethIcWbT0GuljbqRnZat2_Vs6CNAug70cXceXDx1CFnOeYi1WeyYoJny1wfiqlJDZkGuW60m8StU6LZVn3Z1zyi-Os88IqViM24K56Z_UgpPWV1pS',
      'https://lh3.googleusercontent.com/pw/AP1GczPatHYhXaXF_AYa4zHUuRj4Kwb4dvR9d9R6gE2lZ6gt2Npi2REJ8wWrQrcfvAOa0JLg1Pj3gPVJyIastijT3Eh48E74vVW2HVNwU-uqyAQn-LF1FNo8',
      'https://lh3.googleusercontent.com/pw/AP1GczP6AMNTDR7CUKR_kvOTh-RMiApOWUh0ip8KVxUppFEhbp-yt7Zzqp1ppERIkKtUDbv0w2tZhPhlAtBs3SkBKZQx-qNmdCfrSk5ye99s6F3Cig5FefGy',
      'https://lh3.googleusercontent.com/pw/AP1GczMQK4M7nfE9W9N5lPcXrrvKpAbU4U4J2g5CAg-9vvM8K7P_HCsoWOJ7UdcmCAWxHxBM9tL1-TBPTi3CwjuEE5acwLyYqtTR8K3Mmizg9d2BWiELVHOS',
      'https://lh3.googleusercontent.com/pw/AP1GczOh7AwzBEdC8hQ2aGzpBk7qvUvq446M0PIYwmyLwOksDeYEgUVvY0bPTbu615RS10t3cXSel2FFHoWgv8Ve28_zz3h6HYCr2s7SFVSDYVgfJ_RpP-Wo',
      'https://lh3.googleusercontent.com/pw/AP1GczOuwpve8wrtLWmVpeElXDe5_eX5fehTKuvD9-QyyDryivPrX5mA91HGgx1r5Zko9chAq6BJi-OXtpxahun-W4zE9c7J9awrbi1769JxJHFcpLjDRg',
      'https://lh3.googleusercontent.com/pw/AP1GczMYxM-g_oD_w3Vp_jleesCY5zF-92_xFK-aFhF3dULSq-dplu_bNvhUnYYWJg98b9KJTOO-4BDntYA1N0vW8s94nchFtM4yBkvazaFKa2d5QDM0OBF4',
      'https://lh3.googleusercontent.com/pw/AP1GczPrUflXJkDvcBLmUCBXEwDq74zKCkITTJ9qujkeaCZJ68Qgu7yMcABgGTmFno9qSLDHjFEB9_Nfqw4r9bX2OqLi0AayltHgcdCVfsoeA56WNyMuciHd',
      'https://lh3.googleusercontent.com/pw/AP1GczNgjSOL1Q2kVkIDPlvI-WPKuO0NVZIQF4OQncXMWBz8XGe_akU1sUBQ4B-KEaOLIuXX2_2x3qnuVFVPIdmXjD1oSjV_u1SZ7RZymiIENmUFl3ryfZEt',
      'https://lh3.googleusercontent.com/pw/AP1GczPzxLUf40HZzevbyXO-gYc4_Qg0CuaoT1I-sD4Jasgrof0N5WFBCGTXwsXjU7DIzO1wuacWMZftQhsXYk93kgkID5slP5pW6XUtkkXhWTloCP1AjExy',
      'https://lh3.googleusercontent.com/pw/AP1GczMeqSeCLVLWSp2SwE0aVfyNpBknI1JmFOwXq6KIWhmIKFoahfV15UDSlOSey9_YYwb-g65f4HIpEuiiPXp8Uf0LCdMkGZeX2DZ07cKvmTRuMNJBkxBE',
      'https://lh3.googleusercontent.com/pw/AP1GczOS4fRt59VeZGZoo7a_FFoEdFz0HxM2fj--iZCHgAD_wvCdSDBm6QacOg15Lux2n8_kDrdLsreHvojj5GFSApZTmf3yUPshFdUDkkSSgmogcGmJBHG0',
      'https://lh3.googleusercontent.com/pw/AP1GczO9tvTojC4aUjlKotyfKWuH6JEz5XXe92nO8O65YrUbvcB1ghtMqlqgVmZv4aiT75LDcZnrVraKvwErlc5ttKW3WpupRs-tZEFylhL8kqQvWo2XztXv',
      'https://lh3.googleusercontent.com/pw/AP1GczO1mJlkRvxmyB73PCfAuw1pTbt2KSy1jIQaXPSOf5JDXByw5SsBoV6UAdCArt9wy4G2zclcZOhhtsHAV1fc0bzJHJ_brUxVdbizQ7ElgNEHYCs-naBi',
      'https://lh3.googleusercontent.com/pw/AP1GczNL91T9lVFjFBPiZ8CMWKcmFORNZ44_aqu4TgXAJQghkK27mQGKJfcjCvMfy1L-G33aeFmSoaL8wj5mGa2ubE3v08uVBQBe8Yk-wCsmoOCgXA-wP4qR'
    ]
  },
  'https://photos.app.goo.gl/ab5Sg4TzEm8vnDqc6': {
    rideId: '37514',
    title: "They Ride, We Ride",
    dateIso: '2026-07-25',
    photoCount: 39,
    coverPhotoUrl: 'https://lh3.googleusercontent.com/pw/AP1GczMMKxQz0vt0DcIJPNia8zBIIRt10sCs3MpT6nOZ0zl907nCNrDj5zBk0siVp2M1OKfl3YrYCrgl4RLYD2jLOu-UYdyZY0Z3xkItjDlkjSIJDuXMw6bN=w600-h315-p-k',
    photos: [
      'https://lh3.googleusercontent.com/pw/AP1GczMMKxQz0vt0DcIJPNia8zBIIRt10sCs3MpT6nOZ0zl907nCNrDj5zBk0siVp2M1OKfl3YrYCrgl4RLYD2jLOu-UYdyZY0Z3xkItjDlkjSIJDuXMw6bN',
      'https://lh3.googleusercontent.com/pw/AP1GczPcDecoE5sD3vIkoOOvJkG42GQFjZI6Gvn-cZCPcUVZ7aW9Gpk7HQrarkO1yYVkJfGFU7hmJX8kFhwCdYwhd9U4y3gqWKEXOzD3vFj8CihKYEpEA54s',
      'https://lh3.googleusercontent.com/pw/AP1GczP8Qvt1qJp6wmDPNkOxmqlnOzsp2Ql7ZBo0Ah5LgW_3zxgMZP3UEl5ZoDwrIa5GmhLhExDDuHCKk1rE5byEYRgNwXoWHwVsgEvXsU4AQekYLeiFDWM4',
      'https://lh3.googleusercontent.com/pw/AP1GczMn3gW5LWuAuKROo5wxnzmlz11ATUr3foz-yD7IERxXu-EF0ayWGOzL0Hi3dscQGv91cy0IzPVmXdfKDqbnH4Cp880aGHpk8YuLHzglNaQtC2cVvoRs',
      'https://lh3.googleusercontent.com/pw/AP1GczMNQI3dj3qRneAN9Q6ZHqOxWssJPLb1nqzb2u4CGrFVTTLMGEvl3dDdrGVzV97CIy6oUTQo9SdcMN7jc6EUf3hLQL40rTsawUVVDR_OF70YdJFjvTaf',
      'https://lh3.googleusercontent.com/pw/AP1GczOvViYcMn8hr8wW6zGhCqOQpyS9LAQoKfulg2R9FglXsJH1t32Dy060lPpitpiJxgsypLAM_xsTK3L-SjAOQc0JVyGtxC9iJwqJvlYpqXmH9N1YBq1Z',
      'https://lh3.googleusercontent.com/pw/AP1GczPqkqnJPznLFzOaYEqO-1h_Al7oss3kC1Xw6XmTW1LO9wylCKMEQppz5oB8ikApl14VqN9zJUFmgX1rW0b3HV2nVyuhMdQTK-PIwSGAkvo2do4nN6s',
      'https://lh3.googleusercontent.com/pw/AP1GczOZ9sBTo77aCJptrucGaI_hNpnMC4-4AK49t--_i2xz6hSkt83Kuf3sUtCH4r0A7Uz8yvFIj5zvNoJ5mnmDvt5oSSLhF8ouskym1S-NLF9DiG58SYcY',
      'https://lh3.googleusercontent.com/pw/AP1GczNitmUN0doIFXjpCxYalNIELPN3We13UIG5UwZxH9Uia6qqCUcPX0TqKviOcM579_QestQocg9mhb64hZGCGLwDQQ6Wn3jZGHASwQBxvHU8RsLM77s',
      'https://lh3.googleusercontent.com/pw/AP1GczO7eDptE7uZftqaNcmtRpNn5Sn7SE8V4eNNRvU-CE1d4Alg-fEM4O_53HfZuYDQbGrxU_7WPmNRQA41PldZUN4CdA__wP1bwemDzpZmZyF0Svh9NuAW',
      'https://lh3.googleusercontent.com/pw/AP1GczPof_3n9sgmZAnRh3YASTGKm2rNYdZPa5d1TaMye66H3KmQ-je31ItsjAolLKJoZykUDtF9i6ttaewDb-Gqsd2sAu-B5ky09HV4eH_calekc_IEXKeC',
      'https://lh3.googleusercontent.com/pw/AP1GczPuwys1Sf86xWnyGP1GAzSz46iFRcSxMQki4mNLiECfWr3gdHHhtCywkYtO3rP_LtaUbWCZbX7iUkHtKRJp6tCMBHNRpCQMTDQbLsnxQvv8rqWk8xjA',
      'https://lh3.googleusercontent.com/pw/AP1GczNExOkioIt91q87CWD6bGginHEnuTSWFNrLv6n_IunbVawHMMmfoinRStrnSzOkC0VI8ETh53d64rVseZ-OvH-miR2GTkcEw4gDmqJebJ07kTUiF-1g',
      'https://lh3.googleusercontent.com/pw/AP1GczM10qnkMUe_76tj2-7_NXUicTt8rWJbhLuhvpbLjHhGe2fXEfUO9Mgly2wU1mQ6xwQuy5TtQ5-r2TlV2kjpe8JO_yZfVT_mWVtN5OM8A9r5BX-nKjV5',
      'https://lh3.googleusercontent.com/pw/AP1GczNO4cjpQTMPtsM_5jxmyQdgMOXUtEznFlCOSO-WS1MeupSshzXfqSyO8HL_ymrcf15RnQQ25-bktEDc1KKqpunOUmvsw5fqGFK9EdPVNvFNymJmLuRx',
      'https://lh3.googleusercontent.com/pw/AP1GczNsUfrS67Sl3pThMeqKlWVWYWR2Q-cfxWuWZYgjpdGrrqBgICjALxQZJDXCTT-28SvogNB7b0Qv_c1_NC2AbiUPN0ZW2HYwB1LxHiAs8riT0lv0G93K',
      'https://lh3.googleusercontent.com/pw/AP1GczPzTiVpOZ98SliPx4H0-Of6niRzNZcj_pFuT4ipdbyq2gD8umb7I2CsNCBYO2usO7MBrEJgekfhzwDenD8nfNWYmS_dese2x_KHImVNqZgaUxwSACZO',
      'https://lh3.googleusercontent.com/pw/AP1GczPzWK7J8lRJRJYI25Ep_RP5Bkpu8oD19XEiJXBA1A7RkEdV9mvNcJZz_JGapbgp6qV_uF8ZKbhrUfDnJKVnwidQKUT58ibHpO3hZNxvjeBdBxbr0lCb',
      'https://lh3.googleusercontent.com/pw/AP1GczNfis8aC77Z6WjY5SikhlPwZjMWUiD-jdAVRyDiNEHdw7JQSSjO9NZ0fkHrkTyRaOohh30_xMGq6ODEGNuT6_xyBpGnFKdN0GHw8m5i7qbvdnBbvqcB',
      'https://lh3.googleusercontent.com/pw/AP1GczNgk3lEN9ZHjeQinLpziypRLXx_jzOwfON5UAcN1PISydrdcxZl95YT_kx0LCxg_yFqo2ZbH1pHfR0mi4Hpn8Bly0h0RbRRj1_gXfgRCZpgVXPxJvcS',
      'https://lh3.googleusercontent.com/pw/AP1GczMmVD0IKeyoIO6WU-hfyxo63l7CgFfyyvWgEecHqmRxIIr_Ax8miPISbfbjsBKo7A2buVWjvTZCJ44kiDKiUcIto36fH_nrAloL724E4SsygBBw8pJT',
      'https://lh3.googleusercontent.com/pw/AP1GczPw8RhWMDHSy_bGQMLNbPb2Ml-TcculbrDCSbGRJEHX1Y3GHCmSk9Va6d5rt4_zd-KNYHdQBx-H5cZiP5WNr0isSBm-NLDHPKZYzpyrsWLJNVJKwt-z',
      'https://lh3.googleusercontent.com/pw/AP1GczNbIw2LtF3b-dmPUdbz8AucpKyFeBdN5F6AQv1__zk2kl4Rmfcfw9wmZirqWe10NtJ5EaOQ4-oYuD8G5zk0bo2jcBO-nrXD0Hbal-9vzIuSbFUw_Ez2',
      'https://lh3.googleusercontent.com/pw/AP1GczMGWEHXf0lOvCVDxI29QrsxNOwFXAvsrD_Aaj0lBhyh8xMCvgz8Uo2pPHJeMgFFLB59CUBNZvRb74G9D5or9AKl79-YpYiXEvJhhF-kC43HW_Wbk4Rc',
      'https://lh3.googleusercontent.com/pw/AP1GczODnCiXnSk5lNN4Wxb3F8uh9Pd7D27k7myNyjCpQ23VAyVrW6Wegd7XQf7d2ooj77Q7KVzpjhUSsXxJocfyXpNc2tGGSwfmM0ABd3eaGRJzhk7_Eae5',
      'https://lh3.googleusercontent.com/pw/AP1GczOhk3QyRYCnv8nLcM2KhiYpueWGSctlsFH1ySU5779rBbRu0f4XdxeNsuLkLjHLh31cGkePboc-tHt3mlg3AVsV7alNPq9NEl2lAuiFUL3MGTqtxwfV',
      'https://lh3.googleusercontent.com/pw/AP1GczP_UFNwtxXNrboLm6QDdY8GM2ogf3n-2mUUTVr_Trlp81xOsiAiyqWfbJdfgE5GAELcLPDN7rFJkS0DQCgJoa7kCID2K4LwCuBNvI5A5LHWGeI-t-1E',
      'https://lh3.googleusercontent.com/pw/AP1GczMP8BfX28K7a6jnF8eQXo4jO8zdv2cyn-XHfIMvAh2-tWV7-2lRNIr_i0HAvuUveRUFTLwkS1Boo2vsXWhC3uzjTfjkM3wb7YII8AEmE685pIwqURsb',
      'https://lh3.googleusercontent.com/pw/AP1GczPjyHYwU9wqpYkcaSS4LoTybzaCwstwU9ufEWONR6lWBr43HpNJ_S8WIPG3CL7OEkG21V-lQuYfhABLdzJ7IwBivyfm0QE3P6KYqgS2ETfGKDqJRo-b',
      'https://lh3.googleusercontent.com/pw/AP1GczMOKfFq3m8Uakv2jJDcURvz__AOQBPcAPDbHfhNIRn4dk7smeaM9uHcgsirSW1_jU7x0uV0sS6JvQRu0xiAHnxx0nGiFAbEcy_UC7wmgGgmCVvfp8YU',
      'https://lh3.googleusercontent.com/pw/AP1GczNtn2sUUZwvJld1AU3mJ9X3X7tf6hwiAJkeb8ryYZo6X5UFaJjhVdB3R6MuWuBRs1EMyTdYQ2qPFCmebXB6fLnYcaAqZb1uBtCW0Ur0sWEgGcfF2cSe',
      'https://lh3.googleusercontent.com/pw/AP1GczOX78Qvz_Fc0r2JvS3NC_vQSlbaBoSU_95VME-W5jWNGjWRUgW46AC3roAJ-iFykRDch4lyz2XotshEFJMaTgKqUQN4b6EDu5UigYd-9YITQvCoq8Q0',
      'https://lh3.googleusercontent.com/pw/AP1GczOCgyRBQ2ATaB1XrxX8lLOkhY_heLbvUWi2YduXC7HpDZko5bRJGpEHYH7mo_Jd_4ZXRtMBpZVp-IyOvqnR0cQXxck88EUNUO6HcOyy9yOe79Rq0lql',
      'https://lh3.googleusercontent.com/pw/AP1GczPEUeKLOJcPP61t4gFNbklvyIPfSxAHFydIZwwSpfsHFia4UE4mF6gGeZcRkiczHK-g086DEJz7D8fmYHCIU-JW4sBM5km47YEEPLrRMCypaY9-jrJ2',
      'https://lh3.googleusercontent.com/pw/AP1GczOK8YqF6n9ABh5eiAAz6dHa0kfhmw-jP3JJq7Ccqh0Tnv6xT6J1PIr92IRbITM1tjYD_UsSVo3A8J4EkYAZiflDrteICDBDtpXmQ4J5AqNTh-HHVcAS',
      'https://lh3.googleusercontent.com/pw/AP1GczP2Fk-TlPbRi1vdSyD0ps4UAFfISPEwCd6turHoiOrdw3hxDcyu59qIE9iKVjSYTLiFGiHJSAdxxQ36u8Bu0m2tK8kq-GRyEJ_saYAmH7MwX6Y2Ok7L',
      'https://lh3.googleusercontent.com/pw/AP1GczMMg0jFOxXDs8X5hBe8FOJZ5TqnM80mw7LpenteQg7EwrzjYvCziKGKO7utz7MUAYrC1FqVuGU55roWEvGN2YC32tJV0NtfP_MCkkOtmi7m9u1P0z9D',
      'https://lh3.googleusercontent.com/pw/AP1GczMhlxSrk6SXce4YWNBCynKebOjfCSiGrfO9CF250ZR3L5lPuMC4JBG_I_QxTGwY4FEeVCm4CT--oj6PuNLwsmLCGTLMoocOFKDqBTQMXbqxg_QHzMU',
      'https://lh3.googleusercontent.com/pw/AP1GczM3AaK1-CHuwV9kTEkm2V_B4zgsyy3Od07Ob6P2iiY-qRPIt8ET4V7Qa0RKizRgyx41xt6AbWTAVLrU3YPdb7h1xsMHkzDGgTlyVjsDzGVhQq9cUnQ'
    ]
  },
  'https://photos.app.goo.gl/n2EwWB85dRN5bFcM9': {
    dateIso: '2025-09-27',
    photoCount: 64,
    coverPhotoUrl: 'https://lh3.googleusercontent.com/pw/AP1GczP3JC4jGB0jpOj36J2oZ5OjXghCXDXkxH5AMNAotJLPelqFBcMvuw98oRGnS6oi8GIZl4QRZQbRP7zTWhUMHqrJNSg79xzKSkGA0Ip9H5-CF6LXvkuo=w600-h315-p-k'
  },
  'https://photos.app.goo.gl/YQnTmfYS9WcQrdaCA': {
    dateIso: '2025-10-11',
    photoCount: 35,
    coverPhotoUrl: 'https://lh3.googleusercontent.com/pw/AP1GczNRcsk0lnF2uZPjBVMsplIMlgKhZ41zjNlNOO6dPwbuit4e9k9HEy-8qsg4H7jmyqvppa2okVy7al_gQUnY51HsDLVXtgJrYGaIZRRdMiDbWA3G2YJB=w600-h315-p-k'
  },
  'https://photos.app.goo.gl/r8yyKyA1ov9EMPGD9': {
    dateIso: '2025-10-18',
    photoCount: 13,
    coverPhotoUrl: 'https://lh3.googleusercontent.com/pw/AP1GczO0pw2qWcnhSlfEPreO5XiWSGF709gJyK1okBKm_rbHbh89jWDMo_bMUcpP-Phtps3Kt1Pdpfc9iejIB9skYcvB1iO6-UzVC0KRe_F2ab73EgVoWv-c=w600-h315-p-k'
  },
  'https://photos.app.goo.gl/6VcFE6MBhiL1pT5Z7': {
    dateIso: '2025-11-01',
    photoCount: 33,
    coverPhotoUrl: 'https://lh3.googleusercontent.com/pw/AP1GczNiVOzlPv2XUPgAPo-ne02rZWJq-FdVttL_xSX8RzD_IYWEom7AqGVP2JoViU7XJWSi9GguC_XLVEcorQJXis_JUMjSiAE1IU-E7v6DyfS4zrV9he-x=w600-h315-p-k'
  },
  'https://photos.app.goo.gl/KPJa58dYZQfwDdkD8': {
    dateIso: '2026-10-11',
    photoCount: 171,
    coverPhotoUrl: 'https://lh3.googleusercontent.com/pw/AP1GczPjmA3_tCclhM682_T2sB8R3K6vP8H4dC8yJq7D1E7sX8n5wB2V1M6p9L0k=w600-h315-p-k'
  }
};

// Verified Google Photos Album Covers Pre-Index
export const ALBUM_COVERS_MAP = {
  '2026-09-26': 'https://lh3.googleusercontent.com/pw/AP1GczP2hF8HQTNssv6KHgA-lwDRVESO1z5RV5rRs86lOF1Tu9WMfFo6fXdWyAHcAkNVfQZm1q-oNoUR7ATn1FI2Qun5tideFdbAI0lYKH9XWWHugIYkjgMy=w600-h315-p-k',
  '2026-09-19': 'https://lh3.googleusercontent.com/pw/AP1GczNA5F5FMOqXxk43GINsCO5xLFjw96adErWgu3w33-VmUjnLRFrY7rrdh6BqQCdO7hS-eUs8qhHG0rboTJdDbjzF6EHc5H5eL-LspJPqctXivv8qnPOk=w600-h315-p-k',
  '2026-09-12': 'https://lh3.googleusercontent.com/pw/AP1GczOxsCq3H4RhzJ7JNvBTYqNctalibdpksOvDYPg9SU0VU4x7pjSomLz7cTAPxeGDWuoksRW6XKQ34LQR9q6_A2Zdzk3bcH9_uDPdmnQRlM51UvrOhqWI=w600-h315-p-k',
  '2026-09-05': 'https://lh3.googleusercontent.com/pw/AP1GczNvtBOchnu_gh_ol5aCj4tcfPJY3T2lfpRAg2OXrxtDn9h4AcojKUZgzFmoobx_3ZGzHq86SrhCp8xO4cC6VPNQkTwHCzmInssl2TdAgXi0Yow9MxIA=w600-h315-p-k',
  '2026-08-30': 'https://lh3.googleusercontent.com/pw/AP1GczMfNNJ9eaghe7AmcRSm8SMNV92U2CM9qOjLm7WlB8tBOGI7cPjZGlsQbaBY9t2Ge_s6gUcKEHanwq5lyifw7fumCiiGH8sDajQp8NfCI3gxGIRwHea4=w600-h315-p-k',
  '2026-08-16': 'https://lh3.googleusercontent.com/pw/AP1GczMfsJaIvfuUfpVrlW5gG5ivJHdWTRuc_wpletaHg9e0JVtz7QhTdxaZMTb6Ah1gDJEaeaDO50m55nhyFRSFNKNOYyS3_GQNLwOjwjamhfruBPL_nAeC=w399-h315-p-k',
  '2026-08-15': 'https://lh3.googleusercontent.com/pw/AP1GczOQq61SPZSJU4kLFFPZUYHK-G2-VwJVnjChBY-p4xO41rtb-_ropyJGKmExEeMH2XiwMZNTgKDstN5Xj63-TuKc4wSWsBAtCe393_KNQFKb5l5HgpZ4=w600-h315-p-k',
  '2026-08-08': 'https://lh3.googleusercontent.com/pw/AP1GczPxOSuQAOKtKLUT2-Hdhma4fpqzteXpEzvAmHpyW2cY8n8Tx9nmK7DkBpfCAXTCn9BFs3dO8eBMnWHwDqJAghtVa-K2RX7l28LQ0IL31Ai_ZSjF83pe=w600-h315-p-k',
  '2026-08-01': 'https://lh3.googleusercontent.com/pw/AP1GczO80cbjG4evdSWeRV4AunY9w_h3UJv3_T6UY44OvDC7vUG4aq0jLtImOQioVGTAaidMp0oaSbEy1lnme3JxDO8Bl6nY1PWESon8Opsz4GyWbUl7JxQr=w600-h315-p-k',
  '2026-07-25': 'https://lh3.googleusercontent.com/pw/AP1GczMMKxQz0vt0DcIJPNia8zBIIRt10sCs3MpT6nOZ0zl907nCNrDj5zBk0siVp2M1OKfl3YrYCrgl4RLYD2jLOu-UYdyZY0Z3xkItjDlkjSIJDuXMw6bN=w600-h315-p-k',
  '2025-11-01': 'https://lh3.googleusercontent.com/pw/AP1GczNiVOzlPv2XUPgAPo-ne02rZWJq-FdVttL_xSX8RzD_IYWEom7AqGVP2JoViU7XJWSi9GguC_XLVEcorQJXis_JUMjSiAE1IU-E7v6DyfS4zrV9he-x=w600-h315-p-k',
  '2025-10-18': 'https://lh3.googleusercontent.com/pw/AP1GczO0pw2qWcnhSlfEPreO5XiWSGF709gJyK1okBKm_rbHbh89jWDMo_bMUcpP-Phtps3Kt1Pdpfc9iejIB9skYcvB1iO6-UzVC0KRe_F2ab73EgVoWv-c=w600-h315-p-k',
  '2025-10-11': 'https://lh3.googleusercontent.com/pw/AP1GczNRcsk0lnF2uZPjBVMsplIMlgKhZ41zjNlNOO6dPwbuit4e9k9HEy-8qsg4H7jmyqvppa2okVy7al_gQUnY51HsDLVXtgJrYGaIZRRdMiDbWA3G2YJB=w600-h315-p-k',
  '2025-09-27': 'https://lh3.googleusercontent.com/pw/AP1GczP3JC4jGB0jpOj36J2oZ5OjXghCXDXkxH5AMNAotJLPelqFBcMvuw98oRGnS6oi8GIZl4QRZQbRP7zTWhUMHqrJNSg79xzKSkGA0Ip9H5-CF6LXvkuo=w600-h315-p-k'
};

/**
 * Hydrates missing photoCount or coverPhotoUrl from authoritative registry.
 */
export function hydrateRidePhotoMetadata(ride) {
  if (!ride) return;
  const albumUrl = ride.photoAlbumUrl ? String(ride.photoAlbumUrl).trim() : '';
  const rideIdStr = ride.rideId ? String(ride.rideId).trim() : (ride.id ? String(ride.id).trim() : '');
  const dKey = ride.dateIso || (ride.dateStr && ride.dateStr.match(/\d{4}-\d{2}-\d{2}/)?.[0]);

  const known = (albumUrl && KNOWN_ALBUMS_MAP[albumUrl])
    || (rideIdStr && Object.values(KNOWN_ALBUMS_MAP).find(k => k.rideId && String(k.rideId).trim() === rideIdStr))
    || (!rideIdStr && !albumUrl && dKey && Object.values(KNOWN_ALBUMS_MAP).find(k => k.dateIso === dKey));

  if (known) {
    if (!ride.photoCount || ride.photoCount <= 0) {
      ride.photoCount = known.photoCount;
    }
    if (!ride.coverPhotoUrl) {
      ride.coverPhotoUrl = known.coverPhotoUrl;
    }
    if (Array.isArray(known.photos) && known.photos.length > 0) {
      ride.photos = [...known.photos];
    }
  }

  if (!ride.coverPhotoUrl && !rideIdStr && dKey && ALBUM_COVERS_MAP[dKey]) {
    ride.coverPhotoUrl = ALBUM_COVERS_MAP[dKey];
  }
}

/**
 * Reconciles a cached ride with authoritative build metadata before initial DOM paint.
 * Guarantees zero reload flicker if cached counts differ from build-verified counts.
 */
export function reconcileCachedRideWithBuildMetadata(ride) {
  if (!ride) return;
  const albumUrl = ride.photoAlbumUrl ? String(ride.photoAlbumUrl).trim() : '';
  const rideIdStr = ride.rideId ? String(ride.rideId).trim() : (ride.id ? String(ride.id).trim() : '');
  const dKey = ride.dateIso || (ride.dateStr && ride.dateStr.match(/\d{4}-\d{2}-\d{2}/)?.[0]);

  const known = (albumUrl && KNOWN_ALBUMS_MAP[albumUrl])
    || (rideIdStr && Object.values(KNOWN_ALBUMS_MAP).find(k => k.rideId && String(k.rideId).trim() === rideIdStr))
    || (!rideIdStr && !albumUrl && dKey && Object.values(KNOWN_ALBUMS_MAP).find(k => k.dateIso === dKey));

  if (known) {
    ride.photoCount = Math.max(ride.photoCount || 0, known.photoCount || 0);
    if (!ride.coverPhotoUrl) {
      ride.coverPhotoUrl = known.coverPhotoUrl;
    }
  }

  // Authoritative Video Count Pre-Paint Synchronization
  // Guarantees cached cards reflect at least the statically cataloged videos before network resolution
  if (ride && ride.title && /boonton/i.test(ride.title)) {
    const staticBoontonVids = PLAYLIST_VIDEOS.filter(v => /boonton/i.test(v.title));
    if (staticBoontonVids.length > 0 && (!Array.isArray(ride.matchedVideos) || ride.matchedVideos.length < staticBoontonVids.length)) {
      ride.matchedVideos = staticBoontonVids;
    }
  }
}

/**
 * Proactively verifies and synchronizes media count for a single ride on-demand / interaction (JIT SWR).
 */
export async function proactiveSyncRideMedia(ride, force = false) {
  if (!ride) return;
  const now = Date.now();
  if (!force && ride._lastJitSyncAt && (now - ride._lastJitSyncAt < 5 * 60 * 1000)) {
    return; // Fresh within 5 mins unless forced
  }
  ride._lastJitSyncAt = now;

  const rawId = String(ride.rideId || '').trim();
  const fullId = String(ride.id || '').trim();
  const targetRideId = String(ride.rideId || ride.id || '').replace(/^btcnj-/, '').trim();
  const idMatches = Array.from(new Set([
    fullId,
    rawId,
    targetRideId,
    rawId ? `btcnj-${rawId}` : null
  ].filter(Boolean)));
  const selectorList = idMatches.map(id => `[data-ride-id="${id}"], [data-card-id="${id}"]`).join(', ');

  // 1. YouTube Video Count Reconciliation & Badge Verification
  try {
    const allOverrides = (typeof getVideoOverrides === 'function') ? getVideoOverrides() : {};
    const overrideKey = String(ride.rideId || ride.id);
    const override = allOverrides[overrideKey] || (ride.rideId && allOverrides[String(ride.rideId)]);

    const autoList = ride._autoMatchedVideos || (Array.isArray(ride.matchedVideos) ? [...ride.matchedVideos] : []);
    if (!ride._autoMatchedVideos && autoList.length > 0) {
      ride._autoMatchedVideos = [...autoList];
    }

    if (override && ride._autoMatchedVideos) {
      const prevVideoCount = Array.isArray(ride.matchedVideos) ? ride.matchedVideos.length : 0;
      ride.matchedVideos = reconcileRideVideos(ride._autoMatchedVideos, override);
      const newVideoCount = Array.isArray(ride.matchedVideos) ? ride.matchedVideos.length : 0;
      const countChanged = newVideoCount !== prevVideoCount;

      if (typeof currentLoadedRides !== 'undefined' && Array.isArray(currentLoadedRides)) {
        const memRide = currentLoadedRides.find(r => r.id === ride.id || (ride.rideId && r.rideId === ride.rideId));
        if (memRide) {
          memRide.matchedVideos = ride.matchedVideos;
          if (ride._autoMatchedVideos) memRide._autoMatchedVideos = ride._autoMatchedVideos;
        }
        if (countChanged) {
          setCachedMediaRides(currentLoadedRides);
        }
      }

      if (typeof document !== 'undefined') {
        const rawEls = Array.from(document.querySelectorAll(selectorList));
        const cardEls = Array.from(new Set(
          rawEls.map(el => (el && typeof el.closest === 'function' && el.closest('.media-ride-card, .photo-center-card, .media-list-row')) || el)
        ));
        cardEls.forEach(cardEl => {
          const videoSection = cardEl.querySelector('.matched-videos-section');
          if (videoSection) {
            videoSection.setAttribute('data-video-count', String(newVideoCount));
            const countLabel = videoSection.querySelector('.videos-count-label');
            if (countLabel) {
              const isDual = cardEl.querySelector('.media-photo-spotlight') !== null || Boolean(ride.photoAlbumUrl);
              if (isDual) {
                countLabel.innerHTML = `(${newVideoCount})`;
              } else {
                countLabel.innerHTML = `<span class="media-qty">${newVideoCount}</span> <span class="media-label">${newVideoCount > 1 ? 'Videos' : 'Video'}</span>`;
              }
              if (countChanged) {
                countLabel.classList.add('badge-highlight-pulse');
                setTimeout(() => countLabel.classList.remove('badge-highlight-pulse'), 800);
              }
            }
          }
        });
      }
    }
  } catch (vErr) {
    console.warn('Video SWR sync error:', vErr);
  }

  // 2. Google Photos Album Count Verification
  if (!ride.photoAlbumUrl) return;

  try {
    const url = `${CONFIG.SYNC_SINGLE_MEDIA_API_URL}?rideId=${encodeURIComponent(targetRideId)}&photoAlbumUrl=${encodeURIComponent(ride.photoAlbumUrl)}`;
    const res = await fetch(url, { signal: AbortSignal.timeout(6000) });
    if (!res.ok) return;
    const contentType = res.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) return;

    const data = await res.json();
    if (data && data.success && typeof data.photoCount === 'number') {
      const countChanged = data.photoCount !== ride.photoCount;
      ride.photoCount = data.photoCount;
      ride._lastJitSyncAt = Date.now();
      if (data.coverPhotoUrl) ride.coverPhotoUrl = data.coverPhotoUrl;

      // Synchronize in-memory catalog and localStorage cache
      if (typeof currentLoadedRides !== 'undefined' && Array.isArray(currentLoadedRides)) {
        const memRide = currentLoadedRides.find(r => r.id === ride.id || (ride.rideId && r.rideId === ride.rideId));
        if (memRide) {
          memRide.photoCount = data.photoCount;
          memRide._lastJitSyncAt = Date.now();
          if (data.coverPhotoUrl) memRide.coverPhotoUrl = data.coverPhotoUrl;
        }
        if (countChanged) {
          setCachedMediaRides(currentLoadedRides);
        }
      }

      // Self-heal mediaOverrides (localStorage + Firestore) when an overridden/promoted card had 0 or stale count
      if (data.photoCount > 0 && typeof getMediaOverrides === 'function') {
        const mediaOverrides = getMediaOverrides();
        const candidateKeys = [ride.id, ride.rideId, targetRideId, ride.dateIso].filter(Boolean).map(String);
        let matchedOverrideKey = candidateKeys.find(k => Boolean(mediaOverrides[k]));
        if (!matchedOverrideKey && typeof ride.id === 'string' && ride.id.startsWith('cluster-')) {
          const stemPrefix = ride.id.replace(/-(?:\d{4}-\d{2}-\d{2}|undated)$/, '');
          matchedOverrideKey = Object.keys(mediaOverrides).find(
            k => k.replace(/-(?:\d{4}-\d{2}-\d{2}|undated)$/, '') === stemPrefix
          );
        }
        if (matchedOverrideKey && mediaOverrides[matchedOverrideKey]) {
          const ov = mediaOverrides[matchedOverrideKey];
          if (ov.photoAlbumUrl === ride.photoAlbumUrl && Number(ov.photoCount || 0) !== data.photoCount) {
            const patch = { photoCount: data.photoCount };
            if (!ov.coverPhotoUrl && data.coverPhotoUrl) {
              patch.coverPhotoUrl = data.coverPhotoUrl;
            }
            saveMediaOverride(matchedOverrideKey, patch).catch(() => {});
          }
        }
      }

      // Smoothly update badge in the DOM if currently visible
      if (typeof document !== 'undefined') {
        const rawEls = Array.from(document.querySelectorAll(selectorList));
        const cardEls = Array.from(new Set(
          rawEls.map(el => (el && typeof el.closest === 'function' && el.closest('.media-ride-card, .photo-center-card, .media-list-row')) || el)
        ));

        cardEls.forEach(cardEl => {
          // 1. Media Center Feed & Spotlight Card Count Badge
          const countSpan = cardEl.querySelector('.photo-spotlight-count');
          if (countSpan) {
            const titleEl = cardEl.querySelector('.photo-spotlight-title');
            const isDual = cardEl.querySelector('.matched-videos-section') !== null
              || Boolean(titleEl && titleEl.textContent && titleEl.textContent.trim() === 'Photos')
              || Boolean(Array.isArray(ride.matchedVideos) && ride.matchedVideos.length > 0);
            if (isDual) {
              countSpan.textContent = `(${data.photoCount})`;
            } else {
              countSpan.innerHTML = data.photoCount > 0
                ? `<span class="media-qty">${data.photoCount}</span> <span class="media-label">${data.photoCount === 1 ? 'Photo' : 'Photos'}</span>`
                : `<span class="media-label">Photos</span>`;
            }
            countSpan.classList.add('badge-highlight-pulse');
            setTimeout(() => countSpan.classList.remove('badge-highlight-pulse'), 800);
          } else {
            const spotlightRight = cardEl.querySelector('.photo-spotlight-right');
            const glyph = cardEl.querySelector('.photo-external-glyph');
            if (spotlightRight) {
              const newBadge = document.createElement('span');
              newBadge.className = 'photo-spotlight-count badge-highlight-pulse';
              newBadge.textContent = `(${data.photoCount})`;
              if (glyph) {
                spotlightRight.insertBefore(newBadge, glyph);
              } else {
                spotlightRight.appendChild(newBadge);
              }
              setTimeout(() => newBadge.classList.remove('badge-highlight-pulse'), 800);
            }
          }

          // 2. Google Photos Anchor Title Tooltip
          const albumAnchor = cardEl.querySelector('a.media-photo-spotlight');
          if (albumAnchor) {
            const countLabel = data.photoCount === 1 ? '1 Photo' : `${data.photoCount} Photos`;
            albumAnchor.title = `Open Google Photos Album (${countLabel})`;
          }

          // 3. Photo Center Action Button / List Pill (if active)
          const photoActionBadge = cardEl.querySelector('.photo-action-btn, .photo-list-action, .btn-open-album');
          if (photoActionBadge) {
            const isList = photoActionBadge.classList.contains('photo-list-action') || photoActionBadge.classList.contains('btn-sm');
            const text = isList ? `\u2197 Album (${data.photoCount})` : `\u2197 Open Album (${data.photoCount})`;
            const innerSpan = photoActionBadge.querySelector('span');
            if (innerSpan) {
              innerSpan.textContent = text;
            } else {
              photoActionBadge.textContent = text;
            }
            photoActionBadge.classList.add('badge-highlight-pulse');
            setTimeout(() => photoActionBadge.classList.remove('badge-highlight-pulse'), 800);
          }

          // 4. Photo Center & Media Center Card Cover Image Hot-Patch
          const photoCardImgs = cardEl.querySelectorAll('.photo-card-img, .photo-list-thumb img, .card-media-img');
          if (photoCardImgs.length > 0 && data.coverPhotoUrl) {
            photoCardImgs.forEach(img => {
              const isCompact = Boolean(img.closest('.photo-list-thumb'));
              const isSpot = Boolean(cardEl.classList.contains('is-spotlight'));
              img.src = getOptimizedAlbumCoverUrl(
                { ...ride, coverPhotoUrl: data.coverPhotoUrl },
                isCompact ? 160 : (isSpot ? 1920 : 600),
                isCompact ? 160 : (isSpot ? 1080 : 338)
              );
            });
          }
        });
      }
    }
  } catch (_) {}
}

let _lastPhotoBgSyncAt = Date.now();

/**
 * Evaluates whether a ride falls within the active hot window (-30d to +14d).
 * Rides outside this window are cold/historical and read from pre-compiled system_cache.
 * @param {Object} ride 
 * @param {number} [nowMs=Date.now()] 
 * @returns {boolean}
 */
export function isHotRide(ride, nowMs = Date.now()) {
  if (!ride || !ride.dateIso) return true; // Default to true if no date
  const rideMs = new Date(ride.dateIso).getTime();
  if (isNaN(rideMs)) return true;
  const diffDays = (rideMs - nowMs) / (1000 * 60 * 60 * 24);
  return diffDays >= -30 && diffDays <= 14;
}

/**
 * Resolves the temporal status tier for a future-dated ride:
 * - 'soon': Scheduled between today and the immediate upcoming Sunday (inclusive, covering weekdays & weekend)
 * - 'later': Scheduled strictly after the immediate upcoming Sunday
 * - null: Scheduled in the past (historical, not pending)
 * 
 * @param {Object} ride 
 * @param {Date|string} [refDate=new Date()] 
 * @returns {'soon'|'later'|null}
 */
export function getRideTemporalTier(ride, refDate = new Date()) {
  if (!ride) return null;
  const rawDate = ride.dateIso || (ride.date && String(ride.date).match(/\d{4}-\d{2}-\d{2}/)?.[0]) || (ride.dateStr && String(ride.dateStr).match(/\d{4}-\d{2}-\d{2}/)?.[0]);
  if (!rawDate) return null;

  const now = refDate instanceof Date ? refDate : new Date(refDate);
  const nowYear = now.getFullYear();
  const nowMonth = String(now.getMonth() + 1).padStart(2, '0');
  const nowDay = String(now.getDate()).padStart(2, '0');
  const todayIso = `${nowYear}-${nowMonth}-${nowDay}`;

  // If ride date is before today, it is historical (not pending)
  if (rawDate < todayIso) return null;

  // If ride date is today, it is 'today' (supersedes soon and later)
  if (rawDate === todayIso) return 'today';

  // Calculate upcoming Sunday boundary in local calendar
  const dayOfWeek = now.getDay(); // 0 = Sun, 1 = Mon, ..., 6 = Sat
  const daysUntilSunday = dayOfWeek === 0 ? 0 : (7 - dayOfWeek);

  const sundayDate = new Date(nowYear, now.getMonth(), now.getDate() + daysUntilSunday);
  const sunYear = sundayDate.getFullYear();
  const sunMonth = String(sundayDate.getMonth() + 1).padStart(2, '0');
  const sunDay = String(sundayDate.getDate()).padStart(2, '0');
  const sundayIso = `${sunYear}-${sunMonth}-${sunDay}`;

  if (rawDate <= sundayIso) {
    return 'soon';
  } else {
    return 'later';
  }
}

/**
 * Builds HTML markup for the streamlined monolithic date & proximity capsule.
 * Combines temporal proximity status and ride date into a single, high-contrast
 * unnested pill box with zero internal sub-framing.
 *
 * @param {Object} ride
 * @param {'soon'|'later'|null} tier
 * @param {string} [extraClass='']
 * @returns {string}
 */
export function buildUnifiedDateCapsuleHtml(ride, tier, extraClass = '') {
  if (!ride) return '';
  const dateStr = formatRideDate(ride.dateIso || ride.dateStr);
  const classes = ['unified-date-capsule', extraClass];
  if (!tier) {
    return `<span class="${classes.filter(Boolean).join(' ')}"><span class="capsule-date-text">${escapeHtml(dateStr)}</span></span>`;
  }
  const isToday = tier === 'today';
  const isSoon = tier === 'soon';
  const label = isToday ? 'TODAY' : (isSoon ? 'SOON' : 'LATER');
  const title = isToday ? 'Happening today!' : (isSoon ? 'Happening soon in current cycle' : 'Scheduled for future date');
  const tierClass = isToday ? 'tier-today' : (isSoon ? 'tier-soon' : 'tier-later');
  classes.push('is-pending', tierClass, 'pending-ride-pill');

  return `<span class="${classes.filter(Boolean).join(' ')}" role="status" aria-label="${label}: ${escapeHtml(dateStr)}" title="${title}"><span class="pending-pulse-dot" aria-hidden="true"></span><span class="capsule-status-text">${label}</span><span class="capsule-dot-sep" aria-hidden="true">·</span><span class="capsule-date-text">${escapeHtml(dateStr)}</span></span>`;
}

/**
 * Builds HTML markup for the pending ride indicator pill.
 * @param {'soon'|'later'} tier 
 * @param {string} [extraClass=''] 
 * @returns {string}
 */
export function buildPendingBadgeHtml(tier, extraClass = '') {
  if (!tier) return '';
  const isToday = tier === 'today';
  const isSoon = tier === 'soon';
  const label = isToday ? 'TODAY' : (isSoon ? 'SOON' : 'LATER');
  const title = isToday ? 'Pending: Happening today!' : (isSoon ? 'Pending: Happening soon in the current cycle' : 'Pending: Scheduled for a future date');
  const tierClass = isToday ? 'tier-today' : (isSoon ? 'tier-soon' : 'tier-later');
  const classes = ['pending-ride-pill', tierClass, extraClass].filter(Boolean).join(' ');

  return `<span class="${classes}" role="status" aria-label="${title}" title="${title}"><span class="pending-pulse-dot" aria-hidden="true"></span><span class="pending-pill-text">${label}</span></span>`;
}

/**
 * Synchronizes photo counts for the most recent and upcoming rides with active albums.
 * Scopes unforced background sync to the active hot window (-30d..+14d) to eliminate unnecessary reads.
 * @param {boolean} [force=false] If true, bypasses recency throttling and runs immediate parallel sweep.
 * @returns {Promise<any>}
 */
export function syncActivePhotoCounts(force = false) {
  if (!currentLoadedRides || !Array.isArray(currentLoadedRides) || currentLoadedRides.length === 0) return Promise.resolve();
  _lastPhotoBgSyncAt = Date.now();

  const albumRides = currentLoadedRides.filter(r => r && r.photoAlbumUrl && typeof r.photoAlbumUrl === 'string' && r.photoAlbumUrl.trim().length > 0 && !r.isDeleted);
  if (albumRides.length === 0) return Promise.resolve();

  // If not forced, filter to active hot-window rides (-30d to +14d) to eliminate cold reads
  const candidateRides = force ? albumRides : albumRides.filter(r => isHotRide(r));
  const finalRides = candidateRides.length > 0 ? candidateRides : albumRides.slice(0, 4);

  // Prioritize upcoming and recent rides
  const sortedRides = [...finalRides].sort((a, b) => {
    const da = a.dateIso ? new Date(a.dateIso).getTime() : 0;
    const db = b.dateIso ? new Date(b.dateIso).getTime() : 0;
    return db - da;
  });

  const batch = sortedRides.slice(0, 8);
  if (force) {
    return Promise.allSettled(batch.map(ride => proactiveSyncRideMedia(ride, true)));
  }

  batch.forEach((ride, idx) => {
    setTimeout(() => {
      proactiveSyncRideMedia(ride, false);
    }, idx * 250);
  });
  return Promise.resolve();
}

/**
 * Initializes periodic autonomous background photo sync (every 5m), visibility resume, and 3s startup pass.
 */
export function initPeriodicPhotoCountSync() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window._hasPeriodicPhotoCountSync) return;
  window._hasPeriodicPhotoCountSync = true;

  // 1. Initial background pass 3s after boot to refresh upcoming/recent album counts
  setTimeout(() => {
    syncActivePhotoCounts();
  }, 3000);

  // 2. Periodic 5-minute background refresh loop (discovers newly pre-cached rides + refreshes active counts)
  setInterval(() => {
    fetchAndRenderRides(false, false).catch(() => {});
    syncActivePhotoCounts();
  }, 5 * 60 * 1000);

  // 3. Tab visibility resume: when user returns to tab after >= 3 minutes
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      if (Date.now() - _lastPhotoBgSyncAt >= 3 * 60 * 1000) {
        syncActivePhotoCounts();
      }
    }
  });
}


/**
 * Resolves the primary thumbnail image for a ride card:
 * 1. If YouTube action footage is linked, use the high-quality YouTube thumbnail.
 * 2. If no YouTube footage is available, prioritize the genuine photo album cover
 *    (from ride.coverPhotoUrl or pre-indexed ALBUM_COVERS_MAP).
 * 3. Fallback to an elegant SVG cycling silhouette, never a generic/unrelated photo.
 */
export function resolveRideThumbnail(ride, isSpotlight = false) {
  if (!ride) return getCyclingFallbackSvg();

  // 1. YouTube Video Thumbnail (Top Priority when Video Footage Exists)
  if (ride.matchedVideos && ride.matchedVideos.length > 0) {
    const allOverrides = (typeof getVideoOverrides === 'function') ? getVideoOverrides() : {};
    const overrideKey = String(ride.rideId || ride.id);
    const override = allOverrides[overrideKey] || (ride.rideId && allOverrides[String(ride.rideId)]);
    const hasExplicitOrder = Boolean(override && Array.isArray(override.orderedVideoIds) && override.orderedVideoIds.length > 0);
    const displayVideos = hasExplicitOrder
      ? ride.matchedVideos
      : (typeof sortMatchedVideos === 'function' ? sortMatchedVideos(ride.matchedVideos, ride.title) : ride.matchedVideos);
    const firstVideo = displayVideos[0] || ride.matchedVideos[0];
    if (firstVideo && firstVideo.videoId) {
      return isSpotlight
        ? `https://img.youtube.com/vi/${firstVideo.videoId}/maxresdefault.jpg`
        : `https://img.youtube.com/vi/${firstVideo.videoId}/mqdefault.jpg`;
    }
  }

  // 2. Photo Album Cover Artwork (When Dedicated Cover Exists for THIS Ride)
  const albumUrl = ride.photoAlbumUrl ? String(ride.photoAlbumUrl).trim() : '';
  const rideIdStr = ride.rideId ? String(ride.rideId).trim() : (ride.id ? String(ride.id).trim() : '');
  const hasDedicatedCover = Boolean(
    (ride.coverPhotoUrl && typeof ride.coverPhotoUrl === 'string' && ride.coverPhotoUrl.trim().length > 0) ||
    (albumUrl && KNOWN_ALBUMS_MAP[albumUrl]?.coverPhotoUrl) ||
    (rideIdStr && Object.values(KNOWN_ALBUMS_MAP).find(k => k.rideId && String(k.rideId).trim() === rideIdStr)?.coverPhotoUrl)
  );

  if (hasDedicatedCover) {
    return getOptimizedAlbumCoverUrl(ride, isSpotlight ? 1920 : 600, isSpotlight ? 1080 : 338);
  }

  // 3. Fallback to generic cycling vector placeholder (never cross-contaminate from unrelated same-day rides)
  return getCyclingFallbackSvg();
}

export function getCyclingFallbackSvg() {
  return '/assets/btcnj-placeholder.jpg';
}

/**
 * Autonomous self-healing cover image error handler.
 * Seamlessly transitions to crisp vector SVG and fires background JIT scrape to fetch updated Google Photos cover.
 */
export function handlePhotoCoverImgError(imgEl, rideId) {
  if (!imgEl) return;
  imgEl.onerror = null;
  imgEl.src = (typeof getCyclingFallbackSvg === 'function') ? getCyclingFallbackSvg() : '/cycling-blueprint.png';
  if (rideId && typeof currentLoadedRides !== 'undefined' && Array.isArray(currentLoadedRides)) {
    const rawId = String(rideId).trim();
    const targetRide = currentLoadedRides.find(r => String(r.rideId || '').trim() === rawId || String(r.id || '').trim() === rawId || String(r.id || '').trim() === `btcnj-${rawId}`);
    if (targetRide && targetRide.photoAlbumUrl) {
      proactiveSyncRideMedia(targetRide, true);
    }
  }
}

if (typeof window !== 'undefined') {
  window.getCyclingFallbackSvg = getCyclingFallbackSvg;
  window.handlePhotoCoverImgError = handlePhotoCoverImgError;
  window.renderRideCardHtml = renderRideCardHtml;
  window.bindRideCardInteractions = bindRideCardInteractions;
  window.bindCardHoverPreviews = bindCardHoverPreviews;
  window.openRideSpotlight = openRideSpotlight;
  window.openRwgpsLightbox = openRwgpsLightbox;
  window.openVideoLightbox = openVideoLightbox;
  window.closeVideoLightbox = closeVideoLightbox;
}

// ============================================================================
// 3. Automated YouTube Video Association Heuristic Engine
// ============================================================================

/**
 * Extracts formatted ISO date (YYYY-MM-DD) from a video title.
 * Matches 8-digit date representations like 20260919, 20251011.11, etc.
 */
export function extractVideoDate(title) {
  if (!title) return null;
  const match = title.match(/(202\d)(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])/);
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }
  return null;
}

/**
 * Formats an ISO date string (YYYY-MM-DD) into a succinct, human-readable date with day of week.
 * Example: '2026-09-19' -> 'Sat, Sep 19, 2026'
 */
export function formatRideDate(dateStr) {
  if (!dateStr) return '';
  const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return String(dateStr);
  const year = parseInt(match[1], 10);
  const monthIdx = parseInt(match[2], 10) - 1;
  const day = parseInt(match[3], 10);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[monthIdx]} ${day}, ${year}`;
}

/**
 * Formats a ride's date into an unambiguous date-only string with day of week for Spotlight view.
 * Strictly guarantees ZERO time leakage (no departure times, no AM/PM).
 * Example: '2026-10-17' -> 'Sat, Oct 17, 2026'
 *
 * @param {Object|string} rideOrDateStr
 * @returns {string}
 */
export function formatSpotlightDate(rideOrDateStr) {
  if (!rideOrDateStr) return '';
  const dStr = typeof rideOrDateStr === 'string'
    ? rideOrDateStr
    : (rideOrDateStr.dateIso || rideOrDateStr.dateStr || rideOrDateStr.dateText || '');
  if (!dStr) return '';

  const match = String(dStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const year = parseInt(match[1], 10);
    const monthIdx = parseInt(match[2], 10) - 1;
    const day = parseInt(match[3], 10);
    const dObj = new Date(year, monthIdx, day);
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dayName = dayNames[dObj.getDay()] || '';
    const monthName = monthNames[monthIdx] || '';
    return dayName ? `${dayName}, ${monthName} ${day}, ${year}` : `${monthName} ${day}, ${year}`;
  }

  // Fallback for non-ISO strings: strip any trailing time indicators
  return String(dStr)
    .replace(/\s*·\s*\d{1,2}:\d{2}\s*(?:[ap]m)?/gi, '')
    .replace(/\s+\d{1,2}:\d{2}\s*(?:[ap]m)?/gi, '')
    .trim();
}

/**
 * Cleans raw YouTube video title for display as an adaptive video chip label.
 * Strips redundant ride/location names, channel boilerplate, and date codes.
 * Returns succinct chapter chips (e.g. "Part 1", "Main Cut", "Alt Cut 01", "Climbs").
 */
export function cleanVideoChipTitle(rawTitle, rideTitle = '', index = 0, allVideos = []) {
  const str = String(rawTitle || '').trim();
  if (!str) return `Pt ${index + 1}`;

  // 1. Detect Alt / Alternate cuts (e.g. "alt 01", "(alt 1)")
  const altMatch = str.match(/\((?:alt|alternate)\s*0?(\d+)\)/i) || str.match(/[-\s]+(?:alt|alternate)\s*0?(\d+)/i);
  if (altMatch) {
    const altNum = altMatch[1];
    return `Alt Cut ${altNum.length === 1 ? '0' + altNum : altNum}`;
  }

  // 2. Check if a sibling video in the same card has an Alt cut, making this the Main Cut
  const hasSiblingAlt = Array.isArray(allVideos) && allVideos.some(v => {
    const s = String((v && v.title) || '');
    return /\b(?:alt|alternate)\b/i.test(s);
  });

  // 3. Detect trailing part/segment number (e.g. "20260919 01", "20260815 02", "Part 1", "Pt. 2", "01")
  let partNumber = null;
  const partMatch = str.match(/(?:[-\u2013\u2014\s]+(?:(?:alt|part|reel|pt\.?)\s*)?(\d{1,2}|[a-z]+\.\d+))\s*$/i);
  if (partMatch) {
    const rawNum = partMatch[1];
    const parsed = parseInt(rawNum.replace(/^0+/, ''), 10);
    if (!isNaN(parsed)) {
      partNumber = parsed;
    }
  }

  // 4. Strip redundant ride name / location prefix if rawTitle starts with it
  let cleaned = str;
  if (rideTitle) {
    const escapedRide = rideTitle.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    const prefixRegex = new RegExp(`^(?:btcnj|vids|music)?\\s*[-–—:]?\\s*${escapedRide}\\s*[-–—:|]?\\s*`, 'i');
    cleaned = cleaned.replace(prefixRegex, '');
  }

  // 5. Strip standard boilerplate (BTCNJ, channel tags, date stamps, video specs, IHEFC/YIR)
  cleaned = cleaned
    .replace(/^(?:btcnj|vids|music)\s*[-–—:]?\s*/i, '')
    .replace(/\s*[-–—]\s*202\d{5}.*$/i, '')
    .replace(/\s*202\d{5}.*$/i, '')
    .replace(/\b(?:ihefc|yir)\b\s*[-–—:]?\s*/gi, '')
    .replace(/\s*[-–—]\s*ihefc.*$/i, '')
    .replace(/\s*[-–—]\s*yir.*$/i, '')
    .replace(/\b(4K|60fps|HD|1080p)\b/gi, '')
    .replace(/^[–—:|\s-]+|[–—:|\s-]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  // 6. Sibling Alt pairing: If sibling has alt cut, and this video has no alt/part suffix, it's the "Main Cut"
  if (hasSiblingAlt && !partNumber && (cleaned === '' || cleaned.toLowerCase() === String(rideTitle || '').toLowerCase())) {
    return 'Main Cut';
  }

  // 7. If partNumber was detected, return canonical "Pt X"
  if (partNumber !== null) {
    return `Pt ${partNumber}`;
  }

  // 8. Meaningful descriptive sub-topic (e.g. "Climbs", "Group Chilling", "Fast Descent Tucks", "The Devil's Ride")
  if (cleaned.length > 0 && cleaned.toLowerCase() !== String(rideTitle || '').toLowerCase()) {
    return cleaned.length > 24 ? cleaned.slice(0, 22).trim() + '…' : cleaned;
  }

  // 9. Fallback if identical to rideTitle or empty
  if (Array.isArray(allVideos) && allVideos.length > 1) {
    return `Pt ${index + 1}`;
  }
  return 'Watch Video';
}

/**
 * Sorts matched YouTube videos in natural alphanumeric order
 * so that multi-part reels (Part 1, Part 2, Part 3...) and Main/Alt cuts appear in correct sequence.
 */
export function sortMatchedVideos(videos, rideTitle = '') {
  if (!Array.isArray(videos) || videos.length <= 1) return videos || [];
  return [...videos].sort((a, b) => {
    const cleanA = cleanVideoChipTitle(a && a.title, rideTitle, 0, videos);
    const cleanB = cleanVideoChipTitle(b && b.title, rideTitle, 0, videos);

    // Ensure Main Cut always precedes Alt Cut
    if (cleanA.startsWith('Main') && cleanB.startsWith('Alt')) return -1;
    if (cleanA.startsWith('Alt') && cleanB.startsWith('Main')) return 1;

    const cmp = cleanA.localeCompare(cleanB, undefined, { numeric: true, sensitivity: 'base' });
    if (cmp !== 0) return cmp;
    const rawA = String((a && a.title) || '');
    const rawB = String((b && b.title) || '');
    return rawA.localeCompare(rawB, undefined, { numeric: true, sensitivity: 'base' });
  });
}

// Clean title for synthetic ride creation (without individual part numbers)
function cleanRideTitle(rawTitle) {
  const cleaned = String(rawTitle || '')
    .replace(/^(?:btcnj|vids)\s*[-\u2013\u2014:]?\s*/i, '')
    .replace(/\s*[-\u2013\u2014]\s*202\d{5}.*$/i, '')
    .replace(/\s*202\d{5}.*$/i, '')
    .replace(/\s*[-\u2013\u2014]\s*ihefc.*$/i, '')
    .replace(/\s*[-\u2013\u2014]\s*yir.*$/i, '')
    .replace(/\s+/g, ' ')
    .trim();
  return (cleaned.charAt(0).toUpperCase() + cleaned.slice(1)) || 'BTCNJ Club Ride';
}

// ============================================================================
// 3.4 Community Member Media Clustering & Semantic Topic Engine
// ============================================================================

/**
 * Extracts semantic topic stem from video title for auto-grouping into community cards.
 * Normalizes multi-part reels (e.g. Mt Greylock Pt 1..5, High Point, Arden Valley, Action Figures).
 */
export function extractSemanticStem(title) {
  if (!title) return '';
  let str = String(title)
    .replace(/^\s*(?:btcnj|vids)\s*[-–—:]?\s*/i, '')
    .replace(/\s*[-–—]\s*202\d{5}.*$/i, '')
    .replace(/\s*202\d{5}.*$/i, '')
    .replace(/\s*[-–—]\s*ihefc.*$/i, '')
    .replace(/\s*[-–—]\s*yir.*$/i, '')
    .replace(/\b(4K|60fps|HD|1080p)\b/gi, '')
    .replace(/\(.*?\)/g, '')
    .trim();

  const KNOWN_TOPICS = [
    { regex: /\b(?:mt\s*greylock|mount\s*greylock|greylock)\b/i, name: 'Mt Greylock' },
    { regex: /\b(?:high\s*point)\b/i, name: 'High Point' },
    { regex: /\b(?:arden\s*valley)\b/i, name: 'Arden Valley' },
    { regex: /\b(?:action\s*figure|action\s*figures)\b/i, name: 'Action Figures' },
    { regex: /\b(?:cookie\s*monster|cookie\s*ride)\b/i, name: 'Cookie Ride' },
    { regex: /\b(?:peconic|shelter\s*island)\b/i, name: 'Tour de Peconic Bay' },
    { regex: /\b(?:catskills)\b/i, name: 'Catskills Bound' },
    { regex: /\b(?:katonah)\b/i, name: 'Acme Katonah' },
    { regex: /\b(?:morris\s*co)\b/i, name: 'Morris Co' },
    { regex: /\b(?:donuts?|ihefc)\b/i, name: 'Did Someone Say Donuts' },
    { regex: /\b(?:cows)\b/i, name: 'Cows & Country Roads' },
    { regex: /\b(?:hawk'?s\s*nest)\b/i, name: "Hawk's Nest" },
    { regex: /\b(?:jersey\s*shores?)\b/i, name: 'Jersey Shore' },
    { regex: /\b(?:sundown\s*wild\s*forest)\b/i, name: 'Sundown Wild Forest' }
  ];

  for (const topic of KNOWN_TOPICS) {
    if (topic.regex.test(str)) {
      return topic.name;
    }
  }

  const firstSegment = str.split(/[-–—:|]/)[0].trim();
  if (firstSegment.length > 2) {
    return firstSegment;
  }
  return str;
}

/**
 * Derives a clean cluster card title from clustered videos and stem.
 */
export function deriveClusterTitle(stem, videos) {
  if (Array.isArray(videos) && videos.length === 1) {
    let t = cleanRideTitle(videos[0].title);
    if (t.length > 0) return t;
  }
  return stem || 'Community Member Media';
}

/**
 * Clusters unassigned member videos into cohesive community cards based on date proximity and semantic stems.
 * @param {Array<object>} memberVideos - Videos in candidate member pool
 * @param {object} manualGroupOverrides - Optional admin split/merge overrides
 * @returns {Array<object>} Clustered member ride card models
 */
export function clusterMemberVideos(memberVideos, manualGroupOverrides = {}) {
  if (!Array.isArray(memberVideos) || memberVideos.length === 0) return [];

  // Sort memberVideos newest date first
  const sorted = [...memberVideos].sort((a, b) => {
    const da = extractVideoDate(a && a.title) || '';
    const db = extractVideoDate(b && b.title) || '';
    return db.localeCompare(da);
  });

  const clusters = [];

  for (const v of sorted) {
    const manualKey = manualGroupOverrides[v.videoId];
    const stem = extractSemanticStem(v.title);
    const dateStr = extractVideoDate(v.title);

    let matched = null;
    if (manualKey) {
      matched = clusters.find(c => c.id === manualKey);
    } else if (stem) {
      // Find existing cluster with same stem within 4 days (covers Friday-Monday tours)
      matched = clusters.find(c => {
        if (c.stem.toLowerCase() !== stem.toLowerCase()) return false;
        if (!dateStr || !c.primaryDate) return true;
        const d1 = new Date(dateStr).getTime();
        const d2 = new Date(c.primaryDate).getTime();
        const diffDays = Math.abs(d1 - d2) / (1000 * 60 * 60 * 24);
        return diffDays <= 4;
      });
    }

    if (matched) {
      matched.videos.push(v);
    } else {
      const clusterId = manualKey || `cluster-${(stem || 'community').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${dateStr || 'undated'}`;
      clusters.push({
        id: clusterId,
        stem: stem || 'Community Ride',
        primaryDate: dateStr,
        videos: [v]
      });
    }
  }

  return clusters.map(c => {
    const sortedVids = sortMatchedVideos(c.videos);
    const primaryVid = sortedVids[0];
    const bestDate = c.videos.map(v => extractVideoDate(v.title)).filter(Boolean).sort()[0] || c.primaryDate || '2025-01-01';
    const title = deriveClusterTitle(c.stem, sortedVids);

    return {
      id: c.id,
      rideId: null,
      isBtcnjRide: false,
      isCommunityCard: true,
      title: title,
      dateIso: bestDate,
      dateStr: bestDate,
      originalDateIso: bestDate,
      location: 'Community Member Media',
      leaders: [],
      matchedVideos: sortedVids,
      coverPhotoUrl: `https://i.ytimg.com/vi/${primaryVid.videoId}/mqdefault.jpg`,
      photoAlbumUrl: null,
      photoCount: 0
    };
  }).sort((a, b) => b.dateIso.localeCompare(a.dateIso));
}

// ============================================================================
// 3.5 Rule #5 Mobile Deep-Linking & Firebase DB Integration
// ============================================================================

/**
 * In-memory cache for Firebase DB ride documents fetched by rideId.
 */
export const firebaseRideCache = new Map();

/**
 * Queries the quarr-rule-5 Firestore DB for the given BTCNJ rideId and extracts route telemetry.
 * @param {string|number} rideId
 * @returns {Promise<object|null>}
 */
export async function fetchRideFromFirebaseDb(rideId) {
  if (!rideId) return null;
  const strId = String(rideId).trim();
  if (firebaseRideCache.has(strId)) {
    return firebaseRideCache.get(strId);
  }

  try {
    const url = `${CONFIG.FIRESTORE_BTCNJ_RIDES_URL}/${encodeURIComponent(strId)}`;
    const res = await fetch(url, { signal: AbortSignal.timeout(4500) });
    if (!res.ok) {
      if (res.status === 404) {
        firebaseRideCache.set(strId, null);
      }
      return null;
    }
    const json = await res.json();
    const fields = json?.fields || {};

    const rwgpsUrl = fields.rwgpsUrl?.stringValue || '';
    const miles = fields.miles ? (fields.miles.integerValue ? parseInt(fields.miles.integerValue, 10) : parseFloat(fields.miles.doubleValue || 0)) : null;
    const elevation = fields.elevation ? (fields.elevation.integerValue ? parseInt(fields.elevation.integerValue, 10) : parseFloat(fields.elevation.doubleValue || 0)) : null;
    const timeRaw = fields.timeRaw?.stringValue || fields.time24?.stringValue || null;
    const time24 = fields.time24?.stringValue || null;
    const category = fields.category?.stringValue || null;
    const dateStr = fields.dateStr?.stringValue || null;
    const title = fields.title?.stringValue || null;
    const leaders = (fields.rideLeaders?.arrayValue?.values || fields.joinedLeaders?.arrayValue?.values || [])
      .map(v => v.stringValue?.trim()).filter(Boolean);
    const members = (fields.riders?.arrayValue?.values || fields.joinedMembers?.arrayValue?.values || fields.members?.arrayValue?.values || [])
      .map(v => v.stringValue?.trim()).filter(Boolean);
    const photoAlbumUrl = fields.photoAlbumUrl?.stringValue || fields.albumUrl?.stringValue || fields.photosUrl?.stringValue || null;
    const photoAlbumId = fields.photoAlbumId?.stringValue || null;
    const coverPhotoUrl = fields.coverPhotoUrl?.stringValue || null;
    const photoCount = fields.photoCount ? (fields.photoCount.integerValue ? parseInt(fields.photoCount.integerValue, 10) : parseFloat(fields.photoCount.doubleValue || 0)) : 0;
    const description = fields.desc?.stringValue || fields.description?.stringValue || fields.notes?.stringValue || null;
    const mapName = fields.mapName?.stringValue || fields.startLoc?.stringValue || fields.location?.stringValue || null;
    const directions = fields.directions?.stringValue || null;
    const mapUrl = fields.mapUrl?.stringValue || null;
    const zipCode = fields.zipCode?.stringValue || null;

    const rideData = {
      rideId: strId,
      rwgpsUrl,
      miles,
      climbFt: elevation,
      elevation,
      timeRaw,
      time24,
      category,
      dateStr,
      title,
      leaders,
      members,
      description,
      desc: description,
      mapName,
      startLoc: mapName,
      directions,
      mapUrl,
      zipCode,
      photoAlbumUrl,
      photoAlbumId,
      coverPhotoUrl,
      photoCount
    };

    firebaseRideCache.set(strId, rideData);
    return rideData;
  } catch (err) {
    console.warn(`[Firebase DB] Failed to fetch ride ${strId}:`, err);
    return null;
  }
}

/**
 * Constructs a canonical Rule #5 mobile deep-link using the standardized rideid query parameter.
 * Target: https://r5m-quarr.web.app/?rideid=XXXXX
 * @param {object} ride
 * @param {string} [baseUrl]
 * @returns {string}
 */
export function buildR5mRideUrl(ride, baseUrl = CONFIG.R5M_BASE_URL) {
  if (!ride) return baseUrl;

  const rawId = ride.rideId != null ? String(ride.rideId).trim() : '';
  if (rawId && rawId !== 'null' && rawId !== 'undefined') {
    return `${baseUrl}/?rideid=${encodeURIComponent(rawId)}`;
  }

  // Fallback to base URL for custom/routeless rides without a BTCNJ Ride ID
  return baseUrl;
}

// ============================================================================
// 3.8 Video Overrides Data Layer & Auto-Match Reconciliation
// ============================================================================
let cachedVideoOverrides = null;
let _lastVideoOverridesSyncAt = 0;
let _lastMediaOverridesSyncAt = 0;
let _lastPlaylistsSyncAt = 0;
let _lastMemberExclusionsSyncAt = 0;
export const CLOUD_OVERRIDE_CACHE_TTL_MS = 5 * 60 * 1000; // 5-minute SWR cache to prevent 429 quota exhaustion

export function getVideoOverrides() {
  if (cachedVideoOverrides) return cachedVideoOverrides;
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(CONFIG.STORAGE_KEY_VIDEO_OVERRIDES) : null;
    cachedVideoOverrides = raw ? JSON.parse(raw) : {};
  } catch (err) {
    cachedVideoOverrides = {};
  }
  return cachedVideoOverrides || {};
}

export function setVideoOverrides(overrides) {
  cachedVideoOverrides = overrides || {};
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(CONFIG.STORAGE_KEY_VIDEO_OVERRIDES, JSON.stringify(cachedVideoOverrides));
    }
  } catch (err) {
    console.warn('Failed to save local video overrides:', err);
  }
}

export async function fetchCloudVideoOverrides(forceRefresh = false) {
  const now = Date.now();
  if (!forceRefresh && cachedVideoOverrides && (now - _lastVideoOverridesSyncAt < CLOUD_OVERRIDE_CACHE_TTL_MS)) {
    return cachedVideoOverrides;
  }

  let cloudOverrides = null;

  // Tier 1: Try live Firestore (respecting 5-minute client cache)
  try {
    const res = await fetch(CONFIG.FIRESTORE_VIDEO_OVERRIDES_URL, {
      cache: forceRefresh ? 'no-store' : 'default',
      signal: AbortSignal.timeout(forceRefresh ? 6000 : 4000)
    });
    if (res.ok) {
      const json = await res.json();
      if (json && Array.isArray(json.documents)) {
        cloudOverrides = {};
        json.documents.forEach(doc => {
          const parts = doc.name.split('/');
          const docId = parts[parts.length - 1];
          const fields = doc.fields || {};

          const orderedVideoIds = fields.orderedVideoIds?.arrayValue?.values?.map(v => v.stringValue).filter(Boolean) || [];
          const excludedVideoIds = fields.excludedVideoIds?.arrayValue?.values?.map(v => v.stringValue).filter(Boolean) || [];
          
          let addedVideos = [];
          if (fields.addedVideosJson?.stringValue) {
            try { addedVideos = JSON.parse(fields.addedVideosJson.stringValue); } catch (_) {}
          }

          cloudOverrides[docId] = {
            orderedVideoIds,
            excludedVideoIds,
            addedVideos,
            updatedAt: fields.updatedAt?.stringValue || new Date().toISOString()
          };
        });
      }
    } else {
      console.warn(`Firestore video overrides query returned HTTP ${res.status}, falling back to static seed.`);
    }
  } catch (fErr) {
    console.warn('Live Firestore video override fetch notice (using seed fallback):', fErr);
  }

  // Tier 2: Static Edge CDN Seed Fallback (/data/video-overrides.json) with 0 Firestore reads
  if (!cloudOverrides) {
    try {
      const seedUrl = `/data/video-overrides.json${forceRefresh ? `?_t=${Date.now()}` : ''}`;
      const seedRes = await fetch(seedUrl, { signal: AbortSignal.timeout(3000) });
      if (seedRes.ok) {
        const seedJson = await seedRes.json();
        if (seedJson && typeof seedJson === 'object') {
          cloudOverrides = seedJson;
        }
      }
    } catch (seedErr) {
      console.warn('Static edge video seed fetch notice:', seedErr);
    }
  }

  const local = (forceRefresh && cloudOverrides) ? {} : getVideoOverrides();

  // Timestamp-aware merge: Never overwrite a newer local edit with an older seed/cloud payload
  const merged = { ...(cloudOverrides || {}) };
  if (local && typeof local === 'object') {
    Object.keys(local).forEach(rideKey => {
      const localDoc = local[rideKey];
      const cloudDoc = merged[rideKey];
      if (!cloudDoc) {
        merged[rideKey] = localDoc;
        // Auto-heal: push orphaned local override up
        if (!forceRefresh) {
          console.log(`[AutoHeal] Pushing orphaned local override for ${rideKey} to Firestore...`);
          saveRideVideoOverride(rideKey, localDoc).catch(() => {});
        }
      } else {
        const localTime = new Date(localDoc.updatedAt || 0).getTime();
        const cloudTime = new Date(cloudDoc.updatedAt || 0).getTime();
        if (localTime > cloudTime) {
          merged[rideKey] = localDoc;
          if (!forceRefresh) {
            console.log(`[AutoHeal] Pushing orphaned local override for ${rideKey} to Firestore...`);
            saveRideVideoOverride(rideKey, localDoc).catch(() => {});
          }
        }
      }
    });
  }

  cachedVideoOverrides = merged;
  _lastVideoOverridesSyncAt = Date.now();
  setVideoOverrides(merged);
  return merged;
}

export async function saveRideVideoOverride(rideKey, overrideData) {
  const current = getVideoOverrides();
  current[rideKey] = overrideData;
  setVideoOverrides(current);

  // 1. Dispatch to Centralized quarr-rule-5 API (Option A SSOT)
  try {
    const payload = {
      rideId: String(rideKey),
      videos: (overrideData.orderedVideoIds || []).map(id => {
        const added = (overrideData.addedVideos || []).find(v => v.videoId === id);
        return { videoId: id, title: added?.title || '', url: `https://www.youtube.com/watch?v=${id}` };
      }),
      orderedVideoIds: overrideData.orderedVideoIds || [],
      excludedVideoIds: overrideData.excludedVideoIds || [],
      addedVideos: overrideData.addedVideos || [],
      passcode: 'btc2026'
    };
    fetch(CONFIG.SET_RIDE_MEDIA_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Passcode': 'btc2026'
      },
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(4000)
    }).catch(e => console.warn('Central set-ride-media video sync notice:', e));
  } catch (apiErr) {
    console.warn('Central set-ride-media video sync notice:', apiErr);
  }

  // 2. Dispatch to btcnjcc Firestore for backward compatibility
  try {
    const url = `${CONFIG.FIRESTORE_VIDEO_OVERRIDES_URL}/${encodeURIComponent(rideKey)}`;
    const firestorePayload = {
      fields: {
        rideKey: { stringValue: String(rideKey) },
        orderedVideoIds: {
          arrayValue: {
            values: (overrideData.orderedVideoIds || []).map(id => ({ stringValue: String(id) }))
          }
        },
        excludedVideoIds: {
          arrayValue: {
            values: (overrideData.excludedVideoIds || []).map(id => ({ stringValue: String(id) }))
          }
        },
        addedVideosJson: {
          stringValue: JSON.stringify(overrideData.addedVideos || [])
        },
        updatedAt: { stringValue: new Date().toISOString() }
      }
    };
    const res = await fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(firestorePayload),
      signal: AbortSignal.timeout(4000)
    });
    if (!res.ok) {
      console.error(`[Firestore] Video override sync failed for ${rideKey}: HTTP ${res.status}`);
    }
  } catch (err) {
    console.warn('Background Firestore override sync failed (saved locally):', err);
  }
}

export async function resetRideVideoOverride(rideKey) {
  const current = getVideoOverrides();
  delete current[rideKey];
  setVideoOverrides(current);

  try {
    const url = `${CONFIG.FIRESTORE_VIDEO_OVERRIDES_URL}/${encodeURIComponent(rideKey)}`;
    const res = await fetch(url, { method: 'DELETE', signal: AbortSignal.timeout(4000) });
    if (!res.ok) {
      console.error(`[Firestore] Video override delete failed for ${rideKey}: HTTP ${res.status}`);
    }
  } catch (err) {
    console.warn('Failed to delete cloud override:', err);
  }
}

// ============================================================================
// 3.85 YouTube Playlist Registry & Member Media Exclusions Data Layers
// ============================================================================

export const DEFAULT_PLAYLISTS = [
  {
    id: 'PLgvVTo25qNhbFz-aoolyQ5JCllHUw3Mt9',
    title: 'BTCNJ Member & Club Rides',
    url: 'https://youtube.com/playlist?list=PLgvVTo25qNhbFz-aoolyQ5JCllHUw3Mt9',
    isDefault: true,
    createdAt: '2025-01-01T00:00:00.000Z'
  }
];

let cachedYoutubePlaylists = null;

export function getYoutubePlaylists() {
  if (cachedYoutubePlaylists && cachedYoutubePlaylists.length > 0) return cachedYoutubePlaylists;
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(CONFIG.STORAGE_KEY_PLAYLISTS) : null;
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed) && parsed.length > 0) {
      cachedYoutubePlaylists = parsed;
      return cachedYoutubePlaylists;
    }
  } catch (_) {}
  cachedYoutubePlaylists = [...DEFAULT_PLAYLISTS];
  return cachedYoutubePlaylists;
}

export function setYoutubePlaylists(playlists) {
  cachedYoutubePlaylists = Array.isArray(playlists) && playlists.length > 0 ? playlists : [...DEFAULT_PLAYLISTS];
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(CONFIG.STORAGE_KEY_PLAYLISTS, JSON.stringify(cachedYoutubePlaylists));
    }
  } catch (err) {
    console.warn('Failed to save local playlist registry:', err);
  }
}

export async function fetchCloudYoutubePlaylists(forceRefresh = false) {
  const now = Date.now();
  if (!forceRefresh && cachedYoutubePlaylists && (now - _lastPlaylistsSyncAt < CLOUD_OVERRIDE_CACHE_TTL_MS)) {
    return cachedYoutubePlaylists;
  }

  try {
    const res = await fetch(CONFIG.FIRESTORE_PLAYLISTS_URL, { signal: AbortSignal.timeout(4000) });
    if (!res.ok) {
      _lastPlaylistsSyncAt = Date.now();
      return null;
    }
    const json = await res.json();
    if (!json || !Array.isArray(json.documents)) {
      _lastPlaylistsSyncAt = Date.now();
      return null;
    }

    const cloudPlaylists = json.documents.map(doc => {
      const parts = doc.name.split('/');
      const docId = parts[parts.length - 1];
      const fields = doc.fields || {};
      return {
        id: fields.id?.stringValue || docId,
        title: fields.title?.stringValue || 'YouTube Playlist',
        url: fields.url?.stringValue || `https://youtube.com/playlist?list=${docId}`,
        isDefault: Boolean(fields.isDefault?.booleanValue),
        createdAt: fields.createdAt?.stringValue || new Date().toISOString()
      };
    });

    if (cloudPlaylists.length > 0) {
      if (!cloudPlaylists.some(p => p.id === DEFAULT_PLAYLISTS[0].id)) {
        cloudPlaylists.unshift(DEFAULT_PLAYLISTS[0]);
      }
      _lastPlaylistsSyncAt = Date.now();
      setYoutubePlaylists(cloudPlaylists);
      return cloudPlaylists;
    }
    _lastPlaylistsSyncAt = Date.now();
    return null;
  } catch (err) {
    _lastPlaylistsSyncAt = Date.now();
    return null;
  }
}

export async function saveCloudPlaylist(playlist) {
  const current = getYoutubePlaylists();
  const existingIdx = current.findIndex(p => p.id === playlist.id);
  if (existingIdx >= 0) {
    current[existingIdx] = { ...current[existingIdx], ...playlist };
  } else {
    current.push(playlist);
  }
  setYoutubePlaylists(current);

  try {
    const url = `${CONFIG.FIRESTORE_PLAYLISTS_URL}/${encodeURIComponent(playlist.id)}`;
    const payload = {
      fields: {
        id: { stringValue: String(playlist.id) },
        title: { stringValue: String(playlist.title || 'YouTube Playlist') },
        url: { stringValue: String(playlist.url || '') },
        isDefault: { booleanValue: Boolean(playlist.isDefault) },
        createdAt: { stringValue: String(playlist.createdAt || new Date().toISOString()) },
        updatedAt: { stringValue: new Date().toISOString() }
      }
    };
    await fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(4000)
    });
  } catch (err) {
    console.warn('Background playlist save failed (saved locally):', err);
  }
}

export async function removeCloudPlaylist(playlistId) {
  const current = getYoutubePlaylists().filter(p => p.id !== playlistId);
  setYoutubePlaylists(current);

  try {
    const url = `${CONFIG.FIRESTORE_PLAYLISTS_URL}/${encodeURIComponent(playlistId)}`;
    await fetch(url, { method: 'DELETE', signal: AbortSignal.timeout(4000) });
  } catch (err) {
    console.warn('Background playlist removal failed:', err);
  }
}

// --- Member Media Exclusions Data Layer ---
let cachedMemberMediaExclusions = null;

export function getMemberMediaExclusions() {
  if (cachedMemberMediaExclusions) return cachedMemberMediaExclusions;
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(CONFIG.STORAGE_KEY_MEMBER_EXCLUSIONS) : null;
    cachedMemberMediaExclusions = raw ? JSON.parse(raw) : [];
  } catch (_) {
    cachedMemberMediaExclusions = [];
  }
  return cachedMemberMediaExclusions || [];
}

export function setMemberMediaExclusions(exclusions) {
  cachedMemberMediaExclusions = Array.isArray(exclusions) ? exclusions : [];
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(CONFIG.STORAGE_KEY_MEMBER_EXCLUSIONS, JSON.stringify(cachedMemberMediaExclusions));
    }
  } catch (err) {
    console.warn('Failed to save local member media exclusions:', err);
  }
}

export async function fetchCloudMemberMediaExclusions(forceRefresh = false) {
  const now = Date.now();
  if (!forceRefresh && cachedMemberMediaExclusions && (now - _lastMemberExclusionsSyncAt < CLOUD_OVERRIDE_CACHE_TTL_MS)) {
    return cachedMemberMediaExclusions;
  }

  try {
    const res = await fetch(CONFIG.FIRESTORE_MEMBER_EXCLUSIONS_URL, { signal: AbortSignal.timeout(4000) });
    if (!res.ok) {
      _lastMemberExclusionsSyncAt = Date.now();
      return null;
    }
    const json = await res.json();
    if (!json || !Array.isArray(json.documents)) {
      _lastMemberExclusionsSyncAt = Date.now();
      return null;
    }

    const cloudExclusions = json.documents.map(doc => {
      const parts = doc.name.split('/');
      const docId = parts[parts.length - 1];
      const fields = doc.fields || {};
      return fields.videoId?.stringValue || docId;
    }).filter(Boolean);

    const local = getMemberMediaExclusions();
    const merged = Array.from(new Set([...local, ...cloudExclusions]));
    _lastMemberExclusionsSyncAt = Date.now();
    setMemberMediaExclusions(merged);
    return merged;
  } catch (err) {
    _lastMemberExclusionsSyncAt = Date.now();
    return null;
  }
}

export async function excludeMemberMediaVideo(videoId, reason = 'admin_member_exclusion') {
  const current = getMemberMediaExclusions();
  if (!current.includes(videoId)) {
    current.push(videoId);
    setMemberMediaExclusions(current);
  }

  try {
    const url = `${CONFIG.FIRESTORE_MEMBER_EXCLUSIONS_URL}/${encodeURIComponent(videoId)}`;
    const payload = {
      fields: {
        videoId: { stringValue: String(videoId) },
        reason: { stringValue: String(reason) },
        excludedAt: { stringValue: new Date().toISOString() }
      }
    };
    await fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(4000)
    });
  } catch (err) {
    console.warn('Background member video exclusion sync failed (saved locally):', err);
  }
}

export async function restoreMemberMediaVideo(videoId) {
  const current = getMemberMediaExclusions().filter(id => id !== videoId);
  setMemberMediaExclusions(current);

  try {
    const url = `${CONFIG.FIRESTORE_MEMBER_EXCLUSIONS_URL}/${encodeURIComponent(videoId)}`;
    await fetch(url, { method: 'DELETE', signal: AbortSignal.timeout(4000) });
  } catch (err) {
    console.warn('Background member video restore failed:', err);
  }
}

export function reconcileRideVideos(autoVideos, override) {
  if (!override) return autoVideos || [];

  const excludedSet = new Set((override.excludedVideoIds || []).map(id => String(id).trim()));
  const manualAdded = Array.isArray(override.addedVideos) ? override.addedVideos : [];

  // 1. Filter out excluded videos
  let activeList = (autoVideos || []).filter(v => v && v.videoId && !excludedSet.has(String(v.videoId).trim()));

  // 2. Append manually added videos that aren't already present
  manualAdded.forEach(mv => {
    if (mv && mv.videoId && !excludedSet.has(String(mv.videoId).trim()) && !activeList.some(v => v.videoId === mv.videoId)) {
      activeList.push({
        ...mv,
        isManual: true
      });
    }
  });

  // 3. Reorder per explicit sequence if defined
  const orderedIds = override.orderedVideoIds;
  if (Array.isArray(orderedIds) && orderedIds.length > 0) {
    // Resilience guard: If any ID in orderedVideoIds is missing from activeList and not excluded, restore it
    orderedIds.forEach(id => {
      const cleanId = String(id).trim();
      if (cleanId && !excludedSet.has(cleanId) && !activeList.some(v => String(v.videoId).trim() === cleanId)) {
        activeList.push({
          videoId: cleanId,
          title: `YouTube Video (${cleanId})`,
          thumbnail: `https://img.youtube.com/vi/${cleanId}/mqdefault.jpg`,
          isManual: true,
          url: `https://www.youtube.com/watch?v=${cleanId}`
        });
      }
    });

    const orderMap = new Map();
    orderedIds.forEach((id, idx) => orderMap.set(String(id).trim(), idx));

    activeList.sort((a, b) => {
      const idxA = orderMap.has(String(a.videoId).trim()) ? orderMap.get(String(a.videoId).trim()) : 99999;
      const idxB = orderMap.has(String(b.videoId).trim()) ? orderMap.get(String(b.videoId).trim()) : 99999;
      if (idxA !== idxB) return idxA - idxB;
      return (a.title || '').localeCompare(b.title || '', undefined, { numeric: true });
    });
  } else if (activeList.length > 1) {
    activeList = sortMatchedVideos(activeList);
  }

  return activeList;
}

export function extractYouTubeVideoId(input) {
  if (!input || typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) return trimmed;
  const urlMatch = trimmed.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=|shorts\/))([a-zA-Z0-9_-]{11})/i);
  return urlMatch ? urlMatch[1] : null;
}

// ============================================================================
// 3.7 Admin Media Overrides (Ride Conversion & Album Manager)
// ============================================================================
let cachedMediaOverrides = null;

export function getMediaOverrides() {
  if (cachedMediaOverrides) return cachedMediaOverrides;
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(CONFIG.STORAGE_KEY_MEDIA_OVERRIDES) : null;
    cachedMediaOverrides = raw ? JSON.parse(raw) : {};
  } catch (err) {
    cachedMediaOverrides = {};
  }
  return cachedMediaOverrides || {};
}

export function setMediaOverrides(overrides) {
  cachedMediaOverrides = overrides || {};
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(CONFIG.STORAGE_KEY_MEDIA_OVERRIDES, JSON.stringify(cachedMediaOverrides));
    }
  } catch (err) {
    console.warn('Failed to save local media overrides:', err);
  }
}

export async function fetchCloudMediaOverrides(forceRefresh = false) {
  const now = Date.now();
  if (!forceRefresh && cachedMediaOverrides && (now - _lastMediaOverridesSyncAt < CLOUD_OVERRIDE_CACHE_TTL_MS)) {
    return cachedMediaOverrides;
  }

  try {
    const res = await fetch(CONFIG.FIRESTORE_MEDIA_OVERRIDES_URL, {
      cache: forceRefresh ? 'no-store' : 'default',
      signal: AbortSignal.timeout(forceRefresh ? 6000 : 4000)
    });
    if (!res.ok) {
      _lastMediaOverridesSyncAt = Date.now();
      return cachedMediaOverrides || getMediaOverrides();
    }
    const json = await res.json();
    if (!json || !Array.isArray(json.documents)) {
      _lastMediaOverridesSyncAt = Date.now();
      return cachedMediaOverrides || getMediaOverrides();
    }

    const cloudOverrides = {};
    json.documents.forEach(doc => {
      const parts = doc.name.split('/');
      const docId = parts[parts.length - 1];
      const fields = doc.fields || {};

      let photoCount = null;
      if (fields.photoCount?.integerValue !== undefined) {
        photoCount = parseInt(fields.photoCount.integerValue, 10);
      } else if (fields.photoCount?.doubleValue !== undefined) {
        photoCount = fields.photoCount.doubleValue;
      }

      let telemetry = null;
      if (fields.telemetryJson?.stringValue) {
        try { telemetry = JSON.parse(fields.telemetryJson.stringValue); } catch (_) {}
      }

      let customData = null;
      if (fields.customDataJson?.stringValue) {
        try { customData = JSON.parse(fields.customDataJson.stringValue); } catch (_) {}
      }

      cloudOverrides[docId] = {
        cardId: fields.cardId?.stringValue || docId,
        assignedRideId: fields.assignedRideId?.stringValue || null,
        isPromotedToMediaCenter: Boolean(fields.isPromotedToMediaCenter?.booleanValue),
        assignedDate: fields.assignedDate?.stringValue || null,
        assignedCategory: fields.assignedCategory?.stringValue || null,
        revertToOther: Boolean(fields.revertToOther?.booleanValue),
        photoAlbumUrl: fields.photoAlbumUrl?.stringValue || null,
        photoCount: photoCount,
        coverPhotoUrl: fields.coverPhotoUrl?.stringValue || null,
        hidePhotoAlbum: Boolean(fields.hidePhotoAlbum?.booleanValue),
        telemetry: telemetry,
        archived: Boolean(fields.archived?.booleanValue),
        deleted: Boolean(fields.deleted?.booleanValue),
        isCustom: Boolean(fields.isCustom?.booleanValue),
        customData: customData,
        updatedAt: fields.updatedAt?.stringValue || new Date().toISOString()
      };
    });

    // Cloud is Authoritative (SSOT): Cloud documents replace stale local mutations
    const local = forceRefresh ? {} : getMediaOverrides();
    const merged = { ...cloudOverrides };

    // For any keys existing only locally in offline mode, preserve if not forced
    if (!forceRefresh) {
      for (const [k, localDoc] of Object.entries(local)) {
        if (!merged[k]) {
          merged[k] = localDoc;
        }
      }
    }
    for (const [k, cloudDoc] of Object.entries(cloudOverrides)) {
      const localDoc = local[k] || {};
      merged[k] = {
        ...cloudDoc,
        cardId: cloudDoc.cardId || localDoc.cardId || k
      };
    }
    cachedMediaOverrides = merged;
    _lastMediaOverridesSyncAt = Date.now();
    setMediaOverrides(merged);
    return merged;
  } catch (err) {
    _lastMediaOverridesSyncAt = Date.now();
    return cachedMediaOverrides || getMediaOverrides();
  }
}

export async function saveMediaOverride(cardKey, overrideData) {
  const current = getMediaOverrides();
  const prevOverride = current[cardKey] || {};
  const mergedOverride = {
    ...prevOverride,
    ...overrideData,
    cardId: cardKey,
    updatedAt: new Date().toISOString()
  };
  // Preserve known photoCount > 0 if saving the same album URL with an unhydrated 0 count
  if (
    mergedOverride.photoAlbumUrl &&
    (!mergedOverride.photoCount || Number(mergedOverride.photoCount) <= 0) &&
    prevOverride.photoAlbumUrl === mergedOverride.photoAlbumUrl &&
    Number(prevOverride.photoCount) > 0
  ) {
    mergedOverride.photoCount = Number(prevOverride.photoCount);
  }
  current[cardKey] = mergedOverride;
  setMediaOverrides(current);

  const mergedData = current[cardKey];

  // 1. Dispatch to Centralized quarr-rule-5 API (Option A SSOT)
  try {
    const payload = {
      cardId: String(cardKey),
      rideId: mergedData.assignedRideId || cardKey,
      photoAlbumUrl: mergedData.photoAlbumUrl,
      photoCount: mergedData.photoCount,
      coverPhotoUrl: mergedData.coverPhotoUrl,
      archived: mergedData.archived,
      deleted: mergedData.deleted,
      revertToOther: mergedData.revertToOther,
      isPromotedToMediaCenter: mergedData.isPromotedToMediaCenter,
      assignedDate: mergedData.assignedDate,
      assignedCategory: mergedData.assignedCategory,
      isCustom: mergedData.isCustom,
      customData: mergedData.customData,
      passcode: 'btc2026'
    };
    fetch(CONFIG.SET_RIDE_MEDIA_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Passcode': 'btc2026'
      },
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(4000)
    }).catch(e => console.warn('Central set-ride-media sync notice:', e));
  } catch (apiErr) {
    console.warn('Central set-ride-media sync notice:', apiErr);
  }

  try {
    const firestoreFields = {
      cardId: { stringValue: String(cardKey) },
      updatedAt: { stringValue: new Date().toISOString() }
    };
    if (mergedData.assignedRideId !== undefined && mergedData.assignedRideId !== null) {
      firestoreFields.assignedRideId = { stringValue: String(mergedData.assignedRideId || '') };
    }
    if (mergedData.isPromotedToMediaCenter !== undefined) {
      firestoreFields.isPromotedToMediaCenter = { booleanValue: Boolean(mergedData.isPromotedToMediaCenter) };
    }
    if (mergedData.assignedDate !== undefined && mergedData.assignedDate !== null) {
      firestoreFields.assignedDate = { stringValue: String(mergedData.assignedDate || '') };
    }
    if (mergedData.assignedCategory !== undefined && mergedData.assignedCategory !== null) {
      firestoreFields.assignedCategory = { stringValue: String(mergedData.assignedCategory || '') };
    }
    if (mergedData.revertToOther !== undefined) {
      firestoreFields.revertToOther = { booleanValue: Boolean(mergedData.revertToOther) };
    }
    if (mergedData.photoAlbumUrl !== undefined && mergedData.photoAlbumUrl !== null) {
      firestoreFields.photoAlbumUrl = { stringValue: String(mergedData.photoAlbumUrl || '') };
    }
    if (mergedData.photoCount !== undefined && mergedData.photoCount !== null) {
      firestoreFields.photoCount = { integerValue: String(mergedData.photoCount) };
    }
    if (mergedData.coverPhotoUrl !== undefined && mergedData.coverPhotoUrl !== null) {
      firestoreFields.coverPhotoUrl = { stringValue: String(mergedData.coverPhotoUrl || '') };
    }
    if (mergedData.hidePhotoAlbum !== undefined) {
      firestoreFields.hidePhotoAlbum = { booleanValue: Boolean(mergedData.hidePhotoAlbum) };
    }
    if (mergedData.telemetry) {
      firestoreFields.telemetryJson = { stringValue: JSON.stringify(mergedData.telemetry) };
    }
    if (mergedData.archived !== undefined) {
      firestoreFields.archived = { booleanValue: Boolean(mergedData.archived) };
    }
    if (mergedData.deleted !== undefined) {
      firestoreFields.deleted = { booleanValue: Boolean(mergedData.deleted) };
    }
    if (mergedData.isCustom !== undefined) {
      firestoreFields.isCustom = { booleanValue: Boolean(mergedData.isCustom) };
    }
    if (mergedData.customData) {
      firestoreFields.customDataJson = { stringValue: JSON.stringify(mergedData.customData) };
    }

    const fieldMask = Object.keys(firestoreFields)
      .map(f => `updateMask.fieldPaths=${encodeURIComponent(f)}`)
      .join('&');
    const url = `${CONFIG.FIRESTORE_MEDIA_OVERRIDES_URL}/${encodeURIComponent(cardKey)}?${fieldMask}`;

    await fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields: firestoreFields }),
      signal: AbortSignal.timeout(4000)
    });
  } catch (err) {
    console.warn('Background Firestore media override sync failed (saved locally):', err);
  }
}

export async function deleteMediaOverride(cardKey) {
  const current = getMediaOverrides();
  delete current[cardKey];
  setMediaOverrides(current);

  try {
    const url = `${CONFIG.FIRESTORE_MEDIA_OVERRIDES_URL}/${encodeURIComponent(cardKey)}`;
    await fetch(url, { method: 'DELETE', signal: AbortSignal.timeout(4000) });
  } catch (err) {
    console.warn('Failed to delete cloud media override:', err);
  }
}

export async function lookupBtcnjRide(rideId) {
  if (!rideId || !String(rideId).trim()) return null;
  const cleanId = String(rideId).trim();
  const url = `${CONFIG.FIRESTORE_BTCNJ_RIDES_LOOKUP_URL}/${encodeURIComponent(cleanId)}`;
  try {
    const res = await fetch(url, { signal: AbortSignal.timeout(5000) });
    if (!res.ok) return null;
    const json = await res.json();
    if (!json || !json.fields) return null;
    const fields = json.fields;

    let miles = null;
    if (fields.miles?.doubleValue !== undefined) miles = fields.miles.doubleValue;
    else if (fields.miles?.integerValue !== undefined) miles = parseInt(fields.miles.integerValue, 10);
    else if (fields.miles?.stringValue !== undefined) miles = parseFloat(fields.miles.stringValue);

    let climbFt = null;
    if (fields.elevation?.integerValue !== undefined) climbFt = parseInt(fields.elevation.integerValue, 10);
    else if (fields.elevation?.doubleValue !== undefined) climbFt = fields.elevation.doubleValue;
    else if (fields.elevation?.stringValue !== undefined) climbFt = parseInt(fields.elevation.stringValue, 10);
    else if (fields.climbFt?.integerValue !== undefined) climbFt = parseInt(fields.climbFt.integerValue, 10);
    else if (fields.climbFt?.doubleValue !== undefined) climbFt = fields.climbFt.doubleValue;

    let leaders = [];
    if (fields.rideLeaders?.arrayValue?.values?.length > 0) {
      leaders = normalizeRideLeaders(fields.rideLeaders.arrayValue.values.map(v => v.stringValue?.trim()).filter(Boolean));
    } else if (fields.joinedLeaders?.arrayValue?.values?.length > 0) {
      leaders = normalizeRideLeaders(fields.joinedLeaders.arrayValue.values.map(v => v.stringValue?.trim()).filter(Boolean));
    } else if (fields.leaders?.stringValue || fields.rawLeadersText?.stringValue) {
      const raw = fields.leaders?.stringValue || fields.rawLeadersText?.stringValue;
      leaders = normalizeRideLeaders(raw);
    }

    let members = [];
    if (fields.riders?.arrayValue?.values?.length > 0) {
      members = fields.riders.arrayValue.values.map(v => v.stringValue?.trim()).filter(Boolean);
    } else if (fields.joinedMembers?.arrayValue?.values?.length > 0) {
      members = fields.joinedMembers.arrayValue.values.map(v => v.stringValue?.trim()).filter(Boolean);
    } else if (fields.members?.arrayValue?.values?.length > 0) {
      members = fields.members.arrayValue.values.map(v => v.stringValue?.trim()).filter(Boolean);
    } else if (fields.riders?.stringValue || fields.members?.stringValue || fields.joinedMembers?.stringValue) {
      const raw = fields.riders?.stringValue || fields.members?.stringValue || fields.joinedMembers?.stringValue;
      members = raw.split(/[,●•+]|\s*\+\s*\d+/).map(s => s.trim()).filter(Boolean);
    }

    let photoAlbumUrl = fields.photoAlbumUrl?.stringValue || null;
    if (!photoAlbumUrl && fields.desc?.stringValue) {
      const match = fields.desc.stringValue.match(/https:\/\/photos\.(?:app\.goo\.gl|google\.com)\/[A-Za-z0-9_\-\/?=&]+/);
      if (match) photoAlbumUrl = match[0].replace(/&amp;/g, '&');
    }
    let photoCount = null;
    if (fields.photoCount?.integerValue !== undefined) photoCount = parseInt(fields.photoCount.integerValue, 10);
    else if (fields.mediaItemsCount?.integerValue !== undefined) photoCount = parseInt(fields.mediaItemsCount.integerValue, 10);
    let coverPhotoUrl = fields.coverPhotoUrl?.stringValue || null;
    const dateStr = fields.dateStr?.stringValue || null;
    const dateText = fields.dateText?.stringValue || null;
    const timeRaw = fields.timeRaw?.stringValue || null;
    const time24 = fields.time24?.stringValue || null;
    const description = fields.desc?.stringValue || fields.description?.stringValue || fields.notes?.stringValue || null;
    const mapName = fields.mapName?.stringValue || fields.startLoc?.stringValue || fields.location?.stringValue || null;
    const directions = fields.directions?.stringValue || null;
    const mapUrl = fields.mapUrl?.stringValue || null;
    const zipCode = fields.zipCode?.stringValue || null;

    const resObj = {
      rideId: cleanId,
      title: fields.title?.stringValue || `BTCNJ Ride #${cleanId}`,
      category: fields.category?.stringValue || null,
      dateIso: dateStr,
      dateStr: dateStr,
      dateText: dateText,
      timeRaw: timeRaw,
      time24: time24,
      miles: miles,
      climbFt: climbFt,
      leaders: leaders,
      members: members,
      description: description,
      desc: description,
      mapName: mapName,
      startLoc: mapName,
      directions: directions,
      mapUrl: mapUrl,
      zipCode: zipCode,
      rwgpsUrl: fields.rwgpsUrl?.stringValue || fields.mapUrl?.stringValue || null,
      photoAlbumUrl: photoAlbumUrl,
      photoCount: photoCount,
      coverPhotoUrl: coverPhotoUrl
    };
    hydrateRidePhotoMetadata(resObj);
    return resObj;
  } catch (err) {
    console.warn(`Failed to lookup BTCNJ ride #${cleanId}:`, err);
    return null;
  }
}

// ============================================================================
// 4. Data Aggregation & Media Filtering Pipeline
// ============================================================================

/**
 * Automatically associates a YouTube video with the most appropriate official club ride
 * using a multi-pass hierarchical matching engine:
 * 1. Exact Date Matching (via 8-digit date in video title)
 * 2. Multi-Ride Same-Date Token Disambiguation
 * 3. Generalized Full Ride Title & Significant Core Substring Containment
 * 4. Date Proximity (within 7 days) with Keyword Correlation
 * 5. Distinctive Weighted Token Overlap Scoring (>= 4 chars, ignoring noise stopwords)
 *
 * @param {Object} video - YouTube video object { videoId, title, ... }
 * @param {Array<Object>} clubRides - List of available club rides
 * @returns {Object|null} The matched club ride or null if no confident match
 */
export function matchVideoToClubRide(video, clubRides) {
  if (!video || !video.title || !Array.isArray(clubRides) || clubRides.length === 0) return null;
  const vTitle = String(video.title || '').trim();
  const vTitleLower = vTitle.toLowerCase();
  const d = extractVideoDate(vTitle);

  const DISTINCTIVE_KEYWORDS = ['cookie', 'peconic', 'catskills', 'katonah', 'high point', 'morris', 'ramapo', 'rally', 'boonton', 'coffee'];
  const NOISE_WORDS = new Set(['btcnj', 'music', 'vids', 'ride', 'club', 'ihefc', 'yir', 'with', 'from', 'tour', 'hill', 'road', 'north', 'jersey', 'the', 'and', 'for']);

  // Pass 1: Exact Date Match (Highest Confidence)
  if (d) {
    const ridesOnDate = clubRides.filter(r => r.dateIso === d);
    if (ridesOnDate.length === 1) {
      return ridesOnDate[0];
    }
    if (ridesOnDate.length > 1) {
      // Disambiguate multi-ride dates via token overlap or distinctive keywords
      const matched = ridesOnDate.find(r => {
        const rTitleLower = (r.title || '').toLowerCase();
        return DISTINCTIVE_KEYWORDS.some(k => rTitleLower.includes(k) && vTitleLower.includes(k))
          || rTitleLower.split(/\s+/).some(word => word.length > 3 && vTitleLower.includes(word));
      });
      return matched || ridesOnDate[0];
    }
  }

  // Pass 2: Generalized Full Title & Significant Core Substring Containment
  // (e.g. "Boonton Coffee Ride" inside "Music Boonton Coffee Ride IHEFC Monster Hill")
  for (const ride of clubRides) {
    const rTitleLower = (ride.title || '').toLowerCase().trim();
    if (!rTitleLower) continue;

    // Check direct full title containment
    if (vTitleLower.includes(rTitleLower)) {
      return ride;
    }

    // Check significant title core (strip generic words like "ride", "club", "annual", "tour", category codes)
    const rCore = rTitleLower.replace(/\b(ride|club|annual|edition|tour|b\*|b|c|a)\b/gi, '').replace(/\s+/g, ' ').trim();
    if (rCore.length >= 6 && vTitleLower.includes(rCore)) {
      return ride;
    }
  }

  // Pass 3: Date Proximity Match within 7 days (if video title has an extracted date)
  if (d) {
    let closestRide = null;
    let minDelta = Infinity;
    for (const existingRide of clubRides) {
      const rDate = existingRide.dateIso;
      if (!rDate) continue;
      const rTitleLower = (existingRide.title || '').toLowerCase();
      const matchesKeyword = DISTINCTIVE_KEYWORDS.some(k => rTitleLower.includes(k) && vTitleLower.includes(k))
        || rTitleLower.split(/[^a-z0-9]+/).some(w => w.length >= 5 && !NOISE_WORDS.has(w) && vTitleLower.includes(w));
      if (matchesKeyword) {
        const dayDelta = Math.abs((new Date(d).getTime() - new Date(rDate).getTime()) / (1000 * 60 * 60 * 24));
        if (dayDelta <= 7 && dayDelta < minDelta) {
          minDelta = dayDelta;
          closestRide = existingRide;
        }
      }
    }
    if (closestRide) {
      return closestRide;
    }
  }

  // Pass 4: Distinctive Weighted Token Overlap Scoring (>= 4 chars, ignoring common stopwords)
  const vTokens = vTitleLower.split(/[^a-z0-9]+/).filter(w => w.length >= 4 && !NOISE_WORDS.has(w));
  let bestRide = null;
  let maxScore = 0;

  for (const ride of clubRides) {
    const rTitleLower = (ride.title || '').toLowerCase();
    const rTokens = rTitleLower.split(/[^a-z0-9]+/).filter(w => w.length >= 4 && !NOISE_WORDS.has(w));
    
    let score = 0;
    for (const token of rTokens) {
      if (vTokens.includes(token)) {
        score += token.length; // Weight longer distinctive words more heavily (e.g. 'boonton' = 7, 'coffee' = 6)
      }
    }

    // Require at least a strong 6-point match (e.g. single 6+ letter token or multiple matching tokens)
    if (score >= 6 && score > maxScore) {
      maxScore = score;
      bestRide = ride;
    }
  }

  return bestRide;
}

/**
 * Aggregates photo albums and YouTube videos into reverse-chronological media rides.
 */
export function buildMediaRides(rawAlbums, videos) {
  const ridesMap = new Map();

  // 1. Ingest albums into official club rides with unique composite ride key
  (rawAlbums || []).forEach(album => {
    const d = album.dateIso || (album.dateStr && album.dateStr.match(/\d{4}-\d{2}-\d{2}/)?.[0]);
    if (!d) return;

    const leadersList = normalizeRideLeaders(album.leaders);

    let membersList = [];
    if (Array.isArray(album.joinedMembers) && album.joinedMembers.length > 0) {
      membersList = album.joinedMembers;
    } else if (Array.isArray(album.members) && album.members.length > 0) {
      membersList = album.members;
    } else if (Array.isArray(album.riders) && album.riders.length > 0) {
      membersList = album.riders;
    } else if (typeof album.joinedMembers === 'string' && album.joinedMembers.trim().length > 0) {
      membersList = album.joinedMembers.split(/[,●•+]|\s*\+\s*\d+/).map(s => s.trim()).filter(Boolean);
    } else if (typeof album.members === 'string' && album.members.trim().length > 0) {
      membersList = album.members.split(/[,●•+]|\s*\+\s*\d+/).map(s => s.trim()).filter(Boolean);
    } else if (typeof album.riders === 'string' && album.riders.trim().length > 0) {
      membersList = album.riders.split(/[,●•+]|\s*\+\s*\d+/).map(s => s.trim()).filter(Boolean);
    }

    const rawRideId = album.rideId || (album.id && !String(album.id).startsWith('ride-') ? album.id : null);
    const cleanRideId = (rawRideId && String(rawRideId).trim().length > 0 && String(rawRideId) !== 'undefined') ? String(rawRideId).trim() : null;
    const isBtcnjRide = Boolean(cleanRideId || album.isBtcnjRide || album.btcnjRide);
    const btcnjUrl = cleanRideId ? `https://btcnj.com/pages/rideSignupEdit?rideid=${cleanRideId}` : (isBtcnjRide ? 'https://btcnj.com/pages/rideScheduleMember' : null);
    const r5mUrl = buildR5mRideUrl({
      ...album,
      rideId: cleanRideId,
      rwgpsUrl: album.rwgpsUrl || null
    }, CONFIG.R5M_BASE_URL);

    const rawDesc = album.description || album.desc || album.notes || null;
    const rawMapName = album.mapName || album.startLoc || album.location || album.start || null;
    const startLoc = rawMapName || 'North Jersey / Hudson Highlands';
    const rideKey = cleanRideId ? `btcnj-${cleanRideId}` : (album.photoAlbumUrl ? `album-${album.photoAlbumUrl}` : (album.id || `date-${d}`));

    // Bidirectional Recency-Aware Reconciliation: Supports both photo additions and deletions
    const existingRides = (typeof currentLoadedRides !== 'undefined' && Array.isArray(currentLoadedRides) && currentLoadedRides.length > 0)
      ? currentLoadedRides
      : (typeof getCachedMediaRides === 'function' ? (getCachedMediaRides() || []) : []);
    const existingRide = existingRides.find(r => 
      (cleanRideId && (String(r.rideId) === String(cleanRideId) || String(r.id) === String(cleanRideId) || String(r.id) === `btcnj-${cleanRideId}`)) ||
      (album.photoAlbumUrl && r.photoAlbumUrl === album.photoAlbumUrl) ||
      r.id === rideKey
    );
    const existingCount = (existingRide && typeof existingRide.photoCount === 'number') ? existingRide.photoCount : 0;
    const incomingCount = (typeof album.photoCount === 'number' && album.photoCount > 0) ? album.photoCount : 0;
    const hasRecentJitSync = Boolean(existingRide && existingRide._lastJitSyncAt && (Date.now() - existingRide._lastJitSyncAt < 5 * 60 * 1000));
    
    // Bidirectional Recency-Aware Reconciliation
    let resolvedPhotoCount = incomingCount;
    if (hasRecentJitSync && existingCount > 0) {
      // Protect fresh live JIT scraped count from being overwritten by stale cached API responses
      resolvedPhotoCount = existingCount;
    } else if (incomingCount > 0) {
      resolvedPhotoCount = incomingCount;
    } else if (existingCount > 0) {
      resolvedPhotoCount = existingCount;
    } else {
      resolvedPhotoCount = 0;
    }
    const resolvedCoverPhoto = (hasRecentJitSync && existingRide.coverPhotoUrl)
      ? existingRide.coverPhotoUrl
      : (album.coverPhotoUrl || (existingRide && existingRide.coverPhotoUrl) || null);

    ridesMap.set(rideKey, {
      id: cleanRideId || `ride-${d}`,
      rideId: cleanRideId,
      isBtcnjRide: isBtcnjRide,
      btcnjUrl: btcnjUrl,
      r5mUrl: r5mUrl,
      rwgpsUrl: album.rwgpsUrl || null,
      title: album.title || 'BTCNJ Club Ride',
      dateIso: d,
      dateStr: album.dateStr || d,
      time24: album.time24 || (existingRide && existingRide.time24) || null,
      timeRaw: album.timeRaw || (existingRide && existingRide.timeRaw) || null,
      startTime: album.startTime || album.time24 || (existingRide && existingRide.startTime) || null,
      meetTime: album.meetTime || (existingRide && existingRide.meetTime) || null,
      leaders: leadersList,
      members: membersList,
      location: startLoc,
      mapName: rawMapName,
      startLoc: startLoc,
      directions: album.directions || null,
      mapUrl: album.mapUrl || null,
      zipCode: album.zipCode || null,
      description: rawDesc,
      desc: rawDesc,
      miles: (album.miles !== undefined && album.miles !== null) ? Number(album.miles) : null,
      climbFt: (album.climbFt !== undefined && album.climbFt !== null) ? Number(album.climbFt) : ((album.elevation !== undefined && album.elevation !== null) ? Number(album.elevation) : null),
      category: album.category || null,
      photoAlbumUrl: album.photoAlbumUrl || null,
      photoCount: resolvedPhotoCount,
      coverPhotoUrl: resolvedCoverPhoto,
      _lastJitSyncAt: (hasRecentJitSync && existingRide._lastJitSyncAt) ? existingRide._lastJitSyncAt : undefined,
      matchedVideos: (Array.isArray(album.videos) && album.videos.length > 0) ? [...album.videos] : []
    });
    hydrateRidePhotoMetadata(ridesMap.get(rideKey));
  });

  // 2. Associate YouTube videos with Club Rides via Multi-Pass Hierarchical Matching Engine
  const clubRidesList = Array.from(ridesMap.values()).filter(r => {
    return Boolean(r.isBtcnjRide || (r.rideId && String(r.rideId).trim().length > 0));
  });

  (videos || []).forEach(v => {
    const matchedClubRide = matchVideoToClubRide(v, clubRidesList);
    if (matchedClubRide) {
      if (!matchedClubRide.matchedVideos.some(m => m.videoId === v.videoId)) {
        matchedClubRide.matchedVideos.push(v);
      }
    }
  });

  // 3. Reconcile Club Rides with Admin Video Overrides
  const allOverrides = getVideoOverrides();
  for (const ride of ridesMap.values()) {
    ride._autoMatchedVideos = [...(ride.matchedVideos || [])];
    const overrideKey = String(ride.rideId || ride.id);
    const override = allOverrides[overrideKey] || (ride.rideId && allOverrides[String(ride.rideId)]);
    if (override) {
      ride.matchedVideos = reconcileRideVideos(ride.matchedVideos, override);
    }
  }

  // 4. Mathematical Set Partitioning for Member Media Pool:
  // V_member_pool = (V_all \ V_club) \ V_member_excluded
  const clubVideoIds = new Set();
  for (const ride of ridesMap.values()) {
    const isClub = Boolean(ride.isBtcnjRide || (ride.rideId && String(ride.rideId).trim().length > 0));
    if (isClub) {
      (ride.matchedVideos || []).forEach(v => {
        if (v && v.videoId) clubVideoIds.add(v.videoId);
      });
    }
  }

  const memberExclusions = new Set(getMemberMediaExclusions());
  const memberCandidateVideos = (videos || []).filter(v => {
    if (!v || !v.videoId) return false;
    if (clubVideoIds.has(v.videoId)) return false; // Strictly minus videos in Club Media
    if (memberExclusions.has(v.videoId)) return false; // Strictly minus videos excluded by admin
    return true;
  });

  // 5. Intelligent Auto-Grouping Clustering Engine for Member Media
  const clusteredMemberRides = clusterMemberVideos(memberCandidateVideos);
  clusteredMemberRides.forEach(mRide => {
    ridesMap.set(mRide.id, mRide);
  });

  // 6. Reconcile with Admin Media Overrides (Ride Conversion & Album Manager)
  const mediaOverrides = getMediaOverrides();
  for (const ride of ridesMap.values()) {
    const d = ride.dateIso;
    const keysToCheck = [
      ride.id,
      ride.rideId,
      d,
      d ? `ride-${d}` : null,
      d ? `ride-yt-${d}` : null
    ].filter(Boolean).map(String);
    let override = null;
    for (const k of keysToCheck) {
      if (mediaOverrides[k]) {
        override = mediaOverrides[k];
        break;
      }
    }
    if (!override && typeof ride.id === 'string' && ride.id.startsWith('cluster-')) {
      const stemPrefix = ride.id.replace(/-(?:\d{4}-\d{2}-\d{2}|undated)$/, '');
      const matchingKey = Object.keys(mediaOverrides).find(k => k.replace(/-(?:\d{4}-\d{2}-\d{2}|undated)$/, '') === stemPrefix);
      if (matchingKey) override = mediaOverrides[matchingKey];
    }
    if (override) {
      if (override.deleted) ride.isDeleted = true;
      if (override.archived) ride.isArchived = true;

      if (override.isPromotedToMediaCenter) {
        ride.isPromotedToMediaCenter = true;
        if (override.assignedDate) {
          ride.dateIso = override.assignedDate;
          ride.dateStr = override.assignedDate;
        }
        if (override.assignedCategory) {
          ride.category = override.assignedCategory;
        }
      }

      if (override.assignedRideId && String(override.assignedRideId).trim()) {
        const assignedId = String(override.assignedRideId).trim();
        ride.rideId = assignedId;
        ride.isBtcnjRide = true;
        ride.btcnjUrl = `https://btcnj.com/pages/rideSignupEdit?rideid=${encodeURIComponent(assignedId)}`;
        ride.r5mUrl = buildR5mRideUrl({ ...ride, rideId: assignedId }, CONFIG.R5M_BASE_URL);

        if (override.telemetry) {
          if (override.telemetry.title) ride.title = override.telemetry.title;
          if (override.telemetry.category) ride.category = override.telemetry.category;
          if (override.telemetry.miles !== undefined && override.telemetry.miles !== null) ride.miles = override.telemetry.miles;
          if (override.telemetry.climbFt !== undefined && override.telemetry.climbFt !== null) ride.climbFt = override.telemetry.climbFt;
          if (override.telemetry.leaders) ride.leaders = normalizeRideLeaders(override.telemetry.leaders);
          const ovMembers = override.telemetry.members || override.telemetry.riders || override.telemetry.joinedMembers;
          if (Array.isArray(ovMembers) && ovMembers.length > 0) {
            ride.members = ovMembers;
          }
          if (override.telemetry.rwgpsUrl) ride.rwgpsUrl = override.telemetry.rwgpsUrl;
          if (override.telemetry.photoAlbumUrl && !ride.photoAlbumUrl) ride.photoAlbumUrl = override.telemetry.photoAlbumUrl;
        }
      } else if (override.assignedRideId === '' || override.revertToOther) {
        ride.rideId = null;
        ride.isBtcnjRide = false;
        ride.btcnjUrl = null;
        if (override.revertToOther) {
          ride.isPromotedToMediaCenter = false;
          if (ride.originalDateIso) {
            ride.dateIso = ride.originalDateIso;
            ride.dateStr = ride.originalDateIso;
          }
        }
      }

      if (override.hidePhotoAlbum) {
        ride.photoAlbumUrl = null;
        ride.photoCount = 0;
      } else if (override.photoAlbumUrl) {
        ride.photoAlbumUrl = override.photoAlbumUrl;
        if (override.photoCount !== undefined && override.photoCount !== null && override.photoCount > 0) {
          ride.photoCount = override.photoCount;
        }
        if (override.coverPhotoUrl && override.coverPhotoUrl.trim().length > 0) {
          ride.coverPhotoUrl = override.coverPhotoUrl;
        }
      }
      // Bidirectional Recency-Aware Reconciliation for Member Clusters & Overridden Cards
      if (ride.photoAlbumUrl && !override.hidePhotoAlbum) {
        const existingRidesPool = (typeof currentLoadedRides !== 'undefined' && Array.isArray(currentLoadedRides) && currentLoadedRides.length > 0)
          ? currentLoadedRides
          : (typeof getCachedMediaRides === 'function' ? (getCachedMediaRides() || []) : []);
        const existingMatch = existingRidesPool.find(r =>
          r && (
            r.id === ride.id ||
            (ride.rideId && String(r.rideId) === String(ride.rideId)) ||
            (r.photoAlbumUrl && r.photoAlbumUrl === ride.photoAlbumUrl)
          )
        );
        if (existingMatch && existingMatch.photoAlbumUrl === ride.photoAlbumUrl) {
          const existingCount = typeof existingMatch.photoCount === 'number' ? existingMatch.photoCount : 0;
          const hasRecentJit = Boolean(existingMatch._lastJitSyncAt && (Date.now() - existingMatch._lastJitSyncAt < 5 * 60 * 1000));
          if ((hasRecentJit && existingCount > 0) || ((!ride.photoCount || ride.photoCount <= 0) && existingCount > 0)) {
            ride.photoCount = existingCount;
          }
          if (existingMatch._lastJitSyncAt) {
            ride._lastJitSyncAt = existingMatch._lastJitSyncAt;
          }
          if (!ride.coverPhotoUrl && existingMatch.coverPhotoUrl) {
            ride.coverPhotoUrl = existingMatch.coverPhotoUrl;
          }
        }
      }
      hydrateRidePhotoMetadata(ride);
    }
  }

  // 7. Ingest Custom Created Rides from Overrides
  for (const [k, cOverride] of Object.entries(mediaOverrides)) {
    if (cOverride && cOverride.isCustom && !cOverride.deleted) {
      const cData = cOverride.customData || {};
      const d = cData.dateIso || cOverride.dateIso || '2026-01-01';
      const customId = cOverride.cardId || k;

      const customRide = {
        id: customId,
        rideId: cOverride.assignedRideId || cData.rideId || null,
        isBtcnjRide: Boolean(cOverride.assignedRideId || cData.rideId),
        btcnjUrl: (cOverride.assignedRideId || cData.rideId) ? `https://btcnj.com/pages/rideSignupEdit?rideid=${encodeURIComponent(cOverride.assignedRideId || cData.rideId)}` : null,
        r5mUrl: null,
        rwgpsUrl: cData.rwgpsUrl || null,
        title: cData.title || cOverride.title || 'Custom Club Ride',
        dateIso: d,
        dateStr: cData.dateStr || d,
        leaders: cData.leaders || [],
        members: cData.members || cData.riders || cData.joinedMembers || [],
        location: cData.location || 'North Jersey',
        miles: cData.miles || null,
        climbFt: cData.climbFt || null,
        category: cData.category || null,
        photoAlbumUrl: cOverride.photoAlbumUrl || cData.photoAlbumUrl || null,
        photoCount: cOverride.photoCount || cData.photoCount || 0,
        coverPhotoUrl: cOverride.coverPhotoUrl || cData.coverPhotoUrl || null,
        matchedVideos: Array.isArray(cData.matchedVideos) ? cData.matchedVideos : [],
        isCustom: true,
        isArchived: Boolean(cOverride.archived),
        isDeleted: false
      };
      if (customRide.photoAlbumUrl) {
        const existingRidesPool = (typeof currentLoadedRides !== 'undefined' && Array.isArray(currentLoadedRides) && currentLoadedRides.length > 0)
          ? currentLoadedRides
          : (typeof getCachedMediaRides === 'function' ? (getCachedMediaRides() || []) : []);
        const existingCustom = existingRidesPool.find(r =>
          r && (r.id === customId || (r.photoAlbumUrl && r.photoAlbumUrl === customRide.photoAlbumUrl))
        );
        if (existingCustom && existingCustom.photoAlbumUrl === customRide.photoAlbumUrl) {
          const existingCount = typeof existingCustom.photoCount === 'number' ? existingCustom.photoCount : 0;
          if ((!customRide.photoCount || customRide.photoCount <= 0) && existingCount > 0) {
            customRide.photoCount = existingCount;
          }
          if (existingCustom._lastJitSyncAt) {
            customRide._lastJitSyncAt = existingCustom._lastJitSyncAt;
          }
        }
      }
      hydrateRidePhotoMetadata(customRide);
      ridesMap.set(customId, customRide);
    }
  }

  // 8. Strict Media Filter: only include rides with photo album OR YouTube videos (exclude deleted)
  const mediaOnlyRides = Array.from(ridesMap.values()).filter(r => {
    if (r.isDeleted) return false;
    const hasAlbum = typeof r.photoAlbumUrl === 'string' && r.photoAlbumUrl.trim().length > 0;
    const hasVideos = Array.isArray(r.matchedVideos) && r.matchedVideos.length > 0;
    return hasAlbum || hasVideos;
  });

  // 9. Sort matched videos
  mediaOnlyRides.forEach(ride => {
    const overrideKey = String(ride.rideId || ride.id);
    const override = allOverrides[overrideKey] || (ride.rideId && allOverrides[String(ride.rideId)]);
    if ((!override || !override.orderedVideoIds || override.orderedVideoIds.length === 0) &&
        Array.isArray(ride.matchedVideos) && ride.matchedVideos.length > 1) {
      ride.matchedVideos = sortMatchedVideos(ride.matchedVideos);
    }
  });

  // 10. Reverse Chronological Sort
  mediaOnlyRides.sort((a, b) => b.dateIso.localeCompare(a.dateIso));

  return mediaOnlyRides;
}

function formatTime(timeStr) {
  if (!timeStr) return '8:30 AM';
  if (/^\d{2}:\d{2}$/.test(timeStr)) {
    const [h, m] = timeStr.split(':').map(Number);
    const suffix = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${String(m).padStart(2, '0')} ${suffix}`;
  }
  return String(timeStr).replace(/^0(\d:)/, '$1');
}

// ============================================================================

// ============================================================================
// PHOTO CENTER STATE & RENDERING ENGINE
// ============================================================================

export const PHOTO_CENTER_STATE = {
  searchQuery: '',
  selectedYear: 'all',
  selectedCategory: 'all',
  viewMode: (() => {
    if (typeof window !== 'undefined' && window.location) {
      try {
        const params = new URLSearchParams(window.location.search || '');
        const layout = (params.get('layout') || params.get('mode') || '').toLowerCase();
        if (layout === 'list' || layout === 'grid') return layout;
        const viewParam = (params.get('view') || '').toLowerCase();
        if (viewParam === 'list' || viewParam === 'grid') return viewParam;
      } catch (_) {}
    }
    if (typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('btcnjcc_photo_view_mode');
      if (saved === 'list' || saved === 'grid') return saved;
    }
    return 'grid';
  })(),
  sortBy: 'date-desc',
  isSearchOpen: false,
  isMobileSearchOpen: false,
  cardsPerPage: (typeof localStorage !== 'undefined' && parseInt(localStorage.getItem('btcnjcc_photo_cards_per_page'), 10)) || 30,
  currentPage: 1
};

export function resolveInitialPhotoViewMode() {
  if (typeof window !== 'undefined' && window.location) {
    try {
      const pathname = (window.location.pathname || '').toLowerCase().replace(/\/+$/, '');
      const params = new URLSearchParams(window.location.search || '');
      const viewParam = (params.get('view') || '').toLowerCase();
      const isPhoto = ['/photos', '/photo', '/p'].includes(pathname) || ['photos', 'photo'].includes(viewParam);
      if (isPhoto) {
        const layout = (params.get('layout') || params.get('mode') || '').toLowerCase();
        if (layout === 'list' || layout === 'grid') return layout;
        if (viewParam === 'list' || viewParam === 'grid') return viewParam;
      }
    } catch (_) {}
  }
  if (typeof localStorage !== 'undefined') {
    const saved = localStorage.getItem('btcnjcc_photo_view_mode');
    if (saved === 'list' || saved === 'grid') return saved;
  }
  return 'grid';
}

export function resolveInitialMediaViewMode() {
  if (typeof window !== 'undefined' && window.location) {
    try {
      const pathname = (window.location.pathname || '').toLowerCase().replace(/\/+$/, '');
      const params = new URLSearchParams(window.location.search || '');
      const viewParam = (params.get('view') || '').toLowerCase();
      const isPhoto = ['/photos', '/photo', '/p'].includes(pathname) || ['photos', 'photo'].includes(viewParam);
      if (!isPhoto) {
        const layout = (params.get('layout') || params.get('mode') || '').toLowerCase();
        if (layout === 'list' || layout === 'grid') return layout;
        if (viewParam === 'list' || viewParam === 'grid') return viewParam;
      }
    } catch (_) {}
  }
  if (typeof localStorage !== 'undefined') {
    const saved = localStorage.getItem('btcnjcc_media_view_mode');
    if (saved === 'list' || saved === 'grid') return saved;
  }
  return 'grid';
}

export function getOptimizedAlbumCoverUrl(ride, width = 600, height = 338) {
  if (!ride) return getCyclingFallbackSvg();
  const albumUrl = ride.photoAlbumUrl ? String(ride.photoAlbumUrl).trim() : '';
  const rideIdStr = ride.rideId ? String(ride.rideId).trim() : (ride.id ? String(ride.id).trim() : '');

  let url = (ride.coverPhotoUrl && typeof ride.coverPhotoUrl === 'string' && ride.coverPhotoUrl.trim().length > 0 ? ride.coverPhotoUrl.trim() : '')
    || (albumUrl && KNOWN_ALBUMS_MAP[albumUrl]?.coverPhotoUrl)
    || (rideIdStr && Object.values(KNOWN_ALBUMS_MAP).find(k => k.rideId && String(k.rideId).trim() === rideIdStr)?.coverPhotoUrl)
    || '';

  if (url && typeof url === 'string') {
    if (url.includes('googleusercontent.com/pw/') || url.includes('googleusercontent.com/')) {
      const clean = url.replace(/=.*$/, '');
      return `${clean}=w${width}-h${height}`;
    }
    return url;
  }
  return getCyclingFallbackSvg();
}

export function extractAlbumYear(ride) {
  if (ride.dateIso && /^\d{4}/.test(ride.dateIso)) {
    return parseInt(ride.dateIso.slice(0, 4), 10);
  }
  if (ride.dateStr) {
    const match = String(ride.dateStr).match(/\b(20\d{2})\b/);
    if (match) return parseInt(match[1], 10);
    const parsed = new Date(ride.dateStr);
    if (!isNaN(parsed.getFullYear())) return parsed.getFullYear();
  }
  return new Date().getFullYear();
}

export function formatAlbumDate(dateStr, dateIso) {
  const d = dateIso || dateStr;
  if (!d) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [year, month, day] = d.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const mName = months[parseInt(month, 10) - 1] || month;
    return `${mName} ${parseInt(day, 10)}, ${year}`;
  }
  return String(d);
}

export function filterPhotoAlbums(albums, state = PHOTO_CENTER_STATE) {
  if (!Array.isArray(albums)) return [];
  const q = (state.searchQuery || '').trim();
  const yearFilter = state.selectedYear;
  const catFilter = state.selectedCategory;

  return albums.filter(ride => {
    if (!ride || !ride.photoAlbumUrl || ride.isDeleted) return false;

    const rideYear = getRideYear(ride);

    // Year filter (if explicitly filtered by year state)
    if (yearFilter && yearFilter !== 'all') {
      if (String(rideYear) !== String(yearFilter)) return false;
    }

    // Category filter
    if (catFilter && catFilter !== 'all') {
      const cleanCat = String(ride.category || '').trim().toLowerCase();
      const targetCat = String(catFilter).trim().toLowerCase();
      if (cleanCat !== targetCat && !cleanCat.startsWith(targetCat)) return false;
    }

    // Multi-token search across album title, leaders, roster, year, and dates (excluding video titles)
    if (q) {
      const cleanQ = q.trim().toLowerCase();
      const tokens = cleanQ.split(/\s+/).filter(Boolean);
      const title = (ride.title || '').toLowerCase();
      const rideYear = getRideYear(ride).toLowerCase();
      const dateStr = (ride.dateIso || ride.dateStr || '').toLowerCase();
      const formattedDate = (formatAlbumDate(ride.dateStr, ride.dateIso) || '').toLowerCase();
      const rawLeadersList = Array.isArray(ride.leaders) ? ride.leaders : (ride.leaders ? [ride.leaders] : []);
      const enrichedLeaders = rawLeadersList.map(n => formatLeaderDisplayName(n, { firstNameOnly: false })).join(' ').toLowerCase();
      const rawLeaders = rawLeadersList.join(' ').toLowerCase();
      const rawMembers = Array.isArray(ride.members || ride.joinedMembers || ride.riders) ? (ride.members || ride.joinedMembers || ride.riders).join(' ').toLowerCase() : String(ride.members || ride.joinedMembers || ride.riders || '').toLowerCase();
      const category = (ride.category || '').toLowerCase();
      const location = (ride.location || ride.start || '').toLowerCase();
      const albumCorpus = `${title} ${rideYear} ${dateStr} ${formattedDate} ${rawLeaders} ${enrichedLeaders} ${rawMembers} ${category} ${location}`;
      if (!tokens.every(token => albumCorpus.includes(token))) return false;
    }

    return true;
  }).sort((a, b) => {
    if (state.sortBy === 'date-asc') {
      return (a.dateIso || '').localeCompare(b.dateIso || '');
    }
    if (state.sortBy === 'photos-desc') {
      return (b.photoCount || 0) - (a.photoCount || 0);
    }
    if (state.sortBy === 'miles-desc') {
      return (b.miles || 0) - (a.miles || 0);
    }
    // Default: date-desc
    return (b.dateIso || '').localeCompare(a.dateIso || '');
  });
}

export function renderCurrentActiveView(rides = null, container = null, countBadge = null) {
  const mountContainer = container || document.getElementById('rides-container');
  const targetBadge = countBadge || document.getElementById('rides-count-badge');
  const targetRides = rides || currentLoadedRides || getCachedMediaRides() || buildMediaRides(FALLBACK_ALBUM_RIDES, PLAYLIST_VIDEOS);

  if (currentView === 'photos') {
    renderPhotoCenter(targetRides, mountContainer);
  } else {
    const spotlightId = activeSpotlightRideId || getSpotlightQueryId();
    if (spotlightId) {
      renderSpotlightView(spotlightId, targetRides, mountContainer, targetBadge);
    } else {
      renderRides(targetRides, mountContainer, targetBadge);
    }
  }
}

export function switchView(targetView, updateUrl = true, options = {}) {
  const previousView = currentView;
  currentView = targetView === 'photos' ? 'photos' : 'videos';
  activeSpotlightRideId = null;

  // Update header shortcuts active state
  const photoBtn = document.getElementById('header-shortcut-photos');
  if (photoBtn) {
    photoBtn.classList.toggle('is-active', currentView === 'photos');
    photoBtn.setAttribute('aria-pressed', currentView === 'photos' ? 'true' : 'false');
  }

  // Sync URL state: '/photos' or '/'
  if (updateUrl && typeof window !== 'undefined' && window.history) {
    const targetPath = currentView === 'photos' ? '/photos' : '/';
    let queryStr = '';
    if (currentView === 'photos' && PHOTO_CENTER_STATE.viewMode === 'list') {
      queryStr = '?layout=list';
    } else if (currentView === 'videos' && PAGINATION_STATE.viewMode === 'list') {
      queryStr = '?layout=list';
    }
    const cleanUrl = targetPath + queryStr + (window.location.hash || '');
    if ((window.location.pathname + window.location.search) !== (targetPath + queryStr)) {
      if (typeof window.history.pushState === 'function') {
        window.history.pushState(null, '', cleanUrl);
      } else if (typeof window.history.replaceState === 'function') {
        window.history.replaceState(null, '', cleanUrl);
      }
    }
  }

  const container = document.getElementById('rides-container');
  const countBadge = document.getElementById('rides-count-badge');
  const rides = (currentLoadedRides && currentLoadedRides.length > 0) ? currentLoadedRides : (getCachedMediaRides() || buildMediaRides(FALLBACK_ALBUM_RIDES, PLAYLIST_VIDEOS));

  const peelDir = options.direction || (previousView !== currentView ? 'tab_switch' : 'forward');
  transitionWithPeel(container, async (mountEl) => {
    renderCurrentActiveView(rides, mountEl, countBadge);
  }, { direction: peelDir, skipAnimation: options.skipAnimation });

  // Notify parent iframe if embedded
  if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
    window.parent.postMessage({
      type: 'BTCNJCC_VIEW_CHANGED',
      view: currentView,
      layout: currentView === 'photos' ? PHOTO_CENTER_STATE.viewMode : PAGINATION_STATE.viewMode
    }, '*');
  }
}

export function syncPhotoViewModeUrl(mode) {
  if (typeof window === 'undefined' || !window.history || typeof window.history.replaceState !== 'function') return;
  try {
    const url = new URL(window.location.href);
    if (mode === 'list') {
      url.searchParams.set('layout', 'list');
    } else {
      url.searchParams.delete('layout');
      url.searchParams.delete('mode');
    }
    const cleanSearch = url.searchParams.toString();
    const targetUrl = url.pathname + (cleanSearch ? '?' + cleanSearch : '') + (url.hash || '');
    if ((window.location.pathname + window.location.search) !== (url.pathname + (cleanSearch ? '?' + cleanSearch : ''))) {
      window.history.replaceState(window.history.state || null, '', targetUrl);
    }
  } catch (_) {}

  // Cross-origin iframe postMessage to parent host if embedded
  if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
    try {
      window.parent.postMessage({
        type: 'BTCNJCC_VIEW_CHANGED',
        view: 'photos',
        layout: mode
      }, '*');
    } catch (_) {}
  }
}

export function syncMediaViewModeUrl(mode) {
  if (typeof window === 'undefined' || !window.history || typeof window.history.replaceState !== 'function') return;
  try {
    const url = new URL(window.location.href);
    if (mode === 'list') {
      url.searchParams.set('layout', 'list');
    } else {
      url.searchParams.delete('layout');
      url.searchParams.delete('mode');
      if (url.searchParams.get('view') === 'list' || url.searchParams.get('view') === 'grid') {
        url.searchParams.delete('view');
      }
    }
    const cleanSearch = url.searchParams.toString();
    const targetUrl = url.pathname + (cleanSearch ? '?' + cleanSearch : '') + (url.hash || '');
    if ((window.location.pathname + window.location.search) !== (url.pathname + (cleanSearch ? '?' + cleanSearch : ''))) {
      window.history.replaceState(window.history.state || null, '', targetUrl);
    }
  } catch (_) {}

  // Cross-origin iframe postMessage to parent host if embedded
  if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
    try {
      window.parent.postMessage({
        type: 'BTCNJCC_VIEW_CHANGED',
        view: 'videos',
        layout: mode
      }, '*');
    } catch (_) {}
  }
}

export function renderPhotoCenter(ridesData, container) {
  const mountEl = container || document.getElementById('rides-container');
  if (!mountEl) return;

  const isEmbed = isEmbeddedMode();
  if (isEmbed) {
    document.body.classList.add('is-embedded');
  } else {
    document.body.classList.remove('is-embedded');
  }

  const isHeadless = isHeadlessMode();
  if (isHeadless) {
    document.body.classList.add('headless-mode');
  } else {
    document.body.classList.remove('headless-mode');
  }

  const rides = ridesData || currentLoadedRides || getCachedMediaRides() || buildMediaRides(FALLBACK_ALBUM_RIDES, PLAYLIST_VIDEOS);

  // Strict album rides filter
  const allAlbumRides = (rides || []).filter(r => r && typeof r.photoAlbumUrl === 'string' && r.photoAlbumUrl.trim().length > 0 && !r.isDeleted);
  allAlbumRides.forEach(hydrateRidePhotoMetadata);

  // Autonomous JIT SWR: If a specific ride is highlighted via ?ride=, re-validate it immediately
  const targetRideId = getSpotlightQueryId();
  if (targetRideId) {
    const activeRide = allAlbumRides.find(r => String(r.rideId || '').trim() === targetRideId || String(r.id || '').trim() === targetRideId || String(r.id || '').trim() === `btcnj-${targetRideId}`);
    if (activeRide) proactiveSyncRideMedia(activeRide, false);
  }

  const filteredAlbums = filterPhotoAlbums(allAlbumRides, PHOTO_CENTER_STATE);

  const rawQ = PHOTO_CENTER_STATE.searchQuery || '';
  const isSearchActive = rawQ.trim().length > 0;
  const isGrid = PHOTO_CENTER_STATE.viewMode === 'grid';

  // Build smart chronological items (ascending future -> divider -> descending past)
  const allGridItems = buildSmartChronologicalGridItems(filteredAlbums, {
    isSearchActive,
    currentYear: '2026'
  });

  // Pagination slicing
  const totalItems = allGridItems.length;
  const perPage = PHOTO_CENTER_STATE.cardsPerPage || 30;
  let displayedItems = [];
  let totalPages = 1;

  if (perPage === -1 || isSearchActive) {
    displayedItems = allGridItems;
    totalPages = 1;
  } else {
    totalPages = Math.max(1, Math.ceil(totalItems / perPage));
    if (PHOTO_CENTER_STATE.currentPage > totalPages) {
      PHOTO_CENTER_STATE.currentPage = totalPages;
    }
    if (PHOTO_CENTER_STATE.currentPage < 1) {
      PHOTO_CENTER_STATE.currentPage = 1;
    }
    const startIndex = (PHOTO_CENTER_STATE.currentPage - 1) * perPage;
    displayedItems = allGridItems.slice(startIndex, startIndex + perPage);
  }

  const isPhotoSearchExpanded = Boolean(PHOTO_CENTER_STATE.isMobileSearchOpen || isSearchActive);

  let html = `
    <section class="content-panel photo-center-panel" id="photo-center-panel">
      <!-- Photo Center Partition Header & Integrated Sticky Cockpit -->
      <div class="partition-header integrated-cockpit photo-cockpit-header ${isPhotoSearchExpanded ? 'search-expanded' : ''}">
        <div class="partition-title-group">
          <button type="button" id="btn-photo-back-home" class="photo-back-btn" title="Return to All Media" aria-label="Return to All Media">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
            <span>All Media</span>
          </button>
          <span class="panel-tag tag-amber">PHOTO CENTER</span>
        </div>

        <!-- Live Instant Search Bar (Unified Architecture with Amber Styling - Left Anchored) -->
        <div class="header-search-wrapper photo-search-wrapper">
          <span class="header-search-icon photo-search-icon" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </span>
          <input
            type="text"
            id="photo-search-input"
            class="header-search-input photo-search-input"
            placeholder="Search rides, leads, year..."
            value="${escapeHtml(rawQ)}"
            autocomplete="off"
            spellcheck="false"
            aria-label="Search rides by title, leader, or year"
          />
          <button
            type="button"
            id="photo-clear-search-btn"
            class="header-search-clear-btn photo-search-clear-btn"
            aria-label="Clear search text"
            title="Clear search"
            style="${isSearchActive ? '' : 'display: none;'}"
          >&times;</button>
          <button
            type="button"
            id="btn-collapse-photo-search"
            class="header-search-collapse-btn photo-search-collapse-btn"
            aria-label="Close search"
            title="Close search"
          >&times;</button>
        </div>

        <!-- Integrated Right Controls -->
        <div class="cockpit-controls photo-cockpit-controls">
          <!-- Mobile Search Toggle Button (Hidden on Desktop) -->
          <button
            type="button"
            id="btn-photo-mobile-search-toggle"
            class="btn-mobile-search-toggle photo-mobile-search-toggle ${isPhotoSearchExpanded ? 'active' : ''}"
            aria-label="Toggle search"
            title="Toggle search"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </button>

          <!-- Owner Media Studio Trigger -->
          <button
            type="button"
            id="btn-open-owner-media-studio"
            class="btn btn-secondary photo-cockpit-studio-btn"
            title="Open Owner Media Studio"
            aria-label="Open Owner Media Studio"
          >
            <svg class="btn-icon-svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
            <span class="btn-text">Studio</span>
          </button>

          <!-- Admin Annual Year ZIP Trigger (Admin Only) -->
          ${typeof sessionStorage !== 'undefined' && sessionStorage.getItem('btcnjcc_admin_authed') === 'true' ? `
          <button
            type="button"
            id="btn-admin-annual-zip"
            class="btn btn-secondary photo-cockpit-studio-btn admin-annual-zip-btn"
            title="Admin Annual Media Center Bulk ZIP"
            aria-label="Admin Annual Media Center Bulk ZIP"
          >
            <svg class="btn-icon-svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
            <span class="btn-text">Year ZIP</span>
          </button>
          ` : ''}

          <!-- View Mode Switcher (Grid / List - Hidden on Mobile) -->
          <div class="photo-view-switch-group" role="group" aria-label="View Mode Switcher">
            <button type="button" id="btn-photo-mode-grid" class="photo-view-mode-btn ${isGrid ? 'active' : ''}" title="Visual Card Grid" aria-pressed="${isGrid}">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M4 4h4v4H4zm6 0h4v4h-4zm6 0h4v4h-4zM4 10h4v4H4zm6 0h4v4h-4zm6 0h4v4h-4zM4 16h4v4H4zm6 0h4v4h-4zm6 0h4v4h-4z"/></svg>
              <span class="btn-text">Grid</span>
            </button>
            <button type="button" id="btn-photo-mode-list" class="photo-view-mode-btn ${!isGrid ? 'active' : ''}" title="Compact List" aria-pressed="${!isGrid}">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
              <span class="btn-text">List</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Albums Container -->
      <div class="${isGrid ? 'photo-center-grid' : 'photo-center-list'}" id="photo-albums-container" role="list">
  `;

  if (filteredAlbums.length === 0) {
    html += `
      <div class="photo-empty-state">
        <div class="empty-icon">📸</div>
        <div class="empty-msg">No photo albums found matching your filter criteria.</div>
        <button type="button" id="btn-reset-photo-filters" class="btn btn-secondary" style="margin-top: 12px;">Reset Filters</button>
      </div>
    `;
  } else {
    displayedItems.forEach(item => {
      if (item.isHistoricalDivider) {
        if (isGrid) {
          html += renderPhotoHistoricalDividerCardHtml(item);
        }
        return;
      }

      if (item.isMilestone) {
        if (isGrid) {
          html += renderPhotoYearMilestoneCardHtml(item);
        } else {
          html += `
            <div class="photo-list-year-divider" role="separator" aria-label="Season ${escapeHtml(item.year)}">
              <div class="year-divider-line"></div>
              <div class="year-divider-badge">
                <span class="year-num">${escapeHtml(item.year)}</span>
                <span class="year-meta">${item.stats.albumCount || item.stats.rideCount} Albums</span>
              </div>
              <div class="year-divider-line"></div>
            </div>
          `;
        }
        return;
      }

      const ride = item.ride;
      const year = extractAlbumYear(ride);
      const dateDisplay = formatAlbumDate(ride.dateStr, ride.dateIso);
      const photoCount = typeof ride.photoCount === 'number' ? ride.photoCount : 0;
      const thumbUrl = getOptimizedAlbumCoverUrl(ride, 600, 338);
      const compactThumbUrl = getOptimizedAlbumCoverUrl(ride, 160, 160);
      const rawTitle = ride.title || 'BTCNJ Ride';
      const rawLeadersList = normalizeRideLeaders(ride.leaders);
      const leaderFirstNames = rawLeadersList.map(n => formatLeaderDisplayName(n, { firstNameOnly: true })).filter(Boolean).join(', ');

      const photoTemporalTier = getRideTemporalTier(ride);
      const photoPendingBadgeHtml = photoTemporalTier ? buildPendingBadgeHtml(photoTemporalTier) : '';

      const photoSearchTokens = isSearchActive ? rawQ.trim().toLowerCase().split(/\s+/).filter(Boolean) : [];
      const highlightedPhotoTitle = isSearchActive ? highlightSearchText(rawTitle, photoSearchTokens) : escapeHtml(rawTitle);
      const photoSearchContext = isSearchActive ? extractSearchMatchContext(ride, rawQ) : null;
      const photoSearchContextHtml = photoSearchContext ? `<div class="card-search-context photo-search-context">${photoSearchContext.html}</div>` : '';

      const statsItems = [];
      if (ride.miles && Number(ride.miles) > 0) {
        const miVal = Number(ride.miles);
        const miStr = miVal % 1 === 0 ? String(Math.round(miVal)) : miVal.toFixed(1);
        statsItems.push(`${miStr} mi`);
      }
      if (ride.climbFt && Number(ride.climbFt) > 0) {
        statsItems.push(`${Number(ride.climbFt).toLocaleString()} ft`);
      }
      if (leaderFirstNames) {
        const leaderSvg = `<span class="photo-leader-icon" aria-hidden="true"><svg class="leader-icon-svg" width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg></span>`;
        statsItems.push(`<span class="photo-leaders-wrap">${leaderSvg}<span>${escapeHtml(leaderFirstNames)}</span></span>`);
      }
      const statsLineHtml = statsItems.length > 0
        ? `<div class="photo-card-stats-line" title="${escapeHtml(statsItems.map(s => s.replace(/<[^>]*>/g, '')).join(' · '))}">${statsItems.join(' · ')}</div>`
        : '';

      if (isGrid) {
        // Visual Card Grid Layout (Single Primary Button)
        html += `
          <article class="photo-album-card ${photoTemporalTier ? `is-pending-ride tier-${photoTemporalTier}` : ''}" role="listitem" data-ride-id="${escapeHtml(ride.rideId || ride.id || '')}" ${photoTemporalTier ? `data-temporal-tier="${photoTemporalTier}"` : ''}>
            <!-- 16:9 Cover Thumbnail Container -->
            <div class="photo-thumb-wrap">
              <img src="${escapeHtml(thumbUrl)}" alt="${escapeHtml(rawTitle)}" class="photo-card-img" loading="lazy" decoding="async" referrerpolicy="no-referrer" onerror="window.handlePhotoCoverImgError ? window.handlePhotoCoverImgError(this, '${escapeHtml(ride.rideId || ride.id || '')}') : (this.onerror=null, this.src=(window.getCyclingFallbackSvg ? window.getCyclingFallbackSvg() : '/cycling-blueprint.png'));">
              <div class="photo-card-badges-top">
                ${buildUnifiedDateCapsuleHtml(ride, photoTemporalTier, 'photo-card-unified-capsule photo-card-date-badge')}
              </div>
            </div>

            <!-- Card Body & Telemetry -->
            <div class="photo-card-body">
              <h3 class="photo-card-title" title="${escapeHtml(rawTitle)}">${highlightedPhotoTitle}</h3>
              ${photoSearchContextHtml}

              ${statsLineHtml}

              <!-- Action Links (Single Primary Button) -->
              <div class="photo-card-actions photo-card-actions-single">
                <a href="${escapeHtml(ride.photoAlbumUrl)}" target="_blank" rel="noopener noreferrer" class="btn btn-primary btn-open-album btn-open-album-single" title="Open Google Photos Album (${photoCount} ${photoCount === 1 ? 'Photo' : 'Photos'})">
                  <span>↗ Open Album (${photoCount})</span>
                </a>
                <button type="button" class="btn btn-secondary btn-card-owner-studio" data-action="open-owner-studio" data-ride-id="${escapeHtml(ride.rideId || ride.id || '')}" data-album-url="${escapeHtml(ride.photoAlbumUrl || '')}" data-ride-title="${escapeHtml(rawTitle)}" data-ride-leaders="${escapeHtml(rawLeadersList.join(','))}" title="Owner Media Studio">
                  <span aria-hidden="true">🏷️</span>
                </button>
              </div>
            </div>
          </article>
        `;
      } else {
        // Compact List Row Layout (Single Primary Button)
        html += `
          <article class="photo-list-row ${photoTemporalTier ? `is-pending-ride tier-${photoTemporalTier}` : ''}" role="listitem" data-ride-id="${escapeHtml(ride.rideId || ride.id || '')}" ${photoTemporalTier ? `data-temporal-tier="${photoTemporalTier}"` : ''}>
            <div class="photo-list-thumb">
              <img src="${escapeHtml(compactThumbUrl)}" alt="${escapeHtml(rawTitle)}" loading="lazy" decoding="async" referrerpolicy="no-referrer" onerror="window.handlePhotoCoverImgError ? window.handlePhotoCoverImgError(this, '${escapeHtml(ride.rideId || ride.id || '')}') : (this.onerror=null, this.src=(window.getCyclingFallbackSvg ? window.getCyclingFallbackSvg() : '/cycling-blueprint.png'));">
              <span class="photo-list-count-overlay">${photoCount} 📸</span>
            </div>
            <div class="photo-list-content">
              <div class="photo-list-header-row">
                ${photoPendingBadgeHtml}
                <span class="photo-list-date">${escapeHtml(dateDisplay)}</span>
              </div>
              <h3 class="photo-list-title">${highlightedPhotoTitle}</h3>
              ${photoSearchContextHtml}
              ${statsLineHtml ? `<div class="photo-list-sub">${statsItems.join(' · ')}</div>` : ''}
            </div>
            <div class="photo-list-actions">
              <a href="${escapeHtml(ride.photoAlbumUrl)}" target="_blank" rel="noopener noreferrer" class="btn btn-primary btn-sm btn-open-album" title="Open Album (${photoCount} ${photoCount === 1 ? 'Photo' : 'Photos'})">↗ Album (${photoCount})</a>
              <button type="button" class="btn btn-secondary btn-sm btn-card-owner-studio" data-action="open-owner-studio" data-ride-id="${escapeHtml(ride.rideId || ride.id || '')}" data-album-url="${escapeHtml(ride.photoAlbumUrl || '')}" data-ride-title="${escapeHtml(rawTitle)}" data-ride-leaders="${escapeHtml(rawLeadersList.join(','))}" title="Owner Media Studio">🏷️</button>
            </div>
          </article>
        `;
      }
    });
  }

  html += `
      </div>
  `;

  // Yellow-Themed Bottom Pagination HUD
  let paginationDeckHtml = '';
  if (!isSearchActive && totalPages > 1) {
    const currentPage = PHOTO_CENTER_STATE.currentPage;
    const hasPrev = currentPage > 1;
    const hasNext = currentPage < totalPages;

    let pageNumbersHtml = '';
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + 4);
    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }

    for (let p = startPage; p <= endPage; p++) {
      const isActive = p === currentPage;
      pageNumbersHtml += `
        <button type="button" class="pagination-page-btn ${isActive ? 'active' : ''}" data-page="${p}" ${isActive ? 'aria-current="page"' : ''}>
          ${p}
        </button>
      `;
    }

    const startItem = (currentPage - 1) * perPage + 1;
    const endItem = Math.min(currentPage * perPage, totalItems);

    paginationDeckHtml = `
      <div class="feed-pagination-deck photo-pagination-deck" id="photo-pagination-deck" role="navigation" aria-label="Photo Feed Pagination">
        <button type="button" id="photo-prev-page-btn" class="pagination-arrow-btn" ${hasPrev ? '' : 'disabled'} aria-label="Previous Page" title="Previous Page">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
        </button>
        <div class="pagination-page-numbers">
          ${pageNumbersHtml}
        </div>
        <button type="button" id="photo-next-page-btn" class="pagination-arrow-btn" ${hasNext ? '' : 'disabled'} aria-label="Next Page" title="Next Page">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
        </button>
        <div class="pagination-range-text">
          <span>Showing ${startItem}–${endItem} of ${totalItems} items</span>
        </div>
        <div class="pagination-combo-group">
          <select id="photo-cards-per-page-select" class="combo-select telegraphic-select photo-telegraphic-select" aria-label="Albums per page" title="Albums per page">
            <option value="15" ${perPage === 15 ? 'selected' : ''}>15</option>
            <option value="30" ${perPage === 30 ? 'selected' : ''}>30</option>
            <option value="60" ${perPage === 60 ? 'selected' : ''}>60</option>
            <option value="-1" ${perPage === -1 ? 'selected' : ''}>All</option>
          </select>
        </div>
      </div>
    `;
  } else if (!isSearchActive && totalItems > 0) {
    paginationDeckHtml = `
      <div class="feed-pagination-deck single-page-deck photo-pagination-deck" id="photo-pagination-deck" role="navigation" aria-label="Photo Feed Pagination">
        <div class="pagination-single-note">
          <span>Showing all items</span>
        </div>
        <div class="pagination-combo-group">
          <select id="photo-cards-per-page-select" class="combo-select telegraphic-select photo-telegraphic-select" aria-label="Albums per page" title="Albums per page">
            <option value="15" ${perPage === 15 ? 'selected' : ''}>15</option>
            <option value="30" ${perPage === 30 ? 'selected' : ''}>30</option>
            <option value="60" ${perPage === 60 ? 'selected' : ''}>60</option>
            <option value="-1" ${perPage === -1 ? 'selected' : ''}>All</option>
          </select>
        </div>
      </div>
    `;
  }

  html += paginationDeckHtml;
  html += `
    </section>
  `;

  // Preserve search input focus and selection range across re-renders
  const activeEl = typeof document !== 'undefined' ? document.activeElement : null;
  const isInputActive = activeEl && activeEl.id === 'photo-search-input';
  const cursorStart = isInputActive ? activeEl.selectionStart : null;
  const cursorEnd = isInputActive ? activeEl.selectionEnd : null;

  mountEl.innerHTML = html;

  if (isInputActive) {
    const newInput = document.getElementById('photo-search-input');
    if (newInput) {
      newInput.focus();
      if (cursorStart !== null && cursorEnd !== null) {
        newInput.setSelectionRange(cursorStart, cursorEnd);
      }
    }
  }

  // Bind Photo Center Interactive Controls
  bindPhotoCenterControls(mountEl, rides);
}

export let photoSearchDebounceTimer = null;

export function executeStablePaginationUpdate(renderFn) {
  const deck = (typeof document !== 'undefined')
    ? (document.getElementById('feed-pagination-deck') || document.getElementById('photo-pagination-deck'))
    : null;
  const rectBefore = deck ? deck.getBoundingClientRect() : null;

  if (typeof renderFn === 'function') {
    renderFn();
  }

  if (rectBefore && typeof window !== 'undefined' && typeof window.scrollBy === 'function') {
    const deckAfter = document.getElementById('feed-pagination-deck') || document.getElementById('photo-pagination-deck');
    if (deckAfter) {
      const rectAfter = deckAfter.getBoundingClientRect();
      const deltaY = rectAfter.top - rectBefore.top;
      if (Math.abs(deltaY) > 0.5) {
        window.scrollBy({ top: deltaY, behavior: 'instant' });
      }
    }
  }
}

export function bindPhotoCenterControls(container, rides) {
  if (!container) return;

  const backBtn = container.querySelector('#btn-photo-back-home');
  const searchInput = container.querySelector('#photo-search-input');
  const clearBtn = container.querySelector('#photo-clear-search-btn');
  const resetBtn = container.querySelector('#btn-reset-photo-filters');
  const mobileToggleBtn = container.querySelector('#btn-photo-mobile-search-toggle');
  const collapseBtn = container.querySelector('#btn-collapse-photo-search');
  const cockpitHeader = container.querySelector('.partition-header.integrated-cockpit.photo-cockpit-header');
  const gridBtn = container.querySelector('#btn-photo-mode-grid');
  const listBtn = container.querySelector('#btn-photo-mode-list');
  const perPageSelect = container.querySelector('#photo-cards-per-page-select');
  const prevBtn = container.querySelector('#photo-prev-page-btn');
  const nextBtn = container.querySelector('#photo-next-page-btn');

  // Back to Rides Home
  if (backBtn) {
    backBtn.addEventListener('click', (e) => {
      e.preventDefault();
      switchView('videos', true);
    });
  }

  // Debounced search input typing
  if (searchInput) {
    searchInput.addEventListener('input', () => {
      clearTimeout(photoSearchDebounceTimer);
      const query = searchInput.value;
      if (clearBtn) {
        clearBtn.style.display = query.trim().length > 0 ? 'flex' : 'none';
      }
      photoSearchDebounceTimer = setTimeout(() => {
        PHOTO_CENTER_STATE.searchQuery = query;
        PHOTO_CENTER_STATE.currentPage = 1;
        renderPhotoCenter(rides, container);
      }, 100);
    });
  }

  const handleClear = () => {
    if (searchInput) searchInput.value = '';
    if (clearBtn) clearBtn.style.display = 'none';
    PHOTO_CENTER_STATE.searchQuery = '';
    PHOTO_CENTER_STATE.currentPage = 1;
    if (searchInput) searchInput.focus();
    renderPhotoCenter(rides, container);
  };

  // Clear search
  if (clearBtn) {
    clearBtn.addEventListener('click', handleClear);
  }

  // Reset filters
  if (resetBtn) {
    resetBtn.addEventListener('click', handleClear);
  }

  // Mobile search toggle button
  if (mobileToggleBtn) {
    mobileToggleBtn.addEventListener('click', () => {
      PHOTO_CENTER_STATE.isMobileSearchOpen = !PHOTO_CENTER_STATE.isMobileSearchOpen;
      if (cockpitHeader) {
        cockpitHeader.classList.toggle('search-expanded', PHOTO_CENTER_STATE.isMobileSearchOpen);
      }
      mobileToggleBtn.classList.toggle('active', PHOTO_CENTER_STATE.isMobileSearchOpen);
      if (PHOTO_CENTER_STATE.isMobileSearchOpen && searchInput) {
        searchInput.focus();
      }
    });
  }

  // Mobile search collapse button
  if (collapseBtn) {
    collapseBtn.addEventListener('click', () => {
      PHOTO_CENTER_STATE.isMobileSearchOpen = false;
      if (cockpitHeader) {
        cockpitHeader.classList.remove('search-expanded');
      }
      if (mobileToggleBtn) {
        mobileToggleBtn.classList.remove('active');
      }
      if (searchInput && searchInput.value) {
        handleClear();
      } else if (searchInput) {
        searchInput.blur();
      }
    });
  }

  // View mode switcher
  if (gridBtn) {
    gridBtn.addEventListener('click', () => {
      PHOTO_CENTER_STATE.viewMode = 'grid';
      try { localStorage.setItem('btcnjcc_photo_view_mode', 'grid'); } catch (_) {}
      syncPhotoViewModeUrl('grid');
      renderPhotoCenter(rides, container);
    });
  }

  if (listBtn) {
    listBtn.addEventListener('click', () => {
      PHOTO_CENTER_STATE.viewMode = 'list';
      try { localStorage.setItem('btcnjcc_photo_view_mode', 'list'); } catch (_) {}
      syncPhotoViewModeUrl('list');
      renderPhotoCenter(rides, container);
    });
  }

  // Cards per page select
  if (perPageSelect) {
    perPageSelect.addEventListener('change', () => {
      const val = parseInt(perPageSelect.value, 10);
      PHOTO_CENTER_STATE.cardsPerPage = val;
      PHOTO_CENTER_STATE.currentPage = 1;
      try { localStorage.setItem('btcnjcc_photo_cards_per_page', String(val)); } catch (_) {}
      executeStablePaginationUpdate(() => {
        renderPhotoCenter(rides, container);
      });
    });
  }

  // Pagination navigation
  if (prevBtn) {
    prevBtn.addEventListener('click', () => {
      if (PHOTO_CENTER_STATE.currentPage > 1) {
        PHOTO_CENTER_STATE.currentPage--;
        executeStablePaginationUpdate(() => {
          renderPhotoCenter(rides, container);
        });
      }
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      PHOTO_CENTER_STATE.currentPage++;
      executeStablePaginationUpdate(() => {
        renderPhotoCenter(rides, container);
      });
    });
  }

  container.querySelectorAll('#photo-pagination-deck .pagination-page-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const p = parseInt(btn.getAttribute('data-page'), 10);
      if (p && p !== PHOTO_CENTER_STATE.currentPage) {
        PHOTO_CENTER_STATE.currentPage = p;
        executeStablePaginationUpdate(() => {
          renderPhotoCenter(rides, container);
        });
      }
    });
  });

  // JIT SWR Media Count refresh on Photo Center album button clicks
  container.querySelectorAll('.btn-open-album, .photo-album-card, .photo-list-row').forEach(el => {
    el.addEventListener('click', () => {
      const cardEl = el.closest('.photo-album-card, .photo-list-row') || el;
      const rideId = cardEl.getAttribute('data-ride-id') || cardEl.getAttribute('data-card-id');
      const ride = rides.find(r => r.id === rideId || r.rideId === rideId || `btcnj-${r.rideId}` === rideId);
      if (ride) {
        proactiveSyncRideMedia(ride, true);
      }
    });
  });

  // Cross-Origin PostMessage support for R5m Embed
  container.querySelectorAll('.btn-r5m-link').forEach(btn => {
    btn.addEventListener('click', (e) => {
      if (isEmbeddedMode()) {
        e.preventDefault();
        const rideId = btn.getAttribute('data-ride-id');
        const rwgpsUrl = btn.getAttribute('data-rwgps-url');
        const title = btn.getAttribute('data-title');
        if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
          window.parent.postMessage({
            type: 'BTCNJCC_SELECT_RIDE',
            rideId,
            rwgpsUrl,
            title
          }, '*');
        }
      }
    });
  });

  // Synchronize Historical Divider Card Live Clock
  if (typeof initHistoricalClockSync === 'function') {
    initHistoricalClockSync(container);
  }

  // Owner Media Studio & Admin Annual Year ZIP triggers inside Photo Center cockpit
  const studioBtn = container.querySelector('#btn-open-owner-media-studio');
  if (studioBtn) {
    studioBtn.addEventListener('click', () => {
      openOwnerMediaStudioModal({ tab: 'gphotos' });
    });
  }
  const cockpitAnnualBtn = container.querySelector('#btn-admin-annual-zip');
  if (cockpitAnnualBtn) {
    cockpitAnnualBtn.addEventListener('click', () => {
      openOwnerMediaStudioModal({ tab: 'annual' });
    });
  }
}


// 5. UI Rendering & DOM Management
// ============================================================================

export function reconcileLoadedRidesWithOverrides(rides, videoOverrides, mediaOverrides) {
  if (!Array.isArray(rides)) return { changed: false, rides: [] };
  let changed = false;
  const currentRides = [...rides];
  const vOverrides = videoOverrides || {};
  const mOverrides = mediaOverrides || {};

  currentRides.forEach(ride => {
    // 1. Reconcile Video Overrides
    const vKey = String(ride.rideId || ride.id);
    const vOv = vOverrides[vKey];
    if (vOv) {
      const reconciled = reconcileRideVideos(ride._autoMatchedVideos || ride.matchedVideos, vOv);
      if (JSON.stringify(reconciled) !== JSON.stringify(ride.matchedVideos)) {
        ride.matchedVideos = reconciled;
        changed = true;
      }
    }

    // 2. Reconcile Media Overrides
    const d = ride.dateIso;
    const mKeys = [
      ride.id,
      ride.rideId,
      d,
      d ? `ride-${d}` : null,
      d ? `ride-yt-${d}` : null
    ].filter(Boolean).map(String);

    let mOv = null;
    for (const k of mKeys) {
      if (mOverrides[k]) {
        mOv = mOverrides[k];
        break;
      }
    }

    if (mOv) {
      // Reconcile archive status
      const shouldArchive = Boolean(mOv.archived);
      if (Boolean(ride.isArchived) !== shouldArchive) {
        ride.isArchived = shouldArchive;
        changed = true;
      }

      // Reconcile delete/tombstone status
      const shouldDelete = Boolean(mOv.deleted);
      if (Boolean(ride.isDeleted) !== shouldDelete) {
        ride.isDeleted = shouldDelete;
        changed = true;
      }

      // Reconcile assigned ride ID / conversion
      if (mOv.assignedRideId && String(mOv.assignedRideId).trim()) {
        const assignedId = String(mOv.assignedRideId).trim();
        if (ride.rideId !== assignedId || !ride.isBtcnjRide) {
          ride.rideId = assignedId;
          ride.isBtcnjRide = true;
          ride.btcnjUrl = `https://btcnj.com/pages/rideSignupEdit?rideid=${encodeURIComponent(assignedId)}`;
          ride.r5mUrl = buildR5mRideUrl({ ...ride, rideId: assignedId }, CONFIG.R5M_BASE_URL);
          if (mOv.telemetry) {
            if (mOv.telemetry.title) ride.title = mOv.telemetry.title;
            if (mOv.telemetry.category) ride.category = mOv.telemetry.category;
            if (mOv.telemetry.miles !== undefined && mOv.telemetry.miles !== null) ride.miles = mOv.telemetry.miles;
            if (mOv.telemetry.climbFt !== undefined && mOv.telemetry.climbFt !== null) ride.climbFt = mOv.telemetry.climbFt;
            if (mOv.telemetry.leaders) ride.leaders = normalizeRideLeaders(mOv.telemetry.leaders);
            const ovMembers = mOv.telemetry.members || mOv.telemetry.riders || mOv.telemetry.joinedMembers;
            if (Array.isArray(ovMembers) && ovMembers.length > 0) {
              ride.members = ovMembers;
            }
            if (mOv.telemetry.rwgpsUrl) ride.rwgpsUrl = mOv.telemetry.rwgpsUrl;
          }
          changed = true;
        }
      } else if (mOv.revertToOther && ride.isBtcnjRide) {
        ride.rideId = null;
        ride.isBtcnjRide = false;
        ride.btcnjUrl = null;
        changed = true;
      }

      // Reconcile photo album overrides
      if (mOv.hidePhotoAlbum && ride.photoAlbumUrl) {
        ride.photoAlbumUrl = null;
        ride.photoCount = 0;
        changed = true;
      } else if (mOv.photoAlbumUrl) {
        if (ride.photoAlbumUrl !== mOv.photoAlbumUrl) {
          ride.photoAlbumUrl = mOv.photoAlbumUrl;
          changed = true;
        }
        if (mOv.photoCount && mOv.photoCount > 0 && ride.photoCount !== mOv.photoCount) {
          ride.photoCount = mOv.photoCount;
          changed = true;
        }
        if (mOv.coverPhotoUrl && mOv.coverPhotoUrl.trim().length > 0 && ride.coverPhotoUrl !== mOv.coverPhotoUrl) {
          ride.coverPhotoUrl = mOv.coverPhotoUrl;
          changed = true;
        }
        hydrateRidePhotoMetadata(ride);
      }
    }
  });

  // 3. Inject any custom rides defined in mediaOverrides that are missing from currentRides
  for (const [k, cOv] of Object.entries(mOverrides)) {
    if (cOv && cOv.isCustom && !cOv.deleted) {
      const customId = cOv.cardId || k;
      if (!currentRides.some(r => r.id === customId)) {
        const cData = cOv.customData || {};
        const d = cData.dateIso || cOv.dateIso || '2026-01-01';
        const customRide = {
          id: customId,
          rideId: cOv.assignedRideId || cData.rideId || null,
          isBtcnjRide: Boolean(cOv.assignedRideId || cData.rideId),
          btcnjUrl: (cOv.assignedRideId || cData.rideId) ? `https://btcnj.com/pages/rideSignupEdit?rideid=${encodeURIComponent(cOv.assignedRideId || cData.rideId)}` : null,
          r5mUrl: null,
          rwgpsUrl: cData.rwgpsUrl || null,
          title: cData.title || cOv.title || 'Custom Club Ride',
          dateIso: d,
          dateStr: cData.dateStr || d,
          leaders: cData.leaders || [],
          members: cData.members || cData.riders || cData.joinedMembers || [],
          location: cData.location || 'North Jersey',
          miles: cData.miles || null,
          climbFt: cData.climbFt || null,
          category: cData.category || null,
          photoAlbumUrl: cOv.photoAlbumUrl || cData.photoAlbumUrl || null,
          photoCount: cOv.photoCount || cData.photoCount || 0,
          coverPhotoUrl: cOv.coverPhotoUrl || cData.coverPhotoUrl || null,
          matchedVideos: Array.isArray(cData.matchedVideos) ? cData.matchedVideos : [],
          isCustom: true,
          isArchived: Boolean(cOv.archived),
          isDeleted: false
        };
        currentRides.push(customRide);
        currentRides.sort((a, b) => (b.dateIso || '').localeCompare(a.dateIso || ''));
        changed = true;
      }
    }
  }

  return { changed, rides: currentRides };
}

if (typeof document !== 'undefined') {
  document.addEventListener('DOMContentLoaded', () => {
    // Bind Top Shortcuts View Switchers
    const photoShortcut = document.getElementById('header-shortcut-photos');

    if (photoShortcut) {
      photoShortcut.addEventListener('click', (e) => {
        e.preventDefault();
        switchView('photos', true);
      });
    }

    window.addEventListener('popstate', () => {
      const targetView = resolveCurrentView();
      switchView(targetView, false);
    });

    initAmbientWallpaper();
    initAmbientAttentionController();
    initHeroBannerAccordion();
    initShareButton();
    initRidesFeed();
    initPeriodicPhotoCountSync();
    initFeedbackModule();
    initAdminFeedbackConsole();
    initRideVideoManagerModal();
    initAdminConversionAndAlbumModals();
    initLightboxModal();
    initPlaylistManagerModal();
    initAutoUpdater();
    initBroadwayMarqueeNudgeEngine();
    initHeaderAeroResponsiveSync();
    initTopAeroDiagnosticCounter();
    initHeaderR5mShortcut();
  });
}

/**
 * Synchronizes the upper-right corner R5m shortcut chip with the active ride context.
 * In spotlight view: mirrors in-card R5m button to deep-link directly into the active ride.
 * In general feed: reverts to the root R5m application link.
 * @param {object|null} [activeRide]
 */
export function syncHeaderR5mShortcut(activeRide = null) {
  if (typeof document === 'undefined') return;
  const shortcutEl = document.getElementById('header-shortcut-r5m') || document.querySelector('.header-shortcuts .chip-cyan');
  if (!shortcutEl) return;

  const defaultUrl = `${CONFIG.R5M_BASE_URL}/`;
  const defaultTooltip = '/r5, /r5m';
  const defaultAria = 'R5m shortcuts: /r5, /r5m';

  if (activeRide) {
    const targetUrl = activeRide.r5mUrl || buildR5mRideUrl(activeRide, CONFIG.R5M_BASE_URL);
    const rideId = activeRide.rideId || activeRide.id || '';
    const cleanId = String(rideId).replace(/^btcnj-/, '');
    const tooltip = cleanId ? `Open Ride #${cleanId} in R5m` : 'Open in R5m';
    const ariaLabel = cleanId ? `Open Ride #${cleanId} in R5m App` : 'Open in R5m App';

    shortcutEl.setAttribute('href', targetUrl);
    shortcutEl.setAttribute('data-tooltip', tooltip);
    shortcutEl.setAttribute('aria-label', ariaLabel);
    shortcutEl.title = tooltip;
    shortcutEl.classList.add('is-context-active');
  } else {
    shortcutEl.setAttribute('href', defaultUrl);
    shortcutEl.setAttribute('data-tooltip', defaultTooltip);
    shortcutEl.setAttribute('aria-label', defaultAria);
    shortcutEl.title = defaultTooltip;
    shortcutEl.classList.remove('is-context-active');
  }
}

/**
 * Initializes upper-right corner R5m shortcut listeners and cross-origin postMessage handling.
 */
export function initHeaderR5mShortcut() {
  if (typeof document === 'undefined') return;
  const shortcutEl = document.getElementById('header-shortcut-r5m') || document.querySelector('.header-shortcuts .chip-cyan');
  if (!shortcutEl || shortcutEl._r5mInitialized) return;
  shortcutEl._r5mInitialized = true;

  shortcutEl.addEventListener('click', (e) => {
    if (isEmbeddedMode() && activeSpotlightRideId) {
      e.preventDefault();
      const rides = currentLoadedRides || getCachedMediaRides() || [];
      const ride = rides.find(r => String(r.rideId || r.id || '') === String(activeSpotlightRideId));
      if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
        window.parent.postMessage({
          type: 'BTCNJCC_SELECT_RIDE',
          rideId: activeSpotlightRideId,
          rwgpsUrl: ride ? ride.rwgpsUrl : null,
          title: ride ? ride.title : null
        }, '*');
      }
    }
  });
}

/**
 * Responsive Top Banner Aero Stage: ViewBox Synchronization
 * On desktop (> 1024px): Uses expanded 1480px viewBox displaying side bike, CAD laser fold, and top bike.
 * On mobile/tablet (<= 1024px): Standardizes on native Version B centered 380px viewBox ('70 10 380 142')
 * displaying the prominent side bike and dense CFD streamlines without top-view bike or CAD laser clutter.
 */
/**
 * Synchronizes the animation clock of the top-view aero group with the side-aero camera rig.
 * Prevents phase skew between side-aero whip-pull and top-view emergence following
 * dynamic display toggles, window resizes, or tab visibility changes.
 */
export function syncAeroAnimationClocks() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const rig = document.getElementById('side-aero-camera-rig');
  const topViewGroup = document.querySelector('.header-aero-svg .top-view-aero-group');
  if (!rig || !topViewGroup) return;

  const rigAnims = typeof rig.getAnimations === 'function' ? rig.getAnimations() : [];
  const topAnims = typeof topViewGroup.getAnimations === 'function' ? topViewGroup.getAnimations() : [];

  if (rigAnims.length > 0 && topAnims.length > 0) {
    const primaryRigAnim = rigAnims[0];
    const primaryTopAnim = topAnims[0];
    if (primaryRigAnim && primaryTopAnim && typeof primaryRigAnim.currentTime === 'number') {
      try {
        primaryTopAnim.currentTime = primaryRigAnim.currentTime;
      } catch (e) {
        // Fallback for mock environments
      }
    }
  }
}

const STORAGE_AERO_ANIM_KEY = 'btcnjcc_top_aero_anim_enabled';

export function isTopAeroAnimEnabled() {
  if (typeof localStorage === 'undefined') return true;
  return localStorage.getItem(STORAGE_AERO_ANIM_KEY) !== 'false';
}

export function setTopAeroAnimEnabled(enabled) {
  const topBackdrop = document.querySelector('.header-top-aero-backdrop');
  if (topBackdrop) {
    topBackdrop.classList.toggle('aero-anim-paused', !enabled);
  }
  const sideBackdrop = document.querySelector('.header-aero-backdrop');
  if (sideBackdrop) {
    sideBackdrop.classList.toggle('aero-anim-paused', !enabled);
  }
  const headerSvg = document.querySelector('.header-aero-svg');
  if (headerSvg) {
    headerSvg.classList.toggle('aero-anim-paused', !enabled);
  }

  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_AERO_ANIM_KEY, enabled ? 'true' : 'false');
  }

  // Update Admin Console switch button & badge
  const adminBtn = document.getElementById('admin-toggle-aero-btn');
  if (adminBtn) {
    adminBtn.setAttribute('aria-checked', enabled ? 'true' : 'false');
    adminBtn.classList.toggle('active', enabled);
    adminBtn.classList.toggle('paused', !enabled);
  }
  const adminBadge = document.getElementById('admin-aero-anim-badge');
  if (adminBadge) {
    adminBadge.textContent = enabled ? 'Aero: ON' : 'Aero: PAUSED';
  }
  const adminToggle = document.getElementById('admin-toggle-aero-anim');
  if (adminToggle && adminToggle.checked !== enabled) {
    adminToggle.checked = enabled;
  }

  // Update Floating Dock switch button & status badge
  const dockBtn = document.getElementById('dock-toggle-aero-btn');
  if (dockBtn) {
    dockBtn.setAttribute('aria-checked', enabled ? 'true' : 'false');
    dockBtn.classList.toggle('active', enabled);
    dockBtn.classList.toggle('paused', !enabled);
  }
  const dockBadge = document.getElementById('dock-aero-anim-badge');
  if (dockBadge) {
    dockBadge.textContent = enabled ? 'ON' : 'PAUSED';
    dockBadge.classList.toggle('active', enabled);
    dockBadge.classList.toggle('paused', !enabled);
  }
  const dockToggle = document.getElementById('dock-toggle-aero-anim');
  if (dockToggle && dockToggle.checked !== enabled) {
    dockToggle.checked = enabled;
  }
}

if (typeof window !== 'undefined') {
  window.isTopAeroAnimEnabled = isTopAeroAnimEnabled;
  window.setTopAeroAnimEnabled = setTopAeroAnimEnabled;
}

export const STORAGE_VORTEX_DIAG_KEY = 'btcnjcc_show_vortex_diag';

export function isVortexSyncEnabled() {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem('btcnjcc_show_vortex_diag') === 'true';
}

export function setVortexSyncEnabled(enabled) {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('btcnjcc_show_vortex_diag', enabled ? 'true' : 'false');
  }

  // Update Top Aero Diag Overlay visibility
  syncTopAeroDiagOverlayVisibility();

  // Update Dock switch button & status badge
  const dockBtn = document.getElementById('dock-toggle-vortex-sync-btn');
  if (dockBtn) {
    dockBtn.setAttribute('aria-checked', enabled ? 'true' : 'false');
    dockBtn.classList.toggle('active', enabled);
    dockBtn.classList.toggle('paused', !enabled);
  }
  const dockBadge = document.getElementById('dock-vortex-sync-badge');
  if (dockBadge) {
    dockBadge.textContent = enabled ? 'ON' : 'OFF';
    dockBadge.classList.toggle('active', enabled);
    dockBadge.classList.toggle('paused', !enabled);
  }

  // Update Admin Console checkbox
  const adminToggle = document.getElementById('admin-toggle-vortex-diag');
  if (adminToggle && adminToggle.checked !== enabled) {
    adminToggle.checked = enabled;
  }

  if (enabled) {
    initTopAeroDiagnosticCounter();
  }
}

if (typeof window !== 'undefined') {
  window.isVortexSyncEnabled = isVortexSyncEnabled;
  window.setVortexSyncEnabled = setVortexSyncEnabled;
}

export function initHeaderAeroResponsiveSync() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  function syncAeroViewBox() {
    const aeroSvg = document.querySelector('.header-aero-svg');
    if (!aeroSvg) return;
    const isMobile = window.innerWidth <= 1024;
    const targetVb = isMobile ? '70 10 380 142' : '-280 -6 1480 160';
    if (aeroSvg.getAttribute('viewBox') !== targetVb) {
      aeroSvg.setAttribute('viewBox', targetVb);
    }
    const topViewGroup = aeroSvg.querySelector('.top-view-aero-group');
    if (topViewGroup) {
      const wasHidden = topViewGroup.style.display === 'none';
      topViewGroup.style.display = isMobile ? 'none' : '';
      if (!isMobile && wasHidden) {
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(() => syncAeroAnimationClocks());
        } else {
          syncAeroAnimationClocks();
        }
      }
    }
  }

  window.addEventListener('resize', syncAeroViewBox, { passive: true });
  window.addEventListener('orientationchange', syncAeroViewBox, { passive: true });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      syncAeroAnimationClocks();
    }
  });
  syncAeroViewBox();
  setTopAeroAnimEnabled(isTopAeroAnimEnabled());
  setTimeout(() => syncAeroAnimationClocks(), 100);
}

export const DEFAULT_VORTEX_PARAMS = {
  nv: 1.10,
  rv: 15.5,
  tv: 1.5,
  arv: 1.05,
  sv: 110,
  lsv: 19.0,
  fv: 0.70,
  speed: 20.0,
  crv: -2.0,
  cv: 0.40
};

export const VORTEX_PRESETS = {
  standard: { nv: 1.10, rv: 15.5, tv: 1.5, arv: 1.05, sv: 110, lsv: 19.0, fv: 0.70, crv: -2.0, cv: 0.40 },
  turbulent: { nv: 1.65, rv: 21.0, tv: 2.2, arv: 1.95, sv: 80, lsv: 26.0, fv: 1.05, crv: 0.0, cv: 0.85 },
  laminar: { nv: 0.65, rv: 9.0, tv: 1.2, arv: 1.00, sv: 145, lsv: 8.0, fv: 0.40, crv: 0.0, cv: 0.05 },
  drafting: { nv: 1.35, rv: 12.0, tv: 1.6, arv: 1.70, sv: 75, lsv: 14.0, fv: 0.90, crv: 0.0, cv: 0.60 }
};

let currentVortexParams = { ...DEFAULT_VORTEX_PARAMS };
let activeVortexPeriodMs = 2000;

export function getAeroVortexParams() {
  return { ...currentVortexParams };
}

export function loadAeroVortexParams() {
  if (typeof localStorage === 'undefined') return { ...DEFAULT_VORTEX_PARAMS };
  try {
    const raw = localStorage.getItem('btcnjcc_vortex_params_v4') || localStorage.getItem('btcnjcc_vortex_params_v3');
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        nv: typeof parsed.nv === 'number' ? parsed.nv : DEFAULT_VORTEX_PARAMS.nv,
        rv: typeof parsed.rv === 'number' ? parsed.rv : DEFAULT_VORTEX_PARAMS.rv,
        tv: typeof parsed.tv === 'number' ? parsed.tv : DEFAULT_VORTEX_PARAMS.tv,
        arv: typeof parsed.arv === 'number' ? parsed.arv : DEFAULT_VORTEX_PARAMS.arv,
        sv: typeof parsed.sv === 'number' ? parsed.sv : DEFAULT_VORTEX_PARAMS.sv,
        lsv: typeof parsed.lsv === 'number' ? parsed.lsv : DEFAULT_VORTEX_PARAMS.lsv,
        fv: typeof parsed.fv === 'number' ? parsed.fv : DEFAULT_VORTEX_PARAMS.fv,
        speed: typeof parsed.speed === 'number' ? parsed.speed : DEFAULT_VORTEX_PARAMS.speed,
        crv: typeof parsed.crv === 'number' ? parsed.crv : DEFAULT_VORTEX_PARAMS.crv,
        cv: typeof parsed.cv === 'number' ? parsed.cv : DEFAULT_VORTEX_PARAMS.cv
      };
    }
  } catch (_) {}
  return { ...DEFAULT_VORTEX_PARAMS };
}

export function saveAeroVortexParams(params) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem('btcnjcc_vortex_params_v4', JSON.stringify(params));
  } catch (_) {}
}

/**
 * Applies aerodynamic fluid parameters to the top-view aero SVG with dynamic speed scaling.
 * Strouhal number coupling: f(v) ~ v, U_c ~ v, convective wavelength lambda ~ const with mild shear stretch.
 * @param {object} params
 */
export function applyAeroVortexParams(params) {
  currentVortexParams = { ...params };
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  const aeroSvg = document.querySelector('.header-top-aero-svg');
  if (!root && !aeroSvg) return;

  const baselineSpeed = 20.0;
  const bikeSpeed = Math.max(5.0, Math.min(40.0, typeof params.speed === 'number' ? params.speed : 20.0));
  const speedRatio = Math.max(0.25, Math.min(2.5, bikeSpeed / baselineSpeed));

  // Fluid dynamics formulations (Strouhal invariant)
  const effectiveFv = Math.max(0.05, params.fv * speedRatio);
  const effectivePeriod = 1.0 / effectiveFv;
  activeVortexPeriodMs = effectivePeriod * 1000;
  const effectiveSv = Math.round(params.sv * (1 + 0.15 * (speedRatio - 1.0)));
  const rawLsv = typeof params.lsv === 'number' ? params.lsv : 12.0;
  const effectiveLsv = Math.max(0.0, Math.min(30.0, rawLsv));
  const lsvHalf = effectiveLsv / 2.0;
  const effectiveAr = Math.max(0.2, params.arv * Math.pow(speedRatio, 0.35));
  const effectiveNv = params.nv * Math.pow(speedRatio, 0.2);
  const effectiveRv = params.rv * (0.85 + 0.15 / Math.pow(speedRatio, 0.2));

  // Geometry aspect ratio and radius transforms
  const sx = (effectiveRv / 11.0) * Math.sqrt(effectiveAr);
  const sy = (effectiveRv / 11.0) / Math.sqrt(effectiveAr);

  // v1.1.82 Nv: visible curl arc (% of spiral, anchored at the eye). sqrt map spans the full
  // 0.20–2.00 slider (no dead zone) and keeps the calibrated default (1.10 -> ~74%).
  const vortexArc = Math.max(10, Math.min(100, 100 * Math.sqrt(Math.max(0, effectiveNv) / 2.0)));
  const innerOpacity = Math.max(0.0, Math.min(1.0, 0.35 + (effectiveNv - 0.6) * 0.4));
  // v1.1.82 Rv decoupling: spin-group scale is (Rv/11)·sqrt(AR) x (Rv/11)/sqrt(AR); dividing stroke by
  // Rv/11 keeps the rendered (geometric-mean) line weight equal to Tv at any core radius.
  const strokeComp = 11.0 / Math.max(1, effectiveRv);
  const lineWeight = Math.max(0.3, Math.min(5.0, typeof params.tv === 'number' ? params.tv : DEFAULT_VORTEX_PARAMS.tv));

  const targetEl = root; // CSS custom properties cascade into .header-top-aero-svg
  targetEl.style.setProperty('--vortex-nv', effectiveNv.toFixed(2));
  targetEl.style.setProperty('--vortex-rv', `${effectiveRv.toFixed(1)}px`);
  targetEl.style.setProperty('--vortex-scale-x', sx.toFixed(3));
  targetEl.style.setProperty('--vortex-scale-y', sy.toFixed(3));
  targetEl.style.setProperty('--vortex-sv', `${effectiveSv}px`);
  targetEl.style.setProperty('--vortex-lsv', `${effectiveLsv.toFixed(1)}px`);
  targetEl.style.setProperty('--vortex-lsv-half', `${lsvHalf.toFixed(1)}px`);
  targetEl.style.setProperty('--vortex-fv', effectiveFv.toFixed(2));
  targetEl.style.setProperty('--vortex-period', `${effectivePeriod.toFixed(3)}s`);
  targetEl.style.setProperty('--vortex-period-half', `-${(effectivePeriod / 2).toFixed(3)}s`);
  targetEl.style.setProperty('--vortex-spin-period', `${(effectivePeriod / 2).toFixed(3)}s`);
  targetEl.style.setProperty('--vortex-spin-period-half', `-${(effectivePeriod / 4).toFixed(3)}s`);
  targetEl.style.setProperty('--vortex-arc', vortexArc.toFixed(2));
  targetEl.style.setProperty('--vortex-tv', lineWeight.toFixed(2));
  targetEl.style.setProperty('--vortex-stroke-comp', strokeComp.toFixed(4));
  targetEl.style.setProperty('--vortex-inner-opacity', innerOpacity.toFixed(2));
  const rawCrv = typeof params.crv === 'number' ? params.crv : DEFAULT_VORTEX_PARAMS.crv;
  const effectiveCrv = Math.max(-15.0, Math.min(15.0, rawCrv));
  targetEl.style.setProperty('--vortex-cro-x', `${effectiveCrv.toFixed(1)}px`);
  targetEl.style.setProperty('--vortex-cro-y', '0px');
  targetEl.style.setProperty('--vortex-cro', `${effectiveCrv.toFixed(1)}px`);

  // Chaos / Micro-Turbulence Parameter & Harmonic Stage Perturbation Matrix
  const rawCv = typeof params.cv === 'number' ? params.cv : DEFAULT_VORTEX_PARAMS.cv;
  const effectiveCv = Math.max(0.0, Math.min(1.0, rawCv));
  targetEl.style.setProperty('--vortex-cv', effectiveCv.toFixed(2));

  for (let k = 0; k <= 6; k++) {
    const seedUpper = (k * 1.73 + 0.5);
    const seedLower = (k * 2.37 + 1.8);
    
    // Upper train perturbation
    const sxUpper = sx * (1.0 + effectiveCv * 0.28 * Math.sin(seedUpper));
    const syUpper = sy * (1.0 - effectiveCv * 0.22 * Math.cos(seedUpper));
    const rotUpper = effectiveCv * 22.0 * Math.sin(seedUpper * 1.5);
    const croX_U = effectiveCrv + effectiveCv * 3.5 * Math.cos(seedUpper * 2.0);
    const croY_U = effectiveCv * 2.5 * Math.sin(seedUpper * 2.0);

    // Lower train perturbation
    const sxLower = sx * (1.0 + effectiveCv * 0.28 * Math.cos(seedLower));
    const syLower = sy * (1.0 - effectiveCv * 0.22 * Math.sin(seedLower));
    const rotLower = effectiveCv * 22.0 * Math.cos(seedLower * 1.5);
    const croX_L = effectiveCrv + effectiveCv * 3.5 * Math.sin(seedLower * 2.0);
    const croY_L = effectiveCv * 2.5 * Math.cos(seedLower * 2.0);

    targetEl.style.setProperty(`--vortex-scale-x-u${k}`, sxUpper.toFixed(3));
    targetEl.style.setProperty(`--vortex-scale-y-u${k}`, syUpper.toFixed(3));
    targetEl.style.setProperty(`--vortex-rot-u${k}`, `${rotUpper.toFixed(1)}deg`);
    targetEl.style.setProperty(`--vortex-cro-x-u${k}`, `${croX_U.toFixed(1)}px`);
    targetEl.style.setProperty(`--vortex-cro-y-u${k}`, `${croY_U.toFixed(1)}px`);

    targetEl.style.setProperty(`--vortex-scale-x-l${k}`, sxLower.toFixed(3));
    targetEl.style.setProperty(`--vortex-scale-y-l${k}`, syLower.toFixed(3));
    targetEl.style.setProperty(`--vortex-rot-l${k}`, `${rotLower.toFixed(1)}deg`);
    targetEl.style.setProperty(`--vortex-cro-x-l${k}`, `${croX_L.toFixed(1)}px`);
    targetEl.style.setProperty(`--vortex-cro-y-l${k}`, `${croY_L.toFixed(1)}px`);
  }

  // Constant spacing invariant offsets for stages 0..6 (Delta X_k = -k * (Sv - 160))
  for (let k = 0; k <= 6; k++) {
    const stageOffset = -k * (effectiveSv - 160);
    targetEl.style.setProperty(`--vortex-offset-${k}`, `${stageOffset}px`);
  }

  // Update Admin UI controls if open
  const nvValEl = document.getElementById('admin-vortex-nv-val');
  const rvValEl = document.getElementById('admin-vortex-rv-val');
  const arvValEl = document.getElementById('admin-vortex-arv-val');
  const svValEl = document.getElementById('admin-vortex-sv-val');
  const lsvValEl = document.getElementById('admin-vortex-lsv-val');
  const fvValEl = document.getElementById('admin-vortex-fv-val');
  const speedValEl = document.getElementById('admin-vortex-speed-val');
  const cvValEl = document.getElementById('admin-vortex-cv-val');

  if (nvValEl) nvValEl.textContent = `${params.nv.toFixed(2)} t`;
  if (rvValEl) rvValEl.textContent = `${params.rv.toFixed(1)} px`;
  if (arvValEl) arvValEl.textContent = `${params.arv.toFixed(2)} : 1`;
  if (svValEl) svValEl.textContent = `${params.sv} px`;
  if (lsvValEl) lsvValEl.textContent = `${effectiveLsv.toFixed(1)} px`;
  if (fvValEl) fvValEl.textContent = `${params.fv.toFixed(2)} Hz`;
  if (speedValEl) speedValEl.textContent = `${bikeSpeed.toFixed(1)} mph`;
  if (cvValEl) cvValEl.textContent = `${effectiveCv.toFixed(2)}`;
  const tvValEl = document.getElementById('admin-vortex-tv-val');
  const dockTvVal = document.getElementById('dock-vortex-tv-val');
  if (tvValEl) tvValEl.textContent = `${lineWeight.toFixed(1)} px`;
  if (dockTvVal) dockTvVal.textContent = `${lineWeight.toFixed(1)} px`;

  const crvValEl = document.getElementById('admin-vortex-crv-val');
  const dockCrvVal = document.getElementById('dock-vortex-crv-val');
  if (crvValEl) crvValEl.textContent = `${effectiveCrv >= 0 ? '+' : ''}${effectiveCrv.toFixed(1)} px`;
  if (dockCrvVal) dockCrvVal.textContent = `${effectiveCrv >= 0 ? '+' : ''}${effectiveCrv.toFixed(1)} px`;

  // Update Floating Dock controls if mounted
  const dockNvVal = document.getElementById('dock-vortex-nv-val');
  const dockRvVal = document.getElementById('dock-vortex-rv-val');
  const dockArvVal = document.getElementById('dock-vortex-arv-val');
  const dockSvVal = document.getElementById('dock-vortex-sv-val');
  const dockLsvVal = document.getElementById('dock-vortex-lsv-val');
  const dockFvVal = document.getElementById('dock-vortex-fv-val');
  const dockSpeedVal = document.getElementById('dock-vortex-speed-val');
  const dockCvVal = document.getElementById('dock-vortex-cv-val');

  if (dockNvVal) dockNvVal.textContent = `${params.nv.toFixed(2)} t`;
  if (dockRvVal) dockRvVal.textContent = `${params.rv.toFixed(1)} px`;
  if (dockArvVal) dockArvVal.textContent = `${params.arv.toFixed(2)} : 1`;
  if (dockSvVal) dockSvVal.textContent = `${params.sv} px`;
  if (dockLsvVal) dockLsvVal.textContent = `${effectiveLsv.toFixed(1)} px`;
  if (dockFvVal) dockFvVal.textContent = `${params.fv.toFixed(2)} Hz`;
  if (dockSpeedVal) dockSpeedVal.textContent = `${bikeSpeed.toFixed(1)} mph`;
  if (dockCvVal) dockCvVal.textContent = `${effectiveCv.toFixed(2)}`;

  // Bidirectional slider synchronization across panels
  const slidersToSync = [
    { el: document.getElementById('admin-vortex-nv'), val: params.nv },
    { el: document.getElementById('admin-vortex-rv'), val: params.rv },
    { el: document.getElementById('admin-vortex-arv'), val: params.arv },
    { el: document.getElementById('admin-vortex-crv'), val: effectiveCrv },
    { el: document.getElementById('admin-vortex-cv'), val: effectiveCv },
    { el: document.getElementById('admin-vortex-sv'), val: params.sv },
    { el: document.getElementById('admin-vortex-lsv'), val: effectiveLsv },
    { el: document.getElementById('admin-vortex-fv'), val: params.fv },
    { el: document.getElementById('admin-vortex-speed'), val: bikeSpeed },
    { el: document.getElementById('dock-vortex-nv'), val: params.nv },
    { el: document.getElementById('dock-vortex-rv'), val: params.rv },
    { el: document.getElementById('dock-vortex-arv'), val: params.arv },
    { el: document.getElementById('dock-vortex-crv'), val: effectiveCrv },
    { el: document.getElementById('dock-vortex-cv'), val: effectiveCv },
    { el: document.getElementById('dock-vortex-sv'), val: params.sv },
    { el: document.getElementById('dock-vortex-lsv'), val: effectiveLsv },
    { el: document.getElementById('dock-vortex-fv'), val: params.fv },
    { el: document.getElementById('dock-vortex-speed'), val: bikeSpeed },
    { el: document.getElementById('admin-vortex-tv'), val: lineWeight },
    { el: document.getElementById('dock-vortex-tv'), val: lineWeight }
  ];
  slidersToSync.forEach(s => {
    if (s.el && document.activeElement !== s.el) {
      s.el.value = s.val;
    }
  });
}

/**
 * Synchronizes visibility of the floating right-margin vortex simulation dock.
 */
export function syncVortexFloatingDockVisibility() {
  if (typeof document === 'undefined') return;
  const dock = document.getElementById('vortex-floating-dock');
  const toggleUnlock = document.getElementById('admin-toggle-vortex-unlock');
  const adminBtn = document.getElementById('admin-toggle-aero-btn');
  if (adminBtn && !adminBtn.dataset.boundAeroToggle) {
    adminBtn.dataset.boundAeroToggle = 'true';
    adminBtn.addEventListener('click', (e) => {
      e.preventDefault();
      setTopAeroAnimEnabled(!isTopAeroAnimEnabled());
    });
  }
  const toggleAeroAnim = document.getElementById('admin-toggle-aero-anim');
  if (toggleAeroAnim && !toggleAeroAnim.dataset.boundAeroToggle) {
    toggleAeroAnim.dataset.boundAeroToggle = 'true';
    toggleAeroAnim.checked = isTopAeroAnimEnabled();
    toggleAeroAnim.addEventListener('change', () => {
      setTopAeroAnimEnabled(toggleAeroAnim.checked);
    });
  }
  const isUnlocked = localStorage.getItem('btcnjcc_vortex_hud_unlocked') === 'true';

  if (toggleUnlock) {
    toggleUnlock.checked = isUnlocked;
  }
  if (dock) {
    if (isUnlocked) {
      dock.classList.remove('hidden');
    } else {
      dock.classList.add('hidden');
    }
    const isMinimized = localStorage.getItem('btcnjcc_vortex_hud_minimized') === 'true';
    if (isMinimized) {
      dock.classList.add('minimized');
    } else {
      dock.classList.remove('minimized');
    }
  }
}

/**
 * Initializes the Unlocked Real-Time Vortex Simulation HUD on the right screen margin.
 */
export function initVortexFloatingDock() {
  if (typeof document === 'undefined') return;
  const dock = document.getElementById('vortex-floating-dock');
  if (!dock) return;

  const pill = document.getElementById('vortex-dock-pill');
  const minBtn = document.getElementById('vortex-dock-min-btn');
  const lockBtn = document.getElementById('vortex-dock-lock-btn');
  const resetBtn = document.getElementById('dock-vortex-reset-btn');

  const sliderNv = document.getElementById('dock-vortex-nv');
  const sliderRv = document.getElementById('dock-vortex-rv');
  const sliderArv = document.getElementById('dock-vortex-arv');
  const sliderSv = document.getElementById('dock-vortex-sv');
  const sliderLsv = document.getElementById('dock-vortex-lsv');
  const sliderFv = document.getElementById('dock-vortex-fv');
  const sliderSpeed = document.getElementById('dock-vortex-speed');
  const dockBtn = document.getElementById('dock-toggle-aero-btn');
  if (dockBtn && !dockBtn.dataset.boundAeroToggle) {
    dockBtn.dataset.boundAeroToggle = 'true';
    dockBtn.addEventListener('click', (e) => {
      e.preventDefault();
      setTopAeroAnimEnabled(!isTopAeroAnimEnabled());
    });
  }
  const toggleAeroAnim = document.getElementById('dock-toggle-aero-anim');
  if (toggleAeroAnim && !toggleAeroAnim.dataset.boundAeroToggle) {
    toggleAeroAnim.dataset.boundAeroToggle = 'true';
    toggleAeroAnim.checked = isTopAeroAnimEnabled();
    toggleAeroAnim.addEventListener('change', () => {
      setTopAeroAnimEnabled(toggleAeroAnim.checked);
    });
  }
  const dockBadge = document.getElementById('dock-aero-anim-badge');
  if (dockBadge) {
    const isAnimOn = isTopAeroAnimEnabled();
    dockBadge.textContent = isAnimOn ? 'ON' : 'PAUSED';
    dockBadge.classList.toggle('active', isAnimOn);
    dockBadge.classList.toggle('paused', !isAnimOn);
  }

  const dockSyncBtn = document.getElementById('dock-toggle-vortex-sync-btn');
  if (dockSyncBtn && !dockSyncBtn.dataset.boundSyncToggle) {
    dockSyncBtn.dataset.boundSyncToggle = 'true';
    dockSyncBtn.addEventListener('click', (e) => {
      e.preventDefault();
      setVortexSyncEnabled(!isVortexSyncEnabled());
    });
  }
  setVortexSyncEnabled(isVortexSyncEnabled());

  const presetBtns = dock.querySelectorAll('.dock-chip');

  let params = loadAeroVortexParams();

  function highlightDockPreset(presetKey) {
    presetBtns.forEach(btn => {
      btn.classList.toggle('active', btn.dataset.preset === presetKey);
    });
    const adminChips = document.querySelectorAll('.admin-vortex-lab .admin-vortex-chip');
    adminChips.forEach(btn => {
      btn.classList.toggle('active', btn.dataset.preset === presetKey);
    });
  }

  if (sliderNv) sliderNv.addEventListener('input', (e) => {
    params.nv = parseFloat(e.target.value);
    applyAeroVortexParams(params);
    saveAeroVortexParams(params);
    highlightDockPreset(null);
  });
  if (sliderRv) sliderRv.addEventListener('input', (e) => {
    params.rv = parseFloat(e.target.value);
    applyAeroVortexParams(params);
    saveAeroVortexParams(params);
    highlightDockPreset(null);
  });
  const sliderTvDock = document.getElementById('dock-vortex-tv');
  if (sliderTvDock) sliderTvDock.addEventListener('input', (e) => {
    params.tv = parseFloat(e.target.value);
    applyAeroVortexParams(params);
    saveAeroVortexParams(params);
    highlightDockPreset(null);
  });
  if (sliderArv) sliderArv.addEventListener('input', (e) => {
    params.arv = parseFloat(e.target.value);
    applyAeroVortexParams(params);
    saveAeroVortexParams(params);
    highlightDockPreset(null);
  });
  const sliderCrvDock = document.getElementById('dock-vortex-crv');
  if (sliderCrvDock) sliderCrvDock.addEventListener('input', (e) => {
    params.crv = parseFloat(e.target.value);
    applyAeroVortexParams(params);
    saveAeroVortexParams(params);
    highlightDockPreset(null);
  });
  const sliderCvDock = document.getElementById('dock-vortex-cv');
  if (sliderCvDock) sliderCvDock.addEventListener('input', (e) => {
    params.cv = parseFloat(e.target.value);
    applyAeroVortexParams(params);
    saveAeroVortexParams(params);
    highlightDockPreset(null);
  });
  if (sliderSv) sliderSv.addEventListener('input', (e) => {
    params.sv = parseInt(e.target.value, 10);
    applyAeroVortexParams(params);
    saveAeroVortexParams(params);
    highlightDockPreset(null);
  });
  if (sliderLsv) sliderLsv.addEventListener('input', (e) => {
    params.lsv = parseFloat(e.target.value);
    applyAeroVortexParams(params);
    saveAeroVortexParams(params);
    highlightDockPreset(null);
  });
  if (sliderFv) sliderFv.addEventListener('input', (e) => {
    params.fv = parseFloat(e.target.value);
    applyAeroVortexParams(params);
    saveAeroVortexParams(params);
    highlightDockPreset(null);
  });
  if (sliderSpeed) sliderSpeed.addEventListener('input', (e) => {
    params.speed = parseFloat(e.target.value);
    applyAeroVortexParams(params);
    saveAeroVortexParams(params);
  });

  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      params = { ...DEFAULT_VORTEX_PARAMS };
      highlightDockPreset('standard');
      applyAeroVortexParams(params);
      saveAeroVortexParams(params);
    });
  }

  presetBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const presetKey = btn.dataset.preset;
      if (VORTEX_PRESETS[presetKey]) {
        params = { ...params, ...VORTEX_PRESETS[presetKey] };
        highlightDockPreset(presetKey);
        applyAeroVortexParams(params);
        saveAeroVortexParams(params);
      }
    });
  });

  if (minBtn) {
    minBtn.addEventListener('click', () => {
      dock.classList.add('minimized');
      localStorage.setItem('btcnjcc_vortex_hud_minimized', 'true');
    });
  }
  if (pill) {
    pill.addEventListener('click', () => {
      dock.classList.remove('minimized');
      localStorage.setItem('btcnjcc_vortex_hud_minimized', 'false');
    });
  }

  if (lockBtn) {
    lockBtn.addEventListener('click', () => {
      localStorage.setItem('btcnjcc_vortex_hud_unlocked', 'false');
      syncVortexFloatingDockVisibility();
      const toast = document.getElementById('admin-spotlight-toast');
      if (toast) {
        toast.textContent = 'Vortex Lab Locked to Admin Console';
        toast.style.display = 'block';
        setTimeout(() => { toast.style.display = 'none'; }, 3000);
      }
    });
  }

  syncVortexFloatingDockVisibility();
}

/**
 * Initializes and wires the Admin Console Vortex Simulation Lab controls.
 */
export function initAdminVortexLabControls() {
  if (typeof document === 'undefined') return;
  const lab = document.querySelector('.admin-vortex-lab');
  if (!lab) return;

  const sliderNv = document.getElementById('admin-vortex-nv');
  const sliderRv = document.getElementById('admin-vortex-rv');
  const sliderArv = document.getElementById('admin-vortex-arv');
  const sliderCrv = document.getElementById('admin-vortex-crv');
  const sliderCv = document.getElementById('admin-vortex-cv');
  const sliderSv = document.getElementById('admin-vortex-sv');
  const sliderLsv = document.getElementById('admin-vortex-lsv');
  const sliderFv = document.getElementById('admin-vortex-fv');
  const sliderSpeed = document.getElementById('admin-vortex-speed');
  const resetBtn = document.getElementById('admin-vortex-reset-btn');
  const presetBtns = lab.querySelectorAll('.admin-vortex-chip');
  const toggleUnlock = document.getElementById('admin-toggle-vortex-unlock');

  let params = loadAeroVortexParams();

  function syncInputs() {
    if (sliderNv) sliderNv.value = params.nv;
    if (sliderRv) sliderRv.value = params.rv;
    if (sliderArv) sliderArv.value = params.arv;
    if (sliderCrv) sliderCrv.value = params.crv;
    if (sliderCv) sliderCv.value = params.cv;
    if (sliderSv) sliderSv.value = params.sv;
    if (sliderLsv) sliderLsv.value = params.lsv;
    if (sliderFv) sliderFv.value = params.fv;
    if (sliderSpeed) sliderSpeed.value = params.speed;
    applyAeroVortexParams(params);
    saveAeroVortexParams(params);
  }

  function highlightPreset(presetName) {
    presetBtns.forEach(btn => {
      btn.classList.toggle('active', btn.dataset.preset === presetName);
    });
    const dockChips = document.querySelectorAll('#vortex-floating-dock .dock-chip');
    dockChips.forEach(btn => {
      btn.classList.toggle('active', btn.dataset.preset === presetName);
    });
  }

  if (toggleUnlock) {
    toggleUnlock.checked = localStorage.getItem('btcnjcc_vortex_hud_unlocked') === 'true';
    toggleUnlock.addEventListener('change', () => {
      const isUnlocked = toggleUnlock.checked;
      localStorage.setItem('btcnjcc_vortex_hud_unlocked', isUnlocked ? 'true' : 'false');
      syncVortexFloatingDockVisibility();
      const toast = document.getElementById('admin-spotlight-toast');
      if (toast) {
        toast.textContent = isUnlocked ? 'Vortex Lab Unlocked to Screen' : 'Vortex Lab Locked';
        toast.style.display = 'block';
        setTimeout(() => { toast.style.display = 'none'; }, 3000);
      }
    });
  }

  if (sliderNv) sliderNv.addEventListener('input', (e) => {
    params.nv = parseFloat(e.target.value);
    applyAeroVortexParams(params);
    saveAeroVortexParams(params);
    highlightPreset(null);
  });
  if (sliderRv) sliderRv.addEventListener('input', (e) => {
    params.rv = parseFloat(e.target.value);
    applyAeroVortexParams(params);
    saveAeroVortexParams(params);
    highlightPreset(null);
  });
  const sliderTvAdmin = document.getElementById('admin-vortex-tv');
  if (sliderTvAdmin) sliderTvAdmin.addEventListener('input', (e) => {
    params.tv = parseFloat(e.target.value);
    applyAeroVortexParams(params);
    saveAeroVortexParams(params);
    highlightPreset(null);
  });
  if (sliderArv) sliderArv.addEventListener('input', (e) => {
    params.arv = parseFloat(e.target.value);
    applyAeroVortexParams(params);
    saveAeroVortexParams(params);
    highlightPreset(null);
  });
  if (sliderCrv) sliderCrv.addEventListener('input', (e) => {
    params.crv = parseFloat(e.target.value);
    applyAeroVortexParams(params);
    saveAeroVortexParams(params);
    highlightPreset(null);
  });
  if (sliderCv) sliderCv.addEventListener('input', (e) => {
    params.cv = parseFloat(e.target.value);
    applyAeroVortexParams(params);
    saveAeroVortexParams(params);
    highlightPreset(null);
  });
  if (sliderSv) sliderSv.addEventListener('input', (e) => {
    params.sv = parseInt(e.target.value, 10);
    applyAeroVortexParams(params);
    saveAeroVortexParams(params);
    highlightPreset(null);
  });
  if (sliderLsv) sliderLsv.addEventListener('input', (e) => {
    params.lsv = parseFloat(e.target.value);
    applyAeroVortexParams(params);
    saveAeroVortexParams(params);
    highlightPreset(null);
  });
  if (sliderFv) sliderFv.addEventListener('input', (e) => {
    params.fv = parseFloat(e.target.value);
    applyAeroVortexParams(params);
    saveAeroVortexParams(params);
    highlightPreset(null);
  });
  if (sliderSpeed) sliderSpeed.addEventListener('input', (e) => {
    params.speed = parseFloat(e.target.value);
    applyAeroVortexParams(params);
    saveAeroVortexParams(params);
  });

  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      params = { ...DEFAULT_VORTEX_PARAMS };
      highlightPreset('standard');
      syncInputs();
    });
  }

  presetBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const presetKey = btn.dataset.preset;
      if (VORTEX_PRESETS[presetKey]) {
        params = { ...params, ...VORTEX_PRESETS[presetKey] };
        highlightPreset(presetKey);
        syncInputs();
      }
    });
  });

  // Initial sync from stored preferences
  syncInputs();
}

/**
 * Synchronizes the visibility of the top-view aerodynamic diagnostic frame overlay.
 * Controls #topAeroDiagOverlay display based on localStorage key 'btcnjcc_show_vortex_diag'.
 * Defaults to false (hidden).
 */
export function syncTopAeroDiagOverlayVisibility() {
  if (typeof document === 'undefined') return;
  const overlay = document.getElementById('topAeroDiagOverlay');
  if (!overlay) return;
  const isEnabled = isVortexSyncEnabled();
  overlay.style.display = isEnabled ? '' : 'none';
}

/**
 * Diagnostic HUD Counter for Top-View Aero Simulation (#00..#39)
 * Ticks across 40 discrete frame indices (50ms intervals) spanning the dynamically scaled convective loop.
 * Synchronized with animationiteration and animationstart on .karman-wake-conveyor.
 */
export function initTopAeroDiagnosticCounter() {
  if (typeof document === 'undefined') return;
  const counterEl = document.getElementById('topAeroDiagCounter');
  const conveyor = document.querySelector('.header-top-aero-svg .vortex-train-upper') || document.querySelector('.header-top-aero-svg .karman-wake-conveyor');
  if (!counterEl || !conveyor) return;

  // Apply stored or default vortex simulation parameters
  const vortexParams = loadAeroVortexParams();
  applyAeroVortexParams(vortexParams);
  initVortexFloatingDock();
  syncVortexFloatingDockVisibility();

  syncTopAeroDiagOverlayVisibility();

  // If disabled by default in admin preferences, do not run the animation loop
  if (!isVortexSyncEnabled()) {
    return;
  }

  let cycleStart = performance.now();
  let lastIdx = -1;
  let rafId = null;

  function onCycleReset(e) {
    // Guard against bubbling animation events from child vortex core rotations (vortexSpinCW/CCW)
    if (e && e.animationName && e.animationName !== 'karmanWakeConvection') return;
    if (e && e.target && e.target !== conveyor) return;
    cycleStart = performance.now();
    lastIdx = -1;
  }

  conveyor.addEventListener('animationstart', onCycleReset);
  conveyor.addEventListener('animationiteration', onCycleReset);

  let wasPaused = false;

  function tick() {
    if (!isVortexSyncEnabled() || (typeof document !== 'undefined' && document.body && document.body.classList.contains('is-video-modal-open'))) {
      rafId = null;
      return;
    }
    const periodMs = activeVortexPeriodMs > 100 ? activeVortexPeriodMs : 2000;

    // Freeze diagnostic frame counter lockstep when aero motion animation is disabled / paused
    if (typeof isTopAeroAnimEnabled === 'function' && !isTopAeroAnimEnabled()) {
      wasPaused = true;
      const anims = conveyor.getAnimations ? conveyor.getAnimations() : [];
      if (anims.length > 0 && typeof anims[0].currentTime === 'number') {
        const frozenElapsed = (anims[0].currentTime % periodMs + periodMs) % periodMs;
        const frozenIdx = Math.floor((frozenElapsed / periodMs) * 40) % 40;
        if (frozenIdx !== lastIdx) {
          lastIdx = frozenIdx;
          counterEl.textContent = '#' + String(frozenIdx).padStart(2, '0');
        }
      }
      rafId = requestAnimationFrame(tick);
      return;
    }

    if (wasPaused) {
      wasPaused = false;
      const anims = conveyor.getAnimations ? conveyor.getAnimations() : [];
      if (anims.length > 0 && typeof anims[0].currentTime === 'number') {
        const currentElapsed = (anims[0].currentTime % periodMs + periodMs) % periodMs;
        cycleStart = performance.now() - currentElapsed;
      } else if (lastIdx >= 0) {
        cycleStart = performance.now() - (lastIdx / 40) * periodMs;
      }
    }

    const elapsed = (performance.now() - cycleStart) % periodMs;
    const idx = Math.floor((elapsed / periodMs) * 40) % 40;
    if (idx !== lastIdx) {
      lastIdx = idx;
      counterEl.textContent = '#' + String(idx).padStart(2, '0');
    }
    rafId = requestAnimationFrame(tick);
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && isVortexSyncEnabled() && !(document.body && document.body.classList.contains('is-video-modal-open'))) {
      cycleStart = performance.now();
      lastIdx = -1;
      if (!rafId) rafId = requestAnimationFrame(tick);
    } else if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  });

  rafId = requestAnimationFrame(tick);
}

export async function copyWebsiteUrl(url = 'https://btcnjcc.web.app') {
  let success = false;
  if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(url);
      success = true;
    } catch (_) {
      success = false;
    }
  }

  if (!success && typeof document !== 'undefined') {
    try {
      const el = document.createElement('textarea');
      el.value = url;
      el.setAttribute('readonly', '');
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      success = document.execCommand('copy');
      document.body.removeChild(el);
    } catch (_) {
      success = false;
    }
  }
  return success;
}

export function resolveCurrentShareUrl() {
  if (typeof window === 'undefined' || !window.location) return 'https://btcnjcc.web.app';
  try {
    const url = new URL(window.location.href);
    if (currentView === 'photos') {
      if (typeof PHOTO_CENTER_STATE !== 'undefined' && PHOTO_CENTER_STATE.viewMode === 'list') {
        url.searchParams.set('layout', 'list');
      } else {
        url.searchParams.delete('layout');
        url.searchParams.delete('mode');
      }
    }
    if (typeof PAGINATION_STATE !== 'undefined' && PAGINATION_STATE.searchQuery && PAGINATION_STATE.searchQuery.trim()) {
      url.searchParams.set('q', PAGINATION_STATE.searchQuery.trim());
    }
    return url.toString();
  } catch (_) {
    return window.location.href;
  }
}
if (typeof window !== 'undefined') {
  window.resolveCurrentShareUrl = resolveCurrentShareUrl;
}

export function openShareQrModal(overrideUrl) {
  if (typeof document === 'undefined') return;
  const modal = document.getElementById('share-qr-modal');
  if (!modal) return;

  const targetUrl = overrideUrl || resolveCurrentShareUrl();

  const qrImg = document.getElementById('share-modal-qr-img');
  const urlText = document.getElementById('share-modal-url-text');

  if (qrImg) {
    qrImg.src = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&ecc=H&data=${encodeURIComponent(targetUrl)}`;
  }
  if (urlText) {
    urlText.textContent = targetUrl;
  }

  modal.classList.add('active');
  modal.setAttribute('aria-hidden', 'false');
  if (typeof document !== 'undefined' && document.body) {
    document.body.classList.add('modal-open');
  }
}

export function closeShareQrModal() {
  if (typeof document === 'undefined') return;
  const modal = document.getElementById('share-qr-modal');
  if (!modal) return;
  modal.classList.remove('active');
  modal.classList.remove('is-fullscreen-active');
  modal.setAttribute('aria-hidden', 'true');
  if (typeof document !== 'undefined' && document.body) {
    document.body.classList.remove('modal-open');
  }
}

export function initShareQrModal() {
  if (typeof document === 'undefined') return;
  const modal = document.getElementById('share-qr-modal');
  if (!modal || modal.dataset.initialized === 'true') return;
  modal.dataset.initialized = 'true';

  const closeBtn = document.getElementById('share-modal-close-btn');
  const backdrop = document.getElementById('share-modal-backdrop');
  const copyBtn = document.getElementById('share-modal-copy-btn');
  const nativeBtn = document.getElementById('share-modal-native-btn');
  const copyLabel = document.getElementById('share-modal-copy-label');

  if (closeBtn) {
    closeBtn.addEventListener('click', () => closeShareQrModal());
  }

  if (backdrop) {
    backdrop.addEventListener('click', () => closeShareQrModal());
  }

  if (copyBtn) {
    copyBtn.addEventListener('click', async () => {
      const urlText = document.getElementById('share-modal-url-text');
      const targetUrl = urlText && urlText.textContent ? urlText.textContent : resolveCurrentShareUrl();
      await copyWebsiteUrl(targetUrl);

      copyBtn.classList.add('copied');
      if (copyLabel) copyLabel.textContent = 'Copied!';

      setTimeout(() => {
        copyBtn.classList.remove('copied');
        if (copyLabel) copyLabel.textContent = 'Copy Link';
      }, 1800);
    });
  }

  if (nativeBtn) {
    nativeBtn.addEventListener('click', async () => {
      const urlText = document.getElementById('share-modal-url-text');
      const targetUrl = urlText && urlText.textContent ? urlText.textContent : resolveCurrentShareUrl();
      if (typeof navigator !== 'undefined' && navigator.share) {
        try {
          await navigator.share({
            title: 'BTCNJ CC Media Center',
            text: 'Explore Bergen Bicycle Club ride videos, routes, and photo galleries.',
            url: targetUrl
          });
        } catch (err) {
          if (err && err.name !== 'AbortError') {
            await copyWebsiteUrl(targetUrl);
          }
        }
      } else {
        await copyWebsiteUrl(targetUrl);
        nativeBtn.classList.add('copied');
        setTimeout(() => nativeBtn.classList.remove('copied'), 1800);
      }
    });
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('active')) {
      closeShareQrModal();
    }
  });
}

export function initShareButton() {
  if (typeof document === 'undefined') return;
  initShareQrModal();

  const shareBtn = document.getElementById('header-share-btn');
  const brandCompact = document.querySelector('.header-brand-compact');

  const triggerShareAction = async (anchorEl) => {
    if (!anchorEl) return;
    const targetUrl = resolveCurrentShareUrl();
    const isSearchActive = !!(typeof PAGINATION_STATE !== 'undefined' && PAGINATION_STATE.searchQuery && PAGINATION_STATE.searchQuery.trim());

    // 1. Instant auto-copy to clipboard buffer
    await copyWebsiteUrl(targetUrl);

    anchorEl.classList.add('copied');

    const existing = anchorEl.querySelector('.share-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'share-toast';
    toast.textContent = isSearchActive ? 'Search Link Copied!' : 'Link Copied!';
    anchorEl.appendChild(toast);

    setTimeout(() => {
      anchorEl.classList.remove('copied');
      if (toast.parentNode) toast.remove();
    }, 1800);

    // 2. Simultaneously pop open the Share QR modal
    openShareQrModal(targetUrl);
  };

  if (shareBtn) {
    shareBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      triggerShareAction(shareBtn);
    });
  }

  // Synchronize dynamic header share state on initial load
  if (typeof PAGINATION_STATE !== 'undefined') {
    updateHeaderShareButtonState(PAGINATION_STATE.searchQuery);
  }

  let brandTapTimer = null;
  const brandLink = document.getElementById('header-brand-link') || brandCompact;
  if (brandLink) {
    brandLink.addEventListener('click', (e) => {
      // Allow new tab open on modified click or middle click
      if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
      e.preventDefault();

      // Trigger 1-second auto-reverting tap animation that guarantees return to baseline
      brandLink.classList.remove('is-tapped');
      void brandLink.offsetWidth; // Force reflow to re-trigger animation if rapidly tapped
      brandLink.classList.add('is-tapped');
      clearTimeout(brandTapTimer);
      brandTapTimer = setTimeout(() => {
        brandLink.classList.remove('is-tapped');
        if (typeof brandLink.blur === 'function') {
          brandLink.blur();
        }
      }, 1000);

      // Clear video search input if active
      const searchInput = document.getElementById('search-input');
      if (searchInput && searchInput.value) {
        searchInput.value = '';
        searchInput.dispatchEvent(new Event('input', { bubbles: true }));
      }

      // Clear photo center search input if active
      const photoSearchInput = document.getElementById('photo-center-search-input');
      if (photoSearchInput && photoSearchInput.value) {
        photoSearchInput.value = '';
        photoSearchInput.dispatchEvent(new Event('input', { bubbles: true }));
      }

      // Exit spotlight mode if active
      if (typeof activeSpotlightRideId !== 'undefined' && activeSpotlightRideId) {
        if (typeof exitSpotlightMode === 'function') {
          exitSpotlightMode();
        } else {
          activeSpotlightRideId = null;
        }
      }

      // Reset view to default 'videos' (Media Center)
      if (typeof currentView !== 'undefined' && currentView !== 'videos' && typeof switchView === 'function') {
        switchView('videos', true);
      } else {
        if (typeof window !== 'undefined' && window.history && typeof window.history.replaceState === 'function') {
          window.history.replaceState(null, '', '/' + (window.location.hash || ''));
        }
      }

      // Smooth scroll to top of viewport
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  }
}

function initHeroBannerAccordion() {
  const details = document.getElementById('hero-banner-details');
  if (!details) return;
  // Auto-collapse accordion on page reload across all viewports for clean, compact screen economy
  details.removeAttribute('open');
}

function initAutoUpdater() {
  new AutoUpdateManager(null, APP_VERSION, {
    isSafeToAutoReload: () => {
      // Defer reload if video lightbox or admin feedback modal is actively open
      const activeModal = document.querySelector('.lightbox-modal.active, .modal.active');
      return !activeModal;
    }
  });
}

export let currentLoadedRides = [];
export let activeSpotlightRideId = null;
export let activeSpotlightPremiereRideId = null;
export let activeSpotlightPremiereTimestamp = 0;

/**
 * Extracts the ride identifier from URL query parameters (strictly ?ride=<id>).
 * @param {string|null} [searchStr] Optional query search string (defaults to window.location.search)
 * @returns {string|null} Trimmed ride ID or null
 */
export function getSpotlightQueryId(searchStr = null) {
  const search = searchStr !== null ? searchStr : (typeof window !== 'undefined' && window.location ? window.location.search : '');
  if (!search) return null;
  const params = new URLSearchParams(search);
  const id = params.get('rideid') || params.get('ride');
  return id && String(id).trim() ? String(id).trim() : null;
}

/**
 * Detects whether embedded mode is active (&embed=1 or &embed=true or &embedded=true).
 * @param {string|null} [searchStr] Optional query search string (defaults to window.location.search)
 * @returns {boolean}
 */
export function isEmbeddedMode(searchStr = null) {
  if (searchStr !== null) {
    if (!searchStr) return false;
    const params = new URLSearchParams(searchStr);
    const val = params.get('embed') || params.get('embedded');
    return val === '1' || val === 'true';
  }
  if (_isStickyEmbedded) return true;
  if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
    _isStickyEmbedded = true;
    return true;
  }
  if (typeof document !== 'undefined' && (document.documentElement.classList.contains('is-embedded') || (document.body && document.body.classList.contains('is-embedded')))) {
    _isStickyEmbedded = true;
    return true;
  }
  const search = typeof window !== 'undefined' && window.location ? window.location.search : '';
  if (!search) return false;
  const params = new URLSearchParams(search);
  const val = params.get('embed') || params.get('embedded');
  if (val === '1' || val === 'true') {
    _isStickyEmbedded = true;
    return true;
  }
  return false;
}

/**
 * Detects whether headless mode is active (&headless=1 or &headless=true or &bare=1 or &bare=true).
 * @param {string|null} [searchStr] Optional query search string (defaults to window.location.search)
 * @returns {boolean}
 */
export function isHeadlessMode(searchStr = null) {
  if (searchStr !== null) {
    if (!searchStr) return false;
    const params = new URLSearchParams(searchStr);
    const val = params.get('headless') || params.get('bare');
    return val === '1' || val === 'true';
  }
  if (_isStickyHeadless) return true;
  if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
    _isStickyHeadless = true;
    return true;
  }
  if (typeof document !== 'undefined' && (document.documentElement.classList.contains('headless-mode') || (document.body && document.body.classList.contains('headless-mode')))) {
    _isStickyHeadless = true;
    return true;
  }
  const search = typeof window !== 'undefined' && window.location ? window.location.search : '';
  if (search) {
    const params = new URLSearchParams(search);
    const val = params.get('headless') || params.get('bare');
    if (val === '1' || val === 'true') {
      _isStickyHeadless = true;
      return true;
    }
  }
  return false;
}

/**
 * Preserves embed=1 and headless=1 parameters during internal client-side navigation.
 * @param {string} targetUrlOrPath
 * @returns {string}
 */
export function formatInternalUrl(targetUrlOrPath) {
  if (!targetUrlOrPath) return targetUrlOrPath;
  const isEmbed = isEmbeddedMode();
  const isHeadless = isHeadlessMode();
  if (!isEmbed && !isHeadless) return targetUrlOrPath;

  const [basePath, searchStr] = targetUrlOrPath.split('?');
  const params = new URLSearchParams(searchStr || '');
  if (isEmbed) params.set('embed', '1');
  if (isHeadless) params.set('headless', '1');
  const qs = params.toString();
  return qs ? `${basePath || ''}?${qs}` : (basePath || '');
}

async function initRidesFeed() {
  activeSpotlightRideId = getSpotlightQueryId();
  const container = document.getElementById('rides-container');
  const countBadge = document.getElementById('rides-count-badge');

  if (container) {
    container.addEventListener('click', async (e) => {
      const refreshBtn = e.target.closest('#refresh-feed-btn');
      if (refreshBtn) {
        refreshBtn.disabled = true;
        refreshBtn.classList.remove('sync-success', 'sync-error');
        refreshBtn.classList.add('spinning');
        // Enforce at least 2 full revolutions (2 x 600ms = 1200ms)
        const minSpinPromise = new Promise(resolve => setTimeout(resolve, 1200));
        let syncSuccess = false;
        let renderCallback = null;
        try {
          _lastMediaOverridesSyncAt = 0;
          _lastVideoOverridesSyncAt = 0;
          _lastPlaylistsSyncAt = 0;
          _lastMemberExclusionsSyncAt = 0;
          const [result] = await Promise.all([
            fetchAndRenderRides(true, true),
            minSpinPromise
          ]);
          renderCallback = result;
          await syncActivePhotoCounts(true);
          syncSuccess = true;
          showGlobalToast('Feed Synced');
        } catch (_) {
          showGlobalToast('Sync Failed');
        } finally {
          if (typeof renderCallback === 'function') {
            renderCallback();
          }
          const currentBtn = document.getElementById('refresh-feed-btn');
          if (currentBtn) {
            currentBtn.classList.remove('spinning');
            if (syncSuccess) {
              currentBtn.classList.add('sync-success');
              setTimeout(() => {
                currentBtn.classList.remove('sync-success');
                currentBtn.disabled = false;
              }, 600);
            } else {
              currentBtn.classList.add('sync-error');
              setTimeout(() => {
                currentBtn.classList.remove('sync-error');
                currentBtn.disabled = false;
              }, 600);
            }
          }
        }
        return;
      }

      // Proactive JIT Interaction SWR on card / media button click
      const cardEl = e.target.closest('.media-ride-card, .ride-card, .photo-album-card, .photo-list-row');
      if (cardEl && currentLoadedRides) {
        const rawCardId = cardEl.getAttribute('data-card-id') || cardEl.getAttribute('data-ride-id');
        const cleanCardId = rawCardId ? String(rawCardId).replace(/^btcnj-/, '').trim() : '';
        const clickedRide = currentLoadedRides.find(r => r.id === rawCardId || r.rideId === rawCardId || `btcnj-${r.rideId}` === rawCardId || String(r.rideId || '').trim() === cleanCardId);
        if (clickedRide) {
          proactiveSyncRideMedia(clickedRide, true);
        }
      }
    });
  }

  // SWR local cache check
  const cached = getCachedMediaRides();
  if (cached && cached.length > 0) {
    // Reconcile with authoritative build metadata before initial DOM paint to guarantee zero reload flicker
    cached.forEach(ride => {
      hydrateRidePhotoMetadata(ride);
      reconcileCachedRideWithBuildMetadata(ride);
    });
    currentLoadedRides = cached;
    if (currentView === 'photos') {
      renderPhotoCenter(cached, container);
    } else if (activeSpotlightRideId) {
      renderSpotlightView(activeSpotlightRideId, cached, container, countBadge);
    } else {
      renderRides(cached, container, countBadge);
    }
  }

  await fetchAndRenderRides(false);

  // Autonomous Low-Friction Background SWR Sync on Tab Focus / Visibility
  if (typeof document !== 'undefined' && typeof window !== 'undefined' && !window._btcnjccFocusSyncInstalled) {
    window._btcnjccFocusSyncInstalled = true;
    let _lastAutoSyncTime = Date.now();
    const triggerLowFrictionAutoSync = () => {
      const now = Date.now();
      if (now - _lastAutoSyncTime > 60 * 1000) {
        _lastAutoSyncTime = now;
        fetchAndRenderRides(false, false).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') triggerLowFrictionAutoSync();
    });
    window.addEventListener('focus', triggerLowFrictionAutoSync);
  }
}

export function getCachedMediaRides() {
  try {
    const raw = localStorage.getItem(CONFIG.CACHE_KEY_MEDIA_RIDES);
    if (!raw) return null;
    const { timestamp, data } = JSON.parse(raw);
    if (Date.now() - timestamp > CONFIG.CACHE_TTL_MS) return null;
    return data;
  } catch (e) {
    return null;
  }
}

export function setCachedMediaRides(data) {
  try {
    localStorage.setItem(CONFIG.CACHE_KEY_MEDIA_RIDES, JSON.stringify({
      timestamp: Date.now(),
      data
    }));
  } catch (e) {
    console.warn('Unable to cache media rides:', e);
  }
}

async function fetchAndRenderRides(forceRefresh = false, deferRender = false) {
  const container = document.getElementById('rides-container');
  const statusPill = document.getElementById('connection-pill');
  const countBadge = document.getElementById('rides-count-badge');

  try {
    const albumUrl = forceRefresh ? `${CONFIG.ALBUMS_API_URL}?force=true&_t=${Date.now()}` : `${CONFIG.ALBUMS_API_URL}?_t=${Date.now()}`;
    const playlistUrl = forceRefresh ? `${CONFIG.PLAYLIST_API_URL}?force=true` : CONFIG.PLAYLIST_API_URL;
    const registeredPlaylists = getYoutubePlaylists();
    const playlistPromises = registeredPlaylists.map(pl => {
      const plUrl = `${playlistUrl}${playlistUrl.includes('?') ? '&' : '?'}list=${encodeURIComponent(pl.id)}&_t=${Date.now()}`;
      return fetch(plUrl, { cache: 'no-cache', signal: AbortSignal.timeout(9000) })
        .then(r => (r.ok ? r.json() : null))
        .catch(err => {
          console.warn(`[Playlist Fetch] ${pl.id} warning:`, err);
          return null;
        });
    });

    // Fetch Google Photos albums, registered YouTube playlists, and cloud overrides concurrently
    const [albumsRes, playlistDataList] = await Promise.allSettled([
      fetch(albumUrl, { cache: 'no-cache', signal: AbortSignal.timeout(9000) }),
      Promise.all(playlistPromises),
      fetchCloudVideoOverrides(forceRefresh),
      fetchCloudMediaOverrides(forceRefresh),
      fetchCloudYoutubePlaylists(forceRefresh),
      fetchCloudMemberMediaExclusions(forceRefresh)
    ]);

    const rawAlbums = [];
    if (albumsRes.status === 'fulfilled' && albumsRes.value.ok) {
      const json = await albumsRes.value.json();
      if (json && Array.isArray(json.years)) {
        json.years.forEach(y => {
          if (Array.isArray(y.albums)) rawAlbums.push(...y.albums);
        });
      }
    }

    // Merge dynamic videos from all registered playlists with static metadata preservation
    let activeVideos = [];
    const seenVideoIds = new Set();
    const staticMetaMap = new Map();
    PLAYLIST_VIDEOS.forEach(v => {
      if (v.isPortrait) staticMetaMap.set(v.videoId, true);
    });

    if (playlistDataList.status === 'fulfilled' && Array.isArray(playlistDataList.value)) {
      playlistDataList.value.forEach(plData => {
        if (plData && Array.isArray(plData.videos)) {
          plData.videos.forEach(v => {
            if (v && v.videoId && !seenVideoIds.has(v.videoId)) {
              seenVideoIds.add(v.videoId);
              activeVideos.push({
                ...v,
                isPortrait: staticMetaMap.get(v.videoId) || Boolean(v.isPortrait)
              });
            }
          });
        }
      });
    }

    if (activeVideos.length === 0) {
      activeVideos = PLAYLIST_VIDEOS;
    }

    const mediaRides = buildMediaRides(rawAlbums.length > 0 ? rawAlbums : FALLBACK_ALBUM_RIDES, activeVideos);

    // Stale downgrade defense: Only trigger if rawAlbums was completely empty/failed (offline fallback mode)
    // and we already have cached state. If live server data arrived or forceRefresh was requested,
    // accept the authoritative server payload!
    if (!forceRefresh && rawAlbums.length === 0 && currentLoadedRides && currentLoadedRides.length > mediaRides.length) {
      console.warn(`[Feed Guard] Preserving existing ${currentLoadedRides.length} rides over degraded offline fallback payload (${mediaRides.length} rides)`);
      if (statusPill) {
        statusPill.className = 'status-pill online';
        statusPill.querySelector('.pill-label').textContent = 'R5m SYNCED';
      }
      return () => {};
    }

    currentLoadedRides = mediaRides;
    setCachedMediaRides(mediaRides);

    const renderAction = () => {
      const currentContainer = document.getElementById('rides-container') || container;
      const currentBadge = document.getElementById('rides-count-badge') || countBadge;
      const spotlightId = activeSpotlightRideId || getSpotlightQueryId();
      if (currentView === 'photos') {
        renderPhotoCenter(mediaRides, currentContainer);
      } else if (spotlightId) {
        activeSpotlightRideId = spotlightId;
        renderSpotlightView(spotlightId, mediaRides, currentContainer, currentBadge);
      } else {
        renderRides(mediaRides, currentContainer, currentBadge);
      }

      if (statusPill) {
        statusPill.className = 'status-pill online';
        statusPill.querySelector('.pill-label').textContent = 'R5m SYNCED';
      }
    };

    if (!deferRender) {
      renderAction();
    }
    return renderAction;
  } catch (err) {
    console.info('Live album feed unreachable, falling back to local media cache.', err);
    const cached = getCachedMediaRides() || buildMediaRides(FALLBACK_ALBUM_RIDES, PLAYLIST_VIDEOS);
    currentLoadedRides = cached;

    const renderAction = () => {
      const currentContainer = document.getElementById('rides-container') || container;
      const currentBadge = document.getElementById('rides-count-badge') || countBadge;
      const spotlightId = activeSpotlightRideId || getSpotlightQueryId();
      if (spotlightId) {
        activeSpotlightRideId = spotlightId;
        renderSpotlightView(spotlightId, cached, currentContainer, currentBadge);
      } else {
        renderRides(cached, currentContainer, currentBadge);
      }

      if (statusPill) {
        statusPill.className = 'status-pill offline';
        statusPill.querySelector('.pill-label').textContent = 'OFFLINE READY';
      }
    };

    if (!deferRender) {
      renderAction();
    }
    return renderAction;
  }
}

export function partitionRides(rides) {
  const btcnjRides = [];
  const otherRides = [];

  (rides || []).forEach(ride => {
    if (ride.isArchived || ride.isDeleted) return;
    const isBtcnj = Boolean(ride.isBtcnjRide || (ride.rideId && String(ride.rideId).trim().length > 0) || ride.isPromotedToMediaCenter);
    if (isBtcnj) {
      btcnjRides.push(ride);
    } else {
      otherRides.push(ride);
    }
  });

  return { btcnjRides, otherRides };
}

/**
 * Normalizes and tokenizes ride leaders from any raw format (array, comma-separated,
 * bullet-separated '●', or containing trailing RSVP counts like '+ 1').
 *
 * @param {string|string[]|null|undefined} rawLeaders
 * @returns {string[]} Clean array of individual full leader names
 */
export function normalizeRideLeaders(rawLeaders) {
  if (!rawLeaders) return [];
  const list = Array.isArray(rawLeaders) ? rawLeaders : [rawLeaders];
  return list
    .flatMap(item => String(item).split(/[,;&/●•·|]|\s*\+\s*\d+/))
    .map(s => s.trim())
    .filter(s => s && !/^\+?\s*\d+$/.test(s) && !/^(and|with|\&)$/i.test(s));
}

/**
 * Post-processes and formats a ride leader name for display, applying Devil Emoji
 * Enrichment (" 😈") exclusively for Eric Schmidt.
 *
 * @param {string} rawName - Raw leader string (e.g. "Eric Schmidt", "Gene Sullivan")
 * @param {Object} [options]
 * @param {boolean} [options.firstNameOnly=true] - Extract first name before enrichment
 * @returns {string} Formatted name (e.g. "Eric 😈", "Gene", or "Eric Schmidt 😈")
 */
export function formatLeaderDisplayName(rawName, { firstNameOnly = true } = {}) {
  if (!rawName) return '';
  let trimmed = String(rawName).trim();
  if (!trimmed || trimmed === 'CC' || trimmed === 'CC (Cedric)') return '';

  // Strip any existing emoji before identity checking to maintain idempotency
  const cleanName = trimmed.replace(/\s*😈+$/u, '').trim();
  const isEricSchmidt = /^eric\s+schmidt$/i.test(cleanName);
  let displayName = firstNameOnly ? cleanName.split(/\s+/)[0] : cleanName;

  if (isEricSchmidt) {
    const formatted = firstNameOnly ? 'Eric' : 'Eric Schmidt';
    return `${formatted} 😈`;
  }
  return displayName;
}

export function formatR5mPill(ride) {
  if (!ride) return '';

  // Other Media (not linked to an official BTCNJ ride and not promoted) must not display club classification pill
  const isBtcnj = Boolean(ride.isBtcnjRide || (ride.rideId && String(ride.rideId).trim().length > 0) || ride.isPromotedToMediaCenter);
  if (!isBtcnj) {
    return '';
  }

  const parts = [];

  // 1. Ride Category (e.g. B*, B, B+, A) - only if explicitly provided, NEVER defaulted
  let cat = ride.category ? String(ride.category).trim() : '';
  if (cat === 'B/B+') cat = 'B*';
  cat = cat.replace(/\s*ride$/i, '').trim();
  if (cat) {
    parts.push(`<span class="r5m-pill-cat">${escapeHtml(cat)}</span>`);
  }

  // 2. Distance in Miles
  if (ride.miles && Number(ride.miles) > 0) {
    const miVal = Number(ride.miles);
    const miStr = miVal % 1 === 0 ? String(Math.round(miVal)) : miVal.toFixed(1);
    parts.push(`<span class="r5m-pill-miles">${escapeHtml(miStr)} mi</span>`);
  }

  // 3. Elevation Gain in Feet
  const climbVal = Number(ride.climbFt ?? ride.elevation ?? 0);
  if (climbVal > 0) {
    const climbFormatted = Math.round(climbVal).toLocaleString();
    parts.push(`<span class="r5m-pill-climb">${escapeHtml(climbFormatted)} ft</span>`);
  }

  if (parts.length === 0) {
    return '';
  }

  const dot = `<span class="r5m-pill-dot" aria-hidden="true">&bull;</span>`;
  return `
    <div class="cockpit-telemetry-zone r5m-ride-pill" title="Ride Category, Distance & Elevation Profile">
      ${parts.join(dot)}
    </div>
  `;
}

export function renderCockpitBarHtml(ride, leadersStr = '') {
  if (!ride) return '';
  const telemetryHtml = formatR5mPill(ride);
  const isBtcnjLink = Boolean(ride.btcnjUrl);
  if (!telemetryHtml && !isBtcnjLink && !leadersStr) {
    return '';
  }

  const containerTag = isBtcnjLink ? 'a' : 'div';
  const linkAttrs = isBtcnjLink
    ? `href="${escapeHtml(ride.btcnjUrl)}" target="_blank" rel="noopener noreferrer" title="BTCNJ Ride #${escapeHtml(ride.rideId || '')}: Open official club ride signup and roster"`
    : `title="Ride Telemetry"`;

  return `
    <${containerTag} ${linkAttrs} class="cockpit-unified-btn telemetry-btcnj-badge ${isBtcnjLink ? 'is-link' : ''} ${leadersStr ? 'has-leaders-tier' : ''}">
      <div class="cockpit-tier-top">
        <div class="cockpit-brand-zone">
          <span class="btcnj-brand-text">BTCNJ</span>
          ${ride.rideId ? `<span class="btcnj-ride-id btcnj-id">#${escapeHtml(ride.rideId)}</span>` : (ride.isPromotedToMediaCenter ? `<span class="btcnj-ride-id btcnj-id">${escapeHtml(ride.category || 'EVENT')}</span>` : '')}
        </div>

        ${telemetryHtml ? `
          <div class="cockpit-bar-separator" aria-hidden="true"></div>
          ${telemetryHtml}
        ` : ''}

        ${isBtcnjLink ? `<span class="cockpit-btn-arrow btcnj-arrow" aria-hidden="true">&#x2197;</span>` : ''}
      </div>

      ${leadersStr ? `
        <div class="cockpit-tier-leaders cockpit-leaders">
          <span class="leader-icon" aria-hidden="true">
            <svg class="leader-icon-svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
          </span>
          <span class="leader-names">${escapeHtml(leadersStr)}</span>
        </div>
      ` : ''}
    </${containerTag}>
  `;
}

export function buildRideFooter(ride) {
  if (!ride) return '';

  const isBtcnj = Boolean(ride.isBtcnjRide || (ride.rideId && String(ride.rideId).trim().length > 0));
  if (!isBtcnj) {
    return '';
  }

  const parts = [];

  // 1. Distance in Miles
  if (ride.miles && Number(ride.miles) > 0) {
    const miVal = Number(ride.miles);
    const miStr = miVal % 1 === 0 ? String(Math.round(miVal)) : miVal.toFixed(1);
    parts.push(`${miStr} mi`);
  }

  // 2. Elevation Gain in Feet
  const footerClimbVal = Number(ride.climbFt ?? ride.elevation ?? 0);
  if (footerClimbVal > 0) {
    const climbFormatted = Math.round(footerClimbVal).toLocaleString();
    parts.push(`${climbFormatted} ft`);
  }

  // 3. List of ride leads, first name only (with Devil Emoji Enrichment for Eric Schmidt)
  const leadersArr = normalizeRideLeaders(ride.leaders);

  const formatFn = typeof formatLeaderDisplayName === 'function'
    ? formatLeaderDisplayName
    : (raw, opts = {}) => {
        if (!raw) return '';
        const trimmed = String(raw).trim();
        if (!trimmed || trimmed === 'CC' || trimmed === 'CC (Cedric)') return '';
        const cleanName = trimmed.replace(/\s*😈+$/u, '').trim();
        const isEricSchmidt = /^eric\s+schmidt$/i.test(cleanName);
        const displayName = (opts.firstNameOnly !== false) ? cleanName.split(/\s+/)[0] : cleanName;
        return isEricSchmidt ? `${(opts.firstNameOnly !== false) ? 'Eric' : 'Eric Schmidt'} 😈` : displayName;
      };

  const firstNames = leadersArr
    .map(name => formatFn(name, { firstNameOnly: true }))
    .filter(Boolean);

  if (firstNames.length > 0) {
    const escapeFn = typeof escapeHtml === 'function' ? escapeHtml : (s) => String(s || '');
    const leaderIconSvg = `<span class="footer-leader-icon" aria-hidden="true"><svg class="leader-icon-svg" width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg></span>`;
    parts.push(`<span class="footer-leaders-wrap">${leaderIconSvg}<span>${escapeFn(firstNames.join(', '))}</span></span>`);
  }

  if (parts.length === 0) {
    return '';
  }

  return parts.join(' \u00B7 ');
}

export function renderRideCardHtml(ride, options = {}) {
  try {
    const isSpotlight = Boolean(options && options.isSpotlight);
    // Determine representative thumbnail image (high-res in spotlight mode)
    const thumbUrl = resolveRideThumbnail(ride, isSpotlight);

    const isBtcnj = Boolean(ride.isBtcnjRide || (ride.rideId && String(ride.rideId).trim().length > 0));

    // Active search query & match context extraction
    const activeQuery = (typeof PAGINATION_STATE !== 'undefined' && PAGINATION_STATE && PAGINATION_STATE.searchQuery)
      ? PAGINATION_STATE.searchQuery
      : '';
    const searchTokens = activeQuery ? activeQuery.trim().toLowerCase().split(/\s+/).filter(Boolean) : [];
    const searchMatchContext = activeQuery ? extractSearchMatchContext(ride, activeQuery) : null;
    const searchContextHtml = searchMatchContext
      ? `<div class="card-search-context" aria-label="Search match context">${searchMatchContext.html}</div>`
      : '';
    const rawTitle = ride.title || 'Untitled Ride';
    const highlightedTitleHtml = (searchTokens.length > 0)
      ? highlightSearchText(rawTitle, searchTokens)
      : escapeHtml(rawTitle);

    const hasPhotos = Boolean(ride.photoAlbumUrl);
    const hasVideos = Boolean(ride.matchedVideos && ride.matchedVideos.length > 0);
    const isDualMedia = Boolean(hasPhotos && hasVideos);

    // Render associated video dock matching Google Photos layout
    let videosSectionHtml = '';
    if (hasVideos) {
      const allOverrides = (typeof getVideoOverrides === 'function') ? getVideoOverrides() : {};
      const overrideKey = String(ride.rideId || ride.id);
      const override = allOverrides[overrideKey] || (ride.rideId && allOverrides[String(ride.rideId)]);
      const hasExplicitOrder = Boolean(override && Array.isArray(override.orderedVideoIds) && override.orderedVideoIds.length > 0);

      // Preserve explicit manual order; only apply title/part sorting if no override exists
      const displayVideos = hasExplicitOrder ? ride.matchedVideos : sortMatchedVideos(ride.matchedVideos, ride.title);
      ride.matchedVideos = displayVideos;
      const videoCount = displayVideos.length;
      const videoCountLabel = videoCount > 1 ? `${videoCount} Videos` : '1 Video';
      const sectionTitle = videoCount > 1 ? 'Play YouTube Videos (Starts at Pt 1)' : 'Play YouTube Video';
      const countBadgeHtml = isDualMedia
        ? `<span class="videos-count-label">(${videoCount})</span>`
        : `<span class="videos-count-label"><span class="media-qty">${videoCount}</span> <span class="media-label">${videoCount > 1 ? 'Videos' : 'Video'}</span></span>`;

      videosSectionHtml = `
        <div class="matched-videos-section single-row-dock" role="button" tabindex="0" data-video-count="${videoCount}" data-ride-id="${escapeHtml(ride.id)}" title="${escapeHtml(sectionTitle)}">
          ${typeof renderMarqueeBulbFrameHtml === 'function' ? renderMarqueeBulbFrameHtml({ isPhotos: false }) : ''}
          <div class="videos-dock-brand">
            <svg class="youtube-header-logo" width="20" height="15" viewBox="0 3 24 18" fill="none" aria-hidden="true">
              <path fill="#FF0000" d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z"/>
              <path fill="#FFFFFF" d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            <span class="youtube-brand-text">YouTube</span>
          </div>
          <div class="videos-dock-right">
            ${countBadgeHtml}
            <span class="videos-nav-glyph" aria-hidden="true">
              <svg class="media-dock-glyph-svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.75" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="8.5 5 15.5 12 8.5 19"/>
              </svg>
            </span>
          </div>
        </div>
      `;
    }

    // Google Photos Spotlight Card (Streamlined Direct 1-Click Launch)
    let photoSpotlightHtml = '';
    if (hasPhotos) {
      const pCount = ride.photoCount || 0;
      const countLabel = pCount > 0 ? (pCount === 1 ? '1 Photo' : `${pCount} Photos`) : 'Photos';
      const brandTitle = isDualMedia ? 'Photos' : 'Google Photos';
      const photoBadgeHtml = isDualMedia
        ? (pCount > 0 ? `<span class="photo-spotlight-count">(${pCount})</span>` : '')
        : `<span class="photo-spotlight-count">${pCount > 0 ? `<span class="media-qty">${pCount}</span> <span class="media-label">${pCount === 1 ? 'Photo' : 'Photos'}</span>` : `<span class="media-label">Photos</span>`}</span>`;

      photoSpotlightHtml = `
        <a
          href="${escapeHtml(ride.photoAlbumUrl)}"
          target="_blank"
          rel="noopener noreferrer"
          class="media-photo-spotlight"
          data-launch="album"
          data-ride-id="${escapeHtml(ride.id)}"
          title="Open Google Photos Album (${escapeHtml(countLabel)})">
          ${typeof renderMarqueeBulbFrameHtml === 'function' ? renderMarqueeBulbFrameHtml({ isPhotos: true }) : ''}
          <div class="photo-spotlight-normal-content">
            <div class="photo-spotlight-left">
              <svg class="photo-pinwheel-logo" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path fill="#EA4335" d="M12,0 A6,6 0 0,0 12,12 Z"/>
                <path fill="#FBBC04" d="M24,12 A6,6 0 0,0 12,12 Z"/>
                <path fill="#34A853" d="M12,24 A6,6 0 0,0 12,12 Z"/>
                <path fill="#4285F4" d="M0,12 A6,6 0 0,0 12,12 Z"/>
              </svg>
              <span class="photo-spotlight-title">${brandTitle}</span>
            </div>
            <div class="photo-spotlight-right">
              ${photoBadgeHtml}
              <span class="photo-external-glyph" aria-hidden="true">
                <svg class="media-dock-glyph-svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.75" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="6" y1="18" x2="18" y2="6"/>
                  <polyline points="8 6 18 6 18 16"/>
                </svg>
              </span>
            </div>
          </div>
          <div class="photo-spotlight-nudge-content" aria-hidden="true">
            <span class="nudge-morph-text">UPLOAD PHOTOS</span>
          </div>
        </a>
      `;
    }

    // Ride Stats & Leaders Footer (Distance, Climb, First Names)
    const footerStats = buildRideFooter(ride);
    const dateFormattedFn = typeof formatSpotlightDate === 'function'
      ? formatSpotlightDate
      : (typeof formatRideDate === 'function' ? formatRideDate : () => '');
    const spotlightDateStr = isSpotlight ? dateFormattedFn(ride) : '';
    const temporalTier = getRideTemporalTier(ride);
    const pendingChipHtml = temporalTier
      ? `<span class="spotlight-pending-chip tier-${temporalTier}"><span class="pending-pulse-dot" aria-hidden="true"></span><span class="spotlight-pending-label">${temporalTier === 'today' ? 'TODAY' : (temporalTier === 'soon' ? 'SOON' : 'LATER')}</span></span>`
      : '';
    let spotlightHudHtml = footerStats;
    if (isSpotlight && spotlightDateStr) {
      const dateChip = `<span class="spotlight-hud-date">${escapeHtml(spotlightDateStr)}</span>`;
      const dateWithPending = pendingChipHtml ? `${pendingChipHtml}${dateChip}` : dateChip;
      spotlightHudHtml = footerStats ? `${dateWithPending} \u00B7 ${footerStats}` : dateWithPending;
    }

    // Admin session gating for card corner dock
    const isAdmin = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('btcnjcc_admin_authed') === 'true';
    const canShowCardSpotlight = Boolean(!isSpotlight && isAdmin && (ride.rideId || ride.id));

    // Bottom Action Row & Capsule Gating
    const hasDrawerActions = Boolean(isBtcnj || ride.rwgpsUrl || ride.r5mUrl);
    const hasBottomActions = Boolean(hasDrawerActions || footerStats);

    // Embedded RWGPS Route Map Bay for Desktop Spotlight
    let spotlightRwgpsBayHtml = '';
    if (isSpotlight && ride.rwgpsUrl) {
      const match = ride.rwgpsUrl.match(/routes\/(\d+)/);
      const routeId = match ? match[1] : null;
      const embedUrl = routeId
        ? `https://ridewithgps.com/embeds?type=route&id=${routeId}&metricUnits=false&sampleGraph=false`
        : ride.rwgpsUrl;
      spotlightRwgpsBayHtml = `
        <div class="spotlight-rwgps-bay">
          <div class="spotlight-rwgps-iframe-wrap">
            <iframe
              src="${escapeHtml(embedUrl)}"
              title="${escapeHtml(ride.title)} Route Map"
              loading="lazy"
              sandbox="allow-scripts allow-same-origin allow-popups"
              allow="geolocation"
              allowfullscreen>
            </iframe>
            <div class="spotlight-rwgps-click-overlay"
                 role="button"
                 tabindex="0"
                 data-ride-id="${escapeHtml(ride.id || ride.rideId)}"
                 title="Click to open Fullscreen Interactive Route Map"
                 aria-label="Open Fullscreen Interactive Route Map">
            </div>
          </div>
        </div>
      `;
    }

    const bannerTitle = isSpotlight
      ? (hasVideos
          ? (ride.matchedVideos.length > 1 ? `Play YouTube Videos (${ride.matchedVideos.length})` : 'Play YouTube Video')
          : (hasPhotos ? `Open Google Photos Album (${ride.photoCount || 0})` : 'Ride Preview'))
      : 'Click to open Spotlight view';

    const pendingBadgeHtml = temporalTier ? buildPendingBadgeHtml(temporalTier) : '';
    const pendingCardClass = temporalTier ? `is-pending-ride tier-${temporalTier}` : '';

    return `
      <article class="media-ride-card ${isSpotlight ? 'is-spotlight' : ''} ${pendingCardClass}" data-ride-id="${escapeHtml(ride.id || ride.rideId)}" ${temporalTier ? `data-temporal-tier="${temporalTier}"` : ''}>
        <!-- Thumbnail Top Banner (16:9 Crystal Clear Artwork) -->
        <div class="card-media-banner" ${isSpotlight ? `role="button" tabindex="0" title="${escapeHtml(bannerTitle)}" aria-label="${escapeHtml(bannerTitle)}"` : ''}>
          <img src="${escapeHtml(thumbUrl)}" alt="${escapeHtml(ride.title)} Thumbnail" class="card-media-img" loading="lazy" referrerpolicy="no-referrer" onerror="if (!this.dataset.fallback) { this.dataset.fallback = '1'; this.src = ${ride.matchedVideos && ride.matchedVideos.length > 0 && thumbUrl !== `https://img.youtube.com/vi/${ride.matchedVideos[0].videoId}/hqdefault.jpg` ? `'https://img.youtube.com/vi/${escapeHtml(ride.matchedVideos[0].videoId)}/hqdefault.jpg'` : '(window.getCyclingFallbackSvg ? window.getCyclingFallbackSvg() : \'\')'}; } else if (this.dataset.fallback === '1') { this.dataset.fallback = '2'; this.src = (window.getCyclingFallbackSvg ? window.getCyclingFallbackSvg() : ''); } else { this.onerror = null; }">
          ${isSpotlight ? '<div class="card-media-overlay"></div>' : ''}

          <div class="card-badges-top">
            ${buildUnifiedDateCapsuleHtml(ride, temporalTier)}
          </div>

          ${isSpotlight ? `
            <div class="card-title-overlay spotlight-identity-shelf">
              <h3 class="ride-card-heading">${highlightedTitleHtml}</h3>
              ${searchContextHtml}
              ${(spotlightHudHtml || isBtcnj || ride.rwgpsUrl || ride.r5mUrl) ? `
                <div class="spotlight-identity-bar">
                  ${spotlightHudHtml ? `
                    <div class="spotlight-telemetry-hud" title="${escapeHtml(spotlightHudHtml.replace(/<[^>]*>/g, '').trim())}">
                      ${spotlightHudHtml}
                    </div>
                  ` : ''}
                  ${(isBtcnj || ride.rwgpsUrl || ride.r5mUrl) ? `
                    <div class="spotlight-identity-actions">
                      ${isBtcnj ? `
                        <a
                          href="${escapeHtml(ride.btcnjUrl || (ride.rideId ? `https://btcnj.com/pages/rideSignupEdit?rideid=${encodeURIComponent(ride.rideId)}` : 'https://www.btcnj.com/'))}"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="btn btn-secondary btn-btcnj"
                          data-launch="btcnj-ride"
                          onclick="event.stopPropagation()"
                          title="BTCNJ Ride #${escapeHtml(ride.rideId || '')}">
                          <img class="btn-icon btn-icon-img" src="/assets/btcnj-logo.png" width="16" height="16" alt="" aria-hidden="true" />
                          <span>#${escapeHtml(ride.rideId || 'Ride')}</span>
                        </a>
                      ` : ''}
                      ${(isBtcnj || ride.rwgpsUrl || ride.r5mUrl) ? `
                        <a href="${escapeHtml(ride.r5mUrl || buildR5mRideUrl(ride, CONFIG.R5M_BASE_URL))}" target="_blank" rel="noopener noreferrer" class="btn btn-secondary btn-r5m" data-launch="r5m-route" onclick="event.stopPropagation()" title="Open R5m Route">
                          <img class="btn-icon btn-icon-img" src="/assets/logo-r5m.png" width="16" height="16" alt="" aria-hidden="true" />
                          <span>R5m</span>
                        </a>
                      ` : ''}
                    </div>
                  ` : ''}
                </div>
              ` : ''}
            </div>
          ` : ''}
        </div>

        <!-- Card Body Details -->
        <div class="card-body">
          ${!isSpotlight ? `
            <h3 class="ride-card-heading" title="${escapeHtml(ride.title)}">${highlightedTitleHtml}</h3>
            ${searchContextHtml}
          ` : ''}

          <!-- 1. Hero Media Showcase Deck -->
          ${(photoSpotlightHtml || videosSectionHtml) ? `
            <div class="card-media-showcase">
              ${photoSpotlightHtml}
              ${videosSectionHtml}
            </div>
          ` : ''}

          <!-- 1b. Embedded RWGPS Route Map (Desktop Spotlight Only) -->
          ${spotlightRwgpsBayHtml}

          <!-- 2. Bottom Actions Deck & Ride Telemetry / Leaders Footer -->
          ${hasBottomActions ? `
            <div class="ride-cockpit-dock ride-cockpit-capsule ${isSpotlight ? 'spotlight-modal-hide-on-desktop' : ''}">
              ${hasDrawerActions ? `
                <div class="cockpit-drawer">
                  <div class="cockpit-drawer-inner">
                    <div class="cockpit-action-row card-footer-actions">
                      ${isBtcnj ? `
                        <a
                          href="${escapeHtml(ride.btcnjUrl || (ride.rideId ? `https://btcnj.com/pages/rideSignupEdit?rideid=${encodeURIComponent(ride.rideId)}` : 'https://www.btcnj.com/'))}"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="btn btn-secondary btn-btcnj"
                          data-launch="btcnj-ride"
                          title="BTCNJ Ride #${escapeHtml(ride.rideId || '')}">
                          <img class="btn-icon btn-icon-img" src="/assets/btcnj-logo.png" width="16" height="16" alt="" aria-hidden="true" />
                          <span>#${escapeHtml(ride.rideId || 'Ride')}</span>
                        </a>
                      ` : ''}

                      ${ride.rwgpsUrl ? `
                        <button
                          type="button"
                          class="btn btn-secondary ${isSpotlight ? 'spotlight-modal-hide-on-desktop' : ''}"
                          data-launch="rwgps-modal"
                          data-ride-id="${escapeHtml(ride.id)}"
                          title="Open Interactive RideWithGPS Route Map">
                          <svg class="btn-icon" width="16" height="12" viewBox="0 0 49.6 35.1" fill="none" aria-hidden="true">
                            <path fill="#F96400" d="M15.2,9.9c0,1.4,0.2,3.1,4,5.4c2.7,1.6,5.2,4.4,5.2,5.4s-4.3,5.8-6.4,8c-0.2,0.2-0.4,0.4-0.7,0.6c-1.6,1.7-3.8,2.6-6,2.5c-4.4-0.1-7.9-3.8-7.8-8.2c0-2.7,1.3-5.2,3.4-6.7c1.2-0.8,2.6-1.3,4-1.5c0.9-0.1,1.9-0.1,2.8,0.2l0.2,0.1c0-0.9-1.7-1.6-2.6-1.7c-0.7-0.1-1.5-0.1-2.2,0C3.8,14.6-0.1,19.2,0,24.5c0,5.9,5.4,10.6,11,10.6c0.7,0,1.3,0,1.9-0.1c3.2-0.2,6.1-1.4,11.2-7.4c0.5-0.6,3.5-4,4.9-5.7l0.1-0.1l0,0c0.2-0.3,0.4-0.5,0.6-0.8l0.2-0.2c1.2-1.3,2.5-2.4,4-3.3c1.4-0.7,3-1,4.5-0.8c4.8,0.4,7.9,3.3,7.9,7.7s-3.9,8-8.7,8c-2.4-0.2-4.8-1.2-6.5-2.8L31,29.4c-0.9,1.5,2.4,5.6,7.7,5.6c0.1,0,0.2,0,0.3,0c5.9,0,10.6-4.8,10.6-10.7c-0.1-5.8-4.7-10.5-10.5-10.6c-5.2-0.5-8.5,2.4-9.4,2.5c-0.7,0.1-8.2-5-8.9-6.1c-0.3-0.5,0.1-1.9,5-2.3c2.2,2.4,4.5,4.2,8.8,4.2c2.1-0.1,4.2-0.7,6.1-1.8C41.7,9.5,41.3,9,41.3,9s0,0.4-1.4,0.7c-1.6,0.4-3.2,0.5-4.9,0.3C34.2,10,29.7,9.7,28.2,4C21.9,3.4,15.2,5.6,15.2,9.9z"/>
                            <path fill="#F96400" d="M37.8,3.8c-0.1-2.1-2.2-3.3-4-3.6c-3.8-0.6-7.6,0.5-8.7,2.4c0,0,0.4-0.2,1-0.3C27.5,1.8,29,2,30.3,2.7c0.4,0.2,0.6,0.6,0.6,1.1c0,1.9,1.6,3.5,3.5,3.5c0.1,0,0.1,0,0.2,0h0C36.4,7.2,37.9,5.6,37.8,3.8z"/>
                          </svg>
                          <span>RWGPS</span>
                        </button>
                      ` : ''}

                      ${(isBtcnj || ride.rwgpsUrl || ride.r5mUrl) ? `
                        <a href="${escapeHtml(ride.r5mUrl || buildR5mRideUrl(ride, CONFIG.R5M_BASE_URL))}" target="_blank" rel="noopener noreferrer" class="btn btn-primary" data-launch="r5m-route" title="Open R5m Route">
                          <img class="btn-icon btn-icon-img" src="/assets/logo-r5m.png" width="16" height="16" alt="" aria-hidden="true" />
                          <span>R5m</span>
                        </a>
                      ` : ''}
                    </div>
                  </div>
                </div>
              ` : ''}

              ${(isSpotlight ? (spotlightHudHtml || footerStats) : footerStats) ? `
                <div class="card-ride-footer ${hasDrawerActions ? 'cockpit-accordion-trigger' : ''}" ${hasDrawerActions ? 'role="button" tabindex="0" aria-expanded="false" aria-label="Toggle ride links"' : ''} title="${escapeHtml((isSpotlight ? (spotlightHudHtml || footerStats) : footerStats).replace(/<[^>]*>/g, '').trim())}">
                  <span class="card-ride-footer-content">${isSpotlight ? (spotlightHudHtml || footerStats) : footerStats}</span>
                  ${hasDrawerActions ? `
                    <span class="cockpit-chevron" aria-hidden="true">▾</span>
                  ` : ''}
                </div>
              ` : hasDrawerActions ? `
                <div class="card-ride-footer cockpit-accordion-trigger cockpit-trigger-only" role="button" tabindex="0" aria-expanded="false" aria-label="Toggle ride links">
                  <span class="card-ride-footer-content">Route & Details</span>
                  <span class="cockpit-chevron" aria-hidden="true">▾</span>
                </div>
              ` : ''}
            </div>
          ` : ''}
        </div>

        <!-- Admin Micro Corner Dock -->
        ${canShowCardSpotlight ? `
          <div class="card-admin-corner-dock" aria-label="Admin Actions">
            <button type="button" class="admin-corner-btn admin-corner-manage-btn" data-action="manage-videos" data-ride-id="${escapeHtml(ride.rideId || ride.id)}" title="Manage Ride Videos">
              <svg class="admin-corner-svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/>
                <line x1="7" y1="2" x2="7" y2="22"/>
                <line x1="17" y1="2" x2="17" y2="22"/>
                <line x1="2" y1="12" x2="22" y2="12"/>
                <line x1="2" y1="7" x2="7" y2="7"/>
                <line x1="2" y1="17" x2="7" y2="17"/>
                <line x1="17" y1="17" x2="22" y2="17"/>
                <line x1="17" y1="7" x2="22" y2="7"/>
              </svg>
            </button>
            <button type="button" class="admin-corner-btn admin-corner-convert-btn" data-action="convert-ride" data-card-id="${escapeHtml(ride.id)}" data-ride-id="${escapeHtml(ride.rideId || '')}" data-ride-title="${escapeHtml(ride.title)}" data-is-btcnj="${isBtcnj ? 'true' : 'false'}" title="${(isBtcnj || ride.isPromotedToMediaCenter) ? 'Edit Media Promotion' : 'Promote to Media Center'}">
              <svg class="admin-corner-svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
              </svg>
            </button>
            <button type="button" class="admin-corner-btn admin-corner-album-btn" data-action="manage-album" data-card-id="${escapeHtml(ride.id)}" data-ride-id="${escapeHtml(ride.rideId || '')}" data-ride-title="${escapeHtml(ride.title)}" data-album-url="${escapeHtml(ride.photoAlbumUrl || '')}" data-photo-count="${escapeHtml(ride.photoCount || 0)}" data-cover-url="${escapeHtml(ride.coverPhotoUrl || '')}" title="${ride.photoAlbumUrl ? 'Manage Photo Album' : 'Add Photo Album'}">
              <svg class="admin-corner-svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
            </button>
            <button type="button" class="admin-corner-btn admin-corner-archive-btn" data-action="archive-card" data-card-id="${escapeHtml(ride.id)}" data-ride-title="${escapeHtml(ride.title)}" title="Archive Media Card">
              <svg class="admin-corner-svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="21 8 21 21 3 21 3 8"/>
                <rect x="1" y="3" width="22" height="5"/>
                <line x1="10" y1="12" x2="14" y2="12"/>
              </svg>
            </button>
            ${(!isBtcnj || ride.isCommunityCard) ? `
              <button type="button" class="admin-corner-btn admin-corner-exclude-btn" data-action="exclude-member-card" data-card-id="${escapeHtml(ride.id)}" data-ride-title="${escapeHtml(ride.title)}" title="Exclude from Member Media" style="color: #ef4444;">
                <svg class="admin-corner-svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            ` : ''}
          </div>
        ` : ''}
      </article>
    `;
  } catch (err) {
    console.warn('Failed to render ride card:', ride?.id || ride?.title, err);
    return '';
  }
}

// ============================================================================
// 5. Feed Search, Season Grouping & Batch Scaling Engine
// ============================================================================

/**
 * Broadway Marquee & Golden Beacon Nudge Engine
 * Tastefully animates glowing incandescent bulbs around Photos and YouTube buttons
 * to guide user attention without annoyance (5s duration, 45s recurring idle cycle,
 * immediate suppression on user interaction or tab blur).
 */
export function renderMarqueeBulbFrameHtml(options = {}) {
  const isPhotos = Boolean(options && options.isPhotos !== false);
  return isPhotos ? `
    <div class="giant-hand-pointer" aria-hidden="true">
      <span class="giant-hand-icon">👇</span>
    </div>
  ` : '';
}

let broadwayBackoffTimeoutId = null;
let broadwaySurgeTimeoutId = null;
let broadwayHandTimeoutId = null;
let broadwayNudgeDismissed = false;
let spotlightAnimationRunCount = 0;
const SPOTLIGHT_BACKOFF_SCHEDULE_MS = [20000, 40000]; // Repeat 1: +20s, Repeat 2: +40s, then STOP

function scheduleNextBroadwayBackoffRun() {
  if (broadwayBackoffTimeoutId) {
    clearTimeout(broadwayBackoffTimeoutId);
    broadwayBackoffTimeoutId = null;
  }
  if (broadwayNudgeDismissed) return;
  if (spotlightAnimationRunCount >= SPOTLIGHT_BACKOFF_SCHEDULE_MS.length + 1) {
    // All 3 scheduled runs completed (Initial + 2 repeats) -> permanently stop for this visit
    return;
  }
  const delay = SPOTLIGHT_BACKOFF_SCHEDULE_MS[spotlightAnimationRunCount - 1];
  if (typeof delay !== 'number') return;

  broadwayBackoffTimeoutId = setTimeout(() => {
    const isSpotlightActive = Boolean(activeSpotlightRideId || (typeof document !== 'undefined' && document.querySelector('.spotlight-page-container, .is-spotlight')));
    if (isSpotlightActive && !broadwayNudgeDismissed && (!document.hidden || document.visibilityState === 'visible')) {
      triggerBroadwayNudge();
    }
  }, delay);
}

export function triggerBroadwayNudge() {
  if (broadwayNudgeDismissed) return;
  if (typeof document === 'undefined') return;

  // Strictly target spotlight media buttons (Spotlight page only)
  const targets = document.querySelectorAll(
    '.is-spotlight .media-photo-spotlight, .spotlight-card-wrapper .media-photo-spotlight, ' +
    '.is-spotlight .matched-videos-section, .spotlight-card-wrapper .matched-videos-section'
  );
  if (!targets || targets.length === 0) return;

  spotlightAnimationRunCount++;
  targets.forEach((el) => {
    el.classList.add('is-marquee-active');
    el.classList.add('is-power-surge-active');
  });

  if (broadwaySurgeTimeoutId) clearTimeout(broadwaySurgeTimeoutId);
  if (broadwayHandTimeoutId) clearTimeout(broadwayHandTimeoutId);

  // Button power surge & text morph restores to normal at 5.0s
  broadwaySurgeTimeoutId = setTimeout(() => {
    targets.forEach((el) => {
      el.classList.remove('is-power-surge-active');
    });
  }, 5000);

  // Pointer finger resides an additional 2s (7.0s total) over restored normal button
  broadwayHandTimeoutId = setTimeout(() => {
    targets.forEach((el) => {
      el.classList.remove('is-marquee-active');
    });
    // Schedule the next stepped backoff repeat after the current sequence completes
    scheduleNextBroadwayBackoffRun();
  }, 7000);
}

export function dismissBroadwayNudge(permanent = false) {
  if (typeof document !== 'undefined') {
    const targets = document.querySelectorAll(
      '.is-spotlight .media-photo-spotlight, .spotlight-card-wrapper .media-photo-spotlight, ' +
      '.is-spotlight .matched-videos-section, .spotlight-card-wrapper .matched-videos-section, ' +
      '.media-photo-spotlight, .matched-videos-section'
    );
    targets.forEach((el) => {
      el.classList.remove('is-marquee-active');
      el.classList.remove('is-power-surge-active');
    });
  }
  if (broadwaySurgeTimeoutId) {
    clearTimeout(broadwaySurgeTimeoutId);
    broadwaySurgeTimeoutId = null;
  }
  if (broadwayHandTimeoutId) {
    clearTimeout(broadwayHandTimeoutId);
    broadwayHandTimeoutId = null;
  }
  if (broadwayBackoffTimeoutId) {
    clearTimeout(broadwayBackoffTimeoutId);
    broadwayBackoffTimeoutId = null;
  }
  if (permanent) {
    broadwayNudgeDismissed = true;
  }
}

export function initBroadwayMarqueeNudgeEngine() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  dismissBroadwayNudge(false);
  spotlightAnimationRunCount = 0;

  // Page visibility awareness: pause when tab hidden
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      dismissBroadwayNudge(false);
    }
  });

  // User click on media buttons clears current animation cycle
  document.addEventListener('click', (e) => {
    if (e.target && e.target.closest && e.target.closest('.media-photo-spotlight, .matched-videos-section, .btn-open-album, .photo-card-actions')) {
      dismissBroadwayNudge(false);
    }
  }, { capture: true, passive: true });
}

export function getSearchQueryFromUrl() {
  if (typeof window === 'undefined' || !window.location || !window.location.search) return '';
  try {
    const params = new URLSearchParams(window.location.search);
    return (params.get('q') || params.get('search') || params.get('query') || '').trim();
  } catch (_) {
    return '';
  }
}

export function updateUrlSearchQuery(query) {
  if (typeof window === 'undefined' || !window.location || !window.history) return;
  try {
    const url = new URL(window.location.href);
    const trimmed = (query || '').trim();
    if (trimmed) {
      url.searchParams.set('q', trimmed);
    } else {
      url.searchParams.delete('q');
      url.searchParams.delete('search');
      url.searchParams.delete('query');
    }
    const relativeUrl = url.pathname + (url.search ? url.search : '') + (url.hash ? url.hash : '');
    window.history.replaceState(null, '', relativeUrl);
  } catch (_) {}
}

export function updateHeaderShareButtonState(query) {
  if (typeof document === 'undefined') return;
  const shareBtn = document.getElementById('header-share-btn');
  if (!shareBtn) return;
  const trimmed = (query || '').trim();
  if (trimmed.length > 0) {
    shareBtn.classList.add('is-search-active');
    shareBtn.setAttribute('data-tooltip', 'Copy Search Link');
    shareBtn.setAttribute('aria-label', `Copy link for search: ${trimmed}`);
  } else {
    shareBtn.classList.remove('is-search-active');
    shareBtn.setAttribute('data-tooltip', 'Copy Link');
    shareBtn.setAttribute('aria-label', 'Copy website link');
  }
}

export const PAGINATION_STATE = {
  currentPage: 1,
  cardsPerPage: (() => {
    try {
      const saved = typeof localStorage !== 'undefined' ? localStorage.getItem('btcnjcc_cards_per_page') : null;
      if (saved === 'all' || saved === '-1') return -1;
      const num = parseInt(saved, 10);
      return [15, 30, 60, -1].includes(num) ? num : 30;
    } catch (_) {
      return 30;
    }
  })(),
  searchQuery: getSearchQueryFromUrl(),
  isMobileSearchOpen: Boolean(getSearchQueryFromUrl()),
  viewMode: (typeof resolveInitialMediaViewMode === 'function' ? resolveInitialMediaViewMode() : 'grid')
};

// Backwards-compatible proxy for legacy test suites referencing FEED_STATE
export const FEED_STATE = {
  get batchSize() { return PAGINATION_STATE.cardsPerPage; },
  set batchSize(v) { PAGINATION_STATE.cardsPerPage = v; },
  get visibleCount() { return PAGINATION_STATE.cardsPerPage > 0 ? PAGINATION_STATE.cardsPerPage : Infinity; },
  set visibleCount(v) {},
  get searchQuery() { return PAGINATION_STATE.searchQuery; },
  set searchQuery(v) { PAGINATION_STATE.searchQuery = v; },
  get selectedSeason() { return 'all'; },
  set selectedSeason(v) {}
};

/**
 * Escapes special regex characters in a query token string.
 */
export function escapeRegExp(string) {
  return String(string).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Wraps matching search tokens in <mark class="search-match-highlight"> with strict HTML entity escaping.
 */
export function highlightSearchText(text, tokens) {
  if (!text) return '';
  const rawStr = String(text);
  if (!tokens || tokens.length === 0) return escapeHtml(rawStr);

  const validTokens = tokens.filter(t => t && t.trim().length > 0);
  if (validTokens.length === 0) return escapeHtml(rawStr);

  const pattern = validTokens.map(t => escapeRegExp(t)).join('|');
  if (!pattern) return escapeHtml(rawStr);

  const regex = new RegExp(`(${pattern})`, 'gi');
  const parts = rawStr.split(regex);
  return parts.map((part, i) => {
    if (i % 2 === 1) {
      return `<mark class="search-match-highlight">${escapeHtml(part)}</mark>`;
    }
    return escapeHtml(part);
  }).join('');
}

/**
 * Discloses why a card matched an active query when the match stems from
 * hidden, non-rendered, or auxiliary metadata (attended members/roster,
 * leader full last names, secondary playlist video titles, or locations).
 */
export function extractSearchMatchContext(ride, query) {
  if (!query || typeof query !== 'string' || !ride) return null;
  const cleanQuery = query.trim().toLowerCase();
  if (cleanQuery.length === 0) return null;

  const tokens = cleanQuery.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return null;

  const titleLower = (ride.title || '').toLowerCase();
  const uncoveredTokens = tokens.filter(t => !titleLower.includes(t));

  // If all tokens are already visibly represented in the card title, no extra pill is needed
  if (uncoveredTokens.length === 0) {
    return null;
  }

  const targetTokens = uncoveredTokens;

  // 1. Check Attended Member Roster (highest priority invisible metadata)
  const rawRoster = ride.members || ride.joinedMembers || ride.riders || [];
  const roster = Array.isArray(rawRoster)
    ? rawRoster
    : (typeof rawRoster === 'string' ? [rawRoster] : []);

  const matchedMembers = [];
  for (const m of roster) {
    if (!m) continue;
    const mStr = String(m).trim();
    const mLower = mStr.toLowerCase();
    if (targetTokens.some(t => mLower.includes(t))) {
      matchedMembers.push(mStr);
    }
  }

  if (matchedMembers.length > 0) {
    const displayMembers = matchedMembers.slice(0, 2);
    const remaining = matchedMembers.length - displayMembers.length;
    const highlightedValues = displayMembers.map(m => highlightSearchText(m, targetTokens));
    const textLabel = matchedMembers.length === 1 ? 'Rider' : 'Riders';
    const remainingText = remaining > 0 ? ` +${remaining} more` : '';
    return {
      type: 'member',
      icon: '👤',
      label: textLabel,
      html: `<span class="search-context-pill pill-member"><span class="context-icon" aria-hidden="true">👤</span><span class="context-label">${textLabel}:</span> <span class="context-value">${highlightedValues.join(', ')}${remainingText}</span></span>`
    };
  }

  // 2. Check Leaders (specifically if full or last name matched, when not visible in firstNameOnly)
  const rawLeaders = ride.leaders || [];
  const leadersList = Array.isArray(rawLeaders)
    ? rawLeaders
    : (typeof rawLeaders === 'string' ? [rawLeaders] : []);

  const matchedLeaders = [];
  for (const l of leadersList) {
    if (!l) continue;
    const lStr = String(l).trim();
    const lLower = lStr.toLowerCase();
    if (targetTokens.some(t => lLower.includes(t))) {
      matchedLeaders.push(lStr);
    }
  }

  if (matchedLeaders.length > 0) {
    const displayLeader = matchedLeaders[0];
    const highlighted = highlightSearchText(displayLeader, targetTokens);
    return {
      type: 'leader',
      icon: '👑',
      label: 'Leader',
      html: `<span class="search-context-pill pill-leader"><span class="context-icon" aria-hidden="true">👑</span><span class="context-label">Leader:</span> <span class="context-value">${highlighted}</span></span>`
    };
  }

  // 3. Check Matched Playlist Videos (secondary video titles)
  if (Array.isArray(ride.matchedVideos) && ride.matchedVideos.length > 0) {
    const matchedVid = ride.matchedVideos.find(v => {
      const vTitle = (v && v.title) ? String(v.title).toLowerCase() : '';
      return targetTokens.some(t => vTitle.includes(t));
    });
    if (matchedVid && matchedVid.title) {
      const cleanTitle = typeof cleanVideoChipTitle === 'function' ? cleanVideoChipTitle(matchedVid.title) : matchedVid.title;
      const highlighted = highlightSearchText(cleanTitle, targetTokens);
      return {
        type: 'video',
        icon: '🎬',
        label: 'Video',
        html: `<span class="search-context-pill pill-video"><span class="context-icon" aria-hidden="true">🎬</span><span class="context-label">Video:</span> <span class="context-value">${highlighted}</span></span>`
      };
    }
  }

  // 4. Check Location / Start Place
  const loc = ride.location || ride.start || '';
  if (loc) {
    const locLower = String(loc).toLowerCase();
    if (targetTokens.some(t => locLower.includes(t))) {
      const highlighted = highlightSearchText(loc, targetTokens);
      return {
        type: 'location',
        icon: '📍',
        label: 'Location',
        html: `<span class="search-context-pill pill-location"><span class="context-icon" aria-hidden="true">📍</span><span class="context-label">Location:</span> <span class="context-value">${highlighted}</span></span>`
      };
    }
  }

  // 5. Check Ride ID (numeric match)
  const rId = String(ride.rideId || ride.id || '');
  if (rId && targetTokens.some(t => rId.toLowerCase().includes(t))) {
    return {
      type: 'id',
      icon: '🔢',
      label: 'Ride ID',
      html: `<span class="search-context-pill pill-id"><span class="context-icon" aria-hidden="true">🔢</span><span class="context-label">Ride ID:</span> <span class="context-value">#${highlightSearchText(rId, targetTokens)}</span></span>`
    };
  }

  return null;
}

/**
 * Multi-token search matching across title, speed category, terrain, dates,
 * calendar year, ride leaders, attended members roster (joinedMembers), and video titles.
 */
export function matchesRideSearch(ride, query) {
  if (!query || typeof query !== 'string') return true;
  const cleanQuery = query.trim().toLowerCase();
  if (cleanQuery.length === 0) return true;

  const tokens = cleanQuery.split(/\s+/).filter(Boolean);

  const title = (ride.title || '').toLowerCase();
  const location = (ride.location || '').toLowerCase();
  const category = (ride.category || '').toLowerCase();
  const terrain = (ride.terrain || '').toLowerCase();
  const dateStr = (ride.dateIso || ride.dateStr || '').toLowerCase();
  const formattedDate = (formatRideDate(ride.dateIso || ride.dateStr) || '').toLowerCase();
  const rideYear = getRideYear(ride).toLowerCase();
  const rideId = String(ride.rideId || ride.id || '').toLowerCase();

  // Leaders (including Devil Emoji Enrichment tokens)
  const normalizedLeaders = normalizeRideLeaders(ride.leaders);
  let leadersText = '';
  if (normalizedLeaders.length > 0) {
    const enriched = normalizedLeaders.map(n => formatLeaderDisplayName(n, { firstNameOnly: false })).join(' ');
    leadersText = `${normalizedLeaders.join(' ')} ${enriched}`.toLowerCase();
  }

  // Attended Members / Roster
  let membersText = '';
  const roster = ride.members || ride.joinedMembers || ride.riders || [];
  if (Array.isArray(roster)) {
    membersText = roster.join(' ').toLowerCase();
  } else if (typeof roster === 'string') {
    membersText = roster.toLowerCase();
  }

  // Videos
  let videoTitles = '';
  if (Array.isArray(ride.matchedVideos)) {
    videoTitles = ride.matchedVideos.map(v => v.title || '').join(' ').toLowerCase();
  }

  const searchableCorpus = `${title} ${rideId} ${leadersText} ${membersText} ${category} ${terrain} ${dateStr} ${formattedDate} ${rideYear} ${location} ${videoTitles}`;

  return tokens.every(token => searchableCorpus.includes(token));
}

/**
 * Extracts a 4-digit calendar year from a ride's date string.
 */
export function getRideYear(ride) {
  const d = String(ride.dateIso || ride.dateStr || '');
  const match = d.match(/^(\d{4})/);
  if (match) return match[1];
  const parsed = new Date(d);
  if (!isNaN(parsed.getFullYear())) {
    return String(parsed.getFullYear());
  }
  return '2026';
}

/**
 * Groups an array of rides into season year buckets sorted descending.
 */
export function groupRidesBySeason(rides) {
  const map = new Map();
  (rides || []).forEach(ride => {
    const year = getRideYear(ride);
    if (!map.has(year)) {
      map.set(year, []);
    }
    map.get(year).push(ride);
  });

  const sortedYears = Array.from(map.keys()).sort((a, b) => {
    return parseInt(b, 10) - parseInt(a, 10);
  });

  return sortedYears.map(year => ({
    year,
    rides: map.get(year),
    stats: calculateSeasonStats(map.get(year))
  }));
}

/**
 * Calculates season telemetry: ride count, video count, photo album count.
 */
export function calculateSeasonStats(rides) {
  let videoCount = 0;
  let albumCount = 0;
  (rides || []).forEach(r => {
    if (Array.isArray(r.matchedVideos)) videoCount += r.matchedVideos.length;
    if (r.photoAlbumUrl) albumCount += 1;
  });
  return {
    rideCount: rides.length,
    videoCount,
    albumCount
  };
}

/**
 * Renders an in-grid stylistic Year Milestone Card for prior seasons (e.g. 2025).
 * Current year (2026) is omitted to reduce clutter and bring rides to the top.
 */
export function renderYearMilestoneCardHtml(item, options = { showVideos: true }) {
  const year = item.year || '';
  const stats = item.stats || { rideCount: 0, videoCount: 0, albumCount: 0 };
  const showVideos = options && options.showVideos !== false;
  return `
    <div class="ride-card media-ride-card year-milestone-card ${showVideos ? '' : 'photo-milestone-card'}" data-season="${escapeHtml(year)}" aria-label="${escapeHtml(year)} Milestone">
      <div class="year-card-backdrop" aria-hidden="true">
        <div class="year-aura-glow"></div>
        <svg class="year-card-contour-svg" viewBox="0 0 320 320" fill="none" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="yearAeroGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stop-color="#2563eb" stop-opacity="0.15"/>
              <stop offset="35%" stop-color="#00f2fe" stop-opacity="0.80"/>
              <stop offset="65%" stop-color="#10b981" stop-opacity="0.75"/>
              <stop offset="100%" stop-color="#fbbf24" stop-opacity="0.50"/>
            </linearGradient>
            <linearGradient id="contourGrad2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stop-color="#fbbf24" stop-opacity="0.05"/>
              <stop offset="45%" stop-color="#fbbf24" stop-opacity="0.75"/>
              <stop offset="100%" stop-color="#f59e0b" stop-opacity="0.1"/>
            </linearGradient>
            <linearGradient id="contourGrad3" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stop-color="#38bdf8" stop-opacity="0.05"/>
              <stop offset="70%" stop-color="#38bdf8" stop-opacity="0.5"/>
              <stop offset="100%" stop-color="#0284c7" stop-opacity="0.05"/>
            </linearGradient>
            <linearGradient id="elevationAreaGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stop-color="#38bdf8" stop-opacity="0.16"/>
              <stop offset="100%" stop-color="#0f172a" stop-opacity="0.0"/>
            </linearGradient>
          </defs>
          <path class="contour-area" d="M0 215 Q 60 135, 130 175 T 230 135 T 320 195 L 320 320 L 0 320 Z" fill="url(#elevationAreaGrad)"/>
          <path class="contour-line contour-line-1 year-streamline year-streamline-1" d="M0 215 Q 60 135, 130 175 T 230 135 T 320 195" stroke="url(#yearAeroGrad)" stroke-width="2.4" fill="none"/>
          <path class="contour-line contour-line-2 year-streamline year-streamline-2" d="M0 238 Q 70 165, 160 205 T 270 165 T 320 225" stroke="url(#contourGrad2)" stroke-width="1.8" fill="none"/>
          <path class="contour-line contour-line-3 year-streamline year-streamline-3" d="M0 258 Q 90 200, 180 235 T 290 195 T 320 248" stroke="url(#contourGrad3)" stroke-width="1.2" fill="none"/>
        </svg>
      </div>
      <div class="year-card-body">
        <div class="year-card-center-stage">
          <span class="year-card-number">${escapeHtml(year)}</span>
        </div>
        <div class="year-card-metrics-grid ${showVideos ? '' : 'photo-metrics-grid-duo'}">
          <div class="year-metric-card metric-rides">
            <span class="year-metric-val stat-cyan">${stats.rideCount}</span>
            <span class="year-metric-lbl">RIDES</span>
          </div>
          ${showVideos ? `
          <div class="year-metric-card metric-videos">
            <span class="year-metric-val stat-red">${stats.videoCount}</span>
            <span class="year-metric-lbl">VIDEOS</span>
          </div>
          ` : ''}
          <div class="year-metric-card metric-albums">
            <span class="year-metric-val stat-amber">${stats.albumCount}</span>
            <span class="year-metric-lbl">ALBUMS</span>
          </div>
        </div>
      </div>
      <div class="year-card-cyclist-layer" aria-hidden="true">
        <svg class="year-card-contour-svg year-card-cyclist-svg" viewBox="0 0 320 320" fill="none" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="yearAeroGradCyclist" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stop-color="#2563eb" stop-opacity="0.15"/>
              <stop offset="35%" stop-color="#00f2fe" stop-opacity="0.80"/>
              <stop offset="65%" stop-color="#10b981" stop-opacity="0.75"/>
              <stop offset="100%" stop-color="#fbbf24" stop-opacity="0.50"/>
            </linearGradient>
          </defs>
          <g class="ridge-climber micro-cad-cyclist" aria-hidden="true">
            <!-- Trailing CFD Aerodynamic Slipstream Wake -->
            <path class="cad-slipstream-wake wake-upper" d="M -5 -11.8 Q -12 -11.8 -20 -10" stroke="url(#yearAeroGradCyclist)" stroke-width="1.1" stroke-dasharray="6 4" fill="none"/>
            <path class="cad-slipstream-wake wake-lower" d="M -9 -4 Q -16 -4 -24 -2" stroke="#2563eb" stroke-width="0.9" stroke-dasharray="5 3" fill="none"/>

            <!-- Dynamic Bicycle Assembly with Lateral Frame Sway ("Dancing on the Pedals") -->
            <g class="cad-frame-group">
              <!-- Rear Deep Carbon Wheel & Bladed Spokes -->
              <g class="cad-wheel cad-wheel-rear">
                <circle cx="-8" cy="-4" r="4" stroke="#38bdf8" stroke-width="1.1" fill="#090d16"/>
                <circle cx="-8" cy="-4" r="3" stroke="rgba(56, 189, 248, 0.45)" stroke-width="1.4" fill="none"/>
                <circle cx="-8" cy="-4" r="1.3" stroke="#fbbf24" stroke-width="0.7" fill="none"/>
                <circle cx="-8" cy="-4" r="0.8" fill="#38bdf8"/>
                <line x1="-8" y1="-8" x2="-8" y2="0" stroke="#38bdf8" stroke-width="0.6"/>
                <line x1="-12" y1="-4" x2="-4" y2="-4" stroke="#38bdf8" stroke-width="0.6"/>
                <line x1="-10.8" y1="-6.8" x2="-5.2" y2="-1.2" stroke="#38bdf8" stroke-width="0.6"/>
                <line x1="-10.8" y1="-1.2" x2="-5.2" y2="-6.8" stroke="#38bdf8" stroke-width="0.6"/>
              </g>

              <!-- Front Deep Carbon Wheel & Bladed Spokes -->
              <g class="cad-wheel cad-wheel-front">
                <circle cx="8" cy="-4" r="4" stroke="#38bdf8" stroke-width="1.1" fill="#090d16"/>
                <circle cx="8" cy="-4" r="3" stroke="rgba(56, 189, 248, 0.45)" stroke-width="1.4" fill="none"/>
                <circle cx="8" cy="-4" r="1.3" stroke="#38bdf8" stroke-width="0.7" fill="none"/>
                <circle cx="8" cy="-4" r="0.8" fill="#38bdf8"/>
                <line x1="8" y1="-8" x2="8" y2="0" stroke="#38bdf8" stroke-width="0.6"/>
                <line x1="4" y1="-4" x2="12" y2="-4" stroke="#38bdf8" stroke-width="0.6"/>
                <line x1="5.2" y1="-6.8" x2="10.8" y2="-1.2" stroke="#38bdf8" stroke-width="0.6"/>
                <line x1="5.2" y1="-1.2" x2="10.8" y2="-6.8" stroke="#38bdf8" stroke-width="0.6"/>
              </g>

              <!-- Specialized Aethos CAD Carbon Frame Geometry -->
              <!-- Chainstay & Dropped Seatstays -->
              <path d="M -8 -4 L 0 -4.5 L -3.2 -10 M -8 -4 L -2.2 -8.2" stroke="#38bdf8" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
              <!-- Downtube, Headtube, Bladed Fork & Toptube -->
              <path d="M 0 -4.5 L 5.5 -11 L 8 -4 M 5.5 -11 L -3.2 -10" stroke="#38bdf8" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
              <!-- Gold Metallic Cockpit Bar Drops & Seatpost -->
              <path d="M 5.5 -11 L 7.2 -12 L 8.5 -10.5" stroke="#fbbf24" stroke-width="1.0" stroke-linecap="round" fill="none"/>
              <line x1="-3.2" y1="-10" x2="-4" y2="-12" stroke="#38bdf8" stroke-width="1.1"/>
              <path d="M -5.8 -12.2 L -2.5 -12" stroke="#0f172a" stroke-width="1.5" stroke-linecap="round"/>
              <circle cx="0" cy="-4.5" r="1.6" stroke="#fbbf24" stroke-width="0.8" fill="none"/>
            </g>

            <!-- Posture A: Seated Aero Tuck (Active on flats, saddle roll & descent plunge) -->
            <g class="cad-posture cad-posture-seated">
              <!-- Seated Pedaling Legs -->
              <g class="cad-crank-assembly">
                <!-- Left Leg (Background Layer) -->
                <path class="cad-leg leg-left" d="M -3.2 -11.5 L -0.5 -7 L 0 -2.5" stroke="#94a3b8" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
                <!-- Right Leg (Foreground Kinetic Layer) -->
                <path class="cad-leg leg-right" d="M -3.2 -11.5 L 1.8 -8.5 L 0 -6.5" stroke="#e2e8f0" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
              </g>

              <!-- Sculpted Seated Torso, Aero Helmet & Visor -->
              <g class="cad-rider-body">
                <!-- Torso tucked into aerodynamic drop posture -->
                <path d="M -3.5 -11.8 C -0.8 -13.5, 2.5 -13.2, 4.5 -12.2 C 5.5 -11.7, 6.5 -10.8, 7.2 -10.8" stroke="#f8fafc" stroke-width="2.4" stroke-linecap="round" fill="none"/>
                <!-- Arm reaching to aero brake hoods -->
                <path d="M 3.5 -12.2 L 5.5 -9.8 L 7.5 -10.8" stroke="#e2e8f0" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
                <!-- Aero Teardrop Helmet with Specular Cyan Edge -->
                <path d="M 3.8 -15 C 6 -16, 8.5 -15.2, 9 -13.2 C 8 -12.8, 5.5 -13.2, 3.8 -13.8 Z" fill="#38bdf8"/>
                <path d="M 4.2 -14.8 C 6.2 -15.6, 8 -14.8, 8.5 -13.5" stroke="#ffffff" stroke-width="0.8" stroke-linecap="round" fill="none"/>
              </g>
            </g>

            <!-- Posture B: Standing Attack ("Dancing on the Pedals" - Active on steep alpine wall) -->
            <g class="cad-posture cad-posture-standing">
              <!-- Standing Left Leg (Background Recovery Layer) -->
              <path class="cad-leg-standing leg-standing-left" d="M -1.2 -10.5 L 0.2 -8.6 L -0.8 -5.8" stroke="#94a3b8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>

              <!-- Sculpted Standing Torso (Continuous athletic silhouette anchored to hips) -->
              <g class="cad-rider-body-standing">
                <!-- Pelvis & Spine driving power (Continuous athletic silhouette anchored to hips) -->
                <path d="M -1.2 -10.5 C -0.5 -11.5, 1.5 -13.2, 4.8 -13.0" stroke="#ffffff" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
                <!-- Arm pulling on hoods (Locked to handlebar grip) -->
                <path d="M 4.6 -12.8 L 6.2 -11.6 L 7.5 -11.0" stroke="#f1f5f9" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
                <!-- Aerodynamic Climbing Helmet & Cyan Edge (Image 2 match) -->
                <path d="M 5.0 -16.5 C 7.0 -17.6, 9.4 -16.6, 9.8 -14.6 C 8.8 -14.2, 6.5 -14.6, 5.0 -15.2 Z" fill="#00f2fe"/>
                <path d="M 5.3 -16.3 C 7.2 -17.2, 8.8 -16.4, 9.2 -14.8" stroke="#ffffff" stroke-width="0.8" stroke-linecap="round" fill="none"/>
                <circle cx="8.6" cy="-14.2" r="0.6" fill="#0f172a"/>

                <!-- Exaggerated Out-of-the-Saddle Climbing Sweat Burst Cluster (11 Droplets Total) -->
                <g class="cad-sweat-cluster" aria-hidden="true">
                  <path class="cad-sweat-drop cad-sweat-1" d="M 8.2 -16.5 C 7.6 -17.5, 6.2 -18.2, 5.4 -18.8 C 5.8 -18.0, 7.2 -17.0, 8.2 -16.5 Z" fill="#00f2fe"/>
                  <path class="cad-sweat-drop cad-sweat-2" d="M 7.2 -15.2 C 6.0 -15.8, 4.4 -16.0, 3.2 -16.2 C 4.2 -15.4, 5.8 -15.0, 7.2 -15.2 Z" fill="#38bdf8"/>
                  <path class="cad-sweat-drop cad-sweat-3" d="M 8.6 -13.6 C 7.8 -13.0, 6.6 -12.4, 5.0 -12.0 C 6.0 -12.8, 7.4 -13.2, 8.6 -13.6 Z" fill="#ffffff"/>
                  <path class="cad-sweat-drop cad-sweat-4" d="M 9.5 -17.2 C 9.0 -18.2, 8.0 -18.9, 7.2 -19.4 C 7.8 -18.4, 8.8 -17.7, 9.5 -17.2 Z" fill="#00f2fe"/>
                  <path class="cad-sweat-drop cad-sweat-5" d="M 8.8 -13.0 C 8.2 -12.2, 7.4 -11.5, 6.4 -11.0 C 7.2 -11.8, 8.2 -12.4, 8.8 -13.0 Z" fill="#38bdf8"/>
                  <path class="cad-sweat-drop cad-sweat-6" d="M 6.8 -17.8 C 6.2 -18.8, 5.0 -19.6, 3.8 -20.2 C 4.6 -19.2, 5.8 -18.4, 6.8 -17.8 Z" fill="#ffffff"/>
                  <path class="cad-sweat-drop cad-sweat-7" d="M 5.6 -15.8 C 4.8 -16.4, 3.6 -16.8, 2.4 -17.0 C 3.4 -16.2, 4.6 -15.8, 5.6 -15.8 Z" fill="#00f2fe"/>
                  <path class="cad-sweat-drop cad-sweat-8" d="M 9.2 -14.8 C 8.6 -15.4, 7.6 -15.8, 6.6 -16.0 C 7.4 -15.2, 8.4 -14.8, 9.2 -14.8 Z" fill="#38bdf8"/>
                  <path class="cad-sweat-drop cad-sweat-9" d="M 7.8 -16.0 C 7.2 -17.0, 5.8 -17.8, 4.6 -18.4 C 5.4 -17.4, 6.8 -16.6, 7.8 -16.0 Z" fill="#ffffff"/>
                  <path class="cad-sweat-drop cad-sweat-10" d="M 7.0 -14.0 C 6.2 -14.6, 5.0 -15.0, 3.8 -15.2 C 4.8 -14.4, 6.0 -14.0, 7.0 -14.0 Z" fill="#00f2fe"/>
                  <path class="cad-sweat-drop cad-sweat-11" d="M 8.0 -17.0 C 7.4 -18.0, 6.0 -18.8, 4.8 -19.4 C 5.6 -18.4, 7.0 -17.6, 8.0 -17.0 Z" fill="#38bdf8"/>
                  <circle class="cad-sweat-ping" cx="7.8" cy="-15.8" r="1.8" stroke="#00f2fe" stroke-width="0.9" fill="none"/>
                </g>
              </g>

              <!-- Standing Right Leg (Foreground Power Downstroke Layer) -->
              <path class="cad-leg-standing leg-standing-right" d="M -1.2 -10.5 L 1.6 -7.5 L 0.8 -4.2" stroke="#ffffff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
            </g>
          </g>
        </svg>
      </div>
    </div>
  `;
}

export function renderPhotoYearMilestoneCardHtml(item) {
  return renderYearMilestoneCardHtml(item, { showVideos: false });
}

export function getTodayLocalIso(refDate = new Date()) {
  const now = refDate instanceof Date ? refDate : new Date(refDate);
  const nowYear = now.getFullYear();
  const nowMonth = String(now.getMonth() + 1).padStart(2, '0');
  const nowDay = String(now.getDate()).padStart(2, '0');
  return `${nowYear}-${nowMonth}-${nowDay}`;
}

export function insertHistoricalDividerCardItem(items, refDate = new Date(), options = {}) {
  if (!Array.isArray(items)) return items;
  if (items.some(item => item && item.isHistoricalDivider)) return items;

  const isSearchActive = Boolean(options && options.isSearchActive);
  const todayIso = getTodayLocalIso(refDate);

  // Check if any rides are in the future (>= todayIso)
  const hasFutureRides = items.some(item => {
    if (!item || item.isMilestone || item.isHistoricalDivider) return false;
    const r = item.ride;
    const d = r?.dateIso || (r?.dateStr && String(r.dateStr).match(/\d{4}-\d{2}-\d{2}/)?.[0]);
    return d && d >= todayIso;
  });

  // With filter applied, if there is no upcoming ride with that filter, then the clock card should not show
  if (isSearchActive && !hasFutureRides) {
    return items;
  }

  const dividerItem = {
    isHistoricalDivider: true,
    todayIso
  };

  if (!hasFutureRides && items.length > 0) {
    // All rides are historical -> index 0 (left-most in feed)
    items.unshift(dividerItem);
    return items;
  }

  const firstPastIdx = items.findIndex(item => {
    if (!item || item.isMilestone || item.isHistoricalDivider) return false;
    const r = item.ride;
    const d = r?.dateIso || (r?.dateStr && String(r.dateStr).match(/\d{4}-\d{2}-\d{2}/)?.[0]);
    return d && d < todayIso;
  });

  if (firstPastIdx !== -1) {
    items.splice(firstPastIdx, 0, dividerItem);
  }
  return items;
}

export function getHistoricalClockAngles(refDate = new Date(), options = {}) {
  const now = refDate instanceof Date ? refDate : new Date(refDate);
  const s = now.getSeconds();
  const ms = now.getMilliseconds();

  // Mechanical Escapement Quantization (Spring-Driven Balance Wheel Physics)
  // Balance wheel oscillation: 2.5 Hz (5 beats/sec = 18,000 vibrations/hour standard)
  const beatsPerSec = (options && typeof options.beatsPerSec === 'number' && options.beatsPerSec > 0)
    ? options.beatsPerSec
    : 5;
  const snapMs = (options && typeof options.snapDurationMs === 'number')
    ? options.snapDurationMs
    : 18; // Pallet impulse unlock drop duration (ms)

  const stepDuration = 1000 / beatsPerSec;
  const stepIndex = Math.floor(ms / stepDuration);
  const stepProgress = ms % stepDuration;

  let snap = 1.0;
  if (snapMs > 0 && stepIndex > 0 && stepProgress < snapMs) {
    const t = stepProgress / snapMs;
    snap = 1 - Math.pow(1 - t, 3);
  }

  let discreteFrac = (stepIndex - 1 + snap) / beatsPerSec;
  if (stepIndex === 0) {
    discreteFrac = 0;
  } else if (stepIndex === beatsPerSec - 1 && snapMs > 0) {
    const rollStart = 1000 - snapMs;
    if (ms >= rollStart) {
      const t = (ms - rollStart) / snapMs;
      const rollEase = 1 - Math.pow(1 - t, 3);
      discreteFrac = (beatsPerSec - 1 + rollEase) / beatsPerSec;
    }
  }

  const secDeg = ((s + discreteFrac) * 6 + 360) % 360;

  // Gear Train Motion Works: continuously geared to solar local time
  const sFrac = s + ms / 1000;
  const m = now.getMinutes() + sFrac / 60;
  const h = (now.getHours() % 12) + m / 60;

  return {
    secDeg,
    minDeg: m * 6,
    hourDeg: h * 30,
    stepIndex,
    beatsPerSec
  };
}

export function parseRideTime(r) {
  if (!r) return null;
  const candidate = r.time24 || r.startTime || r.meetTime || r.timeRaw || r.time;
  if (!candidate) return null;
  const str = String(candidate).trim();
  // 1. 24-hour format (e.g. "08:15", "14:30")
  const m24 = str.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (m24) {
    return { hours: parseInt(m24[1], 10), minutes: parseInt(m24[2], 10) };
  }
  // 2. 12-hour format with optional meridian (e.g. "8:15a", "8:15am", "8:15 AM", "1:30p", "1:30 PM")
  const m12 = str.match(/^([0-1]?\d):([0-5]\d)\s*([ap]m?|[AP]M?)?$/i);
  if (m12) {
    let hrs = parseInt(m12[1], 10);
    const mins = parseInt(m12[2], 10);
    const mer = m12[3] ? m12[3].toLowerCase() : '';
    if (mer.startsWith('p') && hrs < 12) hrs += 12;
    if (mer.startsWith('a') && hrs === 12) hrs = 0;
    return { hours: hrs, minutes: mins };
  }
  return null;
}

export function getUpcomingRideCountdown(rides = currentLoadedRides, refDate = new Date()) {
  const zeroResult = { days: '00', hours: '00', minutes: '00', seconds: '00', hasUpcoming: false, targetRide: null };
  if (!rides || !Array.isArray(rides) || rides.length === 0) return zeroResult;

  const now = refDate instanceof Date ? refDate : new Date(refDate);
  const nowTime = now.getTime();

  // Find all active upcoming rides sorted by earliest scheduled departure
  const upcomingRides = rides
    .filter(r => r && !r.isDeleted && (r.dateIso || r.dateStr))
    .map(r => {
      const dateStr = r.dateIso || String(r.dateStr).match(/\d{4}-\d{2}-\d{2}/)?.[0];
      if (!dateStr) return null;
      const parsed = parseRideTime(r);
      const hrs = parsed ? parsed.hours : 9;
      const mins = parsed ? parsed.minutes : 0;
      const [year, month, day] = dateStr.split('-').map(Number);
      const rideDate = new Date(year, month - 1, day, hrs, mins, 0);
      return { ride: r, time: rideDate.getTime() };
    })
    .filter(item => item !== null && item.time > nowTime)
    .sort((a, b) => a.time - b.time);

  if (upcomingRides.length === 0) return zeroResult;

  const diffMs = upcomingRides[0].time - nowTime;
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

  return {
    days: String(days).padStart(2, '0'),
    hours: String(hours).padStart(2, '0'),
    minutes: String(minutes).padStart(2, '0'),
    seconds: String(seconds).padStart(2, '0'),
    hasUpcoming: true,
    targetRide: upcomingRides[0].ride
  };
}

/**
 * Computes exact numeric timestamp for a ride combining date and parsed meet time.
 */
export function getRideTimestamp(r) {
  if (!r) return 0;
  const dateStr = r.dateIso || (r.dateStr && String(r.dateStr).match(/\d{4}-\d{2}-\d{2}/)?.[0]);
  if (!dateStr) return 0;
  const parsed = parseRideTime(r);
  const hrs = parsed ? parsed.hours : 9;
  const mins = parsed ? parsed.minutes : 0;
  const parts = dateStr.split('-').map(Number);
  if (parts.length < 3 || isNaN(parts[0]) || isNaN(parts[1]) || isNaN(parts[2])) return 0;
  return new Date(parts[0], parts[1] - 1, parts[2], hrs, mins, 0).getTime();
}

/**
 * Builds smart chronological grid items stream:
 * - Upcoming rides (d >= todayIso) sorted ASCENDING by date/time (nearest first)
 * - Historical Divider Card (Penny Farthing clockwork boundary)
 * - Completed rides (d < todayIso) grouped by season descending and sorted DESCENDING (most recent first)
 */
export function buildSmartChronologicalGridItems(rides, options = {}) {
  const isSearchActive = Boolean(options.isSearchActive);
  const refDate = options.refDate || new Date();
  const currentYear = options.currentYear || '2026';
  const todayIso = getTodayLocalIso(refDate);

  const upcomingRides = [];
  const pastRides = [];

  (rides || []).forEach(r => {
    if (!r) return;
    const d = r.dateIso || (r.dateStr && String(r.dateStr).match(/\d{4}-\d{2}-\d{2}/)?.[0]);
    if (d && d >= todayIso) {
      upcomingRides.push(r);
    } else {
      pastRides.push(r);
    }
  });

  // Sort upcoming rides ASCENDING (nearest upcoming first)
  upcomingRides.sort((a, b) => getRideTimestamp(a) - getRideTimestamp(b));

  const allGridItems = [];

  // 1. Future rides before the divider (ascending)
  upcomingRides.forEach(ride => {
    allGridItems.push({
      isMilestone: false,
      ride
    });
  });

  // 2. Past rides grouped by season descending
  const seasonGroups = groupRidesBySeason(pastRides);
  seasonGroups.forEach(group => {
    if (group.year !== currentYear) {
      allGridItems.push({
        isMilestone: true,
        year: group.year,
        stats: group.stats
      });
    }

    const sortedSeasonRides = [...group.rides].sort((a, b) => getRideTimestamp(b) - getRideTimestamp(a));
    sortedSeasonRides.forEach(ride => {
      allGridItems.push({
        isMilestone: false,
        ride
      });
    });
  });

  // 3. Insert Historical Divider Card immediately before first past ride
  insertHistoricalDividerCardItem(allGridItems, refDate, { isSearchActive });

  return allGridItems;
}

let _histClockSyncRaf = null;
let _histCountdownInterval = null;

export function triggerFlipModule(moduleEl, newVal, oldVal) {
  if (!moduleEl || newVal === oldVal) return;

  const topText = moduleEl.querySelector('.hist-text-top');
  const bottomText = moduleEl.querySelector('.hist-text-bottom');
  const upperFlapText = moduleEl.querySelector('.hist-text-flap-upper');
  const lowerFlapText = moduleEl.querySelector('.hist-text-flap-lower');

  if (!topText || !bottomText || !upperFlapText || !lowerFlapText) return;

  // 1. Prepare cards for 2-phase flip:
  // - Top static shows NEW value (revealed behind falling flap)
  topText.textContent = newVal;
  // - Bottom static shows OLD value (until lower flap lands on it)
  bottomText.textContent = oldVal;
  // - Falling upper flap starts with OLD value
  upperFlapText.textContent = oldVal;
  // - Landing lower flap unfolds with NEW value
  lowerFlapText.textContent = newVal;

  // 2. Restart CSS animation via class toggle & reflow
  moduleEl.classList.remove('flip-active');
  void moduleEl.offsetWidth;
  moduleEl.classList.add('flip-active');

  // 3. Once 350ms animation finishes, set bottom static & upper flap to newVal and clean up
  if (moduleEl._flipTimer) clearTimeout(moduleEl._flipTimer);
  moduleEl._flipTimer = setTimeout(() => {
    bottomText.textContent = newVal;
    upperFlapText.textContent = newVal;
    moduleEl.classList.remove('flip-active');
  }, 360);
}

export function updateHistoricalClockCountdown(root = document) {
  if (typeof window === 'undefined') return;
  const activeCards = (root || document).querySelectorAll('.historical-divider-card');
  if (activeCards.length === 0) return;

  const isSearchActive = Boolean(typeof PAGINATION_STATE !== 'undefined' && PAGINATION_STATE.searchQuery && PAGINATION_STATE.searchQuery.trim().length > 0);
  const targetRides = (isSearchActive && typeof currentLoadedRides !== 'undefined')
    ? currentLoadedRides.filter(r => matchesRideSearch(r, PAGINATION_STATE.searchQuery))
    : (typeof currentLoadedRides !== 'undefined' ? currentLoadedRides : []);
  const countdown = getUpcomingRideCountdown(targetRides);
  activeCards.forEach(card => {
    const units = [
      { id: '#hist-flip-days', modId: '#hist-flip-days-module', val: countdown.days },
      { id: '#hist-flip-hours', modId: '#hist-flip-hours-module', val: countdown.hours },
      { id: '#hist-flip-mins', modId: '#hist-flip-mins-module', val: countdown.minutes },
      { id: '#hist-flip-secs', modId: '#hist-flip-secs-module', val: countdown.seconds }
    ];

    units.forEach(({ id, modId, val }) => {
      const el = card.querySelector(id);
      const modEl = card.querySelector(modId);
      const prevVal = el ? el.textContent : null;

      if (el && prevVal !== val) {
        el.textContent = val;
      }

      if (modEl && prevVal !== null && prevVal !== val) {
        triggerFlipModule(modEl, val, prevVal);
      }
    });
  });
}

/**
 * Evaluates a point on the Penny Farthing arched backbone top surface:
 * Cubic Bezier: M 118.2 86.5 C 138 84, 218 108, 261 192 (top surface -3.2px, hip seated/standing offset)
 */
function evalBackboneHipPoint(tau, hipAboveSurface = 2.0) {
  const u = Math.max(0, Math.min(1, tau));
  const inv = 1 - u;
  const b0 = inv * inv * inv;
  const b1 = 3 * inv * inv * u;
  const b2 = 3 * inv * u * u;
  const b3 = u * u * u;
  const x = b0 * 118.2 + b1 * 138.0 + b2 * 218.0 + b3 * 261.0;
  const yTube = b0 * 86.5 + b1 * 84.0 + b2 * 108.0 + b3 * 192.0;
  return { x, y: yTube - 3.2 - hipAboveSurface };
}

// =========================================================================
// 18-ACTIVITY MARKOV PLAYGROUND & MULTI-HUB ENVIRONMENTAL KINEMATICS ENGINE
// =========================================================================

export const PLAYGROUND_ACTIVITIES = {
  'wheel-climb': { id: 'wheel-climb', name: 'Wheel Climb', startHub: 'rim', endHub: 'saddle', prop: 'none' },
  'saddle-air-bike': { id: 'saddle-air-bike', name: 'Saddle Air-Bike', startHub: 'saddle', endHub: 'saddle', prop: 'none' },
  'handlebar-dance': { id: 'handlebar-dance', name: 'Handlebar Dance', startHub: 'saddle', endHub: 'saddle', prop: 'none' },
  'backbone-slide': { id: 'backbone-slide', name: 'Backbone Slide', startHub: 'saddle', endHub: 'rim', prop: 'none' },
  'clock-monkey-bars-min': { id: 'clock-monkey-bars-min', name: 'Minute Hand Monkey Bars', startHub: 'rim', endHub: 'clock', prop: 'none' },
  'clock-monkey-bars-hour-sec': { id: 'clock-monkey-bars-hour-sec', name: 'Hour/Second Hand Monkey Bars', startHub: 'clock', endHub: 'clock', prop: 'none' },
  'rim-dismount': { id: 'rim-dismount', name: 'Rim Dismount', startHub: 'clock', endHub: 'rim', prop: 'none' },
  'martial-arts-kata': { id: 'martial-arts-kata', name: 'Martial Arts Kata', startHub: 'flip', endHub: 'flip', prop: 'chi' },
  'acrobatic-somersault-flips': { id: 'acrobatic-somersault-flips', name: 'Somersault Flips', startHub: 'handlebar', endHub: 'flip', prop: 'none' },
  'spiderman-clock-climb': { id: 'spiderman-clock-climb', name: 'Spider-Man Clock Climb', startHub: 'flip', endHub: 'rim', prop: 'webline' },
  'floss-dance': { id: 'floss-dance', name: 'Floss Dance', startHub: 'saddle', endHub: 'saddle', prop: 'none' },
  'jumping-jacks': { id: 'jumping-jacks', name: 'Jumping Jacks', startHub: 'flip', endHub: 'flip', prop: 'none' },
  'yoga-pilates-flow': { id: 'yoga-pilates-flow', name: 'Yoga & Pilates Flow', startHub: 'saddle', endHub: 'saddle', prop: 'none' },
  'weightlifting-barbell': { id: 'weightlifting-barbell', name: 'Weightlifting Barbell', startHub: 'flip', endHub: 'flip', prop: 'barbell' },
  'donut-coffee-break': { id: 'donut-coffee-break', name: 'Donut & Coffee Break', startHub: 'saddle', endHub: 'saddle', prop: 'donut-coffee' },
  'flip-card-hurdle-parkour': { id: 'flip-card-hurdle-parkour', name: 'Flip-Card Parkour', startHub: 'flip', endHub: 'rear', prop: 'none' },
  'rear-wheel-stunt-roll': { id: 'rear-wheel-stunt-roll', name: 'Rear Wheel Stunt Roll', startHub: 'rear', endHub: 'saddle', prop: 'none' },
  'numeral-bouldering-brass-pole': { id: 'numeral-bouldering-brass-pole', name: 'Numeral Bouldering & Pole', startHub: 'clock', endHub: 'flip', prop: 'none' }
};

const HUB_ADJACENCY = {
  rim: ['rim', 'saddle', 'clock'],
  saddle: ['saddle', 'handlebar'],
  handlebar: ['handlebar', 'saddle'],
  clock: ['clock', 'rim', 'flip'],
  rear: ['rear', 'saddle'],
  flip: ['flip', 'rear', 'clock']
};

function mulberry32(a) {
  return function() {
    let t = a += 0x6D2B79F5;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const _markovCache = new Map();

export function getMarkovPlaygroundSlot(slotIdx, seed = 1978) {
  const normSlot = Math.max(0, Math.floor(Number(slotIdx) || 0));
  const seedKey = Number(seed) || 1978;
  let cache = _markovCache.get(seedKey);
  if (!cache) {
    cache = [];
    _markovCache.set(seedKey, cache);
  }
  if (normSlot < cache.length) {
    return cache[normSlot];
  }

  const targetCount = Math.max(normSlot + 32, 128);
  cache.length = 0;
  const rng = mulberry32(seedKey >>> 0);
  const actKeys = Object.keys(PLAYGROUND_ACTIVITIES);
  let currentHub = 'rim';

  for (let i = 0; i < targetCount; i++) {
    const taboo = cache.slice(Math.max(0, i - 4), i).map(s => s.act);
    const candidates = actKeys.filter(k => !taboo.includes(k));
    const exactCandidates = candidates.filter(k => PLAYGROUND_ACTIVITIES[k].startHub === currentHub);
    const adjHubs = HUB_ADJACENCY[currentHub] || [currentHub];
    const adjCandidates = candidates.filter(k => adjHubs.includes(PLAYGROUND_ACTIVITIES[k].startHub));

    let actId;
    if (exactCandidates.length > 0) {
      actId = exactCandidates[Math.floor(rng() * exactCandidates.length)];
    } else if (adjCandidates.length > 0) {
      actId = adjCandidates[Math.floor(rng() * adjCandidates.length)];
    } else {
      actId = candidates[Math.floor(rng() * candidates.length)];
    }

    const meta = PLAYGROUND_ACTIVITIES[actId];
    currentHub = meta.endHub;
    cache.push({
      slotIdx: i,
      act: actId,
      id: actId,
      name: meta.name,
      startHub: meta.startHub,
      endHub: meta.endHub,
      prop: meta.prop,
      toString() { return this.act; },
      valueOf() { return this.act; }
    });
  }

  return cache[normSlot];
}

export function evaluatePlaygroundActivityPose(actId, uNorm, clockAngles = { hourDeg: 0, minDeg: 0, secDeg: 0 }, state = {}) {
  const u = Math.max(0, Math.min(1, Number(uNorm) || 0));
  const TWO_PI = Math.PI * 2;
  const DEG2RAD = Math.PI / 180;
  const smooth = (v) => {
    const c = Math.max(0, Math.min(1, v));
    return c * c * (3 - 2 * c);
  };
  const lerp = (a, b, v) => a + (b - a) * v;

  const minRad = ((clockAngles && clockAngles.minDeg) || 0) * DEG2RAD;
  const hourRad = ((clockAngles && clockAngles.hourDeg) || 0) * DEG2RAD;
  const secRad = ((clockAngles && clockAngles.secDeg) || 0) * DEG2RAD;

  let x = 116.0, y = 86.0, scaleX = 1, rotate = 14;
  let torso = 6, head = -10, cape = 12, capeScale = 0.98, jerseyHem = 6;
  let leadLeg = -20, rearLeg = 12, leadShin = 32, rearShin = 10;
  let leadArm = -20, rearArm = 10;
  let prop = (PLAYGROUND_ACTIVITIES[actId] && PLAYGROUND_ACTIVITIES[actId].prop) || 'none';

  switch (actId) {
    case 'wheel-climb': {
      // Act 1: 0.0s - 4.5s
      const t = u * 4.499;
      if (t < 3.6) {
        const p = t / 3.6;
        const s = 0.85 * p + 0.15 * smooth(p);
        const alphaDeg = lerp(-8, 88, s);
        const alphaRad = alphaDeg * DEG2RAD;
        x = 116 - 81.5 * Math.cos(alphaRad);
        y = 169 - 81.5 * Math.sin(alphaRad) - 11.2;
        rotate = lerp(16, 22, Math.sin(p * Math.PI));
      } else {
        const p = smooth((t - 3.6) / 0.9);
        const crownX = 116 - 81.5 * Math.cos(88 * DEG2RAD);
        const crownY = 169 - 81.5 * Math.sin(88 * DEG2RAD) - 11.2;
        x = lerp(crownX, 158.0, p);
        y = lerp(crownY, 80.0, p) - 3.5 * Math.sin(p * Math.PI);
        scaleX = p >= 0.65 ? -1 : 1;
        rotate = lerp(14, 10, p);
      }
      const stride = t * 2.2 * TWO_PI;
      leadLeg = -14 + 26 * Math.sin(stride);
      rearLeg = -14 - 26 * Math.sin(stride);
      leadShin = 28 + 22 * Math.max(0, -Math.sin(stride));
      rearShin = 28 + 22 * Math.max(0, Math.sin(stride));
      leadArm = -16 + 22 * Math.cos(stride);
      rearArm = -16 - 22 * Math.cos(stride);
      torso = 8 + 2 * Math.sin(stride * 2);
      head = -12;
      cape = 20 + 6 * Math.sin(stride);
      capeScale = 1.00;
      jerseyHem = 10 + 4 * Math.sin(stride);
      break;
    }

    case 'saddle-air-bike': {
      // Act 2: 4.5s - 9.5s
      const t = 4.5 + u * 4.999;
      if (t < 7.6) {
        const pedalPhase = (t - 4.5) * 3.2 * TWO_PI;
        x = 158.0;
        y = 80.0 + 0.55 * Math.sin(pedalPhase * 2);
        scaleX = -1;
        rotate = 10 + 1.5 * Math.sin(pedalPhase);
        torso = 14 + 2 * Math.sin(pedalPhase);
        head = -6 + 3 * Math.sin(pedalPhase * 0.5);
        leadArm = -42 + 6 * Math.sin(pedalPhase);
        rearArm = -36 - 6 * Math.sin(pedalPhase);
        leadLeg = -24 + 26 * Math.sin(pedalPhase);
        leadShin = 46 + 26 * Math.cos(pedalPhase);
        rearLeg = -24 + 26 * Math.sin(pedalPhase + Math.PI);
        rearShin = 46 + 26 * Math.cos(pedalPhase + Math.PI);
        cape = 8 + 6 * Math.abs(Math.sin(pedalPhase));
        capeScale = 0.97;
        jerseyHem = 8 + 4 * Math.sin(pedalPhase);
      } else {
        const p = (t - 7.6) / 1.9;
        const kickPhase = (t - 7.6) * 2.0 * TWO_PI;
        x = 158.0;
        y = 80.0;
        scaleX = -1;
        rotate = lerp(10, 2, smooth(p * 2));
        torso = lerp(12, 0, smooth(p * 2));
        head = p < 0.68 ? lerp(-6, 26, smooth(p / 0.35)) : lerp(26, -24, smooth((p - 0.68) / 0.32));
        leadArm = p < 0.68 ? 16 + 10 * Math.sin(kickPhase) : lerp(16, -65, smooth((p - 0.68) / 0.32));
        rearArm = p < 0.68 ? -12 - 8 * Math.sin(kickPhase) : lerp(-12, -55, smooth((p - 0.68) / 0.32));
        leadLeg = -8 + 18 * Math.sin(kickPhase);
        rearLeg = -8 - 18 * Math.sin(kickPhase);
        leadShin = 34 + 14 * Math.cos(kickPhase);
        rearShin = 34 - 14 * Math.cos(kickPhase);
        cape = 2;
        capeScale = 0.95;
        jerseyHem = 2;
      }
      break;
    }

    case 'handlebar-dance': {
      // Act 3: 9.5s - 15.0s
      const t = 9.5 + u * 5.499;
      if (t < 11.0) {
        const p = (t - 9.5) / 1.5;
        if (p < 0.45) {
          const s = smooth(p / 0.45);
          x = lerp(158.0, 120.0, s);
          y = lerp(80.0, 56.0, s) - 5.0 * Math.sin(s * Math.PI);
          scaleX = -1;
          rotate = 12;
        } else {
          const s = (p - 0.45) / 0.55;
          const sm = 0.8 * s + 0.2 * smooth(s);
          x = lerp(120.0, 148.0, sm);
          y = lerp(56.0, 2.0, sm) - 4.0 * Math.sin(sm * Math.PI);
          scaleX = 1;
          rotate = lerp(18, 0, sm);
        }
        const climbPhase = (t - 9.5) * 3.0 * TWO_PI;
        leadLeg = -22 + 24 * Math.sin(climbPhase);
        rearLeg = -22 - 24 * Math.sin(climbPhase);
        leadShin = 36 + 20 * Math.cos(climbPhase);
        rearShin = 36 - 20 * Math.cos(climbPhase);
        leadArm = -55 + 30 * Math.sin(climbPhase);
        rearArm = -55 - 30 * Math.sin(climbPhase);
        torso = 10;
        head = -14;
        cape = 22;
        capeScale = 1.02;
      } else if (t < 14.3) {
        const danceElapsed = t - 11.0;
        const danceProgress = danceElapsed / 3.3;
        const beat = danceElapsed * 3.8 * TWO_PI;
        x = 160.0 + 14.0 * Math.sin(danceProgress * TWO_PI);
        const barSurfaceY = 13.0 + Math.max(0, (x - 146.0) * 0.34);
        const bounce = -2.6 * Math.abs(Math.sin(beat));
        y = (barSurfaceY - 11.2) + bounce;
        scaleX = (danceProgress > 0.33 && danceProgress < 0.68) ? -1 : 1;
        rotate = 8 * Math.sin(beat * 0.5);
        torso = 8 * Math.cos(beat);
        head = -6 + 10 * Math.sin(beat);
        leadLeg = -18 + 32 * Math.sin(beat);
        rearLeg = -18 - 32 * Math.sin(beat);
        leadShin = 16 + 34 * (1 - Math.abs(Math.sin(beat)));
        rearShin = 16 + 34 * Math.abs(Math.sin(beat));
        leadArm = -105 + 38 * Math.sin(beat);
        rearArm = -105 - 38 * Math.sin(beat);
        cape = 24 + 14 * Math.abs(Math.sin(beat));
        capeScale = 1.06;
        jerseyHem = 16 + 10 * Math.abs(Math.sin(beat));
      } else {
        const p = smooth((t - 14.3) / 0.7);
        x = lerp(150.0, 162.0, p);
        y = lerp(2.0, 82.0, p * p);
        scaleX = 1;
        rotate = lerp(0, 8, p);
        torso = -6;
        head = 8;
        leadArm = -45;
        rearArm = 20;
        leadLeg = -35;
        rearLeg = -25;
        leadShin = 35;
        rearShin = 35;
        cape = 42;
        capeScale = 1.10;
        jerseyHem = 24;
      }
      break;
    }

    case 'backbone-slide': {
      // Act 4: 15.0s - 19.5s
      const t = 15.0 + u * 4.499;
      if (t < 17.4) {
        const p = Math.max(0, Math.min(1, (t - 15.0) / 2.4));
        const tau = 0.38 + 0.62 * (0.35 * p + 0.65 * p * p);
        const pt = evalBackboneHipPoint(tau, 1.5);
        x = pt.x;
        y = pt.y;
        scaleX = 1;
        rotate = lerp(6, 14, p);
        torso = -10;
        head = 6;
        leadLeg = -52;
        rearLeg = -42;
        leadShin = 26;
        rearShin = 32;
        leadArm = -28;
        rearArm = 24;
        cape = lerp(24, 54, p);
        capeScale = lerp(1.02, 1.14, p);
        jerseyHem = lerp(16, 34, p);
      } else {
        const p = Math.max(0, Math.min(1, (t - 17.4) / 2.1));
        const tau = 1.0 - 0.96 * (0.85 * p + 0.15 * smooth(p));
        const pt = evalBackboneHipPoint(tau, 10.8);
        x = pt.x;
        y = pt.y;
        scaleX = -1;
        rotate = lerp(22, 8, p);
        const sprintPhase = (t - 17.4) * 3.2 * TWO_PI;
        leadLeg = -16 + 32 * Math.sin(sprintPhase);
        rearLeg = -16 - 32 * Math.sin(sprintPhase);
        leadShin = 30 + 24 * Math.max(0, -Math.sin(sprintPhase));
        rearShin = 30 + 24 * Math.max(0, Math.sin(sprintPhase));
        leadArm = -10 + 28 * Math.cos(sprintPhase);
        rearArm = -10 - 28 * Math.cos(sprintPhase);
        torso = 12;
        head = -10;
        cape = 26 + 6 * Math.sin(sprintPhase);
        capeScale = 1.04;
        jerseyHem = 16;
      }
      break;
    }

    case 'clock-monkey-bars-min': {
      // Act 5: 19.5s - 24.5s
      const t = 19.5 + u * 4.999;
      const crownPt = evalBackboneHipPoint(0.04, 10.8);
      if (t < 20.2) {
        const p = smooth((t - 19.5) / 0.7);
        const targetX = 116 + 46 * Math.sin(minRad);
        const targetY = 169 - 46 * Math.cos(minRad) + 12.6;
        x = lerp(crownPt.x, targetX, p);
        y = lerp(crownPt.y, targetY, p) - 6.0 * Math.sin(p * Math.PI);
        scaleX = Math.sin(minRad) >= 0 ? 1 : -1;
        rotate = lerp(0, 12, p);
        leadArm = lerp(-20, -136, p);
        rearArm = lerp(10, -126, p);
        leadLeg = -24;
        rearLeg = -12;
        leadShin = 42;
        rearShin = 38;
        torso = 4;
        head = -16;
        cape = 28;
        capeScale = 1.04;
      } else {
        const p = (t - 20.2) / 4.3;
        const swing = (t - 20.2) * 1.8 * TWO_PI;
        const r = 32 + 14 * Math.cos(p * Math.PI * 2);
        x = 116 + r * Math.sin(minRad);
        y = 169 - r * Math.cos(minRad) + 12.6;
        scaleX = Math.sin(minRad) >= 0 ? 1 : -1;
        rotate = 18 * Math.sin(swing);
        torso = 8 * Math.cos(swing);
        head = -18 + 6 * Math.sin(swing);
        leadArm = -135 + 16 * Math.sin(swing);
        rearArm = -135 - 16 * Math.sin(swing);
        leadLeg = -26 + 22 * Math.sin(swing);
        rearLeg = -26 - 22 * Math.sin(swing);
        leadShin = 48 + 14 * Math.cos(swing);
        rearShin = 48 - 14 * Math.cos(swing);
        cape = 14 + 12 * Math.abs(Math.sin(swing));
        capeScale = 1.00;
        jerseyHem = 10 + 8 * Math.abs(Math.sin(swing));
      }
      break;
    }

    case 'clock-monkey-bars-hour-sec': {
      // Act 6: 24.5s - 29.5s
      const t = 24.5 + u * 4.999;
      const minEndX = 116 + 46 * Math.sin(minRad);
      const minEndY = 169 - 46 * Math.cos(minRad) + 12.6;
      const hourGripX = 116 + 26 * Math.sin(hourRad);
      const hourGripY = 169 - 26 * Math.cos(hourRad) + 12.6;
      const secGripX = 116 + 36 * Math.sin(secRad);
      const secGripY = 169 - 36 * Math.cos(secRad) + 12.6;

      if (t < 25.2) {
        const p = smooth((t - 24.5) / 0.7);
        x = lerp(minEndX, hourGripX, p);
        y = lerp(minEndY, hourGripY, p) - 5.0 * Math.sin(p * Math.PI);
        scaleX = Math.sin(hourRad) >= 0 ? 1 : -1;
        rotate = 14 * Math.sin(p * Math.PI);
        leadArm = -135;
        rearArm = -128;
        leadLeg = -30;
        rearLeg = -20;
        leadShin = 45;
        rearShin = 45;
        head = -16;
        cape = 22;
      } else if (t < 27.6) {
        const p = (t - 25.2) / 2.4;
        const pullWave = 0.5 - 0.5 * Math.cos(p * 3 * TWO_PI);
        x = hourGripX;
        y = hourGripY - 6.2 * pullWave;
        scaleX = Math.sin(hourRad) >= 0 ? 1 : -1;
        rotate = 8 * pullWave;
        torso = 10 * pullWave;
        head = lerp(-18, -4, pullWave);
        leadArm = lerp(-135, -58, pullWave);
        rearArm = lerp(-130, -52, pullWave);
        leadLeg = lerp(-12, -48, pullWave);
        rearLeg = lerp(-8, -42, pullWave);
        leadShin = lerp(24, 62, pullWave);
        rearShin = lerp(20, 58, pullWave);
        cape = 8 + 14 * pullWave;
        capeScale = 0.98;
        jerseyHem = 6 + 10 * pullWave;
      } else {
        const p = smooth(Math.min(1, (t - 27.6) / 0.5));
        const swing = (t - 27.6) * 2.5 * TWO_PI;
        x = lerp(hourGripX, secGripX, p);
        y = lerp(hourGripY, secGripY, p);
        scaleX = Math.sin(secRad) >= 0 ? 1 : -1;
        rotate = 20 * Math.sin(swing);
        torso = 10 * Math.cos(swing);
        head = -14;
        leadArm = -134 + 14 * Math.sin(swing);
        rearArm = -134 - 14 * Math.sin(swing);
        leadLeg = -28 + 26 * Math.sin(swing);
        rearLeg = -28 - 26 * Math.sin(swing);
        leadShin = 44;
        rearShin = 44;
        cape = 22 + 10 * Math.abs(Math.sin(swing));
        capeScale = 1.04;
        jerseyHem = 14;
      }
      break;
    }

    case 'rim-dismount': {
      // Act 7: 29.5s - 32.0s
      const t = 29.5 + u * 2.499;
      const secGripX = 116 + 36 * Math.sin(secRad);
      const secGripY = 169 - 36 * Math.cos(secRad) + 12.6;
      const startAlphaRad = -8 * DEG2RAD;
      const rimStartX = 116 - 81.5 * Math.cos(startAlphaRad);
      const rimStartY = 169 - 81.5 * Math.sin(startAlphaRad) - 11.2;

      if (t < 31.2) {
        const p = smooth((t - 29.5) / 1.7);
        x = lerp(secGripX, rimStartX, p);
        y = lerp(secGripY, rimStartY, p) - 14.0 * Math.sin(p * Math.PI);
        scaleX = secGripX > rimStartX ? -1 : 1;
        rotate = lerp(0, 12, p);
        const stride = (t - 29.5) * 2.0 * TWO_PI;
        leadArm = lerp(-90, -20, p);
        rearArm = lerp(-80, 10, p);
        leadLeg = -20 + 22 * Math.sin(stride);
        rearLeg = -20 - 22 * Math.sin(stride);
        leadShin = 34;
        rearShin = 34;
        torso = 8;
        head = -8;
        cape = 32 * (1 - 0.4 * p);
        capeScale = 1.04;
      } else {
        const p = smooth((t - 31.2) / 0.8);
        x = rimStartX;
        y = rimStartY;
        scaleX = 1;
        rotate = lerp(10, 16, p);
        leadLeg = lerp(-8, -14, p);
        rearLeg = lerp(8, -14, p);
        leadShin = 28;
        rearShin = 28;
        leadArm = lerp(-10, 6, p);
        rearArm = lerp(10, -38, p);
        torso = 8;
        head = -12;
        cape = 20;
        capeScale = 1.00;
      }
      break;
    }

    // =========================================================================
    // 11 NEW ACTIVITIES
    // =========================================================================
    case 'martial-arts-kata': {
      // Kung-Fu / Karate Kata on HOURS (132, 224.8) & MINUTES (208, 224.8) flip cards
      const phase1 = (uVal) => {
        const kick = Math.sin((uVal / 0.28) * Math.PI);
        return {
          x: 132.0, y: 224.8, scaleX: 1, rotate: 0,
          torso: 4, head: -6,
          leadLeg: lerp(-25, -82, kick), leadShin: lerp(68, 6, kick),
          rearLeg: 0, rearShin: 6,
          leadArm: lerp(-45, -95, kick), rearArm: lerp(-35, 20, kick),
          cape: 16 + 10 * kick, jerseyHem: 8 + 6 * kick
        };
      };
      const phase2 = (uVal) => {
        const p = Math.max(0, Math.min(1, (uVal - 0.28) / 0.34)); // 0.28 to 0.62
        return {
          x: lerp(132.0, 208.0, p),
          y: 224.8 - 16.0 * Math.sin(p * Math.PI),
          scaleX: 1,
          rotate: -12 * Math.sin(p * Math.PI),
          torso: -8, head: -4,
          leadLeg: -78, leadShin: 8,
          rearLeg: 28, rearShin: 62,
          leadArm: -55, rearArm: 25,
          cape: 36, jerseyHem: 18
        };
      };
      const phase3 = (uVal) => {
        const p = Math.max(0, Math.min(1, (uVal - 0.62) / 0.38)); // 0.62 to 1.0
        const punchPhase = p * 6.0 * TWO_PI;
        return {
          x: 208.0 + 1.8 * Math.sin(punchPhase),
          y: 225.8,
          scaleX: 1,
          rotate: 4 * Math.sin(punchPhase),
          torso: 8 * Math.sin(punchPhase),
          head: -4,
          leadLeg: -28, leadShin: 32,
          rearLeg: 26, rearShin: 24,
          leadArm: -38 + 26 * Math.sin(punchPhase),
          rearArm: -38 - 26 * Math.sin(punchPhase),
          cape: 24 + 8 * Math.abs(Math.sin(punchPhase)),
          jerseyHem: 12 + 4 * Math.abs(Math.sin(punchPhase))
        };
      };

      let pose;
      if (u < 0.24) {
        pose = phase1(u);
      } else if (u < 0.32) {
        const blend = smooth((u - 0.24) / 0.08);
        const pA = phase1(u);
        const pB = phase2(u);
        pose = { scaleX: 1 };
        for (const k of ['x', 'y', 'rotate', 'torso', 'head', 'leadLeg', 'leadShin', 'rearLeg', 'rearShin', 'leadArm', 'rearArm', 'cape', 'jerseyHem']) {
          pose[k] = lerp(pA[k], pB[k], blend);
        }
      } else if (u < 0.58) {
        pose = phase2(u);
      } else if (u < 0.66) {
        const blend = smooth((u - 0.58) / 0.08);
        const pA = phase2(u);
        const pB = phase3(u);
        pose = { scaleX: 1 };
        for (const k of ['x', 'y', 'rotate', 'torso', 'head', 'leadLeg', 'leadShin', 'rearLeg', 'rearShin', 'leadArm', 'rearArm', 'cape', 'jerseyHem']) {
          pose[k] = lerp(pA[k], pB[k], blend);
        }
      } else {
        pose = phase3(u);
      }
      x = pose.x; y = pose.y; scaleX = pose.scaleX; rotate = pose.rotate;
      torso = pose.torso; head = pose.head; leadLeg = pose.leadLeg;
      leadShin = pose.leadShin; rearLeg = pose.rearLeg; rearShin = pose.rearShin;
      leadArm = pose.leadArm; rearArm = pose.rearArm; cape = pose.cape;
      jerseyHem = pose.jerseyHem;
      break;
    }

    case 'acrobatic-somersault-flips': {
      // Somersaults from handlebar peak (160, 2) down to MINUTES flip deck (208, 224.8) + handspring rebound
      const phase1 = (uVal) => {
        const p = uVal / 0.65;
        return {
          x: lerp(160.0, 208.0, p),
          y: lerp(2.0, 224.8, p * p) - 22.0 * Math.sin(p * Math.PI),
          scaleX: 1,
          rotate: p * 720.0,
          leadLeg: -68, rearLeg: -68,
          leadShin: 72, rearShin: 72,
          leadArm: -75, rearArm: -75,
          torso: -12, head: 14,
          cape: 36 * Math.sin(p * TWO_PI),
          jerseyHem: 18
        };
      };
      const phase2 = (uVal) => {
        const p = (uVal - 0.65) / 0.35;
        return {
          x: lerp(208.0, 132.0, p),
          y: 224.8 - 12.0 * Math.sin(p * Math.PI),
          scaleX: -1,
          rotate: 720.0 + p * 360.0,
          leadLeg: -20 + 35 * Math.sin(p * TWO_PI),
          rearLeg: 20 - 35 * Math.sin(p * TWO_PI),
          leadShin: 25, rearShin: 25,
          leadArm: -120 + 40 * Math.cos(p * TWO_PI),
          rearArm: -60 - 40 * Math.cos(p * TWO_PI),
          torso: 0, head: -10,
          cape: 24, jerseyHem: 12
        };
      };

      let pose;
      if (u < 0.60) {
        pose = phase1(u);
      } else if (u < 0.68) {
        const blend = smooth((u - 0.60) / 0.08);
        const pA = phase1(u);
        const pB = phase2(u);
        pose = { scaleX: blend < 0.5 ? 1 : -1 };
        for (const k of ['x', 'y', 'rotate', 'torso', 'head', 'leadLeg', 'leadShin', 'rearLeg', 'rearShin', 'leadArm', 'rearArm', 'cape', 'jerseyHem']) {
          pose[k] = lerp(pA[k], pB[k], blend);
        }
      } else {
        pose = phase2(u);
      }
      x = pose.x; y = pose.y; scaleX = pose.scaleX; rotate = pose.rotate;
      torso = pose.torso; head = pose.head; leadLeg = pose.leadLeg;
      leadShin = pose.leadShin; rearLeg = pose.rearLeg; rearShin = pose.rearShin;
      leadArm = pose.leadArm; rearArm = pose.rearArm; cape = pose.cape;
      jerseyHem = pose.jerseyHem;
      break;
    }

    case 'spiderman-clock-climb': {
      // Spider-Man wall-crawl, inverted 180deg hang from numeral 12 on webline, flip to rim
      const phase1 = (uVal) => {
        const p = uVal / 0.45;
        const crawlPhase = p * 4.0 * TWO_PI;
        const alpha = lerp(115.0, 0.0, p) * DEG2RAD;
        return {
          x: 116.0 - 53.0 * Math.sin(alpha),
          y: 169.0 - 53.0 * Math.cos(alpha),
          scaleX: 1,
          rotate: -38 + 12 * Math.sin(crawlPhase),
          torso: -10, head: 12,
          leadLeg: -54 + 28 * Math.sin(crawlPhase),
          rearLeg: 42 - 28 * Math.sin(crawlPhase),
          leadShin: 52 + 20 * Math.cos(crawlPhase),
          rearShin: 52 - 20 * Math.cos(crawlPhase),
          leadArm: -110 + 35 * Math.cos(crawlPhase),
          rearArm: -40 - 35 * Math.cos(crawlPhase),
          cape: 20, jerseyHem: 12
        };
      };
      const phase2 = (uVal) => {
        const p = (uVal - 0.45) / 0.30;
        const swing = p * 3.0 * TWO_PI;
        return {
          x: 116.0 + 8.0 * Math.sin(swing),
          y: 120.0 + 3.0 * Math.cos(swing),
          scaleX: 1,
          rotate: 180 + 12 * Math.sin(swing),
          torso: 0, head: -10,
          leadLeg: -24 + 10 * Math.sin(swing),
          rearLeg: 20 - 10 * Math.sin(swing),
          leadShin: 45, rearShin: 45,
          leadArm: -145 + 10 * Math.cos(swing),
          rearArm: -145 - 10 * Math.cos(swing),
          cape: -15, jerseyHem: 8
        };
      };
      const phase3 = (uVal) => {
        const p = (uVal - 0.75) / 0.25;
        return {
          x: 116.0,
          y: lerp(123.0, 87.5, p) - 6.0 * Math.sin(p * Math.PI),
          scaleX: 1,
          rotate: lerp(180, 16, p),
          torso: lerp(0, 8, p), head: -10,
          leadLeg: lerp(-24, -14, p), rearLeg: lerp(20, -14, p),
          leadShin: 28, rearShin: 28,
          leadArm: lerp(-145, -20, p), rearArm: lerp(-145, 10, p),
          cape: 20, jerseyHem: 10
        };
      };

      let pose;
      if (u < 0.40) {
        pose = phase1(u);
      } else if (u < 0.48) {
        const blend = smooth((u - 0.40) / 0.08);
        const pA = phase1(u);
        const pB = phase2(u);
        pose = { scaleX: 1 };
        for (const k of ['x', 'y', 'rotate', 'torso', 'head', 'leadLeg', 'leadShin', 'rearLeg', 'rearShin', 'leadArm', 'rearArm', 'cape', 'jerseyHem']) {
          pose[k] = lerp(pA[k], pB[k], blend);
        }
      } else if (u < 0.72) {
        pose = phase2(u);
      } else if (u < 0.78) {
        const blend = smooth((u - 0.72) / 0.06);
        const pA = phase2(u);
        const pB = phase3(u);
        pose = { scaleX: 1 };
        for (const k of ['x', 'y', 'rotate', 'torso', 'head', 'leadLeg', 'leadShin', 'rearLeg', 'rearShin', 'leadArm', 'rearArm', 'cape', 'jerseyHem']) {
          pose[k] = lerp(pA[k], pB[k], blend);
        }
      } else {
        pose = phase3(u);
      }
      x = pose.x; y = pose.y; scaleX = pose.scaleX; rotate = pose.rotate;
      torso = pose.torso; head = pose.head; leadLeg = pose.leadLeg;
      leadShin = pose.leadShin; rearLeg = pose.rearLeg; rearShin = pose.rearShin;
      leadArm = pose.leadArm; rearArm = pose.rearArm; cape = pose.cape;
      jerseyHem = pose.jerseyHem;
      break;
    }

    case 'floss-dance': {
      // 4.5 Hz counter-phase hip & arm floss dance standing on saddle (158, 68.8)
      const beat = u * 5.0 * 4.5 * TWO_PI;
      x = 158.0 + 1.2 * Math.sin(beat);
      y = 68.8 + 1.2 * Math.abs(Math.sin(beat));
      scaleX = 1;
      rotate = 6 * Math.sin(beat);
      torso = 10 * Math.sin(beat);
      head = -6 - 4 * Math.cos(beat * 2);
      leadArm = -10 + 42 * Math.cos(beat);
      rearArm = -10 - 42 * Math.cos(beat);
      leadLeg = -8 + 6 * Math.sin(beat);
      rearLeg = 6 - 6 * Math.sin(beat);
      leadShin = 12;
      rearShin = 12;
      cape = 16 + 10 * Math.abs(Math.sin(beat));
      jerseyHem = 8 + 4 * Math.sin(beat);
      break;
    }

    case 'jumping-jacks': {
      // Calisthenic star-jumps on HOURS flip card roof (132, 224.8)
      const jackPhase = u * 5.0 * 2.2 * TWO_PI;
      const wave = 0.5 - 0.5 * Math.cos(jackPhase);
      x = 132.0;
      y = 224.8 - 5.8 * wave;
      scaleX = 1;
      rotate = 0;
      torso = 0;
      head = -6 + 4 * wave;
      leadArm = lerp(-15, -158, wave);
      rearArm = lerp(-15, -158, wave);
      leadLeg = lerp(-6, -44, wave);
      rearLeg = lerp(6, 44, wave);
      leadShin = lerp(12, 18, wave);
      rearShin = lerp(12, 18, wave);
      cape = 10 + 20 * wave;
      jerseyHem = 6 + 10 * wave;
      break;
    }

    case 'yoga-pilates-flow': {
      // Tree Pose -> Warrior III -> Pilates Teaser on saddle (158, 80)
      const phase1 = (uVal) => ({
        x: 158.0, y: 68.8, scaleX: 1, rotate: 0,
        torso: 2, head: -6,
        leadLeg: 0, leadShin: 4, rearLeg: -55, rearShin: 85,
        leadArm: -142, rearArm: -138, cape: 8, jerseyHem: 4
      });
      const phase2 = (uVal) => ({
        x: 158.0, y: 69.5, scaleX: 1, rotate: 56,
        torso: 10, head: -18,
        leadLeg: -56, leadShin: 6, rearLeg: 30, rearShin: 6,
        leadArm: -145, rearArm: -140, cape: 14, jerseyHem: 8
      });
      const phase3 = (uVal) => {
        const teaserWave = Math.sin(uVal * 6 * TWO_PI);
        return {
          x: 158.0, y: 78.2, scaleX: 1, rotate: -18,
          torso: -10, head: 8,
          leadLeg: -62 + 4 * teaserWave, leadShin: 8,
          rearLeg: -54 - 4 * teaserWave, rearShin: 12,
          leadArm: -58, rearArm: -52, cape: 6, jerseyHem: 4
        };
      };

      let pose;
      if (u < 0.28) {
        pose = phase1(u);
      } else if (u < 0.40) {
        const blend = smooth((u - 0.28) / 0.12);
        const pA = phase1(u);
        const pB = phase2(u);
        pose = { scaleX: 1 };
        for (const k of ['x', 'y', 'rotate', 'torso', 'head', 'leadLeg', 'leadShin', 'rearLeg', 'rearShin', 'leadArm', 'rearArm', 'cape', 'jerseyHem']) {
          pose[k] = lerp(pA[k], pB[k], blend);
        }
      } else if (u < 0.62) {
        pose = phase2(u);
      } else if (u < 0.74) {
        const blend = smooth((u - 0.62) / 0.12);
        const pA = phase2(u);
        const pB = phase3(u);
        pose = { scaleX: 1 };
        for (const k of ['x', 'y', 'rotate', 'torso', 'head', 'leadLeg', 'leadShin', 'rearLeg', 'rearShin', 'leadArm', 'rearArm', 'cape', 'jerseyHem']) {
          pose[k] = lerp(pA[k], pB[k], blend);
        }
      } else {
        pose = phase3(u);
      }
      x = pose.x; y = pose.y; scaleX = pose.scaleX; rotate = pose.rotate;
      torso = pose.torso; head = pose.head; leadLeg = pose.leadLeg;
      leadShin = pose.leadShin; rearLeg = pose.rearLeg; rearShin = pose.rearShin;
      leadArm = pose.leadArm; rearArm = pose.rearArm; cape = pose.cape;
      jerseyHem = pose.jerseyHem;
      break;
    }

    case 'weightlifting-barbell': {
      // Clean, overhead press with 18Hz tremor, proud bow on MINUTES card (208, 224.8)
      const phase1 = (uVal) => {
        const p = uVal / 0.35;
        const squat = Math.sin(p * Math.PI);
        return {
          x: 208.0, y: 224.8 + 4.2 * squat, scaleX: 1, rotate: 0,
          torso: lerp(10, 2, smooth(p)), head: -6,
          leadLeg: -20 - 32 * squat, rearLeg: 15 + 32 * squat,
          leadShin: 30 + 40 * squat, rearShin: 30 + 40 * squat,
          leadArm: lerp(-25, -65, smooth(p)), rearArm: lerp(-25, -65, smooth(p)),
          cape: 12, jerseyHem: 6
        };
      };
      const phase2 = (uVal) => {
        const p = (uVal - 0.35) / 0.43;
        const tremor = Math.sin(p * 5.0 * 18.0 * TWO_PI) * 0.45 * Math.sin(p * Math.PI);
        return {
          x: 208.0 + tremor, y: 224.8, scaleX: 1, rotate: 0,
          torso: 2 + tremor * 2, head: -14,
          leadLeg: -14, rearLeg: 12,
          leadShin: 16, rearShin: 16,
          leadArm: lerp(-65, -152, smooth(p)) + tremor * 4,
          rearArm: lerp(-65, -148, smooth(p)) - tremor * 4,
          cape: 12, jerseyHem: 6
        };
      };
      const phase3 = (uVal) => {
        const p = (uVal - 0.78) / 0.22;
        const bow = Math.sin(p * Math.PI);
        const sp = smooth(p);
        return {
          x: 208.0, y: 224.8, scaleX: 1, rotate: lerp(0, 12, bow),
          torso: lerp(2, 10, bow), head: lerp(-14, 12, bow),
          leadLeg: -12, rearLeg: 12,
          leadShin: 16, rearShin: 16,
          leadArm: lerp(-152, -20, sp), rearArm: lerp(-148, 15, sp),
          cape: 8, jerseyHem: 4
        };
      };

      let pose;
      if (u < 0.35) {
        pose = phase1(u);
      } else if (u < 0.78) {
        pose = phase2(u);
      } else {
        pose = phase3(u);
      }
      x = pose.x; y = pose.y; scaleX = pose.scaleX; rotate = pose.rotate;
      torso = pose.torso; head = pose.head; leadLeg = pose.leadLeg;
      leadShin = pose.leadShin; rearLeg = pose.rearLeg; rearShin = pose.rearShin;
      leadArm = pose.leadArm; rearArm = pose.rearArm; cape = pose.cape;
      jerseyHem = pose.jerseyHem;
      break;
    }

    case 'donut-coffee-break': {
      // Seated refuel on saddle (158, 78.2) dangling legs, alternating donut bite and coffee sip
      const legSwing = Math.sin(u * 5.0 * 1.5 * TWO_PI);
      const cycle = Math.sin(u * 5.0 * 0.8 * TWO_PI);
      x = 158.0;
      y = 78.2;
      scaleX = 1;
      rotate = 4;
      torso = 4;
      leadLeg = 12 + 10 * legSwing;
      rearLeg = 12 - 10 * legSwing;
      leadShin = 44 + 8 * legSwing;
      rearShin = 44 - 8 * legSwing;
      if (cycle > 0) {
        leadArm = lerp(-25, -68, cycle);
        rearArm = 18;
        head = lerp(-4, 12, cycle);
      } else {
        const bite = -cycle;
        rearArm = lerp(18, -65, bite);
        leadArm = -25;
        head = lerp(-4, -12, bite);
      }
      cape = 4;
      jerseyHem = 2;
      break;
    }

    case 'flip-card-hurdle-parkour': {
      // Hurdle sprint across DAYS (56) -> HOURS (132) -> MINUTES (208) -> SECONDS (284), leap to rear wheel (265, 180.5)
      const phase1 = (uVal) => {
        const p = uVal / 0.72;
        const hurdleHop = -12.0 * Math.pow(Math.abs(Math.sin(p * 3.0 * Math.PI)), 1.5);
        const sprintPhase = p * 6.0 * TWO_PI;
        return {
          x: lerp(56.0, 284.0, p),
          y: 224.8 + hurdleHop,
          scaleX: 1,
          rotate: 8 * Math.sin(sprintPhase * 0.5),
          torso: 10,
          head: -8,
          leadLeg: -24 + 38 * Math.sin(sprintPhase),
          rearLeg: -24 - 38 * Math.sin(sprintPhase),
          leadShin: 28 + 26 * Math.max(0, -Math.sin(sprintPhase)),
          rearShin: 28 + 26 * Math.max(0, Math.sin(sprintPhase)),
          leadArm: -15 + 35 * Math.cos(sprintPhase),
          rearArm: -15 - 35 * Math.cos(sprintPhase),
          cape: 32, jerseyHem: 18
        };
      };
      const phase2 = (uVal) => {
        return {
          x: 284.0,
          y: 224.8,
          scaleX: -1,
          rotate: 0,
          torso: 10,
          head: -6,
          leadLeg: -8,
          rearLeg: 10,
          leadShin: 20,
          rearShin: 20,
          leadArm: 18,
          rearArm: -24,
          cape: 8, jerseyHem: 4
        };
      };
      const phase3 = (uVal) => {
        const p = (uVal - 0.86) / 0.14;
        const sp = smooth(p);
        return {
          x: lerp(284.0, 265.0, sp),
          y: lerp(224.8, 180.5, sp) - 10.0 * Math.sin(sp * Math.PI),
          scaleX: -1,
          rotate: lerp(0, 12, sp),
          torso: 10,
          head: -8,
          leadLeg: -24,
          rearLeg: -15,
          leadShin: 30,
          rearShin: 30,
          leadArm: -45,
          rearArm: 15,
          cape: 22, jerseyHem: 12
        };
      };

      let pose;
      if (u < 0.70) {
        pose = phase1(u);
      } else if (u < 0.74) {
        const blend = smooth((u - 0.70) / 0.04);
        const pA = phase1(u);
        const pB = phase2(u);
        pose = { scaleX: blend < 0.5 ? 1 : -1 };
        for (const k of ['x', 'y', 'rotate', 'torso', 'head', 'leadLeg', 'leadShin', 'rearLeg', 'rearShin', 'leadArm', 'rearArm', 'cape', 'jerseyHem']) {
          pose[k] = lerp(pA[k], pB[k], blend);
        }
      } else if (u < 0.86) {
        pose = phase2(u);
      } else if (u < 0.90) {
        const blend = smooth((u - 0.86) / 0.04);
        const pA = phase2(u);
        const pB = phase3(u);
        pose = { scaleX: -1 };
        for (const k of ['x', 'y', 'rotate', 'torso', 'head', 'leadLeg', 'leadShin', 'rearLeg', 'rearShin', 'leadArm', 'rearArm', 'cape', 'jerseyHem']) {
          pose[k] = lerp(pA[k], pB[k], blend);
        }
      } else {
        pose = phase3(u);
      }
      x = pose.x; y = pose.y; scaleX = pose.scaleX; rotate = pose.rotate;
      torso = pose.torso; head = pose.head; leadLeg = pose.leadLeg;
      leadShin = pose.leadShin; rearLeg = pose.rearLeg; rearShin = pose.rearShin;
      leadArm = pose.leadArm; rearArm = pose.rearArm; cape = pose.cape;
      jerseyHem = pose.jerseyHem;
      break;
    }

    case 'rear-wheel-stunt-roll': {
      // Circus log-roll on rear wheel tire crown (265, 180.5), handstand, sprint up backbone
      const phase1 = (uVal) => {
        const p = uVal / 0.40;
        const rollPhase = p * 6.0 * TWO_PI;
        return {
          x: 265.0 + 1.2 * Math.sin(rollPhase * 0.5),
          y: 180.5 + 1.0 * Math.abs(Math.sin(rollPhase)),
          scaleX: -1,
          rotate: 6 * Math.sin(rollPhase * 0.5),
          torso: 6,
          head: -10,
          leadLeg: -15 + 26 * Math.sin(rollPhase),
          rearLeg: -15 - 26 * Math.sin(rollPhase),
          leadShin: 24 + 16 * Math.abs(Math.sin(rollPhase)),
          rearShin: 24 + 16 * Math.abs(Math.cos(rollPhase)),
          leadArm: -45 + 15 * Math.sin(rollPhase),
          rearArm: -45 - 15 * Math.sin(rollPhase),
          cape: 18, jerseyHem: 10
        };
      };
      const phase2 = (uVal) => {
        const p = (uVal - 0.40) / 0.32;
        const balanceTremor = Math.sin(p * 4.0 * TWO_PI) * 2.0;
        return {
          x: 265.0,
          y: 182.0,
          scaleX: -1,
          rotate: 176 + balanceTremor,
          torso: 2,
          head: 14,
          leadLeg: -15 + balanceTremor * 2,
          rearLeg: 20 - balanceTremor * 2,
          leadShin: 18, rearShin: 22,
          leadArm: -158, rearArm: -60,
          cape: 12, jerseyHem: 6
        };
      };
      const phase3 = (uVal) => {
        const p = (uVal - 0.72) / 0.28;
        const tau = 1.0 - 0.62 * (0.85 * p + 0.15 * smooth(p));
        const pt = evalBackboneHipPoint(tau, 8.0);
        const sprintPhase = p * 4.0 * TWO_PI;
        return {
          x: pt.x,
          y: pt.y,
          scaleX: -1,
          rotate: 12,
          torso: 8,
          head: -8,
          leadLeg: -16 + 26 * Math.sin(sprintPhase),
          rearLeg: -16 - 26 * Math.sin(sprintPhase),
          leadShin: 28, rearShin: 28,
          leadArm: -12 + 22 * Math.cos(sprintPhase),
          rearArm: -12 - 22 * Math.cos(sprintPhase),
          cape: 22, jerseyHem: 12
        };
      };

      let pose;
      if (u < 0.36) {
        pose = phase1(u);
      } else if (u < 0.44) {
        const blend = smooth((u - 0.36) / 0.08);
        const pA = phase1(u);
        const pB = phase2(u);
        pose = { scaleX: -1 };
        for (const k of ['x', 'y', 'rotate', 'torso', 'head', 'leadLeg', 'leadShin', 'rearLeg', 'rearShin', 'leadArm', 'rearArm', 'cape', 'jerseyHem']) {
          pose[k] = lerp(pA[k], pB[k], blend);
        }
      } else if (u < 0.68) {
        pose = phase2(u);
      } else if (u < 0.76) {
        const blend = smooth((u - 0.68) / 0.08);
        const pA = phase2(u);
        const pB = phase3(u);
        pose = { scaleX: -1 };
        for (const k of ['x', 'y', 'rotate', 'torso', 'head', 'leadLeg', 'leadShin', 'rearLeg', 'rearShin', 'leadArm', 'rearArm', 'cape', 'jerseyHem']) {
          pose[k] = lerp(pA[k], pB[k], blend);
        }
      } else {
        pose = phase3(u);
      }
      x = pose.x; y = pose.y; scaleX = pose.scaleX; rotate = pose.rotate;
      torso = pose.torso; head = pose.head; leadLeg = pose.leadLeg;
      leadShin = pose.leadShin; rearLeg = pose.rearLeg; rearShin = pose.rearShin;
      leadArm = pose.leadArm; rearArm = pose.rearArm; cape = pose.cape;
      jerseyHem = pose.jerseyHem;
      break;
    }

    case 'numeral-bouldering-brass-pole': {
      // Clock dial rock climbing (63, 169 -> 116, 169) -> brass stanchion pole slide (114, 175..225)
      const phase1 = (uVal) => {
        const p = uVal / 0.55;
        let px, py;
        if (p < 0.33) {
          const q = p / 0.33;
          px = lerp(63.0, 70.0, q);
          py = lerp(169.0, 140.0, q);
        } else if (p < 0.66) {
          const q = (p - 0.33) / 0.33;
          px = lerp(70.0, 116.0, q);
          py = lerp(140.0, 120.0, q);
        } else {
          const q = (p - 0.66) / 0.34;
          px = lerp(116.0, 116.0, q);
          py = lerp(120.0, 169.0, q);
        }
        const climb = p * 5.0 * TWO_PI;
        return {
          x: px, y: py, scaleX: 1,
          rotate: 12 * Math.sin(climb * 0.5),
          torso: 6, head: -12,
          leadLeg: -35 + 25 * Math.sin(climb),
          rearLeg: 20 - 25 * Math.sin(climb),
          leadShin: 45, rearShin: 45,
          leadArm: -110 + 30 * Math.cos(climb),
          rearArm: -60 - 30 * Math.cos(climb),
          cape: 18, jerseyHem: 10
        };
      };
      const phase2 = (uVal) => {
        const p = (uVal - 0.55) / 0.45;
        return {
          x: 114.0,
          y: lerp(175.0, 225.0, p * p),
          scaleX: 1,
          rotate: 4,
          torso: -4, head: 10,
          leadLeg: 18, rearLeg: -12,
          leadShin: 55, rearShin: 55,
          leadArm: -130, rearArm: -115,
          cape: lerp(14, 42, p),
          jerseyHem: lerp(8, 26, p)
        };
      };

      let pose;
      if (u < 0.50) {
        pose = phase1(u);
      } else if (u < 0.58) {
        const blend = smooth((u - 0.50) / 0.08);
        const pA = phase1(u);
        const pB = phase2(u);
        pose = { scaleX: 1 };
        for (const k of ['x', 'y', 'rotate', 'torso', 'head', 'leadLeg', 'leadShin', 'rearLeg', 'rearShin', 'leadArm', 'rearArm', 'cape', 'jerseyHem']) {
          pose[k] = lerp(pA[k], pB[k], blend);
        }
      } else {
        pose = phase2(u);
      }
      x = pose.x; y = pose.y; scaleX = pose.scaleX; rotate = pose.rotate;
      torso = pose.torso; head = pose.head; leadLeg = pose.leadLeg;
      leadShin = pose.leadShin; rearLeg = pose.rearLeg; rearShin = pose.rearShin;
      leadArm = pose.leadArm; rearArm = pose.rearArm; cape = pose.cape;
      jerseyHem = pose.jerseyHem;
      break;
    }
  }

  state.act = actId;
  state.prop = prop;
  state.x = x;
  state.y = y;
  state.scaleX = scaleX;
  state.rotate = rotate;
  state.torso = torso;
  state.head = head;
  state.cape = cape;
  state.capeScale = capeScale;
  state.jerseyHem = jerseyHem;
  state.leadLeg = leadLeg;
  state.rearLeg = rearLeg;
  state.leadShin = leadShin;
  state.rearShin = rearShin;
  state.leadArm = leadArm;
  state.rearArm = rearArm;
  return state;
}

const _cyclistBlendScratchPrev = {};
const _cyclistBlendScratchCurr = {};

/**
 * Continuous C2 Parametric Physics & Live Clock-Hand Playground Kinematics Engine
 * Supports 18 unscripted activities across 6 environmental hubs:
 *  - When options.act is provided, evaluates that specific activity directly.
 *  - When 0 <= elapsedSec < 32.0 and !options.markovMode, runs canonical 7-act baseline.
 *  - When elapsedSec >= 32.0 or options.markovMode, runs 5.0s-slot Markov sequence with C2 quintic transitions.
 */
export function computeCyclistPlaygroundFrame(elapsedSec, clockAngles = { hourDeg: 0, minDeg: 0, secDeg: 0 }, out = null, options = null) {
  const state = out || {};
  const lerp = (a, b, u) => a + (b - a) * u;

  if (options && options.act) {
    const totalT = Number(elapsedSec) || 0;
    const u = (((totalT % 5.0) + 5.0) % 5.0) / 5.0;
    return evaluatePlaygroundActivityPose(options.act, u, clockAngles, state);
  }

  const rawElapsed = Number(elapsedSec) || 0;
  const isMarkov = (rawElapsed >= 32.0) || Boolean(options && options.markovMode);

  if (!isMarkov && rawElapsed >= 0.0 && rawElapsed < 32.0) {
    const T_CYCLE = 32.0;
    const t = ((rawElapsed % T_CYCLE) + T_CYCLE) % T_CYCLE;
    const TWO_PI = Math.PI * 2;
    const DEG2RAD = Math.PI / 180;

    const smooth = (v) => {
      const c = Math.max(0, Math.min(1, v));
      return c * c * (3 - 2 * c);
    };

    const minRad = ((clockAngles && clockAngles.minDeg) || 0) * DEG2RAD;
    const hourRad = ((clockAngles && clockAngles.hourDeg) || 0) * DEG2RAD;
    const secRad = ((clockAngles && clockAngles.secDeg) || 0) * DEG2RAD;

    // Default upright anatomical posture
    let x = 35.3, y = 169.1, scaleX = 1, rotate = 14;
    let torso = 6, head = -10, cape = 12, capeScale = 0.98, jerseyHem = 6;
    let leadLeg = -20, rearLeg = 12, leadShin = 32, rearShin = 10;
    let leadArm = -20, rearArm = 10;
    let act = 'wheel-climb';

  if (t < 4.5) {
    // =========================================================================
    // ACT 1 (0.0s - 4.5s): Smooth Uninterrupted Front-Wheel Rim Climb to Saddle
    // =========================================================================
    act = 'wheel-climb';
    if (t < 3.6) {
      const u = t / 3.6;
      // Blend 85% constant cruise velocity + 15% smoothstep so climb never stops mid-arc
      const s = 0.85 * u + 0.15 * smooth(u);
      const alphaDeg = lerp(-8, 88, s);
      const alphaRad = alphaDeg * DEG2RAD;
      x = 116 - 81.5 * Math.cos(alphaRad);
      y = 169 - 81.5 * Math.sin(alphaRad) - 11.2;
      rotate = lerp(16, 22, Math.sin(u * Math.PI));
    } else {
      const u = smooth((t - 3.6) / 0.9);
      const crownX = 116 - 81.5 * Math.cos(88 * DEG2RAD);
      const crownY = 169 - 81.5 * Math.sin(88 * DEG2RAD) - 11.2;
      x = lerp(crownX, 158.0, u);
      y = lerp(crownY, 80.0, u) - 3.5 * Math.sin(u * Math.PI);
      // Turn around on mounting the saddle so rider faces LEFT (scaleX = -1) toward the front wheel & head tube
      scaleX = u >= 0.65 ? -1 : 1;
      rotate = lerp(14, 10, u);
    }
    const stride = t * 2.2 * TWO_PI;
    leadLeg = -14 + 26 * Math.sin(stride);
    rearLeg = -14 - 26 * Math.sin(stride);
    leadShin = 28 + 22 * Math.max(0, -Math.sin(stride));
    rearShin = 28 + 22 * Math.max(0, Math.sin(stride));
    leadArm = -16 + 22 * Math.cos(stride);
    rearArm = -16 - 22 * Math.cos(stride);
    torso = 8 + 2 * Math.sin(stride * 2);
    head = -12;
    cape = 20 + 6 * Math.sin(stride);
    capeScale = 1.00;
    jerseyHem = 10 + 4 * Math.sin(stride);
  } else if (t < 9.5) {
    // =========================================================================
    // ACT 2 (4.5s - 9.5s): Seated Saddle Air-Biking ("Too Small for Pedals!")
    // Penny Farthing front wheel & steering column are on the LEFT (x=116),
    // so rider sits on the saddle (x=158, y=80) facing LEFT (scaleX = -1)!
    // =========================================================================
    act = 'saddle-air-bike';
    if (t < 7.6) {
      // Furious circular air-pedaling high above the unreachable front wheel axle
      const pedalPhase = (t - 4.5) * 3.2 * TWO_PI;
      x = 158.0;
      y = 80.0 + 0.55 * Math.sin(pedalPhase * 2);
      scaleX = -1;
      rotate = 10 + 1.5 * Math.sin(pedalPhase);
      torso = 14 + 2 * Math.sin(pedalPhase);
      head = -6 + 3 * Math.sin(pedalPhase * 0.5);
      // Reaching forward/upward toward the tall chrome steering column & handlebars
      leadArm = -42 + 6 * Math.sin(pedalPhase);
      rearArm = -36 - 6 * Math.sin(pedalPhase);
      // True circular pedaling kinematics (hip & knee in phase quadrature)
      leadLeg = -24 + 26 * Math.sin(pedalPhase);
      leadShin = 46 + 26 * Math.cos(pedalPhase);
      rearLeg = -24 + 26 * Math.sin(pedalPhase + Math.PI);
      rearShin = 46 + 26 * Math.cos(pedalPhase + Math.PI);
      cape = 8 + 6 * Math.abs(Math.sin(pedalPhase));
      capeScale = 0.97;
      jerseyHem = 8 + 4 * Math.sin(pedalPhase);
    } else {
      // Comedic pause: stops pedaling, looks way down-left at dangling feet & faraway axle, then up at handlebars
      const u = (t - 7.6) / 1.9;
      const kickPhase = (t - 7.6) * 2.0 * TWO_PI;
      x = 158.0;
      y = 80.0;
      scaleX = -1;
      rotate = lerp(10, 2, smooth(u * 2));
      torso = lerp(12, 0, smooth(u * 2));
      // Look down (+26deg) at unreachable pedals for first 1.3s, then look up (-24deg) at handlebars
      head = u < 0.68 ? lerp(-6, 26, smooth(u / 0.35)) : lerp(26, -24, smooth((u - 0.68) / 0.32));
      leadArm = u < 0.68 ? 16 + 10 * Math.sin(kickPhase) : lerp(16, -65, smooth((u - 0.68) / 0.32));
      rearArm = u < 0.68 ? -12 - 8 * Math.sin(kickPhase) : lerp(-12, -55, smooth((u - 0.68) / 0.32));
      leadLeg = -8 + 18 * Math.sin(kickPhase);
      rearLeg = -8 - 18 * Math.sin(kickPhase);
      leadShin = 34 + 14 * Math.cos(kickPhase);
      rearShin = 34 - 14 * Math.cos(kickPhase);
      cape = 2;
      capeScale = 0.95;
      jerseyHem = 2;
    }
  } else if (t < 15.0) {
    // =========================================================================
    // ACT 3 (9.5s - 15.0s): Handlebar Stem Climb & Rooftop Dance on Handlebars
    // =========================================================================
    act = 'handlebar-dance';
    if (t < 11.0) {
      // Smooth climb from saddle (158, 80) -> head tube (119, 64) -> quill stem top -> handlebar arch (148, 2)
      const u = (t - 9.5) / 1.5;
      if (u < 0.45) {
        const s = smooth(u / 0.45);
        x = lerp(158.0, 120.0, s);
        y = lerp(80.0, 56.0, s) - 5.0 * Math.sin(s * Math.PI);
        scaleX = -1;
        rotate = 12;
      } else {
        const s = (u - 0.45) / 0.55;
        const sm = 0.8 * s + 0.2 * smooth(s);
        x = lerp(120.0, 148.0, sm);
        y = lerp(56.0, 2.0, sm) - 4.0 * Math.sin(sm * Math.PI);
        scaleX = 1;
        rotate = lerp(18, 0, sm);
      }
      const climbPhase = (t - 9.5) * 3.0 * TWO_PI;
      leadLeg = -22 + 24 * Math.sin(climbPhase);
      rearLeg = -22 - 24 * Math.sin(climbPhase);
      leadShin = 36 + 20 * Math.cos(climbPhase);
      rearShin = 36 - 20 * Math.cos(climbPhase);
      leadArm = -55 + 30 * Math.sin(climbPhase);
      rearArm = -55 - 30 * Math.sin(climbPhase);
      torso = 10;
      head = -14;
      cape = 22;
      capeScale = 1.02;
    } else if (t < 14.3) {
      // Joyful Rooftop Dance on top of the arched chrome handlebars & grip (x in [146, 178], surface y in [13, 24])
      const danceElapsed = t - 11.0;
      const danceProgress = danceElapsed / 3.3;
      const beat = danceElapsed * 3.8 * TWO_PI;
      // Footwork travels smoothly across the chrome handlebar arch and grip
      x = 160.0 + 14.0 * Math.sin(danceProgress * TWO_PI);
      const barSurfaceY = 13.0 + Math.max(0, (x - 146.0) * 0.34);
      const bounce = -2.6 * Math.abs(Math.sin(beat));
      y = (barSurfaceY - 11.2) + bounce;
      // Spin left and right during the dance
      scaleX = (danceProgress > 0.33 && danceProgress < 0.68) ? -1 : 1;
      rotate = 8 * Math.sin(beat * 0.5);
      torso = 8 * Math.cos(beat);
      head = -6 + 10 * Math.sin(beat);
      // Alternating high Charleston / Moonwalk kicks & knee bounce
      leadLeg = -18 + 32 * Math.sin(beat);
      rearLeg = -18 - 32 * Math.sin(beat);
      leadShin = 16 + 34 * (1 - Math.abs(Math.sin(beat)));
      rearShin = 16 + 34 * Math.abs(Math.sin(beat));
      // Celebratory overhead disco arm waves
      leadArm = -105 + 38 * Math.sin(beat);
      rearArm = -105 - 38 * Math.sin(beat);
      cape = 24 + 14 * Math.abs(Math.sin(beat));
      capeScale = 1.06;
      jerseyHem = 16 + 10 * Math.abs(Math.sin(beat));
    } else {
      // Graceful parabolic hop from handlebar peak (150, 2) back down to saddle backbone (162, 82)
      const u = smooth((t - 14.3) / 0.7);
      x = lerp(150.0, 162.0, u);
      y = lerp(2.0, 82.0, u * u);
      scaleX = 1;
      rotate = lerp(0, 8, u);
      torso = -6;
      head = 8;
      leadArm = -45;
      rearArm = 20;
      leadLeg = -35;
      rearLeg = -25;
      leadShin = 35;
      rearShin = 35;
      cape = 42;
      capeScale = 1.10;
      jerseyHem = 24;
    }
  } else if (t < 19.5) {
    // =========================================================================
    // ACT 4 (15.0s - 19.5s): Continuous Gravity-Accelerated Backbone Slide & Hike
    // =========================================================================
    act = 'backbone-slide';
    if (t < 17.4) {
      // Continuous gravity-accelerated slide down exact cubic Bezier (ZERO intermediate stops)
      const u = Math.max(0, Math.min(1, (t - 15.0) / 2.4));
      const tau = 0.38 + 0.62 * (0.35 * u + 0.65 * u * u);
      const pt = evalBackboneHipPoint(tau, 1.5);
      x = pt.x;
      y = pt.y;
      scaleX = 1;
      rotate = lerp(6, 14, u);
      torso = -10;
      head = 6;
      leadLeg = -52;
      rearLeg = -42;
      leadShin = 26;
      rearShin = 32;
      leadArm = -28;
      rearArm = 24;
      // High dynamic pressure q = 1/2 rho v^2 lifts cape smoothly as speed builds
      cape = lerp(24, 54, u);
      capeScale = lerp(1.02, 1.14, u);
      jerseyHem = lerp(16, 34, u);
    } else {
      // Smooth continuous uphill sprint back up the backbone to the fork crown (118, 75)
      const u = Math.max(0, Math.min(1, (t - 17.4) / 2.1));
      const tau = 1.0 - 0.96 * (0.85 * u + 0.15 * smooth(u));
      const pt = evalBackboneHipPoint(tau, 10.8);
      x = pt.x;
      y = pt.y;
      scaleX = -1;
      rotate = lerp(22, 8, u);
      const sprintPhase = (t - 17.4) * 3.2 * TWO_PI;
      leadLeg = -16 + 32 * Math.sin(sprintPhase);
      rearLeg = -16 - 32 * Math.sin(sprintPhase);
      leadShin = 30 + 24 * Math.max(0, -Math.sin(sprintPhase));
      rearShin = 30 + 24 * Math.max(0, Math.sin(sprintPhase));
      leadArm = -10 + 28 * Math.cos(sprintPhase);
      rearArm = -10 - 28 * Math.cos(sprintPhase);
      torso = 12;
      head = -10;
      cape = 26 + 6 * Math.sin(sprintPhase);
      capeScale = 1.04;
      jerseyHem = 16;
    }
  } else if (t < 24.5) {
    // =========================================================================
    // ACT 5 (19.5s - 24.5s): Live Minute-Hand Monkey Bars (#live-min-hand)
    // =========================================================================
    act = 'clock-monkey-bars-min';
    const crownPt = evalBackboneHipPoint(0.04, 10.8);
    if (t < 20.2) {
      // Leap from fork crown to grip live Minute Hand overhead at r = 46px
      const u = smooth((t - 19.5) / 0.7);
      const targetX = 116 + 46 * Math.sin(minRad);
      const targetY = 169 - 46 * Math.cos(minRad) + 12.6;
      x = lerp(crownPt.x, targetX, u);
      y = lerp(crownPt.y, targetY, u) - 6.0 * Math.sin(u * Math.PI);
      scaleX = Math.sin(minRad) >= 0 ? 1 : -1;
      rotate = lerp(0, 12, u);
      leadArm = lerp(-20, -136, u);
      rearArm = lerp(10, -126, u);
      leadLeg = -24;
      rearLeg = -12;
      leadShin = 42;
      rearShin = 38;
      torso = 4;
      head = -16;
      cape = 28;
      capeScale = 1.04;
    } else {
      // Hand-over-hand monkey-bar traverse & gymnast pendulum swing along live Minute Hand
      const u = (t - 20.2) / 4.3;
      const swing = (t - 20.2) * 1.8 * TWO_PI;
      // Traverse along live Minute Hand between r=46px and r=18px
      const r = 32 + 14 * Math.cos(u * Math.PI * 2);
      x = 116 + r * Math.sin(minRad);
      y = 169 - r * Math.cos(minRad) + 12.6;
      scaleX = Math.sin(minRad) >= 0 ? 1 : -1;
      rotate = 18 * Math.sin(swing);
      torso = 8 * Math.cos(swing);
      head = -18 + 6 * Math.sin(swing);
      // Overhead hand-over-hand monkey bar reach
      leadArm = -135 + 16 * Math.sin(swing);
      rearArm = -135 - 16 * Math.sin(swing);
      // Tucked gymnast legs kicking in swing rhythm
      leadLeg = -26 + 22 * Math.sin(swing);
      rearLeg = -26 - 22 * Math.sin(swing);
      leadShin = 48 + 14 * Math.cos(swing);
      rearShin = 48 - 14 * Math.cos(swing);
      cape = 14 + 12 * Math.abs(Math.sin(swing));
      capeScale = 1.00;
      jerseyHem = 10 + 8 * Math.abs(Math.sin(swing));
    }
  } else if (t < 29.5) {
    // =========================================================================
    // ACT 6 (24.5s - 29.5s): Live Hour-Hand Pull-Ups & Live Second-Hand Play
    // =========================================================================
    act = 'clock-monkey-bars-hour-sec';
    const minEndX = 116 + 46 * Math.sin(minRad);
    const minEndY = 169 - 46 * Math.cos(minRad) + 12.6;
    const hourGripX = 116 + 26 * Math.sin(hourRad);
    const hourGripY = 169 - 26 * Math.cos(hourRad) + 12.6;
    const secGripX = 116 + 36 * Math.sin(secRad);
    const secGripY = 169 - 36 * Math.cos(secRad) + 12.6;

    if (t < 25.2) {
      // Swing transfer from Minute Hand to Live Hour Hand
      const u = smooth((t - 24.5) / 0.7);
      x = lerp(minEndX, hourGripX, u);
      y = lerp(minEndY, hourGripY, u) - 5.0 * Math.sin(u * Math.PI);
      scaleX = Math.sin(hourRad) >= 0 ? 1 : -1;
      rotate = 14 * Math.sin(u * Math.PI);
      leadArm = -135;
      rearArm = -128;
      leadLeg = -30;
      rearLeg = -20;
      leadShin = 45;
      rearShin = 45;
      head = -16;
      cape = 22;
    } else if (t < 27.6) {
      // 3 Athletic Monkey-Bar Pull-Ups / Kips on the Live Hour Hand
      const u = (t - 25.2) / 2.4;
      const pullWave = 0.5 - 0.5 * Math.cos(u * 3 * TWO_PI);
      x = hourGripX;
      y = hourGripY - 6.2 * pullWave;
      scaleX = Math.sin(hourRad) >= 0 ? 1 : -1;
      rotate = 8 * pullWave;
      torso = 10 * pullWave;
      head = lerp(-18, -4, pullWave);
      // Arms flex from -135deg dead hang to -58deg chin-up at top of pull-up
      leadArm = lerp(-135, -58, pullWave);
      rearArm = lerp(-130, -52, pullWave);
      // Kip knee tuck on pull-up
      leadLeg = lerp(-12, -48, pullWave);
      rearLeg = lerp(-8, -42, pullWave);
      leadShin = lerp(24, 62, pullWave);
      rearShin = lerp(20, 58, pullWave);
      cape = 8 + 14 * pullWave;
      capeScale = 0.98;
      jerseyHem = 6 + 10 * pullWave;
    } else {
      // Swing & hang on the Live Ticking Second Hand (#live-sec-hand)
      const u = smooth(Math.min(1, (t - 27.6) / 0.5));
      const swing = (t - 27.6) * 2.5 * TWO_PI;
      x = lerp(hourGripX, secGripX, u);
      y = lerp(hourGripY, secGripY, u);
      scaleX = Math.sin(secRad) >= 0 ? 1 : -1;
      rotate = 20 * Math.sin(swing);
      torso = 10 * Math.cos(swing);
      head = -14;
      leadArm = -134 + 14 * Math.sin(swing);
      rearArm = -134 - 14 * Math.sin(swing);
      leadLeg = -28 + 26 * Math.sin(swing);
      rearLeg = -28 - 26 * Math.sin(swing);
      leadShin = 44;
      rearShin = 44;
      cape = 22 + 10 * Math.abs(Math.sin(swing));
      capeScale = 1.04;
      jerseyHem = 14;
    }
  } else {
    // =========================================================================
    // ACT 7 (29.5s - 32.0s): Smooth Vault to Left Tire Rim & Seamless Loop
    // =========================================================================
    act = 'rim-dismount';
    const secGripX = 116 + 36 * Math.sin(secRad);
    const secGripY = 169 - 36 * Math.cos(secRad) + 12.6;
    const startAlphaRad = -8 * DEG2RAD;
    const rimStartX = 116 - 81.5 * Math.cos(startAlphaRad);
    const rimStartY = 169 - 81.5 * Math.sin(startAlphaRad) - 11.2;

    if (t < 31.2) {
      const u = smooth((t - 29.5) / 1.7);
      x = lerp(secGripX, rimStartX, u);
      y = lerp(secGripY, rimStartY, u) - 14.0 * Math.sin(u * Math.PI);
      scaleX = secGripX > rimStartX ? -1 : 1;
      rotate = lerp(0, 12, u);
      const stride = (t - 29.5) * 2.0 * TWO_PI;
      leadArm = lerp(-90, -20, u);
      rearArm = lerp(-80, 10, u);
      leadLeg = -20 + 22 * Math.sin(stride);
      rearLeg = -20 - 22 * Math.sin(stride);
      leadShin = 34;
      rearShin = 34;
      torso = 8;
      head = -8;
      cape = 32 * (1 - 0.4 * u);
      capeScale = 1.04;
    } else {
      const u = smooth((t - 31.2) / 0.8);
      x = rimStartX;
      y = rimStartY;
      scaleX = 1;
      rotate = lerp(10, 16, u);
      leadLeg = lerp(-8, -14, u);
      rearLeg = lerp(8, -14, u);
      leadShin = 28;
      rearShin = 28;
      leadArm = lerp(-10, 6, u);
      rearArm = lerp(10, -38, u);
      torso = 8;
      head = -12;
      cape = 20;
      capeScale = 1.00;
    }
  }

    state.act = act;
    state.prop = 'none';
    state.x = x;
    state.y = y;
    state.scaleX = scaleX;
    state.rotate = rotate;
    state.torso = torso;
    state.head = head;
    state.cape = cape;
    state.capeScale = capeScale;
    state.jerseyHem = jerseyHem;
    state.leadLeg = leadLeg;
    state.rearLeg = rearLeg;
    state.leadShin = leadShin;
    state.rearShin = rearShin;
    state.leadArm = leadArm;
    state.rearArm = rearArm;
    return state;
  }

  // =========================================================================
  // MARKOV PLAYGROUND MODE (5.0s slots, C2 quintic Hermite transition blender)
  // =========================================================================
  const seed = (options && options.seed !== undefined) ? options.seed : 1978;
  const totalT = Math.max(0, rawElapsed);
  const slotIdx = Math.floor(totalT / 5.0);
  const localT = totalT - slotIdx * 5.0;
  const currSlot = getMarkovPlaygroundSlot(slotIdx, seed);
  const currActId = currSlot.act;
  const blendDuration = 0.70;

  if (localT < blendDuration && slotIdx > 0) {
    const prevSlot = getMarkovPlaygroundSlot(slotIdx - 1, seed);
    const prevPose = evaluatePlaygroundActivityPose(prevSlot.act, 1.0, clockAngles, _cyclistBlendScratchPrev);
    const currPose = evaluatePlaygroundActivityPose(currActId, localT / 5.0, clockAngles, _cyclistBlendScratchCurr);
    const blendU = localT / blendDuration;
    const w = blendU * blendU * blendU * (blendU * (blendU * 6.0 - 15.0) + 10.0);

    const dist = Math.hypot(currPose.x - prevPose.x, currPose.y - prevPose.y);
    const arcHeight = Math.min(18.0, 0.16 * dist);

    state.x = lerp(prevPose.x, currPose.x, w);
    state.y = lerp(prevPose.y, currPose.y, w) - arcHeight * Math.sin(Math.PI * w);

    let dRot = (currPose.rotate - prevPose.rotate) % 360;
    if (dRot > 180) dRot -= 360;
    if (dRot < -180) dRot += 360;
    state.rotate = prevPose.rotate + dRot * w;

    state.torso = lerp(prevPose.torso, currPose.torso, w);
    state.head = lerp(prevPose.head, currPose.head, w);
    state.cape = lerp(prevPose.cape, currPose.cape, w);
    state.capeScale = lerp(prevPose.capeScale, currPose.capeScale, w);
    state.jerseyHem = lerp(prevPose.jerseyHem, currPose.jerseyHem, w);
    state.leadLeg = lerp(prevPose.leadLeg, currPose.leadLeg, w);
    state.rearLeg = lerp(prevPose.rearLeg, currPose.rearLeg, w);
    state.leadShin = lerp(prevPose.leadShin, currPose.leadShin, w);
    state.rearShin = lerp(prevPose.rearShin, currPose.rearShin, w);
    state.leadArm = lerp(prevPose.leadArm, currPose.leadArm, w);
    state.rearArm = lerp(prevPose.rearArm, currPose.rearArm, w);
    state.scaleX = w >= 0.5 ? currPose.scaleX : prevPose.scaleX;
    state.act = currActId;
    state.prop = w >= 0.5 ? (currPose.prop || 'none') : (prevPose.prop || 'none');
  } else {
    evaluatePlaygroundActivityPose(currActId, localT / 5.0, clockAngles, state);
  }

  return state;
}

const _cyclistFrameScratch = {};

export function initHistoricalClockSync(root = document) {
  if (typeof window === 'undefined') return;

  const clockCards = (root || document).querySelectorAll('.historical-divider-card');
  if (clockCards.length === 0) return;

  // Initial countdown sync
  updateHistoricalClockCountdown(root);

  if (!_histCountdownInterval) {
    _histCountdownInterval = setInterval(() => {
      const activeCards = document.querySelectorAll('.historical-divider-card');
      if (activeCards.length === 0) {
        clearInterval(_histCountdownInterval);
        _histCountdownInterval = null;
        return;
      }
      updateHistoricalClockCountdown();
    }, 1000);
  }

  const sessionSeed = Math.floor(Math.random() * 100000) + 1337;
  let lastProp = null, lastAct = null;
  let lastSec = null, lastMin = null, lastHour = null;
  let handCache = null;
  const reducedMotionQuery = typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

  function collectHands() {
    handCache = Array.from(document.querySelectorAll('.historical-divider-card')).map(card => {
      const actor = card.querySelector('#hist-cyclist-actor');
      if (actor && !(reducedMotionQuery && reducedMotionQuery.matches)) {
        actor.classList.add('is-js-kinematics-active');
      }
      return {
        h: card.querySelector('#live-hour-hand'),
        m: card.querySelector('#live-min-hand'),
        s: card.querySelector('#live-sec-hand'),
        actor,
        cape: card.querySelector('#hist-actor-cape'),
        jerseyHem: card.querySelector('#hist-jersey-hem'),
        torso: card.querySelector('#hist-actor-torso'),
        head: card.querySelector('#hist-actor-head'),
        leadArm: card.querySelector('#hist-actor-lead-arm'),
        rearArm: card.querySelector('#hist-actor-rear-arm'),
        leadLeg: card.querySelector('#hist-actor-lead-leg'),
        rearLeg: card.querySelector('#hist-actor-rear-leg'),
        leadShin: card.querySelector('#hist-actor-lead-shin'),
        rearShin: card.querySelector('#hist-actor-rear-shin')
      };
    });
  }

  function update(nowPerf) {
    if (typeof document !== 'undefined' && document.body && (document.body.classList.contains('is-ambient-idle') || document.body.classList.contains('is-video-modal-open'))) {
      _histClockSyncRaf = null; // halted; resumes on modal close or attention restore
      return;
    }
    const activeCards = document.querySelectorAll('.historical-divider-card');
    if (activeCards.length === 0) {
      if (_histClockSyncRaf) {
        cancelAnimationFrame(_histClockSyncRaf);
        _histClockSyncRaf = null;
      }
      if (_histCountdownInterval) {
        clearInterval(_histCountdownInterval);
        _histCountdownInterval = null;
      }
      return;
    }

    if (!handCache || handCache.length !== activeCards.length || (handCache[0] && handCache[0].s && !handCache[0].s.isConnected)) {
      collectHands();
      lastSec = lastMin = lastHour = null;
      lastProp = lastAct = null;
    }

    const angles = getHistoricalClockAngles();
    const { secDeg, minDeg, hourDeg } = angles;
    const sChanged = secDeg !== lastSec;
    const mChanged = minDeg !== lastMin;
    const hChanged = hourDeg !== lastHour;
    if (sChanged || mChanged || hChanged) {
      handCache.forEach(({ h, m, s }) => {
        if (hChanged && h) h.style.transform = `rotate(${hourDeg}deg)`;
        if (mChanged && m) m.style.transform = `rotate(${minDeg}deg)`;
        if (sChanged && s) s.style.transform = `rotate(${secDeg}deg)`;
      });
      lastSec = secDeg; lastMin = minDeg; lastHour = hourDeg;
    }

    // Continuous C2 Cyclist Playground Kinematics (60-120 FPS smooth locomotion + live clock-hand monkey bars)
    const isReducedMotion = Boolean(reducedMotionQuery && reducedMotionQuery.matches);
    if (!isReducedMotion) {
      const elapsedSec = (typeof nowPerf === 'number' ? nowPerf : performance.now()) * 0.001;
      const f = computeCyclistPlaygroundFrame(elapsedSec, angles, _cyclistFrameScratch, { markovMode: true, seed: sessionSeed });
      const tRad = (f.torso || 0) * (Math.PI / 180);
      const cosT = Math.cos(tRad), sinT = Math.sin(tRad);
      const shiftJoint = (ox, oy) => {
        const dx = (ox * cosT - oy * sinT) - ox;
        const dy = (ox * sinT + oy * cosT) - oy;
        return `translate(${dx.toFixed(2)}px, ${dy.toFixed(2)}px) `;
      };
      const actorTransform = `translate(${f.x.toFixed(2)}px, ${f.y.toFixed(2)}px) scaleX(${f.scaleX}) rotate(${f.rotate.toFixed(2)}deg)`;
      const capeTransform = `${shiftJoint(-0.6, -8.4)}rotate(${f.cape.toFixed(2)}deg) scale(${f.capeScale.toFixed(3)})`;
      const hemTransform = `rotate(${f.jerseyHem.toFixed(2)}deg)`;
      const torsoTransform = `rotate(${f.torso.toFixed(2)}deg)`;
      const headTransform = `${shiftJoint(0.4, -9.0)}rotate(${(f.torso * 0.45 + f.head).toFixed(2)}deg)`;
      const leadArmTransform = `${shiftJoint(0.4, -7.8)}rotate(${f.leadArm.toFixed(2)}deg)`;
      const rearArmTransform = `${shiftJoint(0.0, -7.8)}rotate(${f.rearArm.toFixed(2)}deg)`;
      const leadLegTransform = `rotate(${f.leadLeg.toFixed(2)}deg)`;
      const rearLegTransform = `rotate(${f.rearLeg.toFixed(2)}deg)`;
      const leadShinTransform = `rotate(${f.leadShin.toFixed(2)}deg)`;
      const rearShinTransform = `rotate(${f.rearShin.toFixed(2)}deg)`;

      const propChanged = f.prop !== lastProp;
      const actChanged = f.act !== lastAct;

      for (let i = 0; i < handCache.length; i++) {
        const c = handCache[i];
        if (!c.actor) continue;
        c.actor.style.transform = actorTransform;
        if (propChanged) {
          c.actor.setAttribute('data-prop', f.prop || 'none');
        }
        if (actChanged) {
          c.actor.setAttribute('data-act', f.act || '');
        }
        if (c.cape) c.cape.style.transform = capeTransform;
        if (c.jerseyHem) c.jerseyHem.style.transform = hemTransform;
        if (c.torso) c.torso.style.transform = torsoTransform;
        if (c.head) c.head.style.transform = headTransform;
        if (c.leadArm) c.leadArm.style.transform = leadArmTransform;
        if (c.rearArm) c.rearArm.style.transform = rearArmTransform;
        if (c.leadLeg) c.leadLeg.style.transform = leadLegTransform;
        if (c.rearLeg) c.rearLeg.style.transform = rearLegTransform;
        if (c.leadShin) c.leadShin.style.transform = leadShinTransform;
        if (c.rearShin) c.rearShin.style.transform = rearShinTransform;
      }
      if (propChanged) lastProp = f.prop;
      if (actChanged) lastAct = f.act;
    }

    _histClockSyncRaf = requestAnimationFrame(update);
  }

  _histClockUpdateFn = update;
  if (!_histClockSyncRaf) {
    _histClockSyncRaf = requestAnimationFrame(update);
  }
}

let _histClockUpdateFn = null;
export function pauseHistoricalClockSync() {
  if (_histClockSyncRaf) {
    cancelAnimationFrame(_histClockSyncRaf);
    _histClockSyncRaf = null;
  }
}

export function resumeHistoricalClockSync() {
  if (_histClockSyncRaf || !_histClockUpdateFn) return;
  _histClockSyncRaf = requestAnimationFrame(_histClockUpdateFn);
}

export function renderHistoricalDividerCardHtml(item = {}, options = {}) {
  const isPhoto = options && options.isPhoto === true;
  const isSearchActive = Boolean(typeof PAGINATION_STATE !== 'undefined' && PAGINATION_STATE.searchQuery && PAGINATION_STATE.searchQuery.trim().length > 0);
  const targetRides = (isSearchActive && typeof currentLoadedRides !== 'undefined')
    ? currentLoadedRides.filter(r => matchesRideSearch(r, PAGINATION_STATE.searchQuery))
    : (typeof currentLoadedRides !== 'undefined' ? currentLoadedRides : []);
  const countdown = getUpcomingRideCountdown(targetRides);
  const daysStr = countdown.days;
  const hoursStr = countdown.hours;
  const minsStr = countdown.minutes;
  const secsStr = countdown.seconds;

  return `
    <div class="ride-card media-ride-card historical-divider-card ${isPhoto ? 'photo-historical-card photo-album-card' : ''}" role="listitem" aria-label="Historical Ride Archive Boundary: Antique Penny Farthing Clock">
      <div class="historical-card-backdrop" aria-hidden="true">
        <div class="historical-aura-glow"></div>
      </div>
      <div class="historical-card-body">
        <div class="historical-clock-stage" aria-hidden="true">
          <svg class="historical-clock-svg" viewBox="0 -14 340 344" width="100%" height="100%" preserveAspectRatio="xMidYMid meet">
            <defs>
              <linearGradient id="histPlinthCasingGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stop-color="#334155"/>
                <stop offset="15%" stop-color="#1e293b"/>
                <stop offset="70%" stop-color="#0f172a"/>
                <stop offset="100%" stop-color="#020617"/>
              </linearGradient>

              <!-- Rich Polished Mahogany Wood Plank Gradient -->
              <linearGradient id="histWoodGrainGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stop-color="#b45309"/>
                <stop offset="18%" stop-color="#7c2d12"/>
                <stop offset="50%" stop-color="#9a3412"/>
                <stop offset="80%" stop-color="#541c09"/>
                <stop offset="100%" stop-color="#351206"/>
              </linearGradient>

              <!-- Polished Cast Brass / Burnished Gold Gradient -->
              <linearGradient id="histPolishedBrassGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stop-color="#78350f"/>
                <stop offset="20%" stop-color="#b45309"/>
                <stop offset="42%" stop-color="#fde047"/>
                <stop offset="55%" stop-color="#fef08a"/>
                <stop offset="75%" stop-color="#d97706"/>
                <stop offset="100%" stop-color="#78350f"/>
              </linearGradient>

              <!-- Vertical Brass Stanchion Gradient -->
              <linearGradient id="histBrassStanchionGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stop-color="#451a03"/>
                <stop offset="25%" stop-color="#b45309"/>
                <stop offset="48%" stop-color="#fde047"/>
                <stop offset="70%" stop-color="#d97706"/>
                <stop offset="100%" stop-color="#541c09"/>
              </linearGradient>

              <linearGradient id="histFlipCardBevel" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stop-color="#475569"/>
                <stop offset="20%" stop-color="#1e293b"/>
                <stop offset="85%" stop-color="#0f172a"/>
                <stop offset="100%" stop-color="#020617"/>
              </linearGradient>

              <linearGradient id="histLeafTop" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stop-color="#ffffff"/>
                <stop offset="88%" stop-color="#f1f5f9"/>
                <stop offset="100%" stop-color="#e2e8f0"/>
              </linearGradient>

              <linearGradient id="histLeafBottom" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stop-color="#cbd5e1"/>
                <stop offset="14%" stop-color="#e2e8f0"/>
                <stop offset="100%" stop-color="#f8fafc"/>
              </linearGradient>

              <!-- Split-Flap Card Clip-Paths (Horizontal Split at Y=26) -->
              <clipPath id="histFlipClipTop">
                <rect x="3" y="3" width="54" height="23" rx="4"/>
              </clipPath>
              <clipPath id="histFlipClipBottom">
                <rect x="3" y="26" width="54" height="23" rx="4"/>
              </clipPath>

              <linearGradient id="histTube3DGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stop-color="#64748b"/>
                <stop offset="20%" stop-color="#334155"/>
                <stop offset="60%" stop-color="#1e293b"/>
                <stop offset="100%" stop-color="#090d16"/>
              </linearGradient>

              <radialGradient id="histAntiqueDialGrad" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stop-color="#faf5ea"/>
                <stop offset="65%" stop-color="#f2ead3"/>
                <stop offset="85%" stop-color="#e5dcbe"/>
                <stop offset="100%" stop-color="#cbbf9b"/>
              </radialGradient>

              <linearGradient id="histAgedVignette" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stop-color="#78350f" stop-opacity="0.18"/>
                <stop offset="50%" stop-color="#78350f" stop-opacity="0.0"/>
                <stop offset="100%" stop-color="#451a03" stop-opacity="0.25"/>
              </linearGradient>

              <linearGradient id="histLeatherSaddleGrad" x1="0%" y1="0%" x2="100%" y2="50%">
                <stop offset="0%" stop-color="#9a3412"/>
                <stop offset="40%" stop-color="#7c2d12"/>
                <stop offset="100%" stop-color="#431407"/>
              </linearGradient>

              <linearGradient id="histChromeLuster" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stop-color="#475569"/>
                <stop offset="35%" stop-color="#94a3b8"/>
                <stop offset="55%" stop-color="#ffffff"/>
                <stop offset="75%" stop-color="#cbd5e1"/>
                <stop offset="100%" stop-color="#334155"/>
              </linearGradient>

              <linearGradient id="histBluedSteelGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stop-color="#090d16"/>
                <stop offset="35%" stop-color="#1e293b"/>
                <stop offset="55%" stop-color="#334155"/>
                <stop offset="75%" stop-color="#1e293b"/>
                <stop offset="100%" stop-color="#090d16"/>
              </linearGradient>

              <filter id="histCastShadow" x="-10%" y="-10%" width="120%" height="130%">
                <feDropShadow dx="0" dy="5" stdDeviation="4" flood-color="#000000" flood-opacity="0.6"/>
              </filter>
              <filter id="histBrassGlow" x="-15%" y="-15%" width="130%" height="130%">
                <feDropShadow dx="0" dy="3" stdDeviation="2.5" flood-color="#78350f" flood-opacity="0.75"/>
              </filter>
            </defs>

            <!-- 1. HIGH-CONTRAST POLISHED MAHOGANY & BRASS EXHIBITION PEDESTAL STAND (LOWERED TO Y=243-251) -->
            <g id="hist-pedestal-stand" filter="url(#histCastShadow)">
              <!-- Lower Shadow Rim -->
              <rect x="50" y="243" width="250" height="9" rx="4" fill="#1c0702" filter="url(#histCastShadow)"/>
              <!-- Main Mahogany Timber Rail along Y=243 -->
              <rect x="52" y="243" width="246" height="8" rx="3.5" fill="url(#histWoodGrainGrad)" stroke="#451a03" stroke-width="0.8"/>
              <!-- Upper Golden Chamfered Highlight Line -->
              <line x1="55" y1="244" x2="295" y2="244" stroke="#fde047" stroke-width="0.75" opacity="0.85"/>
              <!-- Subtle Woodgrain Inlay Striations -->
              <line x1="68" y1="247" x2="148" y2="247" stroke="#f59e0b" stroke-width="0.5" opacity="0.4"/>
              <line x1="168" y1="247" x2="278" y2="247" stroke="#f59e0b" stroke-width="0.5" opacity="0.4"/>
              <line x1="55" y1="250" x2="295" y2="250" stroke="#2a0c04" stroke-width="0.7" opacity="0.8"/>

              <!-- Gleaming Solid Brass Fastener Rivets -->
              <circle cx="62" cy="247" r="1.8" fill="url(#histPolishedBrassGrad)" stroke="#451a03" stroke-width="0.4"/>
              <circle cx="170" cy="247" r="1.8" fill="url(#histPolishedBrassGrad)" stroke="#451a03" stroke-width="0.4"/>
              <circle cx="288" cy="247" r="1.8" fill="url(#histPolishedBrassGrad)" stroke="#451a03" stroke-width="0.4"/>

              <!-- Polished Cast-Brass Front Wheel Cradle Chock (Cupping Clock Wheel at x=116, y=246) -->
              <g id="hist-front-cradle" filter="url(#histBrassGlow)">
                <path d="M 92 244 C 96 250, 136 250, 140 244 L 144 249 C 137 255, 95 255, 88 249 Z" fill="url(#histPolishedBrassGrad)" stroke="#541c09" stroke-width="0.8"/>
                <path d="M 94 245 C 98 249, 134 249, 138 245" stroke="#fef08a" stroke-width="0.7" fill="none"/>
                <circle cx="100" cy="248" r="1.6" fill="#fef08a" stroke="#78350f" stroke-width="0.4"/>
                <circle cx="132" cy="248" r="1.6" fill="#fef08a" stroke="#78350f" stroke-width="0.4"/>
                <!-- Flared Victorian Solid Brass Front Stanchion Pillar -->
                <path d="M 105 251 L 100 270 L 128 270 L 123 251 Z" fill="url(#histBrassStanchionGrad)" stroke="#451a03" stroke-width="0.9"/>
                <line x1="114" y1="252" x2="114" y2="269" stroke="#fef08a" stroke-width="0.8" opacity="0.75"/>
                <!-- Heavy Polished Brass Base Mounting Shoe -->
                <rect x="97" y="267" width="34" height="3.5" rx="1.2" fill="url(#histPolishedBrassGrad)" stroke="#451a03" stroke-width="0.6"/>
              </g>

              <!-- Polished Cast-Brass Rear Wheel Cradle Chock (Cupping Rear Wheel at x=265, y=246) -->
              <g id="hist-rear-cradle" filter="url(#histBrassGlow)">
                <path d="M 246 244 C 250 250, 280 250, 284 244 L 288 249 C 281 255, 249 255, 242 249 Z" fill="url(#histPolishedBrassGrad)" stroke="#541c09" stroke-width="0.8"/>
                <path d="M 248 245 C 252 249, 278 249, 282 245" stroke="#fef08a" stroke-width="0.7" fill="none"/>
                <circle cx="252" cy="248" r="1.6" fill="#fef08a" stroke="#78350f" stroke-width="0.4"/>
                <circle cx="278" cy="248" r="1.6" fill="#fef08a" stroke="#78350f" stroke-width="0.4"/>
                <!-- Flared Victorian Solid Brass Rear Stanchion Pillar -->
                <path d="M 256 251 L 251 270 L 275 270 L 270 251 Z" fill="url(#histBrassStanchionGrad)" stroke="#451a03" stroke-width="0.9"/>
                <line x1="263" y1="252" x2="263" y2="269" stroke="#fef08a" stroke-width="0.8" opacity="0.75"/>
                <!-- Heavy Polished Brass Base Mounting Shoe -->
                <rect x="248" y="267" width="30" height="3.5" rx="1.2" fill="url(#histPolishedBrassGrad)" stroke="#451a03" stroke-width="0.6"/>
              </g>
            </g>

            <!-- 2. HIGH-WHEELER BICYCLE ASSEMBLY (WHEELS RESTING EXACTLY ON BASELINE Y=246) -->
            <g id="hist-bike-assembly">
              <!-- Rear Wheel Assembly (Center cy=220, r=26 -> bottom=246) -->
              <g id="hist-rear-wheel">
                <!-- Rim & Tire (Transparent open-air spoke voids) -->
                <circle cx="265" cy="220" r="26" stroke="#1e293b" stroke-width="4.5" fill="none"/>
                <circle cx="265" cy="220" r="24" stroke="#475569" stroke-width="1.2" fill="none"/>
                <!-- Hub & Bearings with Gold Accent -->
                <circle cx="265" cy="220" r="4.5" fill="#1e293b" stroke="#f59e0b" stroke-width="1.4"/>
                <circle cx="265" cy="220" r="2" fill="#fde047"/>
                <!-- 8 Spokes for Crisp Victorian Detail -->
                <line x1="265" y1="196" x2="265" y2="244" stroke="#94a3b8" stroke-width="0.9" opacity="0.75"/>
                <line x1="241" y1="220" x2="289" y2="220" stroke="#94a3b8" stroke-width="0.9" opacity="0.75"/>
                <line x1="248" y1="203" x2="282" y2="237" stroke="#94a3b8" stroke-width="0.9" opacity="0.75"/>
                <line x1="248" y1="237" x2="282" y2="203" stroke="#94a3b8" stroke-width="0.9" opacity="0.75"/>
                <!-- Rear Fork Stay -->
                <path d="M 265 220 L 261 192" stroke="url(#histTube3DGrad)" stroke-width="3" stroke-linecap="round"/>
                <circle cx="261" cy="192" r="2.5" fill="#475569"/>
              </g>

              <!-- Arched Backbone Frame & Cockpit -->
              <g id="hist-frame">
                <!-- Head Tube to Top Tube Welded Junction Collar -->
                <path d="M 118.2 87.5 L 120 75 C 120.5 81, 123 84, 127 85.5 L 118.2 87.5 Z" fill="url(#histTube3DGrad)" stroke="#020617" stroke-width="0.6"/>

                <!-- High-Wheeler Arched Backbone Frame Tube -->
                <path d="M 118.2 86.5 C 138 84, 218 108, 261 192" stroke="#090d16" stroke-width="6.5" stroke-linecap="round" fill="none" opacity="0.8"/>
                <path d="M 118.2 86.5 C 138 84, 218 108, 261 192" stroke="url(#histTube3DGrad)" stroke-width="5.2" stroke-linecap="round" fill="none"/>
                <path d="M 119.5 85 C 139 82.5, 218 106, 260 190" stroke="#94a3b8" stroke-width="1.2" stroke-linecap="round" fill="none" opacity="0.6"/>

                <!-- Fork Crown & Blades -->
                <path d="M 109 102 C 112 92, 120 92, 123 102" stroke="url(#histTube3DGrad)" stroke-width="3.6" stroke-linecap="round" fill="none"/>
                <circle cx="116" cy="92" r="3.4" fill="#1e293b" stroke="#475569" stroke-width="0.9"/>
                <circle cx="116" cy="92" r="1.5" fill="#fbbf24"/>

                <!-- Lower Chrome Headset Cup -->
                <rect x="112.5" y="87.5" width="7" height="2.2" rx="0.5" fill="url(#histChromeLuster)" stroke="#475569" stroke-width="0.5"/>

                <!-- Gloss Black Head Tube -->
                <path d="M 113.8 87.5 L 116.8 65 L 121.2 65 L 118.2 87.5 Z" fill="url(#histTube3DGrad)" stroke="#020617" stroke-width="0.8"/>
                <line x1="115" y1="87" x2="117.8" y2="65.5" stroke="#64748b" stroke-width="0.8" opacity="0.7"/>

                <!-- Upper Chrome Headset Locknut & Threaded Cup -->
                <rect x="115.5" y="63" width="7" height="2.4" rx="0.6" fill="url(#histChromeLuster)" stroke="#475569" stroke-width="0.5"/>
                <line x1="116" y1="64.2" x2="122" y2="64.2" stroke="#ffffff" stroke-width="0.5"/>

                <!-- Chrome Quill Stem -->
                <rect x="117" y="44" width="4.2" height="19" rx="0.6" fill="url(#histChromeLuster)" stroke="#475569" stroke-width="0.5"/>
                <line x1="118.2" y1="44.5" x2="118.2" y2="62.5" stroke="#ffffff" stroke-width="0.8" opacity="0.8"/>

                <!-- Chrome Handlebar Clamp Lug & Pinch Bolt -->
                <path d="M 117 45 L 116 41 L 121 32 L 126 32.5 L 121.5 45 Z" fill="url(#histChromeLuster)" stroke="#334155" stroke-width="0.6"/>
                <circle cx="122.5" cy="32.5" r="3.2" fill="#1e293b" stroke="url(#histChromeLuster)" stroke-width="1.2"/>
                <line x1="115" y1="37" x2="119" y2="37" stroke="#ffffff" stroke-width="1.2" stroke-linecap="round"/>
                <circle cx="115" cy="37" r="1.0" fill="#cbd5e1" stroke="#0f172a" stroke-width="0.4"/>

                <!-- Background Handlebar Grip -->
                <g id="hist-handlebar-bg" opacity="0.75">
                  <path d="M 122.5 32.5 C 124 18, 134 12, 146 14 C 156 16, 166 22, 178 26" stroke="#334155" stroke-width="3.2" fill="none" stroke-linecap="round"/>
                  <ellipse cx="178" cy="26" rx="2.2" ry="4.2" transform="rotate(18, 178, 26)" fill="#090d16" stroke="#334155" stroke-width="0.8"/>
                  <rect x="179" y="23.2" width="12.5" height="5.2" rx="1.8" transform="rotate(18, 185, 26)" fill="#090d16" stroke="#334155" stroke-width="0.8"/>
                </g>

                <!-- Foreground Tubular Chrome Handlebar & Ribbed Grip (Restored Original) -->
                <g id="hist-handlebar-fg">
                  <path d="M 122.5 32.5 C 124 18, 135 11, 148 13 C 158 15, 168 21, 180 26" stroke="url(#histChromeLuster)" stroke-width="3.8" stroke-linecap="round" fill="none"/>
                  <path d="M 122.5 32.5 C 124 18, 135 11, 148 13 C 158 15, 168 21, 180 26" stroke="#ffffff" stroke-width="0.9" stroke-linecap="round" fill="none" opacity="0.75"/>
                  <ellipse cx="180" cy="26" rx="2.4" ry="4.5" transform="rotate(18, 180, 26)" fill="#090d16" stroke="#334155" stroke-width="0.8"/>
                  <rect x="181" y="23.2" width="13.5" height="5.6" rx="2" transform="rotate(18, 188, 26)" fill="#0f172a" stroke="#334155" stroke-width="0.9"/>
                  <line x1="185" y1="23" x2="185" y2="28" stroke="#1e293b" stroke-width="0.8" transform="rotate(18, 188, 26)"/>
                  <line x1="188" y1="23" x2="188" y2="28" stroke="#1e293b" stroke-width="0.8" transform="rotate(18, 188, 26)"/>
                  <line x1="191" y1="23" x2="191" y2="28" stroke="#1e293b" stroke-width="0.8" transform="rotate(18, 188, 26)"/>
                  <circle cx="194.5" cy="30.8" r="2.2" fill="#090d16" stroke="#475569" stroke-width="0.8"/>
                </g>

                <g id="hist-saddle">
                  <path d="M 152 89 Q 155 95 158 89 T 164 89" stroke="#fbbf24" stroke-width="1.4" fill="none"/>
                  <line x1="154" y1="89" x2="154" y2="83" stroke="#475569" stroke-width="2.5"/>
                  <line x1="162" y1="89" x2="162" y2="83" stroke="#475569" stroke-width="2.5"/>
                  <line x1="158" y1="90" x2="158" y2="96" stroke="#334155" stroke-width="3"/>
                  <path d="M 144 83 C 140 79, 168 75, 178 81 C 174 85, 150 87, 144 83 Z" fill="url(#histLeatherSaddleGrad)" stroke="#431407" stroke-width="1.2"/>
                  <circle cx="174" cy="80" r="1" fill="#fbbf24"/>
                  <circle cx="147" cy="82" r="0.9" fill="#fbbf24"/>
                </g>
              </g>

              <!-- Front Wheel & Clock Face (Center cy=169, r=77 -> bottom=246) -->
              <g id="hist-clock-face" filter="url(#histCastShadow)">
                <circle cx="116" cy="169" r="77" stroke="#090d16" stroke-width="9" fill="none"/>
                <circle cx="116" cy="169" r="76" stroke="#1e293b" stroke-width="7" fill="none"/>
                <circle cx="116" cy="169" r="72" stroke="#475569" stroke-width="1.5" fill="none"/>
                <circle cx="116" cy="169" r="70" stroke="#854d0e" stroke-width="1" fill="none" opacity="0.7"/>

                <circle cx="116" cy="169" r="69" fill="url(#histAntiqueDialGrad)"/>
                <circle cx="116" cy="169" r="69" fill="url(#histAgedVignette)"/>

                <!-- 12 RESTORED ORIGINAL RADIAL PIE SECTORS & DASHED RAYS -->
                <g id="hist-dial-sectors" opacity="0.35">
                  <path d="M 116 169 L 133.8 102.4 A 69 69 0 0 1 164.8 120.2 Z" fill="#854d0e" opacity="0.12"/>
                  <path d="M 116 169 L 182.6 151.1 A 69 69 0 0 1 182.6 186.9 Z" fill="#854d0e" opacity="0.12"/>
                  <path d="M 116 169 L 164.8 217.8 A 69 69 0 0 1 133.8 235.6 Z" fill="#854d0e" opacity="0.12"/>
                  <path d="M 116 169 L 98.2 235.6 A 69 69 0 0 1 67.2 217.8 Z" fill="#854d0e" opacity="0.12"/>
                  <path d="M 116 169 L 49.4 186.9 A 69 69 0 0 1 49.4 151.1 Z" fill="#854d0e" opacity="0.12"/>
                  <path d="M 116 169 L 67.2 120.2 A 69 69 0 0 1 98.2 102.4 Z" fill="#854d0e" opacity="0.12"/>

                  <line x1="127.4" y1="126.5" x2="133.8" y2="102.4" stroke="#451a03" stroke-width="0.8" stroke-dasharray="14 3 6 2"/>
                  <line x1="147.1" y1="137.9" x2="164.8" y2="120.2" stroke="#451a03" stroke-width="0.8" stroke-dasharray="12 4 8 2"/>
                  <line x1="158.5" y1="157.6" x2="182.6" y2="151.1" stroke="#451a03" stroke-width="0.8" stroke-dasharray="15 2 5 2"/>
                  <line x1="158.5" y1="180.4" x2="182.6" y2="186.9" stroke="#451a03" stroke-width="0.8" stroke-dasharray="14 3 6 2"/>
                  <line x1="147.1" y1="200.1" x2="164.8" y2="217.8" stroke="#451a03" stroke-width="0.8" stroke-dasharray="12 4 8 2"/>
                  <line x1="127.4" y1="211.5" x2="133.8" y2="235.6" stroke="#451a03" stroke-width="0.8" stroke-dasharray="15 2 5 2"/>
                  <line x1="104.6" y1="211.5" x2="98.2" y2="235.6" stroke="#451a03" stroke-width="0.8" stroke-dasharray="14 3 6 2"/>
                  <line x1="84.9" y1="200.1" x2="67.2" y2="217.8" stroke="#451a03" stroke-width="0.8" stroke-dasharray="12 4 8 2"/>
                  <line x1="73.5" y1="180.4" x2="49.4" y2="186.9" stroke="#451a03" stroke-width="0.8" stroke-dasharray="15 2 5 2"/>
                  <line x1="73.5" y1="157.6" x2="49.4" y2="151.1" stroke="#451a03" stroke-width="0.8" stroke-dasharray="14 3 6 2"/>
                  <line x1="84.9" y1="137.9" x2="67.2" y2="120.2" stroke="#451a03" stroke-width="0.8" stroke-dasharray="12 4 8 2"/>
                  <line x1="104.6" y1="126.5" x2="98.2" y2="102.4" stroke="#451a03" stroke-width="0.8" stroke-dasharray="15 2 5 2"/>
                </g>

                <!-- Concentric Dashed Alignment Rings -->
                <circle cx="122" cy="161" r="32" stroke="#92400e" stroke-width="0.6" stroke-dasharray="18 4 6 8" fill="none" opacity="0.18"/>
                <circle cx="108" cy="177" r="48" stroke="#78350f" stroke-width="0.5" stroke-dasharray="12 8 4 14" fill="none" opacity="0.15"/>

                <circle cx="116" cy="169" r="67.5" stroke="#451a03" stroke-width="0.9" fill="none" opacity="0.55"/>
                <circle cx="116" cy="169" r="65.2" stroke="#451a03" stroke-width="0.6" stroke-dasharray="1.2 2.3" fill="none" opacity="0.75"/>
                <circle cx="116" cy="169" r="63" stroke="#451a03" stroke-width="0.8" fill="none" opacity="0.55"/>

                <circle cx="116" cy="169" r="44.5" stroke="#78350f" stroke-width="0.9" stroke-dasharray="2.5 2" fill="none" opacity="0.65"/>
                <circle cx="116" cy="169" r="42.5" stroke="#92400e" stroke-width="0.5" fill="none" opacity="0.4"/>

                <!-- BTCNJ INSIGNIA -->
                <g id="hist-dial-btcnj-insignia">
                  <g font-family="'Didot', 'Bodoni MT', 'Baskerville', 'Playfair Display', 'Times New Roman', serif" font-weight="900" font-size="13" text-anchor="middle" dominant-baseline="central">
                    <g transform="translate(85.7, 151.5) rotate(-60)"><text x="0" y="0" fill="#18181b">B</text></g>
                    <g transform="translate(98.5, 138.7) rotate(-30)"><text x="0" y="0" fill="#18181b">T</text></g>
                    <g transform="translate(116, 134.0) rotate(0)"><text x="0" y="0" fill="#18181b">C</text></g>
                    <g transform="translate(133.5, 138.7) rotate(30)"><text x="0" y="0" fill="#18181b">N</text></g>
                    <g transform="translate(146.3, 151.5) rotate(60)"><text x="0" y="0" fill="#18181b">J</text></g>
                  </g>
                  <g transform="translate(116, 206)">
                    <text x="0" y="0" font-family="'Didot', 'Bodoni MT', 'Baskerville', Georgia, serif" font-weight="700" font-size="8.5" fill="#18181b" text-anchor="middle" letter-spacing="0.6">Est. 1978</text>
                  </g>
                </g>

                <!-- Victorian Numerals -->
                <g font-family="'Didot', 'Bodoni MT', 'Baskerville', Georgia, serif" font-weight="900" text-anchor="middle" dominant-baseline="central" fill="#2d333b">
                  <g transform="translate(116, 116)"><text x="0" y="0" font-size="14.5">12</text></g>
                  <g transform="translate(143, 123)"><text x="0" y="0" font-size="13">1</text></g>
                  <g transform="translate(162, 140)"><text x="0" y="0" font-size="13">2</text></g>
                  <g transform="translate(169, 169)"><text x="0" y="0" font-size="14.5">3</text></g>
                  <g transform="translate(162, 198)"><text x="0" y="0" font-size="13">4</text></g>
                  <g transform="translate(143, 215)"><text x="0" y="0" font-size="13">5</text></g>
                  <g transform="translate(116, 222)"><text x="0" y="0" font-size="14.5">6</text></g>
                  <g transform="translate(89, 215)"><text x="0" y="0" font-size="13">7</text></g>
                  <g transform="translate(70, 198)"><text x="0" y="0" font-size="13">8</text></g>
                  <g transform="translate(63, 169)"><text x="0" y="0" font-size="14.5">9</text></g>
                  <g transform="translate(70, 140)"><text x="0" y="0" font-size="13">10</text></g>
                  <g transform="translate(89, 123)"><text x="0" y="0" font-size="13">11</text></g>
                </g>

                <!-- Clock Hands (Showing live rotation at center 116, 169) -->
                <g id="live-hour-hand" class="hist-hour-hand" style="transform-origin: 116px 169px;">
                  <!-- Counterpoise Pear Tail -->
                  <path d="M 114.8 169 L 114.4 175 C 113.8 177.5, 114.8 180, 116 180 C 117.2 180, 118.2 177.5, 117.6 175 L 117.2 169 Z" fill="url(#histBluedSteelGrad)" stroke="#090d16" stroke-width="0.5"/>
                  <circle cx="116" cy="177" r="1.2" fill="#faf5ea" opacity="0.6"/>

                  <!-- Pierced Spade Blade (Heart Window) -->
                  <path d="
                    M 114.8 169
                    L 115 152
                    C 113.5 150, 111.5 147, 111.5 142
                    C 111.5 137.5, 114.5 135, 116 131.5
                    C 117.5 135, 120.5 137.5, 120.5 142
                    C 120.5 147, 118.5 150, 117 152
                    L 117.2 169 Z
                    
                    M 116 135
                    C 114.2 138.5, 113 141, 113 143
                    C 113 145.5, 114.5 146.8, 116 148
                    C 117.5 146.8, 119 145.5, 119 143
                    C 119 141, 117.8 138.5, 116 135 Z
                  " fill-rule="evenodd" fill="url(#histBluedSteelGrad)" stroke="#090d16" stroke-width="0.6"/>

                  <!-- Sharp Needle Tip Extension -->
                  <path d="M 115.4 132.5 L 116 126.5 L 116.6 132.5 Z" fill="url(#histBluedSteelGrad)" stroke="#090d16" stroke-width="0.5"/>
                  <line x1="116" y1="168" x2="116" y2="152" stroke="#64748b" stroke-width="0.5" opacity="0.8"/>
                </g>

                <g id="live-min-hand" class="hist-min-hand" style="transform-origin: 116px 169px;">
                  <!-- Counterpoise Spear Tail -->
                  <path d="M 115 169 L 114.5 178 C 114.2 181, 116 182.5, 116 182.5 C 116 182.5, 117.8 181, 117.5 178 L 117 169 Z" fill="url(#histBluedSteelGrad)" stroke="#090d16" stroke-width="0.5"/>

                  <!-- Pierced Lance Lozenge Blade -->
                  <path d="
                    M 115 169
                    L 115.2 131
                    L 113 123
                    C 113 120, 114.5 117.5, 116 113
                    C 117.5 117.5, 119 120, 119 123
                    L 116.8 131
                    L 117 169 Z

                    M 116 115.5
                    C 114.4 121, 114.2 123, 114.2 124.5
                    C 114.2 126.5, 115.2 128, 116 130
                    C 116.8 128, 117.8 126.5, 117.8 124.5
                    C 117.8 123, 117.6 121, 116 115.5 Z
                  " fill-rule="evenodd" fill="url(#histBluedSteelGrad)" stroke="#090d16" stroke-width="0.6"/>

                  <!-- Long Fine Precision Needle Tip -->
                  <path d="M 115.5 114 L 116 106 L 116.5 114 Z" fill="url(#histBluedSteelGrad)" stroke="#090d16" stroke-width="0.5"/>
                  <line x1="116" y1="168" x2="116" y2="131" stroke="#64748b" stroke-width="0.5" opacity="0.8"/>
                </g>

                <g id="live-sec-hand" class="hist-sec-hand" style="transform-origin: 116px 169px;">
                  <line x1="116" y1="187" x2="116" y2="102" stroke="#dc2626" stroke-width="1.1" stroke-linecap="round"/>
                  <circle cx="116" cy="182" r="3.5" fill="none" stroke="#dc2626" stroke-width="1.2"/>
                  <circle cx="116" cy="182" r="1.2" fill="#fde047"/>
                </g>

                <!-- Polished Turned Brass Center Collet & Pivot Nut -->
                <circle cx="116" cy="169" r="6" fill="#1e293b" stroke="#090d16" stroke-width="0.8"/>
                <circle cx="116" cy="169" r="4.2" fill="url(#histPolishedBrassGrad)" stroke="#78350f" stroke-width="0.6"/>
                <circle cx="116" cy="169" r="1.8" fill="#451a03"/>
                <circle cx="115.4" cy="168.4" r="0.6" fill="#fef08a"/>
              </g>

              <!-- Anatomically Proportionate Articulated Human Cyclist Actor -->
              <g class="playground-cyclist-actor" id="hist-cyclist-actor" filter="url(#histCastShadow)">
                <!-- 1. Multi-Link Cloth Physics Crimson Cycling Cape (Collar Pendulum -> Mid-Body Billow -> Phase-Lagged Tail Whip) -->
                <g class="hist-actor-cape" id="hist-actor-cape">
                  <path d="M -1.2 -8.6 C -0.4 -9.2, 1.2 -9.2, 1.5 -8.3 C 1.0 -7.7, -0.8 -7.8, -1.2 -8.6 Z" fill="#991b1b"/>
                  <g class="hist-cape-flutter">
                    <path class="hist-cape-main" d="M -0.6 -8.4 C -2.0 -7.0, -3.4 -5.2, -3.8 -3.4 C -3.0 -3.0, -2.0 -3.2, -1.6 -4.2 C -1.1 -5.8, -0.6 -7.2, -0.2 -8.2 Z" fill="#dc2626" stroke="#991b1b" stroke-width="0.3"/>
                    <!-- Secondary Phase-Lagged Trailing Cloth Segment (Traveling Kelvin-Helmholtz Wave) -->
                    <g class="hist-cape-tail" id="hist-cape-tail">
                      <path d="M -3.6 -3.6 C -4.6 -2.0, -5.4 0.0, -5.0 1.2 C -4.2 1.5, -3.0 0.6, -2.4 -1.0 C -2.0 -2.2, -1.7 -3.2, -1.7 -4.0 Z" fill="#ef4444" stroke="#b91c1c" stroke-width="0.3"/>
                      <path d="M -2.8 -3.5 C -3.6 -1.8, -4.1 -0.2, -3.9 0.8" stroke="#fca5a5" stroke-width="0.35" stroke-linecap="round" fill="none" opacity="0.65"/>
                    </g>
                  </g>
                </g>
                <!-- 2. Rear Leg (Hip -> Thigh -> Articulated Knee -> Calf/Sock -> Shoe) -->
                <g class="hist-actor-rear-leg" id="hist-actor-rear-leg">
                  <path d="M -0.4 0.2 L 0.2 5.4" stroke="#0f172a" stroke-width="2.6" stroke-linecap="round" fill="none"/>
                  <g class="hist-actor-rear-shin" id="hist-actor-rear-shin">
                    <path d="M 0.2 5.4 L -0.2 9.4" stroke="#d97706" stroke-width="1.9" stroke-linecap="round" fill="none"/>
                    <path d="M -0.1 8.2 L -0.2 10.2" stroke="#cbd5e1" stroke-width="2.0" stroke-linecap="butt" fill="none"/>
                    <path d="M -1.0 10.0 L 2.2 10.5 C 2.6 10.6, 2.6 11.3, 1.8 11.3 L -1.0 11.2 Z" fill="#0f172a" stroke="#475569" stroke-width="0.4"/>
                  </g>
                </g>
                <!-- 3. Rear Arm (Shoulder -> Upper Arm -> Elbow -> Forearm -> Glove) -->
                <g class="hist-actor-rear-arm" id="hist-actor-rear-arm">
                  <path d="M 0.0 -7.8 L 1.2 -4.2 L 4.0 -2.4" stroke="#0369a1" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
                  <circle cx="4.2" cy="-2.3" r="0.85" fill="#f8fafc" stroke="#0f172a" stroke-width="0.35"/>
                  <!-- Pink-Frosted Sprinkled Donut (Held in Rear Glove at 4.2, -2.3) -->
                  <g class="hist-prop-item hist-prop-donut" id="hist-prop-donut" transform="translate(8.4, 0.7)">
                    <circle cx="-4.2" cy="-3.0" r="2.8" fill="#d97706" stroke="#92400e" stroke-width="0.35"/>
                    <circle cx="-4.2" cy="-3.0" r="2.3" fill="#ec4899"/>
                    <circle cx="-4.2" cy="-3.0" r="0.9" fill="#0b1120"/>
                    <circle cx="-5.3" cy="-3.6" r="0.25" fill="#f8fafc"/>
                    <circle cx="-3.4" cy="-3.8" r="0.25" fill="#fbbf24"/>
                    <circle cx="-3.3" cy="-2.2" r="0.25" fill="#38bdf8"/>
                    <circle cx="-5.1" cy="-2.3" r="0.25" fill="#a855f7"/>
                  </g>
                </g>
                <!-- 4. Upright Athletic Human Torso (High-Contrast BTCNJ Sky-Blue & Gold Jersey + Dynamic Jersey Tail Hem) -->
                <g class="hist-actor-torso" id="hist-actor-torso">
                  <!-- Hip / Pelvis Bib Shorts Base -->
                  <ellipse cx="0.0" cy="0.0" rx="2.1" ry="1.8" fill="#0f172a"/>
                  <!-- Sculpted Upright Human Torso in BTCNJ Sky-Blue Jersey -->
                  <path d="M -1.9 -8.2 C -1.4 -9.2, 1.4 -9.2, 2.1 -8.0 C 2.5 -5.4, 2.0 -2.2, 1.7 -0.4 C 0.6 0.4, -1.2 0.4, -1.8 -0.5 C -2.2 -2.8, -2.3 -5.8, -1.9 -8.2 Z" fill="#0284c7" stroke="#0f172a" stroke-width="0.45"/>
                  <!-- Dynamic Rear Jersey Tail / Pocket Hem (Cloth Rustle in Slipstream) -->
                  <g class="hist-jersey-hem" id="hist-jersey-hem">
                    <path d="M -1.9 -2.2 C -2.7 -1.6, -3.1 -0.6, -2.5 0.1 C -1.9 0.3, -1.2 0.1, -1.1 -0.6 Z" fill="#0369a1" stroke="#38bdf8" stroke-width="0.3"/>
                  </g>
                  <!-- BTCNJ Gold & White Championship Chest Stripe -->
                  <path d="M -2.0 -5.4 L 2.1 -5.2 L 2.0 -3.9 L -1.9 -4.1 Z" fill="#fbbf24"/>
                  <!-- Crisp White Cycling Collar -->
                  <path d="M -1.0 -8.7 L 1.4 -8.6 L 1.0 -7.6 L -0.8 -7.7 Z" fill="#f8fafc"/>
                </g>
                <!-- 5. Proportionate Human Head, Neck & Vintage Aero Cycling Cap with Trailing Ribbon -->
                <g class="hist-actor-head" id="hist-actor-head">
                  <!-- Anatomical Neck -->
                  <line x1="0.3" y1="-8.6" x2="0.6" y2="-10.2" stroke="#fdba74" stroke-width="1.7" stroke-linecap="round"/>
                  <!-- Trailing Cap Ribbon / Tassel (Aerodynamic Cloth Streamer) -->
                  <g class="hist-cap-ribbon" id="hist-cap-ribbon">
                    <path d="M -1.2 -11.6 C -2.4 -11.4, -3.4 -10.8, -3.8 -10.0" stroke="#fbbf24" stroke-width="0.65" stroke-linecap="round" fill="none"/>
                  </g>
                  <!-- Sculpted Human Head & Facial Profile -->
                  <circle cx="0.8" cy="-11.5" r="2.1" fill="#fed7aa" stroke="#9a3412" stroke-width="0.35"/>
                  <!-- Nose & Chin Profile Contour + Eye -->
                  <path d="M 2.5 -12.0 L 3.2 -11.3 L 2.6 -10.8" fill="#fed7aa" stroke="#9a3412" stroke-width="0.3" stroke-linejoin="round"/>
                  <circle cx="1.8" cy="-11.7" r="0.35" fill="#0f172a"/>
                  <!-- Vintage BTCNJ Cycling Helmet / Cap with Gold Ribbon & Visor -->
                  <path d="M -1.4 -11.7 C -1.4 -14.2, 2.4 -14.4, 3.0 -12.0 L 4.0 -11.6 C 3.2 -11.3, 0.8 -11.4, -1.4 -11.7 Z" fill="#0284c7" stroke="#0f172a" stroke-width="0.4"/>
                  <path d="M -1.1 -12.2 C 0.2 -12.7, 1.8 -12.7, 2.8 -12.1" stroke="#fbbf24" stroke-width="0.65" fill="none" stroke-linecap="round"/>
                </g>
                <!-- 6. Lead Arm (Shoulder -> Upper Arm -> Elbow -> Forearm -> Glove) -->
                <g class="hist-actor-lead-arm" id="hist-actor-lead-arm">
                  <path d="M 0.4 -7.8 L 1.6 -4.0 L 4.6 -2.0" stroke="#38bdf8" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
                  <circle cx="4.8" cy="-1.9" r="0.9" fill="#f8fafc" stroke="#0f172a" stroke-width="0.4"/>
                  <!-- Articulated Contextual SVG Props Rig (Lead Hand Props) -->
                  <g class="hist-actor-props" id="hist-actor-props">
                    <!-- Spider-Man Webline & Shooters (Anchored to Lead Glove at 4.8, -1.9) -->
                    <g class="hist-prop-item hist-prop-webline" id="hist-prop-webline" transform="translate(2.3, 3.1)">
                      <line x1="2.5" y1="-5.0" x2="-8.0" y2="-48.0" stroke="#e2e8f0" stroke-width="0.7" stroke-dasharray="2,1" stroke-linecap="round"/>
                      <line x1="2.5" y1="-5.0" x2="-6.5" y2="-48.0" stroke="#f8fafc" stroke-width="0.4" stroke-linecap="round"/>
                      <path d="M 1.5 -4.0 L 3.5 -6.0 M 3.5 -4.0 L 1.5 -6.0" stroke="#94a3b8" stroke-width="0.5"/>
                    </g>
                    <!-- Vintage Cast-Iron Barbell & Kettlebell Plates (Centered on Lead Glove at 4.8, -1.9) -->
                    <g class="hist-prop-item hist-prop-barbell" id="hist-prop-barbell" transform="translate(3.8, 12.6)">
                      <line x1="-9.0" y1="-14.5" x2="11.0" y2="-14.5" stroke="#94a3b8" stroke-width="0.9" stroke-linecap="round"/>
                      <rect x="-8.2" y="-19.0" width="1.6" height="9.0" rx="0.5" fill="#1e293b" stroke="#0f172a" stroke-width="0.4"/>
                      <rect x="-10.2" y="-17.5" width="1.4" height="6.0" rx="0.4" fill="#334155" stroke="#0f172a" stroke-width="0.3"/>
                      <rect x="7.6" y="-19.0" width="1.6" height="9.0" rx="0.5" fill="#1e293b" stroke="#0f172a" stroke-width="0.4"/>
                      <rect x="9.8" y="-17.5" width="1.4" height="6.0" rx="0.4" fill="#334155" stroke="#0f172a" stroke-width="0.3"/>
                      <circle cx="-6.0" cy="-14.5" r="0.8" fill="#fbbf24"/>
                      <circle cx="7.0" cy="-14.5" r="0.8" fill="#fbbf24"/>
                    </g>
                    <!-- BTCNJ Espresso Mug with Rising Steam Wisps (Held in Lead Glove at 4.8, -1.9) -->
                    <g class="hist-prop-item hist-prop-coffee" id="hist-prop-coffee">
                      <path d="M 3.2 -5.5 L 6.6 -5.5 L 6.2 -2.2 C 6.1 -1.4, 3.7 -1.4, 3.6 -2.2 Z" fill="#0284c7" stroke="#fbbf24" stroke-width="0.4"/>
                      <path d="M 6.5 -4.8 C 7.8 -4.8, 7.8 -2.8, 6.3 -2.8" fill="none" stroke="#fbbf24" stroke-width="0.4"/>
                      <path class="hist-steam-wisp" d="M 4.2 -6.2 C 3.8 -7.5, 4.8 -8.4, 4.4 -9.6 M 5.6 -6.0 C 5.2 -7.2, 6.2 -8.1, 5.8 -9.2" fill="none" stroke="#fef08a" stroke-width="0.4" stroke-linecap="round" opacity="0.8"/>
                    </g>
                    <!-- Golden Martial Arts Chi Energy Arcs (Radiating from Lead Glove at 4.8, -1.9) -->
                    <g class="hist-prop-item hist-prop-chi" id="hist-prop-chi">
                      <path d="M 6.0 -6.0 C 10.0 -4.0, 12.0 0.0, 9.0 5.0" fill="none" stroke="#fbbf24" stroke-width="0.9" stroke-linecap="round" opacity="0.85"/>
                      <path d="M 4.0 -8.0 C 9.0 -6.0, 11.0 -2.0, 7.0 2.0" fill="none" stroke="#f59e0b" stroke-width="0.6" stroke-linecap="round" opacity="0.7"/>
                      <circle cx="8.5" cy="-1.5" r="1.1" fill="#fef08a" opacity="0.9"/>
                      <path d="M -3.0 7.0 C 1.0 12.0, 7.0 13.0, 12.0 10.0" fill="none" stroke="#fbbf24" stroke-width="0.8" stroke-linecap="round" opacity="0.75"/>
                    </g>
                  </g>
                </g>
                <!-- 7. Lead Leg (Connected Hip -> Thigh -> Articulated Knee -> Calf/Sock -> Shoe) -->
                <g class="hist-actor-lead-leg" id="hist-actor-lead-leg">
                  <path d="M 0.0 0.2 L 0.6 5.4" stroke="#1e293b" stroke-width="2.8" stroke-linecap="round" fill="none"/>
                  <line x1="0.2" y1="4.7" x2="0.9" y2="4.6" stroke="#fbbf24" stroke-width="0.7" stroke-linecap="round"/>
                  <g class="hist-actor-lead-shin" id="hist-actor-lead-shin">
                    <path d="M 0.6 5.4 L 0.2 9.4" stroke="#fdba74" stroke-width="2.0" stroke-linecap="round" fill="none"/>
                    <path d="M 0.3 8.1 L 0.2 10.2" stroke="#f8fafc" stroke-width="2.1" stroke-linecap="butt" fill="none"/>
                    <path d="M -0.7 10.0 L 2.7 10.5 C 3.2 10.6, 3.2 11.4, 2.3 11.4 L -0.7 11.3 Z" fill="#0f172a" stroke="#fbbf24" stroke-width="0.45"/>
                  </g>
                </g>
              </g>
            </g>

            <!-- 3. VINTAGE FLIP-CARD COUNTDOWN CLOCK: 4 PROTRUDING MODULES (DAYS, HOURS, MINUTES, SECONDS) -->
            <g id="hist-plinth" filter="url(#histCastShadow)">
              <!-- Horizontal Base Pedestal Deck -->
              <rect x="10" y="268" width="320" height="48" rx="6" fill="url(#histPlinthCasingGrad)" stroke="#475569" stroke-width="1.2"/>
              <line x1="12" y1="269" x2="328" y2="269" stroke="#94a3b8" stroke-width="0.8" opacity="0.5"/>
              <rect x="8" y="312" width="324" height="6" rx="2" fill="#020617"/>

              <!-- Sub-Labels on Base Pedestal -->
              <text x="56" y="302" font-family="system-ui, -apple-system, sans-serif" font-size="8.5" font-weight="800" letter-spacing="1.2" fill="#94a3b8" text-anchor="middle">DAYS</text>
              <text x="132" y="302" font-family="system-ui, -apple-system, sans-serif" font-size="8.5" font-weight="800" letter-spacing="1.2" fill="#94a3b8" text-anchor="middle">HOURS</text>
              <text x="208" y="302" font-family="system-ui, -apple-system, sans-serif" font-size="8.5" font-weight="800" letter-spacing="1.2" fill="#94a3b8" text-anchor="middle">MINUTES</text>
              <text x="284" y="302" font-family="system-ui, -apple-system, sans-serif" font-size="8.5" font-weight="800" letter-spacing="1.2" fill="#94a3b8" text-anchor="middle">SECONDS</text>

              <!-- Module 1: DAYS -->
              <g id="hist-flip-days-module" class="hist-flip-module" data-unit="days" transform="translate(26, 236)">
                <rect x="0" y="0" width="60" height="52" rx="6" fill="url(#histFlipCardBevel)" stroke="#475569" stroke-width="1.4" filter="url(#histCastShadow)"/>
                <rect x="1.5" y="1.5" width="57" height="1" fill="#94a3b8" opacity="0.5"/>
                <rect x="3" y="3" width="54" height="46" rx="4" fill="#090d16"/>
                <!-- 1. Static Top (New digit revealed as upper flap drops) -->
                <g class="hist-flip-static-top" clip-path="url(#histFlipClipTop)">
                  <rect x="4" y="4" width="52" height="21" rx="2" fill="url(#histLeafTop)"/>
                  <text class="hist-digit-text hist-text-top" x="30" y="26" dominant-baseline="central" font-family="'Impact', 'Arial Black', sans-serif" font-size="36" font-weight="900" fill="#090d16" text-anchor="middle" letter-spacing="0.5">${daysStr}</text>
                </g>
                <!-- 2. Static Bottom (Old digit showing until lower flap lands) -->
                <g class="hist-flip-static-bottom" clip-path="url(#histFlipClipBottom)">
                  <rect x="4" y="27" width="52" height="21" rx="2" fill="url(#histLeafBottom)"/>
                  <text class="hist-digit-text hist-text-bottom" x="30" y="26" dominant-baseline="central" font-family="'Impact', 'Arial Black', sans-serif" font-size="36" font-weight="900" fill="#090d16" text-anchor="middle" letter-spacing="0.5">${daysStr}</text>
                </g>
                <!-- 3. Falling Upper Flap (Old digit folding down) -->
                <g class="hist-flap-upper" clip-path="url(#histFlipClipTop)">
                  <rect x="4" y="4" width="52" height="21" rx="2" fill="url(#histLeafTop)"/>
                  <text class="hist-digit-text hist-text-flap-upper" x="30" y="26" dominant-baseline="central" font-family="'Impact', 'Arial Black', sans-serif" font-size="36" font-weight="900" fill="#090d16" text-anchor="middle" letter-spacing="0.5">${daysStr}</text>
                  <rect class="hist-flap-shade-upper" x="4" y="4" width="52" height="21" fill="#000" opacity="0"/>
                </g>
                <!-- 4. Landing Lower Flap (New digit unfolding down) -->
                <g class="hist-flap-lower" clip-path="url(#histFlipClipBottom)">
                  <rect x="4" y="27" width="52" height="21" rx="2" fill="url(#histLeafBottom)"/>
                  <text class="hist-digit-text hist-text-flap-lower" x="30" y="26" dominant-baseline="central" font-family="'Impact', 'Arial Black', sans-serif" font-size="36" font-weight="900" fill="#090d16" text-anchor="middle" letter-spacing="0.5">${daysStr}</text>
                  <rect class="hist-flap-shade-lower" x="4" y="27" width="52" height="21" fill="#000" opacity="0"/>
                </g>
                <!-- 5. Split Seam & Chrome Pivot Barrel Hinges -->
                <line x1="3" y1="26" x2="57" y2="26" stroke="#020617" stroke-width="1.8"/>
                <line x1="3" y1="27" x2="57" y2="27" stroke="#475569" stroke-width="0.5" opacity="0.7"/>
                <rect x="-1" y="23" width="4" height="6" rx="1" fill="url(#histChromeLuster)" stroke="#090d16" stroke-width="0.5"/>
                <rect x="57" y="23" width="4" height="6" rx="1" fill="url(#histChromeLuster)" stroke="#090d16" stroke-width="0.5"/>
                <!-- Backward-Compatibility Element for Test Selectors -->
                <text class="hist-flip-digit" id="hist-flip-days" style="display:none;">${daysStr}</text>
              </g>

              <!-- Module 2: HOURS -->
              <g id="hist-flip-hours-module" class="hist-flip-module" data-unit="hours" transform="translate(102, 236)">
                <rect x="0" y="0" width="60" height="52" rx="6" fill="url(#histFlipCardBevel)" stroke="#475569" stroke-width="1.4" filter="url(#histCastShadow)"/>
                <rect x="1.5" y="1.5" width="57" height="1" fill="#94a3b8" opacity="0.5"/>
                <rect x="3" y="3" width="54" height="46" rx="4" fill="#090d16"/>
                <!-- 1. Static Top (New digit revealed as upper flap drops) -->
                <g class="hist-flip-static-top" clip-path="url(#histFlipClipTop)">
                  <rect x="4" y="4" width="52" height="21" rx="2" fill="url(#histLeafTop)"/>
                  <text class="hist-digit-text hist-text-top" x="30" y="26" dominant-baseline="central" font-family="'Impact', 'Arial Black', sans-serif" font-size="36" font-weight="900" fill="#090d16" text-anchor="middle" letter-spacing="0.5">${hoursStr}</text>
                </g>
                <!-- 2. Static Bottom (Old digit showing until lower flap lands) -->
                <g class="hist-flip-static-bottom" clip-path="url(#histFlipClipBottom)">
                  <rect x="4" y="27" width="52" height="21" rx="2" fill="url(#histLeafBottom)"/>
                  <text class="hist-digit-text hist-text-bottom" x="30" y="26" dominant-baseline="central" font-family="'Impact', 'Arial Black', sans-serif" font-size="36" font-weight="900" fill="#090d16" text-anchor="middle" letter-spacing="0.5">${hoursStr}</text>
                </g>
                <!-- 3. Falling Upper Flap (Old digit folding down) -->
                <g class="hist-flap-upper" clip-path="url(#histFlipClipTop)">
                  <rect x="4" y="4" width="52" height="21" rx="2" fill="url(#histLeafTop)"/>
                  <text class="hist-digit-text hist-text-flap-upper" x="30" y="26" dominant-baseline="central" font-family="'Impact', 'Arial Black', sans-serif" font-size="36" font-weight="900" fill="#090d16" text-anchor="middle" letter-spacing="0.5">${hoursStr}</text>
                  <rect class="hist-flap-shade-upper" x="4" y="4" width="52" height="21" fill="#000" opacity="0"/>
                </g>
                <!-- 4. Landing Lower Flap (New digit unfolding down) -->
                <g class="hist-flap-lower" clip-path="url(#histFlipClipBottom)">
                  <rect x="4" y="27" width="52" height="21" rx="2" fill="url(#histLeafBottom)"/>
                  <text class="hist-digit-text hist-text-flap-lower" x="30" y="26" dominant-baseline="central" font-family="'Impact', 'Arial Black', sans-serif" font-size="36" font-weight="900" fill="#090d16" text-anchor="middle" letter-spacing="0.5">${hoursStr}</text>
                  <rect class="hist-flap-shade-lower" x="4" y="27" width="52" height="21" fill="#000" opacity="0"/>
                </g>
                <!-- 5. Split Seam & Chrome Pivot Barrel Hinges -->
                <line x1="3" y1="26" x2="57" y2="26" stroke="#020617" stroke-width="1.8"/>
                <line x1="3" y1="27" x2="57" y2="27" stroke="#475569" stroke-width="0.5" opacity="0.7"/>
                <rect x="-1" y="23" width="4" height="6" rx="1" fill="url(#histChromeLuster)" stroke="#090d16" stroke-width="0.5"/>
                <rect x="57" y="23" width="4" height="6" rx="1" fill="url(#histChromeLuster)" stroke="#090d16" stroke-width="0.5"/>
                <!-- Backward-Compatibility Element for Test Selectors -->
                <text class="hist-flip-digit" id="hist-flip-hours" style="display:none;">${hoursStr}</text>
              </g>

              <!-- Module 3: MINUTES -->
              <g id="hist-flip-mins-module" class="hist-flip-module" data-unit="mins" transform="translate(178, 236)">
                <rect x="0" y="0" width="60" height="52" rx="6" fill="url(#histFlipCardBevel)" stroke="#475569" stroke-width="1.4" filter="url(#histCastShadow)"/>
                <rect x="1.5" y="1.5" width="57" height="1" fill="#94a3b8" opacity="0.5"/>
                <rect x="3" y="3" width="54" height="46" rx="4" fill="#090d16"/>
                <!-- 1. Static Top (New digit revealed as upper flap drops) -->
                <g class="hist-flip-static-top" clip-path="url(#histFlipClipTop)">
                  <rect x="4" y="4" width="52" height="21" rx="2" fill="url(#histLeafTop)"/>
                  <text class="hist-digit-text hist-text-top" x="30" y="26" dominant-baseline="central" font-family="'Impact', 'Arial Black', sans-serif" font-size="36" font-weight="900" fill="#090d16" text-anchor="middle" letter-spacing="0.5">${minsStr}</text>
                </g>
                <!-- 2. Static Bottom (Old digit showing until lower flap lands) -->
                <g class="hist-flip-static-bottom" clip-path="url(#histFlipClipBottom)">
                  <rect x="4" y="27" width="52" height="21" rx="2" fill="url(#histLeafBottom)"/>
                  <text class="hist-digit-text hist-text-bottom" x="30" y="26" dominant-baseline="central" font-family="'Impact', 'Arial Black', sans-serif" font-size="36" font-weight="900" fill="#090d16" text-anchor="middle" letter-spacing="0.5">${minsStr}</text>
                </g>
                <!-- 3. Falling Upper Flap (Old digit folding down) -->
                <g class="hist-flap-upper" clip-path="url(#histFlipClipTop)">
                  <rect x="4" y="4" width="52" height="21" rx="2" fill="url(#histLeafTop)"/>
                  <text class="hist-digit-text hist-text-flap-upper" x="30" y="26" dominant-baseline="central" font-family="'Impact', 'Arial Black', sans-serif" font-size="36" font-weight="900" fill="#090d16" text-anchor="middle" letter-spacing="0.5">${minsStr}</text>
                  <rect class="hist-flap-shade-upper" x="4" y="4" width="52" height="21" fill="#000" opacity="0"/>
                </g>
                <!-- 4. Landing Lower Flap (New digit unfolding down) -->
                <g class="hist-flap-lower" clip-path="url(#histFlipClipBottom)">
                  <rect x="4" y="27" width="52" height="21" rx="2" fill="url(#histLeafBottom)"/>
                  <text class="hist-digit-text hist-text-flap-lower" x="30" y="26" dominant-baseline="central" font-family="'Impact', 'Arial Black', sans-serif" font-size="36" font-weight="900" fill="#090d16" text-anchor="middle" letter-spacing="0.5">${minsStr}</text>
                  <rect class="hist-flap-shade-lower" x="4" y="27" width="52" height="21" fill="#000" opacity="0"/>
                </g>
                <!-- 5. Split Seam & Chrome Pivot Barrel Hinges -->
                <line x1="3" y1="26" x2="57" y2="26" stroke="#020617" stroke-width="1.8"/>
                <line x1="3" y1="27" x2="57" y2="27" stroke="#475569" stroke-width="0.5" opacity="0.7"/>
                <rect x="-1" y="23" width="4" height="6" rx="1" fill="url(#histChromeLuster)" stroke="#090d16" stroke-width="0.5"/>
                <rect x="57" y="23" width="4" height="6" rx="1" fill="url(#histChromeLuster)" stroke="#090d16" stroke-width="0.5"/>
                <!-- Backward-Compatibility Element for Test Selectors -->
                <text class="hist-flip-digit" id="hist-flip-mins" style="display:none;">${minsStr}</text>
              </g>

              <!-- Module 4: SECONDS -->
              <g id="hist-flip-secs-module" class="hist-flip-module" data-unit="secs" transform="translate(254, 236)">
                <rect x="0" y="0" width="60" height="52" rx="6" fill="url(#histFlipCardBevel)" stroke="#475569" stroke-width="1.4" filter="url(#histCastShadow)"/>
                <rect x="1.5" y="1.5" width="57" height="1" fill="#94a3b8" opacity="0.5"/>
                <rect x="3" y="3" width="54" height="46" rx="4" fill="#090d16"/>
                <!-- 1. Static Top (New digit revealed as upper flap drops) -->
                <g class="hist-flip-static-top" clip-path="url(#histFlipClipTop)">
                  <rect x="4" y="4" width="52" height="21" rx="2" fill="url(#histLeafTop)"/>
                  <text class="hist-digit-text hist-text-top" x="30" y="26" dominant-baseline="central" font-family="'Impact', 'Arial Black', sans-serif" font-size="36" font-weight="900" fill="#090d16" text-anchor="middle" letter-spacing="0.5">${secsStr}</text>
                </g>
                <!-- 2. Static Bottom (Old digit showing until lower flap lands) -->
                <g class="hist-flip-static-bottom" clip-path="url(#histFlipClipBottom)">
                  <rect x="4" y="27" width="52" height="21" rx="2" fill="url(#histLeafBottom)"/>
                  <text class="hist-digit-text hist-text-bottom" x="30" y="26" dominant-baseline="central" font-family="'Impact', 'Arial Black', sans-serif" font-size="36" font-weight="900" fill="#090d16" text-anchor="middle" letter-spacing="0.5">${secsStr}</text>
                </g>
                <!-- 3. Falling Upper Flap (Old digit folding down) -->
                <g class="hist-flap-upper" clip-path="url(#histFlipClipTop)">
                  <rect x="4" y="4" width="52" height="21" rx="2" fill="url(#histLeafTop)"/>
                  <text class="hist-digit-text hist-text-flap-upper" x="30" y="26" dominant-baseline="central" font-family="'Impact', 'Arial Black', sans-serif" font-size="36" font-weight="900" fill="#090d16" text-anchor="middle" letter-spacing="0.5">${secsStr}</text>
                  <rect class="hist-flap-shade-upper" x="4" y="4" width="52" height="21" fill="#000" opacity="0"/>
                </g>
                <!-- 4. Landing Lower Flap (New digit unfolding down) -->
                <g class="hist-flap-lower" clip-path="url(#histFlipClipBottom)">
                  <rect x="4" y="27" width="52" height="21" rx="2" fill="url(#histLeafBottom)"/>
                  <text class="hist-digit-text hist-text-flap-lower" x="30" y="26" dominant-baseline="central" font-family="'Impact', 'Arial Black', sans-serif" font-size="36" font-weight="900" fill="#090d16" text-anchor="middle" letter-spacing="0.5">${secsStr}</text>
                  <rect class="hist-flap-shade-lower" x="4" y="27" width="52" height="21" fill="#000" opacity="0"/>
                </g>
                <!-- 5. Split Seam & Chrome Pivot Barrel Hinges -->
                <line x1="3" y1="26" x2="57" y2="26" stroke="#020617" stroke-width="1.8"/>
                <line x1="3" y1="27" x2="57" y2="27" stroke="#475569" stroke-width="0.5" opacity="0.7"/>
                <rect x="-1" y="23" width="4" height="6" rx="1" fill="url(#histChromeLuster)" stroke="#090d16" stroke-width="0.5"/>
                <rect x="57" y="23" width="4" height="6" rx="1" fill="url(#histChromeLuster)" stroke="#090d16" stroke-width="0.5"/>
                <!-- Backward-Compatibility Element for Test Selectors -->
                <text class="hist-flip-digit" id="hist-flip-secs" style="display:none;">${secsStr}</text>
              </g>
            </g>
          </svg>
        </div>
      </div>
    </div>
  `;
}

export function renderPhotoHistoricalDividerCardHtml(item) {
  return renderHistoricalDividerCardHtml(item, { isPhoto: true });
}

export function renderMediaHistoricalDividerListHtml(item = {}) {
  return `
    <div class="media-list-historical-divider" role="separator" aria-label="Historical Ride Archive Boundary">
      <div class="year-divider-line"></div>
      <div class="historical-divider-badge">
        <span class="hist-icon">🕰️</span>
        <span class="hist-title">Historical Rides &amp; Media</span>
      </div>
      <div class="year-divider-line"></div>
    </div>
  `;
}

export function renderMediaYearMilestoneListHtml(item = {}) {
  const count = item.stats?.rideCount || item.stats?.count || item.stats?.videoCount || '';
  const countLabel = count ? `${count} Rides` : '';
  return `
    <div class="media-list-year-divider" role="separator" aria-label="Season ${escapeHtml(item.year || '')}">
      <div class="year-divider-line"></div>
      <div class="year-divider-badge">
        <span class="year-num">${escapeHtml(item.year || '')}</span>
        ${countLabel ? `<span class="year-meta">${escapeHtml(countLabel)}</span>` : ''}
      </div>
      <div class="year-divider-line"></div>
    </div>
  `;
}

export function renderMediaListRowHtml(ride, options = {}) {
  const thumbUrl = resolveRideThumbnail(ride, false);
  const rawTitle = ride.title || 'Untitled Ride';
  const temporalTier = getRideTemporalTier(ride);
  const pendingChipHtml = temporalTier ? buildPendingBadgeHtml(temporalTier) : '';

  const activeQuery = (typeof PAGINATION_STATE !== 'undefined' && PAGINATION_STATE && PAGINATION_STATE.searchQuery)
    ? PAGINATION_STATE.searchQuery
    : '';
  const searchTokens = activeQuery ? activeQuery.trim().toLowerCase().split(/\s+/).filter(Boolean) : [];
  const searchMatchContext = activeQuery ? extractSearchMatchContext(ride, activeQuery) : null;
  const searchContextHtml = searchMatchContext
    ? `<div class="card-search-context media-list-search-context" aria-label="Search match context">${searchMatchContext.html}</div>`
    : '';
  const highlightedTitleHtml = (searchTokens.length > 0)
    ? highlightSearchText(rawTitle, searchTokens)
    : escapeHtml(rawTitle);

  const dateFormattedFn = typeof formatSpotlightDate === 'function'
    ? formatSpotlightDate
    : (typeof formatRideDate === 'function' ? formatRideDate : () => '');
  const dateDisplay = dateFormattedFn(ride);

  const hasPhotos = Boolean(ride.photoAlbumUrl);
  const hasVideos = Boolean(ride.matchedVideos && ride.matchedVideos.length > 0);
  const videoCount = hasVideos ? ride.matchedVideos.length : 0;
  const photoCount = typeof ride.photoCount === 'number' ? ride.photoCount : 0;

  // Badge overlay on thumbnail
  let thumbBadgeHtml = '';
  if (hasVideos) {
    thumbBadgeHtml = `<span class="media-list-thumb-badge badge-video">▶ ${videoCount}</span>`;
  } else if (hasPhotos) {
    thumbBadgeHtml = `<span class="media-list-thumb-badge badge-photo">${photoCount} 📸</span>`;
  }

  // Telemetry stats line
  const footerStats = buildRideFooter(ride);
  const statsLineHtml = footerStats
    ? `<div class="media-list-sub" title="${escapeHtml(footerStats.replace(/<[^>]*>/g, '').trim())}">${footerStats}</div>`
    : '';

  // Direct Action Buttons
  const actions = [];
  if (hasVideos) {
    actions.push(`
      <button type="button" class="btn btn-danger btn-sm btn-list-video" data-launch="video-lightbox" data-ride-id="${escapeHtml(ride.id || ride.rideId)}" title="Play YouTube Video${videoCount > 1 ? `s (${videoCount})` : ''}">
        <span>▶ Play${videoCount > 1 ? ` (${videoCount})` : ''}</span>
      </button>
    `);
  }
  if (hasPhotos) {
    actions.push(`
      <a href="${escapeHtml(ride.photoAlbumUrl)}" target="_blank" rel="noopener noreferrer" class="btn btn-secondary btn-sm btn-list-album" data-ride-id="${escapeHtml(ride.id || ride.rideId)}" title="Open Google Photos Album${photoCount > 0 ? ` (${photoCount} Photos)` : ''}">
        <span>📸<span class="btn-list-label-desktop"> Photos</span>${photoCount > 0 ? ` (${photoCount})` : ''}</span>
      </a>
    `);
  }
  if (ride.rwgpsUrl) {
    actions.push(`
      <button type="button" class="btn btn-secondary btn-sm btn-list-rwgps" data-launch="rwgps-modal" data-ride-id="${escapeHtml(ride.id || ride.rideId)}" title="Open Interactive RWGPS Map">
        <span>RWGPS</span>
      </button>
    `);
  }
  const isBtcnj = Boolean(ride.isBtcnjRide || (ride.rideId && String(ride.rideId).trim().length > 0));
  if (isBtcnj) {
    actions.push(`
      <a href="${escapeHtml(ride.btcnjUrl || (ride.rideId ? `https://btcnj.com/pages/rideSignupEdit?rideid=${encodeURIComponent(ride.rideId)}` : 'https://www.btcnj.com/'))}" target="_blank" rel="noopener noreferrer" class="btn btn-secondary btn-sm btn-list-btcnj" title="BTCNJ Ride #${escapeHtml(ride.rideId || '')}">
        <span>#${escapeHtml(ride.rideId || 'Ride')}</span>
      </a>
    `);
  }

  const pendingCardClass = temporalTier ? `is-pending-ride tier-${temporalTier}` : '';

  return `
    <article class="media-list-row ${pendingCardClass}" role="listitem" tabindex="0" data-ride-id="${escapeHtml(ride.id || ride.rideId)}" ${temporalTier ? `data-temporal-tier="${temporalTier}"` : ''}>
      <div class="media-list-thumb">
        <img src="${escapeHtml(thumbUrl)}" alt="${escapeHtml(rawTitle)}" loading="lazy" decoding="async" referrerpolicy="no-referrer" onerror="if (!this.dataset.fallback) { this.dataset.fallback = '1'; this.src = ${hasVideos && thumbUrl !== `https://img.youtube.com/vi/${ride.matchedVideos[0].videoId}/hqdefault.jpg` ? `'https://img.youtube.com/vi/${escapeHtml(ride.matchedVideos[0].videoId)}/hqdefault.jpg'` : '(window.getCyclingFallbackSvg ? window.getCyclingFallbackSvg() : \'\')'}; } else { this.onerror = null; this.src = (window.getCyclingFallbackSvg ? window.getCyclingFallbackSvg() : ''); }">
        ${thumbBadgeHtml}
      </div>
      <div class="media-list-content">
        <div class="media-list-header-row">
          ${pendingChipHtml}
          ${dateDisplay ? `<span class="media-list-date">${escapeHtml(dateDisplay)}</span>` : ''}
        </div>
        <h3 class="media-list-title" title="${escapeHtml(rawTitle)}">${highlightedTitleHtml}</h3>
        ${searchContextHtml}
        ${statsLineHtml}
      </div>
      <div class="media-list-actions">
        ${actions.join('')}
      </div>
    </article>
  `;
}

let isSearchCockpitBound = false;
let searchDebounceTimer = null;

/**
 * Initializes and synchronizes the header search input and bottom pagination HUD controls.
 */
export function initSearchCockpit(rides) {
  if (typeof document === 'undefined') return;
  const input = document.getElementById('media-search-input');
  const clearBtn = document.getElementById('btn-clear-media-search');
  const emptyClearBtn = document.getElementById('btn-reset-search-empty');
  const select = document.getElementById('cards-per-page-select');
  const paginationDeck = document.getElementById('feed-pagination-deck');

  // Pre-populate input and header share button if query is set from URL
  if (PAGINATION_STATE.searchQuery) {
    if (input) input.value = PAGINATION_STATE.searchQuery;
    if (clearBtn) clearBtn.style.display = 'flex';
    PAGINATION_STATE.isMobileSearchOpen = true;
    updateHeaderShareButtonState(PAGINATION_STATE.searchQuery);
    updateUrlSearchQuery(PAGINATION_STATE.searchQuery);
  }

  if (input) {
    input.addEventListener('input', () => {
      clearTimeout(searchDebounceTimer);
      const query = input.value;
      if (clearBtn) {
        clearBtn.style.display = query.trim().length > 0 ? 'flex' : 'none';
      }
      updateHeaderShareButtonState(query);
      updateUrlSearchQuery(query);
      searchDebounceTimer = setTimeout(() => {
        PAGINATION_STATE.searchQuery = query;
        if (query.trim().length > 0) {
          PAGINATION_STATE.isMobileSearchOpen = true;
        }
        PAGINATION_STATE.currentPage = 1;
        const container = document.getElementById('rides-container');
        const countBadge = document.getElementById('rides-count-badge');
        renderRides(currentLoadedRides || rides, container, countBadge);
      }, 120);
    });
  }

  const handleClear = () => {
    if (input) input.value = '';
    if (clearBtn) clearBtn.style.display = 'none';
    PAGINATION_STATE.searchQuery = '';
    PAGINATION_STATE.isMobileSearchOpen = false;
    PAGINATION_STATE.currentPage = 1;
    updateHeaderShareButtonState('');
    updateUrlSearchQuery('');
    if (input) input.focus();
    const container = document.getElementById('rides-container');
    const countBadge = document.getElementById('rides-count-badge');
    renderRides(currentLoadedRides || rides, container, countBadge);
  };

  if (clearBtn) clearBtn.addEventListener('click', handleClear);
  if (emptyClearBtn) emptyClearBtn.addEventListener('click', handleClear);

  if (typeof window !== 'undefined' && !window.__btcnjcc_search_popstate_bound) {
    window.__btcnjcc_search_popstate_bound = true;
    window.addEventListener('popstate', () => {
      const urlQuery = getSearchQueryFromUrl();
      const initialMediaMode = typeof resolveInitialMediaViewMode === 'function' ? resolveInitialMediaViewMode() : 'grid';
      let shouldRerender = false;
      if (urlQuery !== PAGINATION_STATE.searchQuery) {
        PAGINATION_STATE.searchQuery = urlQuery;
        PAGINATION_STATE.isMobileSearchOpen = Boolean(urlQuery && urlQuery.trim().length > 0);
        PAGINATION_STATE.currentPage = 1;
        const currentInput = document.getElementById('media-search-input');
        const currentClearBtn = document.getElementById('btn-clear-media-search');
        if (currentInput) {
          currentInput.value = urlQuery;
        }
        if (currentClearBtn) {
          currentClearBtn.style.display = urlQuery.trim().length > 0 ? 'flex' : 'none';
        }
        updateHeaderShareButtonState(urlQuery);
        shouldRerender = true;
      }
      if (initialMediaMode !== PAGINATION_STATE.viewMode) {
        PAGINATION_STATE.viewMode = initialMediaMode;
        shouldRerender = true;
      }
      if (shouldRerender && currentView === 'videos') {
        const container = document.getElementById('rides-container');
        const countBadge = document.getElementById('rides-count-badge');
        renderRides(currentLoadedRides || rides, container, countBadge);
      }
    });
  }

  const mobileToggleBtn = document.getElementById('btn-mobile-search-toggle');
  const collapseBtn = document.getElementById('btn-collapse-mobile-search');
  const cockpitHeader = document.querySelector('.partition-header.integrated-cockpit');

  if (mobileToggleBtn) {
    mobileToggleBtn.addEventListener('click', () => {
      PAGINATION_STATE.isMobileSearchOpen = !PAGINATION_STATE.isMobileSearchOpen;
      if (cockpitHeader) {
        cockpitHeader.classList.toggle('search-expanded', PAGINATION_STATE.isMobileSearchOpen);
      }
      mobileToggleBtn.classList.toggle('active', PAGINATION_STATE.isMobileSearchOpen);
      if (PAGINATION_STATE.isMobileSearchOpen) {
        if (input) {
          input.focus();
        }
      } else {
        if (input && input.value) {
          handleClear();
        } else if (input) {
          input.blur();
        }
      }
    });
  }

  if (collapseBtn) {
    collapseBtn.addEventListener('click', () => {
      PAGINATION_STATE.isMobileSearchOpen = false;
      if (cockpitHeader) {
        cockpitHeader.classList.remove('search-expanded');
      }
      if (mobileToggleBtn) {
        mobileToggleBtn.classList.remove('active');
      }
      if (input && input.value) {
        handleClear();
      } else if (input) {
        input.blur();
      }
    });
  }

  if (select) {
    select.addEventListener('change', () => {
      const val = parseInt(select.value, 10);
      PAGINATION_STATE.cardsPerPage = val;
      PAGINATION_STATE.currentPage = 1;
      try {
        localStorage.setItem('btcnjcc_cards_per_page', String(val));
      } catch (_) {}
      const container = document.getElementById('rides-container');
      const countBadge = document.getElementById('rides-count-badge');
      executeStablePaginationUpdate(() => {
        renderRides(currentLoadedRides || rides, container, countBadge);
      });
    });
  }

  if (paginationDeck) {
    paginationDeck.querySelectorAll('button[data-page]').forEach(btn => {
      btn.addEventListener('click', () => {
        const targetPage = parseInt(btn.getAttribute('data-page'), 10);
        if (!isNaN(targetPage)) {
          PAGINATION_STATE.currentPage = targetPage;
          const container = document.getElementById('rides-container');
          const countBadge = document.getElementById('rides-count-badge');
          executeStablePaginationUpdate(() => {
            renderRides(currentLoadedRides || rides, container, countBadge);
          });
        }
      });
    });
  }

  if (!isSearchCockpitBound) {
    isSearchCockpitBound = true;
    window.addEventListener('keydown', (e) => {
      const activeInput = document.getElementById('media-search-input');
      if (e.key === '/' && document.activeElement !== activeInput && !['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        PAGINATION_STATE.isMobileSearchOpen = true;
        const cockpit = document.querySelector('.partition-header.integrated-cockpit');
        if (cockpit) cockpit.classList.add('search-expanded');
        const toggle = document.getElementById('btn-mobile-search-toggle');
        if (toggle) toggle.classList.add('active');
        if (activeInput) {
          activeInput.focus();
          activeInput.select();
        }
      } else if (e.key === 'Escape') {
        if (document.body.classList.contains('modal-open') || document.querySelector('.lightbox-modal.active') || document.querySelector('.share-qr-modal.active')) {
          return;
        }
        if (document.activeElement === activeInput) {
          if (activeInput && activeInput.value) {
            handleClear();
          }
          PAGINATION_STATE.isMobileSearchOpen = false;
          const cockpit = document.querySelector('.partition-header.integrated-cockpit');
          if (cockpit) cockpit.classList.remove('search-expanded');
          const toggle = document.getElementById('btn-mobile-search-toggle');
          if (toggle) toggle.classList.remove('active');
          if (activeInput) activeInput.blur();
        }
      }
    });
  }
}

export function renderRides(rides, container, countBadge) {
  if (!container) return;

  if (!rides || rides.length === 0) {
    if (countBadge) countBadge.textContent = '0 rides with media';
    container.innerHTML = `
      <section class="content-panel loading-frame">
        <div class="loading-state">
          <span>No media-associated rides found.</span>
        </div>
      </section>
    `;
    return;
  }

  const { btcnjRides, otherRides } = partitionRides(rides);

  // Filter BTCNJ rides by search query (including native year search)
  let filteredBtcnjRides = btcnjRides;
  if (PAGINATION_STATE.searchQuery && PAGINATION_STATE.searchQuery.trim().length > 0) {
    filteredBtcnjRides = filteredBtcnjRides.filter(r => matchesRideSearch(r, PAGINATION_STATE.searchQuery));
  }

  // Filter Other Media by search query
  let filteredOtherRides = otherRides;
  if (PAGINATION_STATE.searchQuery && PAGINATION_STATE.searchQuery.trim().length > 0) {
    filteredOtherRides = filteredOtherRides.filter(r => matchesRideSearch(r, PAGINATION_STATE.searchQuery));
  }

  const isSearchActive = Boolean(PAGINATION_STATE.searchQuery && PAGINATION_STATE.searchQuery.trim().length > 0);
  const isSearchExpanded = Boolean(PAGINATION_STATE.isMobileSearchOpen || isSearchActive);

  // Update header count badge
  if (countBadge) {
    if (isSearchActive) {
      countBadge.textContent = `${filteredBtcnjRides.length} matching rides`;
    } else {
      countBadge.textContent = `${rides.length} rides with media`;
    }
  }

  let partitionsHtml = '';

  // 1. Frame 1: Official BTCNJ Club Rides
  if (filteredBtcnjRides.length > 0 || isSearchActive) {
    let bodyHtml = '';
    let paginationDeckHtml = '';
    const isGrid = PAGINATION_STATE.viewMode !== 'list';

    if (filteredBtcnjRides.length > 0) {
      // Build smart chronological items (ascending future -> divider -> descending past)
      const allGridItems = buildSmartChronologicalGridItems(filteredBtcnjRides, {
        isSearchActive,
        currentYear: '2026'
      });

      // Pagination slicing
      const totalItems = allGridItems.length;
      const perPage = PAGINATION_STATE.cardsPerPage;
      let displayedItems = [];
      let totalPages = 1;

      if (perPage === -1 || isSearchActive) {
        displayedItems = allGridItems;
        totalPages = 1;
      } else {
        totalPages = Math.max(1, Math.ceil(totalItems / perPage));
        if (PAGINATION_STATE.currentPage > totalPages) {
          PAGINATION_STATE.currentPage = totalPages;
        }
        if (PAGINATION_STATE.currentPage < 1) {
          PAGINATION_STATE.currentPage = 1;
        }
        const startIndex = (PAGINATION_STATE.currentPage - 1) * perPage;
        displayedItems = allGridItems.slice(startIndex, startIndex + perPage);
      }

      const cardsHtml = displayedItems.map((item) => {
        if (item.isHistoricalDivider) {
          return isGrid ? renderHistoricalDividerCardHtml(item) : renderMediaHistoricalDividerListHtml(item);
        }
        if (item.isMilestone) {
          return isGrid ? renderYearMilestoneCardHtml(item) : renderMediaYearMilestoneListHtml(item);
        }
        return isGrid ? renderRideCardHtml(item.ride) : renderMediaListRowHtml(item.ride);
      }).join('');

      bodyHtml = `
        <div class="${isGrid ? 'media-rides-grid btcnj-rides-grid' : 'media-rides-list btcnj-rides-list'}" role="list">
          ${cardsHtml}
        </div>
      `;

      // Google-inspired bottom pagination HUD with telegraphic [ 30 ▾ ] combo selector
      if (!isSearchActive && totalPages > 1) {
        const currentPage = PAGINATION_STATE.currentPage;
        const hasPrev = currentPage > 1;
        const hasNext = currentPage < totalPages;

        let pageNumbersHtml = '';
        let startPage = Math.max(1, currentPage - 2);
        let endPage = Math.min(totalPages, startPage + 4);
        if (endPage - startPage < 4) {
          startPage = Math.max(1, endPage - 4);
        }

        for (let p = startPage; p <= endPage; p++) {
          const isActive = p === currentPage;
          pageNumbersHtml += `
            <button type="button" class="page-num-btn ${isActive ? 'active' : ''}" data-page="${p}" ${isActive ? 'aria-current="page"' : ''}>${p}</button>
          `;
        }

        paginationDeckHtml = `
          <div class="feed-pagination-deck" id="feed-pagination-deck" role="navigation" aria-label="Ride Feed Pagination">
            <div class="pagination-pages-group">
              <button type="button" class="page-nav-btn btn-prev" data-page="${currentPage - 1}" ${!hasPrev ? 'disabled style="opacity: 0.35; pointer-events: none;"' : ''} aria-label="Previous Page">‹ Prev</button>
              ${pageNumbersHtml}
              <button type="button" class="page-nav-btn btn-next" data-page="${currentPage + 1}" ${!hasNext ? 'disabled style="opacity: 0.35; pointer-events: none;"' : ''} aria-label="Next Page">Next ›</button>
            </div>
            <div class="pagination-combo-group">
              <select id="cards-per-page-select" class="combo-select telegraphic-select" aria-label="Cards per page" title="Cards per page">
                <option value="15" ${perPage === 15 ? 'selected' : ''}>15</option>
                <option value="30" ${perPage === 30 ? 'selected' : ''}>30</option>
                <option value="60" ${perPage === 60 ? 'selected' : ''}>60</option>
                <option value="-1" ${perPage === -1 ? 'selected' : ''}>All</option>
              </select>
            </div>
          </div>
        `;
      } else if (!isSearchActive && totalItems > 0) {
        paginationDeckHtml = `
          <div class="feed-pagination-deck single-page-deck" id="feed-pagination-deck" role="navigation" aria-label="Ride Feed Pagination">
            <div class="pagination-single-note">
              <span>Showing all items</span>
            </div>
            <div class="pagination-combo-group">
              <select id="cards-per-page-select" class="combo-select telegraphic-select" aria-label="Cards per page" title="Cards per page">
                <option value="15" ${perPage === 15 ? 'selected' : ''}>15</option>
                <option value="30" ${perPage === 30 ? 'selected' : ''}>30</option>
                <option value="60" ${perPage === 60 ? 'selected' : ''}>60</option>
                <option value="-1" ${perPage === -1 ? 'selected' : ''}>All</option>
              </select>
            </div>
          </div>
        `;
      }
    } else {
      // Empty search state
      bodyHtml = `
        <div class="search-empty-state">
          <div class="empty-icon">🔍</div>
          <div class="empty-title">No matching rides found</div>
          <div class="empty-subtitle">Try searching for another ride title, leader, member, or year.</div>
          <button type="button" id="btn-reset-search-empty" class="btn-reset-search">Clear Search</button>
        </div>
      `;
    }

    partitionsHtml += `
      <section class="content-panel media-frame frame-btcnj" id="partition-btcnj" aria-label="BTCNJ Media Center">
        <div class="panel-header partition-header integrated-cockpit ${isSearchExpanded ? 'search-expanded' : ''}">
          <div class="partition-title-group">
            <span class="panel-tag tag-cyan partition-tag">CLUB</span>
            <h2 class="panel-heading partition-heading">Media Center</h2>
          </div>

          <!-- Integrated Aerodynamic Search Input -->
          <div class="header-search-wrapper">
            <span class="header-search-icon" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </span>
            <input
              type="text"
              id="media-search-input"
              class="header-search-input"
              placeholder="Search rides, leads, year..."
              value="${escapeHtml(PAGINATION_STATE.searchQuery)}"
              autocomplete="off"
              spellcheck="false"
              aria-label="Search rides by title, leader, member, or year"
            />
            <button
              type="button"
              id="btn-clear-media-search"
              class="header-search-clear-btn"
              aria-label="Clear search text"
              title="Clear search"
              style="${PAGINATION_STATE.searchQuery ? '' : 'display: none;'}"
            >✕</button>
            <button
              type="button"
              id="btn-collapse-mobile-search"
              class="header-search-collapse-btn"
              aria-label="Close search"
              title="Close search"
            >✕</button>
          </div>

          <div class="panel-controls partition-controls">
            <!-- View Mode Switcher (Grid / List) -->
            <div class="media-view-switch-group" role="group" aria-label="View Mode Switcher">
              <button type="button" id="btn-media-mode-grid" class="media-view-mode-btn ${isGrid ? 'active' : ''}" title="Visual Card Grid" aria-pressed="${isGrid}">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M4 4h4v4H4zm6 0h4v4h-4zm6 0h4v4h-4zM4 10h4v4H4zm6 0h4v4h-4zm6 0h4v4h-4zM4 16h4v4H4zm6 0h4v4h-4zm6 0h4v4h-4z"/></svg>
                <span class="btn-text">Grid</span>
              </button>
              <button type="button" id="btn-media-mode-list" class="media-view-mode-btn ${!isGrid ? 'active' : ''}" title="Compact List" aria-pressed="${!isGrid}">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                <span class="btn-text">List</span>
              </button>
            </div>

            <button
              type="button"
              id="btn-mobile-search-toggle"
              class="btn-mobile-search-toggle ${isSearchExpanded ? 'active' : ''}"
              aria-label="Toggle search"
              title="Toggle search"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </button>
            <span class="partition-count-pill" id="feed-count-pill" title="${filteredBtcnjRides.length} Rides">
              <span class="count-num">${filteredBtcnjRides.length}</span><span class="count-unit"> Rides</span>
            </span>
            <button id="refresh-feed-btn" class="refresh-btn" type="button" aria-label="Sync ride feed" title="Sync ride feed">
              <svg class="refresh-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                <path d="M21 3v5h-5"/>
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                <path d="M3 21v-5h5"/>
              </svg>
              <span class="refresh-label">Sync</span>
            </button>
          </div>
        </div>

        ${bodyHtml}
        ${paginationDeckHtml}
      </section>
    `;
  }

  // 2. Frame 2: Other Media (Accordion)
  if (filteredOtherRides.length > 0) {
    const isGrid = PAGINATION_STATE.viewMode !== 'list';
    const shouldOpenMemberMedia = Boolean(isSearchActive && filteredOtherRides.length > 0);
    const otherCardsHtml = filteredOtherRides.map(r => isGrid ? renderRideCardHtml(r) : renderMediaListRowHtml(r)).join('');
    partitionsHtml += `
      <section class="content-panel media-frame frame-other" id="partition-other" aria-label="Community Member Media">
        <details class="other-media-accordion" id="other-media-accordion"${shouldOpenMemberMedia ? ' open' : ''}>
          <summary class="panel-header partition-header partition-summary-header" title="Click to toggle community member media">
            <div class="panel-title-wrap partition-title-group">
              <span class="panel-tag tag-amber partition-tag">COMMUNITY</span>
              <h2 class="panel-heading partition-heading">Member Media</h2>
            </div>
            <div class="partition-summary-actions">
              <span class="partition-count-pill" title="${filteredOtherRides.length} Items">
                <span class="count-num">${filteredOtherRides.length}</span><span class="count-unit"> Items</span>
              </span>
              <span class="accordion-toggle-chip" aria-hidden="true">
                <span class="toggle-text">Show Media</span>
                <svg class="toggle-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </span>
            </div>
          </summary>
          <div class="accordion-content-panel">
            <div class="${isGrid ? 'media-rides-grid other-rides-grid' : 'media-rides-list other-rides-list'}" role="list">
              ${otherCardsHtml}
            </div>
          </div>
        </details>
      </section>
    `;
  }

  // Preserve search input focus and cursor state across re-renders
  const activeEl = typeof document !== 'undefined' ? document.activeElement : null;
  const isInputActive = activeEl && activeEl.id === 'media-search-input';
  const cursorStart = isInputActive ? activeEl.selectionStart : null;
  const cursorEnd = isInputActive ? activeEl.selectionEnd : null;

  container.innerHTML = partitionsHtml;

  if (isInputActive) {
    const newInput = document.getElementById('media-search-input');
    if (newInput) {
      newInput.focus();
      if (cursorStart !== null && cursorEnd !== null) {
        try { newInput.setSelectionRange(cursorStart, cursorEnd); } catch (_) {}
      }
    }
  }

  // Toggle label sync on accordion open/close
  const accordion = typeof container.querySelector === 'function'
    ? container.querySelector('#other-media-accordion')
    : null;
  if (accordion) {
    accordion.addEventListener('toggle', () => {
      const toggleText = accordion.querySelector('.toggle-text');
      if (toggleText) {
        toggleText.textContent = accordion.open ? 'Hide Media' : 'Show Media';
      }
    });
  }

  bindRideCardInteractions(container, rides);
  initSearchCockpit(rides);
}

/**
 * Launches the primary hero media for a ride (Priority 1: YouTube Lightbox, Priority 2: Google Photos).
 * Enforces non-blocking JIT SWR media refresh and consistent behavior across button clicks and preview clicks.
 * @param {object} ride
 * @param {object} [options]
 * @returns {boolean} Whether primary media was launched
 */
export function launchRidePrimaryMedia(ride, options = {}) {
  if (!ride) return false;

  // 1. Guaranteed Dual-Media JIT SWR Sync
  if (typeof proactiveSyncRideMedia === 'function') {
    proactiveSyncRideMedia(ride, true);
  }

  const hasVideos = Array.isArray(ride.matchedVideos) && ride.matchedVideos.length > 0;
  const hasPhotos = Boolean(ride.photoAlbumUrl && String(ride.photoAlbumUrl).trim().length > 0);

  // 2. Priority 1: YouTube Video Lightbox
  if (hasVideos) {
    if (typeof openVideoLightbox === 'function') {
      openVideoLightbox(ride.matchedVideos, options.videoIndex || 0, ride);
      return true;
    }
  }

  // 3. Priority 2: Google Photos Album (New Tab)
  if (hasPhotos) {
    if (typeof window !== 'undefined' && typeof window.open === 'function') {
      window.open(ride.photoAlbumUrl, '_blank', 'noopener,noreferrer');
      return true;
    }
  }

  return false;
}

export function bindRideCardInteractions(container, rides) {
  if (!container || !rides) return;

  // 0. Bind Media Center Grid/List Switcher
  const gridBtn = container.querySelector('#btn-media-mode-grid');
  const listBtn = container.querySelector('#btn-media-mode-list');
  if (gridBtn) {
    gridBtn.addEventListener('click', () => {
      PAGINATION_STATE.viewMode = 'grid';
      try { localStorage.setItem('btcnjcc_media_view_mode', 'grid'); } catch (_) {}
      syncMediaViewModeUrl('grid');
      const countBadge = document.getElementById('rides-count-badge');
      renderRides(currentLoadedRides || rides, container, countBadge);
    });
  }
  if (listBtn) {
    listBtn.addEventListener('click', () => {
      PAGINATION_STATE.viewMode = 'list';
      try { localStorage.setItem('btcnjcc_media_view_mode', 'list'); } catch (_) {}
      syncMediaViewModeUrl('list');
      const countBadge = document.getElementById('rides-count-badge');
      renderRides(currentLoadedRides || rides, container, countBadge);
    });
  }

  // 0b. Bind Media List Rows (Spotlight launcher & keyboard accessibility)
  container.querySelectorAll('.media-list-row').forEach(row => {
    row.addEventListener('click', (e) => {
      if (e.target.closest('a, button, .btn, .media-list-actions')) return;
      const rideId = row.getAttribute('data-ride-id');
      const cleanRideId = rideId ? String(rideId).replace(/^btcnj-/, '').trim() : '';
      const ride = rides.find(r => r.id === rideId || r.rideId === rideId || `btcnj-${r.rideId}` === rideId || String(r.rideId || '').trim() === cleanRideId);
      if (ride) {
        openRideSpotlight(ride, rides);
      }
    });
    row.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        if (e.target.closest('a, button, .btn, .media-list-actions')) return;
        e.preventDefault();
        const rideId = row.getAttribute('data-ride-id');
        const cleanRideId = rideId ? String(rideId).replace(/^btcnj-/, '').trim() : '';
        const ride = rides.find(r => r.id === rideId || r.rideId === rideId || `btcnj-${r.rideId}` === rideId || String(r.rideId || '').trim() === cleanRideId);
        if (ride) {
          openRideSpotlight(ride, rides);
        }
      }
    });
  });

  // 0c. Bind List Row Video Play button
  container.querySelectorAll('button[data-launch="video-lightbox"]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const rideId = btn.getAttribute('data-ride-id');
      const cleanRideId = rideId ? String(rideId).replace(/^btcnj-/, '').trim() : '';
      const ride = rides.find(r => r.id === rideId || r.rideId === rideId || `btcnj-${r.rideId}` === rideId || String(r.rideId || '').trim() === cleanRideId);
      if (ride) {
        proactiveSyncRideMedia(ride, true);
        if (Array.isArray(ride.matchedVideos) && ride.matchedVideos.length > 0) {
          openVideoLightbox(ride.matchedVideos, 0, ride);
        }
      }
    });
  });

  // 0d. Bind List Row Album button
  container.querySelectorAll('a.btn-list-album').forEach(link => {
    link.addEventListener('click', (e) => {
      e.stopPropagation();
      const rideId = link.getAttribute('data-ride-id');
      const cleanRideId = rideId ? String(rideId).replace(/^btcnj-/, '').trim() : '';
      const ride = rides.find(r => r.id === rideId || r.rideId === rideId || `btcnj-${r.rideId}` === rideId || String(r.rideId || '').trim() === cleanRideId);
      if (ride) {
        proactiveSyncRideMedia(ride, true);
      }
    });
  });

  // 1. Bind matched videos section to launch Video 1 in lightbox + JIT SWR media refresh
  container.querySelectorAll('.matched-videos-section').forEach(section => {
    section.addEventListener('click', (e) => {
      e.stopPropagation();
      const rideId = section.getAttribute('data-ride-id') || section.closest('.media-ride-card')?.getAttribute('data-ride-id');
      const cleanRideId = rideId ? String(rideId).replace(/^btcnj-/, '').trim() : '';
      const ride = rides.find(r => r.id === rideId || r.rideId === rideId || `btcnj-${r.rideId}` === rideId || String(r.rideId || '').trim() === cleanRideId);
      if (ride) {
        proactiveSyncRideMedia(ride, true);
        if (Array.isArray(ride.matchedVideos) && ride.matchedVideos.length > 0) {
          openVideoLightbox(ride.matchedVideos, 0, ride);
        }
      }
    });
    section.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        section.click();
      }
    });
  });

  // 1b. Legacy video chip button support (if present in custom/injected HTML)
  container.querySelectorAll('.video-chip-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const rideId = btn.getAttribute('data-ride-id');
      const cleanRideId = rideId ? String(rideId).replace(/^btcnj-/, '').trim() : '';
      const ride = rides.find(r => r.id === rideId || r.rideId === rideId || `btcnj-${r.rideId}` === rideId || String(r.rideId || '').trim() === cleanRideId);
      if (ride) {
        proactiveSyncRideMedia(ride, true);
      }
      const videoIdx = parseInt(btn.getAttribute('data-video-idx') || '0', 10);
      const playlist = (ride && ride.matchedVideos && ride.matchedVideos.length > 0)
        ? ride.matchedVideos
        : [{ videoId: btn.getAttribute('data-videoid'), title: btn.getAttribute('data-videotitle') }];
      const contextRide = ride || {
        title: btn.getAttribute('data-ride-title') || '',
        dateIso: btn.getAttribute('data-ride-date') || '',
        category: btn.getAttribute('data-ride-category') || null,
        miles: btn.getAttribute('data-ride-miles') || null,
        climbFt: btn.getAttribute('data-ride-climb') ? parseFloat(btn.getAttribute('data-ride-climb')) : null
      };
      openVideoLightbox(playlist, videoIdx, contextRide);
    });
  });

  // 2. Bind Google Photos direct links (triggers non-blocking JIT SWR media refresh)
  container.querySelectorAll('a.media-photo-spotlight').forEach(link => {
    link.addEventListener('click', (e) => {
      e.stopPropagation();
      const rideId = link.getAttribute('data-ride-id') || link.closest('.media-ride-card')?.getAttribute('data-ride-id');
      const cleanRideId = rideId ? String(rideId).replace(/^btcnj-/, '').trim() : '';
      const ride = rides.find(r => r.id === rideId || r.rideId === rideId || `btcnj-${r.rideId}` === rideId || String(r.rideId || '').trim() === cleanRideId);
      if (ride) {
        proactiveSyncRideMedia(ride, true);
      }
    });
  });

  // 3. Bind RWGPS buttons to in-app interactive map modal launcher + JIT SWR media refresh
  container.querySelectorAll('button[data-launch="rwgps-modal"]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const rideId = btn.getAttribute('data-ride-id');
      const cleanRideId = rideId ? String(rideId).replace(/^btcnj-/, '').trim() : '';
      const ride = rides.find(r => r.id === rideId || r.rideId === rideId || `btcnj-${r.rideId}` === rideId || String(r.rideId || '').trim() === cleanRideId);
      if (ride) {
        proactiveSyncRideMedia(ride, true);
        if (ride.rwgpsUrl) {
          openRwgpsLightbox(ride);
        }
      }
    });
  });

  // 3b. Bind Spotlight RWGPS Bay Click Overlay (Launches full interactive route map)
  container.querySelectorAll('.spotlight-rwgps-click-overlay').forEach(overlay => {
    const handleLaunch = (e) => {
      e.stopPropagation();
      const rideId = overlay.getAttribute('data-ride-id');
      const cleanRideId = rideId ? String(rideId).replace(/^btcnj-/, '').trim() : '';
      const ride = rides.find(r => r.id === rideId || r.rideId === rideId || `btcnj-${r.rideId}` === rideId || String(r.rideId || '').trim() === cleanRideId);
      if (ride && ride.rwgpsUrl) {
        openRwgpsLightbox(ride);
      }
    };
    overlay.addEventListener('click', handleLaunch);
    overlay.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleLaunch(e);
      }
    });
  });

  // Global fallback delegation for dynamically rendered or transitioned Spotlight RWGPS click overlays
  if (typeof document !== 'undefined' && !document._hasRwgpsOverlayDelegation) {
    document._hasRwgpsOverlayDelegation = true;
    document.addEventListener('click', (e) => {
      const overlay = e.target.closest('.spotlight-rwgps-click-overlay');
      if (overlay) {
        e.stopPropagation();
        const rideId = overlay.getAttribute('data-ride-id');
        const cleanRideId = rideId ? String(rideId).replace(/^btcnj-/, '').trim() : '';
        const allRides = currentLoadedRides || getCachedMediaRides() || [];
        const ride = allRides.find(r => r.id === rideId || r.rideId === rideId || `btcnj-${r.rideId}` === rideId || String(r.rideId || '').trim() === cleanRideId);
        if (ride && ride.rwgpsUrl) {
          openRwgpsLightbox(ride);
        }
        return;
      }
    });
  }

  // 4. Bind BTCNJ Portal badges/links + R5m links to trigger JIT SWR media refresh
  container.querySelectorAll('[data-launch="btcnj-portal"]').forEach(el => {
    el.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const cardEl = el.closest('.media-ride-card, .ride-card');
      const rideId = el.getAttribute('data-ride-id') || cardEl?.getAttribute('data-ride-id');
      const cleanRideId = rideId ? String(rideId).replace(/^btcnj-/, '').trim() : '';
      const ride = rides.find(r => r.id === rideId || r.rideId === rideId || `btcnj-${r.rideId}` === rideId || String(r.rideId || '').trim() === cleanRideId);
      if (ride) {
        proactiveSyncRideMedia(ride, true);
      }
      const url = el.getAttribute('data-portal-url') || el.getAttribute('href');
      const title = el.getAttribute('data-portal-title') || 'Official BTCNJ Portal';
      if (url) {
        openBtcnjPortalLightbox(url, title);
      }
    });
  });

  container.querySelectorAll('a[data-launch="btcnj-ride"], a[data-launch="r5m-route"]').forEach(link => {
    link.addEventListener('click', () => {
      const cardEl = link.closest('.media-ride-card, .ride-card');
      const rideId = cardEl?.getAttribute('data-ride-id') || cardEl?.getAttribute('data-card-id');
      const cleanRideId = rideId ? String(rideId).replace(/^btcnj-/, '').trim() : '';
      const ride = rides.find(r => r.id === rideId || r.rideId === rideId || `btcnj-${r.rideId}` === rideId || String(r.rideId || '').trim() === cleanRideId);
      if (ride) {
        proactiveSyncRideMedia(ride, true);
      }
    });
  });

  // 5. Bind Admin micro-dock buttons (Videos, Convert Ride, Photo Album)
  container.querySelectorAll('.admin-corner-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      const action = btn.getAttribute('data-action');
      if (action === 'manage-videos') {
        e.preventDefault();
        e.stopPropagation();
        const rideId = btn.getAttribute('data-ride-id') || btn.getAttribute('data-card-id');
        openRideVideoManager(rideId);
        return;
      }
      if (action === 'convert-ride') {
        e.preventDefault();
        e.stopPropagation();
        const cardId = btn.getAttribute('data-card-id') || btn.getAttribute('data-ride-id');
        openAdminConvertRideModal(cardId);
        return;
      }
      if (action === 'manage-album') {
        e.preventDefault();
        e.stopPropagation();
        const cardId = btn.getAttribute('data-card-id') || btn.getAttribute('data-ride-id');
        openAdminAlbumModal(cardId);
        return;
      }
    });
  });

  // 5b. Bind Cockpit Accordion Triggers to expand/collapse 3-button drawer + JIT SWR media refresh
  container.querySelectorAll('.cockpit-accordion-trigger').forEach(trigger => {
    const handleToggle = (e) => {
      if (e.target.closest('a, button:not(.cockpit-accordion-trigger)')) return;
      e.stopPropagation();
      const dock = trigger.closest('.ride-cockpit-dock');
      const cardEl = trigger.closest('.media-ride-card, .ride-card');
      const rideId = cardEl?.getAttribute('data-ride-id') || cardEl?.getAttribute('data-card-id');
      const cleanRideId = rideId ? String(rideId).replace(/^btcnj-/, '').trim() : '';
      const ride = rides.find(r => r.id === rideId || r.rideId === rideId || `btcnj-${r.rideId}` === rideId || String(r.rideId || '').trim() === cleanRideId);
      if (ride) {
        proactiveSyncRideMedia(ride, true);
      }
      if (dock) {
        const isExpanded = dock.classList.toggle('is-expanded');
        trigger.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      }
      // Release mouse focus so color reverts immediately to baseline when cursor leaves
      if (e.type === 'click') {
        trigger.blur();
      }
    };
    trigger.addEventListener('click', handleToggle);
    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleToggle(e);
      }
    });
  });

  // Enable expanding by tapping anywhere on the dock frame when collapsed + JIT SWR media refresh
  container.querySelectorAll('.ride-cockpit-dock').forEach(dock => {
    dock.addEventListener('click', (e) => {
      if (e.target.closest('a, button, .cockpit-accordion-trigger')) return;
      if (!dock.classList.contains('is-expanded')) {
        e.stopPropagation();
        const cardEl = dock.closest('.media-ride-card, .ride-card');
        const rideId = cardEl?.getAttribute('data-ride-id') || cardEl?.getAttribute('data-card-id');
        const cleanRideId = rideId ? String(rideId).replace(/^btcnj-/, '').trim() : '';
        const ride = rides.find(r => r.id === rideId || r.rideId === rideId || `btcnj-${r.rideId}` === rideId || String(r.rideId || '').trim() === cleanRideId);
        if (ride) {
          proactiveSyncRideMedia(ride, true);
        }
        dock.classList.add('is-expanded');
        const trigger = dock.querySelector('.cockpit-accordion-trigger');
        if (trigger) {
          trigger.setAttribute('aria-expanded', 'true');
          trigger.blur();
        }
        dock.blur();
      }
    });
  });

  // Background Firebase DB Hydration for BTCNJ rides missing route telemetry or roster
  if (typeof window !== 'undefined' && typeof fetch === 'function' && typeof container.querySelector === 'function') {
    rides.forEach(ride => {
      const isBtcnj = Boolean(ride.isBtcnjRide || (ride.rideId && String(ride.rideId).trim().length > 0));
      const needsHydration = isBtcnj && ride.rideId && (!ride.rwgpsUrl || (!ride.timeRaw && !ride.time24) || !Array.isArray(ride.members) || ride.members.length === 0 || !Array.isArray(ride.leaders) || ride.leaders.length === 0);
      if (needsHydration) {
        fetchRideFromFirebaseDb(ride.rideId).then(fbRide => {
          if (fbRide) {
            if (fbRide.rwgpsUrl && !ride.rwgpsUrl) ride.rwgpsUrl = fbRide.rwgpsUrl;
            if (!ride.miles && fbRide.miles) ride.miles = fbRide.miles;
            if (!ride.climbFt && fbRide.climbFt) ride.climbFt = fbRide.climbFt;
            if (!ride.category && fbRide.category) ride.category = fbRide.category;
            if (!ride.timeRaw && fbRide.timeRaw) ride.timeRaw = fbRide.timeRaw;
            if ((!ride.leaders || ride.leaders.length === 0) && Array.isArray(fbRide.leaders) && fbRide.leaders.length > 0) {
              ride.leaders = fbRide.leaders;
            }
            if ((!ride.members || ride.members.length === 0) && Array.isArray(fbRide.members) && fbRide.members.length > 0) {
              ride.members = fbRide.members;
            }
            ride.r5mUrl = buildR5mRideUrl(ride);

            const cardEl = container.querySelector(`article[data-ride-id="${ride.id}"]`);
            if (cardEl) {
              const r5mBtn = cardEl.querySelector('a[data-launch="r5m-route"]');
              if (r5mBtn) {
                r5mBtn.href = ride.r5mUrl;
              }
              const actionGroup = cardEl.querySelector('.card-footer-actions') || cardEl.querySelector('.card-actions-group');
              if (actionGroup && !actionGroup.querySelector('[data-launch="rwgps-modal"]')) {
                const rwgpsBtn = document.createElement('button');
                rwgpsBtn.type = 'button';
                rwgpsBtn.className = 'btn btn-secondary';
                rwgpsBtn.setAttribute('data-launch', 'rwgps-modal');
                rwgpsBtn.setAttribute('data-ride-id', ride.id);
                rwgpsBtn.title = 'Open Interactive RideWithGPS Route Map';
                rwgpsBtn.innerHTML = `
                  <svg class="btn-icon" width="16" height="12" viewBox="0 0 49.6 35.1" fill="none" aria-hidden="true">
                    <path fill="#F96400" d="M15.2,9.9c0,1.4,0.2,3.1,4,5.4c2.7,1.6,5.2,4.4,5.2,5.4s-4.3,5.8-6.4,8c-0.2,0.2-0.4,0.4-0.7,0.6c-1.6,1.7-3.8,2.6-6,2.5c-4.4-0.1-7.9-3.8-7.8-8.2c0-2.7,1.3-5.2,3.4-6.7c1.2-0.8,2.6-1.3,4-1.5c0.9-0.1,1.9-0.1,2.8,0.2l0.2,0.1c0-0.9-1.7-1.6-2.6-1.7c-0.7-0.1-1.5-0.1-2.2,0C3.8,14.6-0.1,19.2,0,24.5c0,5.9,5.4,10.6,11,10.6c0.7,0,1.3,0,1.9-0.1c3.2-0.2,6.1-1.4,11.2-7.4c0.5-0.6,3.5-4,4.9-5.7l0.1-0.1l0,0c0.2-0.3,0.4-0.5,0.6-0.8l0.2-0.2c1.2-1.3,2.5-2.4,4-3.3c1.4-0.7,3-1,4.5-0.8c4.8,0.4,7.9,3.3,7.9,7.7s-3.9,8-8.7,8c-2.4-0.2-4.8-1.2-6.5-2.8L31,29.4c-0.9,1.5,2.4,5.6,7.7,5.6c0.1,0,0.2,0,0.3,0c5.9,0,10.6-4.8,10.6-10.7c-0.1-5.8-4.7-10.5-10.5-10.6c-5.2-0.5-8.5,2.4-9.4,2.5c-0.7,0.1-8.2-5-8.9-6.1c-0.3-0.5,0.1-1.9,5-2.3c2.2,2.4,4.5,4.2,8.8,4.2c2.1-0.1,4.2-0.7,6.1-1.8C41.7,9.5,41.3,9,41.3,9s0,0.4-1.4,0.7c-1.6,0.4-3.2,0.5-4.9,0.3C34.2,10,29.7,9.7,28.2,4C21.9,3.4,15.2,5.6,15.2,9.9z"/>
                    <path fill="#F96400" d="M37.8,3.8c-0.1-2.1-2.2-3.3-4-3.6c-3.8-0.6-7.6,0.5-8.7,2.4c0,0,0.4-0.2,1-0.3C27.5,1.8,29,2,30.3,2.7c0.4,0.2,0.6,0.6,0.6,1.1c0,1.9,1.6,3.5,3.5,3.5c0.1,0,0.1,0,0.2,0h0C36.4,7.2,37.9,5.6,37.8,3.8z"/>
                  </svg>
                  <span>RWGPS</span>
                `;
                rwgpsBtn.addEventListener('click', (e) => {
                  e.stopPropagation();
                  openRwgpsLightbox(ride);
                });
                const r5mAnchor = actionGroup.querySelector('a[data-launch="r5m-route"]');
                if (r5mAnchor) {
                  r5mAnchor.insertAdjacentElement('beforebegin', rwgpsBtn);
                } else {
                  actionGroup.appendChild(rwgpsBtn);
                }
              }
              const footerEl = cardEl.querySelector('.card-ride-footer');
              if (footerEl) {
                const newFooter = buildRideFooter(ride);
                footerEl.innerHTML = newFooter;
                footerEl.title = newFooter.replace(/<[^>]+>/g, '');
              }
              const pillWrap = cardEl.querySelector('.card-pill-wrap');
              if (pillWrap) {
                pillWrap.innerHTML = formatR5mPill(ride);
              }
            }
          }
        }).catch(err => {
          console.warn(`[Firebase DB Hydration] Error hydrating ride ${ride.rideId}:`, err);
        });
      }
    });
  }

  // 6. Bind In-Place Desktop Card Hover Media Previews (YouTube Pattern)
  if (typeof bindCardHoverPreviews === 'function') {
    bindCardHoverPreviews(container, rides);
  }

  // 7. Synchronize Historical Divider Card Live Clock
  if (typeof initHistoricalClockSync === 'function') {
    initHistoricalClockSync(container);
  }
}

// ============================================================================
// 6. Card Hover Media Preview System (YouTube In-Place Viewport Pattern)
// ============================================================================

export const PREVIEW_SESSION_CACHE = new Map();
export const ITEM_PREVIEW_DURATION_MS = 5000;
export let activePreviewCard = null;

export function shuffleArray(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export function ensureYouTubePreconnect() {
  if (typeof document === 'undefined' || document._ytPreconnected) return;
  document._ytPreconnected = true;
  ['https://www.youtube-nocookie.com', 'https://i.ytimg.com', 'https://lh3.googleusercontent.com'].forEach(origin => {
    try {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = origin;
      document.head.appendChild(link);
    } catch (_) {}
  });
}

export function findRideByCardId(rides, cardOrRideId) {
  if (!Array.isArray(rides) || !cardOrRideId) return null;
  const targetId = String(cardOrRideId).trim();
  if (!targetId) return null;
  const cleanTarget = targetId.replace(/^btcnj-/, '');

  return rides.find(r => {
    if (!r) return false;
    const rId = r.id != null ? String(r.id).trim() : '';
    const rRideId = r.rideId != null ? String(r.rideId).trim() : '';
    const cleanRId = rId.replace(/^btcnj-/, '');
    const cleanRRideId = rRideId.replace(/^btcnj-/, '');

    return (rId && (rId === targetId || cleanRId === cleanTarget)) ||
           (rRideId && (rRideId === targetId || cleanRRideId === cleanTarget));
  }) || null;
}

export function buildCardMediaPlaylist(ride) {
  if (!ride) return [];
  if (Array.isArray(ride.playlist) && ride.playlist.length > 0) {
    return ride.playlist;
  }
  const rawPhotos = [];
  const videos = [];
  const seenUrls = new Set();
  const covers = typeof ALBUM_COVERS_MAP !== 'undefined' ? ALBUM_COVERS_MAP : {};

  // 1. Extract cover photo (High-DPI Retina Resolution =w800-h450-p-k)
  let rawCover = ride.coverPhotoUrl
    || (ride.dateIso && covers[ride.dateIso])
    || (ride.dateStr && covers[ride.dateStr.match(/\d{4}-\d{2}-\d{2}/)?.[0]]);

  if (rawCover && typeof rawCover === 'string' && !rawCover.startsWith('data:image/svg')) {
    const highDpiCover = rawCover.includes('googleusercontent.com')
      ? (rawCover.includes('=') ? rawCover.replace(/=.*$/, '=w1920-h1080') : `${rawCover}=w1920-h1080`)
      : rawCover;
    if (!seenUrls.has(highDpiCover)) {
      seenUrls.add(highDpiCover);
      rawPhotos.push({
        type: 'photo',
        url: highDpiCover,
        rawUrl: rawCover,
        label: 'PHOTO',
        title: ride.title || 'Ride Photo'
      });
    }
  }

  // 2. Extract multi-photo album collection
  let albumPhotos = Array.isArray(ride.photos) ? ride.photos : [];
  if (albumPhotos.length === 0) {
    const rideIdStr = ride.rideId ? String(ride.rideId).trim() : (ride.id ? String(ride.id).trim() : '');
    const albumUrl = ride.photoAlbumUrl ? String(ride.photoAlbumUrl).trim() : '';
    const known = (albumUrl && KNOWN_ALBUMS_MAP[albumUrl])
      || (rideIdStr && Object.values(KNOWN_ALBUMS_MAP).find(k => k.rideId && String(k.rideId).trim() === rideIdStr));
    if (known?.photos && Array.isArray(known.photos)) {
      albumPhotos = known.photos;
    }
  }

  albumPhotos.forEach((pUrl, idx) => {
    if (pUrl && typeof pUrl === 'string' && !pUrl.startsWith('data:image/svg')) {
      const highDpi = pUrl.includes('googleusercontent.com')
        ? (pUrl.includes('=') ? pUrl.replace(/=.*$/, '=w1920-h1080') : `${pUrl}=w1920-h1080`)
        : pUrl;
      if (!seenUrls.has(highDpi)) {
        seenUrls.add(highDpi);
        rawPhotos.push({
          type: 'photo',
          url: highDpi,
          rawUrl: pUrl,
          label: 'PHOTO',
          title: `Photo ${idx + 1}`
        });
      }
    }
  });

  // Chunk photos into balanced segments (2s per photo, up to 10s-12s per segment)
  const PHOTO_BATCH_SIZE = rawPhotos.length > 50 ? 8 : 5;
  const PHOTO_DISPLAY_DURATION_MS = 2000;
  const photoBatches = [];
  for (let i = 0; i < rawPhotos.length; i += PHOTO_BATCH_SIZE) {
    const chunk = rawPhotos.slice(i, i + PHOTO_BATCH_SIZE);
    photoBatches.push({
      type: 'photo-batch',
      photos: chunk,
      url: chunk[0]?.url,
      rawUrl: chunk[0]?.rawUrl,
      itemDurationMs: PHOTO_DISPLAY_DURATION_MS,
      totalDurationMs: chunk.length * PHOTO_DISPLAY_DURATION_MS,
      label: 'PHOTOS',
      title: ride.title || 'Ride Photos'
    });
  }

  // 3. Extract video items with safe action-offsets (10s duration per video segment)
  const VIDEO_DURATION_MS = 10000;
  const ACTION_OFFSETS = [0, 15, 30, 45, 60];
  const origin = typeof window !== 'undefined' && window.location ? encodeURIComponent(window.location.origin) : '';
  if (Array.isArray(ride.matchedVideos)) {
    ride.matchedVideos.forEach((v, vIdx) => {
      if (!v || !v.videoId) return;
      const vId = v.videoId;
      const previewThumbUrl = `https://img.youtube.com/vi/${vId}/maxresdefault.jpg`;
      const fallbackThumbUrl = `https://img.youtube.com/vi/${vId}/hqdefault.jpg`;
      const embedUrl = `https://www.youtube-nocookie.com/embed/${vId}?autoplay=1&mute=1&controls=0&playsinline=1&loop=1&playlist=${vId}&modestbranding=1&rel=0&iv_load_policy=3&fs=0&disablekb=1&enablejsapi=1&vq=medium&origin=${origin}`;

      if (!seenUrls.has(vId)) {
        seenUrls.add(vId);
        videos.push({
          type: 'video',
          videoId: vId,
          url: previewThumbUrl,
          fallbackUrl: fallbackThumbUrl,
          embedUrl: embedUrl,
          title: v.title || 'Ride Video',
          label: 'VIDEO',
          muted: true,
          actionOffsets: ACTION_OFFSETS,
          initialOffset: ACTION_OFFSETS[vIdx % ACTION_OFFSETS.length],
          itemDurationMs: VIDEO_DURATION_MS,
          totalDurationMs: VIDEO_DURATION_MS
        });
      }
    });
  }

  // 4. Interleave photo batches and videos
  const interleaved = [];
  const maxLen = Math.max(photoBatches.length, videos.length);
  const photosFirst = Math.random() >= 0.5;

  for (let i = 0; i < maxLen; i++) {
    if (photosFirst) {
      if (i < photoBatches.length) interleaved.push(photoBatches[i]);
      if (i < videos.length) interleaved.push(videos[i]);
    } else {
      if (i < videos.length) interleaved.push(videos[i]);
      if (i < photoBatches.length) interleaved.push(photoBatches[i]);
    }
  }

  return interleaved;
}

export function getOrCreateCardSession(ride) {
  if (!ride) return null;
  const key = String(ride.id || ride.rideId || '');
  if (!key) return null;
  if (!PREVIEW_SESSION_CACHE.has(key)) {
    const playlist = buildCardMediaPlaylist(ride);
    PREVIEW_SESSION_CACHE.set(key, {
      playlist,
      currentIndex: 0,
      elapsedMs: 0,
      isPaused: false
    });
  }
  return PREVIEW_SESSION_CACHE.get(key);
}

export function isDesktopHoverDevice() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia) {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }
  return !('ontouchstart' in window || (navigator && navigator.maxTouchPoints > 0));
}

export function openRideSpotlight(ride, ridesData) {
  if (!ride) return;
  const targetId = ride.rideId || ride.id;
  if (!targetId) return;
  const strId = String(targetId).replace(/^btcnj-/, '');
  if (typeof history !== 'undefined' && history.pushState) {
    history.pushState(null, '', formatInternalUrl(`?ride=${encodeURIComponent(strId)}`));
  }
  const rides = ridesData || currentLoadedRides || getCachedMediaRides() || [];
  const container = document.getElementById('rides-container');
  const countBadge = document.getElementById('rides-count-badge');
  renderSpotlightView(strId, rides, container, countBadge);
  proactiveSyncRideMedia(ride, true);
  if (typeof window !== 'undefined' && window.scrollTo) {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

export function bindCardHoverPreviews(container, rides) {
  if (!container || !Array.isArray(rides) || typeof document === 'undefined') return;

  const cardEls = container.querySelectorAll('.media-ride-card');
  cardEls.forEach(cardEl => {
    const rideId = cardEl.getAttribute('data-ride-id');
    const ride = findRideByCardId(rides, rideId);
    if (!ride) return;

    const bannerEl = cardEl.querySelector('.card-media-banner');
    if (!bannerEl) return;

    if (cardEl._previewBound) return;
    cardEl._previewBound = true;

    const session = getOrCreateCardSession(ride);
    const hasPlaylist = Boolean(session && session.playlist && session.playlist.length > 0);

    let previewLayer = null;
    if (hasPlaylist) {
      previewLayer = bannerEl.querySelector('.card-preview-layer');
      if (!previewLayer) {
        previewLayer = document.createElement('div');
        previewLayer.className = 'card-preview-layer';
        previewLayer.setAttribute('aria-hidden', 'true');
        previewLayer.innerHTML = `
          <div class="card-preview-stage">
            <img class="card-preview-blur-surface card-preview-blur-surface-a is-active" alt="" referrerpolicy="no-referrer" />
            <img class="card-preview-blur-surface card-preview-blur-surface-b" alt="" referrerpolicy="no-referrer" />
            <img class="card-preview-surface card-preview-surface-a is-active" alt="" referrerpolicy="no-referrer" />
            <img class="card-preview-surface card-preview-surface-b" alt="" referrerpolicy="no-referrer" />
            <div class="card-preview-touch-shield"></div>
          </div>
          <div class="card-preview-wheel-badge" aria-hidden="true" title="Preview Active">
            <svg class="preview-wheel-svg" viewBox="3 3 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="24" cy="24" r="19" stroke="#38bdf8" stroke-width="2.6" stroke-dasharray="4.2 2.8"/>
              <circle cx="24" cy="24" r="13.5" stroke="#fbbf24" stroke-width="1.8"/>
              <path d="M 24 10.5 V 24 L 36.8 19.8 M 24 24 L 31.9 34.9 M 24 24 L 16.1 34.9 M 24 24 L 11.2 19.8" stroke="#38bdf8" stroke-width="2" stroke-linecap="round"/>
              <circle cx="24" cy="24" r="4.8" fill="#38bdf8"/>
              <circle cx="24" cy="24" r="2.2" fill="#090d16"/>
            </svg>
          </div>
          <div class="card-preview-vignette"></div>
          <div class="card-preview-pause-indicator">
            <div class="card-preview-pause-glyph">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#ffffff"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
            </div>
          </div>
          <div class="card-preview-scrubber"></div>
        `;
        bannerEl.appendChild(previewLayer);

        const scrubber = previewLayer.querySelector('.card-preview-scrubber');
        if (scrubber) {
          if (session.playlist.length > 1) {
            scrubber.innerHTML = session.playlist.map(() => `
              <div class="preview-scrubber-segment">
                <div class="preview-scrubber-fill"></div>
              </div>
            `).join('');
          } else {
            scrubber.innerHTML = `
              <div class="preview-scrubber-segment" style="flex:1;">
                <div class="preview-scrubber-fill active" style="width:100%;"></div>
              </div>
            `;
          }
        }
      }

      // Desktop hover: mouseenter with 120ms debounce (catalog cards only)
      cardEl.addEventListener('mouseenter', () => {
        if (cardEl._continuousPreview) return;
        if (activeDesktopPreviewEngine) {
          activeDesktopPreviewEngine.onManualHoverStart(cardEl);
        }
        clearTimeout(cardEl._previewDebounce);
        cardEl._previewDebounce = setTimeout(() => {
          startCardHoverPreview(cardEl, ride, bannerEl, previewLayer, { isManualHover: true });
        }, 120);
      });

      // Desktop mouseleave: pause and smoothly revert (unless continuous preview)
      cardEl.addEventListener('mouseleave', () => {
        clearTimeout(cardEl._previewDebounce);
        if (cardEl._continuousPreview) return;
        stopCardHoverPreview(cardEl, ride, previewLayer, false);
        if (activeDesktopPreviewEngine) {
          activeDesktopPreviewEngine.onManualHoverEnd();
        }
      });
    }

    const isSpotlightCard = cardEl.classList.contains('is-spotlight') || Boolean(cardEl.closest('.spotlight-card-wrapper'));

    // Mobile & Touch Horizontal Swipe on Card / Spotlight Media Banner to Scroll Media
    let bannerTouchStartX = 0;
    let bannerTouchStartY = 0;
    let bannerSwiped = false;
    bannerEl.addEventListener('touchstart', (e) => {
      const t = e.touches && e.touches[0];
      if (!t) return;
      bannerTouchStartX = t.clientX;
      bannerTouchStartY = t.clientY;
      bannerSwiped = false;
    }, { passive: true });

    bannerEl.addEventListener('touchend', (e) => {
      const t = e.changedTouches && e.changedTouches[0];
      if (!t || !hasPlaylist || !session || !session.playlist || session.playlist.length <= 1) return;
      const dx = t.clientX - bannerTouchStartX;
      const dy = t.clientY - bannerTouchStartY;
      if (Math.abs(dx) >= 36 && Math.abs(dx) > 1.2 * Math.abs(dy)) {
        bannerSwiped = true;
        const len = session.playlist.length;
        session.currentIndex = dx < 0
          ? (session.currentIndex + 1) % len
          : (session.currentIndex - 1 + len) % len;
        session.elapsedMs = 0;
        startCardHoverPreview(cardEl, ride, bannerEl, previewLayer, {
          continuous: Boolean(cardEl._continuousPreview || isSpotlightCard),
          isSpotlight: isSpotlightCard,
          isMobileAuto: Boolean(cardEl._mobileAutoActive)
        });
      }
    }, { passive: true });

    // Banner click handler (Desktop & Mobile)
    bannerEl.addEventListener('click', (e) => {
      if (bannerSwiped) {
        bannerSwiped = false;
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (e.target.closest('a, button, .btn, .admin-corner-dock, .card-badges-top, .spotlight-identity-shelf, .spotlight-identity-actions')) return;

      if (cardEl._continuousPreview || isSpotlightCard) {
        // Spotlight mode: Delegated Hero Media Launcher (YouTube Lightbox > Photos Album) with Dual-Media JIT SWR Sync
        e.preventDefault();
        e.stopPropagation();

        const launched = launchRidePrimaryMedia(ride);
        if (!launched && hasPlaylist && previewLayer) {
          // Fallback to pause / resume toggle only if no external hero media exists
          if (cardEl.classList.contains('is-paused')) {
            startCardHoverPreview(cardEl, ride, bannerEl, previewLayer, { continuous: true, isSpotlight: true });
          } else {
            stopCardHoverPreview(cardEl, ride, previewLayer, true);
          }
        }
        return;
      }

      // Universal click/tap: Open Spotlight Mode (Desktop & Mobile)
      clearTimeout(cardEl._previewDebounce);
      e.preventDefault();
      e.stopPropagation();
      openRideSpotlight(ride, rides);
    });

    // Keyboard accessibility for Spotlight preview banner
    bannerEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        if (e.target === bannerEl || e.target.classList.contains('card-media-banner')) {
          e.preventDefault();
          bannerEl.click();
        }
      }
    });

    // Universal card body click handler (outside action links/buttons) opens Spotlight (Desktop & Mobile)
    cardEl.addEventListener('click', (e) => {
      if (cardEl._continuousPreview || isSpotlightCard || cardEl.classList.contains('is-spotlight') || cardEl.closest('.spotlight-card-wrapper')) return;
      if (e.target.closest('a, button, .btn, .btn-action, .matched-videos-section, .video-chip-btn, .admin-corner-dock, .card-badges-top, .cockpit-accordion-trigger, .ride-cockpit-dock')) {
        return;
      }
      clearTimeout(cardEl._previewDebounce);
      e.preventDefault();
      openRideSpotlight(ride, rides);
    });
  });

  // Global dismiss when tapping outside active card
  if (typeof document !== 'undefined' && !document._hasPreviewGlobalDismiss) {
    document._hasPreviewGlobalDismiss = true;
    document.addEventListener('click', (e) => {
      if (!activePreviewCard) return;
      if (activePreviewCard._continuousPreview) return;
      if (!e.target.closest('.media-ride-card')) {
        stopCardHoverPreview(activePreviewCard, null, null, false);
      }
    });
  }

  // Mobile Viewport Auto-Preview: Initialize focus observer for mobile viewports
  bindMobileViewportAutoPreview(container, rides);
  // Desktop Viewport Auto-Preview: Initialize raster-scan auto-preview for desktop viewports
  bindDesktopViewportAutoPreview(container, rides);
}

let activeMobilePreviewEngine = null;
let activeDesktopPreviewEngine = null;

/**
 * Mobile Viewport Auto-Preview Engine
 * Observes viewport scroll and automatically initiates muted preview playback
 * on the first fully visible (un-truncated) media card in focus of user attention.
 */
export class MobileViewportPreviewEngine {
  constructor(container, rides) {
    this.container = container;
    this.rides = rides || [];
    this.settleTimer = null;
    this.SETTLE_DELAY_MS = 220; // 220ms idle hysteresis after scroll stops
    this.destroyed = false;

    this.onScroll = this.onScroll.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.evaluate = this.evaluate.bind(this);

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      setTimeout(this.evaluate, 350);
    }
  }

  isMobileViewport() {
    if (typeof window === 'undefined') return false;
    return (window.matchMedia && window.matchMedia('(hover: none)').matches) || window.innerWidth <= 768;
  }

  isModalOrSpotlightActive() {
    if (typeof document === 'undefined') return false;
    if (document.body.classList.contains('is-spotlight-active') || document.body.classList.contains('spotlight-mode') || document.querySelector('.spotlight-view-container, .spotlight-card-wrapper')) {
      return true;
    }
    if (document.body.classList.contains('modal-open')) {
      return true;
    }
    const openModals = document.querySelectorAll(
      '.lightbox-modal.active, .lightbox-modal.is-active, [aria-hidden="false"].lightbox-modal, [aria-hidden="false"].admin-modal'
    );
    return openModals.length > 0;
  }

  onScroll() {
    if (this.destroyed) return;
    if (!this.isMobileViewport()) return;
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(this.evaluate, this.SETTLE_DELAY_MS);
  }

  onVisibilityChange() {
    if (this.destroyed) return;
    if (document.hidden) {
      this.clearActivePreview();
    } else {
      setTimeout(this.evaluate, 300);
    }
  }

  clearActivePreview() {
    if (activePreviewCard && activePreviewCard._mobileAutoActive) {
      activePreviewCard._mobileAutoActive = false;
      stopCardHoverPreview(activePreviewCard, null, null, false, true);
    }
  }

  evaluate() {
    if (this.destroyed) return;
    if (!this.isMobileViewport() || document.hidden || isAmbientIdle() || this.isModalOrSpotlightActive()) {
      this.clearActivePreview();
      return;
    }

    if (typeof navigator !== 'undefined' && navigator.connection && navigator.connection.saveData) {
      return;
    }

    const cards = Array.from(this.container.querySelectorAll(
      '.media-ride-card:not(.year-milestone-card):not(.photo-milestone-card):not(.is-archived):not(.is-deleted)'
    ));
    if (cards.length === 0) {
      this.clearActivePreview();
      return;
    }

    const header = document.querySelector('.app-header, header');
    const headerBottom = header ? header.getBoundingClientRect().bottom : 56;
    const windowHeight = window.innerHeight || 800;
    const safeBottom = windowHeight - 12;

    const fullyVisible = [];
    const partiallyVisible = [];

    cards.forEach(card => {
      if (card.classList && (card.classList.contains('year-milestone-card') || card.classList.contains('photo-milestone-card'))) return;
      if (typeof card.getAttribute === 'function' && !card.getAttribute('data-ride-id')) return;
      const rect = card.getBoundingClientRect();
      const isFullyVisible = (rect.top >= headerBottom - 4) && (rect.bottom <= safeBottom + 4) && (rect.height > 50);
      if (isFullyVisible) {
        fullyVisible.push({ card, top: rect.top });
      } else {
        const visibleHeight = Math.max(0, Math.min(rect.bottom, safeBottom) - Math.max(rect.top, headerBottom));
        const ratio = rect.height > 0 ? (visibleHeight / rect.height) : 0;
        if (ratio > 0.45) {
          const centerY = (rect.top + rect.bottom) / 2;
          const vpCenter = (headerBottom + safeBottom) / 2;
          const distFromCenter = Math.abs(centerY - vpCenter);
          partiallyVisible.push({ card, ratio, distFromCenter });
        }
      }
    });

    let targetCard = null;
    if (fullyVisible.length > 0) {
      // First fully visible media card in reading order
      fullyVisible.sort((a, b) => a.top - b.top);
      targetCard = fullyVisible[0].card;
    } else if (partiallyVisible.length > 0) {
      // Fallback: Highest visibility ratio closest to optical center
      partiallyVisible.sort((a, b) => (b.ratio - a.ratio) || (a.distFromCenter - b.distFromCenter));
      targetCard = partiallyVisible[0].card;
    }

    if (!targetCard) {
      this.clearActivePreview();
      return;
    }

    if (targetCard === activePreviewCard && targetCard.classList.contains('is-previewing')) {
      return;
    }

    const rideId = targetCard.getAttribute('data-ride-id');
    const ride = findRideByCardId(this.rides, rideId);
    if (!ride) return;

    const bannerEl = targetCard.querySelector('.card-media-banner');
    const previewLayer = targetCard.querySelector('.card-preview-layer');
    if (!bannerEl || !previewLayer) return;

    const session = getOrCreateCardSession(ride);
    if (!session || !session.playlist || session.playlist.length === 0) {
      this.clearActivePreview();
      return;
    }

    this.clearActivePreview();
    targetCard._mobileAutoActive = true;
    startCardHoverPreview(targetCard, ride, bannerEl, previewLayer, { isMobileAuto: true });
  }

  destroy() {
    this.destroyed = true;
    clearTimeout(this.settleTimer);
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    this.clearActivePreview();
  }
}

/**
 * Desktop Viewport Auto-Preview Engine
 * Discovers non-truncated media cards in view and choreographs a living raster-scan
 * carousel (left-to-right, row-by-row, looping back to top-left).
 * Suspends immediately on manual hover and resumes smoothly with scroll hysteresis.
 */
export class DesktopViewportPreviewEngine {
  constructor(container, rides) {
    this.container = container;
    this.rides = rides || [];
    this.settleTimer = null;
    this.resumeTimer = null;
    this.SETTLE_DELAY_MS = 250; // 250ms anti-churn scroll hysteresis
    this.RESUME_DELAY_MS = 700; // 700ms idle delay before resuming after mouseleave
    this.isManualHoverActive = false;
    this.currentCardEl = null;
    this.destroyed = false;

    this.onScroll = this.onScroll.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.evaluate = this.evaluate.bind(this);
    this.onManualHoverStart = this.onManualHoverStart.bind(this);
    this.onManualHoverEnd = this.onManualHoverEnd.bind(this);
    this.advanceToNextCard = this.advanceToNextCard.bind(this);

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      setTimeout(this.evaluate, 450);
    }
  }

  isDesktopViewport() {
    if (typeof window === 'undefined') return false;
    const isMobile = (window.matchMedia && window.matchMedia('(hover: none)').matches) || window.innerWidth <= 768;
    return !isMobile;
  }

  isModalOrSpotlightActive() {
    if (typeof document === 'undefined') return false;
    if (document.body.classList.contains('is-spotlight-active') || document.body.classList.contains('spotlight-mode') || document.querySelector('.spotlight-view-container, .spotlight-card-wrapper')) {
      return true;
    }
    if (document.body.classList.contains('modal-open')) {
      return true;
    }
    const openModals = document.querySelectorAll(
      '.lightbox-modal.active, .lightbox-modal.is-active, [aria-hidden="false"].lightbox-modal, [aria-hidden="false"].admin-modal'
    );
    return openModals.length > 0;
  }

  isManualHovering() {
    if (this.isManualHoverActive) return true;
    if (typeof document === 'undefined') return false;
    const hovered = document.querySelectorAll('.media-ride-card:hover');
    return hovered.length > 0;
  }

  onManualHoverStart(hoveredCard) {
    if (this.destroyed) return;
    this.isManualHoverActive = true;
    clearTimeout(this.resumeTimer);
    clearTimeout(this.settleTimer);
    if (this.currentCardEl && this.currentCardEl._desktopAutoActive && this.currentCardEl !== hoveredCard) {
      this.clearActiveDesktopPreview();
    }
  }

  onManualHoverEnd() {
    if (this.destroyed) return;
    this.isManualHoverActive = false;
    clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(() => {
      if (!this.isManualHoverActive && !this.destroyed) {
        this.evaluate();
      }
    }, this.RESUME_DELAY_MS);
  }

  onScroll() {
    if (this.destroyed) return;
    if (!this.isDesktopViewport()) return;
    clearTimeout(this.settleTimer);

    // Fast scroll check: If current active preview card is now truncated or off-screen, stop it immediately
    if (this.currentCardEl && this.currentCardEl._desktopAutoActive) {
      if (!this.isCardEligible(this.currentCardEl)) {
        this.clearActiveDesktopPreview();
      }
    }

    this.settleTimer = setTimeout(this.evaluate, this.SETTLE_DELAY_MS);
  }

  onVisibilityChange() {
    if (this.destroyed) return;
    if (document.hidden) {
      this.clearActiveDesktopPreview();
    } else {
      setTimeout(this.evaluate, 300);
    }
  }

  clearActiveDesktopPreview() {
    if (this.currentCardEl && this.currentCardEl._desktopAutoActive) {
      this.currentCardEl._desktopAutoActive = false;
      stopCardHoverPreview(this.currentCardEl, null, null, false, true);
    }
    if (activePreviewCard && activePreviewCard._desktopAutoActive) {
      activePreviewCard._desktopAutoActive = false;
      stopCardHoverPreview(activePreviewCard, null, null, false, true);
    }
    this.currentCardEl = null;
  }

  isCardEligible(cardEl) {
    if (!cardEl || (typeof cardEl.isConnected === 'boolean' && !cardEl.isConnected)) return false;
    if (cardEl.classList) {
      if (
        cardEl.classList.contains('year-milestone-card') ||
        cardEl.classList.contains('photo-milestone-card') ||
        cardEl.classList.contains('is-archived') ||
        cardEl.classList.contains('is-deleted')
      ) {
        return false;
      }
    }

    if (typeof cardEl.hasAttribute === 'function' && !cardEl.getAttribute('data-ride-id')) {
      return false;
    }

    if (typeof cardEl.querySelector === 'function') {
      const bannerEl = cardEl.querySelector('.card-media-banner');
      const previewLayer = cardEl.querySelector('.card-preview-layer');
      if (!bannerEl || !previewLayer) return false;
    }

    if (this.rides && this.rides.length > 0 && typeof cardEl.getAttribute === 'function') {
      const rideId = cardEl.getAttribute('data-ride-id');
      if (rideId) {
        const ride = findRideByCardId(this.rides, rideId);
        if (!ride) return false;
        if (typeof getOrCreateCardSession === 'function') {
          const session = getOrCreateCardSession(ride);
          if (!session || !session.playlist || session.playlist.length === 0) return false;
        }
      }
    }

    const header = typeof document !== 'undefined' ? document.querySelector('.app-header, header') : null;
    const headerBottom = header && typeof header.getBoundingClientRect === 'function' ? header.getBoundingClientRect().bottom : 56;
    const windowHeight = (typeof window !== 'undefined' && window.innerHeight) ? window.innerHeight : 800;
    const windowWidth = (typeof window !== 'undefined' && window.innerWidth) ? window.innerWidth : 1200;
    const safeBottom = windowHeight - 12;

    const rect = typeof cardEl.getBoundingClientRect === 'function' ? cardEl.getBoundingClientRect() : { top: 100, bottom: 300, left: 20, right: 300, height: 200, width: 280 };
    const isVerticallyClean = (rect.top >= headerBottom - 4) && (rect.bottom <= safeBottom + 4);
    const isHorizontallyClean = (rect.left >= -2) && (rect.right <= windowWidth + 2);
    const isSufficientSize = (rect.height >= 100) && (rect.width >= 120);

    return isVerticallyClean && isHorizontallyClean && isSufficientSize;
  }

  getEligibleCardsInRasterOrder() {
    if (!this.container) return [];
    const cards = Array.from(this.container.querySelectorAll(
      '.media-ride-card:not(.year-milestone-card):not(.photo-milestone-card):not(.is-archived):not(.is-deleted)'
    ));
    if (cards.length === 0) return [];

    const eligible = [];
    cards.forEach(card => {
      if (this.isCardEligible(card)) {
        const rect = card.getBoundingClientRect();
        eligible.push({ card, top: rect.top, left: rect.left });
      }
    });

    // Sort: group by row (vertical threshold 20px), then sort left-to-right
    eligible.sort((a, b) => {
      const rowDelta = Math.abs(a.top - b.top);
      if (rowDelta > 20) {
        return a.top - b.top;
      }
      return a.left - b.left;
    });

    return eligible.map(e => e.card);
  }

  evaluate() {
    if (this.destroyed) return;
    if (!this.isDesktopViewport() || document.hidden || isAmbientIdle() || this.isModalOrSpotlightActive() || this.isManualHovering()) {
      if (this.currentCardEl && this.currentCardEl._desktopAutoActive) {
        this.clearActiveDesktopPreview();
      }
      return;
    }

    if (typeof navigator !== 'undefined' && navigator.connection && navigator.connection.saveData) {
      return;
    }

    const eligibleCards = this.getEligibleCardsInRasterOrder();
    if (eligibleCards.length === 0) {
      this.clearActiveDesktopPreview();
      return;
    }

    // If currently previewing card is still valid and un-truncated in view, continue playing uninterrupted
    if (this.currentCardEl && this.currentCardEl._desktopAutoActive && eligibleCards.includes(this.currentCardEl)) {
      if (this.currentCardEl.classList.contains('is-previewing')) {
        return;
      }
    }

    // Otherwise, start from the first eligible non-truncated card in raster reading order (upper-left)
    const targetCard = eligibleCards[0];
    this.playCard(targetCard);
  }

  advanceToNextCard() {
    if (this.destroyed || !this.isDesktopViewport() || this.isManualHovering() || document.hidden || this.isModalOrSpotlightActive()) {
      this.clearActiveDesktopPreview();
      return;
    }

    const eligibleCards = this.getEligibleCardsInRasterOrder();
    if (eligibleCards.length === 0) {
      this.clearActiveDesktopPreview();
      return;
    }

    let nextIndex = 0;
    if (this.currentCardEl) {
      const currentIndex = eligibleCards.indexOf(this.currentCardEl);
      if (currentIndex !== -1) {
        nextIndex = (currentIndex + 1) % eligibleCards.length;
      }
    }

    const nextCard = eligibleCards[nextIndex];
    this.playCard(nextCard);
  }

  playCard(targetCard, attemptCount = 0) {
    if (!targetCard) return;

    const eligibleCards = this.getEligibleCardsInRasterOrder();
    if (eligibleCards.length === 0) {
      this.clearActiveDesktopPreview();
      return;
    }

    const rideId = typeof targetCard.getAttribute === 'function' ? targetCard.getAttribute('data-ride-id') : null;
    const ride = findRideByCardId(this.rides, rideId);

    const bannerEl = typeof targetCard.querySelector === 'function' ? targetCard.querySelector('.card-media-banner') : null;
    const previewLayer = typeof targetCard.querySelector === 'function' ? targetCard.querySelector('.card-preview-layer') : null;
    const session = ride && typeof getOrCreateCardSession === 'function' ? getOrCreateCardSession(ride) : null;

    // Self-healing fail-over: if this card cannot play, advance to next candidate in raster sequence
    if (!ride || !bannerEl || !previewLayer || !session || !session.playlist || session.playlist.length === 0) {
      if (attemptCount < eligibleCards.length) {
        const currentIndex = eligibleCards.indexOf(targetCard);
        const nextIndex = currentIndex !== -1 ? (currentIndex + 1) % eligibleCards.length : 0;
        const nextCandidate = eligibleCards[nextIndex];
        if (nextCandidate && nextCandidate !== targetCard) {
          this.playCard(nextCandidate, attemptCount + 1);
          return;
        }
      }
      this.clearActiveDesktopPreview();
      return;
    }

    this.clearActiveDesktopPreview();
    this.currentCardEl = targetCard;
    targetCard._desktopAutoActive = true;

    startCardHoverPreview(targetCard, ride, bannerEl, previewLayer, {
      isDesktopAuto: true,
      onCycleComplete: () => {
        if (!this.destroyed && targetCard._desktopAutoActive && !this.isManualHovering()) {
          this.advanceToNextCard();
        }
      }
    });
  }

  destroy() {
    this.destroyed = true;
    clearTimeout(this.settleTimer);
    clearTimeout(this.resumeTimer);
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    this.clearActiveDesktopPreview();
  }
}

export function bindMobileViewportAutoPreview(container, rides) {
  if (activeMobilePreviewEngine) {
    activeMobilePreviewEngine.destroy();
    activeMobilePreviewEngine = null;
  }
  if (!container || !Array.isArray(rides)) return null;
  activeMobilePreviewEngine = new MobileViewportPreviewEngine(container, rides);
  return activeMobilePreviewEngine;
}

export function getMobileViewportPreviewEngine() {
  return activeMobilePreviewEngine;
}

export function bindDesktopViewportAutoPreview(container, rides) {
  if (activeDesktopPreviewEngine) {
    activeDesktopPreviewEngine.destroy();
    activeDesktopPreviewEngine = null;
  }
  if (!container || !Array.isArray(rides)) return null;
  activeDesktopPreviewEngine = new DesktopViewportPreviewEngine(container, rides);
  bindOffscreenAnimationPause(container);
  return activeDesktopPreviewEngine;
}

export function getDesktopViewportPreviewEngine() {
  return activeDesktopPreviewEngine;
}

// ─── Ambient Attention Controller (v1.1.81) ───────────────────────────────────
// Chrome shares ONE GPU process across all tabs/windows. When BTCNJCC is visible but
// the user is watching YouTube elsewhere (or in our own Spotlight iframe), the page's
// 188 CSS animations + filters + rAF loops saturate that GPU process and starve video
// decode/compositing. visibilitychange never fires in that case, so we track attention:
// idle = window blurred (or focus inside an iframe) and no pointer activity. While idle
// we pause decorative motion; any focus/pointer input restores it instantly.
export function isAmbientIdle() {
  return typeof document !== 'undefined' && !!document.body && document.body.classList.contains('is-ambient-idle');
}

const AMBIENT_IDLE_BLUR_DELAY_MS = 1500;
const AMBIENT_IDLE_POINTER_GRACE_MS = 5000;
let _ambientIdleTimer = null;
let _ambientAttentionBound = false;

function _reevaluatePreviewEngines(delayMs = 0) {
  const run = () => {
    if (activeDesktopPreviewEngine && typeof activeDesktopPreviewEngine.evaluate === 'function') activeDesktopPreviewEngine.evaluate();
    if (activeMobilePreviewEngine && typeof activeMobilePreviewEngine.evaluate === 'function') activeMobilePreviewEngine.evaluate();
  };
  if (delayMs > 0) setTimeout(run, delayMs); else run();
}

function _isAttentionElsewhere() {
  if (typeof document === 'undefined') return false;
  if (typeof document.hasFocus === 'function' && !document.hasFocus()) return true;
  const ae = document.activeElement;
  return !!(ae && ae.tagName === 'IFRAME');
}

export function enterAmbientIdle() {
  if (typeof document === 'undefined' || !document.body || isAmbientIdle()) return;
  if (!_isAttentionElsewhere()) return;
  document.body.classList.add('is-ambient-idle');
  if (activeDesktopPreviewEngine) activeDesktopPreviewEngine.clearActiveDesktopPreview();
  if (activeMobilePreviewEngine) activeMobilePreviewEngine.clearActivePreview();
}

export function exitAmbientIdle() {
  if (typeof document === 'undefined' || !document.body || !isAmbientIdle()) return;
  document.body.classList.remove('is-ambient-idle'); // wallpaper resumes via its class observer
  resumeHistoricalClockSync();
  _reevaluatePreviewEngines(300);
}

function _scheduleAmbientIdle(delayMs) {
  clearTimeout(_ambientIdleTimer);
  _ambientIdleTimer = setTimeout(() => {
    if (_isAttentionElsewhere()) enterAmbientIdle();
  }, delayMs);
}

export function initAmbientAttentionController() {
  if (_ambientAttentionBound || typeof window === 'undefined') return;
  _ambientAttentionBound = true;
  window.addEventListener('blur', () => _scheduleAmbientIdle(AMBIENT_IDLE_BLUR_DELAY_MS));
  window.addEventListener('focus', () => { clearTimeout(_ambientIdleTimer); exitAmbientIdle(); });
  const onPointer = () => {
    exitAmbientIdle();
    if (_isAttentionElsewhere()) _scheduleAmbientIdle(AMBIENT_IDLE_POINTER_GRACE_MS);
    else clearTimeout(_ambientIdleTimer);
  };
  window.addEventListener('pointermove', onPointer, { passive: true });
  window.addEventListener('pointerdown', onPointer, { passive: true });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && _isAttentionElsewhere()) _scheduleAmbientIdle(AMBIENT_IDLE_BLUR_DELAY_MS);
  });
  if (_isAttentionElsewhere()) _scheduleAmbientIdle(AMBIENT_IDLE_BLUR_DELAY_MS);
}

// Pause CSS animations on feed cards that are far offscreen (no visual change: they are not visible).
let _offscreenObserver = null;
export function bindOffscreenAnimationPause(container) {
  if (!container || typeof IntersectionObserver === 'undefined') return;
  if (_offscreenObserver) _offscreenObserver.disconnect();
  _offscreenObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => entry.target.classList.toggle('is-offscreen', !entry.isIntersecting));
  }, { root: null, rootMargin: '200px 0px 200px 0px', threshold: 0 });
  container.querySelectorAll('.media-ride-card, .historical-divider-card, .year-milestone-card').forEach(el => {
    el.classList.remove('is-offscreen');
    _offscreenObserver.observe(el);
  });
}

export function startCardHoverPreview(cardEl, ride, bannerEl, previewLayer, options = {}) {
  if (!cardEl || (typeof cardEl.isConnected === 'boolean' && !cardEl.isConnected)) return;
  if (typeof ensureYouTubePreconnect === 'function') ensureYouTubePreconnect();
  if (activePreviewCard && activePreviewCard !== cardEl) {
    if (activePreviewCard._continuousPreview && !(options && options.continuous)) {
      return;
    }
    stopCardHoverPreview(activePreviewCard, null, null, false, true);
  }
  activePreviewCard = cardEl;

  if (options && options.continuous) {
    cardEl._continuousPreview = true;
    cardEl.classList.add('is-continuous-preview');
  }
  if (options && options.isMobileAuto) {
    cardEl._mobileAutoActive = true;
  }
  if (options && options.isDesktopAuto) {
    cardEl._desktopAutoActive = true;
  }

  const session = getOrCreateCardSession(ride || { id: cardEl.getAttribute('data-ride-id') });
  if (!session || !session.playlist || session.playlist.length < 1) return;

  cardEl.classList.add('is-previewing');
  cardEl.classList.remove('is-paused');
  session.isPaused = false;
  session.loopCount = session.loopCount || 0;

  const layer = previewLayer || cardEl.querySelector('.card-preview-layer');
  if (!layer) return;

  const stageEl = layer.querySelector('.card-preview-stage');
  const surfaceA = layer.querySelector('.card-preview-surface.card-preview-surface-a');
  const surfaceB = layer.querySelector('.card-preview-surface.card-preview-surface-b');
  const blurSurfaceA = layer.querySelector('.card-preview-blur-surface-a');
  const blurSurfaceB = layer.querySelector('.card-preview-blur-surface-b');
  const fills = layer.querySelectorAll('.preview-scrubber-fill');

  function renderCurrentItem() {
    if (!cardEl.classList.contains('is-previewing') || cardEl.classList.contains('is-paused')) return;

    const item = session.playlist[session.currentIndex];
    if (!item) return;

    const segmentDurationMs = item.totalDurationMs || 5000;
    const elapsed = session.elapsedMs || 0;
    const remainingMs = Math.max(100, segmentDurationMs - elapsed);

    const currentSurfaceId = cardEl._activeSurface || 'a';
    const nextSurfaceId = currentSurfaceId === 'a' ? 'b' : 'a';
    const activeSurfaceEl = currentSurfaceId === 'a' ? surfaceA : surfaceB;
    const nextSurfaceEl = nextSurfaceId === 'a' ? surfaceA : surfaceB;
    const activeBlurSurfaceEl = currentSurfaceId === 'a' ? blurSurfaceA : blurSurfaceB;
    const nextBlurSurfaceEl = nextSurfaceId === 'a' ? blurSurfaceA : blurSurfaceB;

    function swapSurface(targetSrc, rawFallback, isPhoto) {
      if (nextBlurSurfaceEl) {
        nextBlurSurfaceEl.onerror = function() {
          this.onerror = null;
          if (rawFallback && this.src !== rawFallback) {
            this.src = rawFallback;
          }
        };
        nextBlurSurfaceEl.src = targetSrc;
        nextBlurSurfaceEl.classList.add('is-active');
        if (activeBlurSurfaceEl) {
          activeBlurSurfaceEl.classList.remove('is-active');
          setTimeout(() => {
            if (activeBlurSurfaceEl && !activeBlurSurfaceEl.classList.contains('is-active')) {
              activeBlurSurfaceEl.src = '';
            }
          }, 450);
        }
      }

      if (!nextSurfaceEl) return;
      nextSurfaceEl.onerror = function() {
        this.onerror = null;
        if (rawFallback && this.src !== rawFallback) {
          this.src = rawFallback;
        } else if (typeof window !== 'undefined' && window.getCyclingFallbackSvg) {
          this.src = window.getCyclingFallbackSvg();
        }
      };

      // Reset and apply cycling Ken Burns pan/zoom drift for still photo previews
      nextSurfaceEl.classList.remove('ken-burns-v1', 'ken-burns-v2', 'ken-burns-v3', 'ken-burns-v4');
      if (isPhoto) {
        cardEl._kenBurnsIdx = ((cardEl._kenBurnsIdx || 0) + 1) % 4;
        const vNum = (cardEl._kenBurnsIdx % 4) + 1;
        nextSurfaceEl.classList.add(`ken-burns-v${vNum}`);
      }

      nextSurfaceEl.src = targetSrc;
      nextSurfaceEl.classList.add('is-active');
      if (activeSurfaceEl) activeSurfaceEl.classList.remove('is-active');
      cardEl._activeSurface = nextSurfaceId;
    }

    const existingIframe = stageEl ? stageEl.querySelector('.card-preview-video-iframe') : null;

    const isAutoScan = Boolean(options && (options.isDesktopAuto || options.isMobileAuto));
    const isExplicitInteraction = Boolean(options && (options.isManualHover || options.continuous || options.isSpotlight)) || Boolean(cardEl._continuousPreview);
    // v1.1.81: live iframes for explicit interaction always; auto-scan only while the page is attended.
    const shouldMountLiveIframe = isExplicitInteraction || !isAmbientIdle();

    if (item.type === 'video') {
      if (layer) {
        layer.classList.add('is-video-active');
        layer.classList.remove('is-photo-active');
      }
      clearInterval(cardEl._subPhotoTimer);

      const offsets = item.actionOffsets || [15, 30, 45, 60, 75, 90];
      const offsetIdx = ((session.loopCount || 0) + session.currentIndex) % offsets.length;
      const baseStart = offsets[offsetIdx];
      const startSec = baseStart + Math.floor((session.elapsedMs || 0) / 1000);
      const videoSrc = `${item.embedUrl}&start=${startSec}`;

      // 1. Immediately activate high-resolution video thumbnail filler (z-index: 2.5)
      let fillerEl = stageEl ? stageEl.querySelector('.card-preview-video-filler') : null;
      if (!fillerEl && stageEl) {
        fillerEl = document.createElement('img');
        fillerEl.className = 'card-preview-video-filler';
        fillerEl.setAttribute('referrerpolicy', 'no-referrer');
        stageEl.appendChild(fillerEl);
      }
      if (fillerEl) {
        fillerEl.onerror = function() {
          this.onerror = null;
          if (item.fallbackUrl) this.src = item.fallbackUrl;
        };
        fillerEl.src = item.url;
      }

      if (!shouldMountLiveIframe) {
        // Option A: Kinetic High-Res Poster Preview with Ken Burns drift & ambilight (0 video streaming data)
        if (fillerEl) fillerEl.classList.remove('is-active');
        if (existingIframe) {
          existingIframe.src = 'about:blank';
          existingIframe.remove();
        }
        swapSurface(item.url, item.fallbackUrl || item.url, true);
      } else {
        // Explicit Interaction (Hover dwell or Spotlight): Mount single live video iframe
        if (fillerEl) fillerEl.classList.add('is-active');
        swapSurface(item.url, item.fallbackUrl || item.url, false);

        function activateVideoIframe(targetIframe) {
          if (!targetIframe) return;
          let activated = false;

          function markReady() {
            if (activated) return;
            activated = true;
            if (cardEl._videoMsgListener) {
              window.removeEventListener('message', cardEl._videoMsgListener);
              cardEl._videoMsgListener = null;
            }
            if (!cardEl.classList.contains('is-previewing') || cardEl.classList.contains('is-paused')) return;
            targetIframe.classList.add('is-ready');
            if (fillerEl) fillerEl.classList.remove('is-active');
            if (existingIframe && existingIframe !== targetIframe) {
              setTimeout(() => {
                if (stageEl && stageEl.contains(existingIframe)) {
                  existingIframe.remove();
                }
              }, 300);
            }
          }

          // Clean previous listener if any
          if (cardEl._videoMsgListener) {
            window.removeEventListener('message', cardEl._videoMsgListener);
          }

          cardEl._videoMsgListener = (e) => {
            try {
              const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
              const state = data?.info?.playerState ?? data?.info;
              if (state === 1) { // YT.PlayerState.PLAYING
                markReady();
              } else if (state === 2 || state === 5) {
                // Auto-resume if browser or YouTube paused playback
                targetIframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'playVideo', args: '' }), '*');
              }
            } catch (_) {}
          };
          window.addEventListener('message', cardEl._videoMsgListener);

          // Handshake listening and lightweight resolution signals to iframe
          try {
            targetIframe.contentWindow?.postMessage(JSON.stringify({ event: 'listening' }), '*');
            targetIframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'setPlaybackQuality', args: ['medium'] }), '*');
          } catch (_) {}

          // Safety fallback: reveal after 2.5s if postMessage is blocked
          clearTimeout(cardEl._fillerTimer);
          cardEl._fillerTimer = setTimeout(() => {
            markReady();
          }, 2500);
        }

        if (existingIframe) {
          existingIframe.classList.add('card-preview-video-iframe-retiring');
        }
        if (stageEl) {
          const iframe = document.createElement('iframe');
          iframe.className = 'card-preview-video-iframe';
          iframe.src = videoSrc;
          iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
          iframe.setAttribute('tabindex', '-1');
          iframe.setAttribute('aria-hidden', 'true');
          iframe.addEventListener('load', () => activateVideoIframe(iframe), { once: true });
          stageEl.appendChild(iframe);
        }
      }
    } else {
      if (layer) {
        layer.classList.add('is-photo-active');
        layer.classList.remove('is-video-active');
      }
      // Photo batch item: 2s per photo, up to 5 photos per segment
      const fillerEl = stageEl ? stageEl.querySelector('.card-preview-video-filler') : null;
      if (fillerEl) fillerEl.classList.remove('is-active');

      if (existingIframe) {
        existingIframe.classList.remove('is-ready');
        setTimeout(() => {
          if (stageEl && stageEl.contains(existingIframe)) {
            existingIframe.src = 'about:blank';
            existingIframe.remove();
          }
        }, 200);
      }

      const batchPhotos = Array.isArray(item.photos) && item.photos.length > 0
        ? item.photos
        : [{ url: item.url, rawUrl: item.rawUrl }];

      const itemDuration = item.itemDurationMs || 2000;
      let subIdx = Math.floor(elapsed / itemDuration) % batchPhotos.length;
      swapSurface(batchPhotos[subIdx].url, batchPhotos[subIdx].rawUrl, true);

      // Pre-warm the next sub-photo
      if (batchPhotos.length > 1) {
        const nextSubPhoto = batchPhotos[(subIdx + 1) % batchPhotos.length];
        if (nextSubPhoto && nextSubPhoto.url) {
          try {
            const pre = new Image();
            pre.referrerPolicy = 'no-referrer';
            pre.src = nextSubPhoto.url;
          } catch (_) {}
        }
      }

      // 2-second sub-photo interval timer
      clearInterval(cardEl._subPhotoTimer);
      if (batchPhotos.length > 1) {
        cardEl._subPhotoTimer = setInterval(() => {
          if (!cardEl.classList.contains('is-previewing') || cardEl.classList.contains('is-paused')) return;
          subIdx = (subIdx + 1) % batchPhotos.length;
          swapSurface(batchPhotos[subIdx].url, batchPhotos[subIdx].rawUrl, true);
        }, itemDuration);
      }
    }

    // Pre-warm next segment asset
    const nextIdx = (session.currentIndex + 1) % session.playlist.length;
    const nextItem = session.playlist[nextIdx];
    if (nextItem) {
      const nextUrl = nextItem.type === 'video'
        ? nextItem.url
        : (nextItem.photos?.[0]?.url || nextItem.url);
      if (nextUrl) {
        try {
          const preload = new Image();
          preload.referrerPolicy = 'no-referrer';
          preload.src = nextUrl;
        } catch (_) {}
      }
    }

    // Zero-lag asset prefetch timer cleanup
    clearTimeout(cardEl._prewarmTimer);
    clearTimeout(cardEl._fillerTimer);

    // Compositor-only progress: transform: scaleX() (no per-frame layout like `width` transitions)
    fills.forEach((fill, idx) => {
      fill.style.transition = 'none';
      if (idx < session.currentIndex) {
        fill.style.transform = 'scaleX(1)';
      } else if (idx > session.currentIndex) {
        fill.style.transform = 'scaleX(0)';
      } else {
        const startRatio = Math.min(1, Math.max(0, elapsed / segmentDurationMs));
        fill.style.transform = `scaleX(${startRatio})`;
        void fill.offsetWidth; // Commit start state before transitioning
        fill.style.transition = `transform ${remainingMs}ms linear`;
        fill.style.transform = 'scaleX(1)';
      }
    });

    cardEl._previewItemStartTime = Date.now() - elapsed;

    clearTimeout(cardEl._previewAdvanceTimer);
    cardEl._previewAdvanceTimer = setTimeout(() => {
      clearInterval(cardEl._subPhotoTimer);
      const isLoopEnd = (session.currentIndex + 1) >= session.playlist.length;
      if (isLoopEnd) {
        session.loopCount = (session.loopCount || 0) + 1;
        if (options && options.isDesktopAuto && typeof options.onCycleComplete === 'function') {
          session.currentIndex = 0;
          session.elapsedMs = 0;
          options.onCycleComplete();
          return;
        }
      }
      session.currentIndex = (session.currentIndex + 1) % session.playlist.length;
      session.elapsedMs = 0;
      renderCurrentItem();
    }, remainingMs);
  }

  renderCurrentItem();
}

export function stopCardHoverPreview(cardEl, ride, previewLayer, isMobilePause = false, forceStop = false) {
  if (!cardEl) return;
  if (cardEl._continuousPreview && !forceStop && !isMobilePause) {
    return;
  }
  if (forceStop) {
    cardEl._continuousPreview = false;
    cardEl.classList.remove('is-continuous-preview');
  }
  cardEl._mobileAutoActive = false;
  cardEl._desktopAutoActive = false;
  clearTimeout(cardEl._previewAdvanceTimer);
  clearTimeout(cardEl._prewarmTimer);

  clearTimeout(cardEl._fillerTimer);
  clearInterval(cardEl._subPhotoTimer);
  if (cardEl._videoMsgListener) {
    window.removeEventListener('message', cardEl._videoMsgListener);
    cardEl._videoMsgListener = null;
  }
  const filler = cardEl.querySelector('.card-preview-video-filler');
  if (filler) filler.classList.remove('is-active');

  const session = getOrCreateCardSession(ride || { id: cardEl.getAttribute('data-ride-id') });
  const now = Date.now();
  const start = cardEl._previewItemStartTime || now;
  const elapsed = Math.max(0, now - start);
  const currentItem = session && session.playlist ? session.playlist[session.currentIndex] : null;
  const currentDurationMs = currentItem?.totalDurationMs || 5000;

  if (session) {
    if (elapsed >= currentDurationMs) {
      session.currentIndex = (session.currentIndex + 1) % session.playlist.length;
      session.elapsedMs = 0;
    } else {
      session.elapsedMs = elapsed;
    }
    session.isPaused = true;
  }

  const layer = previewLayer || cardEl.querySelector('.card-preview-layer');

  if (layer && session) {
    const fills = layer.querySelectorAll('.preview-scrubber-fill');
    const currentFill = fills && fills[session.currentIndex];
    if (currentFill && currentFill.style) {
      const currentRatio = Math.min(1, Math.max(0, session.elapsedMs / currentDurationMs));
      currentFill.style.transition = 'none';
      currentFill.style.transform = `scaleX(${currentRatio})`;
    }
  }

  // Teardown all video iframes to free GPU decoding pipeline
  if (layer) {
    layer.querySelectorAll('.card-preview-video-iframe').forEach(iframe => {
      iframe.src = 'about:blank';
      iframe.remove();
    });
    if (!isMobilePause) {
      layer.querySelectorAll('.card-preview-surface').forEach(el => {
        el.classList.remove('ken-burns-v1', 'ken-burns-v2', 'ken-burns-v3', 'ken-burns-v4');
      });
      if (!cardEl._continuousPreview) {
        layer.classList.remove('is-video-active', 'is-photo-active');
      }
    }
  }

  if (isMobilePause) {
    cardEl.classList.add('is-paused');
  } else {
    cardEl.classList.remove('is-previewing', 'is-paused', 'is-continuous-preview');
    if (activePreviewCard === cardEl) {
      activePreviewCard = null;
    }
  }
}

function escapeHtml(str) {
  return String(str || '').replace(/[&<>"']/g, m => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  })[m]);
}

let activeSpotlightTimers = [];

/**
 * Clears any pending spotlight animation timers.
 */
export function clearSpotlightAnimationTimers() {
  if (activeSpotlightTimers && activeSpotlightTimers.length > 0) {
    for (const timerId of activeSpotlightTimers) {
      clearTimeout(timerId);
    }
    activeSpotlightTimers = [];
  }
  document.querySelectorAll('#spotlight-viewport-beams, .spotlight-viewport-beams').forEach(el => el.remove());
}

/**
 * Emits a zero-allocation kinetic particle burst of golden amber and cyan sparks
 * simulating impact dust from the spotlight hallmark stamp.
 * @param {HTMLCanvasElement} canvas
 * @param {HTMLElement} [originEl]
 */
export function launchSpotlightEmbers(canvas, originEl) {
  if (!canvas || typeof window === 'undefined') return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    canvas.style.display = 'none';
    return;
  }
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const rect = canvas.getBoundingClientRect();
  const width = Math.max(10, Math.floor(rect.width || canvas.parentElement?.clientWidth || 320));
  const height = Math.max(10, Math.floor(rect.height || canvas.parentElement?.clientHeight || 480));
  canvas.width = width;
  canvas.height = height;

  let originX = width - 80;
  let originY = 45;
  if (originEl) {
    const originRect = originEl.getBoundingClientRect();
    if (originRect.width > 0) {
      originX = (originRect.left - rect.left) + originRect.width / 2;
      originY = (originRect.top - rect.top) + originRect.height / 2;
    }
  }

  const PARTICLE_COUNT = 22;
  const particles = new Float32Array(PARTICLE_COUNT * 6);
  // Layout: [0: x, 1: y, 2: vx, 3: vy, 4: alpha, 5: size]
  const colors = [
    '#fbbf24', '#f59e0b', '#fde68a', '#38bdf8', '#ffffff'
  ];
  const particleColors = new Array(PARTICLE_COUNT);

  for (let i = 0; i < PARTICLE_COUNT; i++) {
    const offset = i * 6;
    particles[offset] = originX + (Math.random() - 0.5) * 24;
    particles[offset + 1] = originY + (Math.random() - 0.5) * 16;
    const angle = (Math.PI * 0.25) + Math.random() * (Math.PI * 0.9);
    const speed = 1.2 + Math.random() * 3.8;
    particles[offset + 2] = Math.cos(angle) * speed * (i % 2 === 0 ? 1 : -1);
    particles[offset + 3] = Math.sin(angle) * speed - 0.8;
    particles[offset + 4] = 1.0;
    particles[offset + 5] = 1.5 + Math.random() * 2.5;
    particleColors[i] = colors[i % colors.length];
  }

  const startTime = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
  const DURATION_MS = 1200;

  function renderFrame(now) {
    const elapsed = (now || Date.now()) - startTime;
    if (elapsed > DURATION_MS) {
      ctx.clearRect(0, 0, width, height);
      canvas.style.display = 'none';
      return;
    }

    ctx.clearRect(0, 0, width, height);
    let activeCount = 0;

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const offset = i * 6;
      let alpha = particles[offset + 4];
      if (alpha <= 0.01) continue;

      particles[offset] += particles[offset + 2];
      particles[offset + 1] += particles[offset + 3];
      particles[offset + 3] += 0.08;
      particles[offset + 2] *= 0.98;
      particles[offset + 4] = Math.max(0, 1 - (elapsed / DURATION_MS));
      alpha = particles[offset + 4];

      if (alpha > 0.01) {
        activeCount++;
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.fillStyle = particleColors[i];
        ctx.shadowColor = particleColors[i];
        ctx.shadowBlur = 6;
        ctx.beginPath();
        ctx.arc(particles[offset], particles[offset + 1], particles[offset + 5], 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }

    if (activeCount > 0 && canvas.isConnected) {
      requestAnimationFrame(renderFrame);
    } else {
      ctx.clearRect(0, 0, width, height);
      canvas.style.display = 'none';
    }
  }

  requestAnimationFrame(renderFrame);
}

/**
 * Computes total unique rider attendance (inclusive of ride leads & roster),
 * strictly omitting individual rider names to preserve member privacy.
 *
 * @param {Object} ride
 * @returns {number}
 */
export function getRiderAttendanceCount(ride) {
  if (!ride) return 0;
  const uniqueRoster = new Set();

  if (Array.isArray(ride.leaders)) {
    ride.leaders.forEach(l => {
      const clean = String(l || '').trim();
      if (clean && clean !== 'CC' && clean !== 'CC (Cedric)') uniqueRoster.add(clean.toLowerCase());
    });
  }

  const members = ride.members || ride.joinedMembers || ride.riders || [];
  if (Array.isArray(members)) {
    members.forEach(m => {
      const clean = String(m || '').trim();
      if (clean) uniqueRoster.add(clean.toLowerCase());
    });
  } else if (typeof members === 'string') {
    members.split(/[,●•+]|\s*\+\s*\d+/).forEach(m => {
      const clean = m.trim();
      if (clean) uniqueRoster.add(clean.toLowerCase());
    });
  }

  return uniqueRoster.size;
}

/**
 * Formats an abbreviated start location including town, state, and county if known.
 *
 * @param {Object} ride
 * @returns {string}
 */
export function formatRideStartLocation(ride) {
  if (!ride) return 'North Jersey / Hudson Highlands (Bergen / Rockland County)';
  const mapName = String(ride.mapName || '').trim();
  const startLoc = String(ride.startLoc || ride.location || ride.start || '').trim();
  const directions = String(ride.directions || '').trim();
  const title = String(ride.title || '').trim();
  const rawText = `${mapName} ${startLoc} ${directions} ${title}`.toLowerCase();

  // Classic BTCNJ Signature Hub Overrides (when mapName is generic or absent)
  if (!mapName || mapName.toLowerCase().includes('campgaw') || mapName.toLowerCase().includes('darlington') || mapName.toLowerCase().includes('ramapo') || mapName.toLowerCase().includes('acme')) {
    if (rawText.includes('campgaw') || rawText.includes('darlington')) {
      return 'Campgaw / Darlington, Mahwah, NJ (Bergen County)';
    }
    if (rawText.includes('ramapo') || rawText.includes('reserve')) {
      return 'Ramapo Reserve, Mahwah, NJ (Bergen County)';
    }
    if (rawText.includes('katonah') || rawText.includes('acme')) {
      return 'Acme Plaza, Katonah, NY (Westchester County)';
    }
  }
  if (!mapName) {
    if (rawText.includes('new paltz') || rawText.includes('mohonk')) {
      return 'New Paltz, NY (Ulster County)';
    }
    if (rawText.includes('peconic') || rawText.includes('shelter island') || rawText.includes('southold')) {
      return 'Peconic Bay, NY (Suffolk County)';
    }
    if (rawText.includes('high point')) {
      return 'High Point State Park, Sussex, NJ (Sussex County)';
    }
    if (rawText.includes('nyack') || rawText.includes('piermont')) {
      return 'Nyack, NY (Rockland County)';
    }
    if (rawText.includes('catskill') || rawText.includes('hunter') || rawText.includes('windham')) {
      return 'Catskills Region, NY (Greene County)';
    }
    if (rawText.includes('titanic') || rawText.includes('pemberton') || rawText.includes('pine barrens')) {
      return 'Pemberton, NJ (Burlington County)';
    }
  }

  // Helper to determine town & county from keywords
  let townCounty = null;

  // Bergen County, NJ
  if (rawText.includes('fair lawn') || rawText.includes('saddle river rd') || rawText.includes('radburn')) {
    townCounty = { town: 'Fair Lawn', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('oakland') || rawText.includes('oak st')) {
    townCounty = { town: 'Oakland', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('oradell') || rawText.includes('westervelt')) {
    townCounty = { town: 'Oradell', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('glen rock') || rawText.includes('duck pond') || rawText.includes('alan ave')) {
    townCounty = { town: 'Glen Rock', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('campgaw') || rawText.includes('darlington') || rawText.includes('ramapo') || rawText.includes('airmount') || rawText.includes('continental soldiers') || rawText.includes('mahwah')) {
    townCounty = { town: 'Mahwah', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('ridgewood') || rawText.includes('graydon') || rawText.includes('kings pond')) {
    townCounty = { town: 'Ridgewood', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('paramus') || rawText.includes('van saun') || rawText.includes('dunkirk')) {
    townCounty = { town: 'Paramus', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('upper saddle river')) {
    townCounty = { town: 'Upper Saddle River', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('saddle river') || rawText.includes('lions park')) {
    townCounty = { town: 'Saddle River', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('woodcliff lake')) {
    townCounty = { town: 'Woodcliff Lake', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('park ridge')) {
    townCounty = { town: 'Park Ridge', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('montvale')) {
    townCounty = { town: 'Montvale', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('wyckoff')) {
    townCounty = { town: 'Wyckoff', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('franklin lakes')) {
    townCounty = { town: 'Franklin Lakes', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('waldwick')) {
    townCounty = { town: 'Waldwick', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('allendale')) {
    townCounty = { town: 'Allendale', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('ho-ho-kus')) {
    townCounty = { town: 'Ho-Ho-Kus', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('midland park')) {
    townCounty = { town: 'Midland Park', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('westwood')) {
    townCounty = { town: 'Westwood', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('river vale')) {
    townCounty = { town: 'River Vale', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('hillsdale')) {
    townCounty = { town: 'Hillsdale', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('emerson')) {
    townCounty = { town: 'Emerson', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('old tappan')) {
    townCounty = { town: 'Old Tappan', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('northvale') || rawText.includes('norwood')) {
    townCounty = { town: 'Northvale', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('closter') || rawText.includes('demarest') || rawText.includes('cresskill') || rawText.includes('haworth') || rawText.includes('harrington park')) {
    townCounty = { town: 'Closter', state: 'NJ', county: 'Bergen' };
  } else if (rawText.includes('tenafly') || rawText.includes('alpine') || rawText.includes('englewood') || rawText.includes('fort lee')) {
    townCounty = { town: 'Tenafly', state: 'NJ', county: 'Bergen' };

  // Passaic County, NJ
  } else if (rawText.includes('wayne') || rawText.includes('packanack')) {
    townCounty = { town: 'Wayne', state: 'NJ', county: 'Passaic' };
  } else if (rawText.includes('pompton lakes') || rawText.includes('totowa') || rawText.includes('little falls')) {
    townCounty = { town: 'Pompton Lakes', state: 'NJ', county: 'Passaic' };
  } else if (rawText.includes('ringwood') || rawText.includes('skylands') || rawText.includes('shepherd lake')) {
    townCounty = { town: 'Ringwood', state: 'NJ', county: 'Passaic' };
  } else if (rawText.includes('west milford') || rawText.includes('hewitt')) {
    townCounty = { town: 'West Milford', state: 'NJ', county: 'Passaic' };
  } else if (rawText.includes('clifton') || rawText.includes('paterson') || rawText.includes('hawthorne') || rawText.includes('haledon')) {
    townCounty = { town: 'Hawthorne', state: 'NJ', county: 'Passaic' };

  // Morris County, NJ
  } else if (rawText.includes('morristown') || rawText.includes('morris plains') || rawText.includes('morris')) {
    townCounty = { town: 'Morristown', state: 'NJ', county: 'Morris' };
  } else if (rawText.includes('mendham') || rawText.includes('chester')) {
    townCounty = { town: 'Mendham', state: 'NJ', county: 'Morris' };
  } else if (rawText.includes('denville') || rawText.includes('montville') || rawText.includes('boonton')) {
    townCounty = { town: 'Montville', state: 'NJ', county: 'Morris' };
  } else if (rawText.includes('madison') || rawText.includes('chatham') || rawText.includes('florham park')) {
    townCounty = { town: 'Madison', state: 'NJ', county: 'Morris' };
  } else if (rawText.includes('long valley') || rawText.includes('randolph')) {
    townCounty = { town: 'Long Valley', state: 'NJ', county: 'Morris' };

  // Sussex County, NJ
  } else if (rawText.includes('high point') || rawText.includes('sussex') || rawText.includes('wantage') || rawText.includes('port jervis')) {
    townCounty = { town: 'Sussex', state: 'NJ', county: 'Sussex' };
  } else if (rawText.includes('vernon') || rawText.includes('mountain creek')) {
    townCounty = { town: 'Vernon', state: 'NJ', county: 'Sussex' };
  } else if (rawText.includes('sparta') || rawText.includes('newton') || rawText.includes('lafayette') || rawText.includes('branchville') || rawText.includes('andover')) {
    townCounty = { town: 'Sparta', state: 'NJ', county: 'Sussex' };

  // Somerset / Hunterdon / Warren / Burlington, NJ
  } else if (rawText.includes('basking ridge') || rawText.includes('bernardsville') || rawText.includes('bedminster')) {
    townCounty = { town: 'Bedminster', state: 'NJ', county: 'Somerset' };
  } else if (rawText.includes('flemington') || rawText.includes('clinton') || rawText.includes('lambertville') || rawText.includes('frenchtown') || rawText.includes('stockton') || rawText.includes('ringoes')) {
    townCounty = { town: 'Flemington', state: 'NJ', county: 'Hunterdon' };
  } else if (rawText.includes('hope') || rawText.includes('blairstown') || rawText.includes('hackettstown') || rawText.includes('belvidere') || rawText.includes('columbia')) {
    townCounty = { town: 'Blairstown', state: 'NJ', county: 'Warren' };
  } else if (rawText.includes('titanic') || rawText.includes('pemberton') || rawText.includes('pine barrens') || rawText.includes('batsto') || rawText.includes('chatsworth')) {
    townCounty = { town: 'Pemberton', state: 'NJ', county: 'Burlington' };

  // Rockland County, NY
  } else if (rawText.includes('nyack') || rawText.includes('piermont') || rawText.includes('grand view')) {
    townCounty = { town: 'Nyack', state: 'NY', county: 'Rockland' };
  } else if (rawText.includes('haverstraw') || rawText.includes('stony point')) {
    townCounty = { town: 'Haverstraw', state: 'NY', county: 'Rockland' };
  } else if (rawText.includes('suffern') || rawText.includes('sloatsburg') || rawText.includes('tallman')) {
    townCounty = { town: 'Suffern', state: 'NY', county: 'Rockland' };
  } else if (rawText.includes('pearl river') || rawText.includes('nanuet') || rawText.includes('orangeburg') || rawText.includes('tappan') || rawText.includes('sparkill')) {
    townCounty = { town: 'Pearl River', state: 'NY', county: 'Rockland' };
  } else if (rawText.includes('rockland lake') || rawText.includes('congers') || rawText.includes('valley cottage') || rawText.includes('new city')) {
    townCounty = { town: 'New City', state: 'NY', county: 'Rockland' };

  // Orange / Ulster / Westchester / Suffolk, NY
  } else if (rawText.includes('bear mountain') || rawText.includes('harriman') || rawText.includes('west point') || rawText.includes('cornwall') || rawText.includes('highland falls')) {
    townCounty = { town: 'Bear Mountain', state: 'NY', county: 'Orange' };
  } else if (rawText.includes('warwick') || rawText.includes('greenwood lake') || rawText.includes('goshen') || rawText.includes('middletown') || rawText.includes('monroe')) {
    townCounty = { town: 'Warwick', state: 'NY', county: 'Orange' };
  } else if (rawText.includes('new paltz') || rawText.includes('mohonk') || rawText.includes('minnewaska') || rawText.includes('gardiner') || rawText.includes('rosendale') || rawText.includes('kingston')) {
    townCounty = { town: 'New Paltz', state: 'NY', county: 'Ulster' };
  } else if (rawText.includes('katonah') || rawText.includes('acme') || rawText.includes('bedford') || rawText.includes('mount kisco') || rawText.includes('somers') || rawText.includes('pound ridge') || rawText.includes('yorktown') || rawText.includes('peekskill') || rawText.includes('croton') || rawText.includes('tarrytown')) {
    townCounty = { town: 'Katonah', state: 'NY', county: 'Westchester' };
  } else if (rawText.includes('catskill') || rawText.includes('hunter') || rawText.includes('windham') || rawText.includes('tannersville') || rawText.includes('phoenicia')) {
    townCounty = { town: 'Catskills Region', state: 'NY', county: 'Greene' };
  } else if (rawText.includes('peconic') || rawText.includes('shelter island') || rawText.includes('southold') || rawText.includes('greenport') || rawText.includes('riverhead') || rawText.includes('sag harbor') || rawText.includes('montauk')) {
    townCounty = { town: 'Peconic Bay', state: 'NY', county: 'Suffolk' };
  }

  // Compose clean location string
  let primaryName = mapName || (startLoc && startLoc !== 'North Jersey / Hudson Highlands' ? startLoc : '');
  if (!mapName && (rawText.includes('campgaw') || rawText.includes('darlington'))) {
    primaryName = 'Campgaw / Darlington';
  }
  if (townCounty) {
    if (primaryName && !primaryName.toLowerCase().includes(townCounty.town.toLowerCase())) {
      return `${primaryName}, ${townCounty.town}, ${townCounty.state} (${townCounty.county} County)`;
    } else if (primaryName) {
      return `${primaryName}, ${townCounty.state} (${townCounty.county} County)`;
    }
    return `${townCounty.town}, ${townCounty.state} (${townCounty.county} County)`;
  }

  if (primaryName) {
    return primaryName;
  }
  return 'North Jersey / Hudson Highlands (Bergen / Rockland County)';
}

/**
 * Extracts or synthesizes an authentic club ride description/overview.
 *
 * @param {Object} ride
 * @returns {string}
 */
export function extractRideDescription(ride) {
  if (!ride) return '';
  let raw = '';
  if (typeof ride.description === 'string' && ride.description.trim().length > 0) {
    raw = ride.description.trim();
  } else if (typeof ride.desc === 'string' && ride.desc.trim().length > 0) {
    raw = ride.desc.trim();
  } else if (typeof ride.notes === 'string' && ride.notes.trim().length > 0) {
    raw = ride.notes.trim();
  }

  if (raw) {
    // 1. Normalize FontAwesome / PUA bicycle icons and HTML representations to standard Unicode bike emoji
    let cleaned = raw
      .replace(/<i[^>]*class=["'][^"']*(?:fa-bicycle|fa-bike)[^"']*["'][^>]*>[\s\S]*?<\/i>/gi, ' 🚲 ')
      .replace(/<i[^>]*class=["'][^"']*(?:fa-biking|fa-person-biking)[^"']*["'][^>]*>[\s\S]*?<\/i>/gi, ' 🚴 ')
      .replace(/<i[^>]*class=["'][^"']*\bfa\b[^"']*["'][^>]*>([\s\S]*?)<\/i>/gi, (match, inner) => {
        if (/&#x0*f206;|\uf206|&#0*61958;/i.test(inner)) return ' 🚲 ';
        if (/&#x0*f84a;|\uf84a|&#0*63562;/i.test(inner)) return ' 🚴 ';
        return match;
      })
      .replace(/&#x0*f206;/gi, ' 🚲 ')
      .replace(/&#0*61958;/g, ' 🚲 ')
      .replace(/\uf206/g, ' 🚲 ')
      .replace(/&#x0*f84a;/gi, ' 🚴 ')
      .replace(/&#0*63562;/g, ' 🚴 ')
      .replace(/\uf84a/g, ' 🚴 ')
      // Strip URLs, HTML tags, and sanitize entities
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&(?:quot|#34);/gi, '"')
      .replace(/&(?:apos|#39);/gi, "'")
      .replace(/https?:\/\/[^\s]+/g, '')
      .replace(/(\r\n|\n|\r)+/gm, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (cleaned.length > 0) return cleaned;
  }

  const miles = Number(ride.miles) || 0;
  const climb = Number(ride.climbFt ?? ride.elevation ?? ride.climb ?? 0);
  const ratio = miles > 0 ? Math.round(climb / miles) : 0;

  let climbProfile = 'rolling hills and punchy crests';
  if (ratio >= 70) climbProfile = 'sustained mountainous ascents and scenic technical descents';
  else if (ratio >= 45) climbProfile = 'challenging rolling terrain and steady power climbs';
  else if (ratio < 25) climbProfile = 'fast paceline flats and gentle rolling sweeps';

  return `Scenic club route featuring ${climbProfile} across scenic rural backroads with group drafting and mid-ride regrouping.`;
}

/**
 * Formats ride date and meet time into a unified schedule string.
 * Example: 'Sat, Sep 19, 2026 · 8:30 AM'
 *
 * @param {Object} ride
 * @returns {string}
 */
export function formatRideMeetDateTime(ride, options = {}) {
  if (!ride) return '';
  const dStr = ride.dateIso || (ride.dateStr && String(ride.dateStr).match(/\d{4}-\d{2}-\d{2}/)?.[0]);
  let dateFormatted = '';
  if (dStr) {
    const match = String(dStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const year = parseInt(match[1], 10);
      const monthIdx = parseInt(match[2], 10) - 1;
      const day = parseInt(match[3], 10);
      const dObj = new Date(year, monthIdx, day);
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const dayName = dayNames[dObj.getDay()] || '';
      const monthName = monthNames[monthIdx] || '';
      dateFormatted = dayName ? `${dayName}, ${monthName} ${day}, ${year}` : `${monthName} ${day}, ${year}`;
    } else {
      dateFormatted = String(dStr);
    }
  } else if (ride.dateText) {
    dateFormatted = ride.dateText;
  }

  if (options && options.dateOnly) {
    return dateFormatted;
  }

  const rawTime = ride.timeRaw || ride.time24 || ride.meetTime || ride.time || null;
  const timeFormatted = formatTime(rawTime);

  if (dateFormatted && timeFormatted) {
    return `${dateFormatted} · ${timeFormatted}`;
  }
  return dateFormatted || timeFormatted || '';
}

/**
 * Synthesizes the structured plaintext stream for the typing animation briefing.
 *
 * @param {Object} ride
 * @param {Object} options
 * @returns {string}
 */
export function buildRideBriefingStream(ride, options = {}) {
  if (!ride) return '';

  const blocks = [];

  const title = ride.title || 'BTCNJ Club Ride';
  const meetStr = formatRideMeetDateTime(ride, options);
  const category = ride.category || '';

  const miVal = Number(ride.miles || 0);
  const climbVal = Number(ride.climbFt ?? ride.elevation ?? ride.climb ?? 0);
  const metrics = [];
  if (miVal > 0) {
    metrics.push(`${miVal % 1 === 0 ? String(Math.round(miVal)) : miVal.toFixed(1)} mi`);
  }
  if (climbVal > 0) {
    metrics.push(`${Math.round(climbVal).toLocaleString()} ft`);
  }

  const isMobile = Boolean(options && options.isMobile);

  if (isMobile) {
    // Row 1 (Mobile): Title only
    blocks.push(title);

    // Row 2 (Mobile): Date & Time
    if (meetStr) {
      blocks.push(meetStr);
    }

    // Row 3 (Mobile): Category, Miles & Climb
    const telemetryParts = [];
    if (category) {
      telemetryParts.push(category);
    }
    if (metrics.length > 0) {
      telemetryParts.push(metrics.join(' · '));
    }
    if (telemetryParts.length > 0) {
      blocks.push(telemetryParts.join(' · '));
    }
  } else {
    // Desktop: Unified single-line header
    const line1Parts = [title];
    if (meetStr) {
      line1Parts.push(meetStr);
    }
    if (category) {
      line1Parts.push(category);
    }
    if (metrics.length > 0) {
      line1Parts.push(metrics.join(' · '));
    }
    blocks.push(line1Parts.join(' · '));
  }

  // Line 2 (or Row 4 on Mobile): Leaders · Attendance Roster · Start Location
  const line2Parts = [];

  const leadersArr = normalizeRideLeaders(ride.leaders);
  const firstNames = leadersArr
    .map(name => formatLeaderDisplayName(name, { firstNameOnly: true }))
    .filter(Boolean);

  const leadsStr = firstNames.join(', ');
  if (leadsStr) {
    const leaderLabel = firstNames.length === 1 ? 'LEAD' : 'LEADS';
    line2Parts.push(`${leaderLabel}: ${leadsStr}`);
  }

  const riderCount = getRiderAttendanceCount(ride);
  if (riderCount > 0) {
    line2Parts.push(`${riderCount} Riders`);
  }

  const loc = formatRideStartLocation(ride);
  if (loc) {
    line2Parts.push(`START: ${loc}`);
  }

  if (line2Parts.length > 0) {
    blocks.push(line2Parts.join(' · '));
  }

  // 3. Narrative Ride Description (preceded by intentional blank line for visual breathing room)
  const desc = extractRideDescription(ride);
  if (desc) {
    blocks.push(`\nOVERVIEW: ${desc}`);
  }

  return blocks.join('\n')
    .replace(/\uf206/g, '🚲')
    .replace(/\uf84a/g, '🚴')
    .replace(/&#x0*f206;/gi, '🚲')
    .replace(/&#0*61958;/g, '🚲');
}

/**
 * Generates the HTML shell for the briefing typewriter terminal.
 *
 * @returns {string}
 */
export function renderBriefingTerminalHtml() {
  return `
    <div class="spotlight-briefing-terminal">
      <div class="briefing-text-viewport" aria-live="polite"><div class="briefing-corner-badge" aria-label="Ride Briefing Active"><span class="briefing-beacon-dot" aria-hidden="true"></span><span class="briefing-badge-text">RIDE BRIEFING</span></div><span class="briefing-typed-text"></span><span class="briefing-caret" aria-hidden="true"></span></div>
    </div>
  `;
}

/**
 * 4-Line ASCII Cycling Peloton & Paved Road Divider
 * Engineered for Consolas / monospaced fixed-width fonts (3 cyclists + paved road).
 * Compact 34-column footprint strictly prevents line-wrapping across mobile viewports.
 * Cyclists are centered over the 34-char road with equal 2-column margins on both flanks.
 */
export const ASCII_PELOTON_DIVIDER = [
  '     __o        __o        __o',
  '   _`\\<,_     _`\\<,_     _`\\<,_',
  '  (*)/ (*)   (*)/ (*)   (*)/ (*)',
  '=================================='
].join('\n');

// Backward compatibility alias
export const ASCII_PELOTON_DIVIDER_3 = ASCII_PELOTON_DIVIDER;
export const ASCII_PELOTON_DIVIDER_5 = ASCII_PELOTON_DIVIDER;

export function getAsciiPelotonDivider() {
  return ASCII_PELOTON_DIVIDER;
}

/**
 * Transforms raw briefing plaintext into safe, highlighted HTML for terminal rendering.
 * Highlights Row 1 (Title & Telemetry) and Field Labels (LEADS:, START:, OVERVIEW:) in cyan,
 * Paved Road in authentic highway yellow, and dot separators in crisp white.
 *
 * @param {string} plainText
 * @returns {string}
 */
export function formatBriefingTerminalHtml(plainText, typoText = '') {
  return TypewriterBriefingEngine.formatHtml(plainText, typoText);
}

/**
 * TypewriterBriefingEngine simulates organic, human-like typing cadence with
 * natural punctuation pauses, automatic vertical viewport scrolling, and loop cycling.
 */
export class TypewriterBriefingEngine {
  static formatHtml(plainText, typoText = '') {
    if (!plainText && !typoText) return '';

    const lines = plainText.split('\n');
    const formattedLines = [];
    let headerPhase = 0; // 0: expecting Title / Desktop unified line, 1: expecting Mobile Date/Time, 2: expecting Mobile Category/Telemetry, -1: header done

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Escape raw HTML entities to prevent malformed tags or injection
      let escaped = line
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

      // Divider detection: peloton cyclists and paved yellow road
      const isRoadLine = escaped.includes('====') || (/^={2,}$/.test(escaped.trim()));
      const isCyclistLine = escaped.includes('__o') ||
                            escaped.includes('(*)/') ||
                            escaped.includes('\\<,');

      if (isRoadLine) {
        formattedLines.push(`<span class="briefing-hl-road">${escaped}</span>`);
        if (escaped.includes('==================================')) {
          headerPhase = 0; // The line after full road divider will be next loop's title/telemetry header
        }
        continue;
      }

      if (isCyclistLine) {
        formattedLines.push(`<span class="briefing-hl-divider">${escaped}</span>`);
        continue;
      }

      // Check if this line is a label line or narrative, which terminates the header phase
      const trimmedLine = escaped.trim();
      if (/^(LEADS?:|START:|OVERVIEW:|\b(LEADS?|START|OVERVIEW)\b|^(L|LE|LEA)$|^(ST|STA|STAR)$|^(O|OV|OVE|OVER|OVERV|OVERVI|OVERVIE)$)/i.test(trimmedLine)) {
        headerPhase = -1;
      }

      if (headerPhase === 0 && trimmedLine.length > 0) {
        const rawSegments = escaped.split('·');
        const hasHeaderTelemetry = rawSegments.length > 1 && rawSegments.slice(1).some(seg => {
          const t = seg.trim();
          return /^[A-E](\*|\+|-)|\b(Open|Intro|Casual)\b/i.test(t) ||
                 /\b\d+(?:\.\d+)?\s*(mi|ft)\b/i.test(t) ||
                 /\b(Mon|Tue|Wed|Thu|Fri|Sat|Sun|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|\d{1,2}:\d{2}[ap]?m?)\b/i.test(t);
        });

        if (hasHeaderTelemetry) {
          // Desktop mode: Unified single-line header containing title, datetime, category, telemetry
          const formattedSegments = [];
          let seenCategory = false;

          for (let idx = 0; idx < rawSegments.length; idx++) {
            const rawSeg = rawSegments[idx];
            if (idx === 0) {
              formattedSegments.push(`<span class="briefing-hl-title">${rawSeg}</span>`);
              continue;
            }

            const trimmed = rawSeg.trim();
            const isCategory = /^[A-E](\*|\+|-)|\b(Open|Intro|Casual)\b/i.test(trimmed) || (!seenCategory && /^[A-E]\*?$/i.test(trimmed));
            const isTelemetry = /\b\d+(?:\.\d+)?\s*(mi|ft)\b/i.test(trimmed) || (seenCategory && /^\s*[\d,.]+(\s*(mi|ft)?)?\s*$/i.test(trimmed));

            if (isCategory) {
              seenCategory = true;
              formattedSegments.push(`<span class="briefing-hl-category">${rawSeg}</span>`);
            } else if (isTelemetry) {
              formattedSegments.push(`<span class="briefing-hl-telemetry">${rawSeg}</span>`);
            } else {
              formattedSegments.push(`<span class="briefing-hl-datetime">${rawSeg}</span>`);
            }
          }

          formattedLines.push(formattedSegments.join('<span class="briefing-hl-dot">·</span>'));
          headerPhase = -1;
          continue;
        } else {
          // Mobile mode: Row 1 is Title only
          formattedLines.push(`<span class="briefing-hl-title">${escaped}</span>`);
          headerPhase = 1;
          continue;
        }
      }

      if (headerPhase === 1 && trimmedLine.length > 0) {
        // Mobile mode: Row 2 is Date & Time
        // Check if this line is category/telemetry instead of date/time (e.g. if meet date was absent)
        const rawSegs = escaped.split('·');
        const isCatOrTelem = rawSegs.some(s => {
          const t = s.trim();
          return /^[A-E](\*|\+|-)|\b(Open|Intro|Casual)\b/i.test(t) || /\b\d+(?:\.\d+)?\s*(mi|ft)\b/i.test(t);
        });

        if (isCatOrTelem) {
          headerPhase = 2; // Handled by headerPhase === 2 logic below
        } else {
          const formattedSegments = rawSegs.map(seg => `<span class="briefing-hl-datetime">${seg}</span>`);
          formattedLines.push(formattedSegments.join('<span class="briefing-hl-dot">·</span>'));
          headerPhase = 2;
          continue;
        }
      }

      if (headerPhase === 2 && trimmedLine.length > 0) {
        // Mobile mode: Row 3 is Category, Miles & Climb
        const rawSegs = escaped.split('·');
        const formattedSegments = [];
        let seenCategory = false;

        for (let idx = 0; idx < rawSegs.length; idx++) {
          const rawSeg = rawSegs[idx];
          const trimmed = rawSeg.trim();
          const isCategory = /^[A-E](\*|\+|-)|\b(Open|Intro|Casual)\b/i.test(trimmed) || (!seenCategory && /^[A-E]\*?$/i.test(trimmed));
          const isTelemetry = /\b\d+(?:\.\d+)?\s*(mi|ft)\b/i.test(trimmed) || (seenCategory && /^\s*[\d,.]+(\s*(mi|ft)?)?\s*$/i.test(trimmed));

          if (isCategory) {
            seenCategory = true;
            formattedSegments.push(`<span class="briefing-hl-category">${rawSeg}</span>`);
          } else if (isTelemetry) {
            formattedSegments.push(`<span class="briefing-hl-telemetry">${rawSeg}</span>`);
          } else {
            formattedSegments.push(`<span class="briefing-hl-telemetry">${rawSeg}</span>`);
          }
        }

        formattedLines.push(formattedSegments.join('<span class="briefing-hl-dot">·</span>'));
        headerPhase = -1;
        continue;
      }

      // Middle dot separators in subsequent lines styled uniformly as crisp white
      escaped = escaped.replace(/·/g, '<span class="briefing-hl-dot">·</span>');

      // Row 2 / Narrative rows: Highlight field labels LEADS:, LEAD:, START:, OVERVIEW:
      // Support full labels AND keystroke-by-keystroke partial prefixes so highlighting is 100% active during live typing
      if (/^(L|LE|LEA)$/.test(escaped)) {
        escaped = `<span class="briefing-hl-label">${escaped}</span>`;
      } else if (/^(O|OV|OVE|OVER|OVERV|OVERVI|OVERVIE)$/.test(escaped)) {
        escaped = `<span class="briefing-hl-label">${escaped}</span>`;
      } else {
        escaped = escaped.replace(/\b(LEADS?:?|START:?|OVERVIEW:?)/g, '<span class="briefing-hl-label">$1</span>');
        escaped = escaped.replace(/(·\s*|<span class="briefing-hl-dot">·<\/span>\s*)(S|ST|STA|STAR)$/, '$1<span class="briefing-hl-label">$2</span>');
      }
      formattedLines.push(escaped);
    }

    let outputHtml = formattedLines.join('\n');
    if (typoText) {
      const escapedTypo = typoText
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      const typoInlineStyle = 'color: #fb7185 !important; font-style: italic !important; font-weight: 700 !important; text-decoration: underline wavy rgba(251, 113, 133, 0.9) !important; text-underline-offset: 3px !important; text-decoration-thickness: 1.5px !important; text-shadow: 0 0 8px rgba(251, 113, 133, 0.6) !important;';
      outputHtml += `<span class="briefing-easter-egg-typo" style="${typoInlineStyle}">${escapedTypo}</span>`;
    }
    return outputHtml;
  }

  constructor(containers, fullText, options = {}) {
    this.containers = Array.isArray(containers) ? containers : [containers];
    this.fullText = fullText || '';
    if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
      const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
      this.segments = Array.from(segmenter.segment(this.fullText), s => s.segment);
    } else {
      this.segments = Array.from(this.fullText);
    }
    this.dwellSeconds = options.dwellSeconds || 5;
    this.maxHistory = options.maxHistory || 1;
    this.history = [];
    this.accumulatedText = '';
    this.currentIndex = 0;
    this.timer = null;
    this.isPaused = false;
    this.isFastForwarding = false;
    this.destroyed = false;
    this.isReducedMotion = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.isTypingDivider = false;
    this.isDwelling = false;
    this.dividerSegments = [];
    this.dividerIndex = 0;
    this.activeDividerText = '';

    // Whimsical Cycling Easter Egg Typo Engine Configuration (10s - 20s cadence)
    this.easterEggsEnabled = options.easterEggsEnabled !== false;
    this.easterEggIntervalMin = options.easterEggIntervalMin || 10000;
    this.easterEggIntervalMax = options.easterEggIntervalMax || 20000;
    this.easterEggDwellBase = typeof options.easterEggDwellBase === 'number' ? options.easterEggDwellBase : 5150;
    this.leads = options.leads || [];
    this.members = options.members || [];
    this.easterEggState = 'IDLE'; // 'IDLE' | 'TYPING_TYPO' | 'PAUSE_REALIZATION' | 'BACKSPACING_PARTIAL' | 'TYPING_SECOND_THOUGHT' | 'PAUSE_SECOND' | 'BACKSPACING'
    this.typoBuffer = '';
    this.activeEasterEgg = null;
    this.typoTarget = '';
    this.typoIndex = 0;
    this.lastEasterEggTime = Date.now();
    this.nextEasterEggInterval = this.easterEggIntervalMin + Math.random() * (this.easterEggIntervalMax - this.easterEggIntervalMin);

    this.onVisibilityChange = () => {
      if (document.hidden) {
        this.pause();
      } else {
        this.resume();
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    this.boundClickHandler = (e) => {
      if (typeof e?.target?.closest === 'function' && e.target.closest('a, button')) return;
      this.fastForward(2000);
    };

    this.containers.forEach(el => {
      if (el) {
        if (typeof el.addEventListener === 'function') {
          el.addEventListener('click', this.boundClickHandler);
        }
        if (typeof el.setAttribute === 'function') {
          el.setAttribute('title', 'Click to fast-forward briefing');
        }
      }
    });
  }

  start() {
    if (this.destroyed) return;
    if (this.isReducedMotion) {
      this.renderFullTextInstantly();
      return;
    }
    this.history = [];
    this.accumulatedText = '';
    this.currentIndex = 0;
    this.isTypingDivider = false;
    this.isDwelling = false;
    this.typoBuffer = '';
    this.activeEasterEgg = null;
    this.easterEggState = 'IDLE';
    this.lastEasterEggTime = Date.now();
    this.nextEasterEggInterval = this.easterEggIntervalMin + Math.random() * (this.easterEggIntervalMax - this.easterEggIntervalMin);
    this.renderText('');
    this.scheduleNextChar();
  }

  pause() {
    this.isPaused = true;
    clearTimeout(this.timer);
  }

  resume() {
    if (this.destroyed || !this.isPaused) return;
    this.isPaused = false;
    if (this.easterEggState === 'TYPING_TYPO') {
      this.stepEasterEggTyping(false);
    } else if (this.easterEggState === 'TYPING_SECOND_THOUGHT') {
      this.stepEasterEggTyping(true);
    } else if (this.easterEggState === 'BACKSPACING_PARTIAL' || this.easterEggState === 'BACKSPACING') {
      this.stepEasterEggBackspacing(this.easterEggState === 'BACKSPACING_PARTIAL' ? Math.max(0, Math.floor(this.typoBuffer.length * 0.4)) : 0, this.easterEggState === 'BACKSPACING_PARTIAL' ? 'SECOND_THOUGHT' : 'IDLE');
    } else if (this.easterEggState === 'PAUSE_REALIZATION' || this.easterEggState === 'PAUSE_SECOND') {
      this.easterEggState = 'BACKSPACING';
      this.stepEasterEggBackspacing(0, 'IDLE');
    } else if (this.isDwelling) {
      this.timer = setTimeout(() => {
        if (this.destroyed || this.isPaused) return;
        this.isDwelling = false;
        this.startTypingDivider();
      }, 500);
    } else if (this.isTypingDivider) {
      this.scheduleNextDividerChar();
    } else {
      this.scheduleNextChar();
    }
  }

  fastForward(durationMs = 2000) {
    if (this.destroyed || this.isPaused || this.isFastForwarding) return;

    if (this.easterEggState !== 'IDLE' || this.typoBuffer) {
      this.typoBuffer = '';
      this.activeEasterEgg = null;
      this.easterEggState = 'IDLE';
      this.lastEasterEggTime = Date.now();
    }

    if (this.isDwelling) {
      clearTimeout(this.timer);
      this.isDwelling = false;
      this.startTypingDivider();
      return;
    }

    if (this.isTypingDivider) {
      const totalDiv = this.dividerSegments.length;
      if (this.dividerIndex >= totalDiv) return;

      this.isFastForwarding = true;
      clearTimeout(this.timer);
      this.containers.forEach(el => el?.classList?.add('is-fast-forwarding'));

      const startDivTime = Date.now();
      const startDivIdx = this.dividerIndex;
      const remainingDivSegments = totalDiv - startDivIdx;
      const targetDivDuration = Math.min(600, Math.max(100, durationMs / 3));

      const divStep = () => {
        if (this.destroyed || this.isPaused) {
          this.isFastForwarding = false;
          this.containers.forEach(el => el?.classList?.remove('is-fast-forwarding'));
          return;
        }

        const elapsed = Date.now() - startDivTime;
        const progress = Math.min(1, elapsed / targetDivDuration);
        this.dividerIndex = Math.min(totalDiv, startDivIdx + Math.ceil(progress * remainingDivSegments));
        this.renderText(this.accumulatedText + this.fullText + this.dividerSegments.slice(0, this.dividerIndex).join(''));

        if (this.dividerIndex < totalDiv && progress < 1) {
          this.timer = setTimeout(divStep, 16);
        } else {
          this.dividerIndex = totalDiv;
          this.renderText(this.accumulatedText + this.fullText + this.activeDividerText);
          this.isFastForwarding = false;
          this.containers.forEach(el => el?.classList?.remove('is-fast-forwarding'));
          this.finishDividerAndRestart();
        }
      };
      divStep();
      return;
    }

    const total = this.segments.length;
    if (this.currentIndex >= total) return;

    this.isFastForwarding = true;
    clearTimeout(this.timer);
    this.containers.forEach(el => el?.classList?.add('is-fast-forwarding'));

    const startTime = Date.now();
    const startIdx = this.currentIndex;
    const remainingSegments = total - startIdx;
    const targetDuration = Math.max(150, durationMs);

    const step = () => {
      if (this.destroyed || this.isPaused) {
        this.isFastForwarding = false;
        this.containers.forEach(el => el?.classList?.remove('is-fast-forwarding'));
        return;
      }

      const elapsed = Date.now() - startTime;
      const progress = Math.min(1, elapsed / targetDuration);
      this.currentIndex = Math.min(total, startIdx + Math.ceil(progress * remainingSegments));
      this.renderText(this.accumulatedText + this.segments.slice(0, this.currentIndex).join(''));

      if (this.currentIndex < total && progress < 1) {
        this.timer = setTimeout(step, 16);
      } else {
        this.currentIndex = total;
        this.renderText(this.accumulatedText + this.fullText);
        this.isFastForwarding = false;
        this.containers.forEach(el => el?.classList?.remove('is-fast-forwarding'));

        // Dwell pause, then typewriter divider
        this.isDwelling = true;
        this.timer = setTimeout(() => {
          if (this.destroyed || this.isPaused) return;
          this.isDwelling = false;
          this.startTypingDivider();
        }, this.dwellSeconds * 1000);
      }
    };
    step();
  }

  destroy() {
    this.destroyed = true;
    this.isFastForwarding = false;
    this.isTypingDivider = false;
    this.isDwelling = false;
    this.typoBuffer = '';
    this.activeEasterEgg = null;
    this.easterEggState = 'IDLE';
    clearTimeout(this.timer);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    this.containers.forEach(el => {
      if (el) {
        if (typeof el.removeEventListener === 'function') {
          el.removeEventListener('click', this.boundClickHandler);
        }
        el.classList?.remove('is-fast-forwarding');
      }
    });
  }

  renderFullTextInstantly() {
    this.typoBuffer = '';
    this.activeEasterEgg = null;
    this.easterEggState = 'IDLE';
    this.containers.forEach(el => {
      const textSpan = el?.querySelector?.('.briefing-typed-text');
      if (textSpan) {
        const full = this.accumulatedText ? (this.accumulatedText + this.fullText) : this.fullText;
        textSpan.innerHTML = TypewriterBriefingEngine.formatHtml(full);
        if (typeof textSpan.isConnected === 'undefined') {
          textSpan.textContent = full;
        }
      }
    });
  }

  renderText(text, typoText = '') {
    this.containers.forEach(el => {
      const textSpan = el?.querySelector?.('.briefing-typed-text');
      const viewport = el?.querySelector?.('.briefing-text-viewport');
      const clientH = viewport?.clientHeight || 0;
      // Pre-sample bottom proximity BEFORE DOM mutation to prevent multi-line wrap deadlocks
      const wasNearBottom = Boolean(!viewport || clientH === 0 || (viewport.scrollHeight - viewport.scrollTop - clientH) <= 50);

      if (textSpan) {
        textSpan.innerHTML = TypewriterBriefingEngine.formatHtml(text, typoText);
        if (typeof textSpan.isConnected === 'undefined') {
          textSpan.textContent = text + typoText;
        }
      }
      if (viewport) {
        if (wasNearBottom || this.isFastForwarding) {
          viewport.scrollTop = viewport.scrollHeight;
        }
      }
    });
  }

  scheduleNextChar() {
    if (this.destroyed || this.isPaused || this.isFastForwarding) return;

    if (this.currentIndex >= this.segments.length) {
      this.isDwelling = true;
      this.timer = setTimeout(() => {
        if (this.destroyed || this.isPaused) return;
        this.isDwelling = false;
        this.startTypingDivider();
      }, this.dwellSeconds * 1000);
      return;
    }

    const char = this.segments[this.currentIndex];
    this.currentIndex++;
    this.renderText(this.accumulatedText + this.segments.slice(0, this.currentIndex).join(''));

    // Check if we should trigger an authentic cycling easter egg blunder
    const currentText = this.segments.slice(0, this.currentIndex).join('');
    const isNarrative = currentText.includes('LEADS') || currentText.includes('START') || currentText.includes('OVERVIEW') || this.currentIndex > 35;
    const isNearEnd = this.currentIndex >= this.segments.length - 15;
    const isWordBoundary = char === ' ' || char === '\n';
    const now = Date.now();
    const timeSinceLast = now - this.lastEasterEggTime;

    if (
      this.easterEggsEnabled &&
      !this.isReducedMotion &&
      this.easterEggState === 'IDLE' &&
      isNarrative &&
      !isNearEnd &&
      isWordBoundary &&
      timeSinceLast >= this.nextEasterEggInterval
    ) {
      this.startEasterEgg();
      return;
    }

    let delay = 23 + Math.floor(Math.random() * 17); // +50% speed increase: 23ms - 40ms base

    if (char === '\n') {
      delay = 250 + Math.floor(Math.random() * 80);
    } else if (char === '.' || char === '!' || char === '?') {
      delay = 410 + Math.floor(Math.random() * 100);
    } else if (char === ',' || char === ';' || char === ':' || char === '-') {
      delay = 210 + Math.floor(Math.random() * 55);
    } else if (char === ' ' && Math.random() < 0.08) {
      delay += 90;
    }

    this.timer = setTimeout(() => {
      this.scheduleNextChar();
    }, delay);
  }

  startEasterEgg() {
    if (this.destroyed || this.isPaused || this.isFastForwarding) return;
    const egg = getRandomEasterEgg({ leads: this.leads, members: this.members });
    if (!egg || !egg.text) return;

    this.activeEasterEgg = egg;
    this.easterEggState = 'TYPING_TYPO';
    this.typoTarget = egg.text;
    this.typoIndex = 0;
    this.typoBuffer = '';

    // Brief hesitation before typing blunder
    this.timer = setTimeout(() => {
      this.stepEasterEggTyping(false);
    }, 80 + Math.floor(Math.random() * 100));
  }

  stepEasterEggTyping(isSecondThought = false) {
    if (this.destroyed || this.isPaused || this.isFastForwarding) return;

    if (this.typoIndex < this.typoTarget.length) {
      this.typoBuffer += this.typoTarget[this.typoIndex];
      this.typoIndex++;
      const authenticPrefix = this.segments.slice(0, this.currentIndex).join('');
      this.renderText(this.accumulatedText + authenticPrefix, this.typoBuffer);

      const c = this.typoTarget[this.typoIndex - 1];
      let charDelay = 22 + Math.floor(Math.random() * 16);
      if (c === ' ') charDelay += 25;
      if (c === ',' || c === ':') charDelay += 60;
      if (c === '.' || c === '!' || c === '?') charDelay += 100;

      this.timer = setTimeout(() => {
        this.stepEasterEggTyping(isSecondThought);
      }, charDelay);
    } else {
      if (!isSecondThought) {
        this.easterEggState = 'PAUSE_REALIZATION';
        // Calibrated dwell duration (-1s reduction): 5150ms - 5750ms so readers have ample time to read the blunder
        const pauseDuration = (this.easterEggDwellBase || 5150) + Math.floor(Math.random() * 600);
        this.timer = setTimeout(() => {
          if (this.destroyed || this.isPaused || this.isFastForwarding) return;
          if (this.activeEasterEgg && this.activeEasterEgg.followup) {
            this.easterEggState = 'BACKSPACING_PARTIAL';
            const targetLen = Math.max(0, Math.floor(this.typoBuffer.length * (0.35 + Math.random() * 0.25)));
            this.stepEasterEggBackspacing(targetLen, 'SECOND_THOUGHT');
          } else {
            this.easterEggState = 'BACKSPACING';
            this.stepEasterEggBackspacing(0, 'IDLE');
          }
        }, pauseDuration);
      } else {
        this.easterEggState = 'PAUSE_SECOND';
        // Calibrated dwell duration (-1s reduction): 5200ms - 5800ms for second-thought punchline
        const pauseDuration = ((this.easterEggDwellBase ? this.easterEggDwellBase + 50 : 5200)) + Math.floor(Math.random() * 600);
        this.timer = setTimeout(() => {
          if (this.destroyed || this.isPaused || this.isFastForwarding) return;
          this.easterEggState = 'BACKSPACING';
          this.stepEasterEggBackspacing(0, 'IDLE');
        }, pauseDuration);
      }
    }
  }

  stepEasterEggBackspacing(targetLength = 0, nextState = 'IDLE') {
    if (this.destroyed || this.isPaused || this.isFastForwarding) return;

    if (this.typoBuffer.length > targetLength) {
      this.typoBuffer = this.typoBuffer.slice(0, -1);
      const authenticPrefix = this.segments.slice(0, this.currentIndex).join('');
      this.renderText(this.accumulatedText + authenticPrefix, this.typoBuffer);

      const backspaceCadence = 18 + Math.floor(Math.random() * 10);
      this.timer = setTimeout(() => {
        this.stepEasterEggBackspacing(targetLength, nextState);
      }, backspaceCadence);
    } else {
      if (nextState === 'SECOND_THOUGHT' && this.activeEasterEgg && this.activeEasterEgg.followup) {
        // Paired hesitation pause before second blunder
        const hesitation = 650 + Math.floor(Math.random() * 350);
        this.timer = setTimeout(() => {
          if (this.destroyed || this.isPaused || this.isFastForwarding) return;
          this.easterEggState = 'TYPING_SECOND_THOUGHT';
          this.typoTarget = ' ' + this.activeEasterEgg.followup;
          this.typoIndex = 0;
          this.stepEasterEggTyping(true);
        }, hesitation);
      } else {
        this.finishEasterEgg();
      }
    }
  }

  finishEasterEgg() {
    this.typoBuffer = '';
    this.activeEasterEgg = null;
    this.easterEggState = 'IDLE';
    this.lastEasterEggTime = Date.now();
    this.nextEasterEggInterval = this.easterEggIntervalMin + Math.random() * (this.easterEggIntervalMax - this.easterEggIntervalMin);

    const authenticPrefix = this.segments.slice(0, this.currentIndex).join('');
    this.renderText(this.accumulatedText + authenticPrefix);

    this.timer = setTimeout(() => {
      if (this.destroyed || this.isPaused || this.isFastForwarding) return;
      this.scheduleNextChar();
    }, 140 + Math.floor(Math.random() * 120));
  }

  startTypingDivider() {
    if (this.destroyed || this.isPaused) return;
    this.isTypingDivider = true;

    const firstViewport = this.containers[0]?.querySelector?.('.briefing-text-viewport') || this.containers[0];
    const width = firstViewport?.clientWidth || 1000;
    const pelotonArt = getAsciiPelotonDivider(width);
    this.activeDividerText = `\n\n${pelotonArt}\n\n`;

    if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
      const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
      this.dividerSegments = Array.from(segmenter.segment(this.activeDividerText), s => s.segment);
    } else {
      this.dividerSegments = Array.from(this.activeDividerText);
    }
    this.dividerIndex = 0;
    this.scheduleNextDividerChar();
  }

  scheduleNextDividerChar() {
    if (this.destroyed || this.isPaused || this.isFastForwarding) return;

    if (this.dividerIndex >= this.dividerSegments.length) {
      this.finishDividerAndRestart();
      return;
    }

    const char = this.dividerSegments[this.dividerIndex];
    this.dividerIndex++;
    this.renderText(this.accumulatedText + this.fullText + this.dividerSegments.slice(0, this.dividerIndex).join(''));

    let delay = 18 + Math.floor(Math.random() * 10); // 18ms - 28ms base cadence
    if (char === '\n') {
      delay = 130 + Math.floor(Math.random() * 40); // Natural carriage return delay between lines
    } else if (char === '=') {
      delay = 14 + Math.floor(Math.random() * 8); // Smooth paved road stroke
    } else if (char === ' ') {
      delay = 10;
    }

    this.timer = setTimeout(() => {
      this.scheduleNextDividerChar();
    }, delay);
  }

  finishDividerAndRestart() {
    if (this.destroyed || this.isPaused) return;
    this.isTypingDivider = false;
    this.isDwelling = false;
    this.typoBuffer = '';
    this.activeEasterEgg = null;
    this.easterEggState = 'IDLE';
    this.lastEasterEggTime = Date.now();
    this.nextEasterEggInterval = this.easterEggIntervalMin + Math.random() * (this.easterEggIntervalMax - this.easterEggIntervalMin);

    const divider = this.activeDividerText || `\n\n${getAsciiPelotonDivider()}\n\n`;
    this.history.push(this.fullText);
    if (this.history.length > this.maxHistory) {
      this.history.shift();
    }
    this.accumulatedText = this.history.length > 0 ? (this.history.join(divider) + divider) : '';

    this.currentIndex = 0;
    this.renderText(this.accumulatedText);

    this.timer = setTimeout(() => {
      if (this.destroyed || this.isPaused) return;
      this.scheduleNextChar();
    }, 450);
  }

  appendDividerAndRestart() {
    this.finishDividerAndRestart();
  }
}

let activeBriefingTypewriter = null;

export function stopBriefingTypewriter() {
  if (activeBriefingTypewriter) {
    activeBriefingTypewriter.destroy();
    activeBriefingTypewriter = null;
  }
}

export function startBriefingTypewriter(ride, rootEl) {
  stopBriefingTypewriter();
  if (!ride || !rootEl) return null;

  const terminals = Array.from(rootEl.querySelectorAll('.spotlight-briefing-terminal'));
  if (terminals.length === 0) return null;

  const isMobile = typeof window !== 'undefined' && (
    (window.matchMedia && window.matchMedia('(max-width: 899px)').matches) ||
    (window.innerWidth > 0 && window.innerWidth <= 899)
  );

  const streamText = buildRideBriefingStream(ride, { isMobile });
  const leads = ride.leaders || [];
  const members = ride.members || ride.riders || ride.joinedMembers || [];
  activeBriefingTypewriter = new TypewriterBriefingEngine(terminals, streamText, {
    dwellSeconds: 5,
    ride,
    leads,
    members
  });
  activeBriefingTypewriter.start();
  return activeBriefingTypewriter;
}

/**
 * Renders the focused Ride Spotlight media page for a single ride.
 * @param {string|number} targetRideId
 * @param {Array<object>} ridesData
 * @param {HTMLElement} [container]
 * @param {HTMLElement} [countBadge]
 */
export async function renderSpotlightView(targetRideId, ridesData, container, countBadge, options = {}) {
  const mountEl = container || document.getElementById('rides-container');
  const badgeEl = countBadge || document.getElementById('rides-count-badge');
  if (!mountEl) return;

  if (!options._internal && mountEl.children.length > 0 && !mountEl.querySelector('.loading-frame') && !options.skipAnimation) {
    return transitionWithPeel(mountEl, async (peelMount) => {
      await renderSpotlightView(targetRideId, ridesData, peelMount, badgeEl, { ...options, _internal: true });
    }, { direction: options.direction || 'forward', skipAnimation: options.skipAnimation });
  }

  const isEmbed = isEmbeddedMode();
  if (isEmbed) {
    document.documentElement.classList.add('is-embedded');
    document.body.classList.add('is-embedded');
  } else if (!isHeadlessMode() && (typeof window === 'undefined' || window.parent === window)) {
    document.body.classList.remove('is-embedded');
    document.documentElement.classList.remove('is-embedded');
  }

  const isHeadless = isHeadlessMode();
  if (isHeadless) {
    document.documentElement.classList.add('headless-mode');
    document.body.classList.add('headless-mode');
  } else if (typeof window === 'undefined' || window.parent === window) {
    document.body.classList.remove('headless-mode');
    document.documentElement.classList.remove('headless-mode');
  }

  // Hide search cockpit in spotlight mode
  const cockpitEl = document.getElementById('media-search-cockpit');
  if (cockpitEl) cockpitEl.style.display = 'none';

  // Activate spotlight body state class to suppress auxiliary feed panels (feedback & resources)
  if (typeof document !== 'undefined' && document.body) {
    document.body.classList.add('is-spotlight-active');
  }
  if (activeDesktopPreviewEngine) {
    activeDesktopPreviewEngine.clearActiveDesktopPreview();
  }

  const strId = String(targetRideId || '').trim();
  if (!strId) {
    exitSpotlightMode(ridesData, mountEl, badgeEl);
    return;
  }
  if (activeSpotlightRideId !== strId) {
    dismissBroadwayNudge(false);
    spotlightAnimationRunCount = 0;
  }
  activeSpotlightRideId = strId;

  // Dispatch postMessage to parent host if embedded
  if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
    window.parent.postMessage({
      type: 'BTCNJCC_VIEW_CHANGED',
      view: 'spotlight',
      rideId: strId,
      pathname: `/?ride=${encodeURIComponent(strId)}`
    }, '*');
  }

  // 1. Locate ride in memory or cache
  const rides = ridesData || currentLoadedRides || getCachedMediaRides() || [];
  let ride = rides.find(r => 
    String(r.rideId || '').trim() === strId || 
    String(r.id || '').trim() === strId ||
    String(r.id || '').trim() === `btcnj-${strId}`
  );

  // 2. Dynamic DB Fallback & Active JIT Telemetry Enrichment via Firebase
  const lookupId = (ride && ride.rideId) || (/^\d+$/.test(strId) ? strId : null);
  if (!ride && mountEl) {
    mountEl.innerHTML = `
      <div class="spotlight-page-container">
        <div class="loading-state spotlight-loading">
          <div class="spinner"></div>
          <span>Locating Ride #${escapeHtml(strId)}...</span>
        </div>
      </div>
    `;
  }

  if (lookupId && (!ride || !ride.desc || !ride.mapName)) {
    try {
      const fbRide = await fetchRideFromFirebaseDb(lookupId);
      if (fbRide) {
        if (!ride) {
          ride = {
            id: `btcnj-${fbRide.rideId}`,
            rideId: fbRide.rideId,
            title: fbRide.title || `BTCNJ Ride #${fbRide.rideId}`,
            dateIso: fbRide.dateStr || null,
            dateStr: fbRide.dateStr || null,
            category: fbRide.category || null,
            miles: fbRide.miles || null,
            climbFt: fbRide.climbFt || null,
            leaders: fbRide.leaders || [],
            members: fbRide.members || [],
            description: fbRide.description || fbRide.desc || null,
            desc: fbRide.desc || fbRide.description || null,
            mapName: fbRide.mapName || null,
            startLoc: fbRide.startLoc || fbRide.mapName || null,
            directions: fbRide.directions || null,
            mapUrl: fbRide.mapUrl || null,
            zipCode: fbRide.zipCode || null,
            rwgpsUrl: fbRide.rwgpsUrl || null,
            r5mUrl: buildR5mRideUrl(fbRide, CONFIG.R5M_BASE_URL),
            isBtcnjRide: true,
            photoAlbumUrl: fbRide.photoAlbumUrl || null,
            photoAlbumId: fbRide.photoAlbumId || null,
            photoCount: fbRide.photoCount || 0,
            coverPhotoUrl: fbRide.coverPhotoUrl || null,
            matchedVideos: []
          };
          hydrateRidePhotoMetadata(ride);
        } else {
          // Enrich existing catalog ride with authentic BTCNJ telemetry & descriptions
          if (fbRide.desc || fbRide.description) {
            ride.desc = fbRide.desc || fbRide.description;
            ride.description = fbRide.description || fbRide.desc;
          }
          if (fbRide.mapName) {
            ride.mapName = fbRide.mapName;
            ride.startLoc = fbRide.startLoc || fbRide.mapName;
          }
          if (fbRide.directions && !ride.directions) ride.directions = fbRide.directions;
          if (fbRide.mapUrl && !ride.mapUrl) ride.mapUrl = fbRide.mapUrl;
          if (fbRide.zipCode && !ride.zipCode) ride.zipCode = fbRide.zipCode;
          if (fbRide.rwgpsUrl && !ride.rwgpsUrl) ride.rwgpsUrl = fbRide.rwgpsUrl;
          if (fbRide.timeRaw && !ride.timeRaw) ride.timeRaw = fbRide.timeRaw;
          if (fbRide.category && !ride.category) ride.category = fbRide.category;
          if (fbRide.miles && !ride.miles) ride.miles = fbRide.miles;
          if (fbRide.climbFt && !ride.climbFt) ride.climbFt = fbRide.climbFt;
          if (Array.isArray(fbRide.leaders) && fbRide.leaders.length > 0 && (!ride.leaders || ride.leaders.length === 0)) {
            ride.leaders = fbRide.leaders;
          }
          if (Array.isArray(fbRide.members) && fbRide.members.length > 0 && (!ride.members || ride.members.length === 0)) {
            ride.members = fbRide.members;
          }
        }
      }
    } catch (err) {
      console.warn('Spotlight Firebase DB lookup failed:', err);
    }
  }

  // Proactive JIT Interaction SWR: asynchronously verify album counts in the background
  if (ride) {
    proactiveSyncRideMedia(ride, true);
    syncHeaderR5mShortcut(ride);
  } else {
    syncHeaderR5mShortcut(null);
  }

  // 3. If still not found, render 404 card
  if (!ride) {
    if (badgeEl) badgeEl.textContent = 'Ride Not Found';
    mountEl.innerHTML = `
      <div class="spotlight-page-container">
        <div class="spotlight-card-wrapper">
          <div class="spotlight-unified-frame">
            ${!isEmbed ? `
              <header class="spotlight-command-bar">
                <a href="/" class="spotlight-back-link" id="spotlight-back-link" title="Return to All Media" aria-label="Return to All Media">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                  <span>All Media</span>
                </a>
                <div class="spotlight-badge-cluster">
                  <div class="spotlight-badge-tag">
                    <span class="spotlight-dot"></span>
                    <span class="spotlight-badge-text">SPOTLIGHT</span>
                  </div>
                </div>
              </header>
            ` : ''}
            <div class="spotlight-not-found-card">
              <div class="not-found-icon">🔍</div>
              <h3 class="not-found-title">Ride #${escapeHtml(strId)} Not Found</h3>
              <p class="not-found-sub">No media records or telemetry found for this ride identifier.</p>
              ${!isEmbed ? `
                <a href="/" class="btn btn-primary not-found-btn" id="spotlight-return-btn">All Media</a>
              ` : ''}
            </div>
          </div>
        </div>
      </div>
    `;
    const backBtn = mountEl.querySelector('#spotlight-back-link, #spotlight-return-btn');
    if (backBtn) {
      backBtn.addEventListener('click', (e) => {
        e.preventDefault();
        history.pushState(null, '', formatInternalUrl(window.location.pathname || '/'));
        exitSpotlightMode(ridesData, mountEl, badgeEl);
      });
    }
    return;
  }

  // 4. Update count badge if present
  if (badgeEl) {
    badgeEl.textContent = `Ride #${ride.rideId || ride.id}`;
  }

  // Active Premiere Idempotency Guard: Prevent duplicate stamp slamming during rapid async cache re-fetches
  const existingStage = mountEl.querySelector('.spotlight-premiere-stage');
  const now = Date.now();
  if (existingStage && activeSpotlightPremiereRideId === strId && (now - activeSpotlightPremiereTimestamp < 8000)) {
    if (ride) {
      bindRideCardInteractions(mountEl, [ride]);
    }
    return;
  }
  activeSpotlightPremiereRideId = strId;
  activeSpotlightPremiereTimestamp = now;

  clearSpotlightAnimationTimers();

  // Clean up any lingering viewport beams
  document.querySelectorAll('#spotlight-viewport-beams, .spotlight-viewport-beams').forEach(el => el.remove());

  const spotlightTemporalTier = getRideTemporalTier(ride);
  const cardHtml = renderRideCardHtml(ride, { isSpotlight: true });

  mountEl.innerHTML = `
    <div class="spotlight-page-container spotlight-premiere-stage">
      <canvas class="spotlight-vfx-canvas" aria-hidden="true"></canvas>
      <aside class="spotlight-briefing-sidebar" style="display: none !important;" aria-hidden="true"></aside>
      <div class="spotlight-card-wrapper">
        <div class="spotlight-stamp-wrap" aria-hidden="true">
          <div class="spotlight-stamp-badge">★ SPOTLIGHT ★</div>
          <div class="spotlight-shockwave-ring"></div>
        </div>
        <div class="spotlight-unified-frame">
          <header class="spotlight-command-bar">
            <a href="/" class="spotlight-back-link" id="spotlight-back-link" title="Return to All Media" aria-label="Return to All Media">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
              <span>All Media</span>
            </a>
            <div class="spotlight-badge-cluster">
              <div class="spotlight-badge-tag spotlight-title-badge">
                <span class="spotlight-dot"></span>
                <span class="spotlight-badge-text">SPOTLIGHT</span>
              </div>
            </div>
          </header>
          ${cardHtml}
        </div>
        <div class="spotlight-briefing-mobile-dock spotlight-briefing-dock" aria-label="Mission Briefing">
          ${renderBriefingTerminalHtml()}
        </div>
      </div>
    </div>
  `;

  // Initialize and start live rolling ride description typewriter briefing
  startBriefingTypewriter(ride, mountEl);

  const cardWrapper = mountEl.querySelector('.spotlight-card-wrapper');
  const stampBadge = mountEl.querySelector('.spotlight-stamp-badge');
  const strobeTag = mountEl.querySelector('.spotlight-title-badge, .spotlight-badge-tag');
  const vfxCanvas = mountEl.querySelector('.spotlight-vfx-canvas');

  // Trigger Phase 2: Ambient Golden/Cyan Embers Burst at stamp impact (t = 280ms) without stage vibration
  const embersTimer = setTimeout(() => {
    if (vfxCanvas && stampBadge && stampBadge.isConnected) {
      launchSpotlightEmbers(vfxCanvas, stampBadge);
    }
  }, 280);
  activeSpotlightTimers.push(embersTimer);

  // Trigger Phase 3: Hallmark Stamp Dissipation begins at t = 1600ms (smoothly fades out over 600ms)
  const dissipateTimer = setTimeout(() => {
    if (stampBadge && stampBadge.isConnected) {
      stampBadge.classList.add('is-dissipating');
    }
  }, 1600);
  activeSpotlightTimers.push(dissipateTimer);

  // Trigger Phase 4: Hallmark Stamp fully dissipated at t = 2200ms -> unveil underlying badge with premium fade-in
  const dissipatedCompleteTimer = setTimeout(() => {
    if (stampBadge && stampBadge.isConnected) {
      stampBadge.classList.add('is-dissipated');
      stampBadge.classList.add('is-resting');
    }
    if (strobeTag && strobeTag.isConnected) {
      strobeTag.classList.add('is-revealed');
      strobeTag.classList.add('is-strobe');
    }
  }, 2200);
  activeSpotlightTimers.push(dissipatedCompleteTimer);

  // Trigger Phase 5: Underlying Spotlight Badge Showcase concludes (+2.5s AFTER stamp dissipates -> t = 4700ms)
  const strobeEndTimer = setTimeout(() => {
    if (strobeTag && strobeTag.isConnected) {
      strobeTag.classList.remove('is-strobe');
    }
  }, 4700);
  activeSpotlightTimers.push(strobeEndTimer);

  // Trigger Spotlight Media Buttons Power Surge & Golden Pointer Finger Nudge
  const spotlightNudgeTimer = setTimeout(() => {
    if (mountEl && mountEl.isConnected) {
      triggerBroadwayNudge();
    }
  }, 450);
  activeSpotlightTimers.push(spotlightNudgeTimer);

  // Bind return button for seamless in-page SPA navigation
  const backLink = mountEl.querySelector('#spotlight-back-link');
  if (backLink) {
    backLink.addEventListener('click', (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      history.pushState(null, '', formatInternalUrl(window.location.pathname || '/'));
      exitSpotlightMode(ridesData, mountEl, badgeEl);
    });
  }

  // Bind card interactions
  bindRideCardInteractions(mountEl, [ride]);

  // Desktop/Cinematic: Autoplay continuous media preview in spotlight stage
  const spotlightCard = mountEl.querySelector('.spotlight-card-wrapper .media-ride-card');
  if (spotlightCard) {
    const bannerEl = spotlightCard.querySelector('.card-media-banner');
    if (bannerEl) {
      let previewLayer = bannerEl.querySelector('.card-preview-layer');
      if (!previewLayer) {
        bindCardHoverPreviews(mountEl, [ride]);
        previewLayer = bannerEl.querySelector('.card-preview-layer');
      }
      if (previewLayer) {
        startCardHoverPreview(spotlightCard, ride, bannerEl, previewLayer, { continuous: true, isSpotlight: true });
      }
    }
  }
}

/**
 * Restores full catalog feed when exiting spotlight view.
 * @param {Array<object>} ridesData
 * @param {HTMLElement} [container]
 * @param {HTMLElement} [countBadge]
 */
export function exitSpotlightMode(ridesData, container, countBadge, options = {}) {
  const targetContainer = container || document.getElementById('rides-container');
  const targetBadge = countBadge || document.getElementById('rides-count-badge');

  if (!options._internal && targetContainer && targetContainer.children.length > 0 && !targetContainer.querySelector('.loading-frame') && !options.skipAnimation) {
    return transitionWithPeel(targetContainer, async (peelMount) => {
      exitSpotlightMode(ridesData, peelMount, targetBadge, { ...options, _internal: true });
    }, { direction: options.direction || 'backward', skipAnimation: options.skipAnimation });
  }

  clearSpotlightAnimationTimers();
  stopBriefingTypewriter();
  dismissBroadwayNudge(false);
  spotlightAnimationRunCount = 0;
  activeSpotlightRideId = null;
  activeSpotlightPremiereRideId = null;
  activeSpotlightPremiereTimestamp = 0;
  syncHeaderR5mShortcut(null);
  if (typeof document !== 'undefined' && document.body) {
    document.body.classList.remove('is-spotlight-active');
  }
  if (!isEmbeddedMode() && !isHeadlessMode() && (typeof window === 'undefined' || window.parent === window)) {
    document.body.classList.remove('is-embedded');
    document.documentElement.classList.remove('is-embedded');
  }

  if (activePreviewCard) {
    activePreviewCard._continuousPreview = false;
    activePreviewCard.classList.remove('is-continuous-preview');
    stopCardHoverPreview(activePreviewCard, null, null, false, true);
  }

  // Restore search cockpit visibility
  const cockpitEl = document.getElementById('media-search-cockpit');
  if (cockpitEl) cockpitEl.style.display = 'block';

  const rides = ridesData || currentLoadedRides || getCachedMediaRides();
  if (rides && rides.length > 0) {
    renderRides(rides, targetContainer, targetBadge);
  } else {
    fetchAndRenderRides(false);
  }

  // Dispatch postMessage to parent host if embedded
  if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
    window.parent.postMessage({
      type: 'BTCNJCC_VIEW_CHANGED',
      view: 'media',
      pathname: '/media'
    }, '*');
  }
}

// ============================================================================
// 6. Accessible Video Lightbox Modal Controller (Multi-Video Nav & Auto-Advance)
// ============================================================================
let currentPlaylist = [];
let currentPlaylistIndex = 0;
let currentRideContext = null;
let autoAdvanceTimerId = null;
let autoAdvanceCountdown = 3;

export function clearAutoAdvanceTimer() {
  if (autoAdvanceTimerId) {
    clearInterval(autoAdvanceTimerId);
    autoAdvanceTimerId = null;
  }
  const toast = document.getElementById('modal-autoadvance-toast');
  if (toast) {
    toast.style.display = 'none';
  }
}

export function cancelAutoAdvance() {
  clearAutoAdvanceTimer();
}

export function goToPrev() {
  clearAutoAdvanceTimer();
  if (currentPlaylist.length > 1 && currentPlaylistIndex > 0) {
    currentPlaylistIndex--;
    renderActiveVideo();
  }
}

export function goToNext() {
  clearAutoAdvanceTimer();
  if (currentPlaylist.length > 1 && currentPlaylistIndex < currentPlaylist.length - 1) {
    currentPlaylistIndex++;
    renderActiveVideo();
  }
}

export function handleVideoEnded() {
  if (!currentPlaylist || currentPlaylist.length <= 1) return;
  if (currentPlaylistIndex >= currentPlaylist.length - 1) return; // Reached last video

  showAutoAdvanceToast(3);
}

export function showAutoAdvanceToast(seconds = 3) {
  clearAutoAdvanceTimer();
  autoAdvanceCountdown = seconds;

  const toast = document.getElementById('modal-autoadvance-toast');
  if (!toast) return;

  const nextVideo = currentPlaylist[currentPlaylistIndex + 1];
  const nextTitle = nextVideo ? cleanVideoChipTitle(nextVideo.title) : 'Next Video';

  const titleEl = document.getElementById('autoadvance-title');
  const countEl = document.getElementById('autoadvance-count');
  const progressEl = toast.querySelector('.autoadvance-progress');

  if (titleEl) titleEl.textContent = `Next: ${nextTitle}`;
  if (countEl) countEl.textContent = `${autoAdvanceCountdown}s`;
  if (progressEl) progressEl.style.strokeDashoffset = '0';

  toast.style.display = 'flex';

  const totalDash = 69.1; // 2 * PI * 11
  let elapsed = 0;
  const intervalMs = 100;
  const totalMs = seconds * 1000;

  autoAdvanceTimerId = setInterval(() => {
    elapsed += intervalMs;
    const remainingSec = Math.ceil((totalMs - elapsed) / 1000);
    if (countEl && remainingSec >= 0) {
      countEl.textContent = `${remainingSec}s`;
    }
    if (progressEl) {
      const offset = (elapsed / totalMs) * totalDash;
      progressEl.style.strokeDashoffset = `${offset.toFixed(1)}`;
    }

    if (elapsed >= totalMs) {
      clearAutoAdvanceTimer();
      goToNext();
    }
  }, intervalMs);
}

/**
 * TouchSwipeController: Captures horizontal flick gestures across modal surfaces,
 * header, and transparent side rails for seamless Next/Previous playlist navigation.
 */
export function initLightboxSwipeGesture() {
  const modal = document.getElementById('video-lightbox-modal');
  if (!modal || modal._hasSwipeListener) return;
  modal._hasSwipeListener = true;

  let touchStartX = 0;
  let touchStartY = 0;
  let lastTouchX = 0;
  let lastTouchY = 0;
  let touchStartTime = 0;
  let isSwiping = false;

  const executeHorizontalSwipe = (dx, dy) => {
    if (!modal.classList.contains('active')) return;
    if (!currentPlaylist || currentPlaylist.length <= 1) return;

    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    // Enforce horizontal flick intent: delta >= 36px and dominant over vertical delta
    if (absDx >= 36 && absDx > 1.2 * absDy) {
      const mount = document.getElementById('lightbox-iframe-mount');
      const currItem = currentPlaylist[currentPlaylistIndex];
      const isPhotoItem = Boolean(currItem && currItem.type === 'photo');
      if (dx < 0) {
        // Swiped Left -> Advance to Next Media Item
        if (currentPlaylistIndex < currentPlaylist.length - 1) {
          if (mount) {
            mount.classList.remove('is-sliding-next', 'is-sliding-prev');
            void mount.offsetWidth;
            mount.classList.add('is-sliding-next');
            setTimeout(() => mount && mount.classList.remove('is-sliding-next'), 250);
          }
          goToNext();
        } else {
          showGlobalToast(isPhotoItem ? 'Last Photo Reached' : 'Last Video Reached');
        }
      } else {
        // Swiped Right -> Return to Previous Media Item
        if (currentPlaylistIndex > 0) {
          if (mount) {
            mount.classList.remove('is-sliding-next', 'is-sliding-prev');
            void mount.offsetWidth;
            mount.classList.add('is-sliding-prev');
            setTimeout(() => mount && mount.classList.remove('is-sliding-prev'), 250);
          }
          goToPrev();
        } else {
          showGlobalToast(isPhotoItem ? 'First Photo Reached' : 'First Video Reached');
        }
      }
    }
  };

  const onTouchStart = (e) => {
    if (!modal.classList.contains('active')) return;
    const touch = e.touches && e.touches[0];
    if (!touch) return;
    touchStartX = touch.clientX;
    touchStartY = touch.clientY;
    lastTouchX = touch.clientX;
    lastTouchY = touch.clientY;
    touchStartTime = Date.now();
    isSwiping = true;
  };

  const onTouchMove = (e) => {
    // Keep listener passive to preserve native compositor smoothness while tracking live coordinates
    if (!isSwiping) return;
    const touch = e.touches && e.touches[0];
    if (touch) {
      lastTouchX = touch.clientX;
      lastTouchY = touch.clientY;
    }
  };

  const onTouchEnd = (e) => {
    if (!isSwiping) return;
    isSwiping = false;

    const touch = (e.changedTouches && e.changedTouches[0]) || null;
    const endX = touch ? touch.clientX : lastTouchX;
    const endY = touch ? touch.clientY : lastTouchY;
    const dx = endX - touchStartX;
    const dy = endY - touchStartY;

    if (Math.abs(dx) < 12 && Math.abs(dy) < 12 && (Date.now() - touchStartTime) < 380) {
      const railTarget = e.target && typeof e.target.closest === 'function' ? e.target.closest('.lightbox-swipe-rail') : null;
      if (railTarget) {
        const activeIframe = document.getElementById('active-yt-iframe');
        if (activeIframe && activeIframe.contentWindow) {
          const nextFunc = modal._ytIsPlaying === false ? 'playVideo' : 'pauseVideo';
          modal._ytIsPlaying = modal._ytIsPlaying === false;
          try {
            activeIframe.contentWindow.postMessage(JSON.stringify({
              event: 'command',
              func: nextFunc,
              args: []
            }), '*');
          } catch (_) {}
        }
        const wrapEl = document.getElementById('lightbox-player-wrap');
        if (wrapEl && wrapEl.classList) {
          wrapEl.classList.add('is-controls-passthrough');
          clearTimeout(modal._passthroughTimer);
          modal._passthroughTimer = setTimeout(() => {
            wrapEl.classList.remove('is-controls-passthrough');
          }, 2200);
        }
      }
      return;
    }

    executeHorizontalSwipe(dx, dy);
  };

  const onTouchCancel = (e) => {
    if (!isSwiping) return;
    isSwiping = false;
    const touch = (e && e.changedTouches && e.changedTouches[0]) || null;
    const endX = touch ? touch.clientX : lastTouchX;
    const endY = touch ? touch.clientY : lastTouchY;
    executeHorizontalSwipe(endX - touchStartX, endY - touchStartY);
  };

  let pointerDownX = 0;
  let pointerDownY = 0;
  let isPointerDragging = false;

  const onPointerDown = (e) => {
    if (!modal.classList.contains('active')) return;
    if (e.pointerType === 'touch') return; // Handled by touch events
    if (e.button !== undefined && e.button !== 0) return;
    if (e.target && typeof e.target.closest === 'function' && e.target.closest('button, a, input, select')) return;
    pointerDownX = e.clientX;
    pointerDownY = e.clientY;
    isPointerDragging = true;
  };

  const onPointerUp = (e) => {
    if (!isPointerDragging) return;
    isPointerDragging = false;
    if (e.pointerType === 'touch') return;
    executeHorizontalSwipe(e.clientX - pointerDownX, e.clientY - pointerDownY);
  };

  modal.addEventListener('touchstart', onTouchStart, { passive: true });
  modal.addEventListener('touchmove', onTouchMove, { passive: true });
  modal.addEventListener('touchend', onTouchEnd, { passive: true });
  if (typeof modal.addEventListener === 'function') {
    modal.addEventListener('touchcancel', onTouchCancel, { passive: true });
    modal.addEventListener('pointerdown', onPointerDown, { passive: true });
    modal.addEventListener('pointerup', onPointerUp, { passive: true });
  }
}

export function initLightboxModal() {
  const modal = document.getElementById('video-lightbox-modal');
  const closeBtn = document.getElementById('modal-close-btn');
  const backdrop = document.getElementById('modal-backdrop');
  const prevBtn = document.getElementById('modal-prev-btn');
  const nextBtn = document.getElementById('modal-next-btn');
  const playerPrevBtn = document.getElementById('modal-player-prev-btn');
  const playerNextBtn = document.getElementById('modal-player-next-btn');
  const cancelAdvBtn = document.getElementById('autoadvance-cancel-btn');
  const playAdvBtn = document.getElementById('autoadvance-play-btn');

  initLightboxSwipeGesture();

  if (closeBtn) closeBtn.addEventListener('click', closeVideoLightbox);
  if (backdrop) backdrop.addEventListener('click', closeVideoLightbox);

  if (prevBtn) prevBtn.addEventListener('click', goToPrev);
  if (nextBtn) nextBtn.addEventListener('click', goToNext);
  if (playerPrevBtn) {
    playerPrevBtn.addEventListener('click', (e) => {
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
      try { playerPrevBtn.blur(); } catch (_) {}
      goToPrev();
    });
  }
  if (playerNextBtn) {
    playerNextBtn.addEventListener('click', (e) => {
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
      try { playerNextBtn.blur(); } catch (_) {}
      goToNext();
    });
  }

  if (cancelAdvBtn) cancelAdvBtn.addEventListener('click', cancelAutoAdvance);
  if (playAdvBtn) {
    playAdvBtn.addEventListener('click', () => {
      clearAutoAdvanceTimer();
      goToNext();
    });
  }

  // Handle fullscreen orientation locking & aspect ratio state synchronization
  const handleFsChange = () => {
    const isFs = Boolean(document.fullscreenElement || document.webkitFullscreenElement);
    if (modal && modal.classList) {
      modal.classList.toggle('is-fullscreen-active', isFs);
    }
    const curr = (currentPlaylist && currentPlaylist[currentPlaylistIndex]) || null;
    if (isFs && screen.orientation && screen.orientation.lock) {
      const isPortrait = Boolean(curr && curr.isPortrait);
      const desired = isPortrait ? 'portrait' : 'landscape';
      screen.orientation.lock(desired).catch(() => {});
    } else if (!isFs && screen.orientation && screen.orientation.unlock) {
      try { screen.orientation.unlock(); } catch (_) {}
    }
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      setTimeout(() => { try { window.dispatchEvent(new Event('resize')); } catch (_) {} }, 60);
    }
  };
  document.addEventListener('fullscreenchange', handleFsChange);
  document.addEventListener('webkitfullscreenchange', handleFsChange);

  // Native HTML5 postMessage listener for YouTube IFrame state change (ended)
  window.addEventListener('message', (event) => {
    if (!modal || !modal.classList.contains('active')) return;
    if (!event.data) return;

    try {
      const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      if (!data || typeof data !== 'object') return;

      // Proactively enforce highest playback resolution when playback starts
      const isStatePlaying = (data.event === 'onStateChange' && (data.info === 1 || (data.info && data.info.playerState === 1)));
      const isDeliveryPlaying = (data.event === 'infoDelivery' && data.info && data.info.playerState === 1);
      if (isStatePlaying || isDeliveryPlaying) {
        const activeIframe = document.getElementById('active-yt-iframe');
        if (activeIframe) {
          dispatchMaxQualityCommands(activeIframe);
        }
      }

      // Detect YouTube video completion (state 0 = ENDED)
      // Case 1: Widget API explicit onStateChange event
      const isStateEnded = (data.event === 'onStateChange' && (data.info === 0 || (data.info && data.info.playerState === 0)));
      // Case 2: Standard YouTube infoDelivery periodic broadcast
      const isDeliveryEnded = (data.event === 'infoDelivery' && data.info && data.info.playerState === 0);

      if (isStateEnded || isDeliveryEnded) {
        handleVideoEnded();
      }
    } catch (_) {
      // Non-JSON messages from browser extensions or other sources
    }
  });

  window.addEventListener('keydown', (e) => {
    if (!modal || !modal.classList.contains('active')) return;

    if (e.key === 'Escape') {
      closeVideoLightbox();
    } else if (e.key === 'ArrowLeft') {
      goToPrev();
    } else if (e.key === 'ArrowRight') {
      goToNext();
    }
  });

  // Bind static document portal links (e.g. hero banner disclaimer)
  document.querySelectorAll('a[data-launch="btcnj-portal"]').forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const url = link.getAttribute('href');
      const title = link.getAttribute('title') || 'Official BTCNJ Portal';
      if (url) {
        openBtcnjPortalLightbox(url, title);
      }
    });
  });
}

/**
 * Builds clean, uniform, horizontally centered bottom HUD text for media viewports (YouTube & RWGPS).
 * Formats: Title • Date • Category • Miles • Climb (without '+' sign).
 * Gracefully falls back if telemetry or identity fields are omitted.
 */
export function buildModalBottomHudHtml(ride, defaultHint = '') {
  if (!ride) {
    return defaultHint || '';
  }

  const parts = [];

  // 1. Ride Title
  if (ride.title && String(ride.title).trim().length > 0) {
    parts.push(String(ride.title).trim());
  }

  // 2. Formatted Date
  const dateStr = formatRideDate(ride.dateIso || ride.dateStr);
  if (dateStr && String(dateStr).trim().length > 0) {
    parts.push(String(dateStr).trim());
  }

  // 3. Category (e.g. "B*", "B+")
  if (ride.category && String(ride.category).trim().length > 0) {
    parts.push(String(ride.category).trim());
  }

  // 4. Miles (e.g. "74 mi")
  if (ride.miles) {
    parts.push(`${ride.miles} mi`);
  }

  // 5. Elevation in Feet WITHOUT '+' sign (e.g. "5,500 ft")
  if (ride.climbFt) {
    parts.push(`${Math.round(ride.climbFt).toLocaleString()} ft`);
  }

  if (parts.length === 0) {
    return defaultHint || '';
  }

  return parts.join(' \u2022 ');
}

/**
 * Signals YouTube DASH player to stream at highest available resolution (720p/1080p/4K).
 * Dispatches quality preference commands via postMessage across multiple API signatures.
 */
export function dispatchMaxQualityCommands(iframeEl) {
  if (!iframeEl || !iframeEl.contentWindow) return;
  const qualityCommands = [
    { func: 'setPlaybackQualityRange', args: ['hd1080', 'highres'] },
    { func: 'setPlaybackQuality', args: ['highres'] },
    { func: 'setSuggestedQuality', args: ['highres'] },
    { func: 'setPlaybackQuality', args: ['hd1080'] }
  ];
  for (const cmd of qualityCommands) {
    try {
      iframeEl.contentWindow.postMessage(JSON.stringify({
        event: 'command',
        func: cmd.func,
        args: cmd.args
      }), '*');
    } catch (_) {}
  }
}

export function openVideoLightbox(videoOrPlaylist, initialIndexOrTitle = 0, contextRide = null) {
  const modal = document.getElementById('video-lightbox-modal');
  if (!modal) return;

  currentRideContext = contextRide;

  if (Array.isArray(videoOrPlaylist)) {
    currentPlaylist = videoOrPlaylist;
    currentPlaylistIndex = typeof initialIndexOrTitle === 'number' ? initialIndexOrTitle : 0;
  } else if (typeof videoOrPlaylist === 'string') {
    currentPlaylist = [{
      videoId: videoOrPlaylist,
      title: typeof initialIndexOrTitle === 'string' ? initialIndexOrTitle : 'BTCNJ Cycling Reel'
    }];
    currentPlaylistIndex = 0;
  }

  if (!currentPlaylist || currentPlaylist.length === 0) return;

  if (currentPlaylistIndex < 0 || currentPlaylistIndex >= currentPlaylist.length) {
    currentPlaylistIndex = 0;
  }

  modal.classList.add('active');
  modal.setAttribute('aria-hidden', 'false');
  document.body.classList.add('modal-open');
  document.body.classList.add('is-video-modal-open');

  // Suspend active card hover and auto-scan preview engines
  if (typeof activeDesktopPreviewEngine !== 'undefined' && activeDesktopPreviewEngine && typeof activeDesktopPreviewEngine.clearActiveDesktopPreview === 'function') {
    activeDesktopPreviewEngine.clearActiveDesktopPreview();
  }
  if (typeof activeMobilePreviewEngine !== 'undefined' && activeMobilePreviewEngine && typeof activeMobilePreviewEngine.clearActivePreview === 'function') {
    activeMobilePreviewEngine.clearActivePreview();
  }
  if (typeof stopCardHoverPreview === 'function' && typeof activePreviewCard !== 'undefined' && activePreviewCard) {
    stopCardHoverPreview(activePreviewCard, null, null, false, true);
  }

  // Suspend historical clock synchronization during video playback
  if (typeof pauseHistoricalClockSync === 'function') {
    pauseHistoricalClockSync();
  }

  renderActiveVideo();
}

function renderActiveVideo() {
  const modal = document.getElementById('video-lightbox-modal');
  if (!modal) return;

  const mount = document.getElementById('lightbox-iframe-mount');
  const titleEl = document.getElementById('modal-video-title');
  const directLink = document.getElementById('modal-yt-direct-link');
  const navContainer = document.getElementById('modal-nav-container');
  const counterEl = document.getElementById('modal-video-counter');
  const prevBtn = document.getElementById('modal-prev-btn');
  const nextBtn = document.getElementById('modal-next-btn');
  const playerPrevBtn = document.getElementById('modal-player-prev-btn');
  const playerNextBtn = document.getElementById('modal-player-next-btn');

  const total = currentPlaylist.length;
  const currentVideo = currentPlaylist[currentPlaylistIndex];
  if (!currentVideo) return;

  clearAutoAdvanceTimer();

  const hasMultiple = total > 1;
  const isPhotoItem = currentVideo.type === 'photo';
  const playerWrap = document.getElementById('lightbox-player-wrap');
  if (playerWrap && playerWrap.classList) {
    playerWrap.classList.toggle('is-single-media', !hasMultiple);
    playerWrap.classList.remove('is-controls-passthrough');
  }
  modal._ytIsPlaying = true;

  if (navContainer) {
    navContainer.style.display = hasMultiple ? 'flex' : 'none';
  }

  // Zero Left/Right Arrow Overlays on Image/Media Stage (swipe + footer nav only)
  if (playerPrevBtn) {
    playerPrevBtn.style.display = 'none';
    playerPrevBtn.disabled = currentPlaylistIndex <= 0;
  }

  if (playerNextBtn) {
    playerNextBtn.style.display = 'none';
    playerNextBtn.disabled = currentPlaylistIndex >= total - 1;
  }

  if (hasMultiple) {
    if (counterEl) {
      counterEl.textContent = `${currentPlaylistIndex + 1} of ${total}`;
    }
    if (prevBtn) {
      prevBtn.disabled = currentPlaylistIndex <= 0;
    }
    if (nextBtn) {
      nextBtn.disabled = currentPlaylistIndex >= total - 1;
    }
  }

  if (isPhotoItem) {
    const ride = currentRideContext || (currentVideo && currentVideo.ride ? currentVideo.ride : null);
    const baseTitle = (ride && ride.title) || currentVideo.rideTitle || currentVideo.title || 'Ride Photos';
    if (titleEl) {
      titleEl.textContent = `${baseTitle} \u2014 Photos`;
      titleEl.title = titleEl.textContent;
    }

    const headerIcon = document.getElementById('modal-header-icon');
    if (headerIcon) {
      headerIcon.innerHTML = `
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path fill="#EA4335" d="M12,0 A6,6 0 0,0 12,12 Z"/>
          <path fill="#FBBC04" d="M24,12 A6,6 0 0,0 12,12 Z"/>
          <path fill="#34A853" d="M12,24 A6,6 0 0,0 12,12 Z"/>
          <path fill="#4285F4" d="M0,12 A6,6 0 0,0 12,12 Z"/>
        </svg>
      `;
    }

    const countStr = hasMultiple
      ? `Photo ${currentPlaylistIndex + 1} of ${total}${ride && ride.photoCount > total ? ` (${ride.photoCount} in Album)` : ''}`
      : (currentVideo.countStr || (ride && ride.photoCount > 0 ? `${ride.photoCount} Photos` : 'Full-Resolution Photos'));

    if (mount) {
      mount.innerHTML = `
        <div class="lightbox-photo-stage">
          <img src="${escapeHtml(currentVideo.url)}" alt="${escapeHtml(baseTitle)} Photo ${currentPlaylistIndex + 1}" class="lightbox-photo-img" referrerpolicy="no-referrer" draggable="false" onerror="this.onerror=null; this.src=(window.getCyclingFallbackSvg ? window.getCyclingFallbackSvg() : '');" />
          <div class="lightbox-photo-overlay">
            <div>
              <div class="photo-overlay-title">${escapeHtml(baseTitle)}</div>
              <div class="photo-overlay-count">${escapeHtml(countStr)}</div>
            </div>
          </div>
        </div>
      `;
    }

    if (directLink) {
      directLink.href = currentVideo.albumUrl || (ride && ride.photoAlbumUrl) || currentVideo.url || '#';
    }
    const directIcon = document.getElementById('modal-direct-icon');
    if (directIcon) {
      directIcon.innerHTML = `
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path fill="#EA4335" d="M12,0 A6,6 0 0,0 12,12 Z"/>
          <path fill="#FBBC04" d="M24,12 A6,6 0 0,0 12,12 Z"/>
          <path fill="#34A853" d="M12,24 A6,6 0 0,0 12,12 Z"/>
          <path fill="#4285F4" d="M0,12 A6,6 0 0,0 12,12 Z"/>
        </svg>
      `;
    }
    const directLabel = document.getElementById('modal-direct-label');
    if (directLabel) directLabel.textContent = 'Open Album';

    const hintEl = document.getElementById('modal-context-hint');
    if (hintEl) {
      hintEl.textContent = hasMultiple ? 'Swipe left/right to browse photos' : 'Click below for full Google Photos album';
      hintEl.title = hintEl.textContent;
    }

    const dialog = modal.querySelector('.lightbox-dialog');
    if (dialog) {
      dialog.className = 'lightbox-dialog modal-type-photos';
    }
    return;
  }

  if (titleEl) {
    titleEl.textContent = currentVideo.title || 'BTCNJ Cycling Reel';
    titleEl.title = currentVideo.title || '';
  }

  if (directLink) {
    directLink.href = `https://www.youtube.com/watch?v=${currentVideo.videoId}&vq=hd2160`;
  }

  // Mount iframe with standard adaptive resolution, captions off, enablejsapi=1, and playsinline=1
  if (mount) {
    const origin = typeof window !== 'undefined' && window.location ? window.location.origin : '';
    const originParam = origin ? `&origin=${encodeURIComponent(origin)}` : '';
    mount.innerHTML = `
      <iframe
        id="active-yt-iframe"
        src="https://www.youtube-nocookie.com/embed/${encodeURIComponent(currentVideo.videoId)}?autoplay=1&rel=0&cc_load_policy=3&enablejsapi=1&playsinline=1${originParam}"
        title="${escapeHtml(currentVideo.title || 'Cycling Reel')}"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
        allowfullscreen>
      </iframe>
    `;

    const iframeEl = document.getElementById('active-yt-iframe');
    if (iframeEl) {
      const sendHandshake = () => {
        try {
          // 1. Subscribe to state changes via YouTube Widget API
          iframeEl.contentWindow?.postMessage(JSON.stringify({
            event: 'command',
            func: 'addEventListener',
            args: ['onStateChange']
          }), '*');
          // 2. Also send listening confirmation
          iframeEl.contentWindow?.postMessage(JSON.stringify({
            event: 'listening',
            id: 'active-yt-iframe'
          }), '*');
          // 3. Proactively signal highest resolution quality preference
          dispatchMaxQualityCommands(iframeEl);

        } catch (_) {}
      };

      iframeEl.addEventListener('load', () => {
        sendHandshake();
        setTimeout(sendHandshake, 400);
        setTimeout(sendHandshake, 1200);
      });
    }
  }

  // Brand header icon: YouTube logo
  const headerIcon = document.getElementById('modal-header-icon');
  if (headerIcon) {
    headerIcon.innerHTML = `
      <svg width="18" height="13" viewBox="0 3 24 18" fill="none" aria-hidden="true">
        <path fill="#FF0000" d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z"/>
        <path fill="#FFFFFF" d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    `;
  }

  const directIcon = document.getElementById('modal-direct-icon');
  if (directIcon) {
    directIcon.innerHTML = `
      <svg width="16" height="12" viewBox="0 3 24 18" fill="none" aria-hidden="true">
        <path fill="#FF0000" d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z"/>
        <path fill="#FFFFFF" d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    `;
  }

  const directLabel = document.getElementById('modal-direct-label');
  if (directLabel) directLabel.textContent = 'Watch on YouTube';

  const hintEl = document.getElementById('modal-context-hint');
  if (hintEl) {
    const ride = currentRideContext || (currentVideo && currentVideo.ride ? currentVideo.ride : null);
    hintEl.textContent = buildModalBottomHudHtml(ride, '');
    hintEl.title = hintEl.textContent;
  }

  const isPortrait = Boolean(currentVideo.isPortrait);
  const dialog = modal.querySelector('.lightbox-dialog');
  if (dialog) {
    dialog.className = `lightbox-dialog modal-type-video ${isPortrait ? 'is-portrait-video' : ''}`.trim();
  }
}

export function openRwgpsLightbox(ride) {
  const modal = document.getElementById('video-lightbox-modal');
  if (!modal || !ride || !ride.rwgpsUrl) return;

  const match = ride.rwgpsUrl.match(/routes\/(\d+)/);
  const routeId = match ? match[1] : null;
  const embedUrl = routeId
    ? `https://ridewithgps.com/embeds?type=route&id=${routeId}&metricUnits=false&sampleGraph=true`
    : ride.rwgpsUrl;

  const mount = document.getElementById('lightbox-iframe-mount');
  const titleEl = document.getElementById('modal-video-title');
  const headerIcon = document.getElementById('modal-header-icon');
  const directLink = document.getElementById('modal-yt-direct-link');
  const directIcon = document.getElementById('modal-direct-icon');
  const directLabel = document.getElementById('modal-direct-label');
  const navContainer = document.getElementById('modal-nav-container');
  const playerPrevBtn = document.getElementById('modal-player-prev-btn');
  const playerNextBtn = document.getElementById('modal-player-next-btn');
  const hintEl = document.getElementById('modal-context-hint');
  const dialog = modal.querySelector('.lightbox-dialog');

  if (dialog) dialog.className = 'lightbox-dialog modal-type-rwgps';

  if (navContainer) navContainer.style.display = 'none';
  if (playerPrevBtn) playerPrevBtn.style.display = 'none';
  if (playerNextBtn) playerNextBtn.style.display = 'none';

  if (titleEl) {
    titleEl.textContent = `${ride.title} \u2014 Route & Elevation`;
    titleEl.title = titleEl.textContent;
  }

  if (headerIcon) {
    headerIcon.innerHTML = `
      <svg width="20" height="15" viewBox="0 0 49.6 35.1" fill="none" aria-hidden="true">
        <path fill="#F96400" d="M15.2,9.9c0,1.4,0.2,3.1,4,5.4c2.7,1.6,5.2,4.4,5.2,5.4s-4.3,5.8-6.4,8c-0.2,0.2-0.4,0.4-0.7,0.6c-1.6,1.7-3.8,2.6-6,2.5c-4.4-0.1-7.9-3.8-7.8-8.2c0-2.7,1.3-5.2,3.4-6.7c1.2-0.8,2.6-1.3,4-1.5c0.9-0.1,1.9-0.1,2.8,0.2l0.2,0.1c0-0.9-1.7-1.6-2.6-1.7c-0.7-0.1-1.5-0.1-2.2,0C3.8,14.6-0.1,19.2,0,24.5c0,5.9,5.4,10.6,11,10.6c0.7,0,1.3,0,1.9-0.1c3.2-0.2,6.1-1.4,11.2-7.4c0.5-0.6,3.5-4,4.9-5.7l0.1-0.1l0,0c0.2-0.3,0.4-0.5,0.6-0.8l0.2-0.2c1.2-1.3,2.5-2.4,4-3.3c1.4-0.7,3-1,4.5-0.8c4.8,0.4,7.9,3.3,7.9,7.7s-3.9,8-8.7,8c-2.4-0.2-4.8-1.2-6.5-2.8L31,29.4c-0.9,1.5,2.4,5.6,7.7,5.6c0.1,0,0.2,0,0.3,0c5.9,0,10.6-4.8,10.6-10.7c-0.1-5.8-4.7-10.5-10.5-10.6c-5.2-0.5-8.5,2.4-9.4,2.5c-0.7,0.1-8.2-5-8.9-6.1c-0.3-0.5,0.1-1.9,5-2.3c2.2,2.4,4.5,4.2,8.8,4.2c2.1-0.1,4.2-0.7,6.1-1.8C41.7,9.5,41.3,9,41.3,9s0,0.4-1.4,0.7c-1.6,0.4-3.2,0.5-4.9,0.3C34.2,10,29.7,9.7,28.2,4C21.9,3.4,15.2,5.6,15.2,9.9z"/>
      </svg>
    `;
  }

  if (directLink) directLink.href = ride.rwgpsUrl;
  if (directIcon) {
    directIcon.innerHTML = `
      <svg width="16" height="12" viewBox="0 0 49.6 35.1" fill="none" aria-hidden="true">
        <path fill="#F96400" d="M15.2,9.9c0,1.4,0.2,3.1,4,5.4c2.7,1.6,5.2,4.4,5.2,5.4s-4.3,5.8-6.4,8c-0.2,0.2-0.4,0.4-0.7,0.6c-1.6,1.7-3.8,2.6-6,2.5c-4.4-0.1-7.9-3.8-7.8-8.2c0-2.7,1.3-5.2,3.4-6.7c1.2-0.8,2.6-1.3,4-1.5c0.9-0.1,1.9-0.1,2.8,0.2l0.2,0.1c0-0.9-1.7-1.6-2.6-1.7c-0.7-0.1-1.5-0.1-2.2,0C3.8,14.6-0.1,19.2,0,24.5c0,5.9,5.4,10.6,11,10.6c0.7,0,1.3,0,1.9-0.1c3.2-0.2,6.1-1.4,11.2-7.4c0.5-0.6,3.5-4,4.9-5.7l0.1-0.1l0,0c0.2-0.3,0.4-0.5,0.6-0.8l0.2-0.2c1.2-1.3,2.5-2.4,4-3.3c1.4-0.7,3-1,4.5-0.8c4.8,0.4,7.9,3.3,7.9,7.7s-3.9,8-8.7,8c-2.4-0.2-4.8-1.2-6.5-2.8L31,29.4c-0.9,1.5,2.4,5.6,7.7,5.6c0.1,0,0.2,0,0.3,0c5.9,0,10.6-4.8,10.6-10.7c-0.1-5.8-4.7-10.5-10.5-10.6c-5.2-0.5-8.5,2.4-9.4,2.5c-0.7,0.1-8.2-5-8.9-6.1c-0.3-0.5,0.1-1.9,5-2.3c2.2,2.4,4.5,4.2,8.8,4.2c2.1-0.1,4.2-0.7,6.1-1.8C41.7,9.5,41.3,9,41.3,9s0,0.4-1.4,0.7c-1.6,0.4-3.2,0.5-4.9,0.3C34.2,10,29.7,9.7,28.2,4C21.9,3.4,15.2,5.6,15.2,9.9z"/>
      </svg>
    `;
  }
  if (directLabel) directLabel.textContent = 'Open on RWGPS';
  if (hintEl) {
    hintEl.textContent = buildModalBottomHudHtml(ride, 'Interactive route & cues');
    hintEl.title = hintEl.textContent;
  }

  // Activate modal container first so DOM layout resolves real dimensions
  modal.classList.add('active');
  modal.setAttribute('aria-hidden', 'false');
  document.body.classList.add('modal-open');
  document.body.classList.remove('is-video-modal-open');

  if (mount) {
    mount.innerHTML = '';
    requestAnimationFrame(() => {
      mount.innerHTML = `
        <iframe
          src="${escapeHtml(embedUrl)}"
          title="${escapeHtml(ride.title)} Route Map"
          allow="geolocation"
          allowfullscreen>
        </iframe>
      `;
    });
  }
}

export function openBtcnjPortalLightbox(url, title = 'Official BTCNJ Portal') {
  const modal = document.getElementById('video-lightbox-modal');
  if (!modal || !url) return;

  const mount = document.getElementById('lightbox-iframe-mount');
  const titleEl = document.getElementById('modal-video-title');
  const headerIcon = document.getElementById('modal-header-icon');
  const directLink = document.getElementById('modal-yt-direct-link');
  const directIcon = document.getElementById('modal-direct-icon');
  const directLabel = document.getElementById('modal-direct-label');
  const navContainer = document.getElementById('modal-nav-container');
  const playerPrevBtn = document.getElementById('modal-player-prev-btn');
  const playerNextBtn = document.getElementById('modal-player-next-btn');
  const hintEl = document.getElementById('modal-context-hint');
  const dialog = modal.querySelector('.lightbox-dialog');

  if (dialog) dialog.className = 'lightbox-dialog modal-type-portal';

  if (navContainer) navContainer.style.display = 'none';
  if (playerPrevBtn) playerPrevBtn.style.display = 'none';
  if (playerNextBtn) playerNextBtn.style.display = 'none';

  if (titleEl) {
    titleEl.textContent = title;
    titleEl.title = title;
  }

  if (headerIcon) {
    headerIcon.innerHTML = `
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="2" y1="12" x2="22" y2="12"></line>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
      </svg>
    `;
  }

  if (mount) {
    mount.innerHTML = `
      <iframe
        src="${escapeHtml(url)}"
        title="${escapeHtml(title)}"
        sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
        allowfullscreen>
      </iframe>
    `;
  }

  if (directLink) directLink.href = url;
  if (directIcon) {
    directIcon.innerHTML = `
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
        <polyline points="15 3 21 3 21 9"></polyline>
        <line x1="10" y1="14" x2="21" y2="3"></line>
      </svg>
    `;
  }
  if (directLabel) directLabel.textContent = 'Open in New Tab';
  if (hintEl) hintEl.textContent = 'Official BTCNJ Club Portal';

  modal.classList.add('active');
  modal.setAttribute('aria-hidden', 'false');
  document.body.classList.add('modal-open');
  document.body.classList.remove('is-video-modal-open');
}

export function buildPhotoLightboxPlaylist(ride) {
  if (!ride) return [];
  const items = [];
  const seenUrls = new Set();
  const covers = typeof ALBUM_COVERS_MAP !== 'undefined' ? ALBUM_COVERS_MAP : {};
  const pCount = ride.photoCount || 0;
  const countStr = pCount > 0 ? `${pCount} Photos` : 'Full-Resolution Photos';
  const albumUrl = ride.photoAlbumUrl ? String(ride.photoAlbumUrl).trim() : '';

  const addPhotoUrl = (rawUrl, idxLabel) => {
    if (!rawUrl || typeof rawUrl !== 'string') return;
    const isSvg = rawUrl.startsWith('data:image/svg');
    const highDpi = (!isSvg && rawUrl.includes('googleusercontent.com'))
      ? (rawUrl.includes('=') ? rawUrl.replace(/=.*$/, '=w1920-h1080') : `${rawUrl}=w1920-h1080`)
      : rawUrl;
    if (!seenUrls.has(highDpi)) {
      seenUrls.add(highDpi);
      items.push({
        type: 'photo',
        url: highDpi,
        rawUrl,
        title: idxLabel || ride.title || 'Ride Photo',
        rideTitle: ride.title || 'Ride Photos',
        albumUrl,
        countStr
      });
    }
  };

  let rawCover = ride.coverPhotoUrl
    || (ride.dateIso && covers[ride.dateIso])
    || (ride.dateStr && covers[ride.dateStr.match(/\d{4}-\d{2}-\d{2}/)?.[0]]);
  if (rawCover && !String(rawCover).startsWith('data:image/svg')) {
    addPhotoUrl(rawCover, `${ride.title || 'Ride'} — Cover`);
  }

  let albumPhotos = Array.isArray(ride.photos) ? ride.photos : [];
  if (albumPhotos.length === 0 && typeof KNOWN_ALBUMS_MAP !== 'undefined') {
    const rideIdStr = ride.rideId ? String(ride.rideId).trim() : (ride.id ? String(ride.id).trim() : '');
    const known = (albumUrl && KNOWN_ALBUMS_MAP[albumUrl])
      || (rideIdStr && Object.values(KNOWN_ALBUMS_MAP).find(k => k.rideId && String(k.rideId).trim() === rideIdStr));
    if (known && Array.isArray(known.photos)) {
      albumPhotos = known.photos;
    }
  }

  albumPhotos.forEach((pUrl, idx) => {
    if (pUrl && typeof pUrl === 'string' && !pUrl.startsWith('data:image/svg')) {
      addPhotoUrl(pUrl, `${ride.title || 'Ride'} — Photo ${idx + 1}`);
    }
  });

  if (items.length === 0) {
    const fallbackThumb = typeof resolveRideThumbnail === 'function' ? resolveRideThumbnail(ride) : (ride.coverPhotoUrl || '');
    addPhotoUrl(fallbackThumb, ride.title || 'Ride Photos');
  }

  return items;
}

export function openPhotosLightbox(ride, initialIndex = 0) {
  const modal = document.getElementById('video-lightbox-modal');
  if (!modal || !ride || !ride.photoAlbumUrl) return;

  currentRideContext = ride;
  currentPlaylist = buildPhotoLightboxPlaylist(ride);
  currentPlaylistIndex = (typeof initialIndex === 'number' && initialIndex >= 0 && initialIndex < currentPlaylist.length)
    ? initialIndex
    : 0;

  modal.classList.add('active');
  modal.setAttribute('aria-hidden', 'false');
  document.body.classList.add('modal-open');
  document.body.classList.remove('is-video-modal-open');

  renderActiveVideo();
}

export function closeVideoLightbox() {
  const modal = document.getElementById('video-lightbox-modal');
  const mount = document.getElementById('lightbox-iframe-mount');

  if (!modal || !mount) return;

  clearAutoAdvanceTimer();

  const dialog = modal.querySelector('.lightbox-dialog');
  if (dialog) {
    dialog.classList.remove('is-portrait-video');
    dialog.classList.remove('is-theater-mode');
  }

  // Dismount iframe or photo stage immediately to release memory
  mount.innerHTML = '';
  currentPlaylist = [];
  currentPlaylistIndex = 0;
  currentRideContext = null;

  modal.classList.remove('active');
  modal.classList.remove('is-fullscreen-active');
  modal.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('modal-open');
  document.body.classList.remove('is-video-modal-open');

  // Resume diagnostic counter loop if enabled
  if (typeof isVortexSyncEnabled === 'function' && isVortexSyncEnabled()) {
    initTopAeroDiagnosticCounter();
  }

  // Resume clock and preview engines
  if (typeof resumeHistoricalClockSync === 'function') {
    resumeHistoricalClockSync();
  }
  if (typeof _reevaluatePreviewEngines === 'function') {
    _reevaluatePreviewEngines(300);
  }
}

// ============================================================================
// 7. Rider Feedback & Kudos Module (Cloud Firestore + Local Offline Buffer)
// ============================================================================
export const FIRESTORE_FEEDBACK_ENDPOINT = 'https://firestore.googleapis.com/v1/projects/btcnjcc/databases/(default)/documents/feedback';

export function initFeedbackModule() {
  const form = document.getElementById('feedback-form');
  const statusEl = document.getElementById('feedback-status');
  const feedbackAccordion = document.getElementById('feedback-accordion');
  if (feedbackAccordion) {
    const toggleText = feedbackAccordion.querySelector('.toggle-text');
    feedbackAccordion.addEventListener('toggle', () => {
      if (toggleText) {
        toggleText.textContent = feedbackAccordion.open ? 'Hide Form' : 'Give Kudos';
      }
    });
  }

  if (!form) return;

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const name = document.getElementById('rider-name')?.value.trim() || 'Anonymous Rider';
    const ride = document.getElementById('ride-selection')?.value || 'Club Ride';
    const comment = document.getElementById('rider-comment')?.value.trim() || '';

    if (!comment) return;

    const timestamp = new Date().toISOString();

    // 1. Instant offline resilience cache
    try {
      const stored = JSON.parse(localStorage.getItem('btcnjcc_kudos_v1') || '[]');
      stored.unshift({
        name,
        ride,
        comment,
        timestamp,
        status: 'unread'
      });
      localStorage.setItem('btcnjcc_kudos_v1', JSON.stringify(stored.slice(0, 50)));
    } catch (err) {
      console.warn('Feedback local buffer skipped:', err);
    }

    // 2. Cloud Firestore asynchronous ingestion
    const firestorePayload = {
      fields: {
        name: { stringValue: name },
        ride: { stringValue: ride },
        comment: { stringValue: comment },
        timestamp: { stringValue: timestamp },
        status: { stringValue: 'unread' }
      }
    };

    fetch(FIRESTORE_FEEDBACK_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(firestorePayload)
    }).catch(err => {
      console.warn('Firestore offline fallback (cached locally):', err);
    });

    if (statusEl) {
      statusEl.className = 'status-msg status-success';
      statusEl.textContent = 'Kudos Sent!'; // Word budget <= 6
      setTimeout(() => {
        statusEl.textContent = '';
      }, 4000);
    }

    form.reset();
  });
}

// ============================================================================
// 8. Admin Feedback Console & Moderation Vault
// ============================================================================
export function initAdminFeedbackConsole() {
  const modal = document.getElementById('admin-feedback-modal');
  const backdrop = document.getElementById('admin-modal-backdrop');
  const closeBtn = document.getElementById('admin-modal-close-btn');
  const dismissBtn = document.getElementById('admin-modal-dismiss-btn');
  const triggerBtn = document.getElementById('admin-console-trigger');
  const versionBadge = document.getElementById('corner-version-badge');

  const authGate = document.getElementById('admin-auth-gate');
  const authForm = document.getElementById('admin-auth-form');
  const passcodeEl = document.getElementById('admin-passcode-input');
  const authError = document.getElementById('admin-auth-error');

  const consoleContent = document.getElementById('admin-console-content');
  const metricTotalEl = document.getElementById('metric-total-count');
  const metricUnreadEl = document.getElementById('metric-unread-count');
  const metricSourceEl = document.getElementById('metric-source-label');

  const searchInput = document.getElementById('admin-search-input');
  const statusFilter = document.getElementById('admin-status-filter');
  const refreshBtn = document.getElementById('admin-refresh-btn');
  const exportCsvBtn = document.getElementById('admin-export-csv-btn');
  const feedbackList = document.getElementById('admin-feedback-list');

  // Admin Console Testbench & Vortex Controls
  const spotlightToast = document.getElementById('admin-spotlight-toast');
  const signoutBtn = document.getElementById('admin-signout-btn');
  const toggleVortexDiagCheckbox = document.getElementById('admin-toggle-vortex-diag');

  // Admin Navigation Tabs & Panes
  const tabFeedbackBtn = document.getElementById('admin-tab-feedback-btn');
  const tabArchivedBtn = document.getElementById('admin-tab-archived-btn');
  const paneFeedback = document.getElementById('admin-pane-feedback');
  const paneArchived = document.getElementById('admin-pane-archived');
  const archivedCountBadge = document.getElementById('admin-archived-count-badge');
  const archivedSearchInput = document.getElementById('admin-archived-search-input');
  const archivedRefreshBtn = document.getElementById('admin-archived-refresh-btn');
  const archivedList = document.getElementById('admin-archived-cards-list');
  const openCreateCardBtn = document.getElementById('admin-open-create-card-btn');

  if (!modal) return;

  let feedbackItems = [];

  function showSpotlightToast(msg) {
    if (!spotlightToast) return;
    spotlightToast.textContent = msg;
    spotlightToast.style.display = 'block';
    setTimeout(() => { spotlightToast.style.display = 'none'; }, 3000);
  }

  if (toggleVortexDiagCheckbox) {
    toggleVortexDiagCheckbox.addEventListener('change', () => {
      const isChecked = toggleVortexDiagCheckbox.checked;
      setVortexSyncEnabled(isChecked);
      showSpotlightToast(isChecked ? 'Vortex HUD Enabled' : 'Vortex HUD Hidden');
    });

    initAdminVortexLabControls();
  }

  if (signoutBtn) {
    signoutBtn.addEventListener('click', () => {
      sessionStorage.removeItem('btcnjcc_admin_authed');
      if (authGate) authGate.style.display = 'flex';
      if (consoleContent) consoleContent.style.display = 'none';
      if (signoutBtn) signoutBtn.style.display = 'none';
      if (passcodeEl) {
        passcodeEl.value = '';
        passcodeEl.focus();
      }
      if (currentLoadedRides && currentLoadedRides.length > 0) {
        const container = document.getElementById('rides-container');
        const countBadge = document.getElementById('rides-count-badge');
        const spotlightId = getSpotlightQueryId();
        if (!spotlightId) {
          renderRides(currentLoadedRides, container, countBadge);
        }
      }
    });
  }

  function openModal() {
    modal.classList.add('active');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('modal-open');

    const isAuthed = sessionStorage.getItem('btcnjcc_admin_authed') === 'true';
    if (signoutBtn) signoutBtn.style.display = isAuthed ? 'inline-flex' : 'none';
    if (toggleVortexDiagCheckbox) {
      toggleVortexDiagCheckbox.checked = isVortexSyncEnabled();
    }

    if (isAuthed) {
      if (authGate) authGate.style.display = 'none';
      if (consoleContent) consoleContent.style.display = 'flex';
      loadFeedbackData();
      renderArchivedCards();
    } else {
      if (authGate) authGate.style.display = 'flex';
      if (consoleContent) consoleContent.style.display = 'none';
      if (authError) authError.style.display = 'none';
      if (passcodeEl) {
        passcodeEl.value = '';
        setTimeout(() => passcodeEl.focus(), 80);
      }
    }
  }

  function closeModal() {
    modal.classList.remove('active');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('modal-open');
    if (window.location.hash === '#admin') {
      history.replaceState(null, '', window.location.pathname);
    }
  }

  // Authentication handling
  if (authForm) {
    authForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const enteredPin = passcodeEl?.value.trim() || '';
      const validPin = localStorage.getItem('btcnjcc_admin_pin') || 'btcnjcc2026';

      if (enteredPin === validPin) {
        sessionStorage.setItem('btcnjcc_admin_authed', 'true');
        if (signoutBtn) signoutBtn.style.display = 'inline-flex';
        if (toggleVortexDiagCheckbox) {
          toggleVortexDiagCheckbox.checked = isVortexSyncEnabled();
        }
        if (authGate) authGate.style.display = 'none';
        if (consoleContent) consoleContent.style.display = 'flex';
        loadFeedbackData();
        renderArchivedCards();

        // Refresh feed so admin buttons become visible on cards
        if (currentLoadedRides && currentLoadedRides.length > 0) {
          const container = document.getElementById('rides-container');
          const countBadge = document.getElementById('rides-count-badge');
          const spotlightId = getSpotlightQueryId();
          if (!spotlightId) {
            renderRides(currentLoadedRides, container, countBadge);
          }
        }
      } else {
        if (authError) {
          authError.textContent = 'Invalid Passcode';
          authError.style.display = 'block';
        }
      }
    });
  }

  async function loadFeedbackData() {
    if (!feedbackList) return;
    feedbackList.innerHTML = `
      <div class="loading-state">
        <div class="spinner"></div>
        <span>Connecting to Cloud Firestore...</span>
      </div>
    `;

    let cloudDocs = [];
    let isCloudSuccess = false;

    try {
      const res = await fetch(FIRESTORE_FEEDBACK_ENDPOINT);
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data.documents)) {
          cloudDocs = data.documents.map(doc => {
            const fields = doc.fields || {};
            return {
              docPath: doc.name,
              id: doc.name ? doc.name.split('/').pop() : Math.random().toString(36).slice(2),
              name: fields.name?.stringValue || 'Anonymous Rider',
              ride: fields.ride?.stringValue || 'Club Ride',
              comment: fields.comment?.stringValue || '',
              timestamp: fields.timestamp?.stringValue || doc.createTime || new Date().toISOString(),
              status: fields.status?.stringValue || 'unread'
            };
          });
          isCloudSuccess = true;
        } else {
          // Empty collection
          cloudDocs = [];
          isCloudSuccess = true;
        }
      }
    } catch (err) {
      console.warn('Cloud Firestore connection skipped, reading local buffer:', err);
    }

    // Merge local buffer if cloud unavailable or empty
    let localBuffer = [];
    try {
      localBuffer = JSON.parse(localStorage.getItem('btcnjcc_kudos_v1') || '[]');
    } catch (e) {}

    if (isCloudSuccess) {
      feedbackItems = cloudDocs;
      if (metricSourceEl) metricSourceEl.textContent = 'Firestore Cloud';
    } else if (localBuffer.length > 0) {
      feedbackItems = localBuffer.map((item, idx) => ({
        id: `local-${idx}`,
        name: item.name || 'Anonymous Rider',
        ride: item.ride || 'Club Ride',
        comment: item.comment || '',
        timestamp: item.timestamp || new Date().toISOString(),
        status: item.status || 'unread',
        isLocalOnly: true
      }));
      if (metricSourceEl) metricSourceEl.textContent = 'Local Buffer';
    } else {
      feedbackItems = [];
      if (metricSourceEl) metricSourceEl.textContent = 'Firestore Cloud';
    }

    // Sort newest first
    feedbackItems.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    renderFeedbackList();
  }

  function renderFeedbackList() {
    if (!feedbackList) return;

    const q = (searchInput?.value || '').toLowerCase().trim();
    const filter = statusFilter?.value || 'all';

    const filtered = feedbackItems.filter(item => {
      const matchesFilter = filter === 'all' || item.status === filter;
      const matchesQuery = !q ||
        item.name.toLowerCase().includes(q) ||
        item.ride.toLowerCase().includes(q) ||
        item.comment.toLowerCase().includes(q);
      return matchesFilter && matchesQuery;
    });

    // Update Telemetry Counters
    if (metricTotalEl) metricTotalEl.textContent = String(feedbackItems.length);
    if (metricUnreadEl) {
      const unreadCount = feedbackItems.filter(x => x.status === 'unread').length;
      metricUnreadEl.textContent = String(unreadCount);
    }

    if (filtered.length === 0) {
      feedbackList.innerHTML = `
        <div class="loading-state">
          <span>No feedback submissions found.</span>
        </div>
      `;
      return;
    }

    feedbackList.innerHTML = filtered.map(item => {
      const isUnread = item.status === 'unread';
      const timeStr = item.timestamp ? new Date(item.timestamp).toLocaleString() : '';
      const toggleLabel = isUnread ? 'Mark Read' : 'Mark Unread';

      return `
        <article class="admin-feedback-card ${escapeHtml(item.status)}" data-id="${escapeHtml(item.id)}">
          <header class="admin-card-header">
            <div class="admin-card-author">
              <span class="admin-author-name">${escapeHtml(item.name)}</span>
              <span class="admin-ride-tag">${escapeHtml(item.ride)}</span>
              <span class="admin-status-badge ${isUnread ? 'unread' : 'read'}">${isUnread ? 'UNREAD' : 'READ'}</span>
            </div>
            <time class="admin-card-time">${escapeHtml(timeStr)}</time>
          </header>
          <div class="admin-card-body">
            ${escapeHtml(item.comment)}
          </div>
          <footer class="admin-card-actions">
            <button type="button" class="admin-action-btn status-toggle-btn" data-id="${escapeHtml(item.id)}">
              ${toggleLabel}
            </button>
            <button type="button" class="admin-action-btn delete-btn" data-id="${escapeHtml(item.id)}">
              Delete
            </button>
          </footer>
        </article>
      `;
    }).join('');

    // Attach card action listeners
    feedbackList.querySelectorAll('.status-toggle-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-id');
        toggleItemStatus(id);
      });
    });

    feedbackList.querySelectorAll('.delete-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-id');
        deleteItem(id);
      });
    });
  }

  async function toggleItemStatus(id) {
    const item = feedbackItems.find(x => x.id === id);
    if (!item) return;

    const nextStatus = item.status === 'unread' ? 'read' : 'unread';
    item.status = nextStatus;
    renderFeedbackList();

    // Firestore PATCH
    if (item.docPath) {
      try {
        await fetch(`https://firestore.googleapis.com/v1/${item.docPath}?updateMask.fieldPaths=status`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fields: { status: { stringValue: nextStatus } }
          })
        });
      } catch (err) {
        console.warn('Status patch skipped:', err);
      }
    }
  }

  async function deleteItem(id) {
    const item = feedbackItems.find(x => x.id === id);
    if (!item) return;

    feedbackItems = feedbackItems.filter(x => x.id !== id);
    renderFeedbackList();

    // Firestore DELETE
    if (item.docPath) {
      try {
        await fetch(`https://firestore.googleapis.com/v1/${item.docPath}`, {
          method: 'DELETE'
        });
      } catch (err) {
        console.warn('Firestore delete skipped:', err);
      }
    }
  }

  // Filter and Search Events
  if (searchInput) searchInput.addEventListener('input', renderFeedbackList);
  if (statusFilter) statusFilter.addEventListener('change', renderFeedbackList);
  if (refreshBtn) refreshBtn.addEventListener('click', loadFeedbackData);

  // CSV Export Action
  if (exportCsvBtn) {
    exportCsvBtn.addEventListener('click', () => {
      if (feedbackItems.length === 0) {
        alert('No feedback to export.');
        return;
      }

      const headers = ['Timestamp', 'Rider', 'Ride', 'Status', 'Comment'];
      const rows = feedbackItems.map(item => [
        `"${(item.timestamp || '').replace(/"/g, '""')}"`,
        `"${(item.name || '').replace(/"/g, '""')}"`,
        `"${(item.ride || '').replace(/"/g, '""')}"`,
        `"${(item.status || 'unread').replace(/"/g, '""')}"`,
        `"${(item.comment || '').replace(/"/g, '""')}"`
      ]);

      const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\r\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `btcnjcc-feedback-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    });
  }

  // --- Admin Tabs Navigation ---
  function switchAdminTab(activeTab) {
    if (activeTab === 'archived') {
      tabFeedbackBtn?.classList.remove('active');
      tabArchivedBtn?.classList.add('active');
      if (paneFeedback) paneFeedback.style.display = 'none';
      if (paneArchived) paneArchived.style.display = 'block';
      renderArchivedCards();
    } else {
      tabArchivedBtn?.classList.remove('active');
      tabFeedbackBtn?.classList.add('active');
      if (paneArchived) paneArchived.style.display = 'none';
      if (paneFeedback) paneFeedback.style.display = 'block';
      renderFeedbackList();
    }
  }

  if (tabFeedbackBtn) tabFeedbackBtn.addEventListener('click', () => switchAdminTab('feedback'));
  if (tabArchivedBtn) tabArchivedBtn.addEventListener('click', () => switchAdminTab('archived'));
  if (archivedRefreshBtn) archivedRefreshBtn.addEventListener('click', async () => {
    archivedRefreshBtn.textContent = 'Syncing...';
    await fetchCloudMediaOverrides();
    renderArchivedCards();
    archivedRefreshBtn.textContent = 'Sync Cloud';
  });
  if (archivedSearchInput) archivedSearchInput.addEventListener('input', () => renderArchivedCards());

  // --- Render Archived Cards ---
  function renderArchivedCards() {
    if (!archivedList) return;

    const mediaOverrides = getMediaOverrides();
    const archivedMap = new Map();

    (currentLoadedRides || []).forEach(r => {
      if (r.isArchived && !r.isDeleted) archivedMap.set(r.id, r);
    });

    Object.entries(mediaOverrides).forEach(([k, ov]) => {
      if (ov && ov.archived && !ov.deleted && !archivedMap.has(k)) {
        archivedMap.set(k, {
          id: k,
          title: ov.telemetry?.title || ov.customData?.title || k,
          dateIso: ov.telemetry?.dateStr || ov.customData?.dateIso || '',
          dateStr: ov.telemetry?.dateStr || ov.customData?.dateIso || '',
          matchedVideos: ov.customData?.matchedVideos || [],
          photoAlbumUrl: ov.photoAlbumUrl || ov.customData?.photoAlbumUrl || null,
          photoCount: ov.photoCount || 0,
          coverPhotoUrl: ov.coverPhotoUrl || null,
          isArchived: true,
          isCustom: Boolean(ov.isCustom)
        });
      }
    });

    const archivedRides = Array.from(archivedMap.values());
    if (archivedCountBadge) {
      archivedCountBadge.textContent = String(archivedRides.length);
    }

    const q = (archivedSearchInput?.value || '').toLowerCase().trim();
    const filtered = archivedRides.filter(r => {
      if (!q) return true;
      return (r.title || '').toLowerCase().includes(q) ||
             (r.id || '').toLowerCase().includes(q) ||
             (r.dateStr || '').toLowerCase().includes(q);
    });

    if (filtered.length === 0) {
      archivedList.innerHTML = `
        <div class="loading-state">
          <span>${archivedRides.length === 0 ? 'No archived cards. All media cards are active in public feed.' : 'No matching archived cards.'}</span>
        </div>
      `;
      return;
    }

    archivedList.innerHTML = filtered.map(ride => {
      const thumb = resolveRideThumbnail(ride);
      const dateDisplay = (ride.dateIso || ride.dateStr) ? formatRideDate(ride.dateIso || ride.dateStr) : 'No Date';
      const hasVideo = ride.matchedVideos && ride.matchedVideos.length > 0;
      const mediaBadge = hasVideo ? `${ride.matchedVideos.length} Video${ride.matchedVideos.length > 1 ? 's' : ''}` : (ride.photoAlbumUrl ? 'Photos' : 'Media');

      return `
        <article class="admin-archived-card-item" data-card-id="${escapeHtml(ride.id)}">
          <div class="archived-card-thumb-wrap">
            <img src="${escapeHtml(thumb)}" alt="Thumbnail" class="archived-card-thumb" referrerpolicy="no-referrer" onerror="this.onerror=null; this.src=(window.getCyclingFallbackSvg ? window.getCyclingFallbackSvg() : '');">
            <span class="archived-badge-pill">ARCHIVED</span>
          </div>
          <div class="archived-card-info">
            <div class="archived-card-title-row">
              <span class="archived-card-title" title="${escapeHtml(ride.title)}">${escapeHtml(ride.title)}</span>
              <span class="archived-reason-tag">${escapeHtml(mediaBadge)}</span>
            </div>
            <div class="archived-card-meta">${escapeHtml(dateDisplay)} • ID: <code>${escapeHtml(ride.id)}</code></div>
          </div>
          <div class="archived-card-actions">
            <button type="button" class="btn btn-secondary admin-restore-btn" data-card-id="${escapeHtml(ride.id)}" title="Restore to public feed">
              ♻ Restore
            </button>
            <button type="button" class="btn btn-secondary admin-delete-btn" data-card-id="${escapeHtml(ride.id)}" data-ride-title="${escapeHtml(ride.title)}" title="Permanently delete or suppress">
              🗑 Delete
            </button>
          </div>
        </article>
      `;
    }).join('');

    // Wire restore buttons
    archivedList.querySelectorAll('.admin-restore-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const cardId = btn.getAttribute('data-card-id');
        btn.disabled = true;
        btn.textContent = 'Restoring...';
        await saveMediaOverride(cardId, { archived: false });
        const ride = currentLoadedRides.find(r => r.id === cardId);
        if (ride) {
          ride.isArchived = false;
        }
        const container = document.getElementById('rides-container');
        const countBadge = document.getElementById('rides-count-badge');
        renderRides(currentLoadedRides, container, countBadge);
        setCachedMediaRides(currentLoadedRides);
        renderArchivedCards();
        showSpotlightToast('Card Restored');
      });
    });

    // Wire delete buttons
    archivedList.querySelectorAll('.admin-delete-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const cardId = btn.getAttribute('data-card-id');
        const rideTitle = btn.getAttribute('data-ride-title') || cardId;
        if (!confirm(`Permanently delete "${rideTitle}"?\n\nThis will permanently suppress the card from the application.`)) {
          return;
        }
        btn.disabled = true;
        btn.textContent = 'Deleting...';

        const rideIdx = currentLoadedRides.findIndex(r => r.id === cardId);
        const ride = rideIdx !== -1 ? currentLoadedRides[rideIdx] : null;

        if (ride && ride.isCustom) {
          await deleteMediaOverride(cardId);
          if (rideIdx !== -1) currentLoadedRides.splice(rideIdx, 1);
        } else {
          // Auto-ingested ride: apply tombstone
          await saveMediaOverride(cardId, { deleted: true, archived: false });
          if (ride) {
            ride.isDeleted = true;
            ride.isArchived = false;
          }
        }

        const container = document.getElementById('rides-container');
        const countBadge = document.getElementById('rides-count-badge');
        renderRides(currentLoadedRides, container, countBadge);
        setCachedMediaRides(currentLoadedRides);
        renderArchivedCards();
        showSpotlightToast('Card Deleted');
      });
    });
  }

  // --- Create Media Card Modal ---
  const createModal = document.getElementById('admin-create-card-modal');
  const createBackdrop = document.getElementById('admin-create-card-backdrop');
  const createCloseBtn = document.getElementById('admin-create-card-close-btn');
  const createCancelBtn = document.getElementById('admin-create-card-cancel-btn');
  const createForm = document.getElementById('admin-create-card-form');
  const createDateInput = document.getElementById('admin-create-date-input');
  const createBtcnjInput = document.getElementById('admin-create-btcnj-id-input');
  const createLookupBtn = document.getElementById('admin-create-lookup-btn');
  const createLookupCard = document.getElementById('admin-create-lookup-card');
  const createLookupTitle = document.getElementById('admin-create-lookup-title');
  const createLookupCat = document.getElementById('admin-create-lookup-cat');
  const createLookupMiles = document.getElementById('admin-create-lookup-miles');
  const createLookupClimb = document.getElementById('admin-create-lookup-climb');
  const createLookupLeaders = document.getElementById('admin-create-lookup-leaders');
  const createTitleInput = document.getElementById('admin-create-title-input');
  const createAlbumInput = document.getElementById('admin-create-album-url-input');
  const createVideoInput = document.getElementById('admin-create-video-url-input');
  const createCoverInput = document.getElementById('admin-create-cover-url-input');
  const createRwgpsInput = document.getElementById('admin-create-rwgps-url-input');
  const createError = document.getElementById('admin-create-card-error');
  const createSuccess = document.getElementById('admin-create-card-success');
  const createSubmitBtn = document.getElementById('admin-create-card-submit-btn');

  let activeCreateLookup = null;

  function openCreateModal() {
    if (!createModal) return;
    createModal.classList.add('active');
    createModal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('modal-open');
    if (createDateInput) {
      createDateInput.value = new Date().toISOString().slice(0, 10);
    }
    if (createError) createError.style.display = 'none';
    if (createSuccess) createSuccess.style.display = 'none';
  }

  function closeCreateModal() {
    if (!createModal) return;
    createModal.classList.remove('active');
    createModal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('modal-open');
    if (createForm) createForm.reset();
    if (createLookupCard) createLookupCard.style.display = 'none';
    activeCreateLookup = null;
  }

  if (openCreateCardBtn) openCreateCardBtn.addEventListener('click', openCreateModal);
  if (createCloseBtn) createCloseBtn.addEventListener('click', closeCreateModal);
  if (createCancelBtn) createCancelBtn.addEventListener('click', closeCreateModal);
  if (createBackdrop) createBackdrop.addEventListener('click', closeCreateModal);

  if (createLookupBtn && createBtcnjInput) {
    createLookupBtn.addEventListener('click', async () => {
      const rideId = createBtcnjInput.value.trim();
      if (!rideId) return;
      createLookupBtn.disabled = true;
      createLookupBtn.textContent = 'Looking up...';
      try {
        const result = await lookupBtcnjRide(rideId);
        if (result) {
          activeCreateLookup = result;
          if (createDateInput && result.dateIso) createDateInput.value = result.dateIso;
          if (createTitleInput && !createTitleInput.value) createTitleInput.value = result.title;
          if (createLookupTitle) createLookupTitle.textContent = result.title;
          if (createLookupCat) createLookupCat.textContent = result.category || 'BTCNJ';
          if (createLookupMiles) createLookupMiles.textContent = result.miles ? `${result.miles} mi` : '-- mi';
          if (createLookupClimb) createLookupClimb.textContent = result.climbFt ? `${Math.round(result.climbFt).toLocaleString()} ft` : '-- ft';
          if (createLookupLeaders) createLookupLeaders.textContent = `Leaders: ${result.leaders.join(', ') || 'None listed'}`;
          if (createRwgpsInput && result.rwgpsUrl && !createRwgpsInput.value) createRwgpsInput.value = result.rwgpsUrl;
          if (createLookupCard) createLookupCard.style.display = 'flex';
        } else {
          if (createError) {
            createError.textContent = 'BTCNJ Ride ID Not Found';
            createError.style.display = 'block';
          }
        }
      } finally {
        createLookupBtn.disabled = false;
        createLookupBtn.textContent = 'Lookup';
      }
    });
  }

  if (createForm) {
    createForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const dateVal = createDateInput?.value.trim();
      const titleVal = createTitleInput?.value.trim();
      if (!dateVal || !titleVal) {
        if (createError) {
          createError.textContent = 'Date and Title are required';
          createError.style.display = 'block';
        }
        return;
      }

      if (createSubmitBtn) {
        createSubmitBtn.disabled = true;
        createSubmitBtn.textContent = 'Publishing...';
      }

      const customId = `ride-custom-${Date.now()}`;
      const videoInputVal = createVideoInput?.value.trim();
      const videoId = videoInputVal ? extractYouTubeVideoId(videoInputVal) : null;
      const matchedVideos = videoId ? [{ videoId: videoId, title: titleVal, isManual: true }] : [];

      const overrideData = {
        cardId: customId,
        isCustom: true,
        archived: false,
        deleted: false,
        assignedRideId: createBtcnjInput?.value.trim() || null,
        photoAlbumUrl: createAlbumInput?.value.trim() || null,
        photoCount: 0,
        coverPhotoUrl: createCoverInput?.value.trim() || null,
        customData: {
          title: titleVal,
          dateIso: dateVal,
          dateStr: dateVal,
          location: 'North Jersey',
          category: activeCreateLookup?.category || null,
          miles: activeCreateLookup?.miles || null,
          climbFt: activeCreateLookup?.climbFt || null,
          leaders: activeCreateLookup?.leaders || [],
          members: activeCreateLookup?.members || [],
          rwgpsUrl: createRwgpsInput?.value.trim() || activeCreateLookup?.rwgpsUrl || null,
          matchedVideos: matchedVideos
        }
      };

      await saveMediaOverride(customId, overrideData);

      const newRide = {
        id: customId,
        rideId: overrideData.assignedRideId,
        isBtcnjRide: Boolean(overrideData.assignedRideId),
        btcnjUrl: overrideData.assignedRideId ? `https://btcnj.com/pages/rideSignupEdit?rideid=${encodeURIComponent(overrideData.assignedRideId)}` : null,
        r5mUrl: null,
        rwgpsUrl: overrideData.customData.rwgpsUrl,
        title: titleVal,
        dateIso: dateVal,
        dateStr: dateVal,
        leaders: overrideData.customData.leaders,
        members: overrideData.customData.members,
        location: 'North Jersey',
        miles: overrideData.customData.miles,
        climbFt: overrideData.customData.climbFt,
        category: overrideData.customData.category,
        photoAlbumUrl: overrideData.photoAlbumUrl,
        photoCount: 0,
        coverPhotoUrl: overrideData.coverPhotoUrl,
        matchedVideos: matchedVideos,
        isCustom: true,
        isArchived: false,
        isDeleted: false
      };

      currentLoadedRides.push(newRide);
      currentLoadedRides.sort((a, b) => (b.dateIso || '').localeCompare(a.dateIso || ''));

      const container = document.getElementById('rides-container');
      const countBadge = document.getElementById('rides-count-badge');
      renderRides(currentLoadedRides, container, countBadge);

      closeCreateModal();
      showSpotlightToast('Card Published');
      if (createSubmitBtn) {
        createSubmitBtn.disabled = false;
        createSubmitBtn.textContent = 'Publish Card';
      }
    });
  }

  // --- Feed Card Archive Action Delegation ---
  document.addEventListener('click', async (e) => {
    const archiveBtn = e.target.closest('[data-action="archive-card"]');
    if (archiveBtn) {
      e.preventDefault();
      e.stopPropagation();
      const cardId = archiveBtn.getAttribute('data-card-id');
      const rideTitle = archiveBtn.getAttribute('data-ride-title') || cardId;

      if (!confirm(`Archive "${rideTitle}"?\n\nThis will hide the card from the public feed and move it to the Admin Archived section.`)) {
        return;
      }

      await saveMediaOverride(cardId, { archived: true });
      const ride = currentLoadedRides.find(r => r.id === cardId);
      if (ride) {
        ride.isArchived = true;
      }
      const container = document.getElementById('rides-container');
      const countBadge = document.getElementById('rides-count-badge');
      renderRides(currentLoadedRides, container, countBadge);
      setCachedMediaRides(currentLoadedRides);
      showSpotlightToast('Card Archived');
    }
  });

  // --- Feed Card Member Video Exclusion Action Delegation ---
  document.addEventListener('click', async (e) => {
    const excludeBtn = e.target.closest('[data-action="exclude-member-card"]');
    if (excludeBtn) {
      e.preventDefault();
      e.stopPropagation();
      const cardId = excludeBtn.getAttribute('data-card-id');
      const rideTitle = excludeBtn.getAttribute('data-ride-title') || cardId;

      if (!confirm(`Exclude "${rideTitle}" from Member Media?\n\nThis will remove the video(s) from the Community Member Media section.`)) {
        return;
      }

      const ride = (currentLoadedRides || []).find(r => r.id === cardId);
      if (ride && Array.isArray(ride.matchedVideos) && ride.matchedVideos.length > 0) {
        for (const v of ride.matchedVideos) {
          if (v && v.videoId) {
            await excludeMemberMediaVideo(v.videoId);
          }
        }
      } else if (cardId) {
        await excludeMemberMediaVideo(cardId);
      }

      await fetchAndRenderRides(false);
      showSpotlightToast('Video Excluded');
    }
  });

  // Triggers & Modal Management
  if (triggerBtn) triggerBtn.addEventListener('click', openModal);
  if (closeBtn) closeBtn.addEventListener('click', closeModal);
  if (dismissBtn) dismissBtn.addEventListener('click', closeModal);
  if (backdrop) backdrop.addEventListener('click', closeModal);

  // Secret 3-tap trigger on corner version badge
  let clickCount = 0;
  let clickTimer = null;
  if (versionBadge) {
    versionBadge.style.cursor = 'pointer';
    versionBadge.addEventListener('click', () => {
      clickCount++;
      clearTimeout(clickTimer);
      if (clickCount >= 3) {
        clickCount = 0;
        openModal();
      } else {
        clickTimer = setTimeout(() => {
          clickCount = 0;
        }, 1500);
      }
    });
  }

  // Keyboard Shortcuts: Alt+A and Escape
  window.addEventListener('keydown', (e) => {
    if (e.altKey && (e.key === 'a' || e.key === 'A')) {
      e.preventDefault();
      openModal();
    } else if (e.key === 'Escape' && modal.classList.contains('active')) {
      closeModal();
    }
  });

  // URL Hash Trigger (#admin)
  if (window.location.hash === '#admin') {
    openModal();
  }
  window.addEventListener('hashchange', () => {
    if (window.location.hash === '#admin') {
      openModal();
    }
  });
}

// URL History Navigation for Ride Spotlight (Back/Forward browser buttons)
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    const spotlightId = getSpotlightQueryId();
    const container = document.getElementById('rides-container');
    const countBadge = document.getElementById('rides-count-badge');
    if (spotlightId) {
      renderSpotlightView(spotlightId, currentLoadedRides, container, countBadge, { direction: 'forward' });
    } else {
      exitSpotlightMode(currentLoadedRides, container, countBadge, { direction: 'backward' });
    }
  });
}

// ============================================================================
// 9. Admin Ride Video Manager Modal
// ============================================================================
let activeManagerRide = null;
let activeWorkingVideos = [];

export function openRideVideoManager(rideId) {
  if (typeof document === 'undefined' || !rideId) return;
  const modal = document.getElementById('ride-video-modal');
  if (!modal) return;

  const rides = currentLoadedRides || [];
  activeManagerRide = rides.find(r => String(r.id) === String(rideId) || String(r.rideId) === String(rideId));
  if (!activeManagerRide) return;

  if (!activeManagerRide._autoMatchedVideos) {
    activeManagerRide._autoMatchedVideos = [...(activeManagerRide.matchedVideos || [])];
  }

  const allOverrides = getVideoOverrides();
  const overrideKey = String(activeManagerRide.rideId || activeManagerRide.id);
  const override = allOverrides[overrideKey] || (activeManagerRide.rideId && allOverrides[String(activeManagerRide.rideId)]);

  if (override) {
    activeManagerRide.matchedVideos = reconcileRideVideos(activeManagerRide._autoMatchedVideos, override);
  }

  activeWorkingVideos = (activeManagerRide.matchedVideos || []).map(v => ({ ...v }));

  const rideTitleEl = document.getElementById('ride-video-ride-title');
  const rideSubEl = document.getElementById('ride-video-ride-sub');
  const urlInput = document.getElementById('ride-video-url-input');
  const addError = document.getElementById('ride-video-add-error');

  if (rideTitleEl) rideTitleEl.textContent = activeManagerRide.title || 'Untitled Ride';
  if (urlInput) urlInput.value = '';
  if (addError) {
    addError.style.display = 'none';
    addError.textContent = '';
  }

  renderRideVideoManagerList();

  modal.classList.add('active');
  modal.setAttribute('aria-hidden', 'false');
  document.body.classList.add('modal-open');
}

export function closeRideVideoManagerModal() {
  if (typeof document === 'undefined') return;
  const modal = document.getElementById('ride-video-modal');
  if (!modal) return;
  modal.classList.remove('active');
  modal.classList.remove('is-fullscreen-active');
  modal.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('modal-open');
  activeManagerRide = null;
  activeWorkingVideos = [];
}

export function renderRideVideoManagerList() {
  if (typeof document === 'undefined') return;
  const listEl = document.getElementById('ride-video-list');
  const rideSubEl = document.getElementById('ride-video-ride-sub');

  if (rideSubEl && activeManagerRide) {
    rideSubEl.textContent = `${activeManagerRide.date || ''} • ${activeWorkingVideos.length} ${activeWorkingVideos.length === 1 ? 'Video' : 'Videos'}`;
  }

  if (!listEl) return;

  if (activeWorkingVideos.length === 0) {
    listEl.innerHTML = `
      <div style="padding: 24px 16px; text-align: center; color: var(--text-muted); font-size: 0.9rem;">
        No videos currently attached. Add a YouTube link below or reset to auto-match.
      </div>
    `;
    return;
  }

  const totalVideos = activeWorkingVideos.length;

  listEl.innerHTML = activeWorkingVideos.map((v, idx) => {
    const thumb = v.thumbnail || `https://img.youtube.com/vi/${v.videoId}/mqdefault.jpg`;
    const isManual = Boolean(v.isManual || (activeManagerRide?._autoMatchedVideos && !activeManagerRide._autoMatchedVideos.some(av => av.videoId === v.videoId)));
    const isFirst = idx === 0;
    const isLast = idx === totalVideos - 1;

    const posOptions = activeWorkingVideos.map((_, pIdx) => `
      <option value="${pIdx}" ${pIdx === idx ? 'selected' : ''}>#${pIdx + 1}</option>
    `).join('');

    return `
      <div class="ride-video-row" data-index="${idx}" data-video-id="${escapeHtml(v.videoId)}" draggable="true">
        <div class="ride-video-drag-handle" title="Drag to reorder or pick position">
          <svg class="drag-icon-svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="9" cy="5" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="9" cy="19" r="1.5"/>
            <circle cx="15" cy="5" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="15" cy="19" r="1.5"/>
          </svg>
          <select class="ride-video-pos-select" data-index="${idx}" title="Direct Position Selector" aria-label="Change position">
            ${posOptions}
          </select>
        </div>
        <img src="${escapeHtml(thumb)}" alt="Thumbnail" class="ride-video-thumb" loading="lazy" referrerpolicy="no-referrer" onerror="this.onerror=null; this.src=(window.getCyclingFallbackSvg ? window.getCyclingFallbackSvg() : '');" />
        <div class="ride-video-info">
          <div class="ride-video-name" title="${escapeHtml(v.title || '')}">${escapeHtml(v.title || 'YouTube Video')}</div>
          <div class="ride-video-meta-line">
            <span class="ride-video-id">${escapeHtml(v.videoId)}</span>
            <span class="ride-video-badge ${isManual ? 'badge-manual' : 'badge-auto'}">${isManual ? 'MANUAL' : 'AUTO'}</span>
          </div>
        </div>
        <div class="ride-video-actions">
          <button type="button" class="btn-video-reorder btn-move-top" data-index="${idx}" title="Move to Top (#1)" ${isFirst ? 'disabled' : ''}>⤒</button>
          <button type="button" class="btn-video-reorder btn-move-up" data-index="${idx}" title="Move Up" ${isFirst ? 'disabled' : ''}>▲</button>
          <button type="button" class="btn-video-reorder btn-move-down" data-index="${idx}" title="Move Down" ${isLast ? 'disabled' : ''}>▼</button>
          <button type="button" class="btn-video-delete" data-index="${idx}" title="Remove Video">✕</button>
        </div>
      </div>
    `;
  }).join('');

  // 1. Move to Top
  listEl.querySelectorAll('.btn-move-top').forEach(btn => {
    btn.addEventListener('click', () => {
      const i = parseInt(btn.getAttribute('data-index'), 10);
      if (i > 0) {
        const [movedItem] = activeWorkingVideos.splice(i, 1);
        activeWorkingVideos.unshift(movedItem);
        renderRideVideoManagerList();
      }
    });
  });

  // 2. Step Up
  listEl.querySelectorAll('.btn-move-up').forEach(btn => {
    btn.addEventListener('click', () => {
      const i = parseInt(btn.getAttribute('data-index'), 10);
      if (i > 0) {
        const temp = activeWorkingVideos[i];
        activeWorkingVideos[i] = activeWorkingVideos[i - 1];
        activeWorkingVideos[i - 1] = temp;
        renderRideVideoManagerList();
      }
    });
  });

  // 3. Step Down
  listEl.querySelectorAll('.btn-move-down').forEach(btn => {
    btn.addEventListener('click', () => {
      const i = parseInt(btn.getAttribute('data-index'), 10);
      if (i < activeWorkingVideos.length - 1) {
        const temp = activeWorkingVideos[i];
        activeWorkingVideos[i] = activeWorkingVideos[i + 1];
        activeWorkingVideos[i + 1] = temp;
        renderRideVideoManagerList();
      }
    });
  });

  // 4. Direct Position Selector (<select>)
  listEl.querySelectorAll('.ride-video-pos-select').forEach(sel => {
    sel.addEventListener('change', () => {
      const fromIdx = parseInt(sel.getAttribute('data-index'), 10);
      const toIdx = parseInt(sel.value, 10);
      if (fromIdx !== toIdx && fromIdx >= 0 && toIdx >= 0 && fromIdx < activeWorkingVideos.length && toIdx < activeWorkingVideos.length) {
        const [movedItem] = activeWorkingVideos.splice(fromIdx, 1);
        activeWorkingVideos.splice(toIdx, 0, movedItem);
        renderRideVideoManagerList();
      }
    });
  });

  // 5. Delete Video
  listEl.querySelectorAll('.btn-video-delete').forEach(btn => {
    btn.addEventListener('click', () => {
      const i = parseInt(btn.getAttribute('data-index'), 10);
      if (i >= 0 && i < activeWorkingVideos.length) {
        activeWorkingVideos.splice(i, 1);
        renderRideVideoManagerList();
      }
    });
  });

  // 6. Native HTML5 Drag & Drop
  let draggedIndex = null;
  listEl.querySelectorAll('.ride-video-row').forEach(row => {
    row.addEventListener('dragstart', (e) => {
      draggedIndex = parseInt(row.getAttribute('data-index'), 10);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(draggedIndex));
      row.classList.add('is-dragging');
    });

    row.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      row.classList.add('drag-over');
    });

    row.addEventListener('dragleave', () => {
      row.classList.remove('drag-over');
    });

    row.addEventListener('drop', (e) => {
      e.preventDefault();
      row.classList.remove('drag-over');
      const targetIndex = parseInt(row.getAttribute('data-index'), 10);
      if (draggedIndex !== null && !isNaN(draggedIndex) && draggedIndex !== targetIndex) {
        const [movedItem] = activeWorkingVideos.splice(draggedIndex, 1);
        activeWorkingVideos.splice(targetIndex, 0, movedItem);
        renderRideVideoManagerList();
      }
    });

    row.addEventListener('dragend', () => {
      draggedIndex = null;
      row.classList.remove('is-dragging');
      listEl.querySelectorAll('.drag-over').forEach(el => el.classList.remove('drag-over'));
    });
  });
}

export function initRideVideoManagerModal() {
  if (typeof document === 'undefined') return;

  const modal = document.getElementById('ride-video-modal');
  if (!modal) return;

  const backdrop = document.getElementById('ride-video-backdrop');
  const closeBtn = document.getElementById('ride-video-close-btn');
  const cancelBtn = document.getElementById('ride-video-cancel-btn');
  const saveBtn = document.getElementById('ride-video-save-btn');
  const resetBtn = document.getElementById('ride-video-reset-btn');
  const addForm = document.getElementById('ride-video-add-form');
  const urlInput = document.getElementById('ride-video-url-input');
  const addError = document.getElementById('ride-video-add-error');

  // Document delegation for open
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-action="manage-videos"]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const rideId = btn.getAttribute('data-ride-id');
    openRideVideoManager(rideId);
  });

  // Add video form submit
  if (addForm) {
    addForm.addEventListener('submit', (e) => {
      e.preventDefault();
      if (!urlInput) return;
      const raw = urlInput.value.trim();
      const vidId = extractYouTubeVideoId(raw);

      if (!vidId) {
        if (addError) {
          addError.textContent = 'Invalid YouTube URL or ID';
          addError.style.display = 'block';
        }
        return;
      }

      const existingIdx = activeWorkingVideos.findIndex(v => v.videoId === vidId);
      if (existingIdx !== -1) {
        const displayPos = existingIdx + 1;
        if (addError) {
          addError.textContent = `Video already added (#${displayPos} in list — highlighted below)`;
          addError.style.display = 'block';
        }
        const listEl = document.getElementById('ride-video-list');
        const existingRow = listEl ? listEl.querySelector(`.ride-video-row[data-video-id="${vidId}"]`) : null;
        if (existingRow) {
          if (listEl && typeof listEl.scrollTo === 'function') {
            listEl.scrollTo({ top: existingRow.offsetTop - listEl.offsetTop, behavior: 'smooth' });
          }
          existingRow.classList.remove('highlight-pulse');
          void existingRow.offsetWidth; // Trigger reflow for re-animation
          existingRow.classList.add('highlight-pulse');
          setTimeout(() => existingRow.classList.remove('highlight-pulse'), 2200);
        }
        return;
      }

      if (addError) {
        addError.style.display = 'none';
        addError.textContent = '';
      }

      activeWorkingVideos.push({
        videoId: vidId,
        title: `YouTube Video (${vidId})`,
        thumbnail: `https://img.youtube.com/vi/${vidId}/mqdefault.jpg`,
        isManual: true,
        url: `https://www.youtube.com/watch?v=${vidId}`
      });

      urlInput.value = '';
      renderRideVideoManagerList();

      // Scroll to newly added row and pulse highlight
      const listEl = document.getElementById('ride-video-list');
      const newRow = listEl ? listEl.querySelector(`.ride-video-row[data-video-id="${vidId}"]`) : null;
      if (newRow) {
        if (listEl && typeof listEl.scrollTo === 'function') {
          listEl.scrollTo({ top: newRow.offsetTop - listEl.offsetTop, behavior: 'smooth' });
        }
        newRow.classList.add('highlight-pulse');
        setTimeout(() => newRow.classList.remove('highlight-pulse'), 2200);
      }
    });
  }

  // Save changes
  if (saveBtn) {
    saveBtn.addEventListener('click', async () => {
      if (!activeManagerRide) return;
      const rideKey = String(activeManagerRide.rideId || activeManagerRide.id);

      const workingIds = new Set(activeWorkingVideos.map(v => String(v.videoId).trim()));
      const autoList = activeManagerRide._autoMatchedVideos || [];
      const autoIds = new Set(autoList.map(v => String(v.videoId).trim()));

      const excludedVideoIds = autoList
        .map(v => String(v.videoId).trim())
        .filter(id => !workingIds.has(id));

      const addedVideos = activeWorkingVideos.filter(v => v.isManual || !autoIds.has(String(v.videoId).trim()));
      const orderedVideoIds = activeWorkingVideos.map(v => String(v.videoId).trim());

      const overrideData = {
        orderedVideoIds,
        excludedVideoIds,
        addedVideos,
        updatedAt: new Date().toISOString()
      };

      saveBtn.disabled = true;
      saveBtn.textContent = 'Saving...';

      try {
        await saveRideVideoOverride(rideKey, overrideData);
        activeManagerRide.matchedVideos = [...activeWorkingVideos];

        const container = document.getElementById('rides-container');
        const countBadge = document.getElementById('rides-count-badge');
        if (container) {
          renderRides(currentLoadedRides, container, countBadge);
        }
        if (typeof setCachedMediaRides === 'function' && Array.isArray(currentLoadedRides)) {
          setCachedMediaRides(currentLoadedRides);
        }
      } finally {
        saveBtn.disabled = false;
        saveBtn.textContent = 'Save';
        closeRideVideoManagerModal();
      }
    });
  }

  // Reset to auto
  if (resetBtn) {
    resetBtn.addEventListener('click', async () => {
      if (!activeManagerRide) return;
      const rideKey = String(activeManagerRide.rideId || activeManagerRide.id);

      resetBtn.disabled = true;
      resetBtn.textContent = 'Resetting...';

      try {
        await resetRideVideoOverride(rideKey);
        const autoList = activeManagerRide._autoMatchedVideos || [];
        activeManagerRide.matchedVideos = sortMatchedVideos([...autoList], activeManagerRide.title || '');
        activeWorkingVideos = (activeManagerRide.matchedVideos || []).map(v => ({ ...v }));

        const container = document.getElementById('rides-container');
        const countBadge = document.getElementById('rides-count-badge');
        if (container) {
          renderRides(currentLoadedRides, container, countBadge);
        }
        if (typeof setCachedMediaRides === 'function' && Array.isArray(currentLoadedRides)) {
          setCachedMediaRides(currentLoadedRides);
        }

        renderRideVideoManagerList();
      } finally {
        resetBtn.disabled = false;
        resetBtn.textContent = 'Reset Auto';
      }
    });
  }

  if (closeBtn) closeBtn.addEventListener('click', closeRideVideoManagerModal);
  if (cancelBtn) cancelBtn.addEventListener('click', closeRideVideoManagerModal);
  if (backdrop) backdrop.addEventListener('click', closeRideVideoManagerModal);

  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('active')) {
      closeRideVideoManagerModal();
    }
  });
}

// ============================================================================
// 10. Admin Ride Conversion & Photo Album Management Modals
// ============================================================================
let activeConvertRide = null;
let currentLookupResult = null;
let activeAlbumRide = null;
let activeConvertMode = 'date'; // 'date' | 'rideId'

export function syncConvertModalModeUI() {
  if (typeof document === 'undefined') return;
  const tabDate = document.getElementById('admin-convert-tab-date');
  const tabRide = document.getElementById('admin-convert-tab-ride');
  const secDate = document.getElementById('admin-convert-section-date');
  const secRide = document.getElementById('admin-convert-section-ride');
  const submitBtn = document.getElementById('admin-convert-submit-btn');

  const isAlreadyRideId = Boolean(activeConvertRide && activeConvertRide.rideId);
  const isAlreadyPromotedDate = Boolean(activeConvertRide && activeConvertRide.isPromotedToMediaCenter);

  if (activeConvertMode === 'date') {
    if (tabDate) { tabDate.classList.add('active'); tabDate.setAttribute('aria-selected', 'true'); }
    if (tabRide) { tabRide.classList.remove('active'); tabRide.setAttribute('aria-selected', 'false'); }
    if (secDate) secDate.style.display = 'block';
    if (secRide) secRide.style.display = 'none';
    if (submitBtn) submitBtn.textContent = isAlreadyPromotedDate ? 'Update Date' : 'Promote by Date';
  } else {
    if (tabRide) { tabRide.classList.add('active'); tabRide.setAttribute('aria-selected', 'true'); }
    if (tabDate) { tabDate.classList.remove('active'); tabDate.setAttribute('aria-selected', 'false'); }
    if (secRide) secRide.style.display = 'block';
    if (secDate) secDate.style.display = 'none';
    if (submitBtn) submitBtn.textContent = isAlreadyRideId ? 'Update Ride' : 'Link Ride';
  }
}

export function openAdminConvertRideModal(cardOrRideId) {
  if (typeof document === 'undefined' || !cardOrRideId) return;
  const modal = document.getElementById('admin-convert-ride-modal');
  if (!modal) return;

  const rides = currentLoadedRides || [];
  activeConvertRide = rides.find(r => String(r.id) === String(cardOrRideId) || String(r.rideId) === String(cardOrRideId));
  if (!activeConvertRide) return;

  currentLookupResult = null;

  const titleEl = document.getElementById('admin-convert-card-title');
  const subEl = document.getElementById('admin-convert-card-sub');
  const dateInput = document.getElementById('admin-convert-date-input');
  const catSelect = document.getElementById('admin-convert-cat-select');
  const idInput = document.getElementById('admin-convert-ride-id-input');
  const lookupCard = document.getElementById('admin-convert-lookup-card');
  const errorEl = document.getElementById('admin-convert-error');
  const successEl = document.getElementById('admin-convert-success');
  const revertBtn = document.getElementById('admin-convert-revert-btn');
  const submitBtn = document.getElementById('admin-convert-submit-btn');

  if (titleEl) titleEl.textContent = activeConvertRide.title || 'Untitled Media';
  if (subEl) {
    const formattedDate = formatRideDate(activeConvertRide.dateIso || activeConvertRide.dateStr);
    const typeLabel = activeConvertRide.isBtcnjRide
      ? `BTCNJ Ride #${activeConvertRide.rideId}`
      : (activeConvertRide.isPromotedToMediaCenter ? `Promoted (${activeConvertRide.category || 'EVENT'})` : 'Member Media');
    subEl.textContent = `${formattedDate} \u2022 ${typeLabel}`;
  }

  // Pre-fill inputs with existing card data
  if (dateInput) {
    dateInput.value = activeConvertRide.dateIso || (activeConvertRide.dateStr && String(activeConvertRide.dateStr).match(/\d{4}-\d{2}-\d{2}/)?.[0]) || getTodayLocalIso();
  }
  if (catSelect) {
    catSelect.value = activeConvertRide.category || 'EVENT';
  }
  if (idInput) {
    idInput.value = activeConvertRide.rideId || '';
  }

  const isAlreadyRideId = Boolean(activeConvertRide.isBtcnjRide && activeConvertRide.rideId);
  const isAlreadyPromotedDate = Boolean(activeConvertRide.isPromotedToMediaCenter);
  const isAlreadyPromoted = isAlreadyRideId || isAlreadyPromotedDate;

  // Decide mode tab
  if (isAlreadyRideId) {
    activeConvertMode = 'rideId';
  } else {
    activeConvertMode = 'date';
  }
  syncConvertModalModeUI();

  if (lookupCard) lookupCard.style.display = 'none';
  if (errorEl) { errorEl.style.display = 'none'; errorEl.textContent = ''; }
  if (successEl) { successEl.style.display = 'none'; successEl.textContent = ''; }

  if (revertBtn) {
    revertBtn.style.display = isAlreadyPromoted ? 'inline-block' : 'none';
    revertBtn.disabled = false;
    revertBtn.textContent = 'Revert Media';
  }

  if (submitBtn) {
    submitBtn.disabled = false;
  }

  modal.classList.add('active');
  modal.setAttribute('aria-hidden', 'false');
  document.body.classList.add('modal-open');
}

export function closeAdminConvertRideModal() {
  if (typeof document === 'undefined') return;
  const modal = document.getElementById('admin-convert-ride-modal');
  if (!modal) return;
  modal.classList.remove('active');
  modal.classList.remove('is-fullscreen-active');
  modal.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('modal-open');
  activeConvertRide = null;
  currentLookupResult = null;
}

export function openAdminAlbumModal(cardOrRideId) {
  if (typeof document === 'undefined' || !cardOrRideId) return;
  const modal = document.getElementById('admin-album-modal');
  if (!modal) return;

  const rides = currentLoadedRides || [];
  activeAlbumRide = rides.find(r => String(r.id) === String(cardOrRideId) || String(r.rideId) === String(cardOrRideId));
  if (!activeAlbumRide) return;

  const titleEl = document.getElementById('admin-album-ride-title');
  const subEl = document.getElementById('admin-album-ride-sub');
  const urlInput = document.getElementById('admin-album-url-input');
  const countInput = document.getElementById('admin-album-count-input');
  const coverInput = document.getElementById('admin-album-cover-input');
  const errorEl = document.getElementById('admin-album-error');
  const successEl = document.getElementById('admin-album-success');
  const removeBtn = document.getElementById('admin-album-remove-btn');
  const submitBtn = document.getElementById('admin-album-submit-btn');

  if (titleEl) titleEl.textContent = activeAlbumRide.title || 'Untitled Ride';
  if (subEl) {
    const formattedDate = formatRideDate(activeAlbumRide.dateIso || activeAlbumRide.dateStr);
    const albumStatus = activeAlbumRide.photoAlbumUrl ? `${activeAlbumRide.photoCount || 0} Photos Attached` : 'No Album Attached';
    subEl.textContent = `${formattedDate} \u2022 ${albumStatus}`;
  }

  hydrateRidePhotoMetadata(activeAlbumRide);
  if (urlInput) {
    urlInput.value = activeAlbumRide.photoAlbumUrl || '';
    if (!urlInput._hasAutoFillBound) {
      urlInput._hasAutoFillBound = true;
      urlInput.addEventListener('input', () => {
        const val = urlInput.value.trim();
        const known = KNOWN_ALBUMS_MAP[val];
        if (known) {
          if (countInput && !countInput.value) countInput.value = known.photoCount;
          if (coverInput && !coverInput.value) coverInput.value = known.coverPhotoUrl;
        }
      });
    }
  }
  if (countInput) countInput.value = (activeAlbumRide.photoCount && activeAlbumRide.photoCount > 0) ? activeAlbumRide.photoCount : '';
  if (coverInput) coverInput.value = activeAlbumRide.coverPhotoUrl || '';

  if (errorEl) { errorEl.style.display = 'none'; errorEl.textContent = ''; }
  if (successEl) { successEl.style.display = 'none'; successEl.textContent = ''; }

  if (removeBtn) {
    removeBtn.style.display = activeAlbumRide.photoAlbumUrl ? 'inline-block' : 'none';
    removeBtn.disabled = false;
    removeBtn.textContent = 'Remove Album';
  }

  if (submitBtn) {
    submitBtn.textContent = 'Save Album';
    submitBtn.disabled = false;
  }

  modal.classList.add('active');
  modal.setAttribute('aria-hidden', 'false');
  document.body.classList.add('modal-open');
}

export function closeAdminAlbumModal() {
  if (typeof document === 'undefined') return;
  const modal = document.getElementById('admin-album-modal');
  if (!modal) return;
  modal.classList.remove('active');
  modal.classList.remove('is-fullscreen-active');
  modal.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('modal-open');
  activeAlbumRide = null;
}

export function initAdminConversionAndAlbumModals() {

  const adminAlbumInspectBtn = document.getElementById('admin-album-inspect-owners-btn');
  if (adminAlbumInspectBtn) {
    adminAlbumInspectBtn.addEventListener('click', () => {
      const urlInput = document.getElementById('admin-album-url-input');
      const albumUrl = (urlInput && urlInput.value) ? urlInput.value.trim() : (activeAlbumRide?.photoAlbumUrl || '');
      const rideTitle = activeAlbumRide?.title || '';
      const rawLeaders = activeAlbumRide?.leaders ? normalizeRideLeaders(activeAlbumRide.leaders).join(',') : '';
      closeAdminAlbumModal();
      openOwnerMediaStudioModal({ albumUrl, rideTitle, leaders: rawLeaders, tab: 'gphotos' });
    });
  }

  const cockpitStudioBtn = document.getElementById('btn-open-owner-media-studio');
  if (cockpitStudioBtn) {
    cockpitStudioBtn.addEventListener('click', () => {
      openOwnerMediaStudioModal({ tab: 'gphotos' });
    });
  }

  const cockpitAnnualZipBtn = document.getElementById('btn-admin-annual-zip');
  if (cockpitAnnualZipBtn) {
    cockpitAnnualZipBtn.addEventListener('click', () => {
      openOwnerMediaStudioModal({ tab: 'annual' });
    });
  }

  const adminTabAnnualZipBtn = document.getElementById('admin-tab-annual-zip-btn');
  if (adminTabAnnualZipBtn) {
    adminTabAnnualZipBtn.addEventListener('click', () => {
      openOwnerMediaStudioModal({ tab: 'annual' });
    });
  }

  initOwnerMediaStudio();

  if (typeof document === 'undefined') return;

  // Convert Ride Modal elements
  const convertModal = document.getElementById('admin-convert-ride-modal');
  const convertBackdrop = document.getElementById('admin-convert-backdrop');
  const convertCloseBtn = document.getElementById('admin-convert-close-btn');
  const convertCancelBtn = document.getElementById('admin-convert-cancel-btn');
  const convertForm = document.getElementById('admin-convert-form');
  const convertIdInput = document.getElementById('admin-convert-ride-id-input');
  const convertLookupBtn = document.getElementById('admin-convert-lookup-btn');
  const lookupCard = document.getElementById('admin-convert-lookup-card');
  const lookupTitle = document.getElementById('admin-convert-lookup-title');
  const lookupCat = document.getElementById('admin-convert-lookup-cat');
  const lookupMiles = document.getElementById('admin-convert-lookup-miles');
  const lookupClimb = document.getElementById('admin-convert-lookup-climb');
  const lookupLeaders = document.getElementById('admin-convert-lookup-leaders');
  const lookupRwgps = document.getElementById('admin-convert-lookup-rwgps');
  const convertError = document.getElementById('admin-convert-error');
  const convertSuccess = document.getElementById('admin-convert-success');
  const convertRevertBtn = document.getElementById('admin-convert-revert-btn');
  const convertSubmitBtn = document.getElementById('admin-convert-submit-btn');
  const tabDate = document.getElementById('admin-convert-tab-date');
  const tabRide = document.getElementById('admin-convert-tab-ride');
  const dateInput = document.getElementById('admin-convert-date-input');
  const catSelect = document.getElementById('admin-convert-cat-select');

  // Album Modal elements
  const albumModal = document.getElementById('admin-album-modal');
  const albumBackdrop = document.getElementById('admin-album-backdrop');
  const albumCloseBtn = document.getElementById('admin-album-close-btn');
  const albumCancelBtn = document.getElementById('admin-album-cancel-btn');
  const albumForm = document.getElementById('admin-album-form');
  const albumUrlInput = document.getElementById('admin-album-url-input');
  const albumCountInput = document.getElementById('admin-album-count-input');
  const albumCoverInput = document.getElementById('admin-album-cover-input');
  const albumError = document.getElementById('admin-album-error');
  const albumSuccess = document.getElementById('admin-album-success');
  const albumRemoveBtn = document.getElementById('admin-album-remove-btn');
  const albumSubmitBtn = document.getElementById('admin-album-submit-btn');

  // Document delegation for open buttons
  document.addEventListener('click', (e) => {
    const convertBtn = e.target.closest('[data-action="convert-ride"]');
    if (convertBtn) {
      e.preventDefault();
      e.stopPropagation();
      const cardId = convertBtn.getAttribute('data-card-id') || convertBtn.getAttribute('data-ride-id');
      openAdminConvertRideModal(cardId);
      return;
    }

    const albumBtn = e.target.closest('[data-action="manage-album"]');
    if (albumBtn) {
      e.preventDefault();
      e.stopPropagation();
      const cardId = albumBtn.getAttribute('data-card-id') || albumBtn.getAttribute('data-ride-id');
      openAdminAlbumModal(cardId);
      return;
    }
  });

  // Mode tab switching
  if (tabDate) {
    tabDate.addEventListener('click', () => {
      activeConvertMode = 'date';
      syncConvertModalModeUI();
      if (convertError) { convertError.style.display = 'none'; convertError.textContent = ''; }
    });
  }
  if (tabRide) {
    tabRide.addEventListener('click', () => {
      activeConvertMode = 'rideId';
      syncConvertModalModeUI();
      if (convertError) { convertError.style.display = 'none'; convertError.textContent = ''; }
    });
  }

  // Lookup button click
  if (convertLookupBtn && convertIdInput) {
    convertLookupBtn.addEventListener('click', async () => {
      const rideId = convertIdInput.value.trim();
      if (!rideId) {
        if (convertError) {
          convertError.textContent = 'Enter BTCNJ Ride ID';
          convertError.style.display = 'block';
        }
        return;
      }

      convertLookupBtn.disabled = true;
      convertLookupBtn.textContent = 'Looking up...';
      if (convertError) { convertError.style.display = 'none'; convertError.textContent = ''; }

      try {
        const result = await lookupBtcnjRide(rideId);
        if (!result) {
          if (convertError) {
            convertError.textContent = 'Ride Not Found';
            convertError.style.display = 'block';
          }
          if (lookupCard) lookupCard.style.display = 'none';
        } else {
          currentLookupResult = result;
          if (lookupTitle) lookupTitle.textContent = result.title;
          if (lookupCat) lookupCat.textContent = result.category || 'BTCNJ';
          if (lookupMiles) lookupMiles.textContent = result.miles ? `${result.miles} mi` : '-- mi';
          if (lookupClimb) lookupClimb.textContent = result.climbFt ? `${Math.round(result.climbFt).toLocaleString()} ft` : '-- ft';
          if (lookupLeaders) lookupLeaders.textContent = `Leaders: ${result.leaders.join(', ') || 'None listed'}`;
          if (lookupRwgps) lookupRwgps.textContent = `RWGPS: ${result.rwgpsUrl ? 'Route Linked' : 'None'}`;
          if (lookupCard) lookupCard.style.display = 'flex';
        }
      } finally {
        convertLookupBtn.disabled = false;
        convertLookupBtn.textContent = 'Lookup';
      }
    });
  }

  // Convert form submit
  if (convertForm) {
    convertForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!activeConvertRide) return;

      // Mode 1: Promote by Date
      if (activeConvertMode === 'date') {
        const dateVal = dateInput?.value.trim();
        if (!dateVal) {
          if (convertError) {
            convertError.textContent = 'Enter Event Date';
            convertError.style.display = 'block';
          }
          return;
        }

        const catVal = catSelect?.value.trim() || 'EVENT';

        if (convertSubmitBtn) {
          convertSubmitBtn.disabled = true;
          convertSubmitBtn.textContent = 'Saving...';
        }

        try {
          const cardKey = String(activeConvertRide.id);
          const overrideData = {
            isPromotedToMediaCenter: true,
            assignedDate: dateVal,
            assignedCategory: catVal,
            assignedRideId: null,
            revertToOther: false
          };

          await saveMediaOverride(cardKey, overrideData);

          // Update in-memory ride object
          activeConvertRide.isPromotedToMediaCenter = true;
          activeConvertRide.dateIso = dateVal;
          activeConvertRide.dateStr = dateVal;
          activeConvertRide.category = catVal;
          activeConvertRide.rideId = null;
          activeConvertRide.isBtcnjRide = false;
          activeConvertRide.btcnjUrl = null;

          const container = document.getElementById('rides-container');
          const countBadge = document.getElementById('rides-count-badge');
          if (container) {
            renderRides(currentLoadedRides, container, countBadge);
          }
          if (typeof setCachedMediaRides === 'function' && Array.isArray(currentLoadedRides)) {
            setCachedMediaRides(currentLoadedRides);
          }

          showGlobalToast(`Promoted to Media Center (${formatRideDate(dateVal)})`);
          closeAdminConvertRideModal();
        } catch (err) {
          if (convertError) {
            convertError.textContent = 'Promotion Failed';
            convertError.style.display = 'block';
          }
        } finally {
          if (convertSubmitBtn) {
            convertSubmitBtn.disabled = false;
            syncConvertModalModeUI();
          }
        }
        return;
      }

      // Mode 2: Link to BTCNJ Ride ID
      const rideId = convertIdInput?.value.trim();
      if (!rideId) {
        if (convertError) {
          convertError.textContent = 'Enter BTCNJ Ride ID';
          convertError.style.display = 'block';
        }
        return;
      }

      if (convertSubmitBtn) {
        convertSubmitBtn.disabled = true;
        convertSubmitBtn.textContent = 'Saving...';
      }

      try {
        // If user didn't hit lookup, try looking up now
        if (!currentLookupResult || String(currentLookupResult.rideId) !== String(rideId)) {
          const lookedUp = await lookupBtcnjRide(rideId);
          if (lookedUp) currentLookupResult = lookedUp;
        }

        const cardKey = String(activeConvertRide.id);
        const overrideData = {
          assignedRideId: rideId,
          isPromotedToMediaCenter: false,
          assignedDate: null,
          assignedCategory: null,
          revertToOther: false,
          telemetry: currentLookupResult || {
            title: activeConvertRide.title,
            category: activeConvertRide.category,
            miles: activeConvertRide.miles,
            climbFt: activeConvertRide.climbFt,
            leaders: activeConvertRide.leaders,
            members: activeConvertRide.members || [],
            rwgpsUrl: activeConvertRide.rwgpsUrl
          }
        };

        await saveMediaOverride(cardKey, overrideData);

        // Update in-memory ride object
        activeConvertRide.rideId = rideId;
        activeConvertRide.isBtcnjRide = true;
        activeConvertRide.isPromotedToMediaCenter = false;
        activeConvertRide.btcnjUrl = `https://btcnj.com/pages/rideSignupEdit?rideid=${encodeURIComponent(rideId)}`;
        activeConvertRide.r5mUrl = buildR5mRideUrl({ ...activeConvertRide, rideId }, CONFIG.R5M_BASE_URL);

        if (currentLookupResult) {
          if (currentLookupResult.title) activeConvertRide.title = currentLookupResult.title;
          if (currentLookupResult.category) activeConvertRide.category = currentLookupResult.category;
          if (currentLookupResult.miles !== null && currentLookupResult.miles !== undefined) activeConvertRide.miles = currentLookupResult.miles;
          if (currentLookupResult.climbFt !== null && currentLookupResult.climbFt !== undefined) activeConvertRide.climbFt = currentLookupResult.climbFt;
          if (currentLookupResult.leaders?.length > 0) activeConvertRide.leaders = currentLookupResult.leaders;
          if (currentLookupResult.members?.length > 0) activeConvertRide.members = currentLookupResult.members;
          if (currentLookupResult.rwgpsUrl) activeConvertRide.rwgpsUrl = currentLookupResult.rwgpsUrl;
          if (currentLookupResult.photoAlbumUrl && !activeConvertRide.photoAlbumUrl) {
            activeConvertRide.photoAlbumUrl = currentLookupResult.photoAlbumUrl;
            if (currentLookupResult.photoCount) activeConvertRide.photoCount = currentLookupResult.photoCount;
            if (currentLookupResult.coverPhotoUrl) activeConvertRide.coverPhotoUrl = currentLookupResult.coverPhotoUrl;
          }
          hydrateRidePhotoMetadata(activeConvertRide);
        }

        const container = document.getElementById('rides-container');
        const countBadge = document.getElementById('rides-count-badge');
        if (container) {
          renderRides(currentLoadedRides, container, countBadge);
        }
        if (typeof setCachedMediaRides === 'function' && Array.isArray(currentLoadedRides)) {
          setCachedMediaRides(currentLoadedRides);
        }

        showGlobalToast(`Linked to BTCNJ Ride #${rideId}`);
        closeAdminConvertRideModal();
      } catch (err) {
        if (convertError) {
          convertError.textContent = 'Conversion Failed';
          convertError.style.display = 'block';
        }
      } finally {
        if (convertSubmitBtn) {
          convertSubmitBtn.disabled = false;
          syncConvertModalModeUI();
        }
      }
    });
  }

  // Convert revert button
  if (convertRevertBtn) {
    convertRevertBtn.addEventListener('click', async () => {
      if (!activeConvertRide) return;
      convertRevertBtn.disabled = true;
      convertRevertBtn.textContent = 'Reverting...';

      try {
        const cardKey = String(activeConvertRide.id);
        const overrideData = {
          assignedRideId: '',
          assignedDate: null,
          assignedCategory: null,
          isPromotedToMediaCenter: false,
          revertToOther: true
        };

        await saveMediaOverride(cardKey, overrideData);

        activeConvertRide.rideId = null;
        activeConvertRide.isBtcnjRide = false;
        activeConvertRide.isPromotedToMediaCenter = false;
        activeConvertRide.btcnjUrl = null;
        if (activeConvertRide.originalDateIso) {
          activeConvertRide.dateIso = activeConvertRide.originalDateIso;
          activeConvertRide.dateStr = activeConvertRide.originalDateIso;
        }

        const container = document.getElementById('rides-container');
        const countBadge = document.getElementById('rides-count-badge');
        if (container) {
          renderRides(currentLoadedRides, container, countBadge);
        }
        if (typeof setCachedMediaRides === 'function' && Array.isArray(currentLoadedRides)) {
          setCachedMediaRides(currentLoadedRides);
        }

        showGlobalToast('Reverted to Member Media');
        closeAdminConvertRideModal();
      } catch (err) {
        if (convertError) {
          convertError.textContent = 'Revert Failed';
          convertError.style.display = 'block';
        }
      } finally {
        convertRevertBtn.disabled = false;
        convertRevertBtn.textContent = 'Revert Media';
      }
    });
  }

  // Convert modal close & escape
  if (convertCloseBtn) convertCloseBtn.addEventListener('click', closeAdminConvertRideModal);
  if (convertCancelBtn) convertCancelBtn.addEventListener('click', closeAdminConvertRideModal);
  if (convertBackdrop) convertBackdrop.addEventListener('click', closeAdminConvertRideModal);

  // Album form submit
  if (albumForm) {
    albumForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!activeAlbumRide) return;
      const urlVal = albumUrlInput?.value.trim();
      if (!urlVal) {
        if (albumError) {
          albumError.textContent = 'Enter Photo Album URL';
          albumError.style.display = 'block';
        }
        return;
      }

      if (albumSubmitBtn) {
        albumSubmitBtn.disabled = true;
        albumSubmitBtn.textContent = 'Saving...';
      }

      try {
        let countVal = albumCountInput?.value ? parseInt(albumCountInput.value, 10) : 0;
        let coverVal = albumCoverInput?.value.trim() || '';
        const known = KNOWN_ALBUMS_MAP[urlVal];
        if (known) {
          if (countVal <= 0) countVal = known.photoCount;
          if (!coverVal) coverVal = known.coverPhotoUrl;
        }

        const cardKey = String(activeAlbumRide.id);
        const overrideData = {
          photoAlbumUrl: urlVal,
          photoCount: countVal,
          coverPhotoUrl: coverVal,
          hidePhotoAlbum: false
        };

        await saveMediaOverride(cardKey, overrideData);

        activeAlbumRide.photoAlbumUrl = urlVal;
        activeAlbumRide.photoCount = countVal;
        if (coverVal) activeAlbumRide.coverPhotoUrl = coverVal;

        const container = document.getElementById('rides-container');
        const countBadge = document.getElementById('rides-count-badge');
        if (container) {
          renderRides(currentLoadedRides, container, countBadge);
        }
        if (typeof setCachedMediaRides === 'function' && Array.isArray(currentLoadedRides)) {
          setCachedMediaRides(currentLoadedRides);
        }

        closeAdminAlbumModal();
      } catch (err) {
        if (albumError) {
          albumError.textContent = 'Save Failed';
          albumError.style.display = 'block';
        }
      } finally {
        if (albumSubmitBtn) {
          albumSubmitBtn.disabled = false;
          albumSubmitBtn.textContent = 'Save Album';
        }
      }
    });
  }

  // Album remove button
  if (albumRemoveBtn) {
    albumRemoveBtn.addEventListener('click', async () => {
      if (!activeAlbumRide) return;
      albumRemoveBtn.disabled = true;
      albumRemoveBtn.textContent = 'Removing...';

      try {
        const cardKey = String(activeAlbumRide.id);
        const overrideData = {
          photoAlbumUrl: '',
          photoCount: 0,
          hidePhotoAlbum: true
        };

        await saveMediaOverride(cardKey, overrideData);

        activeAlbumRide.photoAlbumUrl = null;
        activeAlbumRide.photoCount = 0;

        const container = document.getElementById('rides-container');
        const countBadge = document.getElementById('rides-count-badge');
        if (container) {
          renderRides(currentLoadedRides, container, countBadge);
        }
        if (typeof setCachedMediaRides === 'function' && Array.isArray(currentLoadedRides)) {
          setCachedMediaRides(currentLoadedRides);
        }

        closeAdminAlbumModal();
      } catch (err) {
        if (albumError) {
          albumError.textContent = 'Removal Failed';
          albumError.style.display = 'block';
        }
      } finally {
        albumRemoveBtn.disabled = false;
        albumRemoveBtn.textContent = 'Remove Album';
      }
    });
  }

  // Album modal close & escape
  if (albumCloseBtn) albumCloseBtn.addEventListener('click', closeAdminAlbumModal);
  if (albumCancelBtn) albumCancelBtn.addEventListener('click', closeAdminAlbumModal);
  if (albumBackdrop) albumBackdrop.addEventListener('click', closeAdminAlbumModal);

  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (convertModal && convertModal.classList.contains('active')) {
        closeAdminConvertRideModal();
      }
      if (albumModal && albumModal.classList.contains('active')) {
        closeAdminAlbumModal();
      }
    }
  });
}




// ============================================================================
// 6.5 YouTube Playlist Registry Modal Management
// ============================================================================
export function initPlaylistManagerModal() {
  if (typeof document === 'undefined') return;

  const modal = document.getElementById('youtube-playlists-modal');
  const triggerBtn = document.getElementById('admin-tab-playlists-btn');
  const closeBtn = document.getElementById('youtube-playlists-close-btn');
  const dismissBtn = document.getElementById('youtube-playlists-dismiss-btn');
  const backdrop = document.getElementById('youtube-playlists-backdrop');
  const listEl = document.getElementById('youtube-playlists-list');
  const addForm = document.getElementById('youtube-playlist-add-form');
  const urlInput = document.getElementById('youtube-playlist-url-input');
  const titleInput = document.getElementById('youtube-playlist-title-input');
  const addBtn = document.getElementById('youtube-playlist-add-btn');
  const syncBtn = document.getElementById('youtube-playlists-sync-btn');
  const errorMsg = document.getElementById('youtube-playlist-add-error');
  const successMsg = document.getElementById('youtube-playlist-add-success');

  if (!modal) return;

  function openPlaylistModal() {
    modal.classList.add('active');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('modal-open');
    renderPlaylistsList();
    if (errorMsg) errorMsg.style.display = 'none';
    if (successMsg) successMsg.style.display = 'none';
  }

  function closePlaylistModal() {
    modal.classList.remove('active');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('modal-open');
  }

  function renderPlaylistsList() {
    if (!listEl) return;
    const playlists = getYoutubePlaylists();
    listEl.innerHTML = '';

    playlists.forEach(pl => {
      const row = document.createElement('div');
      row.className = 'playlist-item-row';
      row.setAttribute('role', 'listitem');

      const isDefault = Boolean(pl.isDefault || pl.id === DEFAULT_PLAYLISTS[0].id);

      row.innerHTML = `
        <div class="playlist-item-left">
          <span style="font-size: 1.1rem;" aria-hidden="true">▶️</span>
          <div class="playlist-item-info">
            <div class="playlist-item-title">${escapeHtml(pl.title || 'YouTube Playlist')}</div>
            <div class="playlist-item-sub">${escapeHtml(pl.id)}</div>
          </div>
        </div>
        <div style="display: flex; align-items: center; gap: 8px;">
          ${isDefault ? '<span class="playlist-primary-tag">Primary Source</span>' : ''}
          ${!isDefault ? `
            <button type="button" class="btn-playlist-remove" data-action="remove-playlist" data-playlist-id="${escapeHtml(pl.id)}" title="Remove playlist">
              ✕
            </button>
          ` : ''}
        </div>
      `;
      listEl.appendChild(row);
    });
  }

  // Add playlist form
  if (addForm) {
    addForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (errorMsg) errorMsg.style.display = 'none';
      if (successMsg) successMsg.style.display = 'none';

      const rawInput = urlInput?.value.trim() || '';
      if (!rawInput) return;

      let playlistId = null;
      const listMatch = rawInput.match(/[?&]list=([a-zA-Z0-9_-]+)/);
      if (listMatch) {
        playlistId = listMatch[1];
      } else if (/^[a-zA-Z0-9_-]{10,}$/.test(rawInput)) {
        playlistId = rawInput;
      }

      if (!playlistId) {
        if (errorMsg) {
          errorMsg.textContent = 'Invalid YouTube playlist URL or ID';
          errorMsg.style.display = 'block';
        }
        return;
      }

      const existing = getYoutubePlaylists();
      if (existing.some(p => p.id === playlistId)) {
        if (errorMsg) {
          errorMsg.textContent = 'Playlist already registered';
          errorMsg.style.display = 'block';
        }
        return;
      }

      if (addBtn) {
        addBtn.disabled = true;
        addBtn.textContent = 'Adding...';
      }

      try {
        const titleVal = titleInput?.value.trim() || 'Community YouTube Playlist';
        const newPl = {
          id: playlistId,
          title: titleVal,
          url: `https://youtube.com/playlist?list=${playlistId}`,
          isDefault: false,
          createdAt: new Date().toISOString()
        };

        await saveCloudPlaylist(newPl);
        renderPlaylistsList();
        if (urlInput) urlInput.value = '';
        if (titleInput) titleInput.value = '';

        if (successMsg) {
          successMsg.textContent = 'Playlist Registered';
          successMsg.style.display = 'block';
          setTimeout(() => { successMsg.style.display = 'none'; }, 3000);
        }

        fetchAndRenderRides(true);
      } catch (err) {
        if (errorMsg) {
          errorMsg.textContent = 'Failed to register playlist';
          errorMsg.style.display = 'block';
        }
      } finally {
        if (addBtn) {
          addBtn.disabled = false;
          addBtn.textContent = 'Add Playlist';
        }
      }
    });
  }

  // Remove playlist delegation
  if (listEl) {
    listEl.addEventListener('click', async (e) => {
      const removeBtn = e.target.closest('[data-action="remove-playlist"]');
      if (removeBtn) {
        const plId = removeBtn.getAttribute('data-playlist-id');
        if (!plId) return;

        if (!confirm('Remove this YouTube playlist source?')) return;

        await removeCloudPlaylist(plId);
        renderPlaylistsList();
        fetchAndRenderRides(true);
      }
    });
  }

  // Sync sources button
  if (syncBtn) {
    syncBtn.addEventListener('click', async () => {
      syncBtn.disabled = true;
      syncBtn.textContent = 'Syncing...';
      try {
        await Promise.allSettled([fetchCloudYoutubePlaylists(), fetchCloudMemberMediaExclusions()]);
        await fetchAndRenderRides(true);
        renderPlaylistsList();
        if (successMsg) {
          successMsg.textContent = 'Playlists Synced';
          successMsg.style.display = 'block';
          setTimeout(() => { successMsg.style.display = 'none'; }, 3000);
        }
      } finally {
        syncBtn.disabled = false;
        syncBtn.textContent = 'Sync Sources';
      }
    });
  }

  if (triggerBtn) triggerBtn.addEventListener('click', openPlaylistModal);
  if (closeBtn) closeBtn.addEventListener('click', closePlaylistModal);
  if (dismissBtn) dismissBtn.addEventListener('click', closePlaylistModal);
  if (backdrop) backdrop.addEventListener('click', closePlaylistModal);

  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('active')) {
      closePlaylistModal();
    }
  });
}


// ============================================================================
// OWNER MEDIA STUDIO (Google Photos Shared Album Extractor & Bulk Tagger)
// Protocol Z-10 / M-10 Compliant
// ============================================================================

export const ownerStudioGphotosState = {
  albumUrl: '',
  albumTitle: '',
  rideTitle: '',
  items: [],
  contributors: [],
  activeContributor: 'all'
};

export const ownerStudioLocalState = {
  contributor: '',
  files: []
};

export const ownerStudioAnnualState = {
  year: '',
  availableYears: [],
  categories: [],
  selectedCategories: new Set(),
  isScanning: false,
  isDownloading: false
};

export function switchOwnerStudioTab(tabName) {
  if (typeof document === 'undefined') return;
  const isAdmin = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('btcnjcc_admin_authed') === 'true';
  const effectiveTab = (tabName === 'annual' && !isAdmin) ? 'gphotos' : tabName;

  const isGphotos = effectiveTab === 'gphotos';
  const isLocal = effectiveTab === 'local';
  const isAnnual = effectiveTab === 'annual';

  const tabGphotos = document.getElementById('owner-studio-tab-gphotos');
  const tabLocal = document.getElementById('owner-studio-tab-local');
  const tabAnnual = document.getElementById('owner-studio-tab-annual');
  const panelGphotos = document.getElementById('owner-studio-panel-gphotos');
  const panelLocal = document.getElementById('owner-studio-panel-local');
  const panelAnnual = document.getElementById('owner-studio-panel-annual');

  if (tabGphotos) {
    tabGphotos.classList.toggle('active', isGphotos);
    tabGphotos.setAttribute('aria-selected', String(isGphotos));
  }
  if (tabLocal) {
    tabLocal.classList.toggle('active', isLocal);
    tabLocal.setAttribute('aria-selected', String(isLocal));
  }
  if (tabAnnual) {
    tabAnnual.style.display = isAdmin ? '' : 'none';
    tabAnnual.classList.toggle('active', isAnnual);
    tabAnnual.setAttribute('aria-selected', String(isAnnual));
  }
  if (panelGphotos) panelGphotos.style.display = isGphotos ? 'flex' : 'none';
  if (panelLocal) panelLocal.style.display = isLocal ? 'flex' : 'none';
  if (panelAnnual) panelAnnual.style.display = isAnnual ? 'flex' : 'none';
}

export function openOwnerMediaStudioModal(options = {}) {
  if (typeof document === 'undefined') return;
  const modal = document.getElementById('owner-media-studio-modal');
  if (!modal) return;

  const isAdmin = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('btcnjcc_admin_authed') === 'true';
  const tabAnnual = document.getElementById('owner-studio-tab-annual');
  if (tabAnnual) {
    tabAnnual.style.display = isAdmin ? '' : 'none';
  }

  const urlInput = document.getElementById('owner-studio-album-url-input');
  const contributorInput = document.getElementById('owner-studio-contributor-input');
  const rideBar = document.getElementById('owner-studio-gphotos-ride-bar');
  const leaderChipsContainer = document.getElementById('owner-studio-leader-chips');

  // Pre-fill Google Photos album URL
  if (options.albumUrl && urlInput) {
    urlInput.value = options.albumUrl;
  }

  // Pre-fill / restore contributor name from localStorage
  if (contributorInput) {
    try {
      const stored = localStorage.getItem('btcnjcc_contributor_profile_v1');
      if (stored && !contributorInput.value) {
        contributorInput.value = stored;
      }
    } catch (_) {}
  }

  // Ride context banner
  if (rideBar) {
    if (options.rideTitle || options.albumUrl) {
      rideBar.style.display = 'flex';
      rideBar.innerHTML = `
        <div><strong>Ride:</strong> ${escapeHtml(options.rideTitle || 'Selected Ride')}</div>
        <div style="color: #94a3b8; font-size: 0.76rem;">${escapeHtml(options.albumUrl || '')}</div>
      `;
    } else {
      rideBar.style.display = 'none';
      rideBar.innerHTML = '';
    }
  }

  // Leader chips for fast tagging
  if (leaderChipsContainer) {
    leaderChipsContainer.innerHTML = '';
    const rawLeaders = options.leaders || '';
    const leadersList = (Array.isArray(rawLeaders) ? rawLeaders : String(rawLeaders).split(','))
      .map(s => s.trim())
      .filter(Boolean);

    if (leadersList.length > 0) {
      leadersList.forEach(ldr => {
        const chip = document.createElement('span');
        chip.className = 'owner-leader-chip';
        chip.textContent = `👤 ${ldr}`;
        chip.setAttribute('data-name', ldr);
        chip.addEventListener('click', () => {
          if (contributorInput) {
            contributorInput.value = ldr;
            try { localStorage.setItem('btcnjcc_contributor_profile_v1', ldr); } catch (_) {}
            retagLocalOwnerQueue();
          }
        });
        leaderChipsContainer.appendChild(chip);
      });
    } else {
      leaderChipsContainer.innerHTML = '<span class="owner-leader-placeholder">Open from any ride card to view ride leader quick-select chips.</span>';
    }
  }

  // Tab switching
  const requestedTab = (options.tab === 'annual' && isAdmin) ? 'annual' : (options.tab || 'gphotos');
  switchOwnerStudioTab(requestedTab);

  if (requestedTab === 'annual') {
    scanAnnualMediaCenterYear(options.year);
  }

  // Open modal
  modal.classList.add('active');
  modal.setAttribute('aria-hidden', 'false');
  document.body.classList.add('modal-open');

  // Auto-inspect if album URL provided
  if (options.albumUrl && requestedTab === 'gphotos') {
    inspectGooglePhotosAlbumOwners(options.albumUrl, options);
  }
}

export function closeOwnerMediaStudioModal() {
  if (typeof document === 'undefined') return;
  const modal = document.getElementById('owner-media-studio-modal');
  if (!modal) return;
  modal.classList.remove('active');
  modal.classList.remove('is-fullscreen-active');
  modal.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('modal-open');
}

export function renderGphotosQueue(items, activeContributor = 'all') {
  if (typeof document === 'undefined') return;
  const queueEl = document.getElementById('owner-studio-gphotos-queue');
  const countEl = document.getElementById('owner-studio-gphotos-count');
  if (!queueEl) return;

  const filtered = activeContributor === 'all'
    ? items
    : items.filter(it => it.contributorName === activeContributor);

  if (countEl) {
    countEl.textContent = `${filtered.length} ${filtered.length === 1 ? 'Item' : 'Items'} Found`;
  }

  if (filtered.length === 0) {
    queueEl.innerHTML = '<div class="owner-queue-empty">No media items found for the selected contributor filter.</div>';
    return;
  }

  queueEl.innerHTML = filtered.map(it => {
    const filenameDisplay = escapeHtml(it.suggestedFilename || 'media.jpg');
    const contributorDisplay = escapeHtml(it.contributorName || 'Contributor');
    const typeLabel = it.isVideo ? 'VIDEO' : 'PHOTO';
    return `
      <div class="owner-queue-item" data-media-id="${escapeHtml(it.mediaId)}">
        <img class="owner-queue-thumb" src="${escapeHtml(it.thumbUrl)}" alt="${filenameDisplay}" loading="lazy" referrerpolicy="no-referrer" onerror="this.onerror=null;this.src='/cycling-blueprint.png';">
        <div class="owner-queue-details">
          <div class="owner-queue-name" title="${filenameDisplay}">${filenameDisplay}</div>
          <div class="owner-queue-meta">
            <span class="owner-queue-tag">${typeLabel}</span>
            <span>👤 ${contributorDisplay}</span>
            ${it.width && it.height ? `<span>${it.width}&times;${it.height}</span>` : ''}
          </div>
        </div>
      </div>
    `;
  }).join('');
}

export async function inspectGooglePhotosAlbumOwners(albumUrl, rideContext = {}) {
  if (typeof document === 'undefined') return;
  const statusEl = document.getElementById('owner-studio-gphotos-status');
  const copyBtn = document.getElementById('owner-studio-copy-manifest-btn');
  const downloadBtn = document.getElementById('owner-studio-download-gphotos-btn');
  const chipsContainer = document.getElementById('owner-studio-contributor-chips');
  const fetchBtn = document.getElementById('owner-studio-fetch-btn');

  const cleanUrl = String(albumUrl || '').trim();
  if (!cleanUrl) {
    if (statusEl) {
      statusEl.className = 'status-msg status-error';
      statusEl.textContent = 'Please enter a valid Google Photos shared album URL';
      statusEl.style.display = 'block';
    }
    return;
  }

  if (fetchBtn) {
    fetchBtn.disabled = true;
    fetchBtn.textContent = 'Inspecting...';
  }
  if (statusEl) {
    statusEl.className = 'status-msg';
    statusEl.textContent = 'Inspecting album contributors and media metadata...';
    statusEl.style.display = 'block';
  }

  try {
    const endpoint = `${CONFIG.SYNC_SINGLE_MEDIA_API_URL}?photoAlbumUrl=${encodeURIComponent(cleanUrl)}&includeManifest=true`;
    const res = await fetch(endpoint, {
      signal: AbortSignal.timeout(12000)
    });
    const data = await res.json();

    if (!res.ok || !data.success) {
      throw new Error(data.error || `Inspection failed with status ${res.status}`);
    }

    ownerStudioGphotosState.albumUrl = cleanUrl;
    ownerStudioGphotosState.albumTitle = data.albumTitle || 'Google Photos Album';
    ownerStudioGphotosState.rideTitle = rideContext.rideTitle || '';
    ownerStudioGphotosState.items = Array.isArray(data.items) ? data.items : [];
    ownerStudioGphotosState.contributors = Array.isArray(data.contributors) ? data.contributors : [];
    ownerStudioGphotosState.activeContributor = 'all';

    // Render Contributor Filter Chips
    if (chipsContainer) {
      chipsContainer.innerHTML = '';
      const allChip = document.createElement('span');
      allChip.className = 'owner-chip active';
      allChip.setAttribute('data-contributor', 'all');
      allChip.textContent = `All Contributors (${ownerStudioGphotosState.items.length})`;
      allChip.addEventListener('click', () => {
        chipsContainer.querySelectorAll('.owner-chip').forEach(c => c.classList.remove('active'));
        allChip.classList.add('active');
        ownerStudioGphotosState.activeContributor = 'all';
        renderGphotosQueue(ownerStudioGphotosState.items, 'all');
      });
      chipsContainer.appendChild(allChip);

      ownerStudioGphotosState.contributors.forEach(c => {
        const chip = document.createElement('span');
        chip.className = 'owner-chip';
        chip.setAttribute('data-contributor', c.name);
        chip.textContent = `${c.name} (${c.count})`;
        chip.addEventListener('click', () => {
          chipsContainer.querySelectorAll('.owner-chip').forEach(el => el.classList.remove('active'));
          chip.classList.add('active');
          ownerStudioGphotosState.activeContributor = c.name;
          renderGphotosQueue(ownerStudioGphotosState.items, c.name);
        });
        chipsContainer.appendChild(chip);
      });
    }

    // Render Queue Items
    renderGphotosQueue(ownerStudioGphotosState.items, 'all');

    if (copyBtn) copyBtn.disabled = ownerStudioGphotosState.items.length === 0;
    if (downloadBtn) downloadBtn.disabled = ownerStudioGphotosState.items.length === 0;

    if (statusEl) {
      statusEl.className = 'status-msg status-success';
      statusEl.textContent = `Extracted ${ownerStudioGphotosState.items.length} media items across ${ownerStudioGphotosState.contributors.length} contributors.`;
      statusEl.style.display = 'block';
    }
  } catch (err) {
    if (statusEl) {
      statusEl.className = 'status-msg status-error';
      statusEl.textContent = `Failed to inspect album: ${err.message}`;
      statusEl.style.display = 'block';
    }
  } finally {
    if (fetchBtn) {
      fetchBtn.disabled = false;
      fetchBtn.textContent = 'Inspect Album';
    }
  }
}

export function retagLocalOwnerQueue() {
  if (typeof document === 'undefined') return;
  const contributorInput = document.getElementById('owner-studio-contributor-input');
  const contributor = contributorInput ? contributorInput.value.trim() : 'Contributor';
  
  ownerStudioLocalState.files = ownerStudioLocalState.files.map(item => {
    return createOwnerTaggedFile(item.file, contributor);
  });
  renderLocalOwnerQueue();
}

export function handleLocalOwnerFilesSelected(fileList) {
  if (!fileList || fileList.length === 0) return;
  const contributorInput = document.getElementById('owner-studio-contributor-input');
  const contributor = contributorInput ? contributorInput.value.trim() : 'Contributor';

  if (contributor && typeof localStorage !== 'undefined') {
    try { localStorage.setItem('btcnjcc_contributor_profile_v1', contributor); } catch (_) {}
  }

  const newItems = Array.from(fileList).map(f => createOwnerTaggedFile(f, contributor));
  ownerStudioLocalState.files.push(...newItems);
  renderLocalOwnerQueue();
}

export function renderLocalOwnerQueue() {
  if (typeof document === 'undefined') return;
  const queueEl = document.getElementById('owner-studio-local-queue');
  const countEl = document.getElementById('owner-studio-local-count');
  const clearBtn = document.getElementById('owner-studio-clear-local-btn');
  const exportBtn = document.getElementById('owner-studio-export-local-btn');
  if (!queueEl) return;

  const files = ownerStudioLocalState.files;
  if (countEl) {
    countEl.textContent = `${files.length} ${files.length === 1 ? 'File' : 'Files'} Queued`;
  }

  if (clearBtn) clearBtn.disabled = files.length === 0;
  if (exportBtn) exportBtn.disabled = files.length === 0;

  if (files.length === 0) {
    queueEl.innerHTML = '<div class="owner-queue-empty">No files queued yet. Drag files into the dropzone above or click to browse.</div>';
    return;
  }

  queueEl.innerHTML = files.map((item, idx) => {
    const filenameDisplay = escapeHtml(item.prefixedFilename);
    const origDisplay = escapeHtml(item.originalFilename);
    const sizeMb = (item.size / (1024 * 1024)).toFixed(2);
    const typeLabel = item.isVideo ? 'VIDEO' : 'PHOTO';
    return `
      <div class="owner-queue-item" data-idx="${idx}">
        <div class="owner-queue-thumb" style="display:flex;align-items:center;justify-content:center;font-size:1.1rem;">
          ${item.isVideo ? '🎥' : '🖼️'}
        </div>
        <div class="owner-queue-details">
          <div class="owner-queue-name" title="${filenameDisplay}">${filenameDisplay}</div>
          <div class="owner-queue-meta">
            <span class="owner-queue-tag">${typeLabel}</span>
            <span>${sizeMb} MB</span>
            <span style="color:#64748b;">(orig: ${origDisplay})</span>
          </div>
        </div>
      </div>
    `;
  }).join('');
}

export async function exportOwnerTaggedBatch(source = 'local') {
  if (typeof document === 'undefined') return;
  const isGphotos = source === 'gphotos';
  const statusEl = document.getElementById(isGphotos ? 'owner-studio-gphotos-status' : 'owner-studio-local-status');
  const actionBtn = document.getElementById(isGphotos ? 'owner-studio-download-gphotos-btn' : 'owner-studio-export-local-btn');

  if (isGphotos) {
    const activeCont = ownerStudioGphotosState.activeContributor;
    const items = activeCont === 'all'
      ? ownerStudioGphotosState.items
      : ownerStudioGphotosState.items.filter(it => it.contributorName === activeCont);

    if (items.length === 0) return;

    const originalBtnText = actionBtn ? actionBtn.textContent : '';
    if (actionBtn) {
      actionBtn.disabled = true;
      actionBtn.textContent = '⏳ Downloading...';
    }

    if (statusEl) {
      statusEl.className = 'status-msg';
      statusEl.textContent = `Preparing download for ${items.length} items...`;
      statusEl.style.display = 'block';
    }

    try {
      const zipEntries = [];
      let completedCount = 0;
      const CONCURRENCY_LIMIT = 4;
      let currentIndex = 0;

      async function worker() {
        while (currentIndex < items.length) {
          const idx = currentIndex++;
          const it = items[idx];
          try {
            const proxyUrl = `${CONFIG.SYNC_SINGLE_MEDIA_API_URL}?proxyDownload=1&mediaUrl=${encodeURIComponent(it.downloadUrl)}&contributor=${encodeURIComponent(it.contributorName)}`;
            const res = await fetch(proxyUrl);
            if (res.ok) {
              let resolvedFilename = '';
              const xPrefixed = res.headers.get('x-prefixed-filename') || res.headers.get('X-Prefixed-Filename');
              if (xPrefixed) {
                try { resolvedFilename = decodeURIComponent(xPrefixed).trim(); } catch (_) {}
              }
              if (!resolvedFilename) {
                resolvedFilename = parseContentDispositionFilename(res.headers.get('content-disposition'));
              }
              if (!resolvedFilename) {
                resolvedFilename = it.suggestedFilename || `media_${idx + 1}.${it.isVideo ? 'mp4' : 'jpg'}`;
              }

              const buffer = await res.arrayBuffer();
              zipEntries.push({
                filename: resolvedFilename,
                data: new Uint8Array(buffer)
              });
            }
          } catch (itemErr) {
            console.warn(`[OwnerMediaStudio] Failed to download item ${idx + 1}:`, itemErr);
          } finally {
            completedCount++;
            if (statusEl) {
              statusEl.textContent = `Downloading (${completedCount}/${items.length}) for ZIP archive...`;
            }
          }
        }
      }

      const workers = [];
      const poolSize = Math.min(CONCURRENCY_LIMIT, items.length);
      for (let i = 0; i < poolSize; i++) {
        workers.push(worker());
      }
      await Promise.all(workers);

      if (zipEntries.length === 0) {
        throw new Error('Failed to download any media items.');
      }

      if (statusEl) {
        statusEl.textContent = `Building ZIP archive (${zipEntries.length} files)...`;
      }

      const rawTitle = ownerStudioGphotosState.rideTitle || ownerStudioGphotosState.albumTitle || 'Google_Photos_Album';
      const cleanTitle = rawTitle.replace(/[<>:"/\\|?*\x00-\x1F]/g, '').trim() || 'Google_Photos_Album';
      const zipFilename = `${cleanTitle} (Tagged).zip`;

      const zipResult = await createZipArchiveBlob(zipEntries);
      const blobUrl = URL.createObjectURL(zipResult.blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = zipFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);

      if (statusEl) {
        statusEl.className = 'status-msg status-success';
        statusEl.textContent = `Successfully downloaded ${zipFilename} (${zipResult.fileCount} files)!`;
      }
    } catch (err) {
      if (statusEl) {
        statusEl.className = 'status-msg status-error';
        statusEl.textContent = `ZIP export failed: ${err.message}`;
      }
    } finally {
      if (actionBtn) {
        actionBtn.disabled = ownerStudioGphotosState.items.length === 0;
        actionBtn.textContent = originalBtnText || '⬇ Download ZIP';
      }
    }
  } else {
    // Local tagged files export
    const files = ownerStudioLocalState.files;
    if (files.length === 0) return;

    const originalBtnText = actionBtn ? actionBtn.textContent : '';
    if (actionBtn) {
      actionBtn.disabled = true;
      actionBtn.textContent = '⏳ Packaging...';
    }

    if (statusEl) {
      statusEl.className = 'status-msg';
      statusEl.textContent = `Packaging ${files.length} tagged files into ZIP...`;
      statusEl.style.display = 'block';
    }

    try {
      const zipEntries = [];
      for (const item of files) {
        const data = new Uint8Array(await item.file.arrayBuffer());
        zipEntries.push({
          filename: item.prefixedFilename,
          data
        });
      }

      const zipResult = await createZipArchiveBlob(zipEntries);
      const blobUrl = URL.createObjectURL(zipResult.blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = 'Tagged_Media_Batch.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);

      if (statusEl) {
        statusEl.className = 'status-msg status-success';
        statusEl.textContent = `Successfully downloaded Tagged_Media_Batch.zip (${zipResult.fileCount} files)!`;
      }
    } catch (err) {
      if (statusEl) {
        statusEl.className = 'status-msg status-error';
        statusEl.textContent = `ZIP packaging failed: ${err.message}`;
      }
    } finally {
      if (actionBtn) {
        actionBtn.disabled = ownerStudioLocalState.files.length === 0;
        actionBtn.textContent = originalBtnText || '⬇ Export ZIP';
      }
    }
  }
}

export function renderAnnualCategoriesGrid() {
  if (typeof document === 'undefined') return;
  const gridEl = document.getElementById('owner-studio-annual-categories-grid');
  const countEl = document.getElementById('owner-studio-annual-count');
  if (!gridEl) return;

  const cats = ownerStudioAnnualState.categories;
  if (countEl) {
    countEl.textContent = `${cats.length} Category Subfolders (${ownerStudioAnnualState.year})`;
  }

  if (cats.length === 0) {
    gridEl.innerHTML = `<div class="owner-queue-empty">No shared photo albums found for ${ownerStudioAnnualState.year}.</div>`;
    return;
  }

  gridEl.innerHTML = cats.map(cat => {
    const isChecked = ownerStudioAnnualState.selectedCategories.has(cat.folderName);
    const nonBtcnjClass = cat.isNonBtcnj ? 'is-non-btcnj' : '';
    const selectedClass = isChecked ? 'is-selected' : '';
    return `
      <div class="annual-category-card ${nonBtcnjClass} ${selectedClass}" data-annual-cat="${escapeHtml(cat.folderName)}">
        <div class="annual-cat-header">
          <label class="annual-cat-title" style="cursor: pointer;">
            <input type="checkbox" class="annual-cat-checkbox" data-annual-cat="${escapeHtml(cat.folderName)}" ${isChecked ? 'checked' : ''} style="margin-right: 6px;">
            📁 ${escapeHtml(cat.folderName)}/
          </label>
          ${cat.isNonBtcnj ? '<span class="annual-cat-badge">NON-BTCNJ</span>' : ''}
        </div>
        <div class="annual-cat-meta">
          <span>📖 ${cat.albumCount} ${cat.albumCount === 1 ? 'Album' : 'Albums'}</span>
          <span>📸 ~${cat.photoCount} Files</span>
        </div>
      </div>
    `;
  }).join('');

  gridEl.querySelectorAll('.annual-category-card').forEach(card => {
    const catName = card.getAttribute('data-annual-cat');
    const cb = card.querySelector('.annual-cat-checkbox');
    card.addEventListener('click', (e) => {
      if (e.target.tagName.toLowerCase() === 'input') return;
      if (cb) {
        cb.checked = !cb.checked;
        if (cb.checked) {
          ownerStudioAnnualState.selectedCategories.add(catName);
        } else {
          ownerStudioAnnualState.selectedCategories.delete(catName);
        }
        card.classList.toggle('is-selected', cb.checked);
        updateAnnualSummary();
      }
    });
    if (cb) {
      cb.addEventListener('change', () => {
        if (cb.checked) {
          ownerStudioAnnualState.selectedCategories.add(catName);
        } else {
          ownerStudioAnnualState.selectedCategories.delete(catName);
        }
        card.classList.toggle('is-selected', cb.checked);
        updateAnnualSummary();
      });
    }
  });
}

export function updateAnnualSummary() {
  if (typeof document === 'undefined') return;
  const summaryEl = document.getElementById('owner-studio-annual-summary');
  const downloadBtn = document.getElementById('owner-studio-download-annual-btn');
  const selectedCats = ownerStudioAnnualState.categories.filter(c => ownerStudioAnnualState.selectedCategories.has(c.folderName));
  const totalAlbums = selectedCats.reduce((sum, c) => sum + c.albumCount, 0);
  const totalPhotos = selectedCats.reduce((sum, c) => sum + c.photoCount, 0);

  if (summaryEl) {
    if (ownerStudioAnnualState.categories.length === 0) {
      summaryEl.textContent = 'No albums found for this year.';
    } else {
      summaryEl.textContent = `${ownerStudioAnnualState.year}: ${selectedCats.length} folder(s), ${totalAlbums} album(s), ~${totalPhotos} media items selected.`;
    }
  }

  if (downloadBtn) {
    downloadBtn.disabled = totalAlbums === 0 || ownerStudioAnnualState.isDownloading;
  }
}

export function scanAnnualMediaCenterYear(targetYear = null) {
  if (typeof document === 'undefined') return;
  const isAdmin = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('btcnjcc_admin_authed') === 'true';
  if (!isAdmin) {
    console.warn('[OwnerMediaStudio] Annual Year ZIP scan rejected: admin authentication required.');
    return;
  }

  const rides = (typeof currentLoadedRides !== 'undefined' && currentLoadedRides && currentLoadedRides.length > 0)
    ? currentLoadedRides
    : (getCachedMediaRides() || buildMediaRides(FALLBACK_ALBUM_RIDES, PLAYLIST_VIDEOS));

  const yearSelect = document.getElementById('owner-studio-annual-year-select');
  const selectedYear = targetYear || (yearSelect ? yearSelect.value : null);

  const grouped = groupMediaCenterRidesByYearAndCategory(rides, selectedYear);
  ownerStudioAnnualState.year = grouped.year;
  ownerStudioAnnualState.availableYears = grouped.availableYears;
  ownerStudioAnnualState.categories = grouped.categories;
  ownerStudioAnnualState.selectedCategories = new Set(grouped.categories.map(c => c.folderName));

  if (yearSelect && grouped.availableYears.length > 0) {
    yearSelect.innerHTML = grouped.availableYears.map(y => `
      <option value="${escapeHtml(y)}" ${y === grouped.year ? 'selected' : ''}>${escapeHtml(y)}</option>
    `).join('');
  }

  renderAnnualCategoriesGrid();
  updateAnnualSummary();
}

export async function executeAnnualMediaCenterZipDownload() {
  if (typeof document === 'undefined') return;
  const isAdmin = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('btcnjcc_admin_authed') === 'true';
  if (!isAdmin) {
    alert('Admin authorization required for Annual Media Center Bulk Download.');
    return;
  }

  const volSelect = document.getElementById('owner-studio-annual-vol-select');
  const rawVolVal = volSelect && volSelect.value !== '' ? Number(volSelect.value) : (4 * 1024 * 1024 * 1024);
  const maxVolumeBytes = rawVolVal === 0 ? Number.MAX_SAFE_INTEGER : (rawVolVal > 0 ? rawVolVal : (4 * 1024 * 1024 * 1024));

  const selectedCats = ownerStudioAnnualState.categories.filter(c => ownerStudioAnnualState.selectedCategories.has(c.folderName));
  if (selectedCats.length === 0) return;

  const downloadBtn = document.getElementById('owner-studio-download-annual-btn');
  const statusEl = document.getElementById('owner-studio-annual-status');
  const progressWrapEl = document.getElementById('owner-studio-annual-progress-wrap');
  const progressLabelEl = document.getElementById('owner-studio-annual-progress-label');
  const progressPctEl = document.getElementById('owner-studio-annual-progress-pct');
  const progressEtaEl = document.getElementById('owner-studio-annual-progress-eta');
  const progressBarEl = document.getElementById('owner-studio-annual-progressbar');
  const progressFillEl = document.getElementById('owner-studio-annual-progress-fill');
  const progressFileEl = document.getElementById('owner-studio-annual-progress-file');
  const progressRateEl = document.getElementById('owner-studio-annual-progress-rate');
  const originalBtnText = downloadBtn ? downloadBtn.textContent : '';

  if (downloadBtn) {
    downloadBtn.disabled = true;
    downloadBtn.textContent = '⏳ Downloading...';
  }
  if (statusEl) {
    statusEl.className = 'status-msg';
    statusEl.textContent = `Starting Annual Bulk Download for ${ownerStudioAnnualState.year}...`;
    statusEl.style.display = 'none';
  }
  if (progressWrapEl) {
    progressWrapEl.style.display = 'flex';
  }

  ownerStudioAnnualState.isDownloading = true;

  const startTimeMs = (typeof performance !== 'undefined' && typeof performance.now === 'function')
    ? performance.now()
    : Date.now();
  const getElapsedMs = () => {
    const now = (typeof performance !== 'undefined' && typeof performance.now === 'function')
      ? performance.now()
      : Date.now();
    return Math.max(0, now - startTimeMs);
  };

  let expectedTotalItems = selectedCats.reduce((sum, c) => sum + (Number(c.photoCount) || 0), 0);
  let totalItemsDownloaded = 0;
  let totalBytesDownloaded = 0;
  let telemetryState = {
    previousPercent: 0,
    previousEmaRate: 0,
    previousEtaText: 'ETA --m --s',
    lastEtaUpdateMs: 0
  };

  function renderAnnualProgressUi({ labelText, fileText, isComplete = false } = {}) {
    const t = computeAnnualDownloadTelemetry({
      completedItems: totalItemsDownloaded,
      expectedTotalItems,
      elapsedMs: getElapsedMs(),
      previousPercent: telemetryState.previousPercent,
      previousEmaRate: telemetryState.previousEmaRate,
      previousEtaText: telemetryState.previousEtaText,
      lastEtaUpdateMs: telemetryState.lastEtaUpdateMs,
      isComplete
    });
    telemetryState.previousPercent = t.percent;
    telemetryState.previousEmaRate = t.emaRate;
    telemetryState.previousEtaText = t.etaText;
    telemetryState.lastEtaUpdateMs = t.lastEtaUpdateMs;

    if (progressLabelEl && labelText) {
      progressLabelEl.textContent = labelText;
    }
    if (progressPctEl) {
      progressPctEl.textContent = t.percentText;
    }
    if (progressEtaEl) {
      progressEtaEl.textContent = t.etaText;
    }
    if (progressFillEl) {
      progressFillEl.style.width = `${t.percent.toFixed(1)}%`;
    }
    if (progressBarEl) {
      progressBarEl.setAttribute('aria-valuenow', String(Math.round(t.percent)));
    }
    if (progressFileEl && fileText) {
      progressFileEl.textContent = fileText;
    }
    if (progressRateEl) {
      const mbStr = `${(totalBytesDownloaded / (1024 * 1024)).toFixed(1)} MB`;
      progressRateEl.textContent = mbStr;
    }
  }

  renderAnnualProgressUi({
    labelText: `Preparing ${ownerStudioAnnualState.year} archive (0 / ~${expectedTotalItems} files)...`,
    fileText: 'Inspecting selected ride category albums...'
  });

  try {
    const collisionRegistry = new Set();
    let partIndex = 1;
    let currentVolumeEntries = [];
    let currentVolumeBytes = 0;

    const baseZipName = `BTCNJCC_${ownerStudioAnnualState.year}_Media_Center (Tagged)`;

    async function flushVolume(isFinal = false) {
      if (currentVolumeEntries.length === 0) return;
      renderAnnualProgressUi({
        labelText: `Packaging ZIP archive (Part ${partIndex}) · ${totalItemsDownloaded} / ${expectedTotalItems} files`,
        fileText: `Writing ${currentVolumeEntries.length} tagged files to ${baseZipName}...`
      });
      const zipResult = await createZipArchiveBlob(currentVolumeEntries);
      const zipFilename = (!isFinal || partIndex > 1)
        ? `${baseZipName} - Part ${partIndex}.zip`
        : `${baseZipName}.zip`;

      const blobUrl = URL.createObjectURL(zipResult.blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = zipFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);

      currentVolumeEntries = [];
      currentVolumeBytes = 0;
      partIndex++;

      await new Promise(r => setTimeout(r, 600));
    }

    const albumJobs = [];
    for (const cat of selectedCats) {
      for (const ride of (cat.albums || [])) {
        albumJobs.push({ catFolder: cat.folderName, ride });
      }
    }

    const totalAlbumsCount = albumJobs.length;
    const totalAlbumPad = String(totalAlbumsCount).length;

    for (let j = 0; j < totalAlbumsCount; j++) {
      const { catFolder, ride } = albumJobs[j];
      const rideTitle = ride.title || 'Ride';
      const albumIndexPadded = String(j + 1).padStart(totalAlbumPad, '0');
      const albumTotalPadded = String(totalAlbumsCount).padStart(totalAlbumPad, '0');

      renderAnnualProgressUi({
        labelText: `Album ${albumIndexPadded}/${albumTotalPadded} · ${totalItemsDownloaded} / ${expectedTotalItems} files`,
        fileText: `Inspecting ${catFolder}/: "${rideTitle}"...`
      });

      try {
        const endpoint = `${CONFIG.SYNC_SINGLE_MEDIA_API_URL}?photoAlbumUrl=${encodeURIComponent(ride.photoAlbumUrl)}&includeManifest=true`;
        const res = await fetch(endpoint, { signal: AbortSignal.timeout(15000) });
        if (!res.ok) continue;
        const data = await res.json();
        const items = Array.isArray(data.items) ? data.items : [];

        // Dynamically refine expectedTotalItems by replacing this ride's initial estimate with actual manifest count
        const rideEstimatedCount = Math.max(1, Number(ride.photoCount) || 0);
        expectedTotalItems = Math.max(
          totalItemsDownloaded + items.length,
          expectedTotalItems - rideEstimatedCount + items.length
        );

        if (items.length === 0) continue;

        const CONCURRENCY = 4;
        let itemCursor = 0;

        async function itemWorker() {
          while (itemCursor < items.length) {
            const idx = itemCursor++;
            const it = items[idx];
            try {
              const proxyUrl = `${CONFIG.SYNC_SINGLE_MEDIA_API_URL}?proxyDownload=1&mediaUrl=${encodeURIComponent(it.downloadUrl)}&contributor=${encodeURIComponent(it.contributorName)}`;
              const itemRes = await fetch(proxyUrl);
              if (itemRes.ok) {
                let resolvedFilename = '';
                const xPrefixed = itemRes.headers.get('x-prefixed-filename') || itemRes.headers.get('X-Prefixed-Filename');
                if (xPrefixed) {
                  try { resolvedFilename = decodeURIComponent(xPrefixed).trim(); } catch (_) {}
                }
                if (!resolvedFilename) {
                  resolvedFilename = parseContentDispositionFilename(itemRes.headers.get('content-disposition'));
                }
                if (!resolvedFilename) {
                  resolvedFilename = it.suggestedFilename || `media_${idx + 1}.${it.isVideo ? 'mp4' : 'jpg'}`;
                }

                const buffer = await itemRes.arrayBuffer();
                const zipEntryPath = resolveOsSafeZipEntryPath(
                  catFolder,
                  resolvedFilename,
                  { dateIso: ride.dateIso || ride.dateStr, rideId: ride.rideId },
                  collisionRegistry
                );

                const entryBytes = buffer.byteLength + zipEntryPath.length + 100;
                if (currentVolumeEntries.length > 0 && (currentVolumeBytes + entryBytes > maxVolumeBytes)) {
                  await flushVolume(false);
                }

                currentVolumeEntries.push({
                  filename: zipEntryPath,
                  data: new Uint8Array(buffer)
                });
                currentVolumeBytes += entryBytes;
                totalBytesDownloaded += buffer.byteLength;
                totalItemsDownloaded++;

                renderAnnualProgressUi({
                  labelText: `Album ${albumIndexPadded}/${albumTotalPadded} · ${totalItemsDownloaded} / ${expectedTotalItems} files`,
                  fileText: `${catFolder}/: ${resolvedFilename}`
                });
              }
            } catch (err) {
              console.warn(`[AnnualZip] Item download failed in album ${ride.rideId}:`, err);
            }
          }
        }

        const workers = [];
        const pool = Math.min(CONCURRENCY, items.length);
        for (let w = 0; w < pool; w++) {
          workers.push(itemWorker());
        }
        await Promise.all(workers);

      } catch (albumErr) {
        console.warn(`[AnnualZip] Album inspection failed for ${ride.rideId}:`, albumErr);
      }
    }

    if (currentVolumeEntries.length > 0) {
      await flushVolume(true);
    }

    if (totalItemsDownloaded === 0) {
      throw new Error('No media files could be retrieved for the selected categories.');
    }

    const volumesCount = Math.max(1, partIndex - 1);
    renderAnnualProgressUi({
      labelText: `Completed · ${totalItemsDownloaded} / ${totalItemsDownloaded} files (${volumesCount} ZIP volume${volumesCount === 1 ? '' : 's'})`,
      fileText: `All ${ownerStudioAnnualState.year} owner-tagged media archived.`,
      isComplete: true
    });

    if (statusEl) {
      statusEl.className = 'status-msg status-success';
      statusEl.textContent = `✅ Successfully downloaded ${totalItemsDownloaded} files across ${volumesCount} ZIP volume(s)!`;
      statusEl.style.display = 'block';
    }
  } catch (err) {
    if (statusEl) {
      statusEl.className = 'status-msg status-error';
      statusEl.textContent = `Annual ZIP download failed: ${err.message}`;
      statusEl.style.display = 'block';
    }
  } finally {
    ownerStudioAnnualState.isDownloading = false;
    if (downloadBtn) {
      downloadBtn.disabled = false;
      downloadBtn.textContent = originalBtnText || '⬇ Download Year ZIP';
    }
    updateAnnualSummary();
  }
}

export function initOwnerMediaStudio() {
  if (typeof document === 'undefined') return;

  // Tabs
  const tabGphotos = document.getElementById('owner-studio-tab-gphotos');
  const tabLocal = document.getElementById('owner-studio-tab-local');
  const tabAnnual = document.getElementById('owner-studio-tab-annual');
  if (tabGphotos) tabGphotos.addEventListener('click', () => switchOwnerStudioTab('gphotos'));
  if (tabLocal) tabLocal.addEventListener('click', () => switchOwnerStudioTab('local'));
  if (tabAnnual) tabAnnual.addEventListener('click', () => switchOwnerStudioTab('annual'));

  // Close buttons
  const closeBtn = document.getElementById('owner-studio-close-btn');
  const backdrop = document.getElementById('owner-studio-backdrop');
  const cancelGphotos = document.getElementById('owner-studio-cancel-gphotos-btn');
  const cancelLocal = document.getElementById('owner-studio-cancel-local-btn');
  const cancelAnnual = document.getElementById('owner-studio-cancel-annual-btn');
  [closeBtn, backdrop, cancelGphotos, cancelLocal, cancelAnnual].forEach(btn => {
    if (btn) btn.addEventListener('click', closeOwnerMediaStudioModal);
  });

  // Fetch button
  const fetchBtn = document.getElementById('owner-studio-fetch-btn');
  const urlInput = document.getElementById('owner-studio-album-url-input');
  if (fetchBtn && urlInput) {
    fetchBtn.addEventListener('click', () => {
      inspectGooglePhotosAlbumOwners(urlInput.value.trim());
    });
    urlInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        inspectGooglePhotosAlbumOwners(urlInput.value.trim());
      }
    });
  }

  // Copy manifest
  const copyBtn = document.getElementById('owner-studio-copy-manifest-btn');
  if (copyBtn) {
    copyBtn.addEventListener('click', () => {
      const activeCont = ownerStudioGphotosState.activeContributor;
      const items = activeCont === 'all'
        ? ownerStudioGphotosState.items
        : ownerStudioGphotosState.items.filter(it => it.contributorName === activeCont);

      const manifestText = items.map(it => `${it.suggestedFilename}\t${it.downloadUrl}`).join('\n');
      if (navigator.clipboard) {
        navigator.clipboard.writeText(manifestText).then(() => {
          copyBtn.textContent = '✅ Copied!';
          setTimeout(() => { copyBtn.textContent = '📋 Copy Manifest'; }, 2000);
        });
      }
    });
  }

  // Download Google Photos
  const downloadGphotosBtn = document.getElementById('owner-studio-download-gphotos-btn');
  if (downloadGphotosBtn) {
    downloadGphotosBtn.addEventListener('click', () => exportOwnerTaggedBatch('gphotos'));
  }

  // Local Dropzone & File Input
  const dropzone = document.getElementById('owner-studio-dropzone');
  const fileInput = document.getElementById('owner-studio-file-input');
  if (dropzone && fileInput) {
    dropzone.addEventListener('click', () => fileInput.click());
    dropzone.addEventListener('dragover', (e) => {
      e.preventDefault();
      dropzone.classList.add('drag-over');
    });
    dropzone.addEventListener('dragleave', () => dropzone.classList.remove('drag-over'));
    dropzone.addEventListener('drop', (e) => {
      e.preventDefault();
      dropzone.classList.remove('drag-over');
      if (e.dataTransfer && e.dataTransfer.files) {
        handleLocalOwnerFilesSelected(e.dataTransfer.files);
      }
    });
    fileInput.addEventListener('change', () => {
      if (fileInput.files) {
        handleLocalOwnerFilesSelected(fileInput.files);
        fileInput.value = '';
      }
    });
  }

  // Local Contributor Name Live Retag
  const contributorInput = document.getElementById('owner-studio-contributor-input');
  if (contributorInput) {
    contributorInput.addEventListener('input', () => {
      try { localStorage.setItem('btcnjcc_contributor_profile_v1', contributorInput.value.trim()); } catch (_) {}
      retagLocalOwnerQueue();
    });
  }

  // Clear Local Queue
  const clearLocalBtn = document.getElementById('owner-studio-clear-local-btn');
  if (clearLocalBtn) {
    clearLocalBtn.addEventListener('click', () => {
      ownerStudioLocalState.files = [];
      renderLocalOwnerQueue();
    });
  }

  // Export Local Tagged
  const exportLocalBtn = document.getElementById('owner-studio-export-local-btn');
  if (exportLocalBtn) {
    exportLocalBtn.addEventListener('click', () => exportOwnerTaggedBatch('local'));
  }

  // Annual Year Controls
  const annualScanBtn = document.getElementById('owner-studio-annual-scan-btn');
  const annualYearSelect = document.getElementById('owner-studio-annual-year-select');
  const annualDownloadBtn = document.getElementById('owner-studio-download-annual-btn');

  if (annualScanBtn) {
    annualScanBtn.addEventListener('click', () => {
      const year = annualYearSelect ? annualYearSelect.value : null;
      scanAnnualMediaCenterYear(year);
    });
  }
  if (annualYearSelect) {
    annualYearSelect.addEventListener('change', () => {
      scanAnnualMediaCenterYear(annualYearSelect.value);
    });
  }
  if (annualDownloadBtn) {
    annualDownloadBtn.addEventListener('click', executeAnnualMediaCenterZipDownload);
  }
}
